import React, { useState, useCallback, useMemo, useRef, useContext, createContext, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Type, Heading1, Image as ImageIcon, MousePointerClick, Square,
  Columns, Rows, Trash2, Copy, ChevronUp, ChevronDown, Plus,
  Eye, EyeOff, Code2, Monitor, Smartphone, Download, Upload, Layers,
  Settings2, Palette, AlignLeft, X, FileJson, Sparkles,
  GalleryHorizontalEnd, LayoutGrid, ChevronLeft, ChevronRight,
  CreditCard, Megaphone, Quote, Package, Boxes, Move,
  TextCursorInput, AlignJustify, ChevronsUpDown, CalendarDays,
  Video, Minus, Star, BarChart3, TrendingUp, ListChecks, Tag, CheckSquare,
  FileInput, SquareStack, Circle, ToggleLeft, ToggleRight, FileCode, Maximize2,
  ChevronsDownUp, PanelRightOpen, PanelLeft, PanelRight, MessageSquare, FolderTree,
  FileText, FileUp, PenLine, FolderClosed, Check, HelpCircle, ShoppingCart, RefreshCw,
  Undo2, Redo2, Tag as TagIcon, Unlock, Lock, Save
} from "lucide-react";
import { SHOP_TEMPLATES, makeShopFollowBar } from "./ShopBoutiqueTemplates";
import PageBuilderHelp from "./PageBuilderHelp";
import { CONTENT_TEMPLATES, getClassicContentDoc, resolveTemplateJsonDoc } from "./ContentPageTemplates";
import { PROJECT_TEMPLATES } from "./ProjectInvestTemplates";
import PageRenderer from "./PageRenderer";
import { elakay_API } from "../../../config";
import { isAuthenticated } from "../../auth";
import { getMyShops, buyShopDisplay, buyShopDownload, buyShopBoth, getShopPricing } from "../../auth/stripe";
import { getPageBuilderById, createPageBuilder, purchasePageBuilder, verifyPageHtmlEditAccess, getPageHtmlSourceAPI, savePageHtmlSourceAPI, restorePageGeneratedHtmlAPI } from "../../auth/apiPageBuilder";
import { getFirstAnimationSettingsAPI } from "../../auth/Animationsettingsapi";
import { getPageTemplateOverride, upsertPageTemplateOverride, listPageTemplateOverrides, assignTemplateToPage, resetPageTemplateOverrideHtml } from "../../auth/apiPageTemplateOverride";
import { resolveCanonicalDataSourceKey, isRealContentPageDataSource } from "./dataSourceIdentity";
import { buildPageBuilderDraftStorageKeys } from "./pageBuilderDraftStorage";
import { Family} from "../../pageInside/Rolle"
import { canEditPageHtml } from "./pageHtmlAccess";
import PageHtmlSourceModal from "./PageHtmlSourceModal";
import { elakayAutoText } from "../dataElakay";
import {
  ROOT_PARENT_ID,
  updateTree, removeFromTree, findInTree, findParentInTree, insertIntoTree, moveInTree,
  moveNodeToParent,
} from "../editorKit/treeOps";
import { useEditorHistory } from "../editorKit/useEditorHistory";
import { useElementDrag } from "../editorKit/useElementDrag";
import { useElementResize, RESIZE_HANDLES } from "../editorKit/useElementResize";
import { useKeyboardManipulation } from "../editorKit/useKeyboardManipulation";
import SelectionOverlay from "../editorKit/SelectionOverlay";
import { getIcon as getRegistryIcon } from "../iconKit/iconRegistry";
import { renderIconSvgMarkup } from "../iconKit/renderIconSvgMarkup";
import IconPicker from "../iconKit/IconPicker";
import ElakaySwiperBlock from "../../dynamicSwipers/ElakaySwiperBlock";
import SwiperLibraryPicker from "../../dynamicSwipers/SwiperLibraryPicker";
import UniversalMediaPicker from "../../mediaLibrary/UniversalMediaPicker";
import { uploadFileRequest } from "../../../utils/uploadRequest";
import { toFileUrl } from "../../../utils/fileUrl";
import {
  getFreeLayout,
  getPageLayoutMode,
  layoutDeviceForPreset,
  nextLayer,
  withFreeLayout,
} from "./pageBuilderFreeLayout";


// ── i18n PageBuilder — pattern identique à AnimationSettings.jsx ─────────────
let __pbInfoSource;
const __setPbInfoSource = (src) => { if (src) __pbInfoSource = src; };
const __pbText = (key, fallback) =>
  __pbInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __pbInfoSource.languageAPI)?.pageBuilder?.[key] || fallback;

// Appareil courant pour le rendu (desktop = style de base ; mobile = base + surcharges mobileStyle)
const DeviceContext = createContext("desktop");

function ViewportModal({ children }) {
  if (typeof document === "undefined") return children;
  return createPortal(children, document.body);
}

const COLLAPSIBLE_TEXT_TYPES = new Set(["heading", "text"]);

function canUseTextCollapse(node) {
  return COLLAPSIBLE_TEXT_TYPES.has(node?.type);
}

function getLocalizedLinkText(key, fallback) {
  return (
    __pbInfoSource?.language?.(__pbInfoSource, "Link", key) ||
    __pbInfoSource?.laguangeElakay?.("Link", __pbInfoSource.languageAPI)?.[key] ||
    fallback
  );
}

function getTextCollapseConfig(node) {
  const cfg = node?.textCollapse || {};
  const maxChars = Math.max(0, Number(cfg.maxChars) || 0);
  return {
    enabled: !!cfg.enabled && maxChars > 0,
    maxChars,
    seeMoreLabel: String(cfg.seeMoreLabel || "").trim() || getLocalizedLinkText("seeMore", "See More"),
    seeLessLabel: String(cfg.seeLessLabel || "").trim() || getLocalizedLinkText("seeLess", "See Less"),
    controlType: cfg.controlType === "button" ? "button" : "link",
    controlColor: String(cfg.controlColor || "").trim(),
    controlFontSize: String(cfg.controlFontSize || "").trim(),
  };
}

function truncateTextByChars(text, maxChars) {
  const raw = String(text || "");
  if (!maxChars || raw.length <= maxChars) return raw;
  const slice = raw.slice(0, maxChars).replace(/\s+\S*$/, "").trimEnd();
  return `${slice || raw.slice(0, maxChars).trimEnd()}...`;
}

// Style effectif d'un nœud selon l'appareil : en mobile on fusionne les surcharges mobileStyle
function effStyle(node, device) {
  if (device === "mobile" && node.mobileStyle) return { ...node.style, ...node.mobileStyle };
  return node.style;
}

function parseConsoleCssToReactStyle(cssText = "") {
  if (!cssText || typeof cssText !== "string") return {};
  return cssText
    .split(";")
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      const colonIndex = line.indexOf(":");
      if (colonIndex <= 0) return acc;
      const rawKey = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (!rawKey || !value) return acc;
      const key = rawKey.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      acc[key] = value;
      return acc;
    }, {});
}

/* ============================================================================
   PAGE BUILDER — Éditeur de pages React piloté par JSON
   Toute la page (structure, styles, texte, attributs HTML, composants) vit
   dans un objet JSON. L'UI édite ce JSON ; le rendu et l'export en découlent.
   ========================================================================== */

// ---------------------------------------------------------------------------
// Catalogue des types d'éléments disponibles
// ---------------------------------------------------------------------------
const ELEMENT_TYPES = {
  heading: {
    get label() { return elakayAutoText("h_eb97899aedcd56", "Titre"); }, icon: Heading1, tag: "h2", group: "base",
    defaults: {
      get text() { return elakayAutoText("h_ce6d1a240b0c4d", "Nouveau titre"); }, tag: "h2",
      style: { fontSize: "32px", fontWeight: "700", color: "#1a1a2e", margin: "0 0 12px 0", lineHeight: "1.2" },
    },
  },
  text: {
    get label() { return elakayAutoText("h_9ad27969bc7090", "Paragraphe"); }, icon: Type, tag: "p", group: "base",
    defaults: {
      get text() { return elakayAutoText("h_7c99ff8028dbbd", "Votre texte ici. Cliquez pour modifier le contenu et les styles."); }, tag: "p",
      style: { fontSize: "16px", color: "#4a4a68", lineHeight: "1.7", margin: "0 0 16px 0" },
    },
  },
  button: {
    get label() { return elakayAutoText("h_fd5aea7172cc0a", "Bouton"); }, icon: MousePointerClick, tag: "button", group: "base",
    defaults: {
      get text() { return elakayAutoText("h_f06bc3012a1673", "Cliquez-moi"); }, href: "",
      style: { display: "inline-block", padding: "12px 28px", fontSize: "15px", fontWeight: "600",
        color: "#ffffff", background: "#6d28d9", border: "none", borderRadius: "10px", cursor: "pointer", textDecoration: "none" },
    },
  },
  link: {
    get label() { return elakayAutoText("h_8fd81573bfc344", "Lien (page)"); }, icon: ChevronRight, tag: "a", isLink: true, group: "base",
    defaults: {
      get text() { return elakayAutoText("h_6bcd094833e544", "Aller à une page"); }, target: "", href: "",
      style: { display: "inline-block", padding: "12px 28px", fontSize: "15px", fontWeight: "600",
        color: "#ffffff", background: "#6d28d9", borderRadius: "10px", cursor: "pointer", textDecoration: "none" },
    },
  },
  image: {
    get label() { return elakayAutoText("h_50e19fda0d5b4b", "Image"); }, icon: ImageIcon, tag: "img", group: "base",
    defaults: {
      src: "https://placehold.co/600x320/6d28d9/ffffff?text=Image", get alt() { return elakayAutoText("h_d67ea414633583", "Description de l'image"); },
      style: { width: "100%", height: "auto", borderRadius: "12px", display: "block" },
    },
  },
  container: {
    get label() { return elakayAutoText("h_a33d642c1d8348", "Conteneur"); }, icon: Square, tag: "div", isContainer: true, group: "layout",
    defaults: {
      style: { padding: "24px", background: "#ffffff", borderRadius: "16px", border: "1px solid #ece9f5" },
      children: [],
    },
  },
  row: {
    get label() { return elakayAutoText("h_53a2544cacfd92", "Colonnes"); }, icon: Columns, tag: "div", isContainer: true, group: "layout",
    defaults: { style: { display: "flex", gap: "20px", flexWrap: "wrap" }, children: [] },
  },
  stack: {
    get label() { return elakayAutoText("h_fadef6dc5ef145", "Pile (vertical)"); }, icon: Rows, tag: "div", isContainer: true, group: "layout",
    defaults: { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [] },
  },
  section: {
    get label() { return elakayAutoText("h_7854785a1428da", "Section pleine"); }, icon: Square, tag: "section", isContainer: true, group: "layout",
    defaults: { style: { width: "100%", padding: "64px 32px", background: "#faf9fd", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }, children: [] },
  },
  banner: {
    get label() { return elakayAutoText("h_e51914802c489c", "Bandeau"); }, icon: Minus, tag: "div", isContainer: true, group: "layout",
    defaults: { style: { width: "100%", padding: "16px 24px", background: "linear-gradient(90deg,#6d28d9,#a855f7)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }, children: [] },
  },
  circle: {
    get label() { return elakayAutoText("h_5a47733e149ed4", "Cercle"); }, icon: Square, tag: "div", isContainer: true, group: "layout",
    defaults: { style: { width: "160px", height: "160px", borderRadius: "50%", background: "#f3effe", border: "1px solid #ece9f5", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }, children: [] },
  },
  panel: {
    get label() { return elakayAutoText("h_8ff8ce91affba8", "Panneau ombré"); }, icon: Square, tag: "div", isContainer: true, group: "layout",
    defaults: { style: { padding: "28px", background: "#ffffff", borderRadius: "20px", boxShadow: "0 12px 40px rgba(60,30,110,0.10)" }, children: [] },
  },
  spacer: {
    get label() { return elakayAutoText("h_e0a35ec4d2a004", "Espacement"); }, icon: Rows, tag: "div", group: "layout",
    defaults: { style: { height: "40px", width: "100%" } },
  },

  // --- Composants riches à items (image + titre + texte par item) ---
  swiper: {
    get label() { return elakayAutoText("h_ef862f23b933ca", "Carrousel"); }, icon: GalleryHorizontalEnd, tag: "div", hasItems: true, group: "rich",
    defaults: {
      style: { borderRadius: "16px", overflow: "hidden", background: "#faf9fd" },
      options: { autoplay: false, interval: 4000, showArrows: true, showDots: true, height: "300px" },
      itemStyle: { titleColor: "#ffffff", textColor: "#f0edf8", overlay: "rgba(26,26,46,0.45)" },
      items: [
        { id: uid(), image: "https://placehold.co/900x300/6d28d9/ffffff?text=Slide+1", get title() { return elakayAutoText("h_6e26cdfb202a47", "Première slide"); }, get text() { return elakayAutoText("h_9cce202841c260", "Décrivez votre offre ici."); } },
        { id: uid(), image: "https://placehold.co/900x300/8b5cf6/ffffff?text=Slide+2", get title() { return elakayAutoText("h_d5f2048b5fa1d3", "Deuxième slide"); }, get text() { return elakayAutoText("h_980edd2fbb05f6", "Un autre message marketing."); } },
        { id: uid(), image: "https://placehold.co/900x300/a855f7/ffffff?text=Slide+3", get title() { return elakayAutoText("h_3310f15f237a56", "Troisième slide"); }, get text() { return elakayAutoText("h_8c8e99343d2581", "Et une troisième accroche."); } },
      ],
    },
  },
  grid: {
    get label() { return elakayAutoText("h_25ee2ce6183c01", "Grille"); }, icon: LayoutGrid, tag: "div", isContainer: true, isGrid: true, group: "rich",
    defaults: {
      style: { display: "grid", gap: "20px" },
      options: { columns: 3 },
      children: [],
    },
  },

  // --- Carte autonome : image + titre + texte (+ bouton optionnel), portrait/paysage ---
  card: {
    get label() { return elakayAutoText("h_8bb6835706fc4e", "Carte"); }, icon: CreditCard, tag: "div", isCard: true, group: "rich",
    defaults: {
      options: { orientation: "portrait", imageSide: "left", showButton: false },
      style: { background: "#ffffff", borderRadius: "14px", overflow: "hidden", border: "1px solid #ece9f5" },
      content: {
        image: "https://placehold.co/400x300/6d28d9/ffffff?text=Carte",
        imageHeight: "180px",
        get title() { return elakayAutoText("h_625969f4e67e19", "Titre de la carte"); },
        titleColor: "#1a1a2e",
        get text() { return elakayAutoText("h_c18f9adfc369e7", "Texte descriptif de la carte. Modifiable dans le panneau de droite."); },
        textColor: "#4a4a68",
        buttonText: "En savoir plus",
        buttonHref: "#",
      },
    },
  },

  // --- Groupe de textes composé : plusieurs lignes empilées (sur-titre, titre, paragraphe...) ---
  textGroup: {
    get label() { return elakayAutoText("h_f71a5c70fd646d", "Groupe de texte"); }, icon: AlignLeft, tag: "div", hasItems: true, isTextGroup: true, group: "rich",
    defaults: {
      style: { display: "flex", flexDirection: "column", gap: "6px" },
      items: [
        { id: uid(), tag: "p", get text() { return elakayAutoText("h_46011604716209", "SUR-TITRE"); }, fontSize: "13px", fontWeight: "700", color: "#6d28d9", letterSpacing: "0.08em" },
        { id: uid(), tag: "h3", get text() { return elakayAutoText("h_cf5e533e472670", "Titre principal du groupe"); }, fontSize: "26px", fontWeight: "800", color: "#1a1a2e", letterSpacing: "normal" },
        { id: uid(), tag: "p", get text() { return elakayAutoText("h_7fe6769493e37c", "Paragraphe descriptif qui complète le titre ci-dessus."); }, fontSize: "16px", fontWeight: "400", color: "#4a4a68", letterSpacing: "normal" },
      ],
    },
  },

  // --- Cadre produit : conteneur curaté (image + titre + prix + note + bouton…), librement réarrangeable ---
  productFrame: {
    get label() { return elakayAutoText("h_pf_label_9f21", "Cadre produit"); }, icon: Package, tag: "div", isContainer: true, isProductFrame: true, group: "rich",
    // Contrairement aux conteneurs génériques (container/row/stack/section/panel/grid),
    // un cadre produit n'accepte qu'un ensemble curaté d'enfants — empêche par exemple
    // qu'un autre cadre produit ou une section entière y soit déposé.
    allowedChildTypes: ["image", "heading", "text", "price", "rating", "button", "link", "badge", "icon"],
    defaults: {
      options: { orientation: "portrait" },
      style: { display: "flex", flexDirection: "column", gap: "10px", padding: "16px",
        background: "#ffffff", borderRadius: "16px", border: "1px solid #ece9f5", maxWidth: "280px" },
      children: [
        { id: uid(), type: "image", src: "https://placehold.co/400x300/6d28d9/ffffff?text=Produit",
          get alt() { return elakayAutoText("h_pf_alt_1a02", "Produit"); },
          style: { width: "100%", height: "auto", borderRadius: "12px", display: "block" } },
        { id: uid(), type: "heading", tag: "h3",
          get text() { return elakayAutoText("h_pf_title_7c31", "Nom du produit"); },
          style: { fontSize: "18px", fontWeight: "700", color: "#1a1a2e", margin: "0" } },
        { id: uid(), type: "price",
          content: { current: 39.99, old: 59.99, currency: "€" },
          style: { display: "flex", alignItems: "baseline", gap: "8px" } },
        { id: uid(), type: "rating",
          content: { value: 4, max: 5, color: "#f59e0b", label: "" },
          style: { display: "flex", alignItems: "center", gap: "6px" } },
        { id: uid(), type: "button",
          get text() { return elakayAutoText("h_pf_cta_4e19", "Ajouter au panier"); },
          style: { display: "block", padding: "12px 20px", fontSize: "14px", fontWeight: "600",
            color: "#ffffff", background: "#6d28d9", border: "none", borderRadius: "10px",
            cursor: "pointer", textDecoration: "none", textAlign: "center" } },
      ],
    },
  },

  // --- Prix : prix courant + ancien prix barré + remise calculée ---
  price: {
    get label() { return elakayAutoText("h_price_label_6b02", "Prix"); }, icon: BarChart3, tag: "div", isPriceBlock: true, group: "data",
    defaults: {
      content: { current: 39.99, old: 0, currency: "€" },
      style: { display: "flex", alignItems: "baseline", gap: "8px" },
    },
  },
  // --- Badge : petite étiquette texte + couleur (ex. "Nouveau", "-30%") ---
  badge: {
    get label() { return elakayAutoText("h_badge_label_2d91", "Badge"); }, icon: TagIcon, tag: "span", isBadge: true, group: "data",
    defaults: {
      get text() { return elakayAutoText("h_badge_text_5f38", "Nouveau"); },
      options: { color: "#ffffff", background: "#6d28d9" },
      style: { display: "inline-block", padding: "4px 10px", fontSize: "12px", fontWeight: "700",
        borderRadius: "999px", lineHeight: 1.4 },
    },
  },

  // ===== FORMULAIRES (BookingPage, CommunityPage, contact) =====
  input: {
    get label() { return elakayAutoText("h_67d0c143fd436e", "Champ texte"); }, icon: TextCursorInput, tag: "input", isField: true, group: "form",
    defaults: {
      field: { get label() { return elakayAutoText("h_cf893e2a7f6afa", "Votre nom"); }, get placeholder() { return elakayAutoText("h_f5f5874d08f842", "Saisissez ici…"); }, inputType: "text" },
      style: { display: "block", width: "100%", padding: "11px 14px", fontSize: "15px", border: "1px solid #d9d3ec", borderRadius: "10px", color: "#2a2547", background: "#fff" },
    },
  },
  textarea: {
    get label() { return elakayAutoText("h_e5d83ebaf6321d", "Zone de texte"); }, icon: AlignJustify, tag: "textarea", isField: true, group: "form",
    defaults: {
      field: { get label() { return elakayAutoText("h_65d92a68c22a2b", "Votre message"); }, get placeholder() { return elakayAutoText("h_c640ceab32354a", "Écrivez votre message…"); }, rows: 4 },
      style: { display: "block", width: "100%", padding: "11px 14px", fontSize: "15px", border: "1px solid #d9d3ec", borderRadius: "10px", color: "#2a2547", background: "#fff", resize: "vertical" },
    },
  },
  select: {
    get label() { return elakayAutoText("h_b9244040062735", "Menu déroulant"); }, icon: ChevronsUpDown, tag: "select", isField: true, hasOptions: true, group: "form",
    defaults: {
      field: { get label() { return elakayAutoText("h_6c7f6b7a0e21e5", "Choisissez une option"); } },
      options: { choices: ["Option 1", "Option 2", "Option 3"] },
      style: { display: "block", width: "100%", padding: "11px 14px", fontSize: "15px", border: "1px solid #d9d3ec", borderRadius: "10px", color: "#2a2547", background: "#fff" },
    },
  },
  checkbox: {
    get label() { return elakayAutoText("h_f4679319a0510a", "Case à cocher"); }, icon: CheckSquare, tag: "label", isCheckbox: true, group: "form",
    defaults: {
      field: { get label() { return elakayAutoText("h_7f0aef1d83e34f", "J'accepte les conditions"); }, checked: false },
      style: { display: "flex", alignItems: "center", gap: "10px", fontSize: "15px", color: "#4a4a68", cursor: "pointer" },
    },
  },
  datefield: {
    get label() { return elakayAutoText("h_387a1d23ae83d7", "Champ date"); }, icon: CalendarDays, tag: "input", isField: true, group: "form",
    defaults: {
      field: { get label() { return elakayAutoText("h_8c2e1ace2c3b78", "Date du rendez-vous"); }, inputType: "date" },
      style: { display: "block", width: "100%", padding: "11px 14px", fontSize: "15px", border: "1px solid #d9d3ec", borderRadius: "10px", color: "#2a2547", background: "#fff" },
    },
  },

  // ===== MÉDIAS RICHES (VideoPage, BlogPage, LandingPage) =====
  video: {
    get label() { return elakayAutoText("h_304f6ca42f3628", "Vidéo"); }, icon: Video, tag: "div", isVideo: true, group: "media",
    defaults: {
      options: { url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", ratio: "16 / 9" },
      style: { width: "100%", borderRadius: "14px", overflow: "hidden", background: "#000" },
    },
  },
  // Swiper du Swiper Manager (/elakay/swipers) référencé par son id — jamais
  // copié : le même rendu (<DynamicSwiper>) que partout ailleurs, et une
  // modification dans le Swiper Manager se voit sur toutes les pages.
  elakaySwiper: {
    get label() { return elakayAutoText("h_esb_label", "Elakay Swiper"); }, icon: GalleryHorizontalEnd, tag: "div", isElakaySwiper: true, group: "media",
    defaults: {
      options: { swiperId: "", swiperName: "", maxWidth: 0 },
      style: { width: "100%" },
    },
  },
  // Catégorie "icons" séparée des autres (Médias, etc.) — voir iconKit/.
  // content.iconId (registre lucide-react/react-icons, vrai SVG) est le mode
  // préféré ; options.glyph (caractère libre) reste le mode de secours pour
  // 100% de compatibilité avec les documents déjà sauvegardés — aucun des
  // deux ne casse l'autre, voir IconBlock plus bas.
  icon: {
    get label() { return elakayAutoText("h_f58b269782f29e", "Icône"); }, icon: Star, tag: "div", isIcon: true, group: "icons",
    defaults: {
      content: { iconId: null },
      options: { glyph: "★", size: "48px", color: "#6d28d9" },
      style: { display: "flex", alignItems: "center", justifyContent: "center", padding: "8px" },
    },
  },
  divider: {
    get label() { return elakayAutoText("h_b9c2ee8d81ed6c", "Séparateur"); }, icon: Minus, tag: "hr", isDivider: true, group: "media",
    defaults: {
      style: { borderLeft: "none", borderRight: "none", borderBottom: "none", borderTop: "1px solid #e4e0f0", margin: "24px 0", width: "100%" },
    },
  },
  quote: {
    get label() { return elakayAutoText("h_6eb49155521358", "Citation"); }, icon: Quote, tag: "blockquote", isQuote: true, group: "media",
    defaults: {
      content: { get text() { return elakayAutoText("h_5bb357532eb4fd", "Une citation marquante qui attire l'attention du lecteur."); }, author: "— Auteur" },
      style: { margin: "0", padding: "20px 28px", borderLeft: "4px solid #6d28d9", background: "#faf9fd", borderRadius: "0 12px 12px 0" },
    },
  },

  // ===== BLOCS DE DONNÉES (InvestmentPage, StorePage, LandingPage) =====
  stat: {
    get label() { return elakayAutoText("h_ad2bc543bcec51", "Statistique"); }, icon: BarChart3, tag: "div", isStat: true, group: "data",
    defaults: {
      content: { value: "1,2 M€", get label() { return elakayAutoText("h_90cfed763528e3", "Levée réalisée"); }, valueColor: "#6d28d9", labelColor: "#7a73a0" },
      style: { padding: "24px", background: "#fff", borderRadius: "14px", border: "1px solid #ece9f5", textAlign: "center" },
    },
  },
  progress: {
    get label() { return elakayAutoText("h_685e262241d88a", "Progression"); }, icon: TrendingUp, tag: "div", isProgress: true, group: "data",
    defaults: {
      content: { get label() { return elakayAutoText("h_cbd675cb89fefc", "Objectif de financement"); }, percent: 68, barColor: "#6d28d9", trackColor: "#ece9f5" },
      style: { padding: "8px 0", width: "100%" },
    },
  },
  rating: {
    get label() { return elakayAutoText("h_73f5e1daadfdf4", "Note (étoiles)"); }, icon: Star, tag: "div", isRating: true, group: "data",
    defaults: {
      content: { value: 4, max: 5, color: "#f59e0b", label: "" },
      style: { display: "flex", alignItems: "center", gap: "6px", padding: "4px 0" },
    },
  },
  accordion: {
    get label() { return elakayAutoText("h_f5fff8bd7e7b14", "Accordéon / FAQ"); }, icon: ListChecks, tag: "div", hasItems: true, isAccordion: true, group: "data",
    defaults: {
      style: { display: "flex", flexDirection: "column", gap: "10px" },
      items: [
        { id: uid(), q: "Première question fréquente ?", a: "Réponse claire à la première question." },
        { id: uid(), q: "Deuxième question ?", a: "Réponse à la deuxième question." },
        { id: uid(), q: "Troisième question ?", a: "Réponse à la troisième question." },
      ],
    },
  },

  // ===== HTML : balises de formulaire complètes (groupe "html") =====
  form: {
    get label() { return elakayAutoText("h_80446347ede53c", "Form"); }, icon: FileCode, tag: "form", isContainer: true, isHtmlForm: true, group: "html",
    defaults: {
      attrs: { action: "", method: "post", name: "" },
      style: { display: "flex", flexDirection: "column", gap: "14px", padding: "20px", border: "1px solid #ece9f5", borderRadius: "14px", background: "#fff" },
      children: [],
    },
  },
  fieldset: {
    get label() { return elakayAutoText("h_b342b6b4029373", "Fieldset"); }, icon: SquareStack, tag: "fieldset", isContainer: true, isFieldset: true, group: "html",
    defaults: {
      legend: "Groupe de champs",
      style: { padding: "16px", border: "1px solid #d9d3ec", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "12px" },
      children: [],
    },
  },
  radio: {
    get label() { return elakayAutoText("h_b11bf19e3f65b4", "Radio"); }, icon: Circle, tag: "label", isRadio: true, group: "html",
    defaults: {
      field: { get label() { return elakayAutoText("h_fb6266e4c4da9f", "Option radio"); }, name: "groupe1", value: "option1", checked: false },
      style: { display: "flex", alignItems: "center", gap: "10px", fontSize: "15px", color: "#4a4a68", cursor: "pointer" },
    },
  },
  toggle: {
    get label() { return elakayAutoText("h_54c9be223abcb2", "Interrupteur"); }, icon: ToggleLeft, tag: "label", isCheckbox: true, group: "html",
    defaults: {
      field: { get label() { return elakayAutoText("h_1a3f73c64286a2", "Activer l'option"); }, checked: true },
      style: { display: "flex", alignItems: "center", gap: "10px", fontSize: "15px", color: "#4a4a68", cursor: "pointer" },
    },
  },
  output: {
    get label() { return elakayAutoText("h_4bed336194a9a5", "Output"); }, icon: FileInput, tag: "output", isOutput: true, group: "html",
    defaults: {
      content: { get text() { return elakayAutoText("h_7186e8ffac7856", "Résultat"); } },
      style: { display: "inline-block", padding: "8px 14px", fontSize: "15px", fontWeight: 600, color: "#6d28d9", background: "#f3effe", borderRadius: "8px" },
    },
  },
  datalist: {
    get label() { return elakayAutoText("h_03465511e590e5", "Champ + suggestions"); }, icon: ListChecks, tag: "input", isField: true, hasDatalist: true, group: "html",
    defaults: {
      field: { get label() { return elakayAutoText("h_f1c3b1927c7e41", "Avec suggestions"); }, get placeholder() { return elakayAutoText("h_ad50d4edcdbb2b", "Tapez ou choisissez…"); }, inputType: "text" },
      options: { choices: ["Suggestion 1", "Suggestion 2", "Suggestion 3"] },
      style: { display: "block", width: "100%", padding: "11px 14px", fontSize: "15px", border: "1px solid #d9d3ec", borderRadius: "10px", color: "#2a2547", background: "#fff" },
    },
  },

  // Modal : conteneur affiché par-dessus la page, ouvert par son bouton intégré ou par un lien
  modal: {
    get label() { return elakayAutoText("h_31324d3291ab26", "Modal / Popup"); }, icon: Maximize2, tag: "div", isContainer: true, isModal: true, group: "rich",
    defaults: {
      triggerText: "Ouvrir la fenêtre",
      get title() { return elakayAutoText("h_00f032234c2509", "Titre de la fenêtre"); },
      triggerStyle: { display: "inline-block", padding: "12px 28px", fontSize: "15px", fontWeight: "600", color: "#fff", background: "#6d28d9", border: "none", borderRadius: "10px", cursor: "pointer" },
      style: { maxWidth: "480px", width: "90%", padding: "28px", background: "#fff", borderRadius: "18px", display: "flex", flexDirection: "column", gap: "14px", boxShadow: "0 24px 80px rgba(0,0,0,0.3)" },
      children: [
        { id: uid(), type: "text", get text() { return elakayAutoText("h_6adc48ffa5666b", "Contenu de la fenêtre modale. Ajoutez ici n'importe quel élément."); }, tag: "p", style: { fontSize: "16px", color: "#4a4a68", lineHeight: "1.6", margin: 0 } },
      ],
    },
  },
  // Menu déroulant : un bouton qui ouvre une liste de liens
  dropdown: {
    get label() { return elakayAutoText("h_b9244040062735", "Menu déroulant"); }, icon: ChevronsDownUp, tag: "div", isDropdown: true, group: "rich",
    defaults: {
      triggerText: "Menu ▾",
      triggerStyle: { display: "inline-block", padding: "10px 22px", fontSize: "15px", fontWeight: 600, color: "#fff", background: "#6d28d9", border: "none", borderRadius: "10px", cursor: "pointer" },
      style: { minWidth: "180px", background: "#fff", borderRadius: "10px", boxShadow: "0 8px 30px rgba(60,30,110,0.15)", border: "1px solid #ece9f5", overflow: "hidden", padding: "6px 0" },
      items: [
        { id: uid(), get label() { return elakayAutoText("h_ceacc7b285016e", "Première action"); }, href: "#" },
        { id: uid(), get label() { return elakayAutoText("h_83d0229af66557", "Deuxième action"); }, href: "#" },
        { id: uid(), get label() { return elakayAutoText("h_706ec2bc36b6a0", "Troisième action"); }, href: "#" },
      ],
    },
  },
  // Onglets : barre d'onglets + panneaux de contenu
  tabs: {
    get label() { return elakayAutoText("h_1bb600afcd0491", "Onglets (Tabs)"); }, icon: FolderTree, tag: "div", isTabs: true, group: "rich",
    defaults: {
      style: { border: "1px solid #ece9f5", borderRadius: "12px", overflow: "hidden" },
      items: [
        { id: uid(), get label() { return elakayAutoText("h_4729153db6930f", "Onglet 1"); }, get text() { return elakayAutoText("h_8e7b135c2e7bc8", "Contenu du premier onglet."); } },
        { id: uid(), get label() { return elakayAutoText("h_6d78edf7f7444c", "Onglet 2"); }, get text() { return elakayAutoText("h_f26d6e6fe9920e", "Contenu du deuxième onglet."); } },
        { id: uid(), get label() { return elakayAutoText("h_00741e5ceefc5d", "Onglet 3"); }, get text() { return elakayAutoText("h_39681050c2a276", "Contenu du troisième onglet."); } },
      ],
    },
  },
  // Offcanvas : panneau latéral coulissant
  offcanvas: {
    get label() { return elakayAutoText("h_6222df462f7a27", "Panneau latéral (Offcanvas)"); }, icon: PanelRightOpen, tag: "div", isOffcanvas: true, group: "rich",
    defaults: {
      triggerText: "Ouvrir le panneau",
      get title() { return elakayAutoText("h_17f7d2e97463bf", "Panneau"); },
      side: "right",
      triggerStyle: { display: "inline-block", padding: "10px 22px", fontSize: "15px", fontWeight: 600, color: "#fff", background: "#6d28d9", border: "none", borderRadius: "10px", cursor: "pointer" },
      style: { width: "320px", height: "100%", background: "#fff", padding: "24px", display: "flex", flexDirection: "column", gap: "14px", boxShadow: "0 0 40px rgba(0,0,0,0.2)" },
      children: [
        { id: uid(), type: "text", get text() { return elakayAutoText("h_a7e8c5367ed204", "Contenu du panneau latéral. Ajoutez-y n'importe quel élément."); }, tag: "p", style: { fontSize: "15px", color: "#4a4a68", lineHeight: 1.6, margin: 0 } },
      ],
    },
  },
  // Tooltip / Popover : bulle d'info au survol
  tooltip: {
    get label() { return elakayAutoText("h_f5b7dfbfdf6d29", "Info-bulle (Tooltip)"); }, icon: MessageSquare, tag: "span", isTooltip: true, group: "rich",
    defaults: {
      get text() { return elakayAutoText("h_39a5b23ef1b3ba", "Survolez-moi"); },
      tip: "Voici l'info-bulle qui apparaît au survol.",
      style: { display: "inline-block", padding: "6px 12px", fontSize: "15px", fontWeight: 600, color: "#6d28d9", background: "#f3effe", borderRadius: "8px", cursor: "help", borderBottom: "2px dotted #6d28d9" },
    },
  },

  // ===== Visualiseur de documents (.doc, .docx, .pdf) =====
  docViewer: {
    get label() { return elakayAutoText("h_0533094a43b168", "Visualiseur de document"); }, icon: FileText, tag: "div", isDocViewer: true, group: "documents",
    defaults: {
      folderLabel: "Mes documents",
      apiPath: "/api/documents",
      autoName: true,
      customName: "",
      buttonText: "Charger un document",
      viewStyle: "card",
      defaultView: "html",
      allowHtml: true,
      allowOffice: true,
      allowPdf: true,
      allowWord: true,
      allowDownload: true,
      allowSign: true,
      requireSign: false,
      signMode: "both",
      fullPageMode: false,
      pageDisplayMode: "all",
      pagesToShow: 1,
      signatures: [],
      sourceLink: "",
      style: { display: "flex", flexDirection: "column", gap: "16px", padding: "24px", background: "#f8f7ff", border: "1px solid #e0daf5", borderRadius: "16px", maxWidth: "820px" },
    },
  },

  // ===== COMPOSANTS BOUTIQUE LIVE (shopLive) =====
  // Synchronisation automatique avec store.* quand la source est liée
  shopVote: {
    get label() { return elakayAutoText("h_4c12d0a3b1e65c", "Vote / Étoiles"); }, icon: Star, tag: "div", isShopVote: true, group: "shopLive",
    defaults: {
      style: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 0" },
      options: { starSize: "28px", clickable: true, maxStars: 5, defaultRating: 4 },
      bindSource: "store.products.0",
    },
  },
  shopCarousel: {
    get label() { return elakayAutoText("h_3b11eb795d1659", "Carrousel Produits"); }, icon: GalleryHorizontalEnd, tag: "div", isShopCarousel: true, group: "shopLive",
    defaults: {
      style: { width: "100%", overflow: "hidden" },
      options: {
        type_Animation: null, activeFollowBtn: false,
        wm0: 2, wm640: 2, wm768: 3, wm1024: 3, wm1200: 3,
        backgroundColor: "#DCDCDC",
        titleColor: "#1f2937", titleFontSize: "1.75rem", titleFontWeight: 700,
        titleFontFamily: "inherit", titleFontStyle: "normal",
        titleLetterSpacing: "-0.5px", titleLineHeight: 1.2,
        titleTextTransform: "none", titleTextAlign: "left",
        titleTextShadow: "0 2px 4px rgba(0,0,0,0.05)",
        headerBackgroundColor: "transparent", headerPadding: null, headerMarginBottom: null,
      },
      bindSource: "store.pubCategoryProdMatrix",
    },
  },
  shopPrice: {
    get label() { return elakayAutoText("h_568077469dc7c1", "Prix & Remise"); }, icon: Tag, tag: "div", isShopPrice: true, group: "shopLive",
    defaults: {
      style: { padding: "12px 0" },
      options: { page: 0, showAddToCartButton: true, tax: 0.1, tca: 0, shipping: 0.3, filtername: "" },
      bindSource: "store.products.0",
    },
  },
  shopCard: {
    get label() { return elakayAutoText("h_236da7e95890a7", "Carte Produit"); }, icon: CreditCard, tag: "div", isShopCard: true, group: "shopLive",
    defaults: {
      style: { width: "100%", fontFamily: "'DM Sans', system-ui, sans-serif" },
      options: { showAddToCartButton: true, showShopV: false, classCard0: "col p-0", productIndex: 0 },
      bindSource: "store.products.0",
    },
  },
  shopSlider: {
    get label() { return elakayAutoText("h_ecc7dc2d04eeed", "Hero Slider"); }, icon: GalleryHorizontalEnd, tag: "div", isShopSlider: true, group: "shopLive",
    defaults: {
      style: { width: "100%" },
      options: { SliderPunShow: false, shop: true },
      bindSource: "store.sliderShop",
    },
  },
  shopProductGrid: {
    get label() { return elakayAutoText("h_5797493d06691f", "Grille Produits"); }, icon: LayoutGrid, tag: "div", isShopProductGrid: true, group: "shopLive",
    defaults: {
      style: { width: "100%" },
      options: {
        limit: 12, prodCat: "Tous les Produits",
        gridAnimated: false, animateCards: false, resultAnimated: false,
        resultStyle: "gradient", gridStyle: "minimal",
        columns: { xs: 2, sm: 2, md: 3, lg: 4 },
      },
      bindSource: "store.products",
    },
  },
  shopCheckout: {
    get label() { return elakayAutoText("h_ac383ed071babe", "Checkout / Panier"); }, icon: Package, tag: "div", isShopCheckout: true, group: "shopLive",
    defaults: {
      style: { padding: "16px 0" },
      options: { get label() { return elakayAutoText("h_b6799d9a25e425", "Ajouter au panier"); }, labelBuy: "Acheter maintenant", quantity: 1, showBuyNow: true },
      bindSource: "store",
    },
  },
  shopHeaderSeller: {
    get label() { return elakayAutoText("h_b4f544b44c9e06", "Header Shop Seller"); }, icon: Monitor, tag: "div", isShopHeaderSeller: true, group: "shopHeader",
    defaults: {
      style: { width: "100%" },
      options: {
        myShop: false,
        outOffPage: true,
        showFilter: true,
        lakaySeller: false,
        backgroundColor: "Snow",
        shadowColor: "rgba(0, 0, 0, 0.1)",
        borderColor: "#ece9f5",
        shopNameColor: "#111111",
        shopNameFontFamily: "inherit",
        shopNameFontSize: "24px",
        shopNameFontWeight: 700,
        menuColor: "#000000",
        menuFontFamily: "inherit",
        menuFontSize: "16px",
        menuFontWeight: 600,
        searchBackgroundColor: "#ffffff",
        searchTextColor: "#000000",
        searchPlaceholderColor: "#9ca3af",
        searchBorderColor: "#cccccc",
        searchFontFamily: "inherit",
        searchFontSize: "16px",
        modeBarBackground: "rgba(255,255,255,0.95)",
        modeBarBorderColor: "#ece9f5",
        modeActiveColor: "#6d28d9",
        modeInactiveColor: "#000000",
        modeActiveBackground: "#f5f3ff",
        modeActiveBorderColor: "#c4b5fd",
        editorChipBackground: "linear-gradient(135deg,#6d28d9,#8b5cf6)",
        editorChipColor: "#ffffff",
      },
      bindSource: "store.owner",
    },
  },
  shopCatMatrix: {
    get label() { return elakayAutoText("h_d45410db6b5705", "Catégories Matrix"); }, icon: LayoutGrid, tag: "div", isShopCatMatrix: true, group: "shopLive",
    defaults: {
      style: { width: "100%" },
      options: {
        get title() { return elakayAutoText("h_f2ef9fd31c91ee", "Nos Catégories"); },
        inView: true,
        nbrArr: 4,
        type_Animation: null,
        wm0: 1.3, wm640: 2, wm768: 3, wm1024: 3, wm1200: 3,
        textFoorter: false,
        titleColor: "#1f2937",
        titleFontSize: "1.75rem",
        titleFontWeight: 700,
        headerBackgroundColor: "transparent",
      },
      bindSource: "store.categories",
    },
  },
  shopPubMatrix: {
    get label() { return elakayAutoText("h_99254079eec200", "Pub Matrix"); }, icon: GalleryHorizontalEnd, tag: "div", isShopPubMatrix: true, group: "shopLive",
    defaults: {
      style: { width: "100%", overflow: "hidden" },
      options: { type_Animation: null, wm0: 2, wm640: 2, wm768: 3, wm1200: 3, activeFollowBtn: false },
      bindSource: "store.shopPubMatrix",
    },
  },
  shopButtonPagination: {
    get label() { return elakayAutoText("h_100325b8d6de92", "Pagination"); }, icon: ChevronsDownUp, tag: "div", isShopButtonPagination: true, group: "shopLive",
    defaults: {
      style: { width: "100%", padding: "8px 0" },
      options: {
        buttonClassL:  "btn btn-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-left",
        buttonClassLD: "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-left",
        buttonClassR:  "btn btn-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-right",
        buttonClassRD: "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-right",
      },
      bindSource: "store.products",
    },
  },
  showImage: {
    get label() { return elakayAutoText("h_1988b016dbaa43", "ShowImage"); }, icon: ImageIcon, tag: "div", isShowImage: true, group: "shopLive",
    defaults: {
      style: { width: "100%", position: "relative", overflow: "hidden", borderRadius: "8px" },
      options: {
        showB: true,
        showShopV: true,
        showButtonUser: false,
        edit: false,
        liveProductIndex: 0,
        liveArraySource: "store.products",
      },
      bindSource: "store.products.0",
    },
  },
};

// ---------------------------------------------------------------------------
// Schéma de configuration live par type d'élément boutique
// ---------------------------------------------------------------------------
const ALL_STORE_KEYS = [
  { value: "store",                                 get label() { return elakayAutoText("h_772d0ee5f9d2a9", "store (complet)"); } },
  { value: "store.products",                        get label() { return elakayAutoText("h_b09a90ed9bb471", "store.products (tableau paginé)"); } },
  { value: "store.products.0",                      get label() { return elakayAutoText("h_ae8ce69f79ff3c", "store.products[0] (1er produit)"); } },
  { value: "store.products.0._id",                  get label() { return elakayAutoText("h_0684e4c4cf44f0", "store.products[0]._id"); } },
  { value: "store.products.0.name",                 get label() { return elakayAutoText("h_cc78270216af96", "store.products[0].name"); } },
  { value: "store.products.0.price",                get label() { return elakayAutoText("h_a31af66f6d3d2c", "store.products[0].price"); } },
  { value: "store.products.0.salePrice",            get label() { return elakayAutoText("h_61ca6def51ced9", "store.products[0].salePrice"); } },
  { value: "store.products.0.Stars",                get label() { return elakayAutoText("h_17090c03db6ad5", "store.products[0].Stars"); } },
  { value: "store.products.0.ProductImageUrls.0", get label() { return elakayAutoText("h_79ce3503a49cb6", "store.products[0].image URL"); } },
  { value: "store.categories",                      get label() { return elakayAutoText("h_7350f7cb437dc8", "store.categories"); } },
  { value: "store.categories.categories",           get label() { return elakayAutoText("h_34a31a5307ca7c", "store.categories.categories (tableau)"); } },
  { value: "store.owner",                           get label() { return elakayAutoText("h_3d4d93c4144a2b", "store.owner"); } },
  { value: "store.owner.shopName.0",                get label() { return elakayAutoText("h_6ad9bff3b0fba7", "store.owner.shopName[0]"); } },
  { value: "store.sliderShop",                      get label() { return elakayAutoText("h_c6455c026d993c", "store.sliderShop (slides hero)"); } },
  { value: "store.pubCategoryProdMatrix",           get label() { return elakayAutoText("h_48ff79d1468849", "store.pubCategoryProdMatrix"); } },
  { value: "store.shopPubMatrix",                   get label() { return elakayAutoText("h_b817e2e0fbb17e", "store.shopPubMatrix"); } },
  { value: "store.filters",                         get label() { return elakayAutoText("h_106cca667031bc", "store.filters"); } },
  { value: "store.siseProduct",                     get label() { return elakayAutoText("h_633d36d4257284", "store.siseProduct (total)"); } },
  { value: "store.siseProductAll",                  get label() { return elakayAutoText("h_70a949d6224c75", "store.siseProductAll (total global)"); } },
  { value: "store.skip",                            get label() { return elakayAutoText("h_413d82012dd695", "store.skip (position)"); } },
  { value: "store.limit",                           get label() { return elakayAutoText("h_820d7f6af18926", "store.limit (par page)"); } },
  { value: "store.links.shopPage",                  get label() { return elakayAutoText("h_e719c6a75745fc", "store.links.shopPage (URL)"); } },
  { value: "store.links.productDetail",             get label() { return elakayAutoText("h_2ad4c3a22f5db4", "store.links.productDetail (URL)"); } },
];

const SHOP_LIVE_SCHEMA = {
  shopVote: {
    sources: ["store.products.0", "store.products", "store"],
    params: [
      { key: "productId",  get label() { return elakayAutoText("h_c259a717e3c148", "ID produit"); },     default: "store.products.0._id" },
      { key: "rating",     get label() { return elakayAutoText("h_c3b6a5782415da", "Note / Stars"); },   default: "store.products.0.Stars" },
    ],
    fns: ["addToCart"],
  },
  shopCarousel: {
    sources: ["store.pubCategoryProdMatrix", "store.shopPubMatrix", "store.products"],
    params: [
      { key: "items",      get label() { return elakayAutoText("h_9d55d39f382698", "Produits vedettes"); }, default: "store.pubCategoryProdMatrix" },
    ],
    fns: ["loadProductsByArrival_"],
  },
  shopPrice: {
    sources: ["store.products.0", "store"],
    params: [
      { key: "product",             get label() { return elakayAutoText("h_5b2f949ed94874", "Produit (objet complet)"); },           default: "store.products.0" },
      { key: "page",                get label() { return elakayAutoText("h_511fddd756b193", "Mode (0 = produit, 1 = panier)"); },    default: 0,     type: "option", inputType: "number", step: 1,    min: 0, max: 1 },
      { key: "showAddToCartButton", get label() { return elakayAutoText("h_7b29287d6a7187", "Afficher bouton panier"); },            default: true,  type: "option", inputType: "boolean" },
      { key: "tax",                 get label() { return elakayAutoText("h_15771af05506c3", "Taxe (ex: 0.1 = 10%)"); },             default: 0.1,   type: "option", inputType: "number", step: 0.01 },
      { key: "tca",                 get label() { return elakayAutoText("h_b8a43d02c8c082", "Taxe complémentaire (ex: 0.05)"); },   default: 0,     type: "option", inputType: "number", step: 0.01 },
      { key: "shipping",            get label() { return elakayAutoText("h_b5e71d29727ea3", "Livraison (ex: 0.3 = 30%)"); },        default: 0.3,   type: "option", inputType: "number", step: 0.01 },
      { key: "filtername",          get label() { return elakayAutoText("h_e65fca79540421", "Filtre catégorie (addToCart)"); },      default: "",    type: "option", inputType: "text" },
      { key: "productId",           get label() { return elakayAutoText("h_c259a717e3c148", "ID produit"); },                        default: "store.products.0._id" },
    ],
    fns: ["addToCart"],
  },
  shopCard: {
    sources: ["store.products.0", "store.products", "store"],
    arraySource: "store.products",
    params: [
      { key: "product",        get label() { return elakayAutoText("h_830cd398c317b9", "product (objet complet)"); },  default: "store.products.0" },
      { key: "productId",      get label() { return elakayAutoText("h_534c6b0888f217", "productId (_id)"); },          default: "store.products.0._id" },
      { key: "urlImg",         get label() { return elakayAutoText("h_7a439e9ed9ff49", "urlImg (image principale)"); },default: "store.products.0.ProductImageUrls.0" },
      { key: "rating",         get label() { return elakayAutoText("h_0c0a88ebd7e9a3", "rating (Stars)"); },           default: "store.products.0.Stars" },
      { key: "nbrUserVote",    get label() { return elakayAutoText("h_48138aeecfc801", "nbrUserVote (nb avis)"); },    default: "store.products.0.count_Comments" },
      { key: "filterProduct",  get label() { return elakayAutoText("h_5f12b9a159fb6c", "filterProduct (filtres)"); },  default: "store.products.0.filterProduct" },
      { key: "filterColor",    get label() { return elakayAutoText("h_5b4220a1b27dd9", "filterColor (couleur)"); },    default: "store.products.0.filterProduct.0.filter.color" },
      { key: "categoryId",     get label() { return elakayAutoText("h_e0589089ebff23", "categoryId (catégorie)"); },   default: "store.products.0.categoriesID.0.catID" },
      { key: "owner",          get label() { return elakayAutoText("h_4cb7ebc8e0e5fe", "owner (vendeur)"); },          default: "store.owner" },
    ],
    fns: ["addToCart", "loadProductsByArrival_"],
  },
  shopSlider: {
    sources: ["store.sliderShop", "store"],
    params: [
      { key: "slides",     get label() { return elakayAutoText("h_1e35470a39e5b3", "Slides"); },         default: "store.sliderShop" },
      { key: "shopName",   get label() { return elakayAutoText("h_542532607b68b7", "Nom boutique"); },   default: "store.owner.shopName.0" },
    ],
    fns: [],
  },
  shopProductGrid: {
    sources: ["store.products", "store"],
    params: [
      { key: "products",   get label() { return elakayAutoText("h_48000473cdad77", "Produits"); },       default: "store.products" },
      { key: "categories", get label() { return elakayAutoText("h_d463d7d2a3db7c", "Catégories"); },     default: "store.categories.categories" },
      { key: "filters",    get label() { return elakayAutoText("h_2a8e76e0d96e7c", "Filtres"); },        default: "store.filters" },
      { key: "siseProduct",get label() { return elakayAutoText("h_e59ab731a16e19", "Total produits"); }, default: "store.siseProduct" },
    ],
    fns: ["loadProductsByArrival", "loadProductsByArrival_", "loadMore", "loadMore0", "loadindSearCh"],
  },
  shopCheckout: {
    sources: ["store", "store.products.0"],
    params: [
      { key: "store",      get label() { return elakayAutoText("h_0e0e6242256dd7", "Store (complet)"); }, default: "store" },
      { key: "productId",  get label() { return elakayAutoText("h_c259a717e3c148", "ID produit"); },      default: "store.products.0._id" },
    ],
    fns: ["addToCart"],
  },
  shopHeaderSeller: {
    sources: ["store.owner", "store"],
    params: [
      { key: "owner",      get label() { return elakayAutoText("h_bdb450bf5d7ddf", "Propriétaire"); },   default: "store.owner" },
      { key: "shopName",   get label() { return elakayAutoText("h_542532607b68b7", "Nom boutique"); },   default: "store.owner.shopName.0" },
    ],
    fns: ["loadindSearCh", "onFilterShop", "setFilterShop", "setSearch", "loadProductsByArrival"],
  },
  shopCatMatrix: {
    sources: ["store.categories", "store"],
    params: [
      { key: "categories", get label() { return elakayAutoText("h_d463d7d2a3db7c", "Catégories"); },     default: "store.categories.categories" },
      { key: "owner",      get label() { return elakayAutoText("h_512fe5148f2234", "Owner ID"); },       default: "store.categories.owner" },
    ],
    fns: ["loadProductsByArrival_"],
  },
  shopPubMatrix: {
    sources: ["store.shopPubMatrix", "store.pubCategoryProdMatrix"],
    params: [
      { key: "items",      get label() { return elakayAutoText("h_9d55d39f382698", "Produits vedettes"); }, default: "store.shopPubMatrix" },
    ],
    fns: ["loadProductsByArrival_"],
  },
  shopButtonPagination: {
    sources: ["store.products", "store"],
    params: [
      { key: "skip",       get label() { return elakayAutoText("h_6870b4716faa88", "Skip (position)"); }, default: "store.skip" },
      { key: "limit",      get label() { return elakayAutoText("h_602ade8a8e69ac", "Limit (par page)"); }, default: "store.limit" },
      { key: "siseProduct",get label() { return elakayAutoText("h_e59ab731a16e19", "Total produits"); },  default: "store.siseProduct" },
    ],
    fns: ["loadMore", "loadMore0"],
  },
  showImage: {
    sources: ["store.products.0", "store.products", "store"],
    arraySource: "store.products",
    params: [
      { key: "item",           get label() { return elakayAutoText("h_d729c867fa3062", "item (objet produit)"); },     default: "store.products.0" },
      { key: "productId",      get label() { return elakayAutoText("h_534c6b0888f217", "productId (_id)"); },          default: "store.products.0._id" },
      { key: "urlImg",         get label() { return elakayAutoText("h_7a439e9ed9ff49", "urlImg (image principale)"); },default: "store.products.0.ProductImageUrls.0" },
      { key: "rating",         get label() { return elakayAutoText("h_0c0a88ebd7e9a3", "rating (Stars)"); },           default: "store.products.0.Stars" },
      { key: "nbrUserVote",    get label() { return elakayAutoText("h_48138aeecfc801", "nbrUserVote (nb avis)"); },    default: "store.products.0.count_Comments" },
      { key: "filterProduct",  get label() { return elakayAutoText("h_5f12b9a159fb6c", "filterProduct (filtres)"); },  default: "store.products.0.filterProduct" },
      { key: "filterColor",    get label() { return elakayAutoText("h_5b4220a1b27dd9", "filterColor (couleur)"); },    default: "store.products.0.filterProduct.0.filter.color" },
      { key: "categoryId",     get label() { return elakayAutoText("h_e0589089ebff23", "categoryId (catégorie)"); },   default: "store.products.0.categoriesID.0.catID" },
      { key: "owner_id",       get label() { return elakayAutoText("h_354055ae7257fb", "owner_id (vendeur _id)"); },   default: "store.owner._id" },
    ],
    fns: [],
  },
};

// --- Composants custom React : assemblages préconfigurés insérés en un clic ---
const CUSTOM_COMPONENTS = {
  hero: {
    get label() { return elakayAutoText("h_cd857fcf61ab28", "Bloc Héros"); }, icon: Megaphone,
    build: () => ({
      id: uid(), type: "container",
      style: { padding: "56px 40px", background: "linear-gradient(135deg,#6d28d9,#a855f7)", borderRadius: "20px", textAlign: "center" },
      children: [
        { id: uid(), type: "heading", tag: "h1", text: elakayAutoText("h_d9507ed7783594", "Votre titre accrocheur"), style: { fontSize: "42px", fontWeight: "800", color: "#ffffff", margin: "0 0 14px 0", lineHeight: "1.1" } },
        { id: uid(), type: "text", tag: "p", text: elakayAutoText("h_4fa422508386b0", "Une phrase d'accroche qui présente votre produit ou service en quelques mots."), style: { fontSize: "18px", color: "#f0e9ff", lineHeight: "1.6", margin: "0 0 28px 0" } },
        { id: uid(), type: "button", text: elakayAutoText("h_173e1575e2fad4", "Démarrer maintenant"), href: "#", style: { display: "inline-block", padding: "15px 36px", fontSize: "16px", fontWeight: "700", color: "#6d28d9", background: "#ffffff", border: "none", borderRadius: "12px", cursor: "pointer", textDecoration: "none" } },
      ],
    }),
  },
  productCard: {
    get label() { return elakayAutoText("h_92ca57a87710b1", "Carte produit"); }, icon: Package,
    build: () => ({
      id: uid(), type: "container",
      style: { padding: "0", background: "#ffffff", borderRadius: "16px", border: "1px solid #ece9f5", overflow: "hidden", maxWidth: "300px" },
      children: [
        { id: uid(), type: "image", src: "https://placehold.co/300x220/6d28d9/ffffff?text=Produit", alt: elakayAutoText("h_ff673dc28e8991", "Produit"), style: { width: "100%", height: "auto", display: "block" } },
        { id: uid(), type: "container", style: { padding: "18px" }, children: [
          { id: uid(), type: "heading", tag: "h3", text: elakayAutoText("h_99bc1441961b7a", "Nom du produit"), style: { fontSize: "19px", fontWeight: "700", color: "#1a1a2e", margin: "0 0 6px 0" } },
          { id: uid(), type: "text", tag: "p", text: elakayAutoText("h_bd94658eba4654", "Courte description du produit et de ses avantages."), style: { fontSize: "14px", color: "#4a4a68", lineHeight: "1.6", margin: "0 0 14px 0" } },
          { id: uid(), type: "button", text: elakayAutoText("h_f7b474bc621809", "Acheter — 29 €"), href: "#", style: { display: "inline-block", padding: "10px 22px", fontSize: "14px", fontWeight: "600", color: "#fff", background: "#6d28d9", border: "none", borderRadius: "9px", cursor: "pointer", textDecoration: "none" } },
        ] },
      ],
    }),
  },
  testimonial: {
    get label() { return elakayAutoText("h_077e24abf15fe5", "Témoignage"); }, icon: Quote,
    build: () => ({
      id: uid(), type: "container",
      style: { padding: "32px", background: "#faf9fd", borderRadius: "16px", borderLeft: "4px solid #6d28d9" },
      children: [
        { id: uid(), type: "text", tag: "p", text: elakayAutoText("h_184462da83361c", "« Ce produit a transformé notre façon de travailler. Je le recommande sans hésiter. »"), style: { fontSize: "19px", fontStyle: "italic", color: "#2a2547", lineHeight: "1.6", margin: "0 0 16px 0" } },
        { id: uid(), type: "text", tag: "p", text: elakayAutoText("h_8fdfe2d64c6e5b", "— Marie D., Directrice Marketing"), style: { fontSize: "14px", fontWeight: "700", color: "#6d28d9", margin: "0" } },
      ],
    }),
  },
  pricing: {
    get label() { return elakayAutoText("h_074ea4f5fa53b9", "Section Tarifs"); }, icon: Tag,
    build: () => {
      const plan = (name, price, feats, highlight) => ({
        id: uid(), type: "container",
        style: { padding: "28px 24px", background: highlight ? "linear-gradient(160deg,#6d28d9,#a855f7)" : "#ffffff", borderRadius: "16px", border: highlight ? "none" : "1px solid #ece9f5", flex: "1", minWidth: "200px" },
        children: [
          { id: uid(), type: "heading", tag: "h3", text: name, style: { fontSize: "20px", fontWeight: "700", color: highlight ? "#fff" : "#1a1a2e", margin: "0 0 8px 0" } },
          { id: uid(), type: "heading", tag: "h2", text: price, style: { fontSize: "38px", fontWeight: "800", color: highlight ? "#fff" : "#6d28d9", margin: "0 0 16px 0" } },
          { id: uid(), type: "textGroup", style: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" },
            items: feats.map((f) => ({ id: uid(), tag: "p", text: "✓ " + f, fontSize: "14px", fontWeight: "400", color: highlight ? "#f0e9ff" : "#4a4a68", letterSpacing: "normal" })) },
          { id: uid(), type: "button", text: elakayAutoText("h_b030d590c21c95", "Choisir"), href: "#", style: { display: "inline-block", padding: "11px 24px", fontSize: "14px", fontWeight: "600", color: highlight ? "#6d28d9" : "#fff", background: highlight ? "#fff" : "#6d28d9", border: "none", borderRadius: "10px", textDecoration: "none", cursor: "pointer" } },
        ],
      });
      return {
        id: uid(), type: "row",
        style: { display: "flex", gap: "20px", flexWrap: "wrap" },
        children: [
          plan("Starter", "9 €", ["1 utilisateur", "Support email", "1 Go stockage"], false),
          plan("Pro", "29 €", ["5 utilisateurs", "Support prioritaire", "50 Go stockage", "Statistiques"], true),
          plan("Entreprise", "99 €", ["Illimité", "Support dédié", "Stockage illimité", "API"], false),
        ],
      };
    },
  },
  faqSection: {
    get label() { return elakayAutoText("h_150b0a4ab55e27", "Section FAQ"); }, icon: ListChecks,
    build: () => ({
      id: uid(), type: "container",
      style: { padding: "8px 0" },
      children: [
        { id: uid(), type: "heading", tag: "h2", text: elakayAutoText("h_16664684bf730f", "Questions fréquentes"), style: { fontSize: "30px", fontWeight: "800", color: "#1a1a2e", margin: "0 0 20px 0", textAlign: "center" } },
        { id: uid(), type: "accordion", style: { display: "flex", flexDirection: "column", gap: "10px" },
          items: [
            { id: uid(), q: "Comment fonctionne l'essai gratuit ?", a: "Vous avez 14 jours pour tester toutes les fonctionnalités, sans carte bancaire." },
            { id: uid(), q: "Puis-je changer de formule ?", a: "Oui, à tout moment depuis votre espace, sans frais." },
            { id: uid(), q: "Mes données sont-elles sécurisées ?", a: "Toutes les données sont chiffrées et hébergées en Europe." },
          ] },
      ],
    }),
  },

  // ===== Composants inspirés de Bootstrap (assemblages éditables) =====
  alert: {
    get label() { return elakayAutoText("h_ef005764dbc49c", "Alerte (Alert)"); }, icon: Megaphone,
    build: () => ({
      id: uid(), type: "container",
      style: { padding: "14px 18px", background: "#e7f1ff", border: "1px solid #b6d4fe", borderRadius: "10px", color: "#084298" },
      children: [
        { id: uid(), type: "text", tag: "p", text: elakayAutoText("h_9ab252d8a82724", "Ceci est une alerte d'information — modifiez son texte et ses couleurs."), style: { fontSize: "15px", margin: 0, lineHeight: 1.5 } },
      ],
    }),
  },
  navbar: {
    get label() { return elakayAutoText("h_c8b7ac5c379b4e", "Barre de nav (Navbar)"); }, icon: Boxes,
    build: () => ({
      id: uid(), type: "container",
      style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", background: "#1a1a2e", borderRadius: "12px" },
      children: [
        { id: uid(), type: "heading", tag: "span", text: elakayAutoText("h_46aeaa66e56e8c", "MonSite"), style: { fontSize: "20px", fontWeight: "800", color: "#fff", margin: 0 } },
        { id: uid(), type: "container", style: { display: "flex", gap: "22px", alignItems: "center" }, children: [
          { id: uid(), type: "link", text: elakayAutoText("h_a8ff8fd2078986", "Accueil"), target: "", style: { color: "#fff", textDecoration: "none", fontSize: "15px", fontWeight: 500 } },
          { id: uid(), type: "link", text: elakayAutoText("h_48000473cdad77", "Produits"), target: "", style: { color: "#cfc8e8", textDecoration: "none", fontSize: "15px", fontWeight: 500 } },
          { id: uid(), type: "link", text: elakayAutoText("h_b37456c4530be8", "Contact"), target: "", style: { color: "#cfc8e8", textDecoration: "none", fontSize: "15px", fontWeight: 500 } },
        ] },
      ],
    }),
  },
  breadcrumb: {
    get label() { return elakayAutoText("h_93b6b748a4b728", "Fil d'Ariane (Breadcrumb)"); }, icon: ChevronRight,
    build: () => ({
      id: uid(), type: "container",
      style: { display: "flex", alignItems: "center", gap: "8px", padding: "10px 0", fontSize: "14px" },
      children: [
        { id: uid(), type: "link", text: elakayAutoText("h_a8ff8fd2078986", "Accueil"), target: "", style: { color: "#6d28d9", textDecoration: "none" } },
        { id: uid(), type: "text", tag: "span", text: "/", style: { color: "#b3aacb", margin: 0 } },
        { id: uid(), type: "link", text: elakayAutoText("h_6b38300a25ce07", "Catégorie"), target: "", style: { color: "#6d28d9", textDecoration: "none" } },
        { id: uid(), type: "text", tag: "span", text: "/", style: { color: "#b3aacb", margin: 0 } },
        { id: uid(), type: "text", tag: "span", text: elakayAutoText("h_22976ea870e9a1", "Page actuelle"), style: { color: "#7a73a0", margin: 0 } },
      ],
    }),
  },
  buttonGroup: {
    get label() { return elakayAutoText("h_08ec1d7530532d", "Groupe de boutons"); }, icon: MousePointerClick,
    build: () => ({
      id: uid(), type: "container",
      style: { display: "inline-flex", borderRadius: "10px", overflow: "hidden", border: "1px solid #6d28d9" },
      children: [
        { id: uid(), type: "button", text: elakayAutoText("h_d8be759feff060", "Gauche"), href: "#", style: { padding: "10px 20px", fontSize: "14px", fontWeight: 600, color: "#fff", background: "#6d28d9", border: "none", cursor: "pointer", textDecoration: "none" } },
        { id: uid(), type: "button", text: elakayAutoText("h_1f9a47cf901ece", "Milieu"), href: "#", style: { padding: "10px 20px", fontSize: "14px", fontWeight: 600, color: "#6d28d9", background: "#fff", borderTop: "none", borderRight: "none", borderBottom: "none", borderLeft: "1px solid #6d28d9", cursor: "pointer", textDecoration: "none" } },
        { id: uid(), type: "button", text: elakayAutoText("h_c73d793d935888", "Droite"), href: "#", style: { padding: "10px 20px", fontSize: "14px", fontWeight: 600, color: "#6d28d9", background: "#fff", borderTop: "none", borderRight: "none", borderBottom: "none", borderLeft: "1px solid #6d28d9", cursor: "pointer", textDecoration: "none" } },
      ],
    }),
  },
  listGroup: {
    get label() { return elakayAutoText("h_6e67ed8dd01f4c", "Liste (List group)"); }, icon: ListChecks,
    build: () => ({
      id: uid(), type: "container",
      style: { display: "flex", flexDirection: "column", border: "1px solid #ece9f5", borderRadius: "12px", overflow: "hidden", maxWidth: "360px" },
      children: [
        { id: uid(), type: "text", tag: "div", text: elakayAutoText("h_a114f8789bcbb0", "Premier élément"), style: { padding: "12px 18px", fontSize: "15px", color: "#2a2547", borderBottom: "1px solid #f0edf8", margin: 0 } },
        { id: uid(), type: "text", tag: "div", text: elakayAutoText("h_466c9820ec02e8", "Deuxième élément"), style: { padding: "12px 18px", fontSize: "15px", color: "#2a2547", borderBottom: "1px solid #f0edf8", margin: 0 } },
        { id: uid(), type: "text", tag: "div", text: elakayAutoText("h_35e3a9a1c3f9b3", "Troisième élément"), style: { padding: "12px 18px", fontSize: "15px", color: "#2a2547", margin: 0 } },
      ],
    }),
  },
  pagination: {
    get label() { return elakayAutoText("h_100325b8d6de92", "Pagination"); }, icon: LayoutGrid,
    build: () => ({
      id: uid(), type: "container",
      style: { display: "inline-flex", gap: "6px", alignItems: "center" },
      children: [
        { id: uid(), type: "link", text: "‹", target: "", clickActionType: "function", clickActionFunction: "loadMore0", style: { padding: "8px 14px", border: "1px solid #ece9f5", borderRadius: "8px", color: "#6d28d9", textDecoration: "none", fontWeight: 600 } },
        { id: uid(), type: "link", text: "1", target: "", style: { padding: "8px 14px", border: "1px solid #6d28d9", borderRadius: "8px", color: "#fff", background: "#6d28d9", textDecoration: "none", fontWeight: 600 } },
        { id: uid(), type: "link", text: "2", target: "", style: { padding: "8px 14px", border: "1px solid #ece9f5", borderRadius: "8px", color: "#6d28d9", textDecoration: "none", fontWeight: 600 } },
        { id: uid(), type: "link", text: "3", target: "", style: { padding: "8px 14px", border: "1px solid #ece9f5", borderRadius: "8px", color: "#6d28d9", textDecoration: "none", fontWeight: 600 } },
        { id: uid(), type: "link", text: "›", target: "", clickActionType: "function", clickActionFunction: "loadMore", style: { padding: "8px 14px", border: "1px solid #ece9f5", borderRadius: "8px", color: "#6d28d9", textDecoration: "none", fontWeight: 600 } },
      ],
    }),
  },
  toast: {
    get label() { return elakayAutoText("h_5927dc4b0d5537", "Notification (Toast)"); }, icon: Megaphone,
    build: () => ({
      id: uid(), type: "container",
      style: { display: "flex", flexDirection: "column", maxWidth: "350px", background: "#fff", borderRadius: "12px", boxShadow: "0 8px 30px rgba(60,30,110,0.15)", overflow: "hidden", border: "1px solid #ece9f5" },
      children: [
        { id: uid(), type: "container", style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0edf8", background: "#faf9fd" }, children: [
          { id: uid(), type: "heading", tag: "strong", text: elakayAutoText("h_c18f8f255ab9c2", "Notification"), style: { fontSize: "14px", fontWeight: 700, color: "#1a1a2e", margin: 0 } },
          { id: uid(), type: "text", tag: "span", text: elakayAutoText("h_2427aa9cb7fa2e", "à l'instant"), style: { fontSize: "12px", color: "#9b93b8", margin: 0 } },
        ] },
        { id: uid(), type: "text", tag: "div", text: elakayAutoText("h_1d428a1539dce3", "Votre message de notification s'affiche ici."), style: { padding: "14px", fontSize: "14px", color: "#4a4a68", margin: 0 } },
      ],
    }),
  },
  spinner: {
    get label() { return elakayAutoText("h_32a0cb4614b95e", "Spinner (chargement)"); }, icon: Sparkles,
    build: () => ({
      id: uid(), type: "container",
      style: { width: "44px", height: "44px", border: "4px solid #ece9f5", borderTopColor: "#6d28d9", borderRadius: "50%", animation: "pb-spin 0.8s linear infinite" },
      children: [],
    }),
  },

  // ===== Navigation Boutique — éléments de navigation catégories =====
  catBar: {
    get label() { return elakayAutoText("h_2c91acf59907c4", "Barre Catégories"); }, icon: LayoutGrid,
    build: () => {
      // ID de groupe unique par instance — partagé entre "Tout" et les pills catégorie
      const gId = `cb-${uid()}`;
      // Styles communs actif / inactif
      const aStyle = { background: "#111827", backgroundColor: "#111827", borderRadius: "50px", border: "none" };
      const iStyle = { background: "#f9fafb", backgroundColor: "#f9fafb", borderRadius: "50px", border: "1px solid #e5e7eb" };
      // color/fontWeight sont transmis à l'enfant texte par PageRenderer.
      const aText  = { color: "#fff",     fontWeight: "700" };
      const iText  = { color: "#374151",  fontWeight: "600" };
      const tgAll  = { id: gId, isDefault: true,  activeStyle: { ...aStyle, ...aText }, inactiveStyle: { ...iStyle, ...iText } };
      const tgPill = { id: gId, isDefault: false, activeStyle: { ...aStyle, ...aText }, inactiveStyle: { ...iStyle, ...iText } };
      return {
        id: uid(), type: "section",
        style: { padding: "12px 32px", background: "#fff", borderBottom: "1px solid #f0f0f0" },
        children: [{
          id: uid(), type: "row",
          style: { display: "flex", alignItems: "center", gap: "8px", overflowX: "auto", flexWrap: "nowrap", scrollbarWidth: "none" },
          children: [
            // "Tout" — actif par défaut (isDefault: true)
            { id: uid(), type: "panel",
              clickActionType: "function", clickActionFunction: "loadProductsByArrival_",
              toggleGroup: tgAll,
              style: { padding: "7px 20px", background: "#111827", borderRadius: "50px", cursor: "pointer", flexShrink: 0, transition: "background 0.2s,border-color 0.2s" },
              children: [{ id: uid(), type: "text", text: elakayAutoText("h_6b0b09b8a698b7", "Tout"), tag: "p", style: { fontSize: "12px", fontWeight: 700, color: "#fff", margin: 0, whiteSpace: "nowrap" } }] },
            // Pill catégorie — répété via repeatBind, inactif par défaut
            { id: uid(), type: "panel",
              repeatBind: { source: "store", path: "categories.categories", mode: "children", autoFill: false, fieldMap: {} },
              toggleGroup: tgPill,
              style: { padding: "7px 20px", background: "#f9fafb", borderRadius: "50px", cursor: "pointer", border: "1px solid #e5e7eb", flexShrink: 0, transition: "background 0.2s,border-color 0.2s" },
              children: [{ id: uid(), type: "text", text: elakayAutoText("h_6b38300a25ce07", "Catégorie"), bindKey: "name.0", tag: "p", style: { fontSize: "12px", fontWeight: 600, color: "#374151", margin: 0, whiteSpace: "nowrap" } }] },
          ],
        }],
      };
    },
  },

  catSidebar: {
    get label() { return elakayAutoText("h_885ed73863554d", "Sidebar Catégories"); }, icon: PanelLeft,
    build: () => {
      const gId = `cs-${uid()}`;
      const aStyle = { background: "#111827", backgroundColor: "#111827", borderRadius: "6px", border: "none" };
      const iStyle = { background: "transparent", backgroundColor: "transparent", borderRadius: "6px", border: "1px solid #e5e7eb" };
      const aText  = { color: "#fff",    fontWeight: "700" };
      const iText  = { color: "#6b7280", fontWeight: "500" };
      const tgAll  = { id: gId, isDefault: true,  activeStyle: { ...aStyle, ...aText }, inactiveStyle: { ...iStyle, ...iText } };
      const tgItem = { id: gId, isDefault: false, activeStyle: { ...aStyle, ...aText }, inactiveStyle: { ...iStyle, ...iText } };
      return {
        id: uid(), type: "row",
        style: { width: "100%", display: "flex", alignItems: "stretch", gap: "20px", flexWrap: "nowrap" },
        mobileStyle: { flexDirection: "column", gap: "14px" },
        children: [
          {
            id: uid(), type: "stack", categorySidebar: true,
            style: { width: "220px", minWidth: "220px", flexShrink: 0, padding: "24px 14px", background: "#fff", border: "1px solid #ece9f5", borderRadius: "12px", display: "flex", flexDirection: "column", gap: "6px" },
            mobileStyle: { width: "100%", minWidth: 0, padding: "12px 14px", flexDirection: "row", alignItems: "center", gap: "8px", overflowX: "auto", overflowY: "hidden", flexWrap: "nowrap", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" },
            children: [
              { id: uid(), type: "text", text: elakayAutoText("h_1d89c18ff9c2d5", "CATÉGORIES"), tag: "p", style: { fontSize: "10px", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.2em", margin: "0 0 12px" }, mobileStyle: { display: "none" } },
              // "Tout voir" — actif par défaut
              { id: uid(), type: "panel",
                clickActionType: "function", clickActionFunction: "loadProductsByArrival_",
                toggleGroup: tgAll,
                style: { padding: "9px 14px", background: "#111827", borderRadius: "6px", cursor: "pointer", transition: "background 0.2s,border-color 0.2s" },
                mobileStyle: { padding: "8px 16px", borderRadius: "999px", flexShrink: 0 },
                children: [{ id: uid(), type: "text", text: elakayAutoText("h_9f442b7c154c80", "Tout voir"), tag: "p", style: { fontSize: "13px", fontWeight: 700, color: "#fff", margin: 0 }, mobileStyle: { fontSize: "12px", whiteSpace: "nowrap" } }] },
              // Item catégorie — répété via repeatBind, inactif par défaut
              { id: uid(), type: "panel",
                repeatBind: { source: "store", path: "categories.categories", mode: "children", autoFill: false, fieldMap: {} },
                toggleGroup: tgItem,
                style: { padding: "9px 14px", background: "transparent", borderRadius: "6px", cursor: "pointer", border: "1px solid #e5e7eb", transition: "background 0.2s,border-color 0.2s" },
                mobileStyle: { padding: "8px 16px", borderRadius: "999px", flexShrink: 0 },
                children: [{ id: uid(), type: "text", text: elakayAutoText("h_6b38300a25ce07", "Catégorie"), bindKey: "name.0", tag: "p", style: { fontSize: "13px", fontWeight: 500, color: "#6b7280", margin: 0 }, mobileStyle: { fontSize: "12px", whiteSpace: "nowrap" } }] },
            ],
          },
          {
            id: uid(), type: "container",
            style: { flex: "1 1 0", minWidth: "280px", minHeight: "360px", padding: "24px", background: "#faf9fd", border: "1px dashed #c4b5fd", borderRadius: "12px" },
            mobileStyle: { minWidth: 0, width: "100%" },
            children: [],
          },
        ],
      };
    },
  },

  // ===== Carte Produit style Case Study =====
  // Binding automatique depuis store.products (données réelles boutique) :
  //   image      → galeriesID.galeries.0.urlImg.0
  //   titre      → name
  //   stat 1     → price   (Prix)
  //   stat 2     → Stars   (Note / étoiles)
  //   stat 3     → condition (État : New / Used…)
  //   description→ description
  caseCard: {
    get label() { return elakayAutoText("h_93ded90b673480", "Carte Case Study"); }, icon: CreditCard,
    build: () => {
      // stat : cercle icône + valeur bold liée + label fixe
      const stat = (icon, valueKey, valueDefault, labelText) => ({
        id: uid(), type: "stack",
        style: { display:"flex", flexDirection:"column", gap:"6px", flex:1, minWidth:"80px" },
        children: [
          { id: uid(), type: "panel",
            style: { width:"46px", height:"46px", borderRadius:"50%", background:"#f1f3f9", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
            children: [{ id: uid(), type: "text", text: icon, tag:"span",
              style: { fontSize:"20px", lineHeight:1 } }] },
          { id: uid(), type: "heading", text: valueDefault, tag:"h4", bindKey: valueKey,
            style: { fontSize:"16px", fontWeight:800, color:"#111827", margin:0, lineHeight:1.2 } },
          { id: uid(), type: "text", text: labelText, tag:"p",
            style: { fontSize:"12px", color:"#6b7280", margin:0, lineHeight:1.4 } },
        ],
      });
      return {
        id: uid(), type: "panel",
        _caseCard: true,   // marker → active le panneau d'options PropertiesPanel
        options: {
          orientation:     "landscape",  // "landscape" | "portrait"
          imageWidth:      "38%",        // paysage uniquement
          imageHeight:     "220px",      // portrait uniquement
          showDescription: true,
          showPrice:       true,
          showStars:       true,
          showCondition:   true,
          stat1Label:      "Prix ($)",
          stat2Label:      "Note client",
          stat3Label:      "État",
          linkText:        "Voir le produit →",
          borderRadius:    "14px",
          imageKey:        "ProductImageUrls.0",
          titleKey:        "name",
          stat1Key:        "price",
          stat2Key:        "Stars",
          stat3Key:        "condition",
          descriptionKey:  "description",
        },
        // bindSource → données du 1er produit réel (usage standalone)
        bindSource: "store.products.0",
        style: { display:"flex", flexDirection:"row", borderRadius:"14px", border:"1px solid #e5e7eb", background:"#ffffff", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" },
        children: [
          // ── Image produit ──────────────────────────────────────────────────
          { id: uid(), type: "image",
            src: "https://placehold.co/420x300/f3f4f6/9ca3af?text=Produit",
            bindKey: "ProductImageUrls.0",
            style: { width:"38%", minWidth:"180px", objectFit:"cover", display:"block", flexShrink:0 } },

          // ── Contenu : nom + description + vote/prix + lien ───────────────
          { id: uid(), type: "stack",
            style: { flex:1, padding:"28px 32px 24px", display:"flex", flexDirection:"column", gap:"14px", justifyContent:"center" },
            children: [
              // Nom du produit
              { id: uid(), type: "heading", tag:"h3", bindKey:"name",
                text:elakayAutoText("h_99bc1441961b7a", "Nom du produit"),
                style: { fontSize:"22px", fontWeight:800, color:"#111827", margin:0, lineHeight:1.25 } },
              // Description courte (2 lignes max)
              { id: uid(), type: "text", tag:"p", bindKey:"description",
                text:elakayAutoText("h_a63a3b69c71dfc", "Description du produit."),
                style: { fontSize:"14px", color:"#6b7280", margin:0, lineHeight:1.6,
                  display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" } },
              // Vote / Étoiles — lit Stars du produit courant (__mapItem en repeat, products.0 standalone)
              { id: uid(), type: "shopVote",
                style: { display:"flex", alignItems:"center", gap:"6px", padding:"4px 0" },
                options: { starSize:"22px", clickable:true, maxStars:5, defaultRating:0, liveProductIndex:0 } },
              // Prix & Remise — lit price/salePrice du produit courant
              { id: uid(), type: "shopPrice",
                style: { padding:"4px 0" },
                options: { page:0, showAddToCartButton:false, tax:0, tca:0, shipping:0, filtername:"", liveProductIndex:0 } },
              // Lien Voir le produit
              { id: uid(), type: "link", text:elakayAutoText("h_c92f7c4aba58a7", "Voir le produit →"), href:"#",
                clickActionType:"function", clickActionFunction:"addToCart",
                style: { fontSize:"13px", fontWeight:600, color:"#374151", textDecoration:"none",
                  paddingTop:"6px", borderTop:"1px solid #f3f4f6" } },
            ] },
        ],
      };
    },
  },

  // ===== Grille Produits style Case Study =====
  //
  //  Responsive : min 1 colonne (mobile) → max 4 colonnes (desktop)
  //  Pagination : boutons ← Previous / Next → liés aux fonctions loadMore0 / loadMore
  //  Automatique par defaut — modifiable manuellement via les options du panneau droit.
  //
  //  Structure :
  //    stack (wrapper)
  //      grid (grille responsive, deviceGrid 1→4 cols)
  //        panel (gabarit carte VERTICALE, repeatBind paginated)
  //          image  (photo produit)
  //          stack  (nom + description + stats + lien)
  //      row (pagination ← →)
  //
  caseGrid: {
    get label() { return elakayAutoText("h_e5871995ca8c27", "Grille Case Study"); }, icon: LayoutGrid,
    build: () => {

      // ── Carte VERTICALE — optimisée pour grille 1 à 4 colonnes ──────────
      const cardTemplate = {
        id: uid(), type: "panel",
        repeatBind: {
          source:   "store",
          path:     "products",   // store.products[]
          mode:     "self",
          autoFill: true,
          paginate: true,         // pagination activée
          pageSize: 8,            // 8 produits par page (2 cols×4 ou 4 cols×2)
          fieldMap: {},
        },
        style: { borderRadius:"14px", border:"1px solid #e5e7eb", background:"#fff", overflow:"hidden",
          boxShadow:"0 1px 4px rgba(0,0,0,0.06)", display:"flex", flexDirection:"column" },
        children: [
          // Image en haut — pleine largeur
          { id: uid(), type: "image",
            src: "https://placehold.co/420x260/f3f4f6/9ca3af?text=Produit",
            bindKey: "ProductImageUrls.0",
            style: { width:"100%", height:"200px", objectFit:"cover", display:"block", flexShrink:0 } },

          // Contenu en bas
          { id: uid(), type: "stack",
            style: { padding:"16px 18px 18px", display:"flex", flexDirection:"column", gap:"10px", flex:1 },
            children: [
              // Nom
              { id: uid(), type: "heading", tag:"h3", bindKey:"name",
                text:elakayAutoText("h_99bc1441961b7a", "Nom du produit"),
                style: { fontSize:"15px", fontWeight:800, color:"#111827", margin:0, lineHeight:1.3 } },
              // Description (2 lignes max)
              { id: uid(), type: "text", tag:"p", bindKey:"description",
                text:elakayAutoText("h_4b208ed5db3dc2", "Description courte du produit."),
                style: { fontSize:"12px", color:"#6b7280", margin:0, lineHeight:1.5,
                  display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" } },
              // Vote / Étoiles — __mapItem injecté par repeatBind → Stars du produit courant
              { id: uid(), type: "shopVote",
                style: { display:"flex", alignItems:"center", gap:"5px", padding:"4px 0" },
                options: { starSize:"18px", clickable:true, maxStars:5, defaultRating:0 } },
              // Prix & Remise — __mapItem injecté par repeatBind → price/salePrice du produit courant
              { id: uid(), type: "shopPrice",
                style: { padding:"4px 0" },
                options: { page:0, showAddToCartButton:false, tax:0, tca:0, shipping:0, filtername:"" } },
              // Lien / CTA
              { id: uid(), type: "link", text:elakayAutoText("h_c92f7c4aba58a7", "Voir le produit →"), href:"#",
                clickActionType:"function", clickActionFunction:"addToCart",
                style: { fontSize:"12px", fontWeight:600, color:"#374151", textDecoration:"none",
                  marginTop:"auto", paddingTop:"6px", borderTop:"1px solid #f3f4f6" } },
            ] },
        ],
      };

      // ── Pagination ← Previous  /  Next → ────────────────────────────────
      const mkPagBtn = (label, fn) => ({
        id: uid(), type: "button", text: label,
        clickActionType:"function", clickActionFunction: fn,
        style: { padding:"9px 22px", fontSize:"13px", fontWeight:700, color:"#fff",
          background:"#6d28d9", border:"none", borderRadius:"9px", cursor:"pointer",
          display:"inline-flex", alignItems:"center", justifyContent:"center" },
      });
      const paginationRow = {
        id: uid(), type: "row",
        style: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 0 0" },
        children: [ mkPagBtn("← Précédent", "loadMore0"), mkPagBtn("Suivant →", "loadMore") ],
      };

      // ── deviceGrid responsive : 1 (mobile) → 4 (desktop) ───────────────
      const deviceGrid = [
        { id: uid(), device:"mobile",  columns:1, gap:"12px" },
        { id: uid(), device:"tablet",  columns:2, gap:"14px" },
        { id: uid(), device:"laptop",  columns:3, gap:"16px" },
        { id: uid(), device:"desktop", columns:4, gap:"16px" },
      ];

      return {
        id: uid(), type: "stack",
        _caseGrid: true,
        options: {
          orientation:     "portrait",   // "landscape" | "portrait" — modifiable dans le panneau
          // Colonnes : auto (deviceGrid) — modifiable manuellement (1-4)
          columnsDesktop: 4,    // desktop max
          columnsLaptop:  3,    // laptop
          columnsTablet:  2,    // tablet
          columnsMobile:  1,    // mobile
          pageSize:        8,   // produits par page
          source:          "store",
          path:            "products",
          imageHeight:     "200px",
          showDescription: true,
          showPrice:       true,
          showStars:       true,
          showCondition:   true,
          stat1Label:      "Prix",
          stat2Label:      "Note",
          stat3Label:      "État",
          linkText:        "Voir le produit →",
          stat1Key:        "price",
          stat2Key:        "Stars",
          stat3Key:        "condition",
        },
        style: { display:"flex", flexDirection:"column", gap:"0", width:"100%", padding:"24px 0 0" },
        children: [
          // ── Grille responsive avec le gabarit carte ──
          { id: uid(), type: "grid",
            options: { columns: 4, deviceGrid },
            style: { display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"16px", width:"100%" },
            children: [cardTemplate] },
          // ── Pagination ──
          paginationRow,
        ],
      };
    },
  },
};

// Un conteneur générique (container/row/stack/section/panel/grid…) reste
// permissif — aucun `allowedChildTypes` déclaré = tout type d'élément est
// accepté, comportement inchangé. Un conteneur curaté (ex. productFrame)
// peut restreindre ses enfants à une liste précise via `allowedChildTypes`.
function isChildTypeAllowed(parentDef, childType) {
  if (!parentDef?.isContainer) return false;
  if (!parentDef.allowedChildTypes) return true;
  return parentDef.allowedChildTypes.includes(childType);
}

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------
function uid() { return Math.random().toString(36).slice(2, 9); }

// Device grid breakpoints — utilisés dans la grille responsive et le repeatBind
const DEVICE_GRID_BREAKPOINTS = {
  all:     { get label() { return elakayAutoText("h_6a72085653e4c5", "All"); },     query: null },
  mobile:  { get label() { return elakayAutoText("h_b1d702459399a2", "Mobile"); },  query: "@media (max-width: 480px)" },
  tablet:  { get label() { return elakayAutoText("h_fabcacd2a76a1a", "Tablet"); },  query: "@media (min-width: 481px) and (max-width: 768px)" },
  laptop:  { get label() { return elakayAutoText("h_ed91cd89f3c81b", "Laptop"); },  query: "@media (min-width: 769px) and (max-width: 1200px)" },
  desktop: { get label() { return elakayAutoText("h_532c67fe1b5afa", "Desktop"); }, query: "@media (min-width: 1201px)" },
};
const DEVICE_GRID_ORDER = ["all", "mobile", "tablet", "laptop", "desktop"];

// Résout la config device grid effective selon le device actuel (canvas preview).
// Priorité : device exact en premier, puis fallbacks du plus proche au plus éloigné.
function resolveDeviceGridConfig(deviceGrid, device) {
  if (!deviceGrid?.length) return null;
  const PRIORITY = {
    mobile:  ["mobile",  "all", "tablet",  "laptop",  "desktop"],
    tablet:  ["tablet",  "mobile", "all",  "laptop",  "desktop"],
    laptop:  ["laptop",  "all", "tablet",  "desktop", "mobile" ],
    desktop: ["desktop", "laptop", "all",  "tablet",  "mobile" ],
  };
  const order = PRIORITY[device] ?? ["all", "mobile", "tablet", "laptop", "desktop"];
  for (const d of order) {
    const match = deviceGrid.find(g => g.device === d);
    if (match) return match;
  }
  return deviceGrid[0];
}

function makeElement(type) {
  const def = ELEMENT_TYPES[type];
  const clone = JSON.parse(JSON.stringify(def.defaults));
  // ré-attribuer des id frais à tous les items / enfants imbriqués
  const freshIds = (n) => {
    if (n.items) n.items = n.items.map((it) => ({ ...it, id: uid() }));
    if (n.children) n.children.forEach(freshIds);
  };
  freshIds(clone);
  return { id: uid(), type, consoleCss: "", consoleJs: "", ...clone };
}

const PAGE_STYLE = { maxWidth: "880px", margin: "0 auto", padding: "48px 24px", fontFamily: "'DM Sans', system-ui, sans-serif" };

// Modes de page disponibles dans le builder. Extensible : ajoutez ici un mode + son label
// et il apparaîtra automatiquement dans le sélecteur du doc + filtrera les sources de données.
const PAGE_MODES = [
  { id: "static",      label: __pbText("t054", "Statique (aucune donnée externe)") },
  { id: "store",       label: __pbText("t055", "Boutique (store)") },
  { id: "contentPage", label: __pbText("t056", "Page de contenu (contentPage)") },
  { id: "project",     label: __pbText("t057", "Projet / Investissement (project)") },
  { id: "elakayJod",   label: __pbText("t111", "Elakay Jod (elakayJod)") },
];

const SHOPUSER_RUNTIME_FUNCTIONS_META = [
  { name: "loadProducts", signature: "loadProducts(params?)", argc: 1 },
  { name: "loadindSearCh", signature: "loadindSearCh(searchText)", argc: 1 },
  { name: "loadProductsByArrival", signature: "loadProductsByArrival(search?, filters?, limit?)", argc: 3 },
  { name: "loadProductsByArrival_", signature: "loadProductsByArrival_(categoryID?, categoryName?, limit?)", argc: 3 },
  { name: "loadMore", signature: "loadMore(limit?)", argc: 1 },
  { name: "loadMore0", signature: "loadMore0(limit?)", argc: 1 },
  { name: "onFilterShop", signature: "onFilterShop()", argc: 0 },
  { name: "setFilterShop", signature: "setFilterShop(value)", argc: 1 },
  { name: "setSearch", signature: "setSearch(text)", argc: 1 },
  { name: "clearSearch", signature: "clearSearch()", argc: 0 },
  { name: "resetShopFilters", signature: "resetShopFilters()", argc: 0 },
  { name: "scrollToProducts", signature: "scrollToProducts()", argc: 0 },
  { name: "viewProduct", signature: "viewProduct(product?)", argc: 1 },
  { name: "goToCart", signature: "goToCart()", argc: 0 },
  { name: "goToCheckout", signature: "goToCheckout()", argc: 0 },
  { name: "goToShop", signature: "goToShop()", argc: 0 },
  { name: "clickToFollowShop", signature: "clickToFollowShop()", argc: 0 },
  { name: "findToFollowU", signature: "findToFollowU(...args)", argc: 3 },
  { name: "unFollowShop", signature: "unFollowShop(...args)", argc: 1 },
  { name: "loadingUserPub", signature: "loadingUserPub(userID)", argc: 1 },
  { name: "addToCart", signature: "addToCart(product?, payload?)", argc: 2 },
  { name: "buyNow", signature: "buyNow(product?, payload?)", argc: 2 },
  { name: "getCart", signature: "getCart()", argc: 0 },
];

function makeStarterDoc() {
  const homeId = uid();
  return {
    meta: { title: elakayAutoText("h_06ef0f40270a07", "Mon site"), description: elakayAutoText("h_5705166e7687ad", "Construit avec le Page Builder JSON") },
    pageMode: "static",
    currentPageId: homeId,
    comments: [],
    combinedElements: [],
    pages: [
      {
        id: homeId, name: "Accueil", style: { ...PAGE_STYLE },
        children: [],
      },
    ],
  };
}
const STARTER_DOC = makeStarterDoc();

// ---------------------------------------------------------------------------
//  Auto-save localStorage — constantes & helper
// ---------------------------------------------------------------------------
// Two complementary auto-save mechanisms, standardized across every editor
// in the app (PageBuilder/HowWeDoItEditor/EmailPageBuilder/Graffiti
// Configurator): a blind periodic safety-net save (AUTOSAVE_MS, 1-10min
// range) that fires regardless of activity, AND a debounce that fires
// AUTOSAVE_DEBOUNCE_MS after the last actual edit (10-30s range) so a
// change is never left un-persisted for the full periodic interval.
const AUTOSAVE_MS           = 10 * 60 * 1000; // 10 minutes
const AUTOSAVE_DEBOUNCE_MS  = 30 * 1000;      // 30 seconds after the last edit

function formatRelativeTime(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - ts) / 60000);
  if (diff < 1) return "à l'instant";
  if (diff < 60) return `il y a ${diff} min`;
  return `il y a ${Math.floor(diff / 60)}h`;
}

// ---------------------------------------------------------------------------
// Recherche / modification récursive d'un élément par id dans l'arbre
// ---------------------------------------------------------------------------
// updateTree / removeFromTree / findInTree / findParentInTree / insertIntoTree /
// moveInTree sont désormais des utilitaires partagés — voir l'import
// "../editorKit/treeOps" en tête de fichier (comportement inchangé, extraits
// tels quels pour être réutilisables par HowWeDoItEditor / emailMarketingEditor).

// Aplatit la liste des nœuds d'une page pour en faire un tableau navigable (checkbox list).
function flattenPageElements(nodes, depth = 0) {
  const result = [];
  for (const node of nodes || []) {
    if (!node || !node.id) continue;
    result.push({
      id: node.id, type: node.type, text: node.text,
      depth, toggleGroup: node.toggleGroup, repeatBind: node.repeatBind,
    });
    if (Array.isArray(node.children) && node.children.length) {
      result.push(...flattenPageElements(node.children, depth + 1));
    }
  }
  return result;
}
function ensureConsoleProps(node) {
  if (!node || typeof node !== "object") return node;
  const next = { consoleCss: "", consoleJs: "", ...node };
  if (Array.isArray(next.children)) next.children = next.children.map(ensureConsoleProps);
  return next;
}

function normalizeHrefLikeValue(value) {
  if (value == null) return "";
  return String(value).trim();
}

function resolveNodeHrefTarget(hrefValue, targetValue) {
  const href = normalizeHrefLikeValue(hrefValue);
  const target = normalizeHrefLikeValue(targetValue);
  if (href) {
    if (href.startsWith("#")) return { href, internalTarget: href.slice(1) || target || "" };
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//") || href.startsWith("/")) {
      return { href, internalTarget: "" };
    }
    return { href: `#${href}`, internalTarget: href };
  }
  if (target) return { href: `#${target}`, internalTarget: target };
  return { href: "#", internalTarget: "" };
}

function getNodeDisplayText(node) {
  const raw = node?.text ?? node?.content?.text ?? node?.field?.label ?? "";
  return String(raw || "").trim();
}

function canAutoWireNodeClickAction(node, fnName) {
  const type = normalizeHrefLikeValue(node?.clickActionType || "");
  if (!type || type === "none") return true;
  if (type !== "function") return false;
  const currentFn = normalizeHrefLikeValue(node?.clickActionFunction || "");
  return !currentFn || currentFn === fnName;
}

function withAutoFunctionAction(node, fnName, arg1 = null, arg2 = null) {
  if (!canAutoWireNodeClickAction(node, fnName)) return node;
  const out = { ...node, clickActionType: "function", clickActionFunction: fnName };
  if (arg1 != null && !normalizeHrefLikeValue(out.clickActionArg1)) out.clickActionArg1 = arg1;
  if (arg2 != null && !normalizeHrefLikeValue(out.clickActionArg2)) out.clickActionArg2 = arg2;
  return out;
}

function isPrevPaginationToken(text) {
  const t = String(text || "").trim().toLowerCase();
  if (!t) return false;
  return ["‹", "«", "<", "←", "⟵", "⟨"].includes(t) || /\b(prev|previous|precedent|pr[ée]c[ée]dent|back)\b/i.test(t);
}

function isNextPaginationToken(text) {
  const t = String(text || "").trim().toLowerCase();
  if (!t) return false;
  return ["›", "»", ">", "→", "⟶", "⟩"].includes(t) || /\b(next|suivant|forward|more)\b/i.test(t);
}

function applyAutoPaginationActions(node) {
  if (!node || typeof node !== "object") return node;
  const visit = (n) => {
    if (!n || typeof n !== "object") return n;
    let out = { ...n };
    const txt = getNodeDisplayText(out);
    if (isPrevPaginationToken(txt)) out = withAutoFunctionAction(out, "loadMore0");
    else if (isNextPaginationToken(txt)) out = withAutoFunctionAction(out, "loadMore");
    if (Array.isArray(out.children)) out.children = out.children.map(visit);
    return out;
  };
  return visit(node);
}

function withAutoShopUserSync(node) {
  if (!node || typeof node !== "object") return node;
  const rb = node.repeatBind || null;
  if (!rb) return node;

  let out = { ...node };
  const pathLower = String(rb.path || "").toLowerCase();
  const isCategoriesMapping = /categor/.test(pathLower);

  if (isCategoriesMapping) {
    out = withAutoFunctionAction(out, "loadProductsByArrival_", "items.categoryID", "items.categoryName");
  }

  // Pagination wiring on arrows / next-prev labels (container + descendants)
  out = applyAutoPaginationActions(out);
  return out;
}

const CATEGORY_TOGGLE_VISUAL_KEYS = [
  "background",
  "backgroundColor",
  "border",
  "borderColor",
  "borderRadius",
  "boxShadow",
  "color",
  "fontWeight",
  "fontSize",
  "textDecoration",
  "opacity",
  "transform",
];

function findFirstTextStyle(node) {
  if (!node || typeof node !== "object") return {};
  if (["text", "heading", "link"].includes(node.type)) return node.style || {};
  for (const child of node.children || []) {
    const style = findFirstTextStyle(child);
    if (Object.keys(style).length) return style;
  }
  return {};
}

function getCategoryToggleVisualStyle(node) {
  const style = node?.style || {};
  const textStyle = findFirstTextStyle(node);
  const visual = {};
  CATEGORY_TOGGLE_VISUAL_KEYS.forEach((key) => {
    if (style[key] != null) visual[key] = style[key];
  });
  ["color", "fontWeight", "fontSize", "textDecoration"].forEach((key) => {
    if (textStyle[key] != null) visual[key] = textStyle[key];
  });
  const background = visual.background ?? visual.backgroundColor;
  if (visual.background == null && visual.backgroundColor != null) {
    visual.background = visual.backgroundColor;
  }
  if (
    visual.backgroundColor == null &&
    background != null &&
    !/(?:gradient|url|image-set)\s*\(/i.test(String(background))
  ) {
    visual.backgroundColor = background;
  }
  return visual;
}

function isGeneratedCategoryToggleScript(script) {
  return typeof script === "string" && /(?:_catBound|dataset\.catIdx|data-cat-idx|\bvar G=)/.test(script);
}

function migrateCategoryToggleGroups(nodes = []) {
  const migrate = (node) => {
    if (!node || typeof node !== "object") return node;
    let children = (node.children || []).map(migrate);
    const defaultIndex = children.findIndex((child) =>
      child?.clickActionFunction === "loadProductsByArrival_" &&
      !child?.repeatBind
    );
    const categoryIndex = children.findIndex((child) =>
      child?.repeatBind?.source === "store" &&
      /categor/i.test(String(child?.repeatBind?.path || ""))
    );
    let next = Array.isArray(node.children) ? { ...node, children } : { ...node };

    if (defaultIndex >= 0 && categoryIndex >= 0) {
      const defaultNode = children[defaultIndex];
      const categoryNode = children[categoryIndex];
      const groupId =
        defaultNode?.toggleGroup?.id ||
        categoryNode?.toggleGroup?.id ||
        `category-${node.id || defaultNode.id || categoryNode.id}`;
      const activeStyle = {
        ...(defaultNode?.toggleGroup?.activeStyle || {}),
        ...getCategoryToggleVisualStyle(defaultNode),
      };
      const inactiveStyle = {
        ...(categoryNode?.toggleGroup?.inactiveStyle || {}),
        ...getCategoryToggleVisualStyle(categoryNode),
      };

      children = children.map((child, index) => {
        if (index !== defaultIndex && index !== categoryIndex) return child;
        const base = isGeneratedCategoryToggleScript(child.consoleJs)
          ? (() => {
              const { consoleJs, ...withoutGeneratedScript } = child; // eslint-disable-line no-unused-vars
              return withoutGeneratedScript;
            })()
          : child;
        return {
          ...base,
          toggleGroup: {
            id: groupId,
            isDefault: index === defaultIndex,
            activeStyle,
            inactiveStyle,
          },
        };
      });
      next.children = children;

      if (isGeneratedCategoryToggleScript(next.consoleJs)) {
        next.consoleJs = "";
      }
    }

    return next;
  };

  return nodes.map(migrate);
}

function hasShopFollowButton(nodes = []) {
  return nodes.some((node) =>
    node?.shopFollowButton ||
    node?.clickActionFunction === "clickToFollowShop" ||
    hasShopFollowButton(node?.children || [])
  );
}

function ensureShopTemplateFollowButton(nodes = [], templateKey = null, pageMode = null) {
  if (pageMode !== "store" || !templateKey || hasShopFollowButton(nodes)) return nodes;
  const followBar = makeShopFollowBar(templateKey);
  return nodes.length ? [nodes[0], followBar, ...nodes.slice(1)] : [followBar];
}

function collectNodeLinks(nodes = [], pageName = "", pageId = "") {
  const out = [];
  const walk = (node, path) => {
    if (!node) return;
    const label = node.text || node.content?.title || node.content?.label || node.type || "élément";
    const universal = resolveNodeHrefTarget(node.linkHref, node.linkTarget);
    if (node.linkHref || node.linkTarget) {
      out.push({
        type: "Universal",
        nodeId: node.id,
        nodeType: node.type,
        pageName,
        pageId,
        label,
        href: universal.href,
        target: universal.internalTarget,
        path,
      });
    }
    if (node.type === "link" || node.type === "button") {
      const direct = resolveNodeHrefTarget(node.href, node.target);
      out.push({
        type: node.type === "link" ? "Link" : "Button",
        nodeId: node.id,
        nodeType: node.type,
        pageName,
        pageId,
        label,
        href: direct.href,
        target: direct.internalTarget,
        path,
      });
    }
    const sourceLink = normalizeHrefLikeValue(node.sourceLink);
    if (sourceLink) {
      out.push({
        type: "Source",
        nodeId: node.id,
        nodeType: node.type,
        pageName,
        pageId,
        label,
        href: sourceLink,
        target: "",
        path,
      });
    }
    const videoUrl = normalizeHrefLikeValue(node.options?.url || node.videoUrl);
    if (videoUrl && /^https?:\/\//i.test(videoUrl)) {
      out.push({
        type: "Media",
        nodeId: node.id,
        nodeType: node.type,
        pageName,
        pageId,
        label,
        href: videoUrl,
        target: "",
        path,
      });
    }
    if (Array.isArray(node.items)) {
      node.items.forEach((item, idx) => {
        const itemHref = normalizeHrefLikeValue(item?.href);
        if (itemHref) {
          out.push({
            type: "Item",
            nodeId: node.id,
            nodeType: node.type,
            pageName,
            pageId,
            label: `${label} · item ${idx + 1}`,
            href: itemHref,
            target: "",
            path: `${path}.items.${idx}`,
          });
        }
      });
    }
    node.children?.forEach((child, idx) => walk(child, `${path}.children.${idx}`));
  };
  nodes.forEach((node, idx) => walk(node, `${pageName || pageId || "page"}.children.${idx}`));
  return out;
}

function collectDocumentLinks(doc) {
  const pages = doc?.pages || [];
  return pages.flatMap((page) => collectNodeLinks(page.children || [], page.name || "", page.id || ""));
}

// ---------------------------------------------------------------------------
// Carrousel (Swiper) — interactif dans l'aperçu
// ---------------------------------------------------------------------------
function Swiper({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [idx, setIdx] = useState(0);
  const items = node.items || [];
  const o = node.options || {};
  const is = node.itemStyle || {};
  const n = items.length || 1;
  const go = (d) => setIdx((p) => (p + d + n) % n);

  React.useEffect(() => { if (idx >= n) setIdx(0); }, [n, idx]);

  return (
    <div
      style={{ position: "relative", ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <div style={{ position: "relative", height: o.height || "300px", overflow: "hidden" }}>
        {items.map((it, i) => (
          <div key={it.id} style={{
            position: "absolute", inset: 0, opacity: i === idx ? 1 : 0,
            transition: "opacity 0.5s ease", pointerEvents: i === idx ? "auto" : "none",
            backgroundImage: `linear-gradient(${is.overlay || "rgba(0,0,0,0.4)"},${is.overlay || "rgba(0,0,0,0.4)"}), url(${it.image})`,
            backgroundSize: "cover", backgroundPosition: "center",
            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
            textAlign: "center", padding: "32px",
          }}>
            <h3 style={{ fontSize: "30px", fontWeight: 800, color: is.titleColor || "#fff", margin: "0 0 10px 0" }}>{it.title}</h3>
            <p style={{ fontSize: "16px", color: is.textColor || "#eee", maxWidth: "560px", margin: 0, lineHeight: 1.6 }}>{it.text}</p>
          </div>
        ))}
        {o.showArrows !== false && n > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); go(-1); }} style={arrowStyle("left")}><ChevronLeft size={20} /></button>
            <button onClick={(e) => { e.stopPropagation(); go(1); }} style={arrowStyle("right")}><ChevronRight size={20} /></button>
          </>
        )}
      </div>
      {o.showDots !== false && n > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: "8px", padding: "12px" }}>
          {items.map((_, i) => (
            <span key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} style={{
              width: i === idx ? "22px" : "8px", height: "8px", borderRadius: "8px",
              background: i === idx ? "#6d28d9" : "#cabfe8", transition: "all 0.3s", cursor: "pointer",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}
function arrowStyle(side) {
  return {
    position: "absolute", top: "50%", [side]: "12px", transform: "translateY(-50%)",
    width: "38px", height: "38px", borderRadius: "50%", border: "none",
    background: "rgba(255,255,255,0.9)", color: "#6d28d9", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  };
}

// ---------------------------------------------------------------------------
// Carte autonome (Card) — portrait ou paysage
// ---------------------------------------------------------------------------
function Card({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const c = node.content || {};
  const landscape = o.orientation === "landscape";
  const imageRight = landscape && o.imageSide === "right";

  const img = (
    <img src={c.image} alt={c.title} style={{
      display: "block", objectFit: "cover",
      width: landscape ? "42%" : "100%",
      height: landscape ? "auto" : (c.imageHeight || "180px"),
      alignSelf: landscape ? "stretch" : undefined,
    }} />
  );
  const body = (
    <div style={{ padding: "18px", flex: landscape ? 1 : undefined }}>
      <h4 style={{ fontSize: "18px", fontWeight: 700, color: c.titleColor || "#1a1a2e", margin: "0 0 8px 0" }}>{c.title}</h4>
      <p style={{ fontSize: "14px", color: c.textColor || "#4a4a68", lineHeight: 1.6, margin: o.showButton ? "0 0 14px 0" : 0 }}>{c.text}</p>
      {o.showButton && (
        editable
          ? <span style={{ display: "inline-block", padding: "9px 20px", fontSize: "14px", fontWeight: 600, color: "#fff", background: "#6d28d9", borderRadius: "9px", cursor: "pointer" }}>{c.buttonText}</span>
          : <a href={c.buttonHref || "#"} style={{ display: "inline-block", padding: "9px 20px", fontSize: "14px", fontWeight: 600, color: "#fff", background: "#6d28d9", borderRadius: "9px", textDecoration: "none", cursor: "pointer" }}>{c.buttonText}</a>
      )}
    </div>
  );

  return (
    <div
      style={{
        ...ns,
        display: "flex", flexDirection: landscape ? "row" : "column",
        ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}),
      }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      {imageRight ? <>{body}{img}</> : <>{img}{body}</>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groupe de texte composé (TextGroup)
// ---------------------------------------------------------------------------
function TextGroup({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const items = node.items || [];
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      {items.map((it) => {
        const T = it.tag || "p";
        return <T key={it.id} style={{ margin: 0, fontSize: it.fontSize, fontWeight: it.fontWeight, color: it.color, letterSpacing: it.letterSpacing, lineHeight: 1.4 }}>{it.text}</T>;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Champ de formulaire (input / textarea / select / date)
// ---------------------------------------------------------------------------
function FormField({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const f = node.field || {};
  const wrap = (inner) => (
    <div
      style={{ marginBottom: "16px", ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "4px", borderRadius: "4px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      {f.label && <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a4a68", marginBottom: "6px" }}>{f.label}</label>}
      <div style={{ pointerEvents: editable ? "none" : "auto" }}>{inner}</div>
    </div>
  );
  if (node.type === "textarea")
    return wrap(<textarea placeholder={f.placeholder} rows={f.rows || 4} style={ns} />);
  if (node.type === "select")
    return wrap(
      <select style={ns}>
        {(node.options?.choices || []).map((c, i) => <option key={i}>{c}</option>)}
      </select>
    );
  return wrap(<input type={f.inputType || "text"} placeholder={f.placeholder} style={ns} />);
}

function CheckboxField({ node, editable, onSelect, isSelected, radio }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const f = node.field || {};
  return (
    <label
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "4px", borderRadius: "4px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <input type={radio ? "radio" : "checkbox"} name={radio ? f.name : undefined} defaultChecked={f.checked} readOnly={editable} style={{ accentColor: "#6d28d9", width: "17px", height: "17px" }} />
      {f.label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Vidéo intégrée (YouTube / Vimeo / fichier)
// ---------------------------------------------------------------------------
const mediaString = (value) => (value === undefined || value === null ? "" : String(value).trim());
const isImageNode = (node) => node?.type === "image" || node?.tag === "img";
const isVideoNode = (node) => node?.type === "video";

function toEmbedUrl(url = "") {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}
// Swiper du Swiper Manager dans l'éditeur : le vrai rendu, mais les liens du
// swiper ne naviguent jamais depuis l'éditeur (un clic sélectionne le bloc).
function ElakaySwiperNode({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  return (
    <div
      style={{ ...ns, position: "relative", ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClickCapture={editable ? (e) => { if (e.target.closest?.("a")) e.preventDefault(); } : undefined}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <ElakaySwiperBlock swiperId={o.swiperId} swiperName={o.swiperName} maxWidth={Number(o.maxWidth) || 0} editorMode={editable} />
    </div>
  );
}

// Panneau de propriétés : choisir le swiper dans la Swiper Library (le
// serveur ne propose à chacun que les swipers qu'il peut utiliser).
function ElakaySwiperField({ element, onUpdate }) {
  const [picking, setPicking] = useState(false);
  const o = element.options || {};
  const setOptions = (patch) => onUpdate({ ...element, options: { ...(element.options || {}), ...patch } });
  return (
    <>
      <p style={{ fontSize: "12px", color: "#6b6592", margin: "0 0 8px" }}>
        {o.swiperId
          ? elakayAutoText("h_esb_selected", "Swiper : {name}", { name: o.swiperName || o.swiperId })
          : elakayAutoText("h_esb_none_selected", "Aucun swiper choisi.")}
      </p>
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
        <button type="button" onClick={() => setPicking(true)} style={{ padding: "7px 12px", borderRadius: "8px", border: "1px solid #6d28d9", background: "#6d28d9", color: "#fff", fontWeight: 700, fontSize: "12px", cursor: "pointer" }}>
          {o.swiperId ? elakayAutoText("h_esb_change", "Changer de swiper") : elakayAutoText("h_esb_choose_btn", "Choisir un swiper")}
        </button>
        {o.swiperId && (
          <button type="button" onClick={() => setOptions({ swiperId: "", swiperName: "" })} style={{ padding: "7px 12px", borderRadius: "8px", border: "1px solid #d9d3ec", background: "#fff", color: "#2a2547", fontSize: "12px", cursor: "pointer" }}>
            {elakayAutoText("h_esb_remove", "Retirer")}
          </button>
        )}
      </div>
      <label style={{ display: "block", marginBottom: "10px" }}>
        <span style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#4a4a68", marginBottom: "4px" }}>{elakayAutoText("h_esb_max_width", "Largeur max (px, 0 = toute la largeur)")}</span>
        <input type="number" min={0} max={2000} value={o.maxWidth || 0} onChange={(e) => setOptions({ maxWidth: Math.max(0, Math.min(2000, Number(e.target.value) || 0)) })}
          style={{ width: "100%", padding: "7px 10px", borderRadius: "8px", border: "1px solid #d9d3ec" }} />
      </label>
      <p style={{ fontSize: "11px", color: "#8a84a6", margin: 0 }}>
        {elakayAutoText("h_esb_reference_hint", "Le swiper est référencé, pas copié : il se modifie dans le Swiper Manager et s'adapte seul à Desktop / Tablet / Mobile.")}
      </p>
      <SwiperLibraryPicker
        show={picking}
        selectedId={o.swiperId || null}
        onClose={() => setPicking(false)}
        onPick={(swiper) => { setOptions({ swiperId: swiper.id, swiperName: swiper.name || "" }); setPicking(false); }}
      />
    </>
  );
}

function VideoBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const url = mediaString(node.videoUrl || node.src || o.url || "");
  if (node._hideWhenEmptyMedia || (node.bindKey && !url) || !url) return null;
  const isFile = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(url);
  return (
    <div
      style={{ ...ns, aspectRatio: o.ratio || "16 / 9", position: "relative", ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      {isFile
        ? <video src={toFileUrl(url)} controls style={{ width: "100%", height: "100%", display: "block", pointerEvents: editable ? "none" : "auto" }} />
        : <iframe
            src={toEmbedUrl(url)} title={elakayAutoText("h_c4277d8b7ae7cc", "vidéo")} allowFullScreen
            style={{ width: "100%", height: "100%", border: "none", pointerEvents: editable ? "none" : "auto" }}
          />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icône (glyphe), Citation, Statistique, Progression, Note étoiles
// ---------------------------------------------------------------------------
function IconBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const size = o.size || "48px";
  const iconStyle = {
    ...(o.rotation ? { transform: `rotate(${o.rotation}deg)` } : {}),
    ...(o.opacity != null ? { opacity: o.opacity } : {}),
  };
  // content.iconId (registre iconKit, vrai SVG) prime sur options.glyph
  // (caractère libre historique) — un id inconnu/absent retombe simplement
  // sur le glyphe, sans jamais casser le rendu d'un vieux document.
  const registryIcon = getRegistryIcon(node.content?.iconId);
  const decorative = o.decorative !== false;
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      {registryIcon ? (
        <registryIcon.Component
          width={size} height={size} color={o.color || "#6d28d9"} style={iconStyle}
          aria-hidden={decorative ? "true" : undefined}
          role={decorative ? undefined : "img"}
          aria-label={decorative ? undefined : (o.ariaLabel || registryIcon.label)}
        />
      ) : (
        <span style={{ fontSize: size, color: o.color || "#6d28d9", lineHeight: 1, ...iconStyle }}>{o.glyph || "★"}</span>
      )}
    </div>
  );
}
function QuoteBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  return (
    <blockquote style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <p style={{ margin: "0 0 8px", fontSize: "18px", fontStyle: "italic", color: "#2a2547", lineHeight: 1.6 }}>{c.text}</p>
      {c.author && <footer style={{ fontSize: "14px", fontWeight: 700, color: "#6d28d9" }}>{c.author}</footer>}
    </blockquote>
  );
}
function StatBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ fontSize: "34px", fontWeight: 800, color: c.valueColor || "#6d28d9", lineHeight: 1.1 }}>{c.value}</div>
      <div style={{ fontSize: "14px", color: c.labelColor || "#7a73a0", marginTop: "6px" }}>{c.label}</div>
    </div>
  );
}
function ProgressBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  const pct = Math.max(0, Math.min(100, Number(c.percent) || 0));
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#4a4a68", marginBottom: "6px" }}>
        <span>{c.label}</span><span style={{ fontWeight: 700 }}>{pct}%</span>
      </div>
      <div style={{ height: "10px", borderRadius: "10px", background: c.trackColor || "#ece9f5", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: c.barColor || "#6d28d9", borderRadius: "10px", transition: "width 0.4s" }} />
      </div>
    </div>
  );
}
function RatingBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  const max = c.max || 5; const val = Number(c.value) || 0;
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <span style={{ fontSize: "22px", color: c.color || "#f59e0b", letterSpacing: "2px" }}>
        {"★".repeat(Math.round(val))}{"☆".repeat(Math.max(0, max - Math.round(val)))}
      </span>
      {c.label && <span style={{ fontSize: "14px", color: "#7a73a0" }}>{c.label}</span>}
    </div>
  );
}
function PriceBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const c = node.content || {};
  const currency = c.currency || "€";
  const current = Number(c.current) || 0;
  const old = Number(c.old) || 0;
  const hasDiscount = old > current;
  const discountPct = hasDiscount ? Math.round(((old - current) / old) * 100) : 0;
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <span style={{ fontSize: "22px", fontWeight: 800, color: "#1a1a2e" }}>{current.toFixed(2)} {currency}</span>
      {hasDiscount && (
        <>
          <span style={{ fontSize: "15px", color: "#9b93b8", textDecoration: "line-through" }}>{old.toFixed(2)} {currency}</span>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#dc2626" }}>-{discountPct}%</span>
        </>
      )}
    </div>
  );
}
function BadgeBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  return (
    <span style={{ ...ns, color: o.color || "#ffffff", background: o.background || "#6d28d9",
      ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      {node.text}
    </span>
  );
}
function AccordionBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(0);
  const items = node.items || [];
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      {items.map((it, i) => (
        <div key={it.id} style={{ border: "1px solid #ece9f5", borderRadius: "12px", overflow: "hidden", background: "#fff" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(open === i ? -1 : i); }}
            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "none", border: "none", cursor: "pointer", fontSize: "15px", fontWeight: 600, color: "#1a1a2e", textAlign: "left" }}
          >
            {it.q}
            <ChevronDown size={18} style={{ transform: open === i ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0, color: "#6d28d9" }} />
          </button>
          {open === i && <div style={{ padding: "0 18px 16px", fontSize: "14px", color: "#4a4a68", lineHeight: 1.6 }}>{it.a}</div>}
        </div>
      ))}
    </div>
  );
}

// Modal : en édition, on montre le bouton + le contenu "ouvert" en ligne pour pouvoir l'éditer.
// En aperçu réel (non éditable), le contenu est masqué et s'ouvre via le bouton.
function ModalBlock({ node, selectedIds, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(false);
  const trigger = (
    <button
      style={node.triggerStyle || {}}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : (e) => { e.stopPropagation(); setOpen(true); }}
    >{node.triggerText || elakayAutoText("h_42c07747756067", "Ouvrir")}</button>
  );

  const isSimple = node.contentMode === "simple";
  // Le panneau du modal (titre + contenu)
  const panel = (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}),
      position:"relative", maxWidth:"min(100%,900px)", maxHeight:"calc(100vh - 40px)", overflowY:"auto" }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : (e) => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {node.title ? <strong style={{ fontSize: "20px", color: "#1a1a2e" }}>{node.title}</strong> : <span />}
        <button onClick={(e) => { e.stopPropagation(); if (!editable) setOpen(false); }}
          style={{ border: "none", background: "none", cursor: "pointer", color: "#9b93b8", fontSize: "20px", lineHeight: 1 }} aria-label={elakayAutoText("h_5ab4ec640d654d", "Fermer")}>×</button>
      </div>
      {isSimple ? (
        <>
          {node.simple?.text && <p style={{ fontSize: "16px", color: "#4a4a68", lineHeight: 1.6, margin: 0 }}>{node.simple.text}</p>}
          {node.simple?.buttonText && (
            <button onClick={(e) => { e.stopPropagation(); if (!editable) setOpen(false); }}
              style={{ alignSelf: "flex-start", padding: "10px 22px", fontSize: "15px", fontWeight: 600, color: "#fff", background: "#6d28d9", border: "none", borderRadius: "10px", cursor: "pointer" }}>
              {node.simple.buttonText}
            </button>
          )}
        </>
      ) : (
        <>
          {node.children?.map((c) => (
            <RenderNode key={c.id} node={c} selectedIds={selectedIds} onSelect={onSelect} editable={editable} />
          ))}
          {editable && (!node.children || node.children.length === 0) && (
            <span style={{ color: "#9b93b8", fontSize: "13px", fontStyle: "italic" }}>{elakayAutoText("h_064454b3f3e8fa", "Contenu du modal vide — ajoutez des éléments à l'intérieur.")}</span>
          )}
        </>
      )}
    </div>
  );

  if (editable) {
    // Vue édition : bouton + cadre du modal affiché en ligne, avec un badge explicatif
    return (
      <div style={{ display: "inline-flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
        {trigger}
        <div style={{ position: "relative", padding: "8px", border: "1px dashed #c9b8f5", borderRadius: "12px", background: "#faf8ff" }}>
          <span style={{ position: "absolute", top: "-10px", left: "10px", fontSize: "10px", fontWeight: 700, color: "#6d28d9", background: "#fff", padding: "1px 8px", borderRadius: "6px", border: "1px solid #e4dcfb" }}>{elakayAutoText("h_5ff001152c44ae", "MODAL (aperçu ouvert)")}</span>
          {panel}
        </div>
      </div>
    );
  }
  // Aperçu réel : bouton, et overlay seulement si ouvert
  return (
    <>
      {trigger}
      {open && (
        <ViewportModal>
          <div onClick={() => setOpen(false)}
            style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center",
              justifyContent:"center", overflowY:"auto", padding:"20px", background:"rgba(20,12,40,0.5)" }}>
            {panel}
          </div>
        </ViewportModal>
      )}
    </>
  );
}

// Menu déroulant : bouton + liste de liens
function DropdownBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(false);
  const items = node.items || [];
  const click = editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : (e) => { e.stopPropagation(); setOpen((v) => !v); };
  return (
    <div style={{ position: "relative", display: "inline-block", ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "3px", borderRadius: "8px" } : {}) }}>
      <button style={node.triggerStyle || {}} onClick={click}>{node.triggerText || elakayAutoText("h_31dfa027034be3", "Menu ▾")}</button>
      {(editable || open) && (
        <div style={{ ...ns, position: editable ? "static" : "absolute", marginTop: "6px", zIndex: 50 }}>
          {items.map((it) => (
            editable
              ? <span key={it.id} style={{ display: "block", padding: "9px 16px", fontSize: "14px", color: "#2a2547", cursor: "pointer" }}>{it.label}</span>
              : <a key={it.id} href={it.href || "#"} style={{ display: "block", padding: "9px 16px", fontSize: "14px", color: "#2a2547", textDecoration: "none" }}>{it.label}</a>
          ))}
        </div>
      )}
    </div>
  );
}

// Onglets : barre d'onglets + panneau actif
function TabsBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [active, setActive] = useState(0);
  const items = node.items || [];
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ display: "flex", borderBottom: "1px solid #ece9f5", background: "#faf9fd" }}>
        {items.map((it, i) => (
          <button key={it.id} onClick={(e) => { e.stopPropagation(); setActive(i); }}
            style={{ padding: "12px 20px", borderTop: "none", borderRight: "none", borderLeft: "none", background: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600,
              color: active === i ? "#6d28d9" : "#7a73a0", borderBottom: active === i ? "2px solid #6d28d9" : "2px solid transparent" }}>
            {it.label}
          </button>
        ))}
      </div>
      <div style={{ padding: "20px", fontSize: "15px", color: "#4a4a68", lineHeight: 1.6 }}>
        {items[active]?.text}
      </div>
    </div>
  );
}

// Offcanvas : bouton + panneau latéral coulissant
function OffcanvasBlock({ node, selectedIds, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [open, setOpen] = useState(false);
  const side = ["left", "right", "top", "bottom"].includes(node.side) ? node.side : "right";
  const sideLabel = { left: "GAUCHE", right: "DROITE", top: "HAUT", bottom: "BAS" }[side];
  // Position du panneau coulissant selon le côté
  const horizontal = side === "left" || side === "right";
  const panelPos = horizontal
    ? { position: "fixed", top: 0, bottom: 0, [side]: 0 }
    : { position: "fixed", left: 0, right: 0, [side]: 0 };
  const panel = (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : (e) => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {node.title ? <strong style={{ fontSize: "18px", color: "#1a1a2e" }}>{node.title}</strong> : <span />}
        <button onClick={(e) => { e.stopPropagation(); if (!editable) setOpen(false); }}
          style={{ border: "none", background: "none", cursor: "pointer", color: "#9b93b8", fontSize: "20px" }} aria-label={elakayAutoText("h_5ab4ec640d654d", "Fermer")}>×</button>
      </div>
      {node.children?.map((c) => (
        <RenderNode key={c.id} node={c} selectedIds={selectedIds} onSelect={onSelect} editable={editable} />
      ))}
    </div>
  );
  if (editable) {
    return (
      <div style={{ display: "inline-flex", flexDirection: "column", gap: "10px", alignItems: "flex-start" }}>
        <button style={node.triggerStyle || {}} onClick={(e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); }}>{node.triggerText || elakayAutoText("h_42c07747756067", "Ouvrir")}</button>
        <div style={{ position: "relative", padding: "8px", border: "1px dashed #c9b8f5", borderRadius: "12px", background: "#faf8ff" }}>
          <span style={{ position: "absolute", top: "-10px", left: "10px", fontSize: "10px", fontWeight: 700, color: "#6d28d9", background: "#fff", padding: "1px 8px", borderRadius: "6px", border: "1px solid #e4dcfb" }}>{elakayAutoText("h_88656b4eabbd05", "PANNEAU")}{sideLabel}
          </span>
          {panel}
        </div>
      </div>
    );
  }
  return (
    <>
      <button style={node.triggerStyle || {}} onClick={(e) => { e.stopPropagation(); setOpen(true); }}>{node.triggerText || elakayAutoText("h_42c07747756067", "Ouvrir")}</button>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(20,12,40,0.4)", zIndex: 998 }}>
          <div style={panelPos} onClick={(e) => e.stopPropagation()}>{panel}</div>
        </div>
      )}
    </>
  );
}

// Info-bulle : texte + bulle au survol
function TooltipBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const [hover, setHover] = useState(false);
  return (
    <span style={{ position: "relative", ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "3px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {node.text}
      {(hover || (editable && isSelected)) && node.tip && (
        <span style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: "#1a1a2e", color: "#fff", padding: "6px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", zIndex: 60 }}>
          {node.tip}
        </span>
      )}
    </span>
  );
}

// --- Positions de signature : grille 3×3 (%, et px) + helper ---
const SIGN_QUICK_POS_GRID = [
  { label: "↖", x: 12, y: 12 }, { label: "↑", x: 50, y: 12 }, { label: "↗", x: 88, y: 12 },
  { label: "←", x: 12, y: 50 }, { label: "·", x: 50, y: 50 }, { label: "→", x: 88, y: 50 },
  { label: "↙", x: 12, y: 88 }, { label: "↓", x: 50, y: 88 }, { label: "↘", x: 88, y: 88 },
];
const SIGN_QUICK_POS_GRID_PX = [
  { label: "↖", x: 24, y: 24 }, { label: "↑", x: 200, y: 24 }, { label: "↗", x: 380, y: 24 },
  { label: "←", x: 24, y: 180 }, { label: "·", x: 200, y: 180 }, { label: "→", x: 380, y: 180 },
  { label: "↙", x: 24, y: 360 }, { label: "↓", x: 200, y: 360 }, { label: "↘", x: 380, y: 360 },
];
function getSignPosStyleXY(x, y, unit = "%") {
  return unit === "%"
    ? { position: "absolute", left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)", zIndex: 2, width: 144 }
    : { position: "absolute", left: x, top: y, zIndex: 2, width: 144 };
}

// --- Zone de signature manuscrite (canvas) ---
function SignaturePad({ width = 380, height = 120, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#1a1a2e";
  }, []);
  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; if (onChange) onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); if (onChange) onChange(""); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <canvas ref={canvasRef} width={width} height={height}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ border: "1px solid #d9d3ec", borderRadius: "10px", background: "#fff", touchAction: "none", cursor: "crosshair" }} />
      <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }}
        style={{ alignSelf: "flex-start", padding: "5px 12px", fontSize: "12px", color: "#e11d48", background: "transparent", border: "1px solid #f3b4c2", borderRadius: "8px", cursor: "pointer" }}>{elakayAutoText("h_fe23de7b572d52", "Effacer")}</button>
    </div>
  );
}

// CSS commun pour le rendu du document Word (injecté dans le HTML converti)
const DOC_CSS = `<style>
  .pb-wdoc{font-family:'Georgia','Times New Roman',serif;font-size:14px;line-height:1.85;color:#1e293b}
  .pb-wdoc p{margin:0 0 10px}.pb-wdoc h1{font-size:22px;font-weight:700;margin:20px 0 10px}
  .pb-wdoc h2{font-size:18px;font-weight:700;margin:16px 0 8px}.pb-wdoc h3{font-size:15px;font-weight:600;margin:12px 0 6px}
  .pb-wdoc table{border-collapse:collapse;width:100%;margin:12px 0}
  .pb-wdoc td,.pb-wdoc th{border:1px solid #e2e8f0;padding:8px 12px}
  .pb-wdoc ul,.pb-wdoc ol{margin:8px 0;padding-left:28px}.pb-wdoc li{margin:4px 0}
  .pb-pg-sep{border:none;border-top:3px solid #e2e8f0;background:#f8fafc;margin:28px 0;height:28px;display:flex;align-items:center;justify-content:center}
  .pb-pg-sep::after{content:'─ ─ ─  Page suivante  ─ ─ ─';font-size:10px;color:#94a3b8;font-style:italic}
</style>`;
function buildDocHtml(rawHtml) {
  const html = rawHtml.replace(/<br clear="all">/gi, '<div class="pb-pg-sep"></div>');
  return `${DOC_CSS}<div class="pb-wdoc">${html}</div>`;
}

function toAbsoluteUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window === "undefined") return url;
  try { return new URL(url, window.location.origin).href; } catch { return url; }
}

function toOfficeEmbedUrl(url) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return "";
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absolute)}`;
}

function inferDocKind(name = "", mime = "", url = "") {
  const s = `${name} ${url}`.toLowerCase();
  const m = (mime || "").toLowerCase();
  if (m.includes("pdf") || s.includes(".pdf")) return "pdf";
  if (m.includes("wordprocessingml") || s.includes(".docx")) return "docx";
  if (m.includes("msword") || s.includes(".doc")) return "doc";
  return "unknown";
}

// --- Visualiseur de document Word : multi-pages, zones de signature multiples ---
function DocViewerBlock({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const saveNode = useContext(NodeUpdateContext);
  const [view, setView] = useState(node.defaultView || "html");
  const [docHtml, setDocHtml] = useState(node.docHtml || "");
  const [docFile, setDocFile] = useState(null);
  const [uploadedMeta, setUploadedMeta] = useState(
    node.docName
      ? { originalName: node.docName, savedName: node.docName, fullPath: node.docSavedPath || "", serverUrl: node.docUrl || "", size: 0 }
      : null
  );
  // "idle" | "converting" | "uploading" | "done" | "error"
  const [uploadStatus, setUploadStatus] = useState(node.docUrl ? "done" : "idle");
  const [signMode, setSignMode] = useState(node.signMode === "type" ? "type" : "draw");
  const [signature, setSignature] = useState({ image: "", name: "", date: "" });
  const [localPdfUrl, setLocalPdfUrl] = useState("");
  const fileRef = useRef(null);
  const sigs = node.signatures || [];
  const docUrl        = uploadedMeta?.serverUrl || node.docUrl || "";
  const officeEmbedUrl = toOfficeEmbedUrl(docUrl);
  const docKind       = inferDocKind(uploadedMeta?.originalName || node.docName || "", docFile?.type || "", docUrl);
  const pdfPreviewUrl = docKind === "pdf" ? (uploadedMeta?.serverUrl || localPdfUrl || node.docUrl || "") : "";
  const isPdf         = docKind === "pdf";
  const isWord        = docKind === "docx" || docKind === "doc";
  const docExt        = isPdf ? ".pdf" : docKind === "docx" ? ".docx" : ".doc";
  const hasDocContent = !!(docHtml || docFile || node.docUrl);

  useEffect(() => {
    if (!docFile) {
      setLocalPdfUrl("");
      return;
    }
    const kind = inferDocKind(docFile.name || "", docFile.type || "", "");
    if (kind !== "pdf") {
      setLocalPdfUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(docFile);
    setLocalPdfUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [docFile]);

  const handleFile = async (e) => {
    e.stopPropagation();
    const f = e.target.files?.[0]; if (!f) return;
    const kind = inferDocKind(f.name || "", f.type || "", "");
    const isDocx = kind === "docx";

    // ── Nom de sauvegarde ──
    const finalName = node.autoName !== false ? `${Date.now()}-${f.name}` : (node.customName || f.name);

    setDocFile(f);
    setUploadedMeta({ originalName: f.name, savedName: finalName, fullPath: "", serverUrl: "", size: f.size });
    setUploadStatus(isDocx ? "converting" : "uploading");
    if (kind !== "docx") setDocHtml("");

    let styledHtml = "";
    if (isDocx) {
      try {
        // ── 1. Conversion mammoth → HTML (client-side) ──
        const mammoth = await import("mammoth").catch(() => null);
        if (mammoth) {
          const arr = await f.arrayBuffer();
          const res = await mammoth.convertToHtml({ arrayBuffer: arr });
          styledHtml = buildDocHtml(res.value);
          setDocHtml(styledHtml);
          setView("html");
        } else {
          styledHtml = "";
          setDocHtml(`<p style="color:#94a3b8;font-style:italic">Prévisualisation indisponible: la librairie mammoth.js n'est pas installée. Le document est sauvegardé et téléchargeable.</p>`);
          setView(node.allowOffice !== false ? "office" : "word");
        }
      } catch (err) {
        setDocHtml(`<p style="color:#e11d48">Erreur de conversion .docx : ${err.message}</p>`);
        setView(node.allowOffice !== false ? "office" : "word");
      }
    } else if (kind === "doc") {
      setDocHtml(`<p style="color:#b45309">Le format <strong>.doc</strong> ne peut pas être converti en HTML ici. Utilisez <strong>Word Live</strong> (si URL publique) ou téléchargez le document.</p>`);
      setView(node.allowOffice !== false ? "office" : "word");
    } else if (kind === "pdf") {
      setDocHtml("");
      setView(node.allowPdf !== false ? "pdf" : "word");
    } else {
      setDocHtml(`<p style="color:#b45309">Format non reconnu. Utilisez un fichier <strong>.doc</strong>, <strong>.docx</strong> ou <strong>.pdf</strong>.</p>`);
      setView("word");
    }

    // ── 2. Upload du fichier vers le serveur (.doc / .docx / .pdf) ──
    setUploadStatus("uploading");
    let serverUrl  = "";
    let serverPath = "";
    let serverKind = kind;
    try {
      const form = new FormData();
      form.append("file", f, finalName);
      const { token: documentToken } = isAuthenticated() || {};
      const resp = await fetch(`${elakay_API}/documents/upload`, { method: "POST", headers: documentToken ? { Authorization: `Bearer ${documentToken}` } : {}, body: form });
      if (resp.ok) {
        const data = await resp.json();
        serverUrl  = data.url  || "";
        serverPath = data.path || "";
        serverKind = data.kind || kind;
        setUploadedMeta((prev) => ({ ...prev, fullPath: serverPath, serverUrl }));
        setUploadStatus("done");
      } else {
        const errBody = await resp.json().catch(() => ({}));
        console.warn("[DocViewer] upload error:", errBody.error || resp.status);
        setUploadStatus("error");
      }
    } catch (uploadErr) {
      console.warn("[DocViewer] upload failed:", uploadErr.message);
      setUploadStatus("error");
    }

    // ── 3. Persistance dans le nœud JSON ──
    if (saveNode) {
      saveNode(node.id, {
        docHtml:      styledHtml,
        docName:      f.name,
        docKind:      serverKind,
        docSavedPath: serverPath,
        docUrl:       serverUrl,
      });
    }
  };

  const download = (e) => {
    e.stopPropagation();
    // Préférence : URL serveur ; sinon blob local
    if (uploadedMeta?.serverUrl) {
      const a = document.createElement("a");
      a.href = uploadedMeta.serverUrl;
      a.download = uploadedMeta.savedName || uploadedMeta.originalName;
      a.click();
      return;
    }
    if (!docFile) return;
    const url = URL.createObjectURL(docFile);
    const a = document.createElement("a"); a.href = url; a.download = uploadedMeta?.savedName || docFile.name; a.click(); URL.revokeObjectURL(url);
  };

  const click = editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined;
  const tabBtn = (active) => ({
    padding: "7px 16px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
    border: `1.5px solid ${active ? "#4f46e5" : "#e0daf5"}`,
    background: active ? "#4f46e5" : "#fff", color: active ? "#fff" : "#4f46e5",
    borderRadius: "8px", transition: "all 0.15s",
  });

  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2.5px solid #4f46e5", outlineOffset: "3px" } : {}) }} onClick={click}>
      <input ref={fileRef} type="file" accept=".docx,.doc,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFile} style={{ display: "none" }} />

      {/* ── Métadonnées builder ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "4px 10px", borderRadius: "999px", background: "#ede9fe", color: "#5b21b6", fontSize: "11px", fontWeight: 600 }}>
          <FolderClosed size={11} /> {node.folderLabel || elakayAutoText("h_c7b30e40742ebe", "Mes documents")}
        </span>
        <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: "999px", background: "#f1f5f9", color: "#475569", fontSize: "11px", fontWeight: 600 }}>
          <code style={{ fontFamily: "ui-monospace,monospace", fontSize: "10px" }}>{node.apiPath || "/api/documents"}</code>
        </span>
        <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: "999px", background: "#f1f5f9", color: "#475569", fontSize: "11px" }}>
          {node.autoName !== false ? elakayAutoText("h_253aaf4c85c123", "timestamp auto") : (node.customName || elakayAutoText("h_5dec035322dba0", "nom personnalisé"))}
        </span>
      </div>

      {/* ── Barre d'actions ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
        <button type="button"
          onClick={(e) => { e.stopPropagation(); if (editable) onSelect(node.id, e.shiftKey); fileRef.current?.click(); }}
          style={{ padding: "9px 18px", fontSize: "12px", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6d28d9,#4f46e5)", border: "none", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "7px", boxShadow: "0 2px 10px rgba(79,70,229,0.35)" }}>
          <FileUp size={14} /> {node.buttonText || elakayAutoText("h_d9ff6a97b3a7f3", "Charger un document")}
        </button>
        {/* Vue HTML — toujours dispo si activée */}
        {node.allowHtml && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setView("html"); }} style={tabBtn(view === "html")}>{elakayAutoText("h_2374f1280853f3", "📄 HTML")}</button>
        )}
        {/* Word Live — uniquement pour les fichiers Word (ou si pas de fichier chargé) */}
        {node.allowOffice !== false && (!hasDocContent || isWord) && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setView("office"); }} style={tabBtn(view === "office")}>{elakayAutoText("h_651708c3a4cab2", "🟦 Word Live")}</button>
        )}
        {/* PDF — uniquement pour les fichiers PDF (ou si pas de fichier chargé) */}
        {node.allowPdf !== false && (!hasDocContent || isPdf) && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setView("pdf"); }} style={tabBtn(view === "pdf")}>{elakayAutoText("h_d8363fafbbb389", "📕 PDF")}</button>
        )}
        {/* Fichier — bouton de téléchargement */}
        {node.allowWord && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setView("word"); }} style={tabBtn(view === "word")}>
            📥 {hasDocContent ? docExt : elakayAutoText("h_f0fa5745ccc974", "Fichier")}
          </button>
        )}
        {node.allowDownload && (docFile || uploadedMeta?.serverUrl) && (
          <button type="button" onClick={download}
            style={{ padding: "9px 14px", fontSize: "12px", fontWeight: 600, color: isPdf ? "#dc2626" : "#4f46e5", background: isPdf ? "#fff1f2" : "#f0eeff", border: `1.5px solid ${isPdf ? "#fda4af" : "#c4b5fd"}`, borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
            <FileText size={13} /> ⬇ {docExt}
          </button>
        )}
      </div>

      {/* ── Chemin de sauvegarde + statut d'upload ── */}
      {uploadedMeta && (() => {
        const statusMap = {
          converting: { bg: "#fffbeb", border: "#fcd34d", icon: "⏳", label: elakayAutoText("h_f4d44947b1915c", "Conversion en cours…"),        labelColor: "#92400e" },
          uploading:  { bg: "#eff6ff", border: "#93c5fd", icon: "⬆",  label: elakayAutoText("h_e7c1f5e6667fe2", "Envoi vers le serveur…"),     labelColor: "#1e40af" },
          done:       { bg: "#f0fdf4", border: "#bbf7d0", icon: null,  label: elakayAutoText("h_2a0b11d30444e9", "Sauvegardé sur le serveur :"), labelColor: "#15803d" },
          error:      { bg: "#fff1f2", border: "#fda4af", icon: "⚠",  label: elakayAutoText("h_8c713119be291a", "Erreur upload — HTML local sauvegardé"), labelColor: "#be123c" },
        };
        const s = statusMap[uploadStatus] || statusMap.done;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 14px", background: s.bg, border: `1px solid ${s.border}`, borderRadius: "10px", flexWrap: "wrap" }}>
            {s.icon ? (
              <span style={{ fontSize: "14px" }}>{s.icon}</span>
            ) : (
              <Check size={14} color="#16a34a" />
            )}
            <span style={{ fontSize: "11px", fontWeight: 700, color: s.labelColor, whiteSpace: "nowrap" }}>{s.label}</span>
            {uploadStatus === "done" && uploadedMeta.serverUrl && (
              <code style={{ fontFamily: "ui-monospace,monospace", fontSize: "10px", color: "#166534", background: "#dcfce7", padding: "2px 8px", borderRadius: "5px", wordBreak: "break-all", flex: 1 }}>
                {uploadedMeta.serverUrl}
              </code>
            )}
            {uploadedMeta.size > 0 && (
              <span style={{ fontSize: "10px", color: "#6b7280", whiteSpace: "nowrap", marginLeft: "auto" }}>
                {(uploadedMeta.size / 1024).toFixed(1)} KB
              </span>
            )}
          </div>
        );
      })()}

      {/* ── Prévisualisation builder (aperçu du mode actif) ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 10px", background: (node.viewStyle || "card") === "inline" ? "#f0fdf4" : "#f0eeff", border: `1.5px solid ${(node.viewStyle || "card") === "inline" ? "#bbf7d0" : "#c4b5fd"}`, borderRadius: "8px" }}>
        <span style={{ fontSize: "12px" }}>{(node.viewStyle || "card") === "inline" ? "📄" : "🗂"}</span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: (node.viewStyle || "card") === "inline" ? "#15803d" : "#6d28d9" }}>{elakayAutoText("h_42b647fd7f5cbc", "Mode :")}{(node.viewStyle || "card") === "inline" ? elakayAutoText("h_969c494fe6212f", "Direct (intégré dans la page)") : elakayAutoText("h_31fb6679e3fd81", "Visualiseur (card avec header)")}
        </span>
        <span style={{ fontSize: "10px", color: "#94a3b8", marginLeft: "auto" }}>{elakayAutoText("h_2760f25f370181", "modifiable dans le panneau →")}</span>
      </div>

      {/* ── Visualiseur style Word lecture seule — toutes les pages ── */}
      <div style={{ background: "#e8e8ed", borderRadius: "14px", padding: "10px", boxShadow: "inset 0 2px 10px rgba(0,0,0,0.08)" }}>
        <div style={{ background: "#fff", borderRadius: "10px", boxShadow: "0 6px 28px rgba(0,0,0,0.13),0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

          {/* En-tête document */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <FileText size={17} color="rgba(255,255,255,0.9)" />
              <span style={{ color: "#fff", fontWeight: 700, fontSize: "13px", letterSpacing: "0.04em" }}>
                {uploadedMeta?.originalName || node.docName || elakayAutoText("h_e214b8a2992305", "Document")}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {sigs.length > 0 && (
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "11px", background: "rgba(255,255,255,0.15)", padding: "3px 10px", borderRadius: "12px" }}>
                  {sigs.length}{elakayAutoText("h_b6931b00fd5c40", "zone")}{sigs.length > 1 ? "s" : ""}{elakayAutoText("h_28868b2d63e390", "de signature")}</span>
              )}
              <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.12)", padding: "2px 8px", borderRadius: "10px" }}>{elakayAutoText("h_14876205a82dba", "aperçu")}{(node.viewStyle || "card") === "inline" ? elakayAutoText("h_24a1733ca8bb0a", "direct") : elakayAutoText("h_2ab0591dbcf5fe", "card")}
              </span>
            </div>
          </div>

          {/* Corps — toutes les pages du document */}
          <div style={{ position: "relative" }}>
            {!docHtml && !docFile ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 320, gap: "14px" }}>
                <div style={{ width: 64, height: 80, background: "#f1f5f9", borderRadius: "6px", border: "2px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FileText size={32} color="#cbd5e1" />
                </div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#94a3b8" }}>{elakayAutoText("h_301ff072a88847", "Aucun document chargé")}</div>
                <div style={{ fontSize: "12px", color: "#b4bfcc" }}>{elakayAutoText("h_09d27bf41abff7", "Cliquez sur «")}{node.buttonText || elakayAutoText("h_d9ff6a97b3a7f3", "Charger un document")} »</div>
              </div>
            ) : view === "html" && docHtml ? (
              <div style={{ padding: "40px 56px", maxHeight: "65vh", overflowY: "auto" }}
                dangerouslySetInnerHTML={{ __html: docHtml }}
              />
            ) : view === "office" ? (
              officeEmbedUrl ? (
                <iframe
                  title={elakayAutoText("h_8c091c803f0a48", "Word Live Preview")}
                  src={officeEmbedUrl}
                  style={{ width: "100%", height: "72vh", border: "none", background: "#fff" }}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: "10px", padding: "24px 32px" }}>
                  <FileText size={40} color="#2b5797" />
                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px", textAlign: "center" }}>{elakayAutoText("h_2492d32500fa0c", "Word Live indisponible")}</div>
                  <div style={{ fontSize: "12px", color: "#64748b", textAlign: "center", maxWidth: 520 }}>{elakayAutoText("h_ee4e05174d195c", "Cette vue exige une URL de document publiquement accessible. En local ou sur URL privée, utilisez la vue HTML/PDF ou téléchargez le fichier.")}</div>
                </div>
              )
            ) : view === "pdf" ? (
              pdfPreviewUrl ? (
                <iframe
                  title={elakayAutoText("h_2bddce78c1ce38", "PDF Preview")}
                  src={pdfPreviewUrl}
                  style={{ width: "100%", height: "72vh", border: "none", background: "#fff" }}
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: "10px", padding: "24px 32px" }}>
                  <FileText size={40} color="#dc2626" />
                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px", textAlign: "center" }}>{elakayAutoText("h_788ca1e3c7ea2d", "Vue PDF indisponible")}</div>
                  <div style={{ fontSize: "12px", color: "#64748b", textAlign: "center", maxWidth: 520 }}>{elakayAutoText("h_24fdbdce6e672d", "Aucun fichier PDF chargé pour l’aperçu.")}</div>
                </div>
              )
            ) : view === "word" || !docHtml ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 260, gap: "12px", padding: "32px" }}>
                <FileText size={44} color="#2b5797" />
                <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{uploadedMeta?.savedName || uploadedMeta?.originalName || node.docName || elakayAutoText("h_e214b8a2992305", "Document")}</div>
                <div style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center", maxWidth: 340 }}>{elakayAutoText("h_00ad55fec7484f", "Format natif — téléchargez le fichier pour l’ouvrir dans l’application adaptée")}</div>
              </div>
            ) : null}

            {/* ── Zones de signature (indicateurs visuels positionnés) ── */}
            {sigs.map((sig, i) => (
              <div key={sig.id} style={{ ...getSignPosStyleXY(sig.x ?? 75, sig.y ?? 85, sig.unit || "%"), pointerEvents: "none" }}>
                <div style={{ border: "2px dashed #4f46e5", borderRadius: "10px", padding: "8px 12px", background: "rgba(79,70,229,0.07)", textAlign: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", marginBottom: "2px" }}>
                    <PenLine size={12} color="#4f46e5" />
                    <span style={{ fontSize: "9px", fontWeight: 700, color: "#4f46e5" }}>{elakayAutoText("h_03efccb49f3ff5", "Zone")}{i + 1}</span>
                  </div>
                  <div style={{ fontSize: "10px", color: "#6366f1", lineHeight: 1.2 }}>{sig.label || elakayAutoText("h_2f32be1dc74166", "Signature")}</div>
                  {sig.required && <div style={{ fontSize: "8px", color: "#e11d48", marginTop: "2px", fontWeight: 700 }}>{elakayAutoText("h_389b678a374f13", "Obligatoire")}</div>}
                </div>
              </div>
            ))}
            {node.allowSign && sigs.length === 0 && (
              <div style={{ position: "absolute", bottom: 14, right: 14, pointerEvents: "none", opacity: 0.45 }}>
                <div style={{ border: "2px dashed #94a3b8", borderRadius: "10px", padding: "8px 12px", textAlign: "center" }}>
                  <PenLine size={13} color="#94a3b8" />
                  <div style={{ fontSize: "9px", color: "#94a3b8", marginTop: "2px" }}>{elakayAutoText("h_b6edb19e1cb5cb", "Ajoutez des zones →")}</div>
                </div>
              </div>
            )}
          </div>

          {/* Pied de page document */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 24px", borderTop: "1px solid #f1f5f9", background: "#fafafa" }}>
            <span style={{ fontSize: "10px", color: "#94a3b8", fontStyle: "italic" }}>{elakayAutoText("h_b3ad63128fffd7", "Document confidentiel")}</span>
            {uploadedMeta && <span style={{ fontSize: "10px", color: "#94a3b8" }}>{uploadedMeta.savedName}</span>}
          </div>
        </div>
      </div>

      {/* ── Aperçu de signature (builder — affiche un pad de prévisualisation) ── */}
      {node.allowSign && sigs.length > 0 && docFile && (
        <div style={{ border: "1.5px solid #e0daf5", borderRadius: "14px", overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", background: "linear-gradient(135deg,#f0eeff,#faf5ff)", borderBottom: "1px solid #e0daf5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <PenLine size={14} color="#6d28d9" />
              <strong style={{ fontSize: "12px", color: "#1e293b" }}>{elakayAutoText("h_de31b669d86bdf", "Aperçu signature (")}{sigs.length}{elakayAutoText("h_b6931b00fd5c40", "zone")}{sigs.length > 1 ? "s" : ""})</strong>
            </div>
            {node.signMode === "both" && (
              <div style={{ display: "flex", gap: "4px" }}>
                <button type="button" onClick={(e) => { e.stopPropagation(); setSignMode("draw"); }} style={{ ...tabBtn(signMode === "draw"), padding: "5px 12px", fontSize: "11px" }}>{elakayAutoText("h_b899438c00d983", "Dessiner")}</button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setSignMode("type"); }} style={{ ...tabBtn(signMode === "type"), padding: "5px 12px", fontSize: "11px" }}>{elakayAutoText("h_aa5dc005d3c6f0", "Taper")}</button>
              </div>
            )}
          </div>
          <div style={{ padding: "14px 16px" }}>
            {(node.signMode === "draw" || (node.signMode === "both" && signMode === "draw")) && (
              <SignaturePad onChange={(img) => setSignature((s) => ({ ...s, image: img }))} />
            )}
            {(node.signMode === "type" || (node.signMode === "both" && signMode === "type")) && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <input type="text" value={signature.name} onChange={(e) => setSignature((s) => ({ ...s, name: e.target.value }))} placeholder={elakayAutoText("h_1cf76756271089", "Votre nom complet")}
                  onClick={(e) => e.stopPropagation()}
                  style={{ padding: "10px 14px", fontSize: "14px", border: "1.5px solid #e0daf5", borderRadius: "9px", outline: "none" }} />
                <input type="date" value={signature.date} onChange={(e) => setSignature((s) => ({ ...s, date: e.target.value }))}
                  onClick={(e) => e.stopPropagation()}
                  style={{ padding: "10px 14px", fontSize: "14px", border: "1.5px solid #e0daf5", borderRadius: "9px", outline: "none" }} />
                {signature.name && (
                  <div style={{ padding: "14px 18px", fontSize: "26px", fontFamily: "'Brush Script MT',cursive", color: "#1e293b", borderTop: "1px solid #f0eeff" }}>
                    {signature.name}{signature.date ? ` — ${signature.date}` : ""}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Previews statiques pour les composants boutique live (dans l'éditeur)
// ---------------------------------------------------------------------------
function ShopVotePreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const filled = Math.round(o.defaultRating || 4);
  const max = o.maxStars || 5;
  const size = o.starSize || "28px";
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {Array.from({ length: max }).map((_, i) => (
          <span key={i} style={{ fontSize: size, color: i < filled ? "goldenRod" : "#d1d5db", lineHeight: 1 }}>★</span>
        ))}
        <span style={{ fontSize: "13px", color: "#6b7280", marginLeft: "6px" }}>{filled}/{max}</span>
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "4px", fontWeight: 600 }}>{elakayAutoText("h_ee0c9a5c8b712f", "🔗 auto-sync: store.products[0].Stars")}</div>
    </div>
  );
}

function ShopCarouselPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const placeholders = ["#dbeafe", "#d1fae5", "#fef3c7", "#fce7f3"];
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ display: "flex", gap: "12px", overflowX: "auto", padding: "8px 0 10px", scrollbarWidth: "thin" }}>
        {placeholders.map((bg, i) => (
          <div key={i} style={{ flexShrink: 0, width: "160px", borderRadius: "14px", overflow: "hidden", background: "#f3f4f6", border: "1px solid #e5e7eb" }}>
            <div style={{ height: "100px", background: bg }} />
            <div style={{ padding: "10px 12px" }}>
              <div style={{ height: "10px", background: "#e5e7eb", borderRadius: "4px", marginBottom: "6px" }} />
              <div style={{ height: "8px", background: "#f3f4f6", borderRadius: "4px", width: "70%" }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "4px", fontWeight: 600 }}>{elakayAutoText("h_f8d2fd56748b4d", "🔗 auto-sync: store.pubCategoryProdMatrix")}</div>
    </div>
  );
}

function ShopPricePreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "24px", fontWeight: 800, color: "#0073AA" }}>$25.00</span>
        <span style={{ fontSize: "16px", color: "#9ca3af", textDecoration: "line-through" }}>$30.00</span>
        <span style={{ padding: "3px 10px", background: "#fee2e2", color: "#ef4444", borderRadius: "6px", fontSize: "13px", fontWeight: 700 }}>-16%</span>
      </div>
      {(node.options?.showAddToCartButton !== false) && (
        <button style={{ marginTop: "10px", padding: "10px 24px", background: "#0073AA", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}>{elakayAutoText("h_7cd672bb87c550", "🛒 Ajouter au panier")}</button>
      )}
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "6px", fontWeight: 600 }}>{elakayAutoText("h_c3c87ce71d3caa", "🔗 auto-sync: store.products[0]")}</div>
    </div>
  );
}

function ShopCheckoutPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <button style={{ padding: "13px 28px", background: "#0073AA", color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
          🛒 {o.label || elakayAutoText("h_b6799d9a25e425", "Ajouter au panier")}
        </button>
        {o.showBuyNow !== false && (
          <button style={{ padding: "13px 28px", background: "#fff", color: "#0073AA", border: "2px solid #0073AA", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            ⚡ {o.labelBuy || elakayAutoText("h_af7dc34a918afa", "Acheter maintenant")}
          </button>
        )}
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "8px", fontWeight: 600 }}>{elakayAutoText("h_c6857ecd15f4cb", "🔗 auto-sync: store (products, owner, adresses)")}</div>
    </div>
  );
}

// ── Preview Carte Produit (Card.js) ──────────────────────────────────────────
function ShopCardPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <div style={{ border: "1px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", background: "#fff", boxShadow: "0 2px 12px rgba(0,115,153,0.07)", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        {/* Image produit */}
        <div style={{ position: "relative", background: "#f0f9ff", height: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: "80%", height: "80%", background: "linear-gradient(135deg,#dbeafe,#e0f2fe)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "40px" }}>📦</span>
          </div>
          {/* Badge promo */}
          <span style={{ position: "absolute", top: "10px", left: "10px", padding: "3px 8px", background: "#ef4444", color: "#fff", borderRadius: "6px", fontSize: "10px", fontWeight: 700 }}>-16%</span>
          {/* Overlay hover simulé */}
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,115,153,0.06)", display: "flex", alignItems: "flex-end", padding: "10px" }}>
            <span style={{ fontSize: "10px", color: "#0073AA", fontWeight: 600 }}>{elakayAutoText("h_33d5d8cad08f0d", "👁 Voir le produit")}</span>
          </div>
        </div>
        {/* Info */}
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a2e", marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{elakayAutoText("h_99bc1441961b7a", "Nom du produit")}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ fontSize: "16px", fontWeight: 800, color: "#0073AA" }}>$25.00</span>
              <span style={{ fontSize: "12px", color: "#9ca3af", textDecoration: "line-through", marginLeft: "6px" }}>$30.00</span>
            </div>
            <button style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#0073AA", color: "#fff", border: "none", fontSize: "18px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
          </div>
          {/* Stars */}
          <div style={{ fontSize: "12px", color: "#f59e0b", marginTop: "4px" }}>★★★★☆ <span style={{ color: "#9ca3af", fontSize: "11px" }}>{elakayAutoText("h_f6cb3fb999a96f", "(12 avis)")}</span></div>
        </div>
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "4px", fontWeight: 600 }}>{elakayAutoText("h_168ec29529b0b9", "🔗 auto-sync: store.products[0] · addItem · isAuthenticated")}</div>
    </div>
  );
}

// ── Preview ShowImage ─────────────────────────────────────────────────────────
function ShowImagePreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const src = o.liveArraySource || "store.products";
  const idx = o.liveProductIndex ?? 0;
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}), position: "relative" }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      {/* image placeholder */}
      <div style={{ width: "100%", paddingBottom: "100%", background: "linear-gradient(135deg,#dbeafe,#ede9fe)", borderRadius: "8px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px" }}>
          <ImageIcon size={28} color="#6d28d9" style={{ opacity: 0.45 }} />
          <span style={{ fontSize: "10px", color: "#6d28d9", fontWeight: 700 }}>{elakayAutoText("h_1988b016dbaa43", "ShowImage")}</span>
          <span style={{ fontSize: "9px", color: "#9b93b8", fontFamily: "ui-monospace,monospace" }}>{src}[{idx}]</span>
        </div>
        {/* action btn indicators */}
        {o.showB !== false && (
          <div style={{ position: "absolute", top: "6px", left: "6px", width: "22px", height: "22px", background: "#fff", borderRadius: "50%", border: "1px solid #e4dcfb", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Eye size={10} color="#6d28d9" />
          </div>
        )}
        {o.showShopV !== false && (
          <div style={{ position: "absolute", top: "6px", right: "6px", width: "22px", height: "22px", background: "#fff", borderRadius: "50%", border: "1px solid #e4dcfb", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Package size={10} color="#6d28d9" />
          </div>
        )}
      </div>
      <div style={{ fontSize: "10px", color: "#a78bfa", marginTop: "3px", fontWeight: 600 }}>
        🔗 {node.bindSource || "store.products.0"}
      </div>
    </div>
  );
}

// ── Preview Hero Slider (SliderPun135) ────────────────────────────────────────
function ShopSliderPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <div style={{ width: "100%", height: "220px", background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 60%,#0c4a6e 100%)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "10px", position: "relative", overflow: "hidden", borderRadius: "4px" }}>
        {/* Simulated slide images */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.25, display: "flex", gap: "2px" }}>
          <div style={{ flex: 1, background: "linear-gradient(180deg,#38bdf8,#0284c7)" }} />
          <div style={{ flex: 1, background: "linear-gradient(180deg,#0284c7,#0c4a6e)" }} />
          <div style={{ flex: 1, background: "linear-gradient(180deg,#0c4a6e,#0f172a)" }} />
        </div>
        <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <div style={{ fontSize: "10px", color: "#94a3b8", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: "6px" }}>{elakayAutoText("h_fb37c9e3d076f0", "HERO SLIDER")}</div>
          <div style={{ fontSize: "22px", fontWeight: 900, color: "#f0f9ff", lineHeight: 1.1 }}>{elakayAutoText("h_c0acd2c5befb35", "Nom de la Boutique")}</div>
          <div style={{ width: "48px", height: "3px", background: "#38bdf8", borderRadius: "2px", margin: "8px auto 6px" }} />
          <div style={{ fontSize: "11px", color: "#94a3b8" }}>{elakayAutoText("h_270a3bd9df4f38", "slide 1 / 3")}</div>
        </div>
        {/* Dots */}
        <div style={{ position: "absolute", bottom: "10px", left: 0, right: 0, display: "flex", justifyContent: "center", gap: "5px" }}>
          {[1,2,3].map((i) => <span key={i} style={{ width: i===1?"18px":"7px", height: "7px", borderRadius: "6px", background: i===1?"#38bdf8":"rgba(255,255,255,0.4)", transition: "width .3s" }} />)}
        </div>
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "4px", fontWeight: 600 }}>{elakayAutoText("h_d53921e4d420aa", "🔗 auto-sync: store.sliderShop · store.owner.shopName")}</div>
    </div>
  );
}

// ── Preview Grille Produits (ProductGridShop) ─────────────────────────────────
function ShopProductGridPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const COLORS = ["#dbeafe","#d1fae5","#fef3c7","#fce7f3","#e0e7ff","#dcfce7","#ffedd5","#f0f9ff"];
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      {/* Result bar */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", background: "linear-gradient(135deg,#f0f9ff,#e0f2fe)", borderRadius: "8px 8px 0 0", marginBottom: "6px" }}>
        <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#0073AA", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#fff", fontSize: "9px" }}>🛍</span>
        </div>
        <div style={{ height: "8px", background: "#bae6fd", borderRadius: "4px", width: "80px" }} />
        <div style={{ height: "8px", background: "#e0f2fe", borderRadius: "4px", width: "50px", marginLeft: "auto" }} />
      </div>
      {/* Product grid 4 cols */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "5px" }}>
        {COLORS.map((bg, i) => (
          <div key={i} style={{ borderRadius: "8px", overflow: "hidden", border: "1px solid #e5e7eb" }}>
            <div style={{ height: "52px", background: bg }} />
            <div style={{ padding: "5px 6px" }}>
              <div style={{ height: "6px", background: "#e5e7eb", borderRadius: "3px", width: "85%", marginBottom: "4px" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ height: "8px", background: "#bfdbfe", borderRadius: "3px", width: "45%" }} />
                <div style={{ width: "14px", height: "14px", borderRadius: "50%", background: "#0073AA" }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "center", gap: "4px", marginTop: "8px" }}>
        {["←","1","2","→"].map((l,i) => (
          <div key={i} style={{ width: i===1?"20px":"24px", height: "20px", borderRadius: "5px", background: i===1?"#0073AA":"#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "9px", color: i===1?"#fff":"#6b7280", fontWeight: 700 }}>{l}</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "6px", fontWeight: 600 }}>{elakayAutoText("h_7a02434168b7dc", "🔗 auto-sync: store.products · siseProduct")}</div>
    </div>
  );
}

function ShopHeaderSellerPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <div style={{ background: o.backgroundColor || "Snow", border: `1px solid ${o.borderColor || "#ece9f5"}`, borderRadius: "10px", boxShadow: `0 4px 8px ${o.shadowColor || "rgba(0,0,0,0.1)"}`, padding: "8px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ fontSize: o.shopNameFontSize || "20px", fontWeight: o.shopNameFontWeight || 700, color: o.shopNameColor || "#000000" }}>{elakayAutoText("h_2fd5d156d3cf5c", "LakayShop")}</div>
          <div style={{ flex: 1, height: "28px", borderRadius: "14px", border: `1px solid ${o.searchBorderColor || "#ccc"}`, background: o.searchBackgroundColor || "#fff", display: "flex", alignItems: "center", padding: "0 10px", color: o.searchTextColor || "#000000", fontSize: o.searchFontSize || "13px" }}>{elakayAutoText("h_6d7a30a931a35e", "Search...")}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px", color: o.menuColor || "#000000", fontSize: o.menuFontSize || "13px", fontWeight: o.menuFontWeight || 600 }}>
          <span>{elakayAutoText("h_70f8bb9a8a5393", "Home")}</span><span>{elakayAutoText("h_3ffb811f9be63c", "About us")}</span><span>{elakayAutoText("h_d7decf1aa22b02", "Filter")}</span>
          {o.myShop ? <span style={{ marginLeft: "auto", fontSize: "11px", color: o.modeActiveColor || "#6d28d9" }}>{elakayAutoText("h_e890b425b9f07e", "Boutique | Builder | Aperçu")}</span> : null}
        </div>
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "6px", fontWeight: 600 }}>{elakayAutoText("h_ac7cbca47afe84", "🔗 auto-sync: store.owner + storeActions (search/filter)")}</div>
    </div>
  );
}

// Previews pour shopCatMatrix et shopPubMatrix
function ShopCatMatrixPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const animLabel = o.type_Animation ? ` — 🎨 anim ${o.type_Animation}` : "";
  const PASTEL = ["#dbeafe","#d1fae5","#fef3c7","#fce7f3","#e0e7ff","#dcfce7"];
  const cardW = "168px";
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      {/* Header titre */}
      {o.inView !== false && o.title && (
        <div style={{ padding: "10px 4px 8px", marginBottom: "10px", borderBottom: "2px solid #e5e7eb" }}>
          <span style={{ fontSize: o.titleFontSize || "1.4rem", fontWeight: o.titleFontWeight || 700, color: o.titleColor || "#1f2937" }}>{o.title}</span>
        </div>
      )}
      {/* Slides */}
      <div style={{ display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "6px", scrollbarWidth: "thin" }}>
        {PASTEL.slice(0, 3).map((bg, i) => (
          <div key={i} style={{ flex: `0 0 ${cardW}`, borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", background: "#fff" }}>
            {/* Card header : nom catégorie + "Voir plus" */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 9px", background: "linear-gradient(135deg,#f8fafc,#f1f5f9)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ height: "9px", background: "#cbd5e1", borderRadius: "4px", width: "55%" }} />
              <span style={{ fontSize: "9px", color: "#007399", fontWeight: 700 }}>{elakayAutoText("h_70d8693d406839", "Voir plus →")}</span>
            </div>
            {/* Grille 2×2 produits */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px", background: "#e5e7eb" }}>
              {[bg, PASTEL[(i+1)%PASTEL.length], PASTEL[(i+2)%PASTEL.length], PASTEL[(i+3)%PASTEL.length]].map((c, j) => (
                <div key={j} style={{ height: "62px", background: c, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: "28px", height: "28px", background: "rgba(255,255,255,0.6)", borderRadius: "4px" }} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Scrollbar indicator */}
      <div style={{ display: "flex", justifyContent: "center", gap: "4px", marginTop: "6px" }}>
        {[0,1,2].map((i) => <div key={i} style={{ width: i===0?"20px":"6px", height: "4px", borderRadius: "3px", background: i===0?"#007399":"#cbd5e1" }} />)}
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "6px", fontWeight: 600 }}>{elakayAutoText("h_593da80e12c31b", "🔗 auto-sync: store.categories")}{animLabel}
      </div>
    </div>
  );
}

function ShopPubMatrixPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  const o = node.options || {};
  const animLabel = o.type_Animation ? ` — 🎨 anim ${o.type_Animation}` : "";
  const COLORS = ["#fef3c7","#dbeafe","#d1fae5","#fce7f3"];
  const NAMES = ["Daphn laptop","Bluetooth earbuds","Allay polo"];
  const cardW = "155px";
  return (
    <div style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}>
      {/* Header titre */}
      {o.inView !== false && o.title && (
        <div style={{ padding: "10px 4px 8px", marginBottom: "10px", borderBottom: "2px solid #e5e7eb" }}>
          <span style={{ fontSize: o.titleFontSize || "1.4rem", fontWeight: o.titleFontWeight || 700, color: o.titleColor || "#1f2937" }}>{o.title}</span>
        </div>
      )}
      {/* Slides produits (prodCategory mode) */}
      <div style={{ display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "6px", scrollbarWidth: "thin" }}>
        {COLORS.slice(0, 3).map((bg, i) => (
          <div key={i} style={{ flex: `0 0 ${cardW}`, borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", background: "#fff" }}>
            {/* Card header : nom produit + "See More →" */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 9px", background: "linear-gradient(135deg,#f8fafc,#f1f5f9)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <span style={{ fontSize: "9px", fontWeight: 700, color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{NAMES[i]}</span>
              <span style={{ fontSize: "8px", color: "#007399", fontWeight: 700, marginLeft: "4px", flexShrink: 0 }}>{elakayAutoText("h_2d862a526343e4", "See More →")}</span>
            </div>
            {/* Grille 2×2 : 1 vrai produit + 3 pastels */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px", background: "#e5e7eb" }}>
              <div style={{ height: "58px", background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: "32px", height: "32px", background: "rgba(255,255,255,0.7)", borderRadius: "5px" }} />
              </div>
              {[COLORS[(i+1)%4], COLORS[(i+2)%4], COLORS[(i+3)%4]].map((c, j) => (
                <div key={j} style={{ height: "58px", background: c, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: "22px", height: "22px", background: "rgba(255,255,255,0.5)", borderRadius: "3px" }} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Scrollbar indicator */}
      <div style={{ display: "flex", justifyContent: "center", gap: "4px", marginTop: "6px" }}>
        {[0,1,2].map((i) => <div key={i} style={{ width: i===0?"20px":"6px", height: "4px", borderRadius: "3px", background: i===0?"#007399":"#cbd5e1" }} />)}
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "6px", fontWeight: 600 }}>{elakayAutoText("h_f8d2fd56748b4d", "🔗 auto-sync: store.pubCategoryProdMatrix")}{animLabel}
      </div>
    </div>
  );
}

// ── Preview ButtonPagination ─────────────────────────────────────────────────
function ShopButtonPaginationPreview({ node, editable, onSelect, isSelected }) {
  const ns = effStyle(node, useContext(DeviceContext));
  return (
    <div
      style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }}
      onClick={editable ? (e) => { e.stopPropagation(); onSelect(node.id, e.shiftKey); } : undefined}
    >
      <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", background: "#f5f3ff", borderRadius: "10px", padding: "6px 14px", border: "1px solid #e4dcfb" }}>
          {/* ← Précédent (désactivé) */}
          <div style={{ padding: "6px 20px", borderRadius: "6px", background: "#e5e7eb", color: "#9ca3af", fontSize: "13px", fontWeight: 600 }}>{elakayAutoText("h_471492a97f49a3", "← Previous")}</div>
          {/* → Suivant (actif) */}
          <div style={{ padding: "6px 20px", borderRadius: "6px", background: "#6d28d9", color: "#fff", fontSize: "13px", fontWeight: 600 }}>{elakayAutoText("h_2f04eb152234ce", "Next →")}</div>
        </div>
      </div>
      <div style={{ fontSize: "11px", color: "#a78bfa", marginTop: "4px", fontWeight: 600, textAlign: "center" }}>{elakayAutoText("h_bfb20f712afc59", "🔗 auto-sync: storeState (skip · limit · size · siseProduct) · loadMore · loadMore0")}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Moteur de rendu : JSON -> éléments React
// ---------------------------------------------------------------------------
function CollapsibleTextContent({ node }) {
  const [expanded, setExpanded] = useState(false);
  const text = String(node?.text || "");
  const cfg = getTextCollapseConfig(node);
  const shouldCollapse = canUseTextCollapse(node) && cfg.enabled && text.length > cfg.maxChars;

  useEffect(() => {
    setExpanded(false);
  }, [node?.id, text, cfg.maxChars, cfg.enabled]);

  if (!shouldCollapse) return text;

  const controlStyle = cfg.controlType === "button"
    ? {
        marginLeft: "0.5em",
        padding: "2px 8px",
        border: "1px solid currentColor",
        borderRadius: "999px",
        background: "transparent",
        color: cfg.controlColor || "inherit",
        fontFamily: "inherit",
        fontSize: cfg.controlFontSize || "inherit",
        fontWeight: 700,
        cursor: "pointer",
      }
    : {
        marginLeft: "0.35em",
        padding: 0,
        border: "none",
        background: "transparent",
        color: cfg.controlColor || "inherit",
        fontFamily: "inherit",
        fontSize: cfg.controlFontSize || "inherit",
        fontWeight: 700,
        textDecoration: "underline",
        cursor: "pointer",
      };

  return (
    <>
      {expanded ? text : truncateTextByChars(text, cfg.maxChars)}
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setExpanded((value) => !value);
        }}
        style={controlStyle}
        title={expanded ? cfg.seeLessLabel : cfg.seeMoreLabel}
      >
        {expanded ? cfg.seeLessLabel : cfg.seeMoreLabel}
      </button>
    </>
  );
}

function RenderNodeInner({ node, selectedIds, onSelect, editable }) {
  const device = useContext(DeviceContext);
  const dataCtx = useContext(DataContext);
  const sourceData = dataCtx?.boundData?.data;
  const sourceKey = dataCtx?.boundData?.key;

  // --- RÉPÉTITION : si ce nœud a un repeatBind, on duplique sur les données ---
  const rb = node.repeatBind;
  if (rb && rb.source && rb.path) {
    if (sourceData && sourceKey === rb.source) {
      let arr = resolvePath(sourceData, rb.path);
      if (Array.isArray(arr) && arr.length > 0) {
        // PAGINATION : si activée, ne garder que la page courante.
        // En édition on garde le gabarit (1er item) ; en aperçu, la 1re page de pageSize items.
        if (rb.paginate) {
          const ps = Math.max(1, rb.pageSize || 12);
          arr = arr.slice(0, ps);
        }
        const itemsToRender = editable ? [arr[0]] : arr;
        const rendered = itemsToRender.map((item, idx) => {
          if (rb.mode === "children") {
            const filledRoot = fillNodeWithItem(
              cloneNodeFresh({ ...node, children: [] }),
              item,
              rb.fieldMap,
              rb.autoFill !== false,
              sourceData,
              sourceKey
            );
            const filled = {
              ...filledRoot,
              children: (node.children || []).map((c) =>
                fillNodeWithItem(cloneNodeFresh(c), item, rb.fieldMap, rb.autoFill !== false, sourceData, sourceKey)
              ),
              repeatBind: null,
            };
            return <RenderNode key={`rep-${node.id}-${idx}`} node={filled} selectedIds={selectedIds} onSelect={onSelect} editable={editable && idx === 0} />;
          }
          const filled = fillNodeWithItem(cloneNodeFresh(node), item, rb.fieldMap, rb.autoFill !== false, sourceData, sourceKey);
          filled.repeatBind = null;
          return <RenderNode key={`rep-${node.id}-${idx}`} node={filled} selectedIds={selectedIds} onSelect={onSelect} editable={editable && idx === 0} />;
        });
        if (editable) {
          return (
            <div style={{ position: "relative", border: "1px dashed #c9b8f5", borderRadius: "8px", padding: "8px" }}>
              <span style={{ position: "absolute", top: "-10px", left: "10px", fontSize: "10px", fontWeight: 700, color: "#6d28d9", background: "#fff", padding: "1px 8px", borderRadius: "6px", border: "1px solid #e4dcfb", zIndex: 2 }}>{elakayAutoText("h_4b0302e9c2e130", "RÉPÉTITION sur")}{rb.source}.{rb.path} ({arr.length}{rb.paginate ? ` / page de ${rb.pageSize || 12}` : ""}{elakayAutoText("h_28bd16c880dfb1", "entrées — gabarit visible)")}</span>
              {rendered}
            </div>
          );
        }
        return <>{rendered}</>;
      }
    }
  }

  // --- bindKey HORS RÉPÉTITION : si l'élément a un bindKey absolu (avec la racine),
  // on remplit la valeur depuis la source globale. Si bindKey est null/absent, le texte saisi reste.
  if (node.bindKey && sourceData && sourceKey) {
    const key = node.bindKey;
    if (key === sourceKey || key.startsWith(sourceKey + ".")) {
      const val = resolvePath(sourceData, key.slice(sourceKey.length + 1));
      if (val != null) {
        const replaced = { ...node, bindKey: null };
        if (isImageNode(node)) {
          const src = mediaString(val);
          if (!src) return null;
          replaced.src = src;
        }
        else if (node.type === "button" || node.type === "link") {
          replaced.text = String(val);
          if (typeof val === "string" && /^https?:\/\//.test(val)) replaced.href = String(val);
        }
        else if (node.type === "input" || node.type === "textarea") replaced.field = { ...(node.field || {}), value: String(val) };
        else if (node.type === "select") replaced.options = { ...(node.options || {}), choices: Array.isArray(val) ? val.map(String) : [String(val)] };
        else if (isVideoNode(node)) {
          const url = mediaString(val);
          if (!url) return null;
          replaced.videoUrl = url;
          replaced.options = { ...(node.options || {}), url };
        }
        else replaced.text = String(val);
        return <RenderNode node={replaced} selectedIds={selectedIds} onSelect={onSelect} editable={editable} />;
      }
    }
  }

  let ns = { ...effStyle(node, device), ...parseConsoleCssToReactStyle(node.consoleCss) };
  // Applique l'animation choisie (apparition ou continue) à n'importe quel élément
  if (node.animation && node.animation !== "none") {
    if (node.animation === "custom") {
      const c = buildCustomAnim(node);
      ns = { ...ns, animation: c.css };
    } else if (ANIMATIONS[node.animation]?.css) {
      ns = { ...ns, animation: ANIMATIONS[node.animation].css,
        ...(node.animDelay ? { animationDelay: node.animDelay } : {}),
        ...(node.animDuration ? { animationDuration: node.animDuration } : {}) };
    }
  }
  const Tag = node.tag || ELEMENT_TYPES[node.type]?.tag || "div";
  const isSelected = editable && selectedIds?.includes(node.id);
  const def = ELEMENT_TYPES[node.type];

  const handleClick = (e) => { if (!editable) return; e.stopPropagation(); onSelect(node.id, e.shiftKey); };

  if (node.type === "swiper") {
    // Si bindSource est défini, résoudre les slides depuis les données live
    let swiperNode = node;
    if (node.bindSource && sourceData && sourceKey) {
      const bPath = node.bindSource.startsWith(sourceKey + ".")
        ? node.bindSource.slice(sourceKey.length + 1) : node.bindSource;
      const slideArr = resolvePath(sourceData, bPath);
      if (Array.isArray(slideArr) && slideArr.length > 0) {
        swiperNode = { ...node, bindSource: null, items: slideArr.map((s, i) => ({
          id: s._id || `ds-${i}`,
          image: s.urlImg || s.image || s.url || s.imgUrl || "",
          title: s.title || s.caption || "",
          text:  s.description || s.subtitle || "",
        })) };
      }
    }
    return <Swiper node={swiperNode} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  }
  if (node.type === "card")
    return <Card node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (node.type === "textGroup")
    return <TextGroup node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  // ── Composants boutique live (previews statiques dans l'éditeur) ──
  if (def?.isShopVote)      return <ShopVotePreview      node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopCarousel)  return <ShopCarouselPreview  node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopPrice)     return <ShopPricePreview     node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopCheckout)  return <ShopCheckoutPreview  node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopCard)          return <ShopCardPreview          node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopCatMatrix)          return <ShopCatMatrixPreview          node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopPubMatrix)          return <ShopPubMatrixPreview          node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopSlider)             return <ShopSliderPreview             node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopProductGrid)        return <ShopProductGridPreview        node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopButtonPagination)   return <ShopButtonPaginationPreview   node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShopHeaderSeller)  return <ShopHeaderSellerPreview  node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isShowImage)         return <ShowImagePreview         node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isField)
    return <FormField node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isCheckbox || def?.isRadio)
    return <CheckboxField node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} radio={def?.isRadio} />;
  if (def?.isOutput)
    return <Tag style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }} onClick={handleClick}>{node.content?.text}</Tag>;
  if (def?.isVideo)
    return <VideoBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isElakaySwiper)
    return <ElakaySwiperNode node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isIcon)
    return <IconBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isQuote)
    return <QuoteBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isStat)
    return <StatBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isProgress)
    return <ProgressBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isRating)
    return <RatingBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isPriceBlock)
    return <PriceBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isBadge)
    return <BadgeBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isAccordion)
    return <AccordionBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isModal)
    return <ModalBlock node={node} selectedIds={selectedIds} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isDropdown)
    return <DropdownBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isTabs)
    return <TabsBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isOffcanvas)
    return <OffcanvasBlock node={node} selectedIds={selectedIds} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isTooltip)
    return <TooltipBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isDocViewer)
    return <DocViewerBlock node={node} editable={editable} onSelect={onSelect} isSelected={isSelected} />;
  if (def?.isDivider)
    return <hr style={{ ...ns, ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}) }} onClick={handleClick} />;

  const isGrid = def?.isGrid;
  // Résoudre la config grid responsive selon le device actuel du canvas
  const _dgConfig = isGrid && node.options?.deviceGrid?.length
    ? resolveDeviceGridConfig(node.options.deviceGrid, device)
    : null;
  const _gridCols = _dgConfig?.columns ?? node.options?.columns ?? 3;
  const _gridGap  = _dgConfig?.gap ?? ns?.gap;
  const wrapperStyle = {
    ...ns,
    ...(isGrid ? { display: "grid", gridTemplateColumns: `repeat(${_gridCols}, minmax(0, 1fr))`, ...(_gridGap ? { gap: _gridGap } : {}) } : {}),
    ...(isSelected ? { outline: "2px solid #6d28d9", outlineOffset: "2px" } : {}),
    ...(editable && def?.isContainer && (!node.children || node.children.length === 0)
      ? { minHeight: "60px", border: ns?.border || "1px dashed #c9c2e0", display: isGrid ? "grid" : ns?.display } : {}),
  };

  if (def?.isContainer) {
    return (
      <Tag style={wrapperStyle} onClick={handleClick}>
        {def?.isFieldset && node.legend && <legend style={{ fontSize: "13px", fontWeight: 700, color: "#6d28d9", padding: "0 6px" }}>{node.legend}</legend>}
        {node.children?.map((c) => (
          <RenderNode key={c.id} node={c} selectedIds={selectedIds} onSelect={onSelect} editable={editable} />
        ))}
        {editable && (!node.children || node.children.length === 0) && (
          <span style={{ color: "#9b93b8", fontSize: "13px", fontStyle: "italic", gridColumn: isGrid ? "1 / -1" : undefined }}>
            {isGrid ? elakayAutoText("h_27f175947b8276", "Grille vide — déposez-y des cartes, groupes de texte, images…") : elakayAutoText("h_97e090336ed8b2", "Conteneur vide — ajoutez des éléments via le panneau de gauche")}
          </span>
        )}
      </Tag>
    );
  }

  switch (node.type) {
    case "image": {
      const src = mediaString(node.src);
      if (node._hideWhenEmptyMedia || (node.bindKey && !src)) return null;
      return <img src={src} alt={node.alt} style={wrapperStyle} onClick={handleClick} />;
    }
    case "button": {
      const fallbackShopHref = sourceKey === "store" ? normalizeHrefLikeValue(resolvePath(sourceData, "links.shopPage")) : "";
      const meta = resolveNodeHrefTarget(node.href || fallbackShopHref, node.target);
      const isCartBtn = editable && node.clickActionFunction === "addToCart";
      return editable
        ? <span style={{ ...wrapperStyle, position: "relative" }} onClick={handleClick} role="button">
            {node.text}
            {isCartBtn && (
              <span title={elakayAutoText("h_4cb2e9bcc2d75c", "addToCart — produit courant passé automatiquement")}
                style={{ position:"absolute", top:"-7px", right:"-7px", fontSize:"8px", background:"#059669",
                  color:"#fff", borderRadius:"50%", width:"16px", height:"16px", display:"flex",
                  alignItems:"center", justifyContent:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.25)",
                  zIndex:10, pointerEvents:"none", lineHeight:1 }}>
                🛒
              </span>
            )}
          </span>
        : <a href={meta.href} style={wrapperStyle} onClick={handleClick} role="button">{node.text}</a>;
    }
    case "link": {
      const fallbackShopHref = sourceKey === "store" ? normalizeHrefLikeValue(resolvePath(sourceData, "links.shopPage")) : "";
      const meta = resolveNodeHrefTarget(node.href || fallbackShopHref, node.target);
      return editable
        ? <span style={wrapperStyle} onClick={handleClick}>{node.text}</span>
        : <a href={meta.href} style={wrapperStyle} onClick={handleClick}>{node.text}</a>;
    }
    case "spacer": return <Tag style={wrapperStyle} onClick={handleClick} />;
    default: return (
      <Tag style={wrapperStyle} onClick={handleClick}>
        {canUseTextCollapse(node) ? <CollapsibleTextContent node={node} /> : node.text}
      </Tag>
    );
  }
}

function RenderNode(props) {
  const { node } = props;
  // Preserve legacy `consoleJs` data, but never execute stored source in the
  // application origin. Purpose-built React interactions remain available.
  return (
    <div data-pb-node-id={node?.id} style={{ display: "contents" }}>
      <RenderNodeInner {...props} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Champs d'édition génériques
// ---------------------------------------------------------------------------
function StyleField({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div style={{ display: "block", marginBottom: "10px" }}>
      <span style={fieldLabel}>{label}</span>
      <div style={{ display: "flex", gap: "6px", marginTop: "4px", alignItems: "center" }}>
        {type === "color" && (
          <input type="color" value={/^#[0-9a-f]{6}$/i.test(value || "") ? value : "#000000"}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: "34px", height: "34px", padding: 0, border: "1px solid #e4e0f0", borderRadius: "8px", cursor: "pointer", background: "none" }} />
        )}
        <input type="text" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", outline: "none", color: "#2a2547", background: "#faf9fd" }} />
      </div>
    </div>
  );
}

// Champ de sélection dans la bibliothèque d'icônes partagée (iconKit) —
// aperçu de l'icône courante + bouton pour ouvrir/fermer le picker inline.
function IconLibraryField({ element, setContent }) {
  const [open, setOpen] = useState(false);
  const current = getRegistryIcon(element.content?.iconId);
  return (
    <div style={{ marginBottom: "10px" }}>
      <span style={fieldLabel}>{elakayAutoText("h_icon_library", "Bibliothèque d'icônes")}</span>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
        <div style={{
          width: "34px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center",
          border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#6d28d9", flexShrink: 0,
        }}>
          {current ? <current.Component size={18} /> : <Star size={16} color="#c9c2e0" />}
        </div>
        <span style={{ flex: 1, fontSize: "12px", color: current ? "#2a2547" : "#9b93b8" }}>
          {current ? current.label : elakayAutoText("h_icon_none", "Aucune — symbole personnalisé ci-dessous")}
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)}
          style={{ padding: "6px 10px", fontSize: "11px", fontWeight: 700, borderRadius: "8px", cursor: "pointer",
            border: "1px solid #c9b8f5", background: open ? "#6d28d9" : "#f5f3ff", color: open ? "#fff" : "#6d28d9" }}>
          {open ? elakayAutoText("h_icon_close", "Fermer") : elakayAutoText("h_icon_choose", "Choisir")}
        </button>
        {current && (
          <button type="button" onClick={() => setContent("iconId", null)} title={elakayAutoText("h_icon_clear", "Retirer")}
            style={{ padding: "6px 8px", fontSize: "11px", borderRadius: "8px", cursor: "pointer", border: "1px solid #ece9f5", background: "#fff", color: "#9b1c1c" }}>
            <X size={13} />
          </button>
        )}
      </div>
      {open && (
        <div style={{ marginTop: "8px", padding: "8px", border: "1px solid #ece9f5", borderRadius: "10px", background: "#fff" }}>
          <IconPicker value={element.content?.iconId} onSelect={(iconId) => { setContent("iconId", iconId); setOpen(false); }} />
        </div>
      )}
    </div>
  );
}

// Champ "texte" qui bascule automatiquement entre <input> et <textarea> selon le volume de texte
function AutoTextField({ label, value, onChange, placeholder, threshold = 60 }) {
  const text = value || "";
  const isLong = text.length > threshold || text.includes("\n");
  const fieldStyle = { width: "100%", marginTop: "4px", padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", outline: "none", color: "#2a2547", background: "#faf9fd", fontFamily: "inherit" };
  return (
    <div style={{ display: "block", marginBottom: "10px" }}>
      <span style={fieldLabel}>{label}</span>
      {isLong ? (
        <textarea value={text} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={4}
          style={{ ...fieldStyle, resize: "vertical", lineHeight: 1.5 }} />
      ) : (
        <input type="text" value={text} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={fieldStyle} />
      )}
    </div>
  );
}

// Champ source de média (URL) avec aperçu, rafraîchissement et téléchargement du fichier
function MediaUrlField({ label, value, onChange, onMediaSelect, placeholder, kind = "image" }) {
  const [, setCacheBust] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleDownload = async () => {
    if (!value) return;
    try {
      const res = await fetch(value, { cache: "no-store" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = value.split("/").pop()?.split("?")[0] || "fichier";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(value, "_blank", "noopener,noreferrer");
    }
  };

  const uploadToEditor = async (f) => {
    if (!f) return;
    setUploading(true);
    setUploadError("");
    try {
      // The media upload is reserved to editor roles: send the session token.
      // Checked / compressed before sending; readable errors (incl. proxy 413).
      const { token: mediaToken } = isAuthenticated() || {};
      const data = await uploadFileRequest({
        url: `${elakay_API}/media/upload`,
        feature: "media",
        file: f,
        headers: mediaToken ? { Authorization: `Bearer ${mediaToken}` } : {},
        throwOnError: true,
      });
      if (data.url) {
        return { ...data, url: data.url, mimeType: data.mimeType || f.type, originalName: f.name };
      } else {
        throw new Error(elakayAutoText("h_8c713119be291a", "Erreur upload"));
      }
    } catch (err) {
      setUploadError(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const selectMedia = (media) => {
    const nextUrl = media?.url || media?.path || "";
    if (!nextUrl) return;
    onChange(nextUrl);
    onMediaSelect?.(media);
    setCacheBust(Date.now());
  };

  return (
    <div style={{ display: "block", marginBottom: "10px" }}>
      <span style={fieldLabel}>{label}</span>
      <div style={{ display: "flex", gap: "6px", marginTop: "4px", alignItems: "center", marginBottom: "8px" }}>
        <input type="text" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", outline: "none", color: "#2a2547", background: "#faf9fd" }} />
        <button type="button" onClick={() => setCacheBust(Date.now())} disabled={!value}
          title={elakayAutoText("h_4f3a1f3a3b8a01", "Rafraîchir")}
          style={{ ...padBtn, width: "34px", flexShrink: 0, opacity: value ? 1 : 0.45, cursor: value ? "pointer" : "not-allowed" }}>
          <RefreshCw size={14} />
        </button>
        <button type="button" onClick={handleDownload} disabled={!value}
          title={elakayAutoText("h_b332d06dcdd0b2", "Télécharger")}
          style={{ ...padBtn, width: "34px", flexShrink: 0, opacity: value ? 1 : 0.45, cursor: value ? "pointer" : "not-allowed" }}>
          <Download size={14} />
        </button>
      </div>
      <UniversalMediaPicker
        compact
        kind={kind}
        value={value}
        onUpload={uploadToEditor}
        onSelect={selectMedia}
      />
      {uploading && <p style={{ fontSize: "11px", color: "#9b93b8", margin: "4px 0 0" }}>{elakayAutoText("h_e7c1f5e6667fe2b", "Envoi vers le serveur…")}</p>}
      {uploadError && <p style={{ fontSize: "11px", color: "#e11d48", margin: "4px 0 0" }}>{uploadError}</p>}
    </div>
  );
}
function CheckField({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "9px", marginBottom: "8px", cursor: "pointer", userSelect: "none" }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} style={{ display: "none" }} />
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: "6px", flexShrink: 0, transition: "all 0.15s",
        background: checked ? "#6d28d9" : "#f0eeff",
        border: `1.5px solid ${checked ? "#6d28d9" : "#d1cce8"}`,
      }}>
        {checked ? <Check size={13} color="#fff" /> : <X size={13} color="#c4bde0" />}
      </span>
      <span style={{ fontSize: "13px", color: checked ? "#1e293b" : "#7a73a0", fontWeight: checked ? 600 : 400, transition: "color 0.15s" }}>
        {label}
      </span>
    </label>
  );
}

// Champ avec menu de valeurs courantes + saisie libre (combine guidé et liberté totale)
function SelectOrFree({ label, value, onChange, choices = [], placeholder }) {
  const known = choices.includes(value);
  return (
    <label style={{ display: "block", marginBottom: "10px" }}>
      <span style={fieldLabel}>{label}</span>
      <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
        <select value={known ? value : ""} onChange={(e) => e.target.value && onChange(e.target.value)}
          style={{ ...selectStyle, marginTop: 0, flex: "0 0 92px", padding: "8px 6px" }}>
          <option value="">—</option>
          {choices.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="text" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", outline: "none", color: "#2a2547", background: "#faf9fd" }} />
      </div>
    </label>
  );
}

// ── Options communes pour les arguments d'actions au clic ────────────────────
const CLICK_ARG_ITEM_KEYS = [
  { value: "items.categoryID",  get label() { return elakayAutoText("h_5f36eec8cc604b", "items.categoryID — ID catégorie (mapping)"); } },
  { value: "items.categoryName", get label() { return elakayAutoText("h_e8a4c75bbfd764", "items.categoryName — Nom catégorie (mapping)"); } },
  { value: "items.cateryName",   get label() { return elakayAutoText("h_1c49425b0abd36", "items.cateryName — Ancien alias du nom catégorie"); } },
  { value: "items._id",         get label() { return elakayAutoText("h_16bd0c47f57cc8", "items._id — ID produit (mapping)"); } },
  { value: "items.name",        get label() { return elakayAutoText("h_3ee35585de291f", "items.name — Nom produit (mapping)"); } },
  { value: "items.price",       get label() { return elakayAutoText("h_77c90b4319a8d0", "items.price — Prix (mapping)"); } },
  { value: "items.Stars",       get label() { return elakayAutoText("h_54bd732ebc89de", "items.Stars — Note (mapping)"); } },
  { value: "items.ProductImageUrls.0", get label() { return elakayAutoText("h_250558debe1c37", "items.ProductImageUrls — Image URL (mapping)"); } },
];
const CLICK_ARG_URL_KEYS = [
  { value: "store.links.shopPage",       get label() { return elakayAutoText("h_a99c685aa3d756", "store.links.shopPage — URL boutique"); } },
  { value: "store.links.productDetail",  get label() { return elakayAutoText("h_c84cb9bfffad29", "store.links.productDetail — URL produit (base)"); } },
  { value: "store.links.myShop",         get label() { return elakayAutoText("h_35bf71fa91ae37", "store.links.myShop — Mon espace"); } },
  { value: "store.owner._id",            get label() { return elakayAutoText("h_96bf7ac64a6fbb", "store.owner._id — ID vendeur"); } },
  { value: "store.products.0._id",       get label() { return elakayAutoText("h_72560babfcbf9d", "store.products[0]._id — ID 1er produit"); } },
];

// Extrait les noms d'arguments depuis la signature de la fonction
function parseArgNames(sig = "") {
  const m = sig.match(/\(([^)]*)\)/);
  if (!m || !m[1].trim()) return [];
  return m[1].split(",").map(a => a.trim().replace(/[?]/g, "").trim()).filter(Boolean);
}

// Champ argument avec bascule select ↔ saisie libre
function ArgInput({ label, value, onChange, selectOptions = [], placeholder = "" }) {
  const [mode, setMode] = useState(() =>
    value && selectOptions.some(o => o.value === value) ? "select" : "input"
  );
  const inpStyle = { width: "100%", padding: "8px 10px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", outline: "none", color: "#2a2547", background: "#faf9fd", fontFamily: "ui-monospace,monospace" };
  return (
    <div style={{ marginBottom: "10px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3px" }}>
        <span style={fieldLabel}>{label}</span>
        <button
          onClick={() => setMode(m => m === "select" ? "input" : "select")}
          style={{ fontSize: "9px", padding: "1px 7px", border: "1px solid #e4dcfb", borderRadius: "5px", color: "#6d28d9", background: "#f3effe", cursor: "pointer", fontWeight: 600 }}
        >
          {mode === "select" ? elakayAutoText("h_3067ca60e857e7", "✏ Saisir") : elakayAutoText("h_2206f1f74bc2d6", "☰ Choisir")}
        </button>
      </div>
      {mode === "select" ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...selectStyle, fontFamily: "ui-monospace,monospace", fontSize: "11px" }}
        >
          <option value="">{elakayAutoText("h_3e5e6213d2bede", "— sélectionner —")}</option>
          <optgroup label={elakayAutoText("h_8388e1098b5e6e", "Contexte mapping (item courant)")}>
            {CLICK_ARG_ITEM_KEYS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </optgroup>
          <optgroup label={elakayAutoText("h_a853946bf956d6", "Store global")}>
            {ALL_STORE_KEYS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </optgroup>
          {selectOptions.filter(o => !CLICK_ARG_ITEM_KEYS.some(k => k.value === o.value) && !ALL_STORE_KEYS.some(k => k.value === o.value)).length > 0 && (
            <optgroup label={elakayAutoText("h_2f0dd0426a8911", "Autres")}>
              {selectOptions.filter(o => !CLICK_ARG_ITEM_KEYS.some(k => k.value === o.value) && !ALL_STORE_KEYS.some(k => k.value === o.value)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </optgroup>
          )}
        </select>
      ) : (
        <input
          type="text"
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={inpStyle}
        />
      )}
      {value && (
        <div style={{ fontSize: "9px", color: "#9b93b8", marginTop: "2px", fontFamily: "ui-monospace,monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          ↳ {value}
        </div>
      )}
    </div>
  );
}

// Éditeur d'une propriété "raccourcie" en 4 côtés (padding, margin, border-radius, border-width)
function SideField({ label, parse, build, value, onChange }) {
  const sides = parse(value);
  const set = (k, v) => onChange(build({ ...sides, [k]: v }));
  const box = { width: "100%", padding: "7px 6px", fontSize: "12px", textAlign: "center", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" };
  return (
    <div style={{ marginBottom: "12px" }}>
      <span style={fieldLabel}>{label}</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "5px", marginTop: "4px" }}>
        {["top", "right", "bottom", "left"].map((k) => (
          <div key={k}>
            <input type="text" value={sides[k] || ""} placeholder={k[0].toUpperCase()} onChange={(e) => set(k, e.target.value)} style={box} />
            <div style={{ fontSize: "9px", color: "#b3aacb", textAlign: "center", marginTop: "2px" }}>{({ top: "Haut", right: "Droite", bottom: "Bas", left: "Gauche" })[k]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
// Helpers parse/build pour les valeurs à 4 côtés (ex. "10px 20px" -> {top,right,bottom,left})
function parseBox(v = "") {
  const p = String(v).trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return {};
  if (p.length === 1) return { top: p[0], right: p[0], bottom: p[0], left: p[0] };
  if (p.length === 2) return { top: p[0], right: p[1], bottom: p[0], left: p[1] };
  if (p.length === 3) return { top: p[0], right: p[1], bottom: p[2], left: p[1] };
  return { top: p[0], right: p[1], bottom: p[2], left: p[3] };
}
function buildBox(s) {
  const t = s.top || "0", r = s.right || "0", b = s.bottom || "0", l = s.left || "0";
  if (t === r && r === b && b === l) return t;
  return `${t} ${r} ${b} ${l}`;
}

// Modèle de positionnement rapide (grille 3×3). Valeurs % (par défaut) et px.
const QUICK_POS = [
  { label: "↖", x: 12, y: 12 }, { label: "↑", x: 50, y: 12 }, { label: "↗", x: 88, y: 12 },
  { label: "←", x: 12, y: 50 }, { label: "·", x: 50, y: 50 }, { label: "→", x: 88, y: 50 },
  { label: "↙", x: 12, y: 88 }, { label: "↓", x: 50, y: 88 }, { label: "↘", x: 88, y: 88 },
];
// Positions équivalentes en pixels (coins/bords avec marge, centre approximatif)
const QUICK_POS_PX = [
  { label: "↖", x: 24, y: 24 }, { label: "↑", x: 300, y: 24 }, { label: "↗", x: 560, y: 24 },
  { label: "←", x: 24, y: 240 }, { label: "·", x: 300, y: 240 }, { label: "→", x: 560, y: 240 },
  { label: "↙", x: 24, y: 460 }, { label: "↓", x: 300, y: 460 }, { label: "↘", x: 560, y: 460 },
];

// Tailles d'élément prédéfinies (largeur, parfois hauteur)
const SIZE_PRESETS = [
  { get label() { return elakayAutoText("h_7c970f6bd1bfdd", "Pleine"); }, w: "100%" },
  { get label() { return elakayAutoText("h_f277a35a12642f", "Moitié"); }, w: "50%" },
  { get label() { return elakayAutoText("h_435080ba669267", "Tiers"); }, w: "33.33%" },
  { get label() { return elakayAutoText("h_3cd5a03af940d1", "Quart"); }, w: "25%" },
  { get label() { return elakayAutoText("h_c614ba7c453cdc", "Auto"); }, w: "auto" },
  { get label() { return elakayAutoText("h_82f67cfbc9265e", "Carré 200"); }, w: "200px", h: "200px" },
];
// Hauteurs prédéfinies
const HEIGHT_PRESETS = [
  { get label() { return elakayAutoText("h_c614ba7c453cdc", "Auto"); }, h: "auto" },
  { get label() { return elakayAutoText("h_156d9280e28bc2", "Petite 80"); }, h: "80px" },
  { get label() { return elakayAutoText("h_6703552649b655", "Moyenne 200"); }, h: "200px" },
  { get label() { return elakayAutoText("h_4b91baef8c9d51", "Grande 400"); }, h: "400px" },
  { get label() { return elakayAutoText("h_b6b98462787164", "Demi-écran"); }, h: "50vh" },
  { get label() { return elakayAutoText("h_289905b9442c2a", "Plein écran"); }, h: "100vh" },
];

// Modèles de taille d'écran pour l'aperçu. kind pilote le rendu des surcharges (desktop/mobile).
const DEVICE_PRESETS = [
  { id: "desktop-xl", get label() { return elakayAutoText("h_8c4d097cfa60a1", "Grand écran"); }, width: "100%", kind: "desktop", icon: Monitor },
  { id: "desktop", get label() { return elakayAutoText("h_a7b13d69008eb2", "Desktop 1280"); }, width: "1280px", kind: "desktop", icon: Monitor },
  { id: "laptop", get label() { return elakayAutoText("h_b7e52b6c82d051", "Laptop 1024"); }, width: "1024px", kind: "desktop", icon: Monitor },
  { id: "tablet", get label() { return elakayAutoText("h_97fa75873e68d4", "Tablette 768"); }, width: "768px", kind: "mobile", icon: Smartphone },
  { id: "mobile-l", get label() { return elakayAutoText("h_750c3447031b7d", "Mobile L 414"); }, width: "414px", kind: "mobile", icon: Smartphone },
  { id: "mobile", get label() { return elakayAutoText("h_696d7248c0f052", "Mobile 390"); }, width: "390px", kind: "mobile", icon: Smartphone },
  { id: "mobile-s", get label() { return elakayAutoText("h_d9e261c04a8ebd", "Mobile S 320"); }, width: "320px", kind: "mobile", icon: Smartphone },
];

// Catalogue d'animations : apparition (jouées une fois) et continues (en boucle).
// 'css' = la valeur de la propriété CSS animation appliquée à l'élément.
const ANIMATIONS = {
  none: { get label() { return elakayAutoText("h_e8f882731020de", "Aucune"); }, group: "none", css: "" },
  // --- Apparition (au chargement) ---
  fadeIn: { get label() { return elakayAutoText("h_d14abf12b33bcc", "Fondu"); }, group: "in", css: "pb-fadeIn 0.7s ease both" },
  slideUp: { get label() { return elakayAutoText("h_c8b5930351aa75", "Glisse du bas"); }, group: "in", css: "pb-slideUp 0.7s ease both" },
  slideDown: { get label() { return elakayAutoText("h_49d12258a4f47a", "Glisse du haut"); }, group: "in", css: "pb-slideDown 0.7s ease both" },
  slideLeft: { get label() { return elakayAutoText("h_62eccdea5a77f2", "Glisse de droite"); }, group: "in", css: "pb-slideLeft 0.7s ease both" },
  slideRight: { get label() { return elakayAutoText("h_f7aa318d37de5b", "Glisse de gauche"); }, group: "in", css: "pb-slideRight 0.7s ease both" },
  zoomIn: { get label() { return elakayAutoText("h_d87c115345ce50", "Zoom avant"); }, group: "in", css: "pb-zoomIn 0.6s ease both" },
  zoomOut: { get label() { return elakayAutoText("h_da7078efe47fbc", "Zoom arrière"); }, group: "in", css: "pb-zoomOut 0.6s ease both" },
  rotateIn: { get label() { return elakayAutoText("h_906fe5068823a5", "Rotation"); }, group: "in", css: "pb-rotateIn 0.7s ease both" },
  flipIn: { get label() { return elakayAutoText("h_da604ae57c9fde", "Retournement"); }, group: "in", css: "pb-flipIn 0.8s ease both" },
  flipInY: { get label() { return elakayAutoText("h_33f332eb3cc49b", "Retournement Y"); }, group: "in", css: "pb-flipInY 0.8s ease both" },
  blurIn: { get label() { return elakayAutoText("h_00c945d287916c", "Net depuis flou"); }, group: "in", css: "pb-blurIn 0.7s ease both" },
  // --- Continues (en boucle) ---
  pulse: { get label() { return elakayAutoText("h_243005c5af2901", "Pulsation"); }, group: "loop", css: "pb-pulse 1.6s ease-in-out infinite" },
  bounce: { get label() { return elakayAutoText("h_960f8a48814b3b", "Rebond"); }, group: "loop", css: "pb-bounce 1.4s ease infinite" },
  spin: { get label() { return elakayAutoText("h_e6fd30b4f62591", "Rotation continue"); }, group: "loop", css: "pb-spin 1.2s linear infinite" },
  spinRev: { get label() { return elakayAutoText("h_d2a412595ba337", "Rotation inverse"); }, group: "loop", css: "pb-spinRev 1.2s linear infinite" },
  float: { get label() { return elakayAutoText("h_2de33960b15fa9", "Flottement"); }, group: "loop", css: "pb-float 3s ease-in-out infinite" },
  shake: { get label() { return elakayAutoText("h_7904bdd862eac9", "Secousse"); }, group: "loop", css: "pb-shake 0.9s ease-in-out infinite" },
  wobble: { get label() { return elakayAutoText("h_479ffa87d18141", "Balancement"); }, group: "loop", css: "pb-wobble 2s ease-in-out infinite" },
  swing: { get label() { return elakayAutoText("h_1db1d970bb36bb", "Pendule"); }, group: "loop", css: "pb-swing 2s ease-in-out infinite" },
  heartbeat: { get label() { return elakayAutoText("h_ff418d80d8df90", "Battement"); }, group: "loop", css: "pb-heartbeat 1.3s ease-in-out infinite" },
  flash: { get label() { return elakayAutoText("h_59df9c91cd1c44", "Clignotement"); }, group: "loop", css: "pb-flash 1.8s ease infinite" },
  // --- Personnalisable (paramètres : axe, motif 0→X / -X→X / X→-X, amplitude) ---
  custom: { get label() { return elakayAutoText("h_babc341713a3cb", "Personnalisée"); }, group: "custom", css: "" },
};

// Génère la keyframe + la propriété animation pour une animation personnalisée d'un élément.
// params: { kind: "translateX"|"translateY"|"rotate", pattern: "0toX"|"-XtoX"|"Xto-X"|"backforth", amp, unit }
function buildCustomAnim(node) {
  const p = node.animParams || {};
  const kind = p.kind || "translateX";
  const pattern = p.pattern || "backforth";
  const amp = p.amp != null && p.amp !== "" ? Number(p.amp) : 30;
  const unit = kind === "rotate" ? "deg" : "px";
  const fn = (v) => kind === "rotate" ? `rotate(${v}${unit})` : (kind === "translateY" ? `translateY(${v}${unit})` : `translateX(${v}${unit})`);
  let frames;
  if (pattern === "0toX") frames = `from{transform:${fn(0)}}to{transform:${fn(amp)}}`;
  else if (pattern === "-XtoX") frames = `from{transform:${fn(-amp)}}to{transform:${fn(amp)}}`;
  else if (pattern === "Xto-X") frames = `from{transform:${fn(amp)}}to{transform:${fn(-amp)}}`;
  else frames = `0%,100%{transform:${fn(-amp)}}50%{transform:${fn(amp)}}`; // backforth (aller-retour)
  const name = `pb-cust-${node.id}`;
  const dur = node.animDuration || "1.2s";
  const delay = node.animDelay ? ` ${node.animDelay}` : "";
  const ease = p.pattern === "backforth" ? "ease-in-out" : "ease";
  const loop = p.loop === false ? "both" : "infinite";
  return { name, keyframe: `@keyframes ${name}{${frames}}`, css: `${name} ${dur} ${ease}${delay} ${loop}` };
}

// Les keyframes CSS correspondantes (injectées dans l'aperçu et à l'export)
const ANIMATION_KEYFRAMES = `
@keyframes pb-fadeIn{from{opacity:0}to{opacity:1}}
@keyframes pb-slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
@keyframes pb-slideDown{from{opacity:0;transform:translateY(-30px)}to{opacity:1;transform:translateY(0)}}
@keyframes pb-slideLeft{from{opacity:0;transform:translateX(30px)}to{opacity:1;transform:translateX(0)}}
@keyframes pb-slideRight{from{opacity:0;transform:translateX(-30px)}to{opacity:1;transform:translateX(0)}}
@keyframes pb-zoomIn{from{opacity:0;transform:scale(0.8)}to{opacity:1;transform:scale(1)}}
@keyframes pb-zoomOut{from{opacity:0;transform:scale(1.2)}to{opacity:1;transform:scale(1)}}
@keyframes pb-rotateIn{from{opacity:0;transform:rotate(-12deg) scale(0.9)}to{opacity:1;transform:rotate(0) scale(1)}}
@keyframes pb-flipIn{from{opacity:0;transform:perspective(500px) rotateX(60deg)}to{opacity:1;transform:perspective(500px) rotateX(0)}}
@keyframes pb-flipInY{from{opacity:0;transform:perspective(500px) rotateY(60deg)}to{opacity:1;transform:perspective(500px) rotateY(0)}}
@keyframes pb-blurIn{from{opacity:0;filter:blur(8px)}to{opacity:1;filter:blur(0)}}
@keyframes pb-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
@keyframes pb-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
@keyframes pb-spin{to{transform:rotate(360deg)}}
@keyframes pb-spinRev{to{transform:rotate(-360deg)}}
@keyframes pb-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
@keyframes pb-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-5px)}75%{transform:translateX(5px)}}
@keyframes pb-wobble{0%,100%{transform:rotate(0)}25%{transform:rotate(-3deg)}75%{transform:rotate(3deg)}}
@keyframes pb-swing{0%,100%{transform:rotate(-8deg)}50%{transform:rotate(8deg)}}
@keyframes pb-heartbeat{0%,100%{transform:scale(1)}25%{transform:scale(1.1)}40%{transform:scale(1)}60%{transform:scale(1.08)}}
@keyframes pb-flash{0%,50%,100%{opacity:1}25%,75%{opacity:0.3}}`;

// ---------------------------------------------------------------------------
// Données d'exemple (Elakay) pour exercer le data-binding dans l'onglet Données
// ---------------------------------------------------------------------------
const STORE_SAMPLE = {
  shopActive: true, siseProductAll: 28, size: 12, siseProduct: 28,
  products: [
    { _id: "69de8650", name: "Best women t-shirt xovaria", get description() { return elakayAutoText("h_5f6f8ef454894e", "Graphic tee révolutionnaire."); }, price: 25, salePrice: 0, Stars: 4, condition: "New",
      filterProduct: [{ quantity: 22, sold: 1, filter: { color: "Red", size: "12", made: "Republique Dominicaine" } }],
      galeriesID: { galeries: [{ urlImg: ["http://localhost:2284/elakay_api/img/69de8650/img1"] }] },
      categoriesID: [{ catID: { name: ["Plain T-Shirts", "T-Shirts", "Tops", "Women", "Clothing"] } }] },
    { _id: "69656248", name: "Daphn laptop", get description() { return elakayAutoText("h_c9b371b2cefbfa", "Laptop portátil compacta."); }, price: 700, salePrice: 0, Stars: 0, condition: "New",
      filterProduct: [{ quantity: 20, sold: 0, filter: { color: "aquamarine", size: "13", made: "Republique Dominicaine" } }],
      galeriesID: { galeries: [{ urlImg: ["http://localhost:2284/elakay_api/img/69656248/img1?bgc=DarkSlateGray"] }] },
      categoriesID: [{ catID: { name: ["Laptop", "Electronics"] } }] },
    { _id: "696542fc", name: "Tablet daphn", get description() { return elakayAutoText("h_1fd861a37f410a", "Tablette grand écran 6.9 pouces."); }, price: 900, salePrice: 0, Stars: 0, condition: "New",
      filterProduct: [{ quantity: 34, sold: 0, filter: { color: "white", size: "12", made: "Republique Dominicaine" } }],
      galeriesID: { galeries: [{ urlImg: ["http://localhost:2284/elakay_api/img/696542fc/img1?bgc=DarkSlateGray"] }] },
      categoriesID: [{ catID: { name: ["tablet", "Electronics"] } }] },
    { _id: "69652fff", name: "Yvox phone", get description() { return elakayAutoText("h_f23b41b36cb987", "iPhone 16 Pro Max, écran Super Retina XDR."); }, price: 700, salePrice: 0, Stars: 0, condition: "New",
      filterProduct: [{ quantity: 12, sold: 0, filter: { color: "gray", size: "13", made: "haiti" } }],
      galeriesID: { galeries: [{ urlImg: ["http://localhost:2284/elakay_api/img/69652fff/img1"] }] },
      categoriesID: [{ catID: { name: ["phone", "Electronics"] } }] },
    { _id: "690a2fc7", name: "Bluetooth wireless earbuds", get description() { return elakayAutoText("h_21fe765b17b584", "Écouteurs à réduction de bruit."); }, price: 58, salePrice: 0, Stars: 0, condition: "New",
      filterProduct: [{ quantity: 22, sold: 1, filter: { color: "silver", size: "13", made: "blazile" } }],
      galeriesID: { galeries: [{ urlImg: ["http://localhost:2284/elakay_api/img/690a2fc7/img1"] }] },
      categoriesID: [{ catID: { name: ["Graphic T-Shirts", "T-Shirts", "Tops", "Girls", "Clothing"] } }] },
    { _id: "67c58ea6", name: "Allay polo t-shirts", get description() { return elakayAutoText("h_a2e9b8f2f63577", "Polo confortable coupe régulière."); }, price: 23, salePrice: 16, Stars: 4, condition: "New",
      filterProduct: [{ quantity: 30, sold: 0, filter: { color: "blue", size: "14", made: "usa" } }],
      galeriesID: { galeries: [{ urlImg: ["http://localhost:2284/elakay_api/img/67c58ea6/img1"] }] },
      categoriesID: [{ catID: { name: ["Polo Shirts", "T-Shirts", "Tops", "Women", "Clothing"] } }] },
  ],
  owner: {
    firstname: "yves", lastname: "oxilus", email: "yvesoxilus@gmail.com", shopName: ["Yvox", "Monnox"],
    address: [
      { shopName: "yvox", tel: "+1 347 481 3174", Address1: "226 Norfeld BLVD", city: "Elmont", State: "New york", country: "Us", addressDefault: true },
      { shopName: "Monnox", tel: "+1 347 782 1232", Address1: "80 moffat st", city: "Brooklyn", State: "New york", country: "Us", addressDefault: false },
    ],
  },
  pubCategoryProdMatrix: {
    shopName: "Yvox",
    products: [
      { _id: "69656248", name: "Daphn laptop", price: 700, salePrice: 0, Stars: 0, totalSold: 0, galeriesID: { galeries: [{ urlImg: "http://localhost:2284/elakay_api/img/69656248/img1?bgc=DarkSlateGray" }] } },
      { _id: "690a2fc7", name: "Bluetooth wireless earbuds", price: 58, salePrice: 0, Stars: 0, totalSold: 1, galeriesID: { galeries: [{ urlImg: "http://localhost:2284/elakay_api/img/690a2fc7/img1" }] } },
      { _id: "67c58ea6", name: "Allay polo t-shirts", price: 23, salePrice: 16, Stars: 4, totalSold: 0, galeriesID: { galeries: [{ urlImg: "http://localhost:2284/elakay_api/img/67c58ea6/img1" }] } },
    ],
  },
  categories: {
    shopName: "yvox",
    categories: [
      { categoryID: "6652a38e", categoryName: "Polo Shirts", name: ["Polo Shirts", "T-Shirts", "Tops", "Women", "Clothing"], products: [
        { _id: "67c58ea6", name: "Allay polo t-shirts", price: 23, salePrice: 16, Stars: 4, totalSold: 0, galeriesID: { galeries: [{ urlImg: "http://localhost:2284/elakay_api/img/67c58ea6/img1" }] } },
        { _id: "66778924", name: "women polo shirts", price: 34, salePrice: 0, Stars: 0, totalSold: 0, galeriesID: { galeries: [{ urlImg: "http://localhost:2284/elakay_api/img/66778924/img1" }] } },
      ] },
      { categoryID: "66fd5c80", categoryName: "phone", name: ["phone", "Electronics"], products: [
        { _id: "69652fff", name: "Yvox phone", price: 700, salePrice: 0, Stars: 0, totalSold: 0, galeriesID: { galeries: [{ urlImg: "http://localhost:2284/elakay_api/img/69652fff/img1" }] } },
      ] },
    ],
  },
  filters: [
    { _id: "63117c09", name: "Made", FilterItem: ["usa", "canada", "haiti", "Republique Dominicaine", "chilie", "blazile", "argentine"] },
    { _id: "629143ab", name: "color", FilterItem: ["black", "gray", "gold", "silver", "white", "aquamarine", "blue", "royalblue", "Red"] },
    { _id: "629143b3", name: "size", FilterItem: ["12", "13", "14", "15", "16", "17", "18"] },
  ],
  sliderShop: [
    { urlImg: "http://localhost:2284/elakay_api/img/69656248/img1?bgc=DarkSlateGray" },
    { urlImg: "http://localhost:2284/elakay_api/img/696542fc/img1?bgc=DarkSlateGray" },
    { urlImg: "http://localhost:2284/elakay_api/img/69652fff/img1" },
  ],
  links: {
    shopPage: "/shop",
    aboutPage: "/shop/about",
    productDetail: "/product/preview",
    cartPage: "/cart",
    checkoutPage: "/cart",
    homePage: "/",
  },
};

const CONTENT_ABOUT_SAMPLE = {
  pageTitle: "About Us",
  sections: [
    { _id: "64376b80", get title() { return elakayAutoText("h_cdab446359f144", "Who we are"); }, text: ["We are a community driven by a shared mission to catalyze business growth across America.", "", "Elakay is committed to three core principles: growth, supporting small business, and individuality."], list: [], stylePara: [4, "a"], VORI: "video", imagePath: "", videoPath: "http://localhost:2284/elakay_api/file/video/about1.MP4", href: { title: "", href: "" }, link: { title: "", href: "" } },
    { _id: "65e3f6bc", get title() { return elakayAutoText("h_d690f2ec040ebb", "Our vision"); }, text: ["Imagine a future where every entrepreneur finds a supportive community and unlimited growth opportunities."], list: [], stylePara: [4, "a"], VORI: "", imagePath: "", videoPath: "", href: { title: "", href: "" }, link: { title: "", href: "" } },
    { _id: "65e3f806", get title() { return elakayAutoText("h_74ba292b84e96c", "Our values"); }, text: ["Our mission is based on three pillars: growth, support for small businesses, and individuality."], list: ["Collaboration", "Inclusion", "Integrity", "Sustainability"], stylePara: [4, "a"], VORI: "image", imagePath: "http://localhost:2284/elakay_api/file/image/values.png", videoPath: "", href: { title: "", href: "" }, link: { title: "", href: "" } },
  ],
};

const CONTENT_SELLER_SAMPLE = {
  pageTitle: "Become a Seller",
  sections: [
    { _id: "64335149", get title() { return elakayAutoText("h_18cb454cd6703d", "Become a Seller and Create your Shop"); }, text: ["In the dynamic world of Elakay, we strive to create an enriching experience for buyers and sellers.", "", "Becoming a seller is by invitation or sponsorship through an existing associate."], list: [], stylePara: [2, "a"], VORI: "image", imagePath: "http://localhost:2284/elakay_api/file/image/seller1.png", videoPath: "", href: { title: "", href: "" }, link: { title: "", href: "" } },
    { _id: "65f31394", get title() { return elakayAutoText("h_4fa5eaf3eb8862", "To become a seller on Elakay"); }, text: ["There are three main ways to begin the process:"], list: ["Direct Contact by an Elakay Associate.", "Personal Initiative to Contact an Associate.", "Select an Associate from the List."], stylePara: [4, "1"], VORI: "", imagePath: "", videoPath: "", href: { title: "", href: "" }, link: { title: "", href: "" } },
    { _id: "65f3ba97", get title() { return elakayAutoText("h_d071f218be1dbf", "To create a store on Elakay"); }, text: ["To create a store, master the process of adding products on PC, laptop, tablet or phone."], list: [], stylePara: [4, "ul"], VORI: "video", imagePath: "", videoPath: "http://localhost:2284/elakay_api/file/video/store.MP4", href: { title: "", href: "" }, link: { title: "", href: "" } },
  ],
};

// ── Registre de toutes les content pages (issu de menuAddLang) ───────────────
const CONTENT_PAGE_REGISTRY = [
  // General
  { key: "aboutUs",               get label() { return elakayAutoText("h_c887b9d3b3905e", "About Us"); },                              group: "general" },
  { key: "makeYourFstep",         get label() { return elakayAutoText("h_32758dbd69dfe3", "Make Your 1st Step"); },                    group: "general" },
  // Royalty
  { key: "becomeRoyalty",         get label() { return elakayAutoText("h_c93740bca044e0", "Become Royalty"); },                        group: "royalty" },
  { key: "planUserRoyal",         get label() { return elakayAutoText("h_5fe7760d881243", "Royalty Plan"); },                          group: "royalty" },
  { key: "termConditionRoyal",    get label() { return elakayAutoText("h_76927b9e26f3d6", "Royalty Terms & Conditions"); },            group: "royalty" },
  { key: "royalAgree",            get label() { return elakayAutoText("h_920270b7938331", "Royalty Agreement"); },                     group: "royalty" },
  // Seller
  { key: "becomeSeller",          get label() { return elakayAutoText("h_b1b4a3a8cd46ae", "Become Seller"); },                         group: "seller" },
  { key: "LinkcustomerShop",      get label() { return elakayAutoText("h_6f9722fc23b4d7", "Link Customer to your Shop"); },            group: "seller" },
  { key: "editProduct",           get label() { return elakayAutoText("h_e12d710e570fa2", "How to Edit Products"); },                  group: "seller" },
  { key: "becomeProvider",        get label() { return elakayAutoText("h_433110c1da1df0", "Become a Provider"); },                     group: "seller" },
  { key: "termConditProdSel",     get label() { return elakayAutoText("h_711b7dd0872eb1", "Terms for Selling Products"); },            group: "seller" },
  { key: "catalogProdSel",        get label() { return elakayAutoText("h_6375fa99b08b0a", "Product Catalog for Sellers"); },           group: "seller" },
  { key: "infoProdSel",           get label() { return elakayAutoText("h_dcc3ae080322d0", "Product Info for Sellers"); },              group: "seller" },
  { key: "filterProdSel",         get label() { return elakayAutoText("h_f831a39be08327", "Filters for Selling Products"); },          group: "seller" },
  { key: "imageProdSel",          get label() { return elakayAutoText("h_4c7f7c9a1dae88", "Images for Selling Products"); },           group: "seller" },
  { key: "planUserSeller",        get label() { return elakayAutoText("h_4d203c8ac7ea56", "Seller Plan"); },                           group: "seller" },
  { key: "termConditionSeller",   get label() { return elakayAutoText("h_3a15f0f2ae9f3d", "Seller Terms & Conditions"); },             group: "seller" },
  { key: "sellerAgree",           get label() { return elakayAutoText("h_e93ab37d98c28a", "Seller Agreement"); },                      group: "seller" },
  // Provider
  { key: "planUserProvider",      get label() { return elakayAutoText("h_756a2a64f9cdc5", "Provider Plan"); },                         group: "provider" },
  { key: "termConditionProvider", get label() { return elakayAutoText("h_f26229d7ed293d", "Provider Terms & Conditions"); },           group: "provider" },
  { key: "providerAgree",         get label() { return elakayAutoText("h_6c8dd0e37ee3e1", "Provider Agreement"); },                    group: "provider" },
  // Associate
  { key: "admAssPage",            get label() { return elakayAutoText("h_332d9fb063afc4", "Admin Associate Introduction"); },          group: "associate" },
  { key: "planAffaireElakay",     get label() { return elakayAutoText("h_886d5b74f9e53f", "Associate Plan & Proposals"); },            group: "associate" },
  { key: "termConditionAssociate",get label() { return elakayAutoText("h_a675e3d6039c1b", "Associate Terms & Conditions"); },          group: "associate" },
  { key: "associateAgree",        get label() { return elakayAutoText("h_f38c17827b4f33", "Associate Agreement"); },                   group: "associate" },
  // Investment
  { key: "planProInvestElakay",   get label() { return elakayAutoText("h_981930ee17823d", "Investment Plan & Proposals"); },           group: "investment" },
  { key: "termConditionInvest",   get label() { return elakayAutoText("h_130117fbcbc93a", "Investment Terms & Conditions"); },         group: "investment" },
  { key: "investmentAgree",       get label() { return elakayAutoText("h_ab9ed47afd9176", "Investment Agreement"); },                  group: "investment" },
  // Admin
  { key: "planProAdminElakay",    get label() { return elakayAutoText("h_9140b4cb3dea00", "Admin Plan & Proposals"); },                group: "admin" },
  { key: "termConditionAdmin",    get label() { return elakayAutoText("h_ebb96ee75cf6d7", "Admin Terms & Conditions"); },              group: "admin" },
  { key: "AdministratorAgree",    get label() { return elakayAutoText("h_4e9111caaec13a", "Administrator Agreement"); },               group: "admin" },
  // Family
  { key: "planProFamillyElakay",  get label() { return elakayAutoText("h_d5d678fa738340", "Developer Plan & Proposals"); },               group: "family" },
  { key: "termConditionFamilly",  get label() { return elakayAutoText("h_b8657488eede8a", "Developer Terms & Conditions"); },             group: "family" },
  { key: "FamillyAgree",          get label() { return elakayAutoText("h_abcbd1396e7ab5", "Developer Agreement"); },                      group: "family" },
];

const CONTENT_PAGE_GROUPS = [
  { id: "general",    get label() { return elakayAutoText("h_9239ee2cda84ec", "General"); } },
  { id: "royalty",    get label() { return elakayAutoText("h_3d683b83b57106", "Royalty"); } },
  { id: "seller",     get label() { return elakayAutoText("h_ec3ef051d5a8bf", "Seller"); } },
  { id: "provider",   get label() { return elakayAutoText("h_7ceee3f3615a2b", "Provider"); } },
  { id: "associate",  get label() { return elakayAutoText("h_5f1e87a5d1f90b", "Associate"); } },
  { id: "investment", get label() { return elakayAutoText("h_0401729c31ea52", "Investment"); } },
  { id: "admin",      get label() { return elakayAutoText("h_4e7afebcfbae00", "Admin"); } },
  { id: "family",     get label() { return elakayAutoText("h_4efb6cb7c018f0", "Developer"); } },
];

// Génère un dataset contentPage générique pour une clé de content page
const makeContentPageDataset = (key, label) => ({
  label,
  pageMode: "contentPage",
  data: {
    _id: `sample:${key}`,
    pageId: `sample:${key}`,
    pageKey: key,
    sourceKey: key,
    pageTitle: label,
    sections: [
      {
        _id: `${key}-s1`,
        title: label,
        text: [`This is the "${label}" content page.`, "Add your content here.", ""],
        list: [],
        stylePara: [4, "a"],
        VORI: "",
        imagePath: "",
        videoPath: "",
        href: { title: elakayAutoText("h_378cbbf54cbb2e", "Learn More"), href: "/" },
        link: { title: "", href: "" },
      },
    ],
  },
});

// Données case studies locales (utilisées par caseCard / caseGrid)
const CASE_STUDIES_SAMPLE = {
  get title() { return elakayAutoText("h_95b63ed0d2d6b3", "Customer Success Stories"); },
  studies: [
    { _id:"cs-001", image:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=420&h=300&fit=crop",
      get title() { return elakayAutoText("h_b40b0fc8fb3350", "How Cooper's Treats Increased AOV by 3% with Platform"); },
      stat1Icon:"📈", stat1Value:"15%",   stat1Label:"Conversion rate",
      stat2Icon:"🛒", stat2Value:"+3%",   stat2Label:"AOV growth",
      stat3Icon:"⏱", stat3Value:"30 min", stat3Label:"Set-up time",
      linkText:"View Case Study ›", link:"/case-studies/coopers-treats" },
    { _id:"cs-002", image:"https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=420&h=300&fit=crop",
      get title() { return elakayAutoText("h_06c48bac04e229", "How Mouvair Generated 60% more Upsell Revenue"); },
      stat1Icon:"📈", stat1Value:"60%",   stat1Label:"More Upsell Revenue",
      stat2Icon:"📅", stat2Value:"1 Day", stat2Label:"To See Results",
      stat3Icon:"💰", stat3Value:"100%",  stat3Label:"More Sales vs Email",
      linkText:"View Case Study ›", link:"/case-studies/mouvair" },
    { _id:"cs-003", image:"https://images.unsplash.com/photo-1560807707-8cc77767d783?w=420&h=300&fit=crop",
      get title() { return elakayAutoText("h_07ae8aed86c419", "How Beast & Buckle Boosted Monthly Revenue by 4%"); },
      stat1Icon:"📊", stat1Value:"5%",     stat1Label:"CVR on Thank You Page",
      stat2Icon:"💹", stat2Value:"20–25x", stat2Label:"ROI against app fees",
      stat3Icon:"📈", stat3Value:"+4%",    stat3Label:"Monthly Revenue",
      linkText:"View Case Study ›", link:"/case-studies/beast-buckle" },
  ],
};

const SAMPLE_DATASETS = {
  // MODE STORE
  store:  { get label() { return elakayAutoText("h_5624504b4fde54", "Boutique Yvox (store)"); }, pageMode: "store", data: STORE_SAMPLE },
  // MODE CONTENT PAGE — exemples avec sections réelles
  about:  { get label() { return elakayAutoText("h_0c0113a6f63c4c", "À propos (contentPage)"); },         pageMode: "contentPage", data: CONTENT_ABOUT_SAMPLE },
  seller: { get label() { return elakayAutoText("h_f142e2ba0c1e4d", "Devenir vendeur (contentPage)"); },   pageMode: "contentPage", data: CONTENT_SELLER_SAMPLE },
  // MODE CONTENT PAGE — toutes les pages menuAddLang (registre complet)
  ...Object.fromEntries(
    CONTENT_PAGE_REGISTRY.map(({ key, label }) => [`cp_${key}`, makeContentPageDataset(key, label)])
  ),
  // MODE STATIC — Case Studies
  caseStudies: { get label() { return elakayAutoText("h_eaafdbf63af718", "Case Studies (études de cas)"); }, pageMode: "static", data: CASE_STUDIES_SAMPLE },
};

// --- Helpers de data-binding ---
// Résout un chemin "a.b.0.c" dans un objet/tableau
function resolvePath(obj, path) {
  if (!path) return obj;
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

// Liste les clés disponibles d'une valeur (1 niveau), en annotant le type
function listKeys(value, prefix = "") {
  if (value == null || typeof value !== "object") return [];
  if (Array.isArray(value)) {
    return [{ key: prefix || "(tableau)", type: `tableau [${value.length}]`, isArray: true, sample: value[0] }];
  }
  return Object.entries(value).map(([k, v]) => {
    const full = prefix ? `${prefix}.${k}` : k;
    let type = typeof v;
    if (Array.isArray(v)) type = `tableau [${v.length}]`;
    else if (v && type === "object") type = "objet";
    return { key: full, type, isArray: Array.isArray(v), isObject: v && typeof v === "object" && !Array.isArray(v), sample: v };
  });
}


// Contexte de données : transmet la donnée chargée + l'item courant dans une répétition
const DataContext = createContext({ boundData: null, item: null });
// Contexte de mise à jour de nœud : permet à DocViewerBlock de persister docHtml/docName
// Signature : (nodeId: string, patch: object) => void
const NodeUpdateContext = createContext(null);

// Clone profond d'un nœud en régénérant les ids (utile pour duplication d'un gabarit)
let _dupCounter = 0;
function cloneNodeFresh(node) {
  const fresh = JSON.parse(JSON.stringify(node));
  const reid = (n) => { n._tplId = n.id; n.id = n.id + "_r" + (++_dupCounter); n.children?.forEach(reid); };
  reid(fresh);
  return fresh;
}

// Remplit un nœud (et ses enfants) à partir d'un item de données.
// Pour chaque nœud du gabarit :
//   - si node.bindKey est défini, on prend la valeur item[node.bindKey] (chemin pointé "a.b.0")
//   - sinon, si auto est activé et que la clé "name"/"text"/"src"/"image"/"price"... matche,
//     on essaie de deviner par le type de l'élément.
// fieldMap : { "name": "elementId" } pour overrider manuellement (la clé va dans CET élément).
function fillNodeWithItem(node, item, fieldMap, autoFill, rootData, rootKey) {
  if (!node) return node;
  const out = { ...node };
  const resolveActionToken = (raw) => {
    if (raw == null) return raw;
    if (typeof raw !== "string") return raw;
    const value = raw.trim();
    if (!value) return raw;

    const normalizePath = (p) => (p.startsWith("items.") ? p.slice(6) : p.startsWith("item.") ? p.slice(5) : null);
    const resolveItemTokenValue = (tokenPathRaw) => {
      const tokenPath = String(tokenPathRaw || "").trim();
      if (!tokenPath) return undefined;
      const normalized = normalizePath(tokenPath);
      if (normalized) {
        const resolved = resolvePath(item, normalized);
        if (resolved !== undefined) return resolved;
      }
      const direct = resolvePath(item, tokenPath);
      if (direct !== undefined) return direct;
      const itemPath = normalized || tokenPath;
      if (["categoryID", "categoryId", "catID"].includes(itemPath)) {
        const categoryValue =
          item?.categoryID ??
          item?.categoryId ??
          item?.catID ??
          item?._id ??
          item?.id ??
          (Array.isArray(item?.categoryIDList) ? item.categoryIDList[0] : null);
        if (categoryValue && typeof categoryValue === "object") {
          return categoryValue._id ?? categoryValue.id ?? categoryValue.categoryID ?? categoryValue.catID;
        }
        return categoryValue;
      }
      if (["cateryName", "categoryName"].includes(itemPath)) {
        const categoryName = item?.cateryName ?? item?.categoryName ?? item?.item_name ?? item?.name;
        return Array.isArray(categoryName) ? categoryName[0] : categoryName;
      }
      if (tokenPath === "productId") return item?.productId || item?._id || item?.productID || item?.id;
      if (tokenPath === "urlFil") return item?.urlFil ?? item?.urlFilter ?? item?.filterUrl ?? "";
      return undefined;
    };

    const plainTokenValue = resolveItemTokenValue(value);
    if (plainTokenValue !== undefined) return plainTokenValue;

    if (value.includes("{{")) {
      return raw.replace(/\{\{\s*(items?|item)\.([^}]+)\s*\}\}/g, (_m, _prefix, path) => {
        const v = resolveItemTokenValue(path);
        return v == null ? "" : String(v);
      });
    }
    if (value.includes("${")) {
      return raw.replace(/\$\{\s*([^}]+)\s*\}/g, (_m, path) => {
        const v = resolveItemTokenValue(path);
        return v == null ? "" : String(v);
      });
    }
    return raw;
  };
  // 1) Override manuel : si CET élément a un id présent comme valeur dans fieldMap, on prend la clé associée
  let manualKey = null;
  if (fieldMap) {
    for (const [k, targetId] of Object.entries(fieldMap)) {
      if (targetId === (node._tplId || node.id)) { manualKey = k; break; }
    }
  }
  // 2) Clé effective : manuelle > bindKey > auto-guess
  const key = manualKey || node.bindKey || (autoFill && item ? guessBindKey(node) : null);
  if (key) {
    // Si la clé commence par le nom de la source (ex. "store.products.0.name"),
    // on la résout depuis la racine ; sinon elle est relative à l'item courant.
    let val;
    if (rootKey && (key === rootKey || key.startsWith(rootKey + "."))) {
      val = resolvePath(rootData, key.slice(rootKey.length + 1));
    } else if (item) {
      val = resolvePath(item, key);
    } else {
      val = resolvePath(rootData, key);
    }
    if (val !== undefined) {
      // Selon le type d'élément, on injecte la valeur au bon endroit
      if (isImageNode(node)) {
        const src = mediaString(val);
        out.src = src;
        out._hideWhenEmptyMedia = !src;
      }
      else if (isVideoNode(node)) {
        const url = mediaString(val);
        out.videoUrl = url;
        out.options = { ...(node.options || {}), url };
        out._hideWhenEmptyMedia = !url;
      }
      else if (val != null) {
        if (node.type === "button") { out.text = String(val); if (typeof val === "string" && /^https?:\/\//.test(val)) out.href = String(val); }
        else if (node.type === "link") { out.text = String(val); if (typeof val === "string" && /^https?:\/\//.test(val)) out.href = String(val); }
        else if (node.type === "input" || node.type === "textarea") out.field = { ...(node.field || {}), value: String(val) };
        else if (node.type === "select") out.options = { ...(node.options || {}), choices: Array.isArray(val) ? val.map(String) : [String(val)] };
        else out.text = String(val);
      }
    }
  }
  out.clickActionArg1 = resolveActionToken(out.clickActionArg1);
  out.clickActionArg2 = resolveActionToken(out.clickActionArg2);
  out.clickActionArg3 = resolveActionToken(out.clickActionArg3);
  out.clickActionHref = resolveActionToken(out.clickActionHref);
  out.clickActionPage = resolveActionToken(out.clickActionPage);
  out.__mapItem = item || out.__mapItem;
  if (node.children) out.children = node.children.map((c) => fillNodeWithItem(c, item, fieldMap, autoFill, rootData, rootKey));
  return out;
}

// Devine la clé à utiliser pour un élément du gabarit selon son type et son texte
function guessBindKey(node) {
  if (node.type === "image") return "image|img|src|urlImg|ProductImageUrls.0|galeriesID.galeries.0.urlImg";
  if (node.type === "video") return "video|videoPath";
  // On lit le texte pour deviner ("Prix", "Name"…)
  const t = (node.text || node.field?.label || "").toLowerCase();
  if (/prix|price|montant|amount/i.test(t)) return "price";
  if (/nom|name|titre|title/i.test(t)) return "name";
  if (/desc/i.test(t)) return "description";
  if (/star|note|rating/i.test(t)) return "Stars";
  return null;
}


// ---------------------------------------------------------------------------
// Éditeur d'items — slides du carrousel OU lignes d'un groupe de texte
// ---------------------------------------------------------------------------
function ItemsEditor({ element, onUpdate }) {
  const isTextGroup = element.type === "textGroup";
  const isAccordion = element.type === "accordion";
  const items = element.items || [];
  const setItems = (next) => onUpdate({ ...element, items: next });
  const updateItem = (id, patch) => setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const addItem = () => {
    let blank;
    if (isTextGroup) blank = { id: uid(), tag: "p", text: elakayAutoText("h_2195728669e39e", "Nouvelle ligne"), fontSize: "16px", fontWeight: "400", color: "#4a4a68", letterSpacing: "normal" };
    else if (isAccordion) blank = { id: uid(), q: "Nouvelle question ?", a: "Réponse à la question." };
    else blank = { id: uid(), image: "https://placehold.co/600x300/8b5cf6/ffffff?text=Nouveau", title: elakayAutoText("h_4546ad0ef606d6", "Nouvel item"), text: elakayAutoText("h_e1459398efa553", "Texte de l'item.") };
    setItems([...items, blank]);
  };
  const removeItem = (id) => setItems(items.filter((it) => it.id !== id));
  const moveItem = (id, dir) => {
    const i = items.findIndex((it) => it.id === id); const t = i + dir;
    if (t < 0 || t >= items.length) return;
    const copy = [...items]; [copy[i], copy[t]] = [copy[t], copy[i]]; setItems(copy);
  };

  const title = isTextGroup ? "Lignes de texte" : isAccordion ? "Questions / réponses" : "Slides";
  const addLabel = isTextGroup ? "une ligne" : isAccordion ? "une question" : "une slide";

  return (
    <Section title={title} icon={Boxes}>
      {items.map((it, i) => (
        <div key={it.id} style={{ border: "1px solid #ece9f5", borderRadius: "10px", padding: "10px", marginBottom: "10px", background: "#faf9fd" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, color: "#6d28d9" }}>#{i + 1}</span>
            <div style={{ display: "flex", gap: "4px" }}>
              <button onClick={() => moveItem(it.id, -1)} style={miniBtn}><ChevronUp size={13} /></button>
              <button onClick={() => moveItem(it.id, 1)} style={miniBtn}><ChevronDown size={13} /></button>
              <button onClick={() => removeItem(it.id)} style={{ ...miniBtn, color: "#e11d48" }}><X size={13} /></button>
            </div>
          </div>
          {isTextGroup ? (
            <>
              <StyleField label={elakayAutoText("h_ff9afef1eae0a9", "Texte")} value={it.text} onChange={(v) => updateItem(it.id, { text: v })} />
              <label style={{ display: "block", marginBottom: "10px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_8d6c6068ec15d7", "Balise")}</span>
                <select value={it.tag} onChange={(e) => updateItem(it.id, { tag: e.target.value })} style={selectStyle}>
                  {["p", "span", "h1", "h2", "h3", "h4", "h5", "h6"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <StyleField label={elakayAutoText("h_2b90839d6bba3b", "Taille")} value={it.fontSize} onChange={(v) => updateItem(it.id, { fontSize: v })} placeholder={elakayAutoText("h_4678bd4d8aa022", "16px")} />
              <StyleField label={elakayAutoText("h_1fc3c47737397e", "Graisse")} value={it.fontWeight} onChange={(v) => updateItem(it.id, { fontWeight: v })} placeholder="400 / 700" />
              <StyleField label={elakayAutoText("h_8d051dc9aebe10", "Espacement lettres")} value={it.letterSpacing} onChange={(v) => updateItem(it.id, { letterSpacing: v })} placeholder={elakayAutoText("h_eb8ba1e19df9c6", "normal / 0.08em")} />
              <StyleField label={elakayAutoText("h_145c4326e92bd9", "Couleur")} value={it.color} onChange={(v) => updateItem(it.id, { color: v })} type="color" />
            </>
          ) : isAccordion ? (
            <>
              <StyleField label={elakayAutoText("h_002ff598115d84", "Question")} value={it.q} onChange={(v) => updateItem(it.id, { q: v })} />
              <StyleField label={elakayAutoText("h_e777631b83df6b", "Réponse")} value={it.a} onChange={(v) => updateItem(it.id, { a: v })} />
            </>
          ) : (
            <>
              <StyleField label={elakayAutoText("h_fa5840470c201c", "Image (URL)")} value={it.image} onChange={(v) => updateItem(it.id, { image: v })} />
              <StyleField label={elakayAutoText("h_eb97899aedcd56", "Titre")} value={it.title} onChange={(v) => updateItem(it.id, { title: v })} />
              <StyleField label={elakayAutoText("h_ff9afef1eae0a9", "Texte")} value={it.text} onChange={(v) => updateItem(it.id, { text: v })} />
            </>
          )}
        </div>
      ))}
      <button onClick={addItem} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "2px" }}>
        <Plus size={15} />{elakayAutoText("h_87c57ed173ea17", "Ajouter")}{addLabel}
      </button>
    </Section>
  );
}

// Éditeur des choix d'un menu déroulant
function ChoicesEditor({ element, onUpdate }) {
  const choices = element.options?.choices || [];
  const setChoices = (next) => onUpdate({ ...element, options: { ...element.options, choices: next } });
  return (
    <div style={{ marginTop: "4px" }}>
      <span style={fieldLabel}>{elakayAutoText("h_d5a2b163f0ef8e", "Options du menu")}</span>
      {choices.map((c, i) => (
        <div key={i} style={{ display: "flex", gap: "6px", marginTop: "6px", alignItems: "center" }}>
          <input type="text" value={c} onChange={(e) => setChoices(choices.map((x, j) => (j === i ? e.target.value : x)))}
            style={{ flex: 1, padding: "7px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
          <button onClick={() => setChoices(choices.filter((_, j) => j !== i))} style={{ ...miniBtn, color: "#e11d48" }}><X size={14} /></button>
        </div>
      ))}
      <button onClick={() => setChoices([...choices, `Option ${choices.length + 1}`])} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "8px" }}>
        <Plus size={15} />{elakayAutoText("h_74ba77eb1e1153", "Ajouter une option")}</button>
    </div>
  );
}

// Choix de la balise HTML + éditeur d'attributs libres (pour n'importe quel élément)
const COMMON_TAGS = ["div", "section", "article", "main", "header", "footer", "nav", "aside",
  "span", "p", "a", "ul", "ol", "li", "figure", "figcaption", "details", "summary", "label", "form", "fieldset"];
function TagAndAttrs({ element, onUpdate, setProp }) {
  const attrs = element.attrs || {};
  const entries = Object.entries(attrs);
  const setAttrs = (next) => onUpdate({ ...element, attrs: next });
  const setKey = (oldKey, newKey) => {
    const next = {}; for (const [k, v] of entries) next[k === oldKey ? newKey : k] = v; setAttrs(next);
  };
  const setVal = (key, val) => setAttrs({ ...attrs, [key]: val });
  const removeAttr = (key) => { const next = { ...attrs }; delete next[key]; setAttrs(next); };
  const addAttr = () => { let i = 1, base = "data-attr"; let k = base; while (attrs[k] !== undefined) k = `${base}${i++}`; setAttrs({ ...attrs, [k]: "" }); };

  return (
    <Section title={elakayAutoText("h_16ac3895e93768", "Balise & attributs HTML")} icon={FileCode}>
      <SelectOrFree label={elakayAutoText("h_91b604f60a5c7f", "Balise (tag)")} value={element.tag} onChange={(v) => setProp("tag", v)} choices={COMMON_TAGS} placeholder={elakayAutoText("h_4a27bccf3ca247", "div, section, a…")} />
      <span style={fieldLabel}>{elakayAutoText("h_d04b6c5183ac7c", "Attributs libres")}</span>
      {entries.length === 0 && <p style={{ fontSize: "11px", color: "#b3aacb", margin: "4px 0 8px" }}>{elakayAutoText("h_d1a8f430de8da4", "Aucun attribut. Ajoutez-en (id, name, role, aria-label, data-*…).")}</p>}
      {entries.map(([k, v], i) => (
        <div key={i} style={{ display: "flex", gap: "5px", marginTop: "6px", alignItems: "center" }}>
          <input type="text" value={k} onChange={(e) => setKey(k, e.target.value)} placeholder={elakayAutoText("h_8d406e3d12cd24", "nom")}
            style={{ flex: "0 0 96px", padding: "7px 8px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
          <input type="text" value={v} onChange={(e) => setVal(k, e.target.value)} placeholder={elakayAutoText("h_4b17ab7b335bfc", "valeur")}
            style={{ flex: 1, padding: "7px 8px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
          <button onClick={() => removeAttr(k)} style={{ ...miniBtn, color: "#e11d48" }}><X size={14} /></button>
        </div>
      ))}
      <button onClick={addAttr} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "8px" }}>
        <Plus size={15} />{elakayAutoText("h_97022a72f07012", "Ajouter un attribut")}</button>
    </Section>
  );
}

// Onglet Animation : présente toutes les animations adaptées à l'élément sélectionné,
// en cartes cliquables qui jouent un aperçu, plus les réglages (délai, durée, vitesse).
function AnimationPanel({ element, onUpdate }) {
  if (!element) {
    return (
      <div style={{ padding: "28px 22px", textAlign: "center", color: "#9b93b8" }}>
        <Sparkles size={28} style={{ marginBottom: "10px", opacity: 0.5 }} />
        <p style={{ fontSize: "14px", lineHeight: "1.6" }}>{elakayAutoText("h_26bf2d3c335a8c", "Sélectionnez un élément pour lui appliquer une animation.")}</p>
      </div>
    );
  }
  const current = element.animation || "none";
  const setAnim = (k) => onUpdate({ ...element, animation: k });
  const setProp = (k, v) => onUpdate({ ...element, [k]: v });

  const Card = ({ k, a }) => {
    const active = current === k;
    return (
      <button onClick={() => setAnim(k)}
        style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px", padding: "14px 8px",
          border: `1px solid ${active ? "#6d28d9" : "#ece9f5"}`, borderRadius: "12px", background: active ? "#f7f3fe" : "#fff", cursor: "pointer", minHeight: "78px" }}>
        <span style={{ width: "26px", height: "26px", borderRadius: "7px", background: active ? "#6d28d9" : "#cabfe8",
          animation: a.css || "none" }} />
        <span style={{ fontSize: "11px", fontWeight: 600, color: active ? "#6d28d9" : "#7a73a0", textAlign: "center", lineHeight: 1.2 }}>{a.label}</span>
      </button>
    );
  };

  return (
    <div style={{ padding: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <Sparkles size={16} color="#6d28d9" />
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#1a1a2e" }}>{elakayAutoText("h_8ea997d2b62bdb", "Animation de l'élément")}</span>
      </div>

      <button onClick={() => setAnim("none")}
        style={{ width: "100%", padding: "9px", marginBottom: "14px", border: `1px solid ${current === "none" ? "#6d28d9" : "#ece9f5"}`, borderRadius: "10px",
          background: current === "none" ? "#f7f3fe" : "#fff", color: current === "none" ? "#6d28d9" : "#7a73a0", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>{elakayAutoText("h_f0ca49340c6680", "Aucune animation")}</button>

      <GroupLabel>{elakayAutoText("h_e718aea6cd23ab", "Apparition (au chargement)")}</GroupLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "16px" }}>
        {Object.entries(ANIMATIONS).filter(([, a]) => a.group === "in").map(([k, a]) => <Card key={k} k={k} a={a} />)}
      </div>

      <GroupLabel>{elakayAutoText("h_efaf262a4554f2", "Continues (en boucle)")}</GroupLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "16px" }}>
        {Object.entries(ANIMATIONS).filter(([, a]) => a.group === "loop").map(([k, a]) => <Card key={k} k={k} a={a} />)}
      </div>

      <GroupLabel>{elakayAutoText("h_2ef4fc77f75ee5", "Personnalisée (mouvement paramétrable)")}</GroupLabel>
      <button onClick={() => setAnim("custom")}
        style={{ width: "100%", padding: "10px", marginBottom: "10px", border: `1px solid ${current === "custom" ? "#6d28d9" : "#ece9f5"}`, borderRadius: "10px",
          background: current === "custom" ? "#f7f3fe" : "#fff", color: current === "custom" ? "#6d28d9" : "#7a73a0", fontWeight: 600, fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
        <Move size={15} />{elakayAutoText("h_c9a0eccbc14b29", "Mouvement personnalisé")}</button>

      {current === "custom" && (() => {
        const p = element.animParams || {};
        const setParam = (k, v) => onUpdate({ ...element, animParams: { ...p, [k]: v } });
        const kind = p.kind || "translateX";
        const pattern = p.pattern || "backforth";
        const ampUnit = kind === "rotate" ? "°" : "px";
        return (
          <Section title={elakayAutoText("h_d7fdf7c063d732", "Réglages du mouvement")} icon={Move}>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_11193a726d4965", "Type de mouvement")}</span>
              <select value={kind} onChange={(e) => setParam("kind", e.target.value)} style={selectStyle}>
                <option value="translateX">{elakayAutoText("h_433c1cfa34889f", "Translation horizontale (X)")}</option>
                <option value="translateY">{elakayAutoText("h_83d220c7dfd350", "Translation verticale (Y)")}</option>
                <option value="rotate">{elakayAutoText("h_25627442a36505", "Rotation (degrés)")}</option>
              </select>
            </label>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_2a198f47f305bb", "Motif")}</span>
              <select value={pattern} onChange={(e) => setParam("pattern", e.target.value)} style={selectStyle}>
                <option value="0toX">{elakayAutoText("h_fa7e271e8f7838", "0 → X (va vers X)")}</option>
                <option value="-XtoX">{elakayAutoText("h_bd4a25b74c41b2", "−X → X (d'un bord à l'autre)")}</option>
                <option value="Xto-X">{elakayAutoText("h_ca3c890de3beef", "X → −X (l'inverse)")}</option>
                <option value="backforth">{elakayAutoText("h_892825affedde4", "Aller-retour (−X ↔ X, en boucle)")}</option>
              </select>
            </label>
            <span style={fieldLabel}>{elakayAutoText("h_8ae7cac19f79d7", "Amplitude X (")}{ampUnit})</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "5px", margin: "4px 0 8px" }}>
              {[10, 30, 60, 100].map((v) => (
                <button key={v} onClick={() => setParam("amp", v)}
                  style={{ ...ghostBtn, justifyContent: "center", fontSize: "12px", padding: "7px 4px", ...(String(p.amp) === String(v) ? { background: "#f3effe", color: "#6d28d9", borderColor: "#c9b8f5" } : {}) }}>{v}</button>
              ))}
            </div>
            <StyleField label={elakayAutoText("h_7f00c4658054db", "ou amplitude libre")} value={p.amp} onChange={(v) => setParam("amp", v)} placeholder={elakayAutoText("h_269479b11917b0", "ex. 40 ({value0})", { value0: ampUnit })} />
            <label style={{ display: "flex", alignItems: "center", gap: "8px", margin: "6px 0 10px", cursor: "pointer" }}>
              <input type="checkbox" checked={p.loop !== false} onChange={(e) => setParam("loop", e.target.checked)} style={{ accentColor: "#6d28d9", width: "16px", height: "16px" }} />
              <span style={{ fontSize: "13px", color: "#4a4a68", fontWeight: 500 }}>{elakayAutoText("h_1a51b0f588b9d1", "Répéter en boucle")}</span>
            </label>
            <StyleField label={elakayAutoText("h_6feac25effc526", "Durée")} value={element.animDuration} onChange={(v) => setProp("animDuration", v)} placeholder={elakayAutoText("h_d4f04ae4a849b8", "ex. 1.2s")} />
            <StyleField label={elakayAutoText("h_be9a94032815b2", "Délai")} value={element.animDelay} onChange={(v) => setProp("animDelay", v)} placeholder={elakayAutoText("h_48cafdcce017f2", "ex. 0.2s")} />
          </Section>
        );
      })()}

      {current !== "none" && current !== "custom" && (
        <Section title={elakayAutoText("h_f80eb95ca306bf", "Réglages de l'animation")} icon={Settings2}>
          <StyleField label={elakayAutoText("h_5cecb087db82e5", "Délai (avant de démarrer)")} value={element.animDelay} onChange={(v) => setProp("animDelay", v)} placeholder={elakayAutoText("h_01ecb476151042", "ex. 0.3s")} />
          <StyleField label={elakayAutoText("h_6feac25effc526", "Durée")} value={element.animDuration} onChange={(v) => setProp("animDuration", v)} placeholder={elakayAutoText("h_094c30a67b77a8", "ex. 0.7s")} />
          <p style={{ fontSize: "11px", color: "#b3aacb", margin: "4px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_844521f4174732", "Les cartes ci-dessus jouent un aperçu en direct. L'effet s'applique à l'élément dans l'aperçu et à l'export.")}</p>
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panneau de modification groupée (plusieurs éléments sélectionnés)
// ---------------------------------------------------------------------------
function MultiSelectPanel({ count, onStyle, onDelete, onClear, freeMode, onNudgeSelected }) {
  return (
    <div style={{ padding: "18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 700, color: "#1a1a2e" }}>
          <Boxes size={16} color="#6d28d9" />{count}{elakayAutoText("h_a9f8e6ce7a1d28", "éléments sélectionnés")}</span>
        <div style={{ display: "flex", gap: "6px" }}>
          <button onClick={onClear} title={elakayAutoText("h_d17ec0b74742f6", "Tout désélectionner")} style={iconBtn}><X size={15} /></button>
          <button onClick={onDelete} title={elakayAutoText("h_783efbc0621ecb", "Tout supprimer")} style={{ ...iconBtn, color: "#e11d48" }}><Trash2 size={15} /></button>
        </div>
      </div>

      <p style={{ fontSize: "12px", color: "#9b93b8", lineHeight: 1.5, margin: "0 0 16px" }}>{elakayAutoText("h_bdf5ee0572f623", "Les modifications ci-dessous s'appliquent à tous les éléments sélectionnés en une fois. Maj+clic pour ajouter ou retirer un élément.")}</p>

      <Section title={elakayAutoText("h_a85cb4517fa2a5", "Taille commune")} icon={Move}>
        <div style={{ display: "flex", gap: "8px" }}>
          <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_70bec8a9bf5ecd", "width")} value="" onChange={(v) => onStyle({ width: v })} placeholder={elakayAutoText("h_474aedaf2a00ec", "ex. 100%")} /></div>
          <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_73e7e545c519a3", "height")} value="" onChange={(v) => onStyle({ height: v })} placeholder={elakayAutoText("h_41931b6187c99f", "ex. 200px")} /></div>
        </div>
        <SelectOrFree label={elakayAutoText("h_10e80591d92f76", "overflow")} value="" onChange={(v) => onStyle({ overflow: v })} choices={["visible", "hidden", "scroll", "auto", "clip"]} />
        <span style={fieldLabel}>{elakayAutoText("h_1b2c1dbbc7faff", "Largeurs prédéfinies")}</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginTop: "4px", marginBottom: "10px" }}>
          {SIZE_PRESETS.map((p) => (
            <button key={p.label} onClick={() => onStyle(p.h ? { width: p.w, height: p.h } : { width: p.w })}
              style={{ ...ghostBtn, justifyContent: "center", fontSize: "12px", padding: "7px 8px" }}>{p.label}</button>
          ))}
        </div>
        <span style={fieldLabel}>{elakayAutoText("h_ba11bd1dbcdca1", "Hauteurs prédéfinies")}</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginTop: "4px" }}>
          {HEIGHT_PRESETS.map((p) => (
            <button key={p.label} onClick={() => onStyle({ height: p.h })}
              style={{ ...ghostBtn, justifyContent: "center", fontSize: "12px", padding: "7px 8px" }}>{p.label}</button>
          ))}
        </div>
      </Section>

      <Section title={elakayAutoText("h_9e16986ed5d17f", "Styles communs")} icon={Palette}>
        <StyleField label={elakayAutoText("h_37e78173dd68de", "Couleur du texte")} value="" onChange={(v) => onStyle({ color: v })} type="color" />
        <StyleField label={elakayAutoText("h_d65caa0e446934", "Fond")} value="" onChange={(v) => onStyle({ background: v })} type="color" />
        <StyleField label={elakayAutoText("h_6e82891c03c862", "Taille de police")} value="" onChange={(v) => onStyle({ fontSize: v })} placeholder={elakayAutoText("h_1489e1ecf2077d", "ex. 18px")} />
        <StyleField label={elakayAutoText("h_d3e38684bf27bf", "Rayon des coins")} value="" onChange={(v) => onStyle({ borderRadius: v })} placeholder={elakayAutoText("h_833938f7deb263", "ex. 12px")} />
        <StyleField label={elakayAutoText("h_2ded2d53c69236", "Marge extérieure")} value="" onChange={(v) => onStyle({ margin: v })} placeholder={elakayAutoText("h_108d6ca2841d3f", "ex. 0 0 16px 0")} />
        <label style={{ display: "block", marginBottom: "10px" }}>
          <span style={fieldLabel}>{elakayAutoText("h_75fce8da70f4ea", "Alignement du texte")}</span>
          <select defaultValue="" onChange={(e) => e.target.value && onStyle({ textAlign: e.target.value })} style={selectStyle}>
            <option value="">{elakayAutoText("h_718f6d24b6b218", "— choisir —")}</option>
            {["left", "center", "right", "justify"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </Section>

      {freeMode && (
        <Section title={elakayAutoText("h_e09d9eebc16cd2", "Déplacer le groupe")} icon={Move}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "4px", width: "120px", margin: "0 auto" }}>
            <span />
            <button onClick={() => onNudgeSelected(0, -10)} style={padBtn} title={elakayAutoText("h_9ef48e7c7a34da", "Haut")}><ChevronUp size={16} /></button>
            <span />
            <button onClick={() => onNudgeSelected(-10, 0)} style={padBtn} title={elakayAutoText("h_d8be759feff060", "Gauche")}><ChevronLeft size={16} /></button>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#9b93b8" }}><Move size={13} /></span>
            <button onClick={() => onNudgeSelected(10, 0)} style={padBtn} title={elakayAutoText("h_c73d793d935888", "Droite")}><ChevronRight size={16} /></button>
            <span />
            <button onClick={() => onNudgeSelected(0, 10)} style={padBtn} title={elakayAutoText("h_5146422c4f9524", "Bas")}><ChevronDown size={16} /></button>
            <span />
          </div>
          <p style={{ fontSize: "11px", color: "#9b93b8", textAlign: "center", margin: "10px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_b16b7b1cdb2683", "Pas de 10px. Les flèches du clavier déplacent aussi tout le groupe (Maj = 10px).")}</p>
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panneau de propriétés
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Inspecteur CSS complet : toutes les propriétés compatibles, en sections
// ---------------------------------------------------------------------------
function CssInspector({ style: s, setStyle, def }) {
  const set = (k) => (v) => setStyle(k, v);
  return (
    <>
      <Section title={elakayAutoText("h_f09e7eee2eefe8", "Mise en page")} icon={Layers}>
        <SelectOrFree label={elakayAutoText("h_59b189070af751", "display")} value={s.display} onChange={set("display")} choices={["block", "flex", "inline-block", "inline-flex", "grid", "inline", "none"]} placeholder={elakayAutoText("h_0214b4b355d11c", "block")} />
        {(s.display === "flex" || s.display === "inline-flex") && (
          <>
            <SelectOrFree label="flex-direction" value={s.flexDirection} onChange={set("flexDirection")} choices={["row", "row-reverse", "column", "column-reverse"]} />
            <SelectOrFree label="justify-content" value={s.justifyContent} onChange={set("justifyContent")} choices={["flex-start", "center", "flex-end", "space-between", "space-around", "space-evenly"]} />
            <SelectOrFree label="align-items" value={s.alignItems} onChange={set("alignItems")} choices={["stretch", "flex-start", "center", "flex-end", "baseline"]} />
            <SelectOrFree label="flex-wrap" value={s.flexWrap} onChange={set("flexWrap")} choices={["nowrap", "wrap", "wrap-reverse"]} />
            <StyleField label={elakayAutoText("h_81bf0145c8f2cc", "gap")} value={s.gap} onChange={set("gap")} placeholder={elakayAutoText("h_998b02c207add3", "20px")} />
          </>
        )}
        {s.display === "grid" && (
          <>
            <StyleField label="grid-template-columns" value={s.gridTemplateColumns} onChange={set("gridTemplateColumns")} placeholder={elakayAutoText("h_64b7684efa90d2", "repeat(3, 1fr)")} />
            <StyleField label="grid-template-rows" value={s.gridTemplateRows} onChange={set("gridTemplateRows")} placeholder={elakayAutoText("h_0d612c12d2ac33", "auto")} />
            <StyleField label={elakayAutoText("h_81bf0145c8f2cc", "gap")} value={s.gap} onChange={set("gap")} placeholder={elakayAutoText("h_998b02c207add3", "20px")} />
          </>
        )}
        <SelectOrFree label={elakayAutoText("h_90be16585da21a", "position")} value={s.position} onChange={set("position")} choices={["static", "relative", "absolute", "fixed", "sticky"]} />
        {s.position && s.position !== "static" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
            <StyleField label={elakayAutoText("h_af2c7b4ca07ae6", "top")} value={s.top} onChange={set("top")} placeholder="0" />
            <StyleField label={elakayAutoText("h_d27a1f11771200", "right")} value={s.right} onChange={set("right")} placeholder={elakayAutoText("h_0d612c12d2ac33", "auto")} />
            <StyleField label={elakayAutoText("h_c03e9099aad17c", "bottom")} value={s.bottom} onChange={set("bottom")} placeholder={elakayAutoText("h_0d612c12d2ac33", "auto")} />
            <StyleField label={elakayAutoText("h_12c0f1fbadc404", "left")} value={s.left} onChange={set("left")} placeholder="0" />
          </div>
        )}
        <StyleField label="z-index" value={s.zIndex} onChange={set("zIndex")} placeholder="1" />
        <SelectOrFree label={elakayAutoText("h_10e80591d92f76", "overflow")} value={s.overflow} onChange={set("overflow")} choices={["visible", "hidden", "scroll", "auto", "clip"]} />
        <SelectOrFree label="overflow-x" value={s.overflowX} onChange={set("overflowX")} choices={["visible", "hidden", "scroll", "auto", "clip"]} />
        <SelectOrFree label="overflow-y" value={s.overflowY} onChange={set("overflowY")} choices={["visible", "hidden", "scroll", "auto", "clip"]} />
      </Section>

      <Section title={elakayAutoText("h_9f4ca9ec08715d", "Dimensions")} icon={Move}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
          <StyleField label={elakayAutoText("h_70bec8a9bf5ecd", "width")} value={s.width} onChange={set("width")} placeholder={elakayAutoText("h_dc2ece3687fd88", "auto / 100%")} />
          <StyleField label={elakayAutoText("h_73e7e545c519a3", "height")} value={s.height} onChange={set("height")} placeholder={elakayAutoText("h_0d612c12d2ac33", "auto")} />
          <StyleField label="min-width" value={s.minWidth} onChange={set("minWidth")} placeholder="0" />
          <StyleField label="min-height" value={s.minHeight} onChange={set("minHeight")} placeholder="0" />
          <StyleField label="max-width" value={s.maxWidth} onChange={set("maxWidth")} placeholder={elakayAutoText("h_71f8e7976e4cbc", "none")} />
          <StyleField label="max-height" value={s.maxHeight} onChange={set("maxHeight")} placeholder={elakayAutoText("h_71f8e7976e4cbc", "none")} />
        </div>
        <StyleField label="aspect-ratio" value={s.aspectRatio} onChange={set("aspectRatio")} placeholder="16 / 9" />
        <SelectOrFree label="box-sizing" value={s.boxSizing} onChange={set("boxSizing")} choices={["content-box", "border-box"]} />
      </Section>

      <Section title={elakayAutoText("h_e0a35ec4d2a004", "Espacement")} icon={Square}>
        <SideField label={elakayAutoText("h_9a3f217f8cbcb7", "padding (intérieur)")} value={s.padding} onChange={set("padding")} parse={parseBox} build={buildBox} />
        <SideField label={elakayAutoText("h_062ba563d07cd3", "margin (extérieur)")} value={s.margin} onChange={set("margin")} parse={parseBox} build={buildBox} />
      </Section>

      <Section title={elakayAutoText("h_c746bb02dd1ad9", "Typographie")} icon={Type}>
        <StyleField label="font-size" value={s.fontSize} onChange={set("fontSize")} placeholder={elakayAutoText("h_4678bd4d8aa022", "16px")} />
        <SelectOrFree label="font-weight" value={s.fontWeight} onChange={set("fontWeight")} choices={["300", "400", "500", "600", "700", "800", "900"]} />
        <StyleField label="line-height" value={s.lineHeight} onChange={set("lineHeight")} placeholder="1.5" />
        <StyleField label="letter-spacing" value={s.letterSpacing} onChange={set("letterSpacing")} placeholder={elakayAutoText("h_9a8f94db0c98de", "normal / 0.05em")} />
        <StyleField label="font-family" value={s.fontFamily} onChange={set("fontFamily")} placeholder={elakayAutoText("h_3ff96d71f2abde", "'DM Sans', sans-serif")} />
        <SelectOrFree label="text-align" value={s.textAlign} onChange={set("textAlign")} choices={["left", "center", "right", "justify"]} />
        <SelectOrFree label="text-transform" value={s.textTransform} onChange={set("textTransform")} choices={["none", "uppercase", "lowercase", "capitalize"]} />
        <SelectOrFree label="font-style" value={s.fontStyle} onChange={set("fontStyle")} choices={["normal", "italic"]} />
        <SelectOrFree label="text-decoration" value={s.textDecoration} onChange={set("textDecoration")} choices={["none", "underline", "line-through", "overline"]} />
        <StyleField label={elakayAutoText("h_6dd0fe8001145b", "color")} value={s.color} onChange={set("color")} type="color" />
      </Section>

      <Section title={elakayAutoText("h_cc9d842c2533e7", "Fond & bordures")} icon={Palette}>
        <StyleField label={elakayAutoText("h_248a20b62efba8", "background")} value={s.background} onChange={set("background")} type="color" />
        <StyleField label={elakayAutoText("h_8d895f4c938c45", "background (libre / dégradé)")} value={s.background} onChange={set("background")} placeholder={elakayAutoText("h_3386928915d4bb", "linear-gradient(...)")} />
        <SelectOrFree label="background-size" value={s.backgroundSize} onChange={set("backgroundSize")} choices={["auto", "cover", "contain"]} />
        <SelectOrFree label="background-position" value={s.backgroundPosition} onChange={set("backgroundPosition")} choices={["center", "top", "bottom", "left", "right"]} />
        <StyleField label={elakayAutoText("h_ca6bcd4b6f3fa9", "border")} value={s.border} onChange={set("border")} placeholder={elakayAutoText("h_55b6d7b7a3ec4a", "1px solid #ddd")} />
        <SideField label={elakayAutoText("h_5dcee066436aab", "border-radius (coins)")} value={s.borderRadius} onChange={set("borderRadius")} parse={parseBox} build={buildBox} />
        <StyleField label="box-shadow" value={s.boxShadow} onChange={set("boxShadow")} placeholder={elakayAutoText("h_ef0e88fe43ebb6", "0 4px 12px rgba(0,0,0,.1)")} />
        <StyleField label={elakayAutoText("h_e57362d579b61a", "outline")} value={s.outline} onChange={set("outline")} placeholder={elakayAutoText("h_71f8e7976e4cbc", "none")} />
      </Section>

      <Section title={elakayAutoText("h_15dddd69071e04", "Effets & transform")} icon={Sparkles}>
        <StyleField label={elakayAutoText("h_4546f429052ef5", "opacity")} value={s.opacity} onChange={set("opacity")} placeholder="1" />
        <StyleField label={elakayAutoText("h_dd8ce13ad1ba5d", "transform")} value={s.transform} onChange={set("transform")} placeholder={elakayAutoText("h_e00116650b73dd", "rotate(5deg) scale(1.1)")} />
        <StyleField label={elakayAutoText("h_56bf8ae82a107d", "transition")} value={s.transition} onChange={set("transition")} placeholder={elakayAutoText("h_a29d7eca223689", "all 0.3s ease")} />
        <StyleField label={elakayAutoText("h_4bb4ca75941b7b", "filter")} value={s.filter} onChange={set("filter")} placeholder={elakayAutoText("h_3d54fe1e26557e", "blur(2px)")} />
        <SelectOrFree label={elakayAutoText("h_7c501e0d2e7496", "cursor")} value={s.cursor} onChange={set("cursor")} choices={["default", "pointer", "move", "text", "not-allowed", "grab"]} />
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
//  Fonction Toggle — génère le script consoleJs à partir de la config toggleGroup
//
//  Structure de node.toggleGroup :
//    { id, isDefault, activeStyle: {...css}, inactiveStyle: {...css} }
//
//  Le script auto-généré :
//    1. Marque le wrapper de l'élément avec data-tg="groupId"
//    2. Au clic, active cet élément (activeStyle) et désactive tous les autres
//       du même groupe (inactiveStyle) en cherchant [data-tg="groupId"] dans le DOM
//    3. À l'init, applique le style selon isDefault et l'état courant du groupe
// ---------------------------------------------------------------------------
function buildToggleConsoleJs(tg) {
  if (!tg || !tg.id) return "";
  const G = JSON.stringify(tg.id);
  const A = JSON.stringify(tg.activeStyle   || {});
  const I = JSON.stringify(tg.inactiveStyle || {});
  const D = tg.isDefault ? 1 : 0;
  // css() : applique les styles sur l'élément ET sur son premier enfant texte
  //   → color / fontWeight / fontSize → transmis au 1er p, span, h1-h6 interne
  //   → tous les autres styles (background, border, borderRadius…) → élément lui-même
  return `(function(){var G=${G},A=${A},I=${I},D=${D};setTimeout(function(){`+
    `element.dataset.tg=G;`+
    `var el=element.firstElementChild||element;if(!el)return;`+
    `function css(el,st){`+
      `var tc=el.querySelector('p,span,h1,h2,h3,h4,h5,h6');`+
      `for(var k in st){if(!Object.prototype.hasOwnProperty.call(st,k))continue;`+
        `if((k==='color'||k==='fontWeight'||k==='fontSize')&&tc){tc.style[k]=st[k];}else{el.style[k]=st[k];}}`+
      `el.style.transition=st.transition||'background 0.2s,border-color 0.2s,color 0.2s';`+
    `}`+
    `function activate(){`+
      `document.querySelectorAll('[data-tg="'+G+'"]').forEach(function(w){`+
        `css(w.firstElementChild||w,w===element?A:I);`+
        `w.dataset.tgActive=w===element?'1':'0';`+
      `});`+
    `}`+
    `if(D){activate();}else{`+
      `var h=Array.from(document.querySelectorAll('[data-tg="'+G+'"][data-tg-active="1"]')).length>0;`+
      `if(!h)css(el,I);`+
    `}`+
    `if(!el._tgBound){el._tgBound=true;el.addEventListener('click',activate);}`+
    `},200);})();`;
}

// ---------------------------------------------------------------------------
// Panneau de propriétés
// ---------------------------------------------------------------------------
function PropertiesPanel({ element, onUpdate, onDelete, onDuplicate, freeMode, onNudge, onAddInside, device, layoutDevice = "desktop", onBringFront, onSendBack, pages, onNavigate, boundData, setBoundData, doc, setPageMode, lockPageMode, datasets = SAMPLE_DATASETS, simpleMode = false, page = null, onUpdateNode = null, onBulkUpdateNodes = null }) {
  const [propTab, setPropTab] = useState("general");
  const [openChildActions, setOpenChildActions] = useState({});
  const toggleChildAction = (childId) => setOpenChildActions(prev => ({ ...prev, [childId]: !prev[childId] }));
  if (!element) {
    return (
      <div style={{ padding: "22px" }}>
        {/* Section Mode de page par défaut — visible quand aucun élément n'est sélectionné */}
        <Section title={elakayAutoText("h_ad70c2afa9050c", "Mode de page (par défaut)")} icon={FolderTree}>
          <p style={{ fontSize: "11px", color: "#9b93b8", margin: "0 0 8px", lineHeight: 1.5 }}>{elakayAutoText("h_05f0acb2077980", "Le mode pilote les sources de données disponibles pour le binding. Sauvegardé avec le doc et utilisé par le PageRenderer.")}</p>
          <div style={{ position: "relative" }}>
            <select value={doc?.pageMode || "static"} onChange={(e) => setPageMode?.(e.target.value)} disabled={lockPageMode}
              style={{ ...selectStyle, opacity: lockPageMode ? 0.7 : 1, cursor: lockPageMode ? "not-allowed" : "pointer", paddingRight: lockPageMode ? "32px" : undefined }}>
              {PAGE_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            {lockPageMode && (
              <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", color: "#6d28d9", pointerEvents: "none" }} title={elakayAutoText("h_40c58af6f1aa1c", "Verrouillé par l'application")}>🔒</span>
            )}
          </div>
          {lockPageMode && (
            <p style={{ fontSize: "10px", color: "#e0a800", margin: "6px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_52590e86919c24", "Mode verrouillé par l'application hôte (prop")}<code style={{ fontFamily: "ui-monospace, monospace" }}>lockPageMode</code>).
            </p>
          )}
          {doc?.pageMode && doc.pageMode !== "static" && (() => {
            // Aperçu des jeux de données coordonnés à ce mode
            const matching = Object.entries(datasets).filter(([, d]) => d.pageMode === doc.pageMode);
            return (
              <div style={{ marginTop: "10px", padding: "8px 10px", background: "#f3effe", borderRadius: "8px" }}>
                <div style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 700, marginBottom: "4px" }}>{elakayAutoText("h_d33820dd5c40c3", "Jeux de données disponibles :")}</div>
                {matching.length ? matching.map(([k, d]) => (
                  <div key={k} style={{ fontSize: "11px", color: "#4a4a68", fontFamily: "ui-monospace, monospace" }}>• {d.label}</div>
                )) : <div style={{ fontSize: "11px", color: "#9b93b8" }}>{elakayAutoText("h_90fa121e0fef7e", "Aucun jeu d'exemple pour ce mode.")}</div>}
              </div>
            );
          })()}
        </Section>
        <div style={{ textAlign: "center", color: "#9b93b8", marginTop: "20px" }}>
          <Settings2 size={28} style={{ marginBottom: "10px", opacity: 0.5 }} />
          <p style={{ fontSize: "13px", lineHeight: "1.6" }}>{__pbText("t041", "Sélectionnez un élément dans l'aperçu pour modifier ses propriétés.")}</p>
        </div>
      </div>
    );
  }
  const isMobile = device === "mobile";
  const styleKey = isMobile ? "mobileStyle" : "style";
  // En mobile, on édite les surcharges : on affiche le style effectif (base + surcharges) mais on écrit dans mobileStyle.
  const setStyle = (k, v) => onUpdate({ ...element, [styleKey]: { ...(element[styleKey] || {}), [k]: v } });
  const setProp = (k, v) => onUpdate({ ...element, [k]: v });
  const setRepeatBind = (repeatBind) => onUpdate(withAutoShopUserSync({ ...element, repeatBind }));
  const setOption = (k, v) => onUpdate({ ...element, options: { ...element.options, [k]: v } });
  const setItemStyle = (k, v) => onUpdate({ ...element, itemStyle: { ...element.itemStyle, [k]: v } });
  const setContent = (k, v) => onUpdate({ ...element, content: { ...element.content, [k]: v } });
  const setField = (k, v) => onUpdate({ ...element, field: { ...element.field, [k]: v } });
  const setAttr = (k, v) => onUpdate({ ...element, attrs: { ...element.attrs, [k]: v } });
  const setTextCollapse = (patch) => onUpdate({
    ...element,
    textCollapse: {
      maxChars: 160,
      controlType: "link",
      controlColor: "",
      controlFontSize: "",
      ...(element.textCollapse || {}),
      ...patch,
    },
  });
  const setPos = (axis, v) => {
    onUpdate(withFreeLayout(element, layoutDevice, { [axis]: Math.round(Number(v) || 0) }));
  };
  // Style affiché dans l'inspecteur : base en desktop ; base + surcharges en mobile
  const s = isMobile ? { ...element.style, ...(element.mobileStyle || {}) } : (element.style || {});
  const def = ELEMENT_TYPES[element.type];
  const pos = getFreeLayout(element, layoutDevice);

  return (
    <div style={{ padding: "18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: "700", color: "#1a1a2e" }}>
          {def?.icon && React.createElement(def.icon, { size: 16, color: "#6d28d9" })}{def?.label}
        </span>
        <div style={{ display: "flex", gap: "6px" }}>
          <button onClick={onDuplicate} title={__pbText("t026","Dupliquer")} style={iconBtn}><Copy size={15} /></button>
          <button onClick={onDelete} title={__pbText("t027","Supprimer")} style={{ ...iconBtn, color: "#e11d48" }}><Trash2 size={15} /></button>
        </div>
      </div>

      {/* Indique quel jeu de styles on édite : desktop (base) ou mobile (surcharges) */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", marginBottom: "16px", borderRadius: "10px", background: isMobile ? "#fff4e6" : "#f3effe", border: `1px solid ${isMobile ? "#ffd9a8" : "#e4dcfb"}` }}>
        {isMobile ? <Smartphone size={15} color="#d97706" /> : <Monitor size={15} color="#6d28d9" />}
        <span style={{ fontSize: "12px", fontWeight: 600, color: isMobile ? "#b45309" : "#6d28d9" }}>
          {isMobile ? elakayAutoText("h_7427f42df9f300", "Styles MOBILE (surcharges du desktop)") : elakayAutoText("h_dc768c8e35af64", "Styles DESKTOP (base)")}
        </span>
      </div>
      {isMobile && (element.mobileStyle && Object.keys(element.mobileStyle).length > 0) && (
        <button onClick={() => onUpdate({ ...element, mobileStyle: {} })} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginBottom: "14px", fontSize: "12px" }}>
          <X size={14} />{elakayAutoText("h_e7ec5b898ae480", "Réinitialiser les surcharges mobile")}</button>
      )}

      {/* ── Onglets Général / Mapping ── */}
      <div style={{ display: "flex", borderBottom: "2px solid #f0edf8", marginBottom: "14px" }}>
        {[
          { key: "general", label: elakayAutoText("h_5376df989fac86", "Général"), icon: Settings2 },
          { key: "mapping", label: elakayAutoText("h_def2eba5956295", "Mapping"), icon: LayoutGrid },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setPropTab(key)} style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "4px",
            padding: "7px 4px", fontSize: "11px", fontWeight: propTab === key ? 700 : 500,
            color: propTab === key ? "#6d28d9" : "#9b93b8", background: "transparent", borderTop: "none", borderRight: "none", borderLeft: "none",
            borderBottom: `2px solid ${propTab === key ? "#6d28d9" : "transparent"}`,
            marginBottom: "-2px", cursor: "pointer", transition: "all 0.15s"
          }}>
            <Icon size={12}/> {label}
          </button>
        ))}
      </div>

      {/* ── Badges récap des propriétés actives ── */}
      {(() => {
        const badges = [];
        const anim = element.animation && element.animation !== "none";
        const hasAction = element.clickActionType && element.clickActionType !== "none";
        const hasLink = element.linkHref || element.linkTarget;
        const hasBind = !!element.bindKey;
        const hasRepeat = !!element.repeatBind;
        if (anim) badges.push({ label: `✨ ${element.animation}`, color: "#a855f7", bg: "#faf5ff", tab: "anim" });
        if (hasAction) {
          const fn = element.clickActionFunction || element.clickActionType;
          const isCart = fn === "addToCart";
          badges.push({ label: isCart ? `🛒 addToCart` : `⚡ ${fn}`, color: isCart ? "#059669" : "#d97706", bg: isCart ? "#ecfdf5" : "#fffbeb", tab: "general" });
        }
        if (hasLink) badges.push({ label: `🌐 lien`, color: "#0369a1", bg: "#f0f9ff", tab: "general" });
        if (hasBind) badges.push({ label: `🔗 ${element.bindKey}`, color: "#059669", bg: "#f0fdf4", tab: "mapping" });
        if (hasRepeat) badges.push({ label: `↻ mapping`, color: "#6d28d9", bg: "#f3effe", tab: "mapping" });
        if (badges.length === 0) return null;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "10px" }}>
            {badges.map((b, i) => (
              <button key={i} onClick={() => setPropTab(b.tab === "anim" ? "general" : b.tab)}
                style={{ fontSize: "9px", fontWeight: 700, padding: "2px 7px", borderRadius: "999px", border: `1px solid ${b.color}20`, background: b.bg, color: b.color, cursor: "pointer", maxWidth: "130px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={b.label}>{b.label}</button>
            ))}
          </div>
        );
      })()}

      {/* Ajouter un élément à l'intérieur (conteneurs, grilles) — desktop seulement */}
      {propTab === "general" && !isMobile && def?.isContainer && (
        <button onClick={onAddInside} style={{ ...primaryBtn, width: "100%", justifyContent: "center", marginBottom: "16px" }}>
          <Plus size={15} /> {__pbText("t028","Ajouter un élément à l'intérieur")}
        </button>
      )}

      {/* Actions de clic universelles : fonction / URL / page interne + fallback lien */}
      {propTab === "general" && !isMobile && (
        <Section title={elakayAutoText("h_2cb3a80181cbf0", "Action Au Clic")} icon={ChevronRight}>
          {/* Type d'action */}
          <label style={{ display: "block", marginBottom: "12px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_b32cc8aaca3ce2", "Type d'action")}</span>
            <select value={element.clickActionType || "none"} onChange={(e) => setProp("clickActionType", e.target.value)} style={selectStyle}>
              <option value="none">{elakayAutoText("h_e8f882731020de", "Aucune")}</option>
              <option value="function">{elakayAutoText("h_4780483a7f34f0", "Fonction shopUser")}</option>
              <option value="url">{elakayAutoText("h_c9ef19a1900484", "URL / href")}</option>
              <option value="page">{elakayAutoText("h_ada824c94f49b9", "Page interne")}</option>
            </select>
          </label>

          {/* ── Fonction shopUser ── */}
          {(element.clickActionType || "none") === "function" && (() => {
            const selectedFn = SHOPUSER_RUNTIME_FUNCTIONS_META.find(f => f.name === element.clickActionFunction);
            const argc = selectedFn?.argc ?? 0;
            const argNames = parseArgNames(selectedFn?.signature || "");
            const argKeys = ["clickActionArg1", "clickActionArg2", "clickActionArg3"];
            return (
              <>
                <label style={{ display: "block", marginBottom: "10px" }}>
                  <span style={fieldLabel}>{elakayAutoText("h_30153102ebaf93", "Fonction")}</span>
                  <select value={element.clickActionFunction || ""} onChange={(e) => setProp("clickActionFunction", e.target.value)} style={selectStyle}>
                    <option value="">{elakayAutoText("h_718f6d24b6b218", "— choisir —")}</option>
                    {SHOPUSER_RUNTIME_FUNCTIONS_META.map((fn) => (
                      <option key={fn.name} value={fn.name}>{fn.signature} [{fn.argc}]</option>
                    ))}
                  </select>
                </label>
                {argc > 0 && Array.from({ length: argc }, (_, i) => (
                  <ArgInput
                    key={argKeys[i]}
                    label={`${argNames[i] || `Arg ${i + 1}`}${argNames[i] ? "" : " (optionnel)"}`}
                    value={element[argKeys[i]] || ""}
                    onChange={(v) => setProp(argKeys[i], v)}
                    placeholder={elakayAutoText("h_46bd8e08fe2166", "ex: items.categoryID, store.products.0._id…")}
                  />
                ))}
              </>
            );
          })()}

          {/* ── URL / href ── */}
          {["url", "href", "link"].includes(element.clickActionType || "none") && (
            <>
              <ArgInput
                label={elakayAutoText("h_c9ef19a1900484", "URL / href")}
                value={element.clickActionHref || ""}
                onChange={(v) => setProp("clickActionHref", v)}
                selectOptions={CLICK_ARG_URL_KEYS}
                placeholder="https://… ou store.links.shopPage"
              />
              <label style={{ display: "block", marginBottom: "10px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_f2e32418126d1f", "Ouverture")}</span>
                <select value={element.clickActionTargetBlank ? "_blank" : "_self"} onChange={(e) => setProp("clickActionTargetBlank", e.target.value === "_blank")} style={selectStyle}>
                  <option value="_self">{elakayAutoText("h_c318cf09991f66", "Même onglet")}</option>
                  <option value="_blank">{elakayAutoText("h_9e3971ab0881a8", "Nouvel onglet")}</option>
                </select>
              </label>
            </>
          )}

          {/* ── Page interne ── */}
          {(element.clickActionType || "none") === "page" && (
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_ada824c94f49b9", "Page interne")}</span>
              <select value={element.clickActionPage || ""} onChange={(e) => setProp("clickActionPage", e.target.value)} style={selectStyle}>
                <option value="">{elakayAutoText("h_6bbdf2ac7c4d7d", "— aucune —")}</option>
                {(pages || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}

          {/* ── Fallback lien universel ── */}
          <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #ece9f5" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "6px" }}>{elakayAutoText("h_fad654bbd8715c", "Fallback Lien Universel")}</span>
            <ArgInput
              label={elakayAutoText("h_b4f1fa2af8238b", "URL / href (fallback)")}
              value={element.linkHref || ""}
              onChange={(v) => setProp("linkHref", v)}
              selectOptions={CLICK_ARG_URL_KEYS}
              placeholder="https://… (optionnel)"
            />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_5b00c1145b2d5c", "ou page interne")}</span>
              <select value={element.linkTarget || ""} onChange={(e) => setProp("linkTarget", e.target.value)} style={selectStyle}>
                <option value="">{elakayAutoText("h_6bbdf2ac7c4d7d", "— aucune —")}</option>
                {(pages || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            {(element.linkHref || element.linkTarget) && (
              <label style={{ display: "block", marginBottom: "4px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_f2e32418126d1f", "Ouverture")}</span>
                <select value={element.linkTargetBlank ? "_blank" : "_self"} onChange={(e) => setProp("linkTargetBlank", e.target.value === "_blank")} style={selectStyle}>
                  <option value="_self">{elakayAutoText("h_c318cf09991f66", "Même onglet")}</option>
                  <option value="_blank">{elakayAutoText("h_9e3971ab0881a8", "Nouvel onglet")}</option>
                </select>
              </label>
            )}
          </div>
          <p style={{ fontSize: "10px", color: "#b3aacb", margin: "6px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_132a5e4cf7668e", "Priorité :")}<strong style={{ color: "#6d28d9" }}>{elakayAutoText("h_1afc0f1a9f214d", "Action au clic")}</strong>{elakayAutoText("h_217229cdd04ac7", "→ Fallback lien universel.")}</p>
        </Section>
      )}

      {/* ── Fonction Toggle : activer / désactiver ── */}
      {propTab === "general" && (() => {
        const tg = element.toggleGroup || null;
        const enabled = !!tg;

        // Applique un patch sur toggleGroup + régénère consoleJs
        const setTg = (patch) => {
          const next = { ...tg, ...patch };
          onUpdate({ ...element, toggleGroup: next, consoleJs: buildToggleConsoleJs(next) });
        };

        const disableToggle = () => {
          const { toggleGroup, consoleJs, ...noToggle } = element; // eslint-disable-line no-unused-vars
          onUpdate(noToggle);
        };

        // ── Auto-configure ────────────────────────────────────────────────────
        // Trouve les frères (siblings) de l'élément dans le conteneur parent
        // et applique le même groupe toggle à tous en un seul appel.
        //   - Élément courant (ou non-mapping) → actif (isDefault:true)
        //   - Frères avec repeatBind           → inactifs (isDefault:false)
        //   - Frères sans repeatBind           → inactifs (isDefault:false)
        // L'utilisateur peut ensuite ajuster manuellement via les checkboxes.
        const autoConfigureToggle = () => {
          if (!onBulkUpdateNodes) return;
          const parent = page ? findParentInTree(page.children || [], element.id) : null;
          const siblings = (parent ? parent.children : page?.children) || [];
          const gId = `tg-${uid()}`;
          const aStyle = tg?.activeStyle  || {};
          const iStyle = tg?.inactiveStyle || {};
          const updates = [];
          for (const sib of siblings) {
            if (sib.id === element.id) {
              // Élément courant → actif
              const tgA = { id: gId, isDefault: true,  activeStyle: aStyle, inactiveStyle: iStyle };
              onUpdate({ ...element, toggleGroup: tgA, consoleJs: buildToggleConsoleJs(tgA) });
            } else {
              // Frères → inactifs
              const tgI = { id: gId, isDefault: false, activeStyle: aStyle, inactiveStyle: iStyle };
              updates.push({ nodeId: sib.id, patch: { toggleGroup: tgI, consoleJs: buildToggleConsoleJs(tgI) } });
            }
          }
          if (updates.length) onBulkUpdateNodes(updates);
        };

        // ── Checkbox membres du groupe ────────────────────────────────────────
        // Ajoute/retire un élément du groupe (inactif) depuis la liste de la page.
        const toggleGroupMember = (targetId, shouldAdd) => {
          if (!onUpdateNode || !tg?.id) return;
          if (shouldAdd) {
            const tgI = { id: tg.id, isDefault: false, activeStyle: tg.activeStyle || {}, inactiveStyle: tg.inactiveStyle || {} };
            onUpdateNode(targetId, { toggleGroup: tgI, consoleJs: buildToggleConsoleJs(tgI) });
          } else {
            onUpdateNode(targetId, { toggleGroup: undefined, consoleJs: undefined });
          }
        };

        // Liste plate de tous les éléments de la page (hors élément courant)
        const allPageElems = flattenPageElements(page?.children || []).filter(e => e.id !== element.id);

        // Mini-éditeur de propriété CSS
        const MiniStyleRow = ({ label, k, s, onChange }) => (
          <label style={{ display:"block", marginBottom:"4px" }}>
            <span style={{ fontSize:"10px", color:"#9b93b8", display:"block", marginBottom:"2px" }}>{label}</span>
            <input value={s?.[k] ?? ""} onChange={(e) => onChange({ ...(s||{}), [k]: e.target.value })}
              style={{ width:"100%", padding:"4px 8px", fontSize:"11px", fontFamily:"ui-monospace,monospace", border:"1px solid #e4dcfb", borderRadius:"6px", outline:"none", boxSizing:"border-box", background:"#fff" }} />
          </label>
        );

        const StyleBlock = ({ title, s, onChange }) => (
          <div style={{ padding:"8px 10px", background:"#faf9fd", borderRadius:"8px", border:"1px solid #e4dcfb", marginBottom:"8px" }}>
            <div style={{ fontSize:"10px", fontWeight:700, color:"#7a73a0", marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.05em" }}>{title}</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"5px" }}>
              {[
                { label:elakayAutoText("h_248a20b62efba8", "background"),   k:"background"   },
                { label:elakayAutoText("h_6dd0fe8001145b", "color"),        k:"color"        },
                { label:elakayAutoText("h_ca6bcd4b6f3fa9", "border"),       k:"border"       },
                { label:elakayAutoText("h_0a1c695c3dc944", "borderRadius"), k:"borderRadius" },
                { label:elakayAutoText("h_7a4c1125f5e27a", "fontWeight"),   k:"fontWeight"   },
                { label:elakayAutoText("h_4546f429052ef5", "opacity"),      k:"opacity"      },
              ].map(({ label, k }) => <MiniStyleRow key={k} label={label} k={k} s={s} onChange={onChange} />)}
            </div>
            <MiniStyleRow label={elakayAutoText("h_dd4355d9d6a299", "padding")}    k="padding"    s={s} onChange={onChange} />
            <MiniStyleRow label={elakayAutoText("h_82a4c59d7b814a", "boxShadow")}  k="boxShadow"  s={s} onChange={onChange} />
            <MiniStyleRow label={elakayAutoText("h_aac3a17e35fbcb", "fontSize")}   k="fontSize"   s={s} onChange={onChange} />
            <MiniStyleRow label={elakayAutoText("h_56bf8ae82a107d", "transition")} k="transition" s={s} onChange={onChange} />
          </div>
        );

        return (
          <Section title={elakayAutoText("h_bec93c21ad3603", "Fonction Toggle — Activer / Désactiver")} icon={ToggleLeft}>

            {/* ── Barre d'actions principale ── */}
            <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"10px" }}>
              {/* Activer / Retirer */}
              <button
                onClick={() => enabled ? disableToggle() : setTg({ id:`tg-${uid()}`, isDefault:true, activeStyle:{}, inactiveStyle:{} })}
                style={{ flex:1, padding:"6px 10px", fontSize:"11px", fontWeight:700,
                  color: enabled ? "#e11d48" : "#fff",
                  background: enabled ? "transparent" : "#6d28d9",
                  border: enabled ? "1px solid #fda4af" : "none",
                  borderRadius:"6px", cursor:"pointer" }}>
                {enabled ? elakayAutoText("h_fafeab2d9e29d8", "✕ Retirer") : elakayAutoText("h_fc329369a42315", "Activer le toggle")}
              </button>
              {/* Auto-configure */}
              {enabled && onBulkUpdateNodes && (
                <button
                  onClick={autoConfigureToggle}
                  title={elakayAutoText("h_ff3f4db9c5b6a5", "Détecte les frères (siblings) du conteneur parent et leur applique automatiquement le même groupe toggle")}
                  style={{ flex:1, padding:"6px 10px", fontSize:"11px", fontWeight:700, color:"#fff",
                    background:"linear-gradient(135deg,#7c3aed,#6d28d9)", border:"none",
                    borderRadius:"6px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"4px" }}>
                  <Sparkles size={11}/>{elakayAutoText("h_c614ba7c453cdc", "Auto")}</button>
              )}
            </div>

            {enabled && (
              <>
                {/* ── ID du groupe ── */}
                <label style={{ display:"block", marginBottom:"8px" }}>
                  <span style={fieldLabel}>{elakayAutoText("h_9a52416ba94ea7", "ID du groupe")}</span>
                  <input value={tg.id || ""} onChange={(e) => setTg({ id: e.target.value })}
                    placeholder={elakayAutoText("h_65186f8aa6959b", "ex: catNav, tabMenu…")}
                    style={{ width:"100%", marginTop:"4px", padding:"6px 10px", fontSize:"12px",
                      fontFamily:"ui-monospace,monospace", border:"1px solid #e4dcfb",
                      borderRadius:"7px", outline:"none", boxSizing:"border-box" }} />
                  <p style={{ fontSize:"9px", color:"#b3aacb", marginTop:"2px" }}>{elakayAutoText("h_38d67a10d053f7", "Même ID = même groupe exclusif (1 actif à la fois).")}</p>
                </label>

                {/* ── Actif par défaut ── */}
                <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"10px",
                  padding:"7px 10px",
                  background: tg.isDefault ? "#f3effe" : "#faf9fd",
                  borderRadius:"7px", border:`1px solid ${tg.isDefault ? "#c4b5fd" : "#e4dcfb"}` }}>
                  <input type="checkbox" id="tg-default" checked={!!tg.isDefault}
                    onChange={(e) => setTg({ isDefault: e.target.checked })}
                    style={{ cursor:"pointer", accentColor:"#6d28d9" }} />
                  <label htmlFor="tg-default" style={{ fontSize:"12px", fontWeight:600,
                    color: tg.isDefault ? "#6d28d9" : "#4a4a68", cursor:"pointer" }}>{elakayAutoText("h_d1b6c22731d51f", "Actif par défaut au chargement")}</label>
                </div>

                {/* ── Styles ACTIF / INACTIF ── */}
                <StyleBlock title={elakayAutoText("h_12bfab1157053b", "Style ACTIF")}   s={tg.activeStyle   || {}} onChange={(s) => setTg({ activeStyle:   s })} />
                <StyleBlock title={elakayAutoText("h_3fd2f79120fa41", "Style INACTIF")} s={tg.inactiveStyle || {}} onChange={(s) => setTg({ inactiveStyle: s })} />

                {/* ── Membres du groupe — méthode manuelle (checkboxes) ── */}
                {tg.id && allPageElems.length > 0 && (
                  <div style={{ marginTop:"10px" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"5px" }}>
                      <span style={fieldLabel}>{elakayAutoText("h_dd6c5b201ea1b0", "Membres du groupe")}<code style={{ fontWeight:400, color:"#6d28d9" }}>{tg.id}</code></span>
                      <span style={{ fontSize:"9px", color:"#b3aacb" }}>
                        {allPageElems.filter(e => e.toggleGroup?.id === tg.id).length}{elakayAutoText("h_e57bd3f08815e3", "lié(s)")}</span>
                    </div>
                    <div style={{ maxHeight:"200px", overflowY:"auto", border:"1px solid #e4dcfb", borderRadius:"8px", background:"#faf9fd" }}>
                      {allPageElems.map((e) => {
                        const inGroup  = e.toggleGroup?.id === tg.id;
                        const isActive = inGroup && e.toggleGroup?.isDefault;
                        const def = ELEMENT_TYPES[e.type];
                        const lbl = (e.text ? (e.text.length > 18 ? e.text.slice(0,18)+"…" : e.text) : (def?.label || e.type));
                        return (
                          <label key={e.id}
                            style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 8px",
                              borderBottom:"1px solid #f0edf8", cursor:"pointer",
                              background: inGroup ? (isActive ? "#f3effe" : "#fafafa") : "transparent" }}>
                            <input type="checkbox" checked={!!inGroup}
                              onChange={(ev) => toggleGroupMember(e.id, ev.target.checked)}
                              style={{ accentColor:"#6d28d9", cursor:"pointer", flexShrink:0 }} />
                            {/* Indentation visuelle selon la profondeur */}
                            <span style={{ display:"inline-block", width:`${e.depth*8}px`, flexShrink:0 }} />
                            {/* Icône type */}
                            {def?.icon && React.createElement(def.icon, { size:10, color: inGroup ? "#6d28d9" : "#b3aacb" })}
                            {/* Label */}
                            <span style={{ fontSize:"10px", color: inGroup ? "#2a2547" : "#7a73a0",
                              flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                              title={lbl}>{lbl}</span>
                            {/* Badges état */}
                            {inGroup && (
                              <span style={{ fontSize:"8px", padding:"1px 5px", borderRadius:"99px", flexShrink:0,
                                background: isActive ? "#6d28d9" : "#ede9fe",
                                color:      isActive ? "#fff"    : "#6d28d9" }}>
                                {isActive ? elakayAutoText("h_232931936a9b0a", "actif") : elakayAutoText("h_3e1d24c5402bd4", "inactif")}
                              </span>
                            )}
                            {e.repeatBind && (
                              <span style={{ fontSize:"8px", color:"#d97706", background:"#fef3c7",
                                padding:"1px 5px", borderRadius:"99px", flexShrink:0 }}>{elakayAutoText("h_37745ed7a0f005", "map")}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <p style={{ fontSize:"9px", color:"#b3aacb", marginTop:"4px", lineHeight:1.4 }}>{elakayAutoText("h_36f74b0611dcbf", "✓ = dans le groupe (inactif par défaut). Cochez pour ajouter, décochez pour retirer.")}<br/>
                      <strong style={{ color:"#6d28d9" }}>{elakayAutoText("h_37745ed7a0f005", "map")}</strong>{elakayAutoText("h_a00473ba0bc2d8", "= élément repeatBind — chaque instance répétée rejoint le groupe.")}</p>
                  </div>
                )}

                {/* ── Script JS généré ── */}
                <details style={{ marginTop:"8px" }}>
                  <summary style={{ fontSize:"10px", color:"#9b93b8", cursor:"pointer", fontFamily:"ui-monospace,monospace" }}>{elakayAutoText("h_f07133789c83ef", "Voir le script JS généré")}</summary>
                  <pre style={{ margin:"6px 0 0", padding:"8px", background:"#1a1a2e", color:"#d6cfec",
                    borderRadius:"6px", fontSize:"9px", lineHeight:1.4, overflowX:"auto",
                    maxHeight:"80px", whiteSpace:"pre-wrap", wordBreak:"break-all" }}>
                    {buildToggleConsoleJs(tg)}
                  </pre>
                </details>

                {/* Notes */}
                <p style={{ fontSize:"9px", color:"#b3aacb", lineHeight:1.5, margin:"6px 0 0" }}>
                  <strong style={{ color:"#6d28d9" }}>{elakayAutoText("h_ce840b0fe86ec7", "color / fontWeight / fontSize")}</strong>{elakayAutoText("h_7597a79ee99cdd", "→ enfant texte (p, span, h1–h6).")}<br/>{elakayAutoText("h_4efae2266d96de", "Autres styles → élément lui-même.")}</p>
                {element.repeatBind && (
                  <div style={{ marginTop:"6px", padding:"6px 9px", background:"#f0fdf4", borderRadius:"6px", border:"1px solid #bbf7d0" }}>
                    <span style={{ fontSize:"9px", fontWeight:700, color:"#16a34a" }}>{elakayAutoText("h_004f04274d5736", "↻ Mapping détecté")}</span>
                    <p style={{ fontSize:"9px", color:"#166534", lineHeight:1.4, margin:"2px 0 0" }}>{elakayAutoText("h_cfe4570a017838", "Chaque instance répétée rejoindra automatiquement le groupe")}<strong>{tg.id}</strong>.
                    </p>
                  </div>
                )}
              </>
            )}

            {!enabled && (
              <p style={{ fontSize:"10px", color:"#b3aacb", lineHeight:1.5, margin:"0" }}>{elakayAutoText("h_f5c24e93528368", "Activez pour lier cet élément à un groupe exclusif. Utilisez")}<strong style={{ color:"#6d28d9" }}>{elakayAutoText("h_c614ba7c453cdc", "Auto")}</strong>{elakayAutoText("h_eb8db5f9eb3383", "pour détecter les frères automatiquement, ou cochez manuellement les autres éléments de la page.")}<br/>{elakayAutoText("h_078a5e8b9356c1", "Compatible avec les éléments")}<strong style={{ color:"#6d28d9" }}>{elakayAutoText("h_746fe09da998d6", "mapping (repeatBind)")}</strong>.
              </p>
            )}
          </Section>
        );
      })()}

      {/* Dimensions rapides : width / height toujours accessibles en tête */}
      <Section title={elakayAutoText("h_6d0058fce26e1c", "Taille rapide")} icon={Move}>
        <div style={{ display: "flex", gap: "8px" }}>
          <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_70bec8a9bf5ecd", "width")} value={s.width} onChange={(v) => setStyle("width", v)} placeholder={elakayAutoText("h_dc2ece3687fd88", "auto / 100%")} /></div>
          <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_73e7e545c519a3", "height")} value={s.height} onChange={(v) => setStyle("height", v)} placeholder={elakayAutoText("h_0d612c12d2ac33", "auto")} /></div>
        </div>
        <SelectOrFree label={elakayAutoText("h_10e80591d92f76", "overflow")} value={s.overflow} onChange={(v) => setStyle("overflow", v)} choices={["visible", "hidden", "scroll", "auto", "clip"]} />
        <div style={{ display: "flex", gap: "8px" }}>
          <div style={{ flex: 1 }}><SelectOrFree label="overflow-x" value={s.overflowX} onChange={(v) => setStyle("overflowX", v)} choices={["visible", "hidden", "scroll", "auto", "clip"]} /></div>
          <div style={{ flex: 1 }}><SelectOrFree label="overflow-y" value={s.overflowY} onChange={(v) => setStyle("overflowY", v)} choices={["visible", "hidden", "scroll", "auto", "clip"]} /></div>
        </div>
      </Section>

      {/* Presets de taille rapides : appliquent une largeur courante en un clic */}
      <Section title={elakayAutoText("h_d8a45d7b599822", "Tailles prédéfinies")} icon={LayoutGrid}>
        <span style={fieldLabel}>{elakayAutoText("h_43a7ce0a2f1de4", "Largeur")}</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginTop: "4px", marginBottom: "12px" }}>
          {SIZE_PRESETS.map((p) => (
            <button key={p.label} onClick={() => { setStyle("width", p.w); if (p.h) setStyle("height", p.h); }}
              style={{ ...ghostBtn, justifyContent: "center", fontSize: "12px", padding: "7px 8px" }} title={`${p.w}${p.h ? " × " + p.h : ""}`}>
              {p.label}
            </button>
          ))}
        </div>
        <span style={fieldLabel}>{elakayAutoText("h_255cbd69a277a0", "Hauteur")}</span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginTop: "4px" }}>
          {HEIGHT_PRESETS.map((p) => (
            <button key={p.label} onClick={() => setStyle("height", p.h)}
              style={{ ...ghostBtn, justifyContent: "center", fontSize: "12px", padding: "7px 8px" }} title={p.h}>
              {p.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Position (mode libre) — disponible pour TOUS les éléments */}
      {freeMode && (
        <Section title={elakayAutoText("h_cf1c85adba548e", "Position")} icon={Move}>
          <div style={{ display: "flex", gap: "8px" }}>
            <label style={{ flex: 1 }}>
              <span style={fieldLabel}>{elakayAutoText("h_aa03f88660c022", "X (px)")}</span>
              <input type="number" value={pos.x} onChange={(e) => setPos("x", e.target.value)}
                style={{ width: "100%", marginTop: "4px", padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
            </label>
            <label style={{ flex: 1 }}>
              <span style={fieldLabel}>{elakayAutoText("h_0978e5c08bde75", "Y (px)")}</span>
              <input type="number" value={pos.y} onChange={(e) => setPos("y", e.target.value)}
                style={{ width: "100%", marginTop: "4px", padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "4px", width: "120px", margin: "12px auto 4px" }}>
            <span />
            <button onClick={() => onNudge(element.id, 0, -1)} style={padBtn} title={elakayAutoText("h_9315cc5d716f5c", "Haut (↑)")}><ChevronUp size={16} /></button>
            <span />
            <button onClick={() => onNudge(element.id, -1, 0)} style={padBtn} title={elakayAutoText("h_e4504e8c4bff6d", "Gauche (←)")}><ChevronLeft size={16} /></button>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#9b93b8" }}><Move size={13} /></span>
            <button onClick={() => onNudge(element.id, 1, 0)} style={padBtn} title={elakayAutoText("h_7c06ac5c6623b8", "Droite (→)")}><ChevronRight size={16} /></button>
            <span />
            <button onClick={() => onNudge(element.id, 0, 1)} style={padBtn} title={elakayAutoText("h_7deba139113e30", "Bas (↓)")}><ChevronDown size={16} /></button>
            <span />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginTop: "10px" }}>
            <button type="button" onClick={onBringFront} style={ghostBtn}><ChevronUp size={14} />{elakayAutoText("h_pb_bring_front", "Au premier plan")}</button>
            <button type="button" onClick={onSendBack} style={ghostBtn}><ChevronDown size={14} />{elakayAutoText("h_pb_send_back", "À l’arrière-plan")}</button>
          </div>
          <label style={{ display: "block", marginTop: "10px" }}>
            <span style={fieldLabel}>Z-index</span>
            <input type="number" value={pos.z} onChange={(event) => onUpdate(withFreeLayout(element, layoutDevice, { z: event.target.value }))} style={{ width: "100%", marginTop: 4, padding: "8px 10px", border: "1px solid #e4e0f0", borderRadius: 8 }} />
          </label>
          <p style={{ fontSize: "11px", color: "#9b93b8", textAlign: "center", margin: "6px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_c9fe800223eca0", "Glissez l'élément à la souris, ou utilisez les flèches du clavier (Maj = pas de 10px).")}</p>
        </Section>
      )}

      {/* Positionnement rapide : grille 3×3 en pourcentage de la page */}
      {freeMode && (
        <Section title={elakayAutoText("h_57e745de71025b", "Position rapide")} icon={LayoutGrid}>
          <div style={{ display: "flex", gap: "6px", marginBottom: "10px", justifyContent: "center" }}>
            <button onClick={() => onUpdate(withFreeLayout(element, layoutDevice, { unit: "%" }))}
              style={{ ...toggleBtn((pos.unit || "%") === "%"), padding: "5px 16px" }}>%</button>
            <button onClick={() => onUpdate(withFreeLayout(element, layoutDevice, { unit: "px" }))}
              style={{ ...toggleBtn(pos.unit === "px"), padding: "5px 16px" }}>{elakayAutoText("h_07a65dd6489a00", "px")}</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", width: "132px", margin: "0 auto" }}>
            {((pos.unit === "px") ? QUICK_POS_PX : QUICK_POS).map((q) => (
              <button key={q.label} onClick={() => onUpdate(withFreeLayout(element, layoutDevice, { x: q.x, y: q.y, unit: pos.unit === "px" ? "px" : "%" }))}
                style={{ ...padBtn, fontSize: "16px", height: "36px" }} title={`x ${q.x}${pos.unit === "px" ? "px" : "%"} · y ${q.y}${pos.unit === "px" ? "px" : "%"}`}>
                {q.label}
              </button>
            ))}
          </div>
          <p style={{ fontSize: "11px", color: "#9b93b8", textAlign: "center", margin: "10px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_066302fb85322d", "Place l'élément dans la zone choisie (coins, bords, centre)")}{pos.unit === "px" ? elakayAutoText("h_1ddda764c6d24a", ", en pixels.") : elakayAutoText("h_f767c55ffdcc5d", ", en pourcentage de la page.")}
          </p>
        </Section>
      )}

      {/* En vue mobile, on s'arrête à taille + position. Le reste (contenu, styles) n'apparaît qu'en desktop. */}
      {propTab === "general" && !isMobile && (<>
      {/* Contenu / attributs (éléments simples uniquement) */}
      {(element.type === "heading" || element.type === "text" || element.type === "image" || element.type === "button") && (
        <Section title={elakayAutoText("h_f3cb82af6c8203", "Contenu")} icon={AlignLeft}>
          {"text" in element && <AutoTextField label={elakayAutoText("h_ff9afef1eae0a9", "Texte")} value={element.text} onChange={(v) => setProp("text", v)} />}
          {element.type === "heading" && (
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_ec78a0b5e0b7d9", "Balise HTML")}</span>
              <select value={element.tag} onChange={(e) => setProp("tag", e.target.value)} style={selectStyle}>
                {["h1", "h2", "h3", "h4", "h5", "h6"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          )}
          {element.type === "image" && (
            <>
              <MediaUrlField
                label={elakayAutoText("h_f9e8e497bf6ba0", "Source (URL)")}
                value={element.src}
                onChange={(v) => setProp("src", v)}
                onMediaSelect={(media) => onUpdate({ ...element, src: media.url, mediaRef: media.assetId || null })}
                kind="image"
              />
              <StyleField label={elakayAutoText("h_6b494cc47f9be4", "Texte alternatif")} value={element.alt} onChange={(v) => setProp("alt", v)} />
            </>
          )}
          {element.type === "button" && <StyleField label={elakayAutoText("h_7ccdb77d26c871", "Lien (href)")} value={element.href} onChange={(v) => setProp("href", v)} placeholder="https://..." />}
        </Section>
      )}

      {canUseTextCollapse(element) && (
        <Section title={elakayAutoText("h_pb_text_collapse_title", "Réduction du texte")} icon={ChevronsUpDown}>
          <CheckField
            label={elakayAutoText("h_pb_text_collapse_enabled", "Réduire le texte après un nombre de caractères")}
            checked={!!element.textCollapse?.enabled}
            onChange={(value) => setTextCollapse({ enabled: value })}
          />
          {element.textCollapse?.enabled && (
            <>
              <label style={{ display: "block", marginBottom: "10px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_pb_text_collapse_max", "Nombre de caractères")}</span>
                <input
                  type="number"
                  min={1}
                  value={Math.max(1, Number(element.textCollapse?.maxChars) || 160)}
                  onChange={(event) => setTextCollapse({ maxChars: Math.max(1, Number(event.target.value) || 1) })}
                  style={selectStyle}
                />
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ flex: 1 }}>
                  <StyleField
                    label={elakayAutoText("h_pb_text_collapse_more", "See more")}
                    value={element.textCollapse?.seeMoreLabel || ""}
                    onChange={(value) => setTextCollapse({ seeMoreLabel: value })}
                    placeholder={getLocalizedLinkText("seeMore", "See More")}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <StyleField
                    label={elakayAutoText("h_pb_text_collapse_less", "See less")}
                    value={element.textCollapse?.seeLessLabel || ""}
                    onChange={(value) => setTextCollapse({ seeLessLabel: value })}
                    placeholder={getLocalizedLinkText("seeLess", "See Less")}
                  />
                </div>
              </div>
              <label style={{ display: "block", marginBottom: "8px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_pb_text_collapse_control", "Contrôle")}</span>
                <select
                  value={element.textCollapse?.controlType || "link"}
                  onChange={(event) => setTextCollapse({ controlType: event.target.value })}
                  style={selectStyle}
                >
                  <option value="link">{elakayAutoText("h_pb_text_collapse_link", "Link")}</option>
                  <option value="button">{elakayAutoText("h_pb_text_collapse_button", "Button")}</option>
                </select>
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ flex: 1 }}>
                  <StyleField
                    type="color"
                    label={elakayAutoText("h_pb_text_collapse_control_color", "Couleur")}
                    value={element.textCollapse?.controlColor || ""}
                    onChange={(value) => setTextCollapse({ controlColor: value })}
                    placeholder={elakayAutoText("h_pb_text_collapse_color_placeholder", "inherit / #6d28d9")}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <StyleField
                    label={elakayAutoText("h_pb_text_collapse_control_size", "Taille")}
                    value={element.textCollapse?.controlFontSize || ""}
                    onChange={(value) => setTextCollapse({ controlFontSize: value })}
                    placeholder={elakayAutoText("h_pb_text_collapse_size_placeholder", "inherit / 14px")}
                  />
                </div>
              </div>
              <p style={{ fontSize: "10px", color: "#b3aacb", margin: "6px 0 0", lineHeight: 1.5 }}>
                {elakayAutoText("h_pb_text_collapse_note", "Le bouton/link apparaît seulement si le texte dépasse la limite. Laissez les libellés vides pour utiliser la langue du client.")}
              </p>
            </>
          )}
        </Section>
      )}

      {/* Lien vers une page : texte + page cible + test de navigation */}
      {element.type === "link" && (
        <Section title={elakayAutoText("h_d0b5c56d7ac63d", "Lien vers une page")} icon={ChevronRight}>
          <StyleField label={elakayAutoText("h_2f2b965513922d", "Texte du lien")} value={element.text} onChange={(v) => setProp("text", v)} />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_20af3d29c77f62", "Page cible")}</span>
            <select value={element.target || ""} onChange={(e) => setProp("target", e.target.value)} style={selectStyle}>
              <option value="">{elakayAutoText("h_683de3498233d1", "— choisir une page —")}</option>
              {(pages || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <StyleField label={elakayAutoText("h_e7d89afa0ed4fb", "ou lien externe (href)")} value={element.href} onChange={(v) => setProp("href", v)} placeholder="https://… (prioritaire si rempli)" />
          {element.target && (
            <button onClick={() => onNavigate && onNavigate(element.target)} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "4px" }}>
              <ChevronRight size={15} />{elakayAutoText("h_c9e5ce72d2b652", "Aller à la page liée")}</button>
          )}
          <p style={{ fontSize: "11px", color: "#b3aacb", margin: "8px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_ff0434501d903b", "Reliez le lien à une page par son nom/identifiant. À l'export, chaque page devient un fichier et le lien y mène.")}</p>
        </Section>
      )}

      {/* Champs de formulaire (input / textarea / select / date) */}
      {def?.isField && (
        <Section title={elakayAutoText("h_b4faae6e43aaf9", "Champ de formulaire")} icon={TextCursorInput}>
          <StyleField label={elakayAutoText("h_f5485bbae83056", "Libellé")} value={element.field?.label} onChange={(v) => setField("label", v)} />
          {(element.type === "input" || element.type === "textarea" || element.type === "datalist") && (
            <StyleField label={elakayAutoText("h_8e654f338bab71", "Texte indicatif")} value={element.field?.placeholder} onChange={(v) => setField("placeholder", v)} />
          )}
          {(element.type === "input" || element.type === "datalist") && (
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_6ff98f10f9b0de", "Type de champ (HTML Input Types)")}</span>
              <select value={element.field?.inputType || "text"} onChange={(e) => setField("inputType", e.target.value)} style={selectStyle}>
                {["text", "password", "email", "number", "tel", "url", "search", "date", "datetime-local", "month", "week", "time", "color", "range", "file", "hidden"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          )}
          {element.type === "textarea" && <StyleField label={elakayAutoText("h_41c7025e0e2420", "Lignes")} value={element.field?.rows} onChange={(v) => setField("rows", Number(v) || 4)} placeholder="4" />}
          {(element.type === "select" || def?.hasDatalist) && <ChoicesEditor element={element} onUpdate={onUpdate} />}
        </Section>
      )}

      {/* Attributs HTML (name, required, pattern, min, max…) pour les champs */}
      {(def?.isField || def?.isRadio || def?.isCheckbox) && (
        <Section title={elakayAutoText("h_a26697be6e7598", "Attributs HTML")} icon={FileCode}>
          <StyleField label={elakayAutoText("h_6ae999552a0d2d", "name")} value={element.field?.name} onChange={(v) => setField("name", v)} placeholder="nom_du_champ" />
          <StyleField label={elakayAutoText("h_f32b67c7e26342", "value")} value={element.field?.value} onChange={(v) => setField("value", v)} placeholder={elakayAutoText("h_4b17ab7b335bfc", "valeur")} />
          <CheckField label={elakayAutoText("h_4af725187dfbc1", "required (obligatoire)")} checked={element.field?.required} onChange={(v) => setField("required", v)} />
          <CheckField label={elakayAutoText("h_68ad848f56be66", "disabled (désactivé)")} checked={element.field?.disabled} onChange={(v) => setField("disabled", v)} />
          <CheckField label={elakayAutoText("h_6f886556562d78", "readonly (lecture seule)")} checked={element.field?.readonly} onChange={(v) => setField("readonly", v)} />
          {(element.field?.inputType === "number" || element.field?.inputType === "range" || element.field?.inputType === "date") && (
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_b6c935d4f3c7b2", "min")} value={element.field?.min} onChange={(v) => setField("min", v)} /></div>
              <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_0706025b2bbcec", "max")} value={element.field?.max} onChange={(v) => setField("max", v)} /></div>
              <div style={{ flex: 1 }}><StyleField label={elakayAutoText("h_bd370d1b6f9b35", "step")} value={element.field?.step} onChange={(v) => setField("step", v)} /></div>
            </div>
          )}
          {(element.type === "input" || element.type === "textarea") && (
            <StyleField label={elakayAutoText("h_e40cdc787c8d0b", "pattern (regex)")} value={element.field?.pattern} onChange={(v) => setField("pattern", v)} placeholder={elakayAutoText("h_b13698090e2ff3", "[A-Za-z]+")} />
          )}
          <StyleField label={elakayAutoText("h_a0c55b9f6981ad", "autocomplete")} value={element.field?.autocomplete} onChange={(v) => setField("autocomplete", v)} placeholder={elakayAutoText("h_0521eea5b7e11d", "on / off / email…")} />
        </Section>
      )}

      {/* Radio : name + value spécifiques */}
      {def?.isRadio && (
        <Section title={elakayAutoText("h_3fbab28f35b6b8", "Bouton radio")} icon={Circle}>
          <StyleField label={elakayAutoText("h_f5485bbae83056", "Libellé")} value={element.field?.label} onChange={(v) => setField("label", v)} />
          <StyleField label={elakayAutoText("h_ca9aa0d776422c", "Groupe (name)")} value={element.field?.name} onChange={(v) => setField("name", v)} placeholder={elakayAutoText("h_e6eaa8011702b7", "groupe1")} />
          <StyleField label={elakayAutoText("h_43d792edd2938e", "Valeur (value)")} value={element.field?.value} onChange={(v) => setField("value", v)} />
          <CheckField label={elakayAutoText("h_ed95b863195e45", "Sélectionné par défaut")} checked={element.field?.checked} onChange={(v) => setField("checked", v)} />
        </Section>
      )}

      {/* Fieldset : légende */}
      {def?.isFieldset && (
        <Section title={elakayAutoText("h_b342b6b4029373", "Fieldset")} icon={SquareStack}>
          <StyleField label={elakayAutoText("h_1333f3fb235450", "Légende (legend)")} value={element.legend} onChange={(v) => setProp("legend", v)} />
        </Section>
      )}

      {/* Form : attributs du formulaire (action, method, etc.) */}
      {def?.isHtmlForm && (
        <Section title={elakayAutoText("h_375b588ea49260", "Formulaire (Form Attributes)")} icon={FileCode}>
          <StyleField label={elakayAutoText("h_34eb4c4ef00520", "action")} value={element.attrs?.action} onChange={(v) => setAttr("action", v)} placeholder="/soumettre" />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_bfbaf8b2d1cdf9", "method")}</span>
            <select value={element.attrs?.method || "post"} onChange={(e) => setAttr("method", e.target.value)} style={selectStyle}>
              {["get", "post"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <StyleField label={elakayAutoText("h_6ae999552a0d2d", "name")} value={element.attrs?.name} onChange={(v) => setAttr("name", v)} />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_8ca9a60fd7c6f1", "enctype")}</span>
            <select value={element.attrs?.enctype || ""} onChange={(e) => setAttr("enctype", e.target.value)} style={selectStyle}>
              <option value="">{elakayAutoText("h_c6c1abe98959dd", "— défaut —")}</option>
              {["application/x-www-form-urlencoded", "multipart/form-data", "text/plain"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <StyleField label={elakayAutoText("h_0e8a3ad980ec17", "target")} value={element.attrs?.target} onChange={(v) => setAttr("target", v)} placeholder={elakayAutoText("h_20551d744dda80", "_self / _blank")} />
          <CheckField label={elakayAutoText("h_4b016cdfcc9c78", "novalidate")} checked={element.attrs?.novalidate} onChange={(v) => setAttr("novalidate", v)} />
          <CheckField label={elakayAutoText("h_a0c55b9f6981ad", "autocomplete")} checked={element.attrs?.autocomplete} onChange={(v) => setAttr("autocomplete", v)} />
        </Section>
      )}

      {/* Output : texte affiché */}
      {def?.isOutput && (
        <Section title={elakayAutoText("h_4bed336194a9a5", "Output")} icon={FileInput}>
          <StyleField label={elakayAutoText("h_22dea2fa5c7c5c", "Texte / résultat")} value={element.content?.text} onChange={(v) => setContent("text", v)} />
        </Section>
      )}

      {/* Modal : titre + texte du bouton déclencheur */}
      {def?.isModal && (
        <Section title={elakayAutoText("h_18b592b8c34427", "Fenêtre modale")} icon={Maximize2}>
          <StyleField label={elakayAutoText("h_6f2c6aed5ddf9a", "Titre (optionnel)")} value={element.title} onChange={(v) => setProp("title", v)} placeholder={elakayAutoText("h_a672c40a56988d", "Laisser vide pour aucun titre")} />
          <StyleField label={elakayAutoText("h_f5b41b6608ceb9", "Texte du bouton d'ouverture")} value={element.triggerText} onChange={(v) => setProp("triggerText", v)} />
          <label style={{ display: "block", margin: "10px 0" }}>
            <span style={fieldLabel}>{elakayAutoText("h_fbcb7b60131bc3", "Type de contenu")}</span>
            <select value={element.contentMode || "free"} onChange={(e) => setProp("contentMode", e.target.value)} style={selectStyle}>
              <option value="free">{elakayAutoText("h_cc582994d50639", "Contenu libre (ajouter des éléments)")}</option>
              <option value="simple">{elakayAutoText("h_755ba9e5932bf2", "Contenu simple (texte + bouton)")}</option>
            </select>
          </label>
          {element.contentMode === "simple" && (
            <>
              <StyleField label={elakayAutoText("h_0c0d7a00276df7", "Texte du contenu")} value={element.simple?.text} onChange={(v) => onUpdate({ ...element, simple: { ...element.simple, text: v } })} />
              <StyleField label={elakayAutoText("h_ae83a81bcac3ca", "Texte du bouton (dans le modal)")} value={element.simple?.buttonText} onChange={(v) => onUpdate({ ...element, simple: { ...element.simple, buttonText: v } })} placeholder={elakayAutoText("h_1c232bbad1e258", "ex. J'ai compris")} />
              <StyleField label={elakayAutoText("h_84cbb12e92f5c1", "Lien du bouton (optionnel)")} value={element.simple?.buttonHref} onChange={(v) => onUpdate({ ...element, simple: { ...element.simple, buttonHref: v } })} placeholder="https://… (vide = ferme le modal)" />
            </>
          )}
          <p style={{ fontSize: "11px", color: "#b3aacb", margin: "6px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_968afd5390c65f", "Identifiant :")}<code style={{ fontFamily: "ui-monospace, monospace", color: "#6d28d9" }}>{element.id}</code>{elakayAutoText("h_7c1d4f806ad05e", "— utilisez-le comme cible de lien depuis n'importe quel élément (section « Rendre cliquable ») pour ouvrir ce modal.")}{element.contentMode !== "simple" ? elakayAutoText("h_d123ce98d859f7", "En mode libre, ajoutez du contenu avec « Ajouter un élément à l'intérieur ».") : ""}
          </p>
        </Section>
      )}

      {/* Menu déroulant : texte du bouton + entrées */}
      {def?.isDropdown && (
        <Section title={elakayAutoText("h_b9244040062735", "Menu déroulant")} icon={ChevronsDownUp}>
          <StyleField label={elakayAutoText("h_5bc213b4da71f5", "Texte du bouton")} value={element.triggerText} onChange={(v) => setProp("triggerText", v)} />
          <span style={fieldLabel}>{elakayAutoText("h_f8e8a53b3302d6", "Entrées du menu")}</span>
          {(element.items || []).map((it, i) => (
            <div key={it.id} style={{ display: "flex", gap: "5px", marginTop: "6px", alignItems: "center" }}>
              <input type="text" value={it.label} onChange={(e) => { const items = element.items.map((x, j) => j === i ? { ...x, label: e.target.value } : x); onUpdate({ ...element, items }); }}
                placeholder={elakayAutoText("h_f5485bbae83056", "Libellé")} style={{ flex: 1, padding: "7px 8px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
              <input type="text" value={it.href} onChange={(e) => { const items = element.items.map((x, j) => j === i ? { ...x, href: e.target.value } : x); onUpdate({ ...element, items }); }}
                placeholder={elakayAutoText("h_80bf0c8d3c58e6", "lien")} style={{ flex: "0 0 80px", padding: "7px 8px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
              <button onClick={() => onUpdate({ ...element, items: element.items.filter((_, j) => j !== i) })} style={{ ...miniBtn, color: "#e11d48" }}><X size={14} /></button>
            </div>
          ))}
          <button onClick={() => onUpdate({ ...element, items: [...(element.items || []), { id: uid(), label: elakayAutoText("h_9d7f4fca0907b5", "Nouvelle entrée"), href: "#" }] })} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "8px" }}><Plus size={15} />{elakayAutoText("h_4e5cac59038fa8", "Ajouter une entrée")}</button>
        </Section>
      )}

      {/* Onglets : libellé + contenu de chaque onglet */}
      {def?.isTabs && (
        <Section title={elakayAutoText("h_6d8254f24c62c0", "Onglets")} icon={FolderTree}>
          {(element.items || []).map((it, i) => (
            <div key={it.id} style={{ marginBottom: "12px", padding: "10px", border: "1px solid #ece9f5", borderRadius: "10px" }}>
              <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
                <input type="text" value={it.label} onChange={(e) => { const items = element.items.map((x, j) => j === i ? { ...x, label: e.target.value } : x); onUpdate({ ...element, items }); }}
                  placeholder={elakayAutoText("h_e97c602ae76f75", "Titre de l'onglet")} style={{ flex: 1, padding: "7px 8px", fontSize: "13px", fontWeight: 600, border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" }} />
                <button onClick={() => onUpdate({ ...element, items: element.items.filter((_, j) => j !== i) })} style={{ ...miniBtn, color: "#e11d48" }}><X size={14} /></button>
              </div>
              <textarea value={it.text} onChange={(e) => { const items = element.items.map((x, j) => j === i ? { ...x, text: e.target.value } : x); onUpdate({ ...element, items }); }}
                placeholder={elakayAutoText("h_b4c78bdafc63e8", "Contenu de l'onglet")} style={{ width: "100%", minHeight: "54px", padding: "7px 8px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none", resize: "vertical" }} />
            </div>
          ))}
          <button onClick={() => onUpdate({ ...element, items: [...(element.items || []), { id: uid(), label: elakayAutoText("h_9e3971ab0881a8", "Nouvel onglet"), text: elakayAutoText("h_3385cc89403986", "Contenu…") }] })} style={{ ...ghostBtn, width: "100%", justifyContent: "center" }}><Plus size={15} />{elakayAutoText("h_3b300c58618242", "Ajouter un onglet")}</button>
        </Section>
      )}

      {/* Panneau latéral */}
      {def?.isOffcanvas && (
        <Section title={elakayAutoText("h_40352a8b732841", "Panneau latéral")} icon={PanelRightOpen}>
          <StyleField label={elakayAutoText("h_eb97899aedcd56", "Titre")} value={element.title} onChange={(v) => setProp("title", v)} />
          <StyleField label={elakayAutoText("h_f5b41b6608ceb9", "Texte du bouton d'ouverture")} value={element.triggerText} onChange={(v) => setProp("triggerText", v)} />
          <label style={{ display: "block", marginBottom: "4px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_69048754903c58", "Côté")}</span>
            <select value={element.side || "right"} onChange={(e) => setProp("side", e.target.value)} style={selectStyle}>
              <option value="right">{elakayAutoText("h_c73d793d935888", "Droite")}</option>
              <option value="left">{elakayAutoText("h_d8be759feff060", "Gauche")}</option>
              <option value="top">{elakayAutoText("h_9ef48e7c7a34da", "Haut")}</option>
              <option value="bottom">{elakayAutoText("h_5146422c4f9524", "Bas")}</option>
            </select>
          </label>
          <p style={{ fontSize: "11px", color: "#b3aacb", margin: "6px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_cc8e580d5e22f8", "Ajoutez du contenu avec « Ajouter un élément à l'intérieur ».")}</p>
        </Section>
      )}

      {/* Info-bulle */}
      {def?.isTooltip && (
        <Section title={elakayAutoText("h_742ca7a82f2698", "Info-bulle")} icon={MessageSquare}>
          <StyleField label={elakayAutoText("h_ab4defe6566463", "Texte visible")} value={element.text} onChange={(v) => setProp("text", v)} />
          <StyleField label={elakayAutoText("h_877e4ce6aa04ec", "Texte de la bulle")} value={element.tip} onChange={(v) => setProp("tip", v)} />
        </Section>
      )}

      {/* Visualiseur de document */}
      {def?.isDocViewer && (
        <Section title={elakayAutoText("h_e98de6a25d4176", "Visualiseur Document")} icon={FileText}>
          {/* Mode d'affichage */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_8027421a807ab5", "Mode d'affichage")}</span>
            <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
              <button type="button"
                onClick={() => setProp("viewStyle", "card")}
                style={{ flex: 1, padding: "9px 8px", fontSize: "12px", fontWeight: 700, cursor: "pointer", border: `1.5px solid ${(element.viewStyle || "card") === "card" ? "#6d28d9" : "#e4e0f0"}`, background: (element.viewStyle || "card") === "card" ? "#f3effe" : "#fff", color: (element.viewStyle || "card") === "card" ? "#6d28d9" : "#64748b", borderRadius: "8px", textAlign: "center" }}>{elakayAutoText("h_5f0059fda0b550", "🗂 Visualiseur")}<br /><span style={{ fontSize: "10px", fontWeight: 400 }}>{elakayAutoText("h_586a3170aa00c0", "Header + onglets")}</span>
              </button>
              <button type="button"
                onClick={() => setProp("viewStyle", "inline")}
                style={{ flex: 1, padding: "9px 8px", fontSize: "12px", fontWeight: 700, cursor: "pointer", border: `1.5px solid ${element.viewStyle === "inline" ? "#6d28d9" : "#e4e0f0"}`, background: element.viewStyle === "inline" ? "#f3effe" : "#fff", color: element.viewStyle === "inline" ? "#6d28d9" : "#64748b", borderRadius: "8px", textAlign: "center" }}>{elakayAutoText("h_a63ec08a1ca989", "📄 Direct")}<br /><span style={{ fontSize: "10px", fontWeight: 400 }}>{elakayAutoText("h_e9cc89058bab42", "Contenu dans la page")}</span>
              </button>
            </div>
          </label>

          <StyleField label={elakayAutoText("h_a20b8bc300b304", "Dossier (label local)")} value={element.folderLabel} onChange={(v) => setProp("folderLabel", v)} placeholder={elakayAutoText("h_32b3cb883276b8", "ex. Contrats clients")} />
          <StyleField label={elakayAutoText("h_cde7bb2b15c730", "Chemin API (sauvegarde du document)")} value={element.apiPath} onChange={(v) => setProp("apiPath", v)} placeholder="/api/documents" />
          <CheckField label={elakayAutoText("h_0e9335454def4b", "Nom du fichier automatique (timestamp)")} checked={element.autoName !== false} onChange={(v) => setProp("autoName", v)} />
          {element.autoName === false && <StyleField label={elakayAutoText("h_8133cb4a3a5cd5", "Nom personnalisé")} value={element.customName} onChange={(v) => setProp("customName", v)} placeholder={elakayAutoText("h_96780cffc4cc48", "contrat-2025.docx")} />}
          <StyleField label={elakayAutoText("h_12157a30868b8c", "Texte du bouton de chargement")} value={element.buttonText} onChange={(v) => setProp("buttonText", v)} />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_5bc0aaafcf6e36", "Vue par défaut")}</span>
            <select value={element.defaultView || "html"} onChange={(e) => setProp("defaultView", e.target.value)} style={selectStyle}>
              <option value="html">{elakayAutoText("h_1caba6a9af89e8", "HTML (converti automatiquement — toutes les pages)")}</option>
              <option value="office">{elakayAutoText("h_3cb719a91a85a4", "Word Live (rendu Microsoft Online)")}</option>
              <option value="pdf">{elakayAutoText("h_43444fdc847239", "PDF (lecteur intégré)")}</option>
              <option value="word">{elakayAutoText("h_41e36bc39d47a9", "Word (téléchargement)")}</option>
            </select>
          </label>
          <CheckField label={elakayAutoText("h_32e06a4af2a5a2", "Mode full page (largeur totale du site)")} checked={!!element.fullPageMode} onChange={(v) => setProp("fullPageMode", v)} />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_68eec0448c159b", "Affichage des pages (vue HTML)")}</span>
            <select value={element.pageDisplayMode || "all"} onChange={(e) => setProp("pageDisplayMode", e.target.value)} style={selectStyle}>
              <option value="all">{elakayAutoText("h_a53539e037f539", "Afficher toutes les pages")}</option>
              <option value="count">{elakayAutoText("h_5449ba907112b4", "Afficher un nombre de pages")}</option>
            </select>
          </label>
          {(element.pageDisplayMode || "all") === "count" && (
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_ffc5f1d259ebdc", "Nombre de pages à afficher")}</span>
              <input
                type="number"
                min={1}
                value={Math.max(1, Number(element.pagesToShow) || 1)}
                onChange={(e) => setProp("pagesToShow", Math.max(1, Number(e.target.value) || 1))}
                style={selectStyle}
              />
            </label>
          )}
          <CheckField label={elakayAutoText("h_06fd86b03de4a9", "Vue HTML — lire le document converti (toutes les pages)")} checked={element.allowHtml} onChange={(v) => setProp("allowHtml", v)} />
          <CheckField label={elakayAutoText("h_454be407e93015", "Vue Word Live — rendu Microsoft Office Online (URL publique requise)")} checked={element.allowOffice !== false} onChange={(v) => setProp("allowOffice", v)} />
          <CheckField label={elakayAutoText("h_6b1a43b49ea898", "Vue PDF — lecteur intégré navigateur (fichier .pdf requis)")} checked={element.allowPdf !== false} onChange={(v) => setProp("allowPdf", v)} />
          <CheckField label={elakayAutoText("h_14180f7c23d434", "Vue Téléchargement — bouton pour récupérer le fichier original")} checked={element.allowWord} onChange={(v) => setProp("allowWord", v)} />
          <CheckField label={elakayAutoText("h_da27043f91fff8", "Téléchargement direct dans le builder (test & prévisualisation)")} checked={element.allowDownload} onChange={(v) => setProp("allowDownload", v)} />
          <CheckField label={elakayAutoText("h_80f9385c0ea4fe", "Autoriser les signatures client")} checked={element.allowSign} onChange={(v) => setProp("allowSign", v)} />
          {element.allowSign && (
            <CheckField label={elakayAutoText("h_898fcf12dac19f", "Signature OBLIGATOIRE (doit signer pour consulter)")} checked={!!element.requireSign} onChange={(v) => setProp("requireSign", v)} />
          )}
          {element.allowSign && (
            <label style={{ display: "block", marginBottom: "6px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_0fb453cc70350a", "Mode de signature (toutes les zones)")}</span>
              <select value={element.signMode || "both"} onChange={(e) => setProp("signMode", e.target.value)} style={selectStyle}>
                <option value="draw">{elakayAutoText("h_1389aba1398bb7", "Manuscrite seulement")}</option>
                <option value="type">{elakayAutoText("h_2500b6a92c988d", "Texte seulement (nom + date)")}</option>
                <option value="both">{elakayAutoText("h_c08c94b5ffdf2f", "Les deux (au choix du client)")}</option>
              </select>
            </label>
          )}
          <p style={{ fontSize: "11px", color: "#b3aacb", margin: "4px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_63672efe6c0b1f", "Gérez les zones de signature (position, label, obligatoire) dans la section « Zones de signature » ci-dessous.")}</p>
        </Section>
      )}

      {/* ── Zones de signature : position style POSITION + POSITION RAPIDE ── */}
      {def?.isDocViewer && element.allowSign && (
        <Section title={elakayAutoText("h_ce6e6685a8d6fb", "Zones de signature")} icon={PenLine}>
          {(element.signatures || []).map((sig, i) => {
            const qGrid = sig.unit === "px" ? SIGN_QUICK_POS_GRID_PX : SIGN_QUICK_POS_GRID;
            const updSigs = (changes) => onUpdate({ ...element, signatures: (element.signatures || []).map((s) => s.id === sig.id ? { ...s, ...changes } : s) });
            const nudge = (dx, dy) => {
              const step = sig.unit === "px" ? 5 : 1;
              updSigs({ x: Math.round((sig.x ?? 75) + dx * step), y: Math.round((sig.y ?? 85) + dy * step) });
            };
            return (
              <div key={sig.id} style={{ marginBottom: "18px", padding: "12px", border: "1px solid #e4e0f0", borderRadius: "12px", background: "#faf9fd" }}>
                {/* En-tête zone : numéro + label + obligatoire + supprimer */}
                <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontSize: "10px", fontWeight: 700, color: "#6d28d9", background: "#f3effe", padding: "2px 8px", borderRadius: "999px", whiteSpace: "nowrap" }}>{elakayAutoText("h_03efccb49f3ff5", "Zone")}{i + 1}</span>
                  <input type="text" value={sig.label || ""} onChange={(e) => updSigs({ label: e.target.value })}
                    placeholder={elakayAutoText("h_f5485bbae83056", "Libellé")} style={{ flex: 1, padding: "6px 10px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "7px", background: "#fff", color: "#2a2547", outline: "none" }} />
                  <label style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "10px", color: "#e11d48", cursor: "pointer", whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={!!sig.required} onChange={(e) => updSigs({ required: e.target.checked })} style={{ accentColor: "#e11d48" }} />{elakayAutoText("h_9a33b681a72499", "Requis")}</label>
                  <button onClick={() => onUpdate({ ...element, signatures: (element.signatures || []).filter((s) => s.id !== sig.id) })} style={{ ...miniBtn, color: "#e11d48" }}><X size={14} /></button>
                </div>

                {/* Unité % / px */}
                <div style={{ display: "flex", gap: "6px", marginBottom: "10px", justifyContent: "center" }}>
                  <button onClick={() => updSigs({ unit: "%" })} style={{ ...toggleBtn((sig.unit || "%") === "%"), padding: "5px 16px" }}>%</button>
                  <button onClick={() => updSigs({ unit: "px" })} style={{ ...toggleBtn(sig.unit === "px"), padding: "5px 16px" }}>{elakayAutoText("h_07a65dd6489a00", "px")}</button>
                </div>

                {/* POSITION : X / Y */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <label style={{ flex: 1 }}>
                    <span style={fieldLabel}>X ({sig.unit || "%"})</span>
                    <input type="number" value={sig.x ?? 75} onChange={(e) => updSigs({ x: Number(e.target.value) })}
                      style={{ width: "100%", marginTop: "4px", padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#fff", color: "#2a2547", outline: "none" }} />
                  </label>
                  <label style={{ flex: 1 }}>
                    <span style={fieldLabel}>Y ({sig.unit || "%"})</span>
                    <input type="number" value={sig.y ?? 85} onChange={(e) => updSigs({ y: Number(e.target.value) })}
                      style={{ width: "100%", marginTop: "4px", padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#fff", color: "#2a2547", outline: "none" }} />
                  </label>
                </div>

                {/* Pavé directionnel */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "4px", width: "120px", margin: "0 auto 4px" }}>
                  <span />
                  <button onClick={() => nudge(0, -1)} style={padBtn} title={elakayAutoText("h_9ef48e7c7a34da", "Haut")}><ChevronUp size={16} /></button>
                  <span />
                  <button onClick={() => nudge(-1, 0)} style={padBtn} title={elakayAutoText("h_d8be759feff060", "Gauche")}><ChevronLeft size={16} /></button>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#9b93b8" }}><Move size={13} /></span>
                  <button onClick={() => nudge(1, 0)} style={padBtn} title={elakayAutoText("h_c73d793d935888", "Droite")}><ChevronRight size={16} /></button>
                  <span />
                  <button onClick={() => nudge(0, 1)} style={padBtn} title={elakayAutoText("h_5146422c4f9524", "Bas")}><ChevronDown size={16} /></button>
                  <span />
                </div>
                <p style={{ fontSize: "11px", color: "#9b93b8", textAlign: "center", margin: "0 0 10px", lineHeight: 1.5 }}>{elakayAutoText("h_638c9811bc45ff", "Glissez ou utilisez les flèches (pas de")}{sig.unit === "px" ? "5px" : "1%"}).
                </p>

                {/* POSITION RAPIDE : grille 3×3 */}
                <div style={{ borderTop: "1px solid #e4e0f0", paddingTop: "10px" }}>
                  <span style={{ ...fieldLabel, display: "block", textAlign: "center", marginBottom: "8px" }}>{elakayAutoText("h_57e745de71025b", "Position rapide")}</span>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", width: "132px", margin: "0 auto" }}>
                    {qGrid.map((q) => (
                      <button key={q.label} onClick={() => updSigs({ x: q.x, y: q.y })}
                        style={{ ...padBtn, fontSize: "16px", height: "36px" }} title={`x:${q.x}${sig.unit || "%"} y:${q.y}${sig.unit || "%"}`}>
                        {q.label}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: "11px", color: "#9b93b8", textAlign: "center", margin: "8px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_10ac055040b794", "Place la zone (coins, bords, centre)")}{sig.unit === "px" ? elakayAutoText("h_1ddda764c6d24a", ", en pixels.") : elakayAutoText("h_72695b0fd7e6cb", ", en %.")}
                  </p>
                </div>
              </div>
            );
          })}

          {(element.signatures || []).length < 10 ? (
            <button onClick={() => onUpdate({ ...element, signatures: [...(element.signatures || []), { id: uid(), label: `Zone ${(element.signatures || []).length + 1}`, x: 75, y: 85, unit: "%", required: false }] })}
              style={{ ...ghostBtn, width: "100%", justifyContent: "center" }}>
              <Plus size={15} />{elakayAutoText("h_019aba897af607", "Ajouter une zone de signature")}</button>
          ) : (
            <p style={{ fontSize: "11px", color: "#b3aacb", textAlign: "center" }}>{elakayAutoText("h_077c04907a6edc", "Maximum 10 zones atteint.")}</p>
          )}
          {(element.signatures || []).length === 0 && (
            <p style={{ fontSize: "11px", color: "#b3aacb", margin: "8px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_c56235484e96bd", "Aucune zone — le document sera lisible mais non signable. Ajoutez au moins une zone pour activer la signature côté client.")}</p>
          )}
        </Section>
      )}

      {/* Case à cocher */}
      {def?.isCheckbox && (
        <Section title={elakayAutoText("h_f4679319a0510a", "Case à cocher")} icon={CheckSquare}>
          <StyleField label={elakayAutoText("h_f5485bbae83056", "Libellé")} value={element.field?.label} onChange={(v) => setField("label", v)} />
          <CheckField label={elakayAutoText("h_17cc06a6cd1885", "Cochée par défaut")} checked={element.field?.checked} onChange={(v) => setField("checked", v)} />
        </Section>
      )}

      {/* Vidéo */}
      {def?.isVideo && (
        <Section title={elakayAutoText("h_304f6ca42f3628", "Vidéo")} icon={Video}>
          <MediaUrlField
            label={elakayAutoText("h_01b76c608a810a", "URL (YouTube, Vimeo, .mp4)")}
            value={element.options?.url}
            onChange={(v) => setOption("url", v)}
            onMediaSelect={(media) => onUpdate({ ...element, mediaRef: media.assetId || null, options: { ...element.options, url: media.url } })}
            kind="video"
          />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_041a5dec481d6b", "Format")}</span>
            <select value={element.options?.ratio || "16 / 9"} onChange={(e) => setOption("ratio", e.target.value)} style={selectStyle}>
              <option value="16 / 9">{elakayAutoText("h_f17de7cc68a98d", "16:9 (paysage)")}</option>
              <option value="4 / 3">4:3</option>
              <option value="1 / 1">{elakayAutoText("h_0f2016b651a146", "1:1 (carré)")}</option>
              <option value="9 / 16">{elakayAutoText("h_2d7700a3967654", "9:16 (vertical)")}</option>
            </select>
          </label>
        </Section>
      )}

      {/* Elakay Swiper (Swiper Library) */}
      {def?.isElakaySwiper && (
        <Section title={elakayAutoText("h_esb_label", "Elakay Swiper")} icon={GalleryHorizontalEnd}>
          <ElakaySwiperField element={element} onUpdate={onUpdate} />
        </Section>
      )}

      {/* Icône */}
      {def?.isIcon && (
        <Section title={elakayAutoText("h_f58b269782f29e", "Icône")} icon={Star}>
          <IconLibraryField key={element.id} element={element} setContent={setContent} />
          <StyleField label={elakayAutoText("h_e1045f23a5e645", "Symbole personnalisé (emoji ou caractère, si aucune icône choisie ci-dessus)")} value={element.options?.glyph} onChange={(v) => setOption("glyph", v)} placeholder="★ ✓ ♥ 🚀" />
          <StyleField label={elakayAutoText("h_2b90839d6bba3b", "Taille")} value={element.options?.size} onChange={(v) => setOption("size", v)} placeholder={elakayAutoText("h_3263fd4157a755", "48px")} />
          <StyleField label={elakayAutoText("h_145c4326e92bd9", "Couleur")} value={element.options?.color} onChange={(v) => setOption("color", v)} type="color" />
          <StyleField label={elakayAutoText("h_icon_rotation", "Rotation (degrés)")} value={element.options?.rotation ?? ""} onChange={(v) => setOption("rotation", Number(v) || 0)} placeholder="0" />
          <StyleField label={elakayAutoText("h_icon_opacity", "Opacité (0 à 1)")} value={element.options?.opacity ?? ""} onChange={(v) => setOption("opacity", v === "" ? undefined : Math.max(0, Math.min(1, Number(v))))} placeholder="1" />
          <CheckField
            label={elakayAutoText("h_icon_decorative", "Décorative (masquée aux lecteurs d'écran)")}
            checked={element.options?.decorative !== false}
            onChange={(v) => setOption("decorative", v)}
          />
          {element.options?.decorative === false && (
            <StyleField label={elakayAutoText("h_icon_arialabel", "Texte alternatif (accessibilité)")} value={element.options?.ariaLabel} onChange={(v) => setOption("ariaLabel", v)} placeholder={elakayAutoText("h_icon_arialabel_ph", "ex : Ajouter aux favoris")} />
          )}
        </Section>
      )}

      {/* Citation */}
      {def?.isQuote && (
        <Section title={elakayAutoText("h_6eb49155521358", "Citation")} icon={Quote}>
          <StyleField label={elakayAutoText("h_bb6b7efb867952", "Texte de la citation")} value={element.content?.text} onChange={(v) => setContent("text", v)} />
          <StyleField label={elakayAutoText("h_06f1962fc5f511", "Auteur")} value={element.content?.author} onChange={(v) => setContent("author", v)} />
        </Section>
      )}

      {/* Statistique */}
      {def?.isStat && (
        <Section title={elakayAutoText("h_ad2bc543bcec51", "Statistique")} icon={BarChart3}>
          <StyleField label={elakayAutoText("h_68f92b0dd425b5", "Valeur")} value={element.content?.value} onChange={(v) => setContent("value", v)} placeholder="1,2 M€" />
          <StyleField label={elakayAutoText("h_f5485bbae83056", "Libellé")} value={element.content?.label} onChange={(v) => setContent("label", v)} />
          <StyleField label={elakayAutoText("h_a323b5fa5a7f82", "Couleur valeur")} value={element.content?.valueColor} onChange={(v) => setContent("valueColor", v)} type="color" />
          <StyleField label={elakayAutoText("h_c876f5294d8462", "Couleur libellé")} value={element.content?.labelColor} onChange={(v) => setContent("labelColor", v)} type="color" />
        </Section>
      )}

      {/* Progression */}
      {def?.isProgress && (
        <Section title={elakayAutoText("h_b43fd8c6063c18", "Barre de progression")} icon={TrendingUp}>
          <StyleField label={elakayAutoText("h_f5485bbae83056", "Libellé")} value={element.content?.label} onChange={(v) => setContent("label", v)} />
          <StyleField label={elakayAutoText("h_f84e12a2195454", "Pourcentage (0-100)")} value={element.content?.percent} onChange={(v) => setContent("percent", Number(v) || 0)} placeholder="68" />
          <StyleField label={elakayAutoText("h_b5ec48c14794a4", "Couleur barre")} value={element.content?.barColor} onChange={(v) => setContent("barColor", v)} type="color" />
          <StyleField label={elakayAutoText("h_14f94378a460d7", "Couleur fond")} value={element.content?.trackColor} onChange={(v) => setContent("trackColor", v)} type="color" />
        </Section>
      )}

      {/* Note étoiles */}
      {def?.isRating && (
        <Section title={elakayAutoText("h_73f5e1daadfdf4", "Note (étoiles)")} icon={Star}>
          <StyleField label={elakayAutoText("h_2c924e3088204e", "Note")} value={element.content?.value} onChange={(v) => setContent("value", Number(v) || 0)} placeholder="4" />
          <StyleField label={elakayAutoText("h_c168fa7c179f15", "Sur (max)")} value={element.content?.max} onChange={(v) => setContent("max", Number(v) || 5)} placeholder="5" />
          <StyleField label={elakayAutoText("h_c73a1aa758dd4e", "Libellé (optionnel)")} value={element.content?.label} onChange={(v) => setContent("label", v)} placeholder={elakayAutoText("h_3c0fc0300b0458", "(128 avis)")} />
          <StyleField label={elakayAutoText("h_145c4326e92bd9", "Couleur")} value={element.content?.color} onChange={(v) => setContent("color", v)} type="color" />
        </Section>
      )}


      {/* Items éditables pour swiper / grid */}
      {def?.hasItems && <ItemsEditor element={element} onUpdate={onUpdate} />}

      {/* Options du carrousel */}
      {element.type === "swiper" && (
        <Section title={elakayAutoText("h_7e5b427b41e4f8", "Options du carrousel")} icon={GalleryHorizontalEnd}>
          <StyleField label={elakayAutoText("h_255cbd69a277a0", "Hauteur")} value={element.options?.height} onChange={(v) => setOption("height", v)} placeholder={elakayAutoText("h_181c734b791e56", "300px")} />
          <CheckField label={elakayAutoText("h_9362e223b21516", "Flèches de navigation")} checked={element.options?.showArrows} onChange={(v) => setOption("showArrows", v)} />
          <CheckField label={elakayAutoText("h_ec5289c3a6b9bd", "Points (pagination)")} checked={element.options?.showDots} onChange={(v) => setOption("showDots", v)} />
          <StyleField label={elakayAutoText("h_f60ac3e7209848", "Couleur titres")} value={element.itemStyle?.titleColor} onChange={(v) => setItemStyle("titleColor", v)} type="color" />
          <StyleField label={elakayAutoText("h_27fc9401a927ab", "Couleur textes")} value={element.itemStyle?.textColor} onChange={(v) => setItemStyle("textColor", v)} type="color" />
          <StyleField label={elakayAutoText("h_1c5a8637216f13", "Voile (overlay)")} value={element.itemStyle?.overlay} onChange={(v) => setItemStyle("overlay", v)} placeholder={elakayAutoText("h_f2051b284fd732", "rgba(26,26,46,0.45)")} />
        </Section>
      )}

      {/* Options de la grille responsive (grid type + elements avec repeatBind) */}
      {(element.type === "grid" || element.repeatBind) && (
        <Section title={element.type === "grid" ? elakayAutoText("h_74b8371d1d6c89", "Options de la grille") : elakayAutoText("h_16bd45e1454323", "Grille responsive (mapping)")} icon={LayoutGrid}>
          {/* Colonnes globale (legacy) — utilisée si aucun deviceGrid défini */}
          {element.type === "grid" && (
            <>
              <label style={{ display: "block", marginBottom: "10px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_99c28ceb930fb6", "Colonnes (défaut global)")}</span>
                <select value={element.options?.columns || 3} onChange={(e) => setOption("columns", Number(e.target.value))} style={selectStyle}>
                  {[1, 2, 3, 4, 5, 6].map((c) => <option key={c} value={c}>{c}{elakayAutoText("h_64e658a42698de", "colonne")}{c > 1 ? "s" : ""}</option>)}
                </select>
              </label>
              <StyleField label={elakayAutoText("h_c0a0e8d0c4ba56", "Gap global (défaut)")} value={s.gap} onChange={(v) => setStyle("gap", v)} placeholder={elakayAutoText("h_998b02c207add3", "20px")} />
            </>
          )}

          {/* Device grid responsive — max 4 devices */}
          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
              <span style={{ ...fieldLabel }}>{elakayAutoText("h_acb9f186214144", "Colonnes par device (responsive)")}</span>
              <button
                onClick={() => {
                  const base = Math.max(1, element.options?.columns || 3);
                  const cols = {
                    mobile:  1,
                    tablet:  base <= 2 ? 1 : 2,
                    laptop:  base <= 3 ? base : Math.max(2, base - 1),
                    desktop: base,
                  };
                  setOption("deviceGrid", ["mobile", "tablet", "laptop", "desktop"].map(dev => ({
                    id: uid(), device: dev, columns: cols[dev], gap: dev === "mobile" ? "8px" : "16px",
                  })));
                }}
                title={elakayAutoText("h_0fa09a709a959c", "Remplir automatiquement mobile/tablet/laptop/desktop depuis les colonnes globales")}
                style={{ ...ghostBtn, fontSize: "10px", padding: "3px 8px", borderRadius: "6px" }}
              >{elakayAutoText("h_632c414f8fa038", "Responsive auto")}</button>
            </div>
            <p style={{ fontSize: "10px", color: "#9b93b8", margin: "0 0 8px", lineHeight: 1.4 }}>
              {DEVICE_GRID_ORDER.filter(d => d !== "all").map(d => `${DEVICE_GRID_BREAKPOINTS[d].label}: ${d === "tablet" ? "≤768px" : d === "laptop" ? "769–1200px" : d === "mobile" ? "≤480px" : "≥1201px"}`).join(" · ")}
            </p>
            {(element.options?.deviceGrid || []).map((dg, i) => {
              const usedDevices = (element.options?.deviceGrid || []).map(x => x.device);
              const availableDevices = DEVICE_GRID_ORDER.filter(d => d === dg.device || !usedDevices.includes(d));
              return (
                <div key={dg.id || i} style={{ display: "grid", gridTemplateColumns: "82px 1fr 1fr auto", gap: "5px", alignItems: "end", marginBottom: "6px", padding: "8px", background: "#faf9fd", borderRadius: "8px", border: "1px solid #ece9f5" }}>
                  <label style={{ display: "block" }}>
                    <span style={{ ...fieldLabel, marginBottom: "2px" }}>{elakayAutoText("h_a5a74a6df09278", "Device")}</span>
                    <select value={dg.device} onChange={(e) => {
                      const updated = (element.options?.deviceGrid || []).map((x, j) => j === i ? { ...x, device: e.target.value } : x);
                      setOption("deviceGrid", updated);
                    }} style={{ ...selectStyle, padding: "5px 6px", fontSize: "11px" }}>
                      {availableDevices.map(d => <option key={d} value={d}>{DEVICE_GRID_BREAKPOINTS[d].label}</option>)}
                    </select>
                  </label>
                  <label style={{ display: "block" }}>
                    <span style={{ ...fieldLabel, marginBottom: "2px" }}>{elakayAutoText("h_53a2544cacfd92", "Colonnes")}</span>
                    <input type="number" min={1} max={12} value={dg.columns ?? 3} onChange={(e) => {
                      const updated = (element.options?.deviceGrid || []).map((x, j) => j === i ? { ...x, columns: Math.max(1, Number(e.target.value)) } : x);
                      setOption("deviceGrid", updated);
                    }} style={{ ...selectStyle, padding: "5px 6px", fontSize: "11px" }} />
                  </label>
                  <label style={{ display: "block" }}>
                    <span style={{ ...fieldLabel, marginBottom: "2px" }}>{elakayAutoText("h_b2464742da1034", "Gap")}</span>
                    <input type="text" value={dg.gap || "16px"} onChange={(e) => {
                      const updated = (element.options?.deviceGrid || []).map((x, j) => j === i ? { ...x, gap: e.target.value } : x);
                      setOption("deviceGrid", updated);
                    }} placeholder={elakayAutoText("h_4678bd4d8aa022", "16px")} style={{ ...selectStyle, padding: "5px 6px", fontSize: "11px" }} />
                  </label>
                  <button onClick={() => setOption("deviceGrid", (element.options?.deviceGrid || []).filter((_, j) => j !== i))}
                    style={{ ...miniBtn, color: "#e11d48", padding: "4px 6px", alignSelf: "flex-end" }}>
                    <X size={12}/>
                  </button>
                </div>
              );
            })}
            {(element.options?.deviceGrid || []).length < 4 && (() => {
              const usedDevices = (element.options?.deviceGrid || []).map(d => d.device);
              const next = DEVICE_GRID_ORDER.find(d => !usedDevices.includes(d));
              return next ? (
                <button onClick={() => setOption("deviceGrid", [...(element.options?.deviceGrid || []), { id: uid(), device: next, columns: next === "tablet" ? 1 : next === "laptop" ? 2 : 3, gap: "16px" }])}
                  style={{ ...ghostBtn, width: "100%", justifyContent: "center", fontSize: "11px", marginTop: "2px" }}>
                  <Plus size={13}/> {DEVICE_GRID_BREAKPOINTS[next].label}
                </button>
              ) : null;
            })()}
          </div>
          {element.type === "grid" && (
            <p style={{ fontSize: "11px", color: "#9b93b8", lineHeight: 1.5, margin: "8px 0 0" }}>{elakayAutoText("h_ab1dccc8108202", "Les configs device écrasent les colonnes globales. Sans config device, la valeur globale s'applique à tous les écrans.")}</p>
          )}
          {element.repeatBind && (
            <p style={{ fontSize: "11px", color: "#9b93b8", lineHeight: 1.5, margin: "8px 0 0" }}>{elakayAutoText("h_a70756d2a6aeb8", "Active une grille CSS autour des éléments répétés. Compatible avec la pagination automatique.")}</p>
          )}
        </Section>
      )}

      {/* Carte : contenu + orientation portrait / paysage */}
      {element.type === "card" && (
        <>
          <Section title={elakayAutoText("h_a319ce993b9cae", "Contenu de la carte")} icon={CreditCard}>
            <StyleField label={elakayAutoText("h_fa5840470c201c", "Image (URL)")} value={element.content?.image} onChange={(v) => setContent("image", v)} />
            <StyleField label={elakayAutoText("h_eb97899aedcd56", "Titre")} value={element.content?.title} onChange={(v) => setContent("title", v)} />
            <StyleField label={elakayAutoText("h_a972816347e945", "Couleur titre")} value={element.content?.titleColor} onChange={(v) => setContent("titleColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_ff9afef1eae0a9", "Texte")} value={element.content?.text} onChange={(v) => setContent("text", v)} />
            <StyleField label={elakayAutoText("h_1dbc652d3f9bd3", "Couleur texte")} value={element.content?.textColor} onChange={(v) => setContent("textColor", v)} type="color" />
            <CheckField label={elakayAutoText("h_ebe779eb96cb8a", "Afficher un bouton")} checked={element.options?.showButton} onChange={(v) => setOption("showButton", v)} />
            {element.options?.showButton && (
              <>
                <StyleField label={elakayAutoText("h_5bc213b4da71f5", "Texte du bouton")} value={element.content?.buttonText} onChange={(v) => setContent("buttonText", v)} />
                <StyleField label={elakayAutoText("h_73c4615dc616f2", "Lien du bouton")} value={element.content?.buttonHref} onChange={(v) => setContent("buttonHref", v)} placeholder="https://..." />
              </>
            )}
          </Section>
          <Section title={elakayAutoText("h_86e4e3875420cd", "Orientation")} icon={LayoutGrid}>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_041a5dec481d6b", "Format")}</span>
              <select value={element.options?.orientation || "portrait"} onChange={(e) => setOption("orientation", e.target.value)} style={selectStyle}>
                <option value="portrait">{elakayAutoText("h_94ffe4d9c38971", "Portrait (image en haut)")}</option>
                <option value="landscape">{elakayAutoText("h_5cfbb302c4bcbd", "Paysage (image à côté)")}</option>
              </select>
            </label>
            {element.options?.orientation === "landscape" ? (
              <label style={{ display: "block", marginBottom: "10px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_365d116249ac2f", "Côté de l'image")}</span>
                <select value={element.options?.imageSide || "left"} onChange={(e) => setOption("imageSide", e.target.value)} style={selectStyle}>
                  <option value="left">{elakayAutoText("h_d8be759feff060", "Gauche")}</option>
                  <option value="right">{elakayAutoText("h_c73d793d935888", "Droite")}</option>
                </select>
              </label>
            ) : (
              <StyleField label={elakayAutoText("h_5c9f92930d7a25", "Hauteur de l'image")} value={element.content?.imageHeight} onChange={(v) => setContent("imageHeight", v)} placeholder={elakayAutoText("h_279882787c949e", "180px")} />
            )}
          </Section>
        </>
      )}

      {/* ── Options Vote / Étoiles ── */}
      {def?.isShopVote && (
        <Section title={elakayAutoText("h_4c12d0a3b1e65c", "Vote / Étoiles")} icon={Star}>
          <StyleField label={elakayAutoText("h_e6067bad59a1eb", "Taille des étoiles")} value={element.options?.starSize} onChange={(v) => setOption("starSize", v)} placeholder={elakayAutoText("h_21f02b519efe1e", "28px")} />
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_6792ac08411aa8", "Note par défaut")}</span>
            <input type="range" min={0} max={5} step={0.5} value={element.options?.defaultRating ?? 4} onChange={(e) => setOption("defaultRating", Number(e.target.value))} style={{ width: "100%", accentColor: "#6d28d9", marginTop: "6px" }} />
            <span style={{ fontSize: "12px", color: "#6d28d9", fontWeight: 600 }}>{element.options?.defaultRating ?? 4} / {element.options?.maxStars || 5}</span>
          </label>
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_fa514eb0c14bae", "Nombre max d'étoiles")}</span>
            <input type="number" min={1} max={10} value={element.options?.maxStars || 5} onChange={(e) => setOption("maxStars", Number(e.target.value))} style={selectStyle} />
          </label>
        </Section>
      )}

      {/* ── Options Carrousel Produits ── */}
      {(def?.isShopCarousel || def?.isShopPubMatrix) && (
        <Section title={elakayAutoText("h_3b11eb795d1659", "Carrousel Produits")} icon={GalleryHorizontalEnd}>
          <CheckField label={elakayAutoText("h_63a195b167b434", "Bouton Suivre actif")} checked={!!element.options?.activeFollowBtn} onChange={(v) => setOption("activeFollowBtn", v)} />

          {/* Animation */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_d92ef7bdc93978", "Animation du container")}</span>
            <select value={element.options?.type_Animation || ""} onChange={(e) => setOption("type_Animation", e.target.value || null)} style={selectStyle}>
              <option value="">{elakayAutoText("h_e8f882731020de", "Aucune")}</option>
              {["Arc-en-ciel","Rose & Violet","Vert & Jaune","Orange & Rouge","Bleu & Indigo","Cyan & Teal","Or & Bronze","Argent","Neon Magenta","Rainbow","Deep Purple","Lime","Pink","Amber","Brown","Blue Grey","Coral","Blanc & Turquoise","Matrix Digital","Neon Glow","Liquid Blob","Holographic","Electric Storm","Orbital","Water Ripples","Smoke","Confetti","Sparkle Stars","Hexagon","Triangle","Morphing","Energy Burst","Aurora"].map((name, i) => (
                <option key={i} value={`${i}_type_Animation`}>{i} — {name}</option>
              ))}
            </select>
          </label>
          <StyleField label={elakayAutoText("h_b506759876dad2", "Couleur fond container")} value={element.options?.backgroundColor} onChange={(v) => setOption("backgroundColor", v)} type="color" />

          {/* Slides par écran */}
          <div style={{ marginTop: "4px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_418dbccc19a7fc", "Slides visibles / écran")}</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "6px" }}>
              {[["wm0","Mobile"],["wm640","640px"],["wm768","768px"],["wm1024","1024px"],["wm1200","1200px"]].map(([k, lbl]) => (
                <label key={k} style={{ display: "block" }}>
                  <span style={{ ...fieldLabel, marginBottom: "3px" }}>{lbl}</span>
                  <input type="number" min={1} max={5} step={0.5} value={element.options?.[k] ?? 2} onChange={(e) => setOption(k, Number(e.target.value))} style={{ ...selectStyle, padding: "6px 8px" }} />
                </label>
              ))}
            </div>
          </div>

          {/* Style du titre */}
          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_a3f00bc98011ed", "Style du titre")}</span>
            <StyleField label={elakayAutoText("h_145c4326e92bd9", "Couleur")} value={element.options?.titleColor} onChange={(v) => setOption("titleColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_25c65b13cdafd0", "Taille (ex: 1.75rem)")} value={element.options?.titleFontSize} onChange={(v) => setOption("titleFontSize", v)} placeholder={elakayAutoText("h_28f667fcee0c87", "1.75rem")} />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_1fc3c47737397e", "Graisse")}</span>
              <select value={String(element.options?.titleFontWeight ?? 700)} onChange={(e) => setOption("titleFontWeight", Number(e.target.value))} style={selectStyle}>
                {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_71ee57ac8fa979", "Police (font-family)")} value={element.options?.titleFontFamily} onChange={(v) => setOption("titleFontFamily", v)} placeholder={elakayAutoText("h_224b69b513b5a3", "inherit / Poppins")} />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_836f22b3170976", "Style police")}</span>
              <select value={element.options?.titleFontStyle || "normal"} onChange={(e) => setOption("titleFontStyle", e.target.value)} style={selectStyle}>
                {["normal","italic","oblique"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_8d051dc9aebe10", "Espacement lettres")} value={element.options?.titleLetterSpacing} onChange={(v) => setOption("titleLetterSpacing", v)} placeholder={elakayAutoText("h_7a57c4b4e3c31a", "-0.5px")} />
            <StyleField label={elakayAutoText("h_a2343e7a0b86a7", "Hauteur de ligne")} value={String(element.options?.titleLineHeight ?? 1.2)} onChange={(v) => setOption("titleLineHeight", isNaN(Number(v)) ? v : Number(v))} placeholder="1.2" />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_0ee170f6dfaf4d", "Transformation texte")}</span>
              <select value={element.options?.titleTextTransform || "none"} onChange={(e) => setOption("titleTextTransform", e.target.value)} style={selectStyle}>
                {["none","uppercase","lowercase","capitalize"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_34ced4bc54549c", "Alignement texte")}</span>
              <select value={element.options?.titleTextAlign || "left"} onChange={(e) => setOption("titleTextAlign", e.target.value)} style={selectStyle}>
                {["left","center","right"].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_71d732f0613e9d", "Ombre texte")} value={element.options?.titleTextShadow} onChange={(v) => setOption("titleTextShadow", v)} placeholder={elakayAutoText("h_405963c16d2791", "0 2px 4px rgba(0,0,0,0.05)")} />
          </div>

          {/* Style du header */}
          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_fb68dd8c79921a", "Style du header")}</span>
            <StyleField label={elakayAutoText("h_c5dcaf53ec2281", "Fond header")} value={element.options?.headerBackgroundColor} onChange={(v) => setOption("headerBackgroundColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_2be7016346bf8d", "Padding header")} value={element.options?.headerPadding} onChange={(v) => setOption("headerPadding", v || null)} placeholder={elakayAutoText("h_990e2236cc6d73", "ex: 8px 16px")} />
            <StyleField label={elakayAutoText("h_2a8a3c9e9d20d4", "Marge bas header")} value={element.options?.headerMarginBottom} onChange={(v) => setOption("headerMarginBottom", v || null)} placeholder={elakayAutoText("h_da6e8f49833a20", "ex: 16px")} />
          </div>
        </Section>
      )}

      {/* ── Options Prix & Remise ── */}
      {def?.isShopPrice && (
        <Section title={elakayAutoText("h_568077469dc7c1", "Prix & Remise")} icon={Tag}>
          {/* ── page ── */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_48c94a43558201", "Mode d'affichage (page)")}</span>
            <select value={element.options?.page ?? 0} onChange={(e) => setOption("page", Number(e.target.value))} style={selectStyle}>
              <option value={0}>{elakayAutoText("h_2dfeead24736aa", "0 — Produit (prix + remise + bouton)")}</option>
              <option value={1}>{elakayAutoText("h_eeef99014b8cd6", "1 — Panier (total avec taxes)")}</option>
            </select>
            <p style={{ fontSize:"10px", color:"#9b93b8", margin:"3px 0 0" }}>{elakayAutoText("h_54fb04d10ec7d6", "0 = affiche prix barré + remise % + bouton · 1 = total panier (products doit être un tableau)")}</p>
          </label>

          {/* ── showAddToCartButton ── */}
          <CheckField label={elakayAutoText("h_e9b01cbf477446", "Afficher 'Ajouter au panier'")} checked={element.options?.showAddToCartButton !== false} onChange={(v) => setOption("showAddToCartButton", v)} />

          {/* ── filtername ── */}
          <StyleField
            label={elakayAutoText("h_99a9510ef0c9ba", "filtername (filtre catégorie pour addToCart)")}
            value={element.options?.filtername ?? ""}
            onChange={(v) => setOption("filtername", v)}
            placeholder={elakayAutoText("h_e01202f4f447a2", "ex: Polo, Laptop… (vide = tous)")}
          />

          {/* ── Taxes & livraison (utiles surtout page=1) ── */}
          <div style={{ marginTop:"6px", paddingTop:"8px", borderTop:`1px solid ${fieldLabel.color || "#e4dcfb"}` }}>
            <p style={{ fontSize:"10px", color:"#9b93b8", margin:"0 0 6px" }}>{elakayAutoText("h_d497cc78c9a052", "Les 3 champs suivants s'appliquent uniquement en mode Panier (page = 1)")}</p>
            <StyleField
              label={elakayAutoText("h_ec6339eeb09515", "Taxe — tax (ex: 0.1 = 10%)")}
              value={String(element.options?.tax ?? 0.1)}
              onChange={(v) => setOption("tax", Number(v))}
              placeholder="0.1"
            />
            <StyleField
              label={elakayAutoText("h_cb7d2ed02b7df7", "Taxe complémentaire — tca (ex: 0.05)")}
              value={String(element.options?.tca ?? 0)}
              onChange={(v) => setOption("tca", Number(v))}
              placeholder="0"
            />
            <StyleField
              label={elakayAutoText("h_bf7fcc88812807", "Livraison — shipping (ex: 0.3 = 30%)")}
              value={String(element.options?.shipping ?? 0.3)}
              onChange={(v) => setOption("shipping", Number(v))}
              placeholder="0.3"
            />
          </div>
        </Section>
      )}

      {/* ── Options Checkout / Panier ── */}
      {def?.isShopCheckout && (
        <Section title={elakayAutoText("h_ac383ed071babe", "Checkout / Panier")} icon={Package}>
          <StyleField label={elakayAutoText("h_5f5c62f0a7b25f", "Texte bouton principal")} value={element.options?.label} onChange={(v) => setOption("label", v)} placeholder={elakayAutoText("h_b6799d9a25e425", "Ajouter au panier")} />
          <CheckField label={elakayAutoText("h_602082794cecc0", "Afficher 'Acheter maintenant'")} checked={element.options?.showBuyNow !== false} onChange={(v) => setOption("showBuyNow", v)} />
          {element.options?.showBuyNow !== false && (
            <StyleField label={elakayAutoText("h_eb55ef9444880a", "Texte 'Acheter maintenant'")} value={element.options?.labelBuy} onChange={(v) => setOption("labelBuy", v)} placeholder={elakayAutoText("h_af7dc34a918afa", "Acheter maintenant")} />
          )}
        </Section>
      )}

      {/* ── Options Carte Produit ── */}
      {def?.isShopCard && (
        <Section title={elakayAutoText("h_236da7e95890a7", "Carte Produit")} icon={CreditCard}>
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_3ab228b1650259", "Index du produit affiché")}</span>
            <input type="number" min={0} value={element.options?.productIndex ?? 0} onChange={(e) => setOption("productIndex", Number(e.target.value))} style={selectStyle} />
            <p style={{ fontSize: "10px", color: "#9b93b8", margin: "4px 0 0" }}>{elakayAutoText("h_9baf7997e750b6", "0 = 1er produit, 1 = 2e, etc. (source : store.products)")}</p>
          </label>
          <CheckField label={elakayAutoText("h_e9b01cbf477446", "Afficher 'Ajouter au panier'")} checked={element.options?.showAddToCartButton !== false} onChange={(v) => setOption("showAddToCartButton", v)} />
        </Section>
      )}

      {/* ── Options Hero Slider ── */}
      {def?.isShopSlider && (
        <Section title={elakayAutoText("h_ecc7dc2d04eeed", "Hero Slider")} icon={GalleryHorizontalEnd}>
          <CheckField label={elakayAutoText("h_bec68545bedb23", "Afficher les pubs (SliderPunShow)")} checked={!!element.options?.SliderPunShow} onChange={(v) => setOption("SliderPunShow", v)} />
          <CheckField label={elakayAutoText("h_b790560b74e4db", "Mode boutique (images shop)")} checked={element.options?.shop !== false} onChange={(v) => setOption("shop", v)} />
        </Section>
      )}

      {/* ── Options Grille Produits ── */}
      {def?.isShopProductGrid && (
        <Section title={elakayAutoText("h_5797493d06691f", "Grille Produits")} icon={LayoutGrid}>
          {/* Présentation & chargement (Shop Builder) */}
          <div style={{ marginBottom: "10px", paddingBottom: "10px", borderBottom: "1px solid #ece9f5" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_spg_presentation", "Présentation des produits")}</span>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_spg_layout", "Disposition")}</span>
              <select value={element.options?.layout || "auto"} onChange={(e) => setOption("layout", e.target.value)} style={selectStyle}>
                <option value="auto">{elakayAutoText("h_spg_layout_auto", "Grille (choix portrait/paysage du visiteur)")}</option>
                <option value="grid">{elakayAutoText("h_spg_layout_grid", "Grille — cartes portrait")}</option>
                <option value="list">{elakayAutoText("h_spg_layout_list", "Liste — cartes paysage")}</option>
              </select>
            </label>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_spg_loading", "Chargement")}</span>
              <select value={element.options?.loadingMode || "inherit"} onChange={(e) => setOption("loadingMode", e.target.value)} style={selectStyle}>
                <option value="inherit">{elakayAutoText("h_spg_loading_inherit", "Réglage de la plateforme")}</option>
                <option value="pagination">{elakayAutoText("h_spg_loading_pagination", "Pagination (boutons)")}</option>
                <option value="infinite">{elakayAutoText("h_spg_loading_infinite", "Défilement infini")}</option>
              </select>
            </label>
            {/* Shop Product Grid / Infinite Scroll par appareil : exception de CETTE
                page à la configuration My Shop de la boutique (élément > boutique >
                grille historique), active seulement si Developer l'autorise. */}
            <div style={{ marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_spd_block_title", "Affichage par appareil (cette page)")}</span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px", marginTop: "6px" }}>
                {[["desktop", elakayAutoText("h_spd_device_desktop", "Desktop / PC")], ["tablet", elakayAutoText("h_spd_device_tablet", "Tablet")], ["mobile", elakayAutoText("h_spd_device_mobile", "Mobile")]].map(([device, deviceLabel]) => (
                  <label key={device} style={{ display: "grid", gridTemplateColumns: "92px 1fr", alignItems: "center", gap: "6px" }}>
                    <span style={{ ...fieldLabel, marginBottom: 0 }}>{deviceLabel}</span>
                    <select value={element.options?.deviceModes?.[device] || "inherit"} onChange={(e) => setOption("deviceModes", { ...(element.options?.deviceModes || {}), [device]: e.target.value })} style={selectStyle}>
                      <option value="inherit">{elakayAutoText("h_spd_block_inherit", "Réglage My Shop de la boutique")}</option>
                      <option value="grid">{elakayAutoText("h_spd_mode_grid", "Shop Product Grid")}</option>
                      <option value="infinite">{elakayAutoText("h_spd_mode_infinite", "Infinite Scroll")}</option>
                    </select>
                  </label>
                ))}
              </div>
              <span style={{ fontSize: "11px", color: "#9b93b8", lineHeight: 1.5, display: "block", marginTop: "4px" }}>{elakayAutoText("h_spd_block_help", "Priorité : ce choix, puis le réglage My Shop, puis Shop Product Grid. Sans l'autorisation Developer, Shop Product Grid est toujours utilisé.")}</span>
            </div>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_spg_sort", "Ordre des produits")}</span>
              <select value={element.options?.sortKey || "newest"} onChange={(e) => setOption("sortKey", e.target.value)} style={selectStyle}>
                <option value="newest">{elakayAutoText("h_spg_sort_newest", "Plus récents")}</option>
                <option value="oldest">{elakayAutoText("h_spg_sort_oldest", "Plus anciens")}</option>
                <option value="priceAsc">{elakayAutoText("h_spg_sort_price_asc", "Prix croissant")}</option>
                <option value="priceDesc">{elakayAutoText("h_spg_sort_price_desc", "Prix décroissant")}</option>
                <option value="rating">{elakayAutoText("h_spg_sort_rating", "Mieux notés")}</option>
                <option value="name">{elakayAutoText("h_spg_sort_name", "Nom (A → Z)")}</option>
              </select>
            </label>
            {(element.options?.loadingMode === "infinite" || Object.values(element.options?.deviceModes || {}).includes("infinite")) && (
              <>
                <label style={{ display: "block", marginBottom: "10px" }}>
                  <span style={fieldLabel}>{elakayAutoText("h_spg_initial", "Produits affichés au départ")}</span>
                  <input type="number" min={1} max={50} value={element.options?.initialCount ?? element.options?.limit ?? 12} onChange={(e) => setOption("initialCount", Math.min(50, Math.max(1, Number(e.target.value) || 1)))} style={selectStyle} />
                </label>
                <label style={{ display: "block", marginBottom: "10px" }}>
                  <span style={fieldLabel}>{elakayAutoText("h_spg_batch", "Produits chargés à chaque lot")}</span>
                  <input type="number" min={1} max={50} value={element.options?.batchSize ?? element.options?.limit ?? 12} onChange={(e) => setOption("batchSize", Math.min(50, Math.max(1, Number(e.target.value) || 1)))} style={selectStyle} />
                </label>
              </>
            )}
            <span style={{ fontSize: "11px", color: "#9b93b8", lineHeight: 1.5, display: "block" }}>{elakayAutoText("h_spg_presentation_help", "Les filtres, catégories et la recherche de la boutique pilotent toujours cette grille. Les produits sont chargés par lots depuis le serveur, sans doublon.")}</span>
          </div>

          {/* Pagination */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{element.options?.loadingMode === "infinite" ? elakayAutoText("h_spg_page_size_fallback", "Taille de page (si l'infini est indisponible)") : elakayAutoText("h_a41c9b079f4435", "Produits par page (limit)")}</span>
            <input type="number" min={1} max={48} value={element.options?.limit ?? 12} onChange={(e) => setOption("limit", Number(e.target.value))} style={selectStyle} />
          </label>
          <StyleField label={elakayAutoText("h_a3d0c9999cefae", "Titre de catégorie")} value={element.options?.prodCat} onChange={(v) => setOption("prodCat", v)} placeholder={elakayAutoText("h_61fda7af636915", "Tous les Produits")} />

          {/* Grille */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_b1c2bd4cebff5b", "Style de grille")}</span>
            <select value={element.options?.gridStyle || "minimal"} onChange={(e) => setOption("gridStyle", e.target.value)} style={selectStyle}>
              {[["minimal","Minimal"],["shadow","Ombres"],["border","Bordures"],["glass","Verre"],["dark","Sombre"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <StyleField label={elakayAutoText("h_9b60ccc8c60cf6", "Fond grille (custom)")} value={element.options?.gridBackground} onChange={(v) => setOption("gridBackground", v || null)} placeholder={elakayAutoText("h_78786f7e79c04e", "ex: #f9fafb")} />
          <CheckField label={elakayAutoText("h_6aacb3fd4377a2", "Grille animée")} checked={element.options?.gridAnimated === true} onChange={(v) => setOption("gridAnimated", v)} />
          <CheckField label={elakayAutoText("h_edbfd675c40c1d", "Cartes animées (fadeInUp)")} checked={element.options?.animateCards === true} onChange={(v) => setOption("animateCards", v)} />

          {/* Colonnes */}
          <div style={{ marginTop: "8px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_ea1bbf5c0fad9f", "Colonnes par breakpoint")}</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "6px" }}>
              {[["xs",elakayAutoText("h_spg_cols_mobile", "Mobile")],["sm",elakayAutoText("h_spg_cols_large_mobile", "Grand mobile (≥576px)")],["md",elakayAutoText("h_spg_cols_tablet", "Tablette (≥768px)")],["lg",elakayAutoText("h_spg_cols_desktop", "Ordinateur (≥992px)")]].map(([bp, lbl]) => (
                <label key={bp} style={{ display: "block" }}>
                  <span style={{ ...fieldLabel, marginBottom: "3px" }}>{lbl}</span>
                  <input type="number" min={1} max={6} value={element.options?.columns?.[bp] ?? (bp === "xs" || bp === "sm" ? 2 : bp === "md" ? 3 : 4)} onChange={(e) => setOption("columns", { ...element.options?.columns, [bp]: Number(e.target.value) })} style={{ ...selectStyle, padding: "6px 8px" }} />
                </label>
              ))}
            </div>
          </div>
          <label style={{ display: "block", marginBottom: "10px", marginTop: "8px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_f56ea7e0759df0", "Cartes vides (placeholder) — emptyCount")}</span>
            <input type="number" min={0} max={24} value={element.options?.emptyCount ?? 12} onChange={(e) => setOption("emptyCount", Number(e.target.value))} style={selectStyle} />
          </label>

          {/* Barre résultats */}
          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_4a048c7fbf1dd0", "Barre de résultats (ResultPagination)")}</span>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_65dcaf0f1d89fb", "Style résultats")}</span>
              <select value={element.options?.resultStyle || "gradient"} onChange={(e) => setOption("resultStyle", e.target.value)} style={selectStyle}>
                {[["gradient","Dégradé"],["glass","Verre"],["dark","Sombre"],["minimal","Minimal"],["success","Succès (vert)"],["warm","Chaud (orange)"],["cool","Froid (bleu)"],["rose","Rose"],["indigo","Indigo"],["slate","Ardoise"],["custom","Personnalisé"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_18dceae57df85d", "Fond personnalisé")} value={element.options?.resultBackground} onChange={(v) => setOption("resultBackground", v || null)} placeholder={elakayAutoText("h_2fbb878cf9bd5d", "ex: linear-gradient(…)")} />
            <CheckField label={elakayAutoText("h_bd5a35febf89b0", "Barre animée (gradientWave)")} checked={element.options?.resultAnimated === true} onChange={(v) => setOption("resultAnimated", v)} />
            <CheckField label={elakayAutoText("h_e8f1f62d42fe9b", "Afficher icône")} checked={element.options?.showResultIcon !== false} onChange={(v) => setOption("showResultIcon", v)} />
            <CheckField label={elakayAutoText("h_d72545b9967e44", "Afficher badge (page)")} checked={element.options?.showResultBadge !== false} onChange={(v) => setOption("showResultBadge", v)} />
            <CheckField label={elakayAutoText("h_c1775fc0d730eb", "Afficher info pagination")} checked={element.options?.showResultPageInfo !== false} onChange={(v) => setOption("showResultPageInfo", v)} />
          </div>
        </Section>
      )}

      {/* ── Options Header Shop Seller ── */}
      {def?.isShopHeaderSeller && (
        <Section title={elakayAutoText("h_b4f544b44c9e06", "Header Shop Seller")} icon={Monitor}>
          <CheckField label={elakayAutoText("h_aa3e533e3bf6cb", "Mode MyShop (affiche tabs Boutique/Builder/Aperçu)")} checked={!!element.options?.myShop} onChange={(v) => setOption("myShop", v)} />
          <CheckField label={elakayAutoText("h_ebd6731ac8fcd5", "Afficher bouton Filter")} checked={element.options?.showFilter !== false} onChange={(v) => setOption("showFilter", v)} />
          <CheckField label={elakayAutoText("h_e7e6b73b971b22", "Activer menu Lakay Seller")} checked={!!element.options?.lakaySeller} onChange={(v) => setOption("lakaySeller", v)} />
          <CheckField label={elakayAutoText("h_7dfda99054fe30", "Mode outOffPage")} checked={element.options?.outOffPage !== false} onChange={(v) => setOption("outOffPage", v)} />

          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_a1d479cc4c093e", "Couleurs entête")}</span>
            <StyleField label={elakayAutoText("h_d65caa0e446934", "Fond")} value={element.options?.backgroundColor} onChange={(v) => setOption("backgroundColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_6807cdd2485580", "Bordure")} value={element.options?.borderColor} onChange={(v) => setOption("borderColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_7430e34e28b186", "Ombre (rgba)")} value={element.options?.shadowColor} onChange={(v) => setOption("shadowColor", v)} placeholder={elakayAutoText("h_c7238605a68eb1", "rgba(0,0,0,0.1)")} />
          </div>

          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_542532607b68b7", "Nom boutique")}</span>
            <StyleField label={elakayAutoText("h_145c4326e92bd9", "Couleur")} value={element.options?.shopNameColor} onChange={(v) => setOption("shopNameColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_71ee57ac8fa979", "Police (font-family)")} value={element.options?.shopNameFontFamily} onChange={(v) => setOption("shopNameFontFamily", v)} placeholder={elakayAutoText("h_224b69b513b5a3", "inherit / Poppins")} />
            <StyleField label={elakayAutoText("h_2b90839d6bba3b", "Taille")} value={element.options?.shopNameFontSize} onChange={(v) => setOption("shopNameFontSize", v)} placeholder={elakayAutoText("h_b29b533397a3e9", "24px")} />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_1fc3c47737397e", "Graisse")}</span>
              <select value={String(element.options?.shopNameFontWeight ?? 700)} onChange={(e) => setOption("shopNameFontWeight", Number(e.target.value))} style={selectStyle}>
                {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
          </div>

          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_57f5f5efbc5990", "Menu")}</span>
            <StyleField label={elakayAutoText("h_1dbc652d3f9bd3", "Couleur texte")} value={element.options?.menuColor} onChange={(v) => setOption("menuColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_a5dfc157da97ac", "Police")} value={element.options?.menuFontFamily} onChange={(v) => setOption("menuFontFamily", v)} placeholder={elakayAutoText("h_224b69b513b5a3", "inherit / Poppins")} />
            <StyleField label={elakayAutoText("h_2b90839d6bba3b", "Taille")} value={element.options?.menuFontSize} onChange={(v) => setOption("menuFontSize", v)} placeholder={elakayAutoText("h_4678bd4d8aa022", "16px")} />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_1fc3c47737397e", "Graisse")}</span>
              <select value={String(element.options?.menuFontWeight ?? 600)} onChange={(e) => setOption("menuFontWeight", Number(e.target.value))} style={selectStyle}>
                {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
          </div>

          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_787b54924ae6ff", "Recherche")}</span>
            <StyleField label={elakayAutoText("h_d65caa0e446934", "Fond")} value={element.options?.searchBackgroundColor} onChange={(v) => setOption("searchBackgroundColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_6807cdd2485580", "Bordure")} value={element.options?.searchBorderColor} onChange={(v) => setOption("searchBorderColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_ff9afef1eae0a9", "Texte")} value={element.options?.searchTextColor} onChange={(v) => setOption("searchTextColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_ed212fa164b940", "Placeholder")} value={element.options?.searchPlaceholderColor} onChange={(v) => setOption("searchPlaceholderColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_a5dfc157da97ac", "Police")} value={element.options?.searchFontFamily} onChange={(v) => setOption("searchFontFamily", v)} placeholder={elakayAutoText("h_224b69b513b5a3", "inherit / Poppins")} />
            <StyleField label={elakayAutoText("h_2b90839d6bba3b", "Taille")} value={element.options?.searchFontSize} onChange={(v) => setOption("searchFontSize", v)} placeholder={elakayAutoText("h_4678bd4d8aa022", "16px")} />
          </div>

          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_9284a8f7ecc7c8", "Tabs myShop")}</span>
            <StyleField label={elakayAutoText("h_b3ec71afc34777", "Fond barre tabs")} value={element.options?.modeBarBackground} onChange={(v) => setOption("modeBarBackground", v)} placeholder={elakayAutoText("h_921faaf523bca2", "rgba(...)")} />
            <StyleField label={elakayAutoText("h_1de51a4143c18c", "Bordure barre tabs")} value={element.options?.modeBarBorderColor} onChange={(v) => setOption("modeBarBorderColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_abb84c93dcd5a2", "Couleur actif")} value={element.options?.modeActiveColor} onChange={(v) => setOption("modeActiveColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_c9af191046d881", "Fond actif")} value={element.options?.modeActiveBackground} onChange={(v) => setOption("modeActiveBackground", v)} type="color" />
            <StyleField label={elakayAutoText("h_3677563baa20e5", "Bordure actif")} value={element.options?.modeActiveBorderColor} onChange={(v) => setOption("modeActiveBorderColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_dea948a6eeb710", "Couleur inactif")} value={element.options?.modeInactiveColor} onChange={(v) => setOption("modeInactiveColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_d5a5ce27433ca0", "Fond chip éditeur")} value={element.options?.editorChipBackground} onChange={(v) => setOption("editorChipBackground", v)} placeholder={elakayAutoText("h_3386928915d4bb", "linear-gradient(...)")} />
            <StyleField label={elakayAutoText("h_5215520af9ac64", "Texte chip éditeur")} value={element.options?.editorChipColor} onChange={(v) => setOption("editorChipColor", v)} type="color" />
          </div>
        </Section>
      )}

      {/* ── Options Catégories Matrix ── */}
      {def?.isShopCatMatrix && (
        <Section title={elakayAutoText("h_d45410db6b5705", "Catégories Matrix")} icon={LayoutGrid}>
          <StyleField label={elakayAutoText("h_eb97899aedcd56", "Titre")} value={element.options?.title} onChange={(v) => setOption("title", v)} placeholder={elakayAutoText("h_f2ef9fd31c91ee", "Nos Catégories")} />
          <CheckField label={elakayAutoText("h_f2337b86f6491c", "Afficher le titre (inView)")} checked={element.options?.inView !== false} onChange={(v) => setOption("inView", v)} />
          <CheckField label={elakayAutoText("h_3d7ae55982f978", "Lien boutique en pied de carte (textFoorter)")} checked={!!element.options?.textFoorter} onChange={(v) => setOption("textFoorter", v)} />

          {/* Animation */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_d92ef7bdc93978", "Animation du container")}</span>
            <select value={element.options?.type_Animation || ""} onChange={(e) => setOption("type_Animation", e.target.value || null)} style={selectStyle}>
              <option value="">{elakayAutoText("h_e8f882731020de", "Aucune")}</option>
              {["Arc-en-ciel","Rose & Violet","Vert & Jaune","Orange & Rouge","Bleu & Indigo","Cyan & Teal","Or & Bronze","Argent","Neon Magenta","Rainbow","Deep Purple","Lime","Pink","Amber","Brown","Blue Grey","Coral","Blanc & Turquoise","Matrix Digital","Neon Glow","Liquid Blob","Holographic","Electric Storm","Orbital","Water Ripples","Smoke","Confetti","Sparkle Stars","Hexagon","Triangle","Morphing","Energy Burst","Aurora"].map((name, i) => (
                <option key={i} value={`${i}_type_Animation`}>{i} — {name}</option>
              ))}
            </select>
          </label>
          <StyleField label={elakayAutoText("h_b506759876dad2", "Couleur fond container")} value={element.options?.backgroundColor} onChange={(v) => setOption("backgroundColor", v)} type="color" />

          {/* Slides & produits */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_55c989dbdb42ad", "Produits max par carte (nbrArr)")}</span>
            <input type="number" min={1} max={8} value={element.options?.nbrArr ?? 4} onChange={(e) => setOption("nbrArr", Number(e.target.value))} style={selectStyle} />
          </label>
          <div style={{ marginTop: "4px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_418dbccc19a7fc", "Slides visibles / écran")}</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "6px" }}>
              {[["wm0","Mobile"],["wm640","640px"],["wm768","768px"],["wm1024","1024px"],["wm1200","1200px"]].map(([k, lbl]) => (
                <label key={k} style={{ display: "block" }}>
                  <span style={{ ...fieldLabel, marginBottom: "3px" }}>{lbl}</span>
                  <input type="number" min={1} max={5} step={0.5} value={element.options?.[k] ?? (k === "wm0" ? 1.3 : k === "wm640" ? 2 : 3)} onChange={(e) => setOption(k, Number(e.target.value))} style={{ ...selectStyle, padding: "6px 8px" }} />
                </label>
              ))}
            </div>
          </div>

          {/* Typographie du titre */}
          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_a35a23014474e6", "Typographie du titre")}</span>
            <StyleField label={elakayAutoText("h_145c4326e92bd9", "Couleur")} value={element.options?.titleColor} onChange={(v) => setOption("titleColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_25c65b13cdafd0", "Taille (ex: 1.75rem)")} value={element.options?.titleFontSize} onChange={(v) => setOption("titleFontSize", v)} placeholder={elakayAutoText("h_28f667fcee0c87", "1.75rem")} />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_1fc3c47737397e", "Graisse")}</span>
              <select value={String(element.options?.titleFontWeight ?? 700)} onChange={(e) => setOption("titleFontWeight", Number(e.target.value))} style={selectStyle}>
                {[400, 500, 600, 700, 800, 900].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_71ee57ac8fa979", "Police (font-family)")} value={element.options?.titleFontFamily} onChange={(v) => setOption("titleFontFamily", v)} placeholder={elakayAutoText("h_224b69b513b5a3", "inherit / Poppins")} />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_836f22b3170976", "Style police")}</span>
              <select value={element.options?.titleFontStyle || "normal"} onChange={(e) => setOption("titleFontStyle", e.target.value)} style={selectStyle}>
                {["normal","italic","oblique"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_8d051dc9aebe10", "Espacement lettres")} value={element.options?.titleLetterSpacing} onChange={(v) => setOption("titleLetterSpacing", v)} placeholder={elakayAutoText("h_7a57c4b4e3c31a", "-0.5px")} />
            <StyleField label={elakayAutoText("h_a2343e7a0b86a7", "Hauteur de ligne")} value={String(element.options?.titleLineHeight ?? 1.2)} onChange={(v) => setOption("titleLineHeight", isNaN(Number(v)) ? v : Number(v))} placeholder="1.2" />
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_0ee170f6dfaf4d", "Transformation texte")}</span>
              <select value={element.options?.titleTextTransform || "none"} onChange={(e) => setOption("titleTextTransform", e.target.value)} style={selectStyle}>
                {["none","uppercase","lowercase","capitalize"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_34ced4bc54549c", "Alignement texte")}</span>
              <select value={element.options?.titleTextAlign || "left"} onChange={(e) => setOption("titleTextAlign", e.target.value)} style={selectStyle}>
                {["left","center","right"].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <StyleField label={elakayAutoText("h_71d732f0613e9d", "Ombre texte")} value={element.options?.titleTextShadow} onChange={(v) => setOption("titleTextShadow", v)} placeholder={elakayAutoText("h_405963c16d2791", "0 2px 4px rgba(0,0,0,0.05)")} />
          </div>

          {/* Style du header */}
          <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_fb68dd8c79921a", "Style du header")}</span>
            <StyleField label={elakayAutoText("h_c5dcaf53ec2281", "Fond header")} value={element.options?.headerBackgroundColor} onChange={(v) => setOption("headerBackgroundColor", v)} type="color" />
            <StyleField label={elakayAutoText("h_2be7016346bf8d", "Padding header")} value={element.options?.headerPadding} onChange={(v) => setOption("headerPadding", v || null)} placeholder={elakayAutoText("h_990e2236cc6d73", "ex: 8px 16px")} />
            <StyleField label={elakayAutoText("h_2a8a3c9e9d20d4", "Marge bas header")} value={element.options?.headerMarginBottom} onChange={(v) => setOption("headerMarginBottom", v || null)} placeholder={elakayAutoText("h_da6e8f49833a20", "ex: 16px")} />
          </div>
        </Section>
      )}

      {/* ── Options Pagination (ButtonPagination) ── */}
      {def?.isShopButtonPagination && (
        <Section title={elakayAutoText("h_93ab09a7753453", "Pagination Boutique")} icon={ChevronsDownUp}>
          <p style={{ fontSize: "11px", color: "#9b93b8", margin: "0 0 10px" }}>{elakayAutoText("h_6199a9cd04eaec", "Classes Bootstrap pour les boutons ← Précédent / Suivant →")}</p>
          {/* Préréglages visuels */}
          <label style={{ display: "block", marginBottom: "10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_7f80dece1405f6", "Préréglage style")}</span>
            <select style={selectStyle} onChange={(e) => {
              const v = e.target.value;
              if (v === "primary") {
                setOption("buttonClassL",  "btn btn-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassLD", "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassR",  "btn btn-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
                setOption("buttonClassRD", "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
              } else if (v === "outline") {
                setOption("buttonClassL",  "btn btn-outline-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassLD", "btn btn-outline-secondary pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassR",  "btn btn-outline-primary pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
                setOption("buttonClassRD", "btn btn-outline-secondary pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
              } else if (v === "dark") {
                setOption("buttonClassL",  "btn btn-dark pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassLD", "btn btn-secondary pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassR",  "btn btn-dark pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
                setOption("buttonClassRD", "btn btn-secondary pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
              } else if (v === "success") {
                setOption("buttonClassL",  "btn btn-success pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassLD", "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-left");
                setOption("buttonClassR",  "btn btn-success pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
                setOption("buttonClassRD", "btn pt-2 pb-2 pr-2 pl-2 rounded-4 float-right");
              }
            }}>
              <option value="">{elakayAutoText("h_820e00b925ceb6", "— Choisir un préréglage —")}</option>
              <option value="primary">{elakayAutoText("h_280676cf8f545f", "Primaire (bleu Bootstrap)")}</option>
              <option value="outline">{elakayAutoText("h_ae77a05fc26766", "Contour")}</option>
              <option value="dark">{elakayAutoText("h_b1fa84a34af116", "Sombre")}</option>
              <option value="success">{elakayAutoText("h_8433d5e88e9226", "Succès (vert)")}</option>
            </select>
          </label>

          {/* Classes individuelles */}
          <div style={{ marginTop: "8px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
            <span style={{ ...fieldLabel, display: "block", marginBottom: "8px" }}>{elakayAutoText("h_76ed300b55d7a5", "Classes CSS détaillées")}</span>
            <StyleField label={elakayAutoText("h_c2de3c3de90e0d", "← Précédent (actif)")} value={element.options?.buttonClassL} onChange={(v) => setOption("buttonClassL", v)} placeholder={elakayAutoText("h_3381e7eaec5d0d", "btn btn-primary … float-left")} />
            <StyleField label={elakayAutoText("h_ae6b00d6532e9a", "← Précédent (désactivé)")} value={element.options?.buttonClassLD} onChange={(v) => setOption("buttonClassLD", v)} placeholder={elakayAutoText("h_de9f61cf82bd18", "btn … float-left")} />
            <StyleField label={elakayAutoText("h_a4be598915e4f6", "Suivant → (actif)")} value={element.options?.buttonClassR} onChange={(v) => setOption("buttonClassR", v)} placeholder={elakayAutoText("h_a21171bc33e46d", "btn btn-primary … float-right")} />
            <StyleField label={elakayAutoText("h_6a56e973b63cfe", "Suivant → (désactivé)")} value={element.options?.buttonClassRD} onChange={(v) => setOption("buttonClassRD", v)} placeholder={elakayAutoText("h_9664bb722e1dfc", "btn … float-right")} />
          </div>
        </Section>
      )}

      {/* ── Options Carte Case Study (caseCard) ── */}
      {element._caseCard && (
        <Section title={elakayAutoText("h_5ebbb0b141a5bc", "Carte Case Study — Options")} icon={CreditCard}>

          {/* ── Orientation : Paysage ↔ Portrait ── */}
          {(() => {
            const orientation = element.options?.orientation || "landscape";
            const isLandscape = orientation === "landscape";
            const applyOrientation = (newOri) => {
              const isL = newOri === "landscape";
              const imgChild     = element.children?.[0];
              const contentChild = element.children?.[1];
              if (!imgChild || !contentChild) { setOption("orientation", newOri); return; }
              const newImg = { ...imgChild, style: isL
                ? { width:"38%", minWidth:"180px", objectFit:"cover", display:"block", flexShrink:0 }
                : { width:"100%", height:"220px", objectFit:"cover", display:"block", flexShrink:0 } };
              const newContent = { ...contentChild, style: isL
                ? { flex:1, padding:"28px 32px 24px", display:"flex", flexDirection:"column", gap:"18px", justifyContent:"center" }
                : { padding:"16px 18px 18px", display:"flex", flexDirection:"column", gap:"12px", flex:1 } };
              onUpdate({ ...element,
                options:  { ...element.options, orientation: newOri },
                style:    { ...element.style, flexDirection: isL ? "row" : "column" },
                children: [newImg, newContent, ...element.children.slice(2)] });
            };
            return (
              <div style={{ marginBottom:"14px" }}>
                <span style={{ ...fieldLabel, display:"block", marginBottom:"7px" }}>{elakayAutoText("h_86e4e3875420cd", "Orientation")}</span>
                <div style={{ display:"flex", gap:"7px" }}>
                  {[{ key:"landscape", label:elakayAutoText("h_6650bfbbc4f4e5", "↔ Paysage"), sub:elakayAutoText("page_builder_image_left", "image on the left") },
                    { key:"portrait",  label:elakayAutoText("h_53e3ba3f94b491", "↕ Portrait"), sub:elakayAutoText("page_builder_image_top", "image at the top") }].map(({ key, label, sub }) => (
                    <button key={key} onClick={() => applyOrientation(key)}
                      style={{ flex:1, padding:"8px 6px", borderRadius:"8px", cursor:"pointer",
                        border:`2px solid ${orientation === key ? "#6d28d9" : "#e4dcfb"}`,
                        background: orientation === key ? "#f5f3ff" : "#faf9fd",
                        display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" }}>
                      <span style={{ fontSize:"16px" }}>{key === "landscape" ? "⬜→" : "⬜↓"}</span>
                      <span style={{ fontSize:"11px", fontWeight:700, color: orientation === key ? "#6d28d9" : "#4a4a68" }}>{label}</span>
                      <span style={{ fontSize:"9px", color:"#9b93b8" }}>{sub}</span>
                    </button>
                  ))}
                </div>
                {isLandscape && (
                  <StyleField label={elakayAutoText("h_56c186e2869aa0", "Largeur image (paysage)")} value={element.options?.imageWidth || "38%"}
                    onChange={(v) => setOption("imageWidth", v)} placeholder={elakayAutoText("h_29241620b49c0e", "38% / 300px")} />
                )}
                {!isLandscape && (
                  <StyleField label={elakayAutoText("h_e9232e751c6f95", "Hauteur image (portrait)")} value={element.options?.imageHeight || "220px"}
                    onChange={(v) => setOption("imageHeight", v)} placeholder={elakayAutoText("h_85cc633a13bad7", "220px")} />
                )}
              </div>
            );
          })()}

          {/* Source de données */}
          <label style={{ display:"block", marginBottom:"10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_ec28057c6c9a3d", "Source de données (bindSource)")}</span>
            <select value={element.bindSource || "store.products.0"}
              onChange={(e) => setProp("bindSource", e.target.value)} style={selectStyle}>
              {[0,1,2,3,4,5].map(i => (
                <option key={i} value={`store.products.${i}`}>{elakayAutoText("h_9965a3d8d3f8e1", "store.products[")}{i}{elakayAutoText("h_78792850a5d8cd", "] (produit")}{i+1})</option>
              ))}
            </select>
          </label>
          {/* Border radius de la carte */}
          <StyleField label={elakayAutoText("h_ce1985770edade", "Border radius carte")} value={element.options?.borderRadius || "14px"}
            onChange={(v) => setOption("borderRadius", v)} placeholder={elakayAutoText("h_d65225386d805a", "14px")} />
          {/* Champs bindKey */}
          <div style={{ marginTop:"10px", paddingTop:"10px", borderTop:"1px solid #ece9f5" }}>
            <span style={{ ...fieldLabel, display:"block", marginBottom:"8px" }}>{elakayAutoText("h_41e2958ef44da6", "Clés de données (bindKey)")}</span>
            <StyleField label={elakayAutoText("h_6f231fc69c47b5", "Clé image")}       value={element.options?.imageKey       || "ProductImageUrls.0"} onChange={(v) => setOption("imageKey", v)}       placeholder={elakayAutoText("h_6eb04bcf5f30d7", "ProductImageUrls.0")} />
            <StyleField label={elakayAutoText("h_96ff26ec7fb3b6", "Clé titre")}        value={element.options?.titleKey        || "name"}        onChange={(v) => setOption("titleKey", v)}        placeholder={elakayAutoText("h_6ae999552a0d2d", "name")} />
            <StyleField label={elakayAutoText("h_298447ac5c866c", "Clé description")}  value={element.options?.descriptionKey  || "description"} onChange={(v) => setOption("descriptionKey", v)}  placeholder={elakayAutoText("h_cb329146a0dd0d", "description")} />
            <StyleField label={elakayAutoText("h_158d195bae22fb", "Clé stat 1")}        value={element.options?.stat1Key        || "price"}       onChange={(v) => setOption("stat1Key", v)}        placeholder={elakayAutoText("h_2097c33723b61c", "price")} />
            <StyleField label={elakayAutoText("h_1d2fc046a7f0c3", "Label stat 1")}      value={element.options?.stat1Label      || "Prix ($)"}    onChange={(v) => setOption("stat1Label", v)}      placeholder={elakayAutoText("h_d16c86af0e22b2", "Prix ($)")} />
            <StyleField label={elakayAutoText("h_93588de183d264", "Clé stat 2")}        value={element.options?.stat2Key        || "Stars"}       onChange={(v) => setOption("stat2Key", v)}        placeholder={elakayAutoText("h_adb03812b4ef51", "Stars")} />
            <StyleField label={elakayAutoText("h_6397036cb1435a", "Label stat 2")}      value={element.options?.stat2Label      || "Note client"} onChange={(v) => setOption("stat2Label", v)}      placeholder={elakayAutoText("h_7a324e67414a55", "Note client")} />
            <StyleField label={elakayAutoText("h_c12aeaf6a3f675", "Clé stat 3")}        value={element.options?.stat3Key        || "condition"}   onChange={(v) => setOption("stat3Key", v)}        placeholder={elakayAutoText("h_afad9a69767e7c", "condition")} />
            <StyleField label={elakayAutoText("h_22093b422df705", "Label stat 3")}      value={element.options?.stat3Label      || "État"}        onChange={(v) => setOption("stat3Label", v)}      placeholder={elakayAutoText("h_d3b28a2adb2712", "État")} />
            <StyleField label={elakayAutoText("h_2f2b965513922d", "Texte du lien")}     value={element.options?.linkText        || "Voir le produit →"} onChange={(v) => setOption("linkText", v)} placeholder={elakayAutoText("h_c92f7c4aba58a7", "Voir le produit →")} />
          </div>
          {/* Visibilité */}
          <div style={{ marginTop:"10px", paddingTop:"10px", borderTop:"1px solid #ece9f5" }}>
            <span style={{ ...fieldLabel, display:"block", marginBottom:"8px" }}>{elakayAutoText("h_44e47704c34c51", "Visibilité")}</span>
            <CheckField label={elakayAutoText("h_7dd6e3aebd3654", "Afficher la description")} checked={element.options?.showDescription !== false} onChange={(v) => setOption("showDescription", v)} />
            <CheckField label={elakayAutoText("h_e8165ca2510783", "Afficher stat 1 (prix)")}  checked={element.options?.showPrice      !== false} onChange={(v) => setOption("showPrice", v)} />
            <CheckField label={elakayAutoText("h_56621508751a47", "Afficher stat 2 (note)")}  checked={element.options?.showStars      !== false} onChange={(v) => setOption("showStars", v)} />
            <CheckField label={elakayAutoText("h_77a5d685b1c0cd", "Afficher stat 3 (état)")}  checked={element.options?.showCondition  !== false} onChange={(v) => setOption("showCondition", v)} />
          </div>
        </Section>
      )}

      {/* ── Options Grille Case Study (caseGrid) ── */}
      {element._caseGrid && (
        <Section title={elakayAutoText("h_db5cb1ce348c41", "Grille Case Study — Options")} icon={LayoutGrid}>

          {/* ── Orientation des cartes de la grille ── */}
          {(() => {
            const orientation = element.options?.orientation || "portrait";
            const applyGridOrientation = (newOri) => {
              const isL = newOri === "landscape";
              const grid    = element.children?.[0];
              const cardTpl = grid?.children?.[0];
              if (!grid || !cardTpl) { setOption("orientation", newOri); return; }
              const imgChild     = cardTpl.children?.[0];
              const contentChild = cardTpl.children?.[1];
              if (!imgChild || !contentChild) { setOption("orientation", newOri); return; }
              const newImg = { ...imgChild, style: isL
                ? { width:"38%", minWidth:"120px", objectFit:"cover", display:"block", flexShrink:0 }
                : { width:"100%", height:(element.options?.imageHeight || "200px"), objectFit:"cover", display:"block", flexShrink:0 } };
              const newContent = { ...contentChild, style: isL
                ? { flex:1, padding:"16px 20px", display:"flex", flexDirection:"column", gap:"10px", justifyContent:"center" }
                : { padding:"16px 18px 18px", display:"flex", flexDirection:"column", gap:"10px", flex:1 } };
              const newCardTpl = { ...cardTpl,
                style: { ...cardTpl.style, flexDirection: isL ? "row" : "column" },
                children: [newImg, newContent, ...cardTpl.children.slice(2)] };
              const newGrid = { ...grid, children: [newCardTpl, ...grid.children.slice(1)] };
              onUpdate({ ...element,
                options:  { ...element.options, orientation: newOri },
                children: [newGrid, ...element.children.slice(1)] });
            };
            return (
              <div style={{ marginBottom:"14px" }}>
                <span style={{ ...fieldLabel, display:"block", marginBottom:"7px" }}>{elakayAutoText("h_65107f07bfacd9", "Orientation des cartes")}</span>
                <div style={{ display:"flex", gap:"7px" }}>
                  {[{ key:"landscape", label:elakayAutoText("h_6650bfbbc4f4e5", "↔ Paysage"), sub:elakayAutoText("page_builder_image_left", "image on the left") },
                    { key:"portrait",  label:elakayAutoText("h_53e3ba3f94b491", "↕ Portrait"), sub:elakayAutoText("page_builder_image_top", "image at the top") }].map(({ key, label, sub }) => (
                    <button key={key} onClick={() => applyGridOrientation(key)}
                      style={{ flex:1, padding:"8px 6px", borderRadius:"8px", cursor:"pointer",
                        border:`2px solid ${orientation === key ? "#6d28d9" : "#e4dcfb"}`,
                        background: orientation === key ? "#f5f3ff" : "#faf9fd",
                        display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" }}>
                      <span style={{ fontSize:"16px" }}>{key === "landscape" ? "⬜→" : "⬜↓"}</span>
                      <span style={{ fontSize:"11px", fontWeight:700, color: orientation === key ? "#6d28d9" : "#4a4a68" }}>{label}</span>
                      <span style={{ fontSize:"9px", color:"#9b93b8" }}>{sub}</span>
                    </button>
                  ))}
                </div>
                <p style={{ fontSize:"9px", color:"#b3aacb", margin:"5px 0 0", lineHeight:1.4 }}>{elakayAutoText("h_493af37e37c180", "Paysage : recommandé pour 1-2 colonnes. Portrait : recommandé pour 3-4 colonnes.")}</p>
              </div>
            );
          })()}

          {/* ── Colonnes responsive (min 1 / max 4) ── */}
          <div style={{ marginBottom:"12px" }}>
            <span style={{ ...fieldLabel, display:"block", marginBottom:"8px" }}>{elakayAutoText("h_36fb45ebccd1e3", "Colonnes par appareil (min 1 — max 4)")}</span>
            {[
              { label:elakayAutoText("h_532c67fe1b5afa", "Desktop"),  key:"columnsDesktop", def:4 },
              { label:elakayAutoText("h_ed91cd89f3c81b", "Laptop"),   key:"columnsLaptop",  def:3 },
              { label:elakayAutoText("h_fabcacd2a76a1a", "Tablet"),   key:"columnsTablet",  def:2 },
              { label:elakayAutoText("h_b1d702459399a2", "Mobile"),   key:"columnsMobile",  def:1 },
            ].map(({ label, key, def }) => (
              <div key={key} style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                <span style={{ fontSize:"11px", color:"#7a73a0", width:"60px", flexShrink:0 }}>{label}</span>
                <input type="range" min={1} max={4} step={1}
                  value={element.options?.[key] ?? def}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setOption(key, v);
                    // Synchronise le deviceGrid du 1er enfant (grid)
                    const grid = element.children?.[0];
                    if (grid?.options?.deviceGrid) {
                      const deviceMap = { columnsDesktop:"desktop", columnsLaptop:"laptop", columnsTablet:"tablet", columnsMobile:"mobile" };
                      const device = deviceMap[key];
                      const newDG = grid.options.deviceGrid.map(d => d.device === device ? { ...d, columns: v } : d);
                      const newGrid = { ...grid, options: { ...grid.options, deviceGrid: newDG },
                        style: { ...grid.style, gridTemplateColumns: `repeat(${element.options?.columnsDesktop ?? 4}, 1fr)` } };
                      onUpdate({ ...element, children: [newGrid, ...element.children.slice(1)], options: { ...element.options, [key]: v } });
                    }
                  }}
                  style={{ flex:1, accentColor:"#6d28d9" }} />
                <span style={{ fontSize:"12px", fontWeight:700, color:"#6d28d9", width:"14px", textAlign:"center" }}>
                  {element.options?.[key] ?? def}
                </span>
              </div>
            ))}
          </div>

          {/* ── Produits par page (pagination) ── */}
          <label style={{ display:"block", marginBottom:"10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_01e0a21ab557e1", "Produits par page (pageSize)")}</span>
            <input type="number" min={1} max={48}
              value={element.options?.pageSize ?? 8}
              onChange={(e) => {
                const v = Math.max(1, Number(e.target.value));
                setOption("pageSize", v);
                // Synchronise le repeatBind du gabarit carte (enfant du grid, premier enfant du wrapper)
                const grid = element.children?.[0];
                const tpl  = grid?.children?.[0];
                if (tpl?.repeatBind) {
                  const newTpl  = { ...tpl,  repeatBind: { ...tpl.repeatBind, pageSize: v } };
                  const newGrid = { ...grid, children: [newTpl, ...grid.children.slice(1)] };
                  onUpdate({ ...element, children: [newGrid, ...element.children.slice(1)], options: { ...element.options, pageSize: v } });
                }
              }}
              style={selectStyle} />
            <p style={{ fontSize:"10px", color:"#9b93b8", margin:"3px 0 0" }}>{elakayAutoText("h_b7a91c6ae63ca2", "Conseil : choisissez un multiple de colonnes (ex. 8 pour 4 col × 2 rangées).")}</p>
          </label>

          {/* Source de données */}
          <label style={{ display:"block", marginBottom:"10px" }}>
            <span style={fieldLabel}>{elakayAutoText("h_c47b2fd69728ed", "Source de données")}</span>
            <select value={element.options?.path || "products"}
              onChange={(e) => setOption("path", e.target.value)} style={selectStyle}>
              <option value="products">{elakayAutoText("h_753503173586ff", "store.products (tous les produits)")}</option>
              <option value="pubCategoryProdMatrix.products">{elakayAutoText("h_ea74a52b269769", "store.pubCategoryProdMatrix (produits vedettes)")}</option>
            </select>
          </label>

          {/* Hauteur image */}
          <StyleField label={elakayAutoText("h_4a22f05f262cab", "Hauteur image")} value={element.options?.imageHeight || "200px"}
            onChange={(v) => setOption("imageHeight", v)} placeholder={elakayAutoText("h_c70ab06fbb7aed", "200px")} />

          {/* Clés de données */}
          <div style={{ marginTop:"10px", paddingTop:"10px", borderTop:"1px solid #ece9f5" }}>
            <span style={{ ...fieldLabel, display:"block", marginBottom:"8px" }}>{elakayAutoText("h_41e2958ef44da6", "Clés de données (bindKey)")}</span>
            <StyleField label={elakayAutoText("h_158d195bae22fb", "Clé stat 1")}   value={element.options?.stat1Key   || "price"}     onChange={(v) => setOption("stat1Key", v)}   placeholder={elakayAutoText("h_2097c33723b61c", "price")} />
            <StyleField label={elakayAutoText("h_1d2fc046a7f0c3", "Label stat 1")} value={element.options?.stat1Label || "Prix"}       onChange={(v) => setOption("stat1Label", v)} placeholder={elakayAutoText("h_2d5eddef33c653", "Prix")} />
            <StyleField label={elakayAutoText("h_93588de183d264", "Clé stat 2")}   value={element.options?.stat2Key   || "Stars"}      onChange={(v) => setOption("stat2Key", v)}   placeholder={elakayAutoText("h_adb03812b4ef51", "Stars")} />
            <StyleField label={elakayAutoText("h_6397036cb1435a", "Label stat 2")} value={element.options?.stat2Label || "Note"}       onChange={(v) => setOption("stat2Label", v)} placeholder={elakayAutoText("h_2c924e3088204e", "Note")} />
            <StyleField label={elakayAutoText("h_c12aeaf6a3f675", "Clé stat 3")}   value={element.options?.stat3Key   || "condition"}  onChange={(v) => setOption("stat3Key", v)}   placeholder={elakayAutoText("h_afad9a69767e7c", "condition")} />
            <StyleField label={elakayAutoText("h_22093b422df705", "Label stat 3")} value={element.options?.stat3Label || "État"}       onChange={(v) => setOption("stat3Label", v)} placeholder={elakayAutoText("h_d3b28a2adb2712", "État")} />
            <StyleField label={elakayAutoText("h_2f2b965513922d", "Texte du lien")} value={element.options?.linkText  || "Voir le produit →"} onChange={(v) => setOption("linkText", v)} placeholder={elakayAutoText("h_c92f7c4aba58a7", "Voir le produit →")} />
          </div>

          {/* Visibilité */}
          <div style={{ marginTop:"10px", paddingTop:"10px", borderTop:"1px solid #ece9f5" }}>
            <span style={{ ...fieldLabel, display:"block", marginBottom:"8px" }}>{elakayAutoText("h_44e47704c34c51", "Visibilité")}</span>
            <CheckField label={elakayAutoText("h_7dd6e3aebd3654", "Afficher la description")} checked={element.options?.showDescription !== false} onChange={(v) => setOption("showDescription", v)} />
            <CheckField label={elakayAutoText("h_e8165ca2510783", "Afficher stat 1 (prix)")}  checked={element.options?.showPrice      !== false} onChange={(v) => setOption("showPrice", v)} />
            <CheckField label={elakayAutoText("h_56621508751a47", "Afficher stat 2 (note)")}  checked={element.options?.showStars      !== false} onChange={(v) => setOption("showStars", v)} />
            <CheckField label={elakayAutoText("h_77a5d685b1c0cd", "Afficher stat 3 (état)")}  checked={element.options?.showCondition  !== false} onChange={(v) => setOption("showCondition", v)} />
          </div>
        </Section>
      )}

      {!simpleMode && <Section title={elakayAutoText("h_e9a0ff24d54de2", "Console CSS / JS")} icon={Code2}>
        <label style={{ display: "block", marginBottom: "10px" }}>
          <span style={fieldLabel}>{elakayAutoText("h_226dd3c30ce590", "consoleCss (déclarations CSS)")}</span>
          <textarea
            value={element.consoleCss || ""}
            onChange={(e) => setProp("consoleCss", e.target.value)}
            placeholder={elakayAutoText("h_f62063817c2315", "color:#1f2937; font-size:18px; background:linear-gradient(...);")}
            rows={5}
            style={{ width: "100%", marginTop: "4px", padding: "10px 12px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          />
        </label>
        <label style={{ display: "block", marginBottom: "6px" }}>
          <span style={fieldLabel}>{elakayAutoText("h_a3557f2f1dabf7", "consoleJs (script élément)")}</span>
          <textarea
            value={element.consoleJs || ""}
            onChange={(e) => setProp("consoleJs", e.target.value)}
            placeholder={elakayAutoText("h_05da6fab726a31", "element.style.outline = '2px dashed #6d28d9';")}
            rows={7}
            style={{ width: "100%", marginTop: "4px", padding: "10px 12px", fontSize: "12px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          />
        </label>
        <p style={{ fontSize: "11px", color: "#9b93b8", margin: 0, lineHeight: 1.5 }}>{elakayAutoText("h_470acbdc0806f5", "`consoleCss` accepte des paires CSS (`propriété: valeur;`). `consoleJs` reçoit `element`, `node`, `context`.")}</p>
      </Section>}

      {/* Balise HTML + attributs libres : pour n'importe quel élément */}
      {!simpleMode && <TagAndAttrs element={element} onUpdate={onUpdate} setProp={setProp} />}

      {/* Inspecteur CSS complet : toutes les propriétés compatibles (desktop) */}
      {!simpleMode && <CssInspector style={s} setStyle={setStyle} def={def} />}
      </>)}

      {isMobile && (
        <p style={{ fontSize: "12px", color: "#9b93b8", lineHeight: 1.6, padding: "4px 2px" }}>{elakayAutoText("h_79243e31b86614", "En vue mobile, vous ajustez la")}<b>{elakayAutoText("h_50217d4d94a22d", "taille")}</b>{elakayAutoText("h_9708659f725bb2", "(largeur/hauteur) et la")}<b>{elakayAutoText("h_90be16585da21a", "position")}</b>{elakayAutoText("h_de5b70083d4776", "de l'élément. Tout le reste (couleurs, texte, etc.) reste hérité du desktop. Repassez en vue Desktop pour modifier les autres styles.")}</p>
      )}

      {/* ── Onglet Mapping ── */}
      {propTab === "mapping" && (
        <div style={{ padding: "2px 0" }}>

          {/* Clé de liaison (bindKey) de cet élément */}
          <Section title={elakayAutoText("h_d37499fa83eb00", "Liaison de données")} icon={Code2}>
            <StyleField label={elakayAutoText("h_2662e00ff375ca", "Clé de liaison (bindKey)")} value={element.bindKey} onChange={(v) => setProp("bindKey", v)} placeholder={elakayAutoText("h_bc3106994100d8", "ex. name, price, galeriesID.galeries.0.urlImg")} />
            <p style={{ fontSize: "11px", color: "#b3aacb", margin: "4px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_7dcc01d7e7f11c", "Si cet élément fait partie d'un gabarit répété, sa valeur sera remplie avec cette clé de chaque entrée du tableau. Laisser vide pour le remplissage automatique.")}</p>
          </Section>

          {/* Pagination (← / →) pour les éléments shopButtonPagination */}
          {def?.isShopButtonPagination && (
            <Section title={elakayAutoText("h_dab4355704781b", "Pagination (← / →)")} icon={ChevronsDownUp}>
              <p style={{ fontSize: "11px", color: "#9b93b8", margin: "0 0 10px", lineHeight: 1.5 }}>{elakayAutoText("h_c31b25e2abe5be", "Se synchronise automatiquement avec")}<code style={{ fontFamily:"ui-monospace,monospace" }}>storeState</code>{elakayAutoText("h_d2c24f2db72d91", "(skip · limit · size · siseProduct) et appelle")}<code style={{ fontFamily:"ui-monospace,monospace" }}>loadMore</code> / <code style={{ fontFamily:"ui-monospace,monospace" }}>loadMore0</code>.
              </p>
              <div style={{ marginBottom: "10px", padding: "8px 10px", borderRadius: "8px", background: "#f5f3ff", border: "1px solid #e4dcfb", fontSize: "11px", color: "#6d28d9" }}>
                <strong>{elakayAutoText("h_3ec988c1b0f8a6", "← Précédent")}</strong>{elakayAutoText("h_3afa71995d129d", ": actif quand skip > 0")}<br/>
                <strong>{elakayAutoText("h_ea96c11e6b8fef", "Suivant →")}</strong>{elakayAutoText("h_d1b3d42abebe1a", ": actif quand skip + limit < siseProduct")}</div>
              <span style={{ ...fieldLabel, display: "block", marginBottom: "6px" }}>{elakayAutoText("h_cf4237f82b20f9", "Classes CSS boutons")}</span>
              <StyleField label={elakayAutoText("h_b0fd07c454ebf9", "← Actif")} value={element.options?.buttonClassL} onChange={(v) => setOption("buttonClassL", v)} placeholder={elakayAutoText("h_99a2ea9e95a346", "btn btn-primary ... float-left")} />
              <StyleField label={elakayAutoText("h_37026e66bd0223", "← Inactif")} value={element.options?.buttonClassLD} onChange={(v) => setOption("buttonClassLD", v)} placeholder={elakayAutoText("h_6743f9ae06558e", "btn ... float-left")} />
              <StyleField label={elakayAutoText("h_36b667cfeda56e", "→ Actif")} value={element.options?.buttonClassR} onChange={(v) => setOption("buttonClassR", v)} placeholder={elakayAutoText("h_4e0fb8db436c9b", "btn btn-primary ... float-right")} />
              <StyleField label={elakayAutoText("h_8a2ba591d0a72f", "→ Inactif")} value={element.options?.buttonClassRD} onChange={(v) => setOption("buttonClassRD", v)} placeholder={elakayAutoText("h_c8c0d8e5020b25", "btn ... float-right")} />
            </Section>
          )}

          {/* Mapping & Pagination */}
          {(() => {
            const rb = element.repeatBind || null;
            const setRb = (patch) => setRepeatBind({ ...(rb || {}), ...patch });

            // Clés scalaires (feuilles) de data[0] pour "Activer le reste"
            const getLeafKeys = (obj, prefix, d) => {
              if (!obj || typeof obj !== "object" || d > 3) return [];
              const keys = [];
              for (const [k, v] of Object.entries(obj)) {
                const p = prefix ? `${prefix}.${k}` : k;
                if (Array.isArray(v)) {
                  if (v.length > 0 && v[0] && typeof v[0] === "object") keys.push(...getLeafKeys(v[0], `${p}.0`, d + 1));
                  else if (v.length > 0) keys.push(`${p}.0`);
                } else if (v && typeof v === "object") {
                  keys.push(...getLeafKeys(v, p, d + 1));
                } else {
                  keys.push(p);
                }
              }
              return keys;
            };

            // Mettre à jour n'importe quelle prop d'un descendant dans l'arbre
            const patchChildProps = (el, childId, propsObj) => {
              const patch = (children) =>
                (children || []).map(c => {
                  if (c.id === childId) {
                    const upd = { ...c, ...propsObj };
                    Object.keys(propsObj).forEach(k => { if (upd[k] === "" || upd[k] === undefined) delete upd[k]; });
                    return upd;
                  }
                  if (c.children) return { ...c, children: patch(c.children) };
                  return c;
                });
              return { ...el, children: patch(el.children || []) };
            };

            // Décompose un chemin dot-notation en chaîne de niveaux navigables dans obj
            const buildPathChain = (obj, pathStr) => {
              const segs = pathStr ? pathStr.split(".") : [];
              const chain = [];
              let curr = obj;
              for (let i = 0; ; i++) {
                if (curr === null || curr === undefined) break;
                if (Array.isArray(curr)) {
                  const seg = segs[i];
                  chain.push({ kind: "array", data: curr, selectedSeg: seg, segIdx: i });
                  const idx = seg !== undefined ? Number(seg) : NaN;
                  if (!isNaN(idx) && curr[idx] !== undefined) { curr = curr[idx]; }
                  else break;
                } else if (typeof curr === "object") {
                  const seg = segs[i];
                  chain.push({ kind: "object", data: curr, selectedSeg: seg, segIdx: i, keys: Object.keys(curr) });
                  if (seg !== undefined && seg !== "" && Object.prototype.hasOwnProperty.call(curr, seg)) { curr = curr[seg]; }
                  else break;
                } else {
                  chain.push({ kind: "done" });
                  break;
                }
              }
              return chain;
            };

            // Sélecteur de chemin navigable (chaîne de selects par niveau)
            const ChildPathBuilder = ({ rootObj, path, onChangePath }) => {
              const chain = buildPathChain(rootObj, path);
              const segs = path ? path.split(".") : [];
              const isDone = chain.length > 0 && chain[chain.length - 1].kind === "done";
              return (
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px", minHeight: "22px" }}>
                  {chain.map((level, i) => {
                    if (level.kind === "done") {
                      return <span key={i} style={{ fontSize: "9px", fontWeight: 700, color: "#16a34a", background: "#dcfce7", border: "1px solid #bbf7d0", padding: "1px 5px", borderRadius: "999px" }}>✓</span>;
                    }
                    const sel = { fontSize: "10px", padding: "1px 3px", border: "1px solid #e4dcfb", borderRadius: "5px", background: "#f3effe", outline: "none", height: "22px", cursor: "pointer" };
                    if (level.kind === "array") {
                      const maxIdx = Math.min(level.data.length - 1, 4);
                      return (
                        <React.Fragment key={i}>
                          {i > 0 && <span style={{ fontSize: "9px", color: "#c4b5fd" }}>·</span>}
                          <select value={level.selectedSeg ?? ""} onChange={(e) => onChangePath([...segs.slice(0, level.segIdx), e.target.value].join("."))}
                            style={{ ...sel, color: level.selectedSeg !== undefined && level.selectedSeg !== "" ? "#6d28d9" : "#9b93b8", minWidth: "38px" }}>
                            <option value="">[ ]</option>
                            {Array.from({ length: maxIdx + 1 }, (_, idx) => <option key={idx} value={String(idx)}>[{idx}]</option>)}
                            {level.data.length > 5 && <option value={String(level.data.length - 1)}>[{level.data.length - 1}]</option>}
                          </select>
                        </React.Fragment>
                      );
                    }
                    if (level.kind === "object") {
                      return (
                        <React.Fragment key={i}>
                          {i > 0 && <span style={{ fontSize: "9px", color: "#c4b5fd" }}>·</span>}
                          <select value={level.selectedSeg ?? ""} onChange={(e) => onChangePath([...segs.slice(0, level.segIdx), e.target.value].join("."))}
                            style={{ ...sel, color: level.selectedSeg ? "#6d28d9" : "#9b93b8", minWidth: "50px" }}>
                            <option value="">{elakayAutoText("h_e86019cad243f5", "— clé —")}</option>
                            {level.keys.map(k => {
                              const v = level.data[k];
                              const badge = Array.isArray(v) ? "[ ]" : v && typeof v === "object" ? "{ }" : "·";
                              return <option key={k} value={k}>{badge} {k}</option>;
                            })}
                          </select>
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}
                  {path && !isDone && (
                    <span style={{ fontSize: "9px", fontWeight: 700, color: "#6d28d9", background: "#ede9fe", border: "1px solid #c4b5fd", padding: "1px 5px", borderRadius: "999px" }}>{ }{elakayAutoText("h_9b5c0b859faba0", "obj")}</span>
                  )}
                  {path && (
                    <button onClick={() => onChangePath("")} style={{ fontSize: "9px", color: "#e11d48", background: "none", border: "none", cursor: "pointer", padding: "0 2px", lineHeight: 1, marginLeft: "1px" }} title={elakayAutoText("h_fe23de7b572d52", "Effacer")}>×</button>
                  )}
                </div>
              );
            };

            // Rendu récursif de l'arbre des enfants (données + actions)
            const renderChildTree = (children, rootObj, depth) =>
              (children || []).map(child => {
                const childDef = ELEMENT_TYPES[child.type];
                const lbl = child.text
                  ? (child.text.length > 22 ? child.text.slice(0, 22) + "…" : child.text)
                  : (childDef?.label || child.type);
                const chain = buildPathChain(rootObj, child.bindKey || "");
                const isDone = chain.length > 0 && chain[chain.length - 1].kind === "done";
                const isObjBound = !!(child.bindKey && !isDone);
                const hasAction = !!(child.clickActionType && child.clickActionType !== "none");
                const hasLink   = !!(child.linkHref || child.linkTarget);
                const actionOpen = openChildActions[child.id];
                const setChildProp = (propsObj) => onUpdate(patchChildProps(element, child.id, propsObj));
                const childActionFn = SHOPUSER_RUNTIME_FUNCTIONS_META.find(f => f.name === child.clickActionFunction);
                const childArgNames = parseArgNames(childActionFn?.signature || "");
                const childArgc = childActionFn?.argc ?? 0;
                return (
                  <React.Fragment key={child.id}>
                    <div style={{ paddingLeft: `${depth * 12}px`, marginTop: depth === 0 ? "7px" : "3px" }}>
                      {/* ── Header: icône + label + badges + bouton action ── */}
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "2px" }}>
                        {childDef?.icon && React.createElement(childDef.icon, { size: 10, color: "#9b93b8" })}
                        <span style={{ fontSize: "10px", color: isDone ? "#16a34a" : isObjBound ? "#6d28d9" : "#4a4a68", fontWeight: (isDone || isObjBound) ? 600 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={lbl}>{lbl}</span>
                        {hasAction && <span style={{ fontSize: "8px", color: "#d97706", background: "#fef9c3", padding: "1px 4px", borderRadius: "4px", border: "1px solid #fde68a", flexShrink: 0 }}>⚡</span>}
                        {hasLink   && <span style={{ fontSize: "8px", color: "#0369a1", background: "#e0f2fe", padding: "1px 4px", borderRadius: "4px", border: "1px solid #bae6fd", flexShrink: 0 }}>🌐</span>}
                        <button onClick={() => toggleChildAction(child.id)}
                          style={{ fontSize: "8px", padding: "1px 5px", borderRadius: "4px", border: `1px solid ${actionOpen ? "#6d28d9" : "#e4dcfb"}`, background: actionOpen ? "#6d28d9" : "#f3effe", color: actionOpen ? "#fff" : "#6d28d9", cursor: "pointer", flexShrink: 0, fontWeight: 700 }}>
                          {actionOpen ? "▲" : "⚡+"}
                        </button>
                      </div>

                      {/* ── Liaison donnée ── */}
                      <div style={{ paddingLeft: "14px", marginBottom: "2px" }}>
                        <ChildPathBuilder rootObj={rootObj} path={child.bindKey || ""} onChangePath={(p) => setChildProp({ bindKey: p || undefined })} />
                      </div>

                      {/* ── Section action (dépliable) ── */}
                      {actionOpen && (
                        <div style={{ paddingLeft: "14px", marginTop: "4px", padding: "8px", background: "#faf9fd", borderRadius: "8px", border: "1px solid #ece9f5" }}>
                          {/* Type d'action */}
                          <div style={{ marginBottom: "6px" }}>
                            <span style={{ ...fieldLabel, display: "block", marginBottom: "3px" }}>{elakayAutoText("h_b32cc8aaca3ce2", "Type d'action")}</span>
                            <select value={child.clickActionType || "none"} onChange={(e) => setChildProp({ clickActionType: e.target.value })}
                              style={{ ...selectStyle, fontSize: "11px", padding: "4px 6px" }}>
                              <option value="none">{elakayAutoText("h_e8f882731020de", "Aucune")}</option>
                              <option value="function">{elakayAutoText("h_4780483a7f34f0", "Fonction shopUser")}</option>
                              <option value="url">{elakayAutoText("h_c9ef19a1900484", "URL / href")}</option>
                              <option value="page">{elakayAutoText("h_ada824c94f49b9", "Page interne")}</option>
                            </select>
                          </div>

                          {/* Fonction */}
                          {child.clickActionType === "function" && (
                            <>
                              <div style={{ marginBottom: "6px" }}>
                                <span style={{ ...fieldLabel, display: "block", marginBottom: "3px" }}>{elakayAutoText("h_30153102ebaf93", "Fonction")}</span>
                                <select value={child.clickActionFunction || ""} onChange={(e) => setChildProp({ clickActionFunction: e.target.value })}
                                  style={{ ...selectStyle, fontSize: "11px", padding: "4px 6px" }}>
                                  <option value="">{elakayAutoText("h_718f6d24b6b218", "— choisir —")}</option>
                                  {SHOPUSER_RUNTIME_FUNCTIONS_META.map(fn => <option key={fn.name} value={fn.name}>{fn.signature} [{fn.argc}]</option>)}
                                </select>
                              </div>
                              {childArgc > 0 && Array.from({ length: childArgc }, (_, i) => {
                                const argKeys = ["clickActionArg1","clickActionArg2","clickActionArg3"];
                                return (
                                  <ArgInput key={argKeys[i]}
                                    label={childArgNames[i] || `Arg ${i+1}`}
                                    value={child[argKeys[i]] || ""}
                                    onChange={(v) => setChildProp({ [argKeys[i]]: v })}
                                    placeholder={elakayAutoText("h_17de463fe56c99", "items.categoryID, items._id…")}
                                  />
                                );
                              })}
                            </>
                          )}

                          {/* URL */}
                          {["url","href","link"].includes(child.clickActionType) && (
                            <>
                              <ArgInput label={elakayAutoText("h_c9ef19a1900484", "URL / href")} value={child.clickActionHref || ""} onChange={(v) => setChildProp({ clickActionHref: v })}
                                selectOptions={CLICK_ARG_URL_KEYS} placeholder="https://… ou items._id" />
                              <div style={{ marginBottom: "6px" }}>
                                <span style={{ ...fieldLabel, display: "block", marginBottom: "3px" }}>{elakayAutoText("h_f2e32418126d1f", "Ouverture")}</span>
                                <select value={child.clickActionTargetBlank ? "_blank" : "_self"} onChange={(e) => setChildProp({ clickActionTargetBlank: e.target.value === "_blank" })}
                                  style={{ ...selectStyle, fontSize: "11px", padding: "4px 6px" }}>
                                  <option value="_self">{elakayAutoText("h_c318cf09991f66", "Même onglet")}</option>
                                  <option value="_blank">{elakayAutoText("h_9e3971ab0881a8", "Nouvel onglet")}</option>
                                </select>
                              </div>
                            </>
                          )}

                          {/* Page interne */}
                          {child.clickActionType === "page" && (
                            <div style={{ marginBottom: "6px" }}>
                              <span style={{ ...fieldLabel, display: "block", marginBottom: "3px" }}>{elakayAutoText("h_ada824c94f49b9", "Page interne")}</span>
                              <select value={child.clickActionPage || ""} onChange={(e) => setChildProp({ clickActionPage: e.target.value })}
                                style={{ ...selectStyle, fontSize: "11px", padding: "4px 6px" }}>
                                <option value="">{elakayAutoText("h_6bbdf2ac7c4d7d", "— aucune —")}</option>
                                {(pages || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </div>
                          )}

                          {/* Lien fallback */}
                          <ArgInput label={elakayAutoText("h_136ffa809227af", "Lien fallback (linkHref)")} value={child.linkHref || ""} onChange={(v) => setChildProp({ linkHref: v })}
                            selectOptions={CLICK_ARG_URL_KEYS} placeholder="https://… (optionnel)" />

                          {/* Effacer l'action */}
                          {(hasAction || hasLink) && (
                            <button onClick={() => setChildProp({ clickActionType: undefined, clickActionFunction: undefined, clickActionArg1: undefined, clickActionArg2: undefined, clickActionArg3: undefined, clickActionHref: undefined, linkHref: undefined })}
                              style={{ ...ghostBtn, fontSize: "9px", padding: "3px 8px", color: "#e11d48", borderColor: "#f3b4c2", marginTop: "4px" }}>{elakayAutoText("h_0b81cb448ce8bb", "✕ Effacer l'action")}</button>
                          )}
                        </div>
                      )}
                    </div>
                    {child.children?.length > 0 && renderChildTree(child.children, rootObj, depth + 1)}
                  </React.Fragment>
                );
              });

            return (
              <Section title={elakayAutoText("h_8916c6ff2d6881", "Mapping & Pagination")} icon={LayoutGrid}>
                {!rb ? (
                  <>
                    <p style={{ fontSize: "11px", color: "#9b93b8", margin: "0 0 8px", lineHeight: 1.5 }}>{elakayAutoText("h_b33846d538f9b5", "Cet élément n'a pas de répétition active. Activez le mapping pour qu'il se duplique sur un tableau de données.")}</p>
                    <button onClick={() => setRepeatBind({ source: "", path: "", mode: "self", autoFill: true, paginate: false, pageSize: 12, fieldMap: {} })}
                      style={{ ...primaryBtn, width: "100%", justifyContent: "center" }}>{elakayAutoText("h_62da21bff5da0e", "Activer le mapping")}</button>
                    <p style={{ fontSize: "10px", color: "#b3aacb", margin: "8px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_ac71d1c6ba9f04", "Astuce : vous pouvez aussi cocher un tableau depuis l'onglet Données — la configuration s'affichera ici automatiquement.")}</p>
                  </>
                ) : (
                  <>
                    {/* Source */}
                    <label style={{ display: "block", marginBottom: "10px" }}>
                      <span style={fieldLabel}>{elakayAutoText("h_c47b2fd69728ed", "Source de données")}</span>
                      <select value={rb.source || ""} onChange={(e) => {
                        const k = e.target.value;
                        setRb({ source: k, path: "" });
                        if (k && datasets[k]) setBoundData?.({ key: k, ...datasets[k] });
                      }} style={selectStyle}>
                        <option value="">{elakayAutoText("h_3e5e6213d2bede", "— sélectionner —")}</option>
                        {Object.entries(datasets)
                          .filter(([, d]) => !doc?.pageMode || doc.pageMode === "static" || d.pageMode === doc.pageMode)
                          .map(([k, d]) => <option key={k} value={k}>{d.isLive ? `● ${d.label}` : d.label}</option>)}
                        {rb.source && !datasets[rb.source] && <option value={rb.source}>{rb.source}{elakayAutoText("h_d0bfca122a20f3", "(personnalisée)")}</option>}
                      </select>
                      {doc?.pageMode && doc.pageMode !== "static" && (
                        <p style={{ fontSize: "10px", color: "#9b93b8", margin: "4px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_e6215707cb7cac", "Sources filtrées par le mode du doc :")}<strong style={{ color: "#6d28d9" }}>{doc.pageMode}</strong>
                        </p>
                      )}
                    </label>

                    {/* Chemin du tableau */}
                    {(() => {
                      const data = (rb.source && datasets[rb.source]?.data) || boundData?.data || null;
                      if (!data) return <p style={{ fontSize: "11px", color: "#e0a800", margin: "0 0 10px" }}>{elakayAutoText("h_60d55f4f5bac46", "Sélectionnez d'abord une source pour voir les chemins de tableaux disponibles.")}</p>;
                      const arrayPaths = [];
                      const walk = (obj, prefix, depth) => {
                        if (depth > 4 || obj == null || typeof obj !== "object") return;
                        for (const [k, v] of Object.entries(obj)) {
                          const p = prefix ? `${prefix}.${k}` : k;
                          if (Array.isArray(v)) arrayPaths.push({ path: p, length: v.length });
                          else if (v && typeof v === "object") walk(v, p, depth + 1);
                        }
                      };
                      walk(data, "", 0);
                      return (
                        <label style={{ display: "block", marginBottom: "10px" }}>
                          <span style={fieldLabel}>{elakayAutoText("h_f0988e9a386812", "Chemin du tableau (à paginer/mapper)")}</span>
                          <select value={rb.path || ""} onChange={(e) => setRb({ path: e.target.value })} style={selectStyle}>
                            <option value="">{elakayAutoText("h_bc8d2b6301fff1", "— sélectionner un tableau —")}</option>
                            {arrayPaths.map((ap) => (
                              <option key={ap.path} value={ap.path}>{ap.path} [{ap.length}{elakayAutoText("h_d7fa79c76130e0", "entrées]")}</option>
                            ))}
                            {rb.path && !arrayPaths.find((ap) => ap.path === rb.path) && <option value={rb.path}>{rb.path}{elakayAutoText("h_32e1402a7e25f9", "(personnalisé)")}</option>}
                          </select>
                        </label>
                      );
                    })()}

                    {/* Aperçu */}
                    {rb.source && rb.path && (() => {
                      const data = (datasets[rb.source]?.data) || boundData?.data || null;
                      const arr = data ? resolvePath(data, rb.path) : null;
                      if (!Array.isArray(arr)) return <p style={{ fontSize: "11px", color: "#e0a800", margin: "0 0 10px" }}>{elakayAutoText("h_dce5f9c4aeae34", "Le chemin sélectionné ne pointe pas vers un tableau.")}</p>;
                      const first = arr[0];
                      const keys = first && typeof first === "object" ? Object.keys(first) : [];
                      return (
                        <div style={{ marginBottom: "12px", padding: "10px", background: "#faf9fd", border: "1px solid #ece9f5", borderRadius: "10px" }}>
                          <div style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 700, marginBottom: "6px" }}>{elakayAutoText("h_33c24358d4d9ef", "Aperçu :")}{arr.length}{elakayAutoText("h_90ad87338d53e5", "entrée")}{arr.length > 1 ? "s" : ""} {rb.paginate ? `(${Math.min(arr.length, rb.pageSize || 12)} affichées par page)` : elakayAutoText("h_295a748715b279", "(toutes affichées)")}
                          </div>
                          {first && typeof first === "object" ? (
                            <>
                              <div style={{ fontSize: "10px", color: "#9b93b8", marginBottom: "4px" }}>{elakayAutoText("h_7b4f0800441972", "Schéma d'une entrée :")}</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "6px" }}>
                                {keys.slice(0, 12).map((k) => (
                                  <span key={k} style={{ fontSize: "10px", fontFamily: "ui-monospace, monospace", padding: "2px 6px", background: "#fff", border: "1px solid #e4dcfb", color: "#6d28d9", borderRadius: "5px" }}>{k}</span>
                                ))}
                                {keys.length > 12 && <span style={{ fontSize: "10px", color: "#9b93b8" }}>+{keys.length - 12}</span>}
                              </div>
                              <details style={{ fontSize: "10px", color: "#7a73a0" }}>
                                <summary style={{ cursor: "pointer", color: "#6d28d9", marginBottom: "4px" }}>{elakayAutoText("h_54f15a88883681", "Voir le 1er objet (JSON)")}</summary>
                                <pre style={{ margin: 0, padding: "6px", background: "#1a1a2e", color: "#d6cfec", borderRadius: "6px", maxHeight: "120px", overflow: "auto", fontSize: "10px", lineHeight: 1.4 }}>{JSON.stringify(first, null, 2).slice(0, 800)}</pre>
                              </details>
                            </>
                          ) : (
                            <div style={{ fontSize: "11px", color: "#7a73a0", fontFamily: "ui-monospace, monospace" }}>{elakayAutoText("h_0cb62a9eedeff4", "Valeurs :")}{arr.slice(0, 5).map(String).join(", ")}{arr.length > 5 ? "…" : ""}</div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Mode gabarit */}
                    <label style={{ display: "block", marginBottom: "10px" }}>
                      <span style={fieldLabel}>{elakayAutoText("h_c00d0e9496f239", "Mode du gabarit")}</span>
                      <select value={rb.mode || "self"} onChange={(e) => setRb({ mode: e.target.value })} style={selectStyle}>
                        <option value="self">{elakayAutoText("h_eef3e2b8142e80", "L'élément est le gabarit (dupliqué)")}</option>
                        <option value="children">{elakayAutoText("h_dbefb20ea75fdf", "Les enfants sont les gabarits (l'élément reste conteneur)")}</option>
                      </select>
                    </label>

                    <CheckField label={elakayAutoText("h_43920149f81216", "Remplissage auto (deviner les clés)")} checked={rb.autoFill !== false} onChange={(v) => setRb({ autoFill: v })} />
                    <CheckField label={elakayAutoText("h_79bc37b58de41a", "Avec pagination (nombre d'éléments par page)")} checked={!!rb.paginate} onChange={(v) => setRb({ paginate: v })} />
                    {rb.paginate && (
                      <label style={{ display: "block", margin: "4px 0 10px" }}>
                        <span style={fieldLabel}>{elakayAutoText("h_43ea813aaed3f0", "Nombre d'objets par page")}</span>
                        <input type="number" min={1} value={rb.pageSize || 12}
                          onChange={(e) => setRb({ pageSize: Math.max(1, Number(e.target.value) || 12) })}
                          style={{ ...selectStyle, width: "100%" }} />
                        <p style={{ fontSize: "10px", color: "#9b93b8", margin: "4px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_35a2a33bd1448c", "Le système applique automatiquement le mapping aux entrées suivantes en respectant cette taille.")}</p>
                      </label>
                    )}

                    {/* fieldMap */}
                    <div style={{ marginTop: "8px" }}>
                      <span style={fieldLabel}>{elakayAutoText("h_3b60a21b5f28f0", "Correspondances clé → élément (fieldMap)")}</span>
                      <p style={{ fontSize: "10px", color: "#9b93b8", margin: "2px 0 6px", lineHeight: 1.4 }}>{elakayAutoText("h_f59897355af2c1", "Forçage manuel des mappings (priorité sur l'auto). Format : nom de la clé de donnée → id de l'élément du gabarit.")}</p>
                      {Object.entries(rb.fieldMap || {}).map(([k, v]) => (
                        <div key={k} style={{ display: "flex", gap: "4px", alignItems: "center", marginBottom: "5px" }}>
                          <input value={k} readOnly style={{ flex: 1, padding: "5px 8px", fontSize: "11px", fontFamily: "ui-monospace, monospace", color: "#6d28d9", background: "#f3effe", border: "1px solid #e4dcfb", borderRadius: "6px" }} />
                          <span style={{ color: "#9b93b8", fontSize: "11px" }}>→</span>
                          <input value={v} onChange={(e) => { const fm = { ...(rb.fieldMap || {}) }; fm[k] = e.target.value; setRb({ fieldMap: fm }); }}
                            placeholder={elakayAutoText("h_41577e0916e1cc", "id élément")}
                            style={{ flex: 1, padding: "5px 8px", fontSize: "11px", fontFamily: "ui-monospace, monospace", border: "1px solid #d9d3ec", borderRadius: "6px" }} />
                          <button onClick={() => { const fm = { ...(rb.fieldMap || {}) }; delete fm[k]; setRb({ fieldMap: fm }); }}
                            style={{ ...miniBtn, padding: "4px 6px", color: "#e11d48" }} title={elakayAutoText("h_1acfc1c7d76131", "Supprimer")}>×</button>
                        </div>
                      ))}
                      <button onClick={() => { const fm = { ...(rb.fieldMap || {}) }; let n = 1, k = "key"; while (fm[k]) k = `key${++n}`; fm[k] = ""; setRb({ fieldMap: fm }); }}
                        style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "4px", fontSize: "12px", padding: "6px 10px" }}>{elakayAutoText("h_55cbb9ec73deeb", "+ Ajouter une correspondance")}</button>
                    </div>

                    {/* ── Liaison des éléments enfants (sélection par clé sur objet[0]) ── */}
                    {rb.source && rb.path && element.children?.length > 0 && (() => {
                      const data = (datasets[rb.source]?.data) || boundData?.data || null;
                      const arr = data ? resolvePath(data, rb.path) : null;
                      if (!Array.isArray(arr) || arr.length === 0) return null;
                      const first = arr[0];
                      if (!first || typeof first !== "object") return null;
                      const leafKeys = getLeafKeys(first, "", 0);

                      const activateRest = () => {
                        const usedKeys = new Set();
                        const collectUsed = (children) => {
                          for (const c of children || []) {
                            if (c.bindKey) usedKeys.add(c.bindKey);
                            collectUsed(c.children);
                          }
                        };
                        collectUsed(element.children);
                        const available = leafKeys.filter(k => !usedKeys.has(k));
                        let idx = 0;
                        const autoAssign = (children) =>
                          (children || []).map(c => {
                            let nc = c;
                            if (!nc.bindKey && idx < available.length) nc = { ...nc, bindKey: available[idx++] };
                            if (nc.children) nc = { ...nc, children: autoAssign(nc.children) };
                            return nc;
                          });
                        onUpdate({ ...element, children: autoAssign(element.children) });
                      };

                      return (
                        <div style={{ marginTop: "14px", borderTop: "1px solid #f0edf8", paddingTop: "12px" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                            <span style={{ ...fieldLabel }}>{elakayAutoText("h_1bedecef3e8900", "Liaison éléments enfants")}</span>
                            <button onClick={activateRest} style={{ ...ghostBtn, fontSize: "10px", padding: "4px 9px", borderRadius: "7px" }}>{elakayAutoText("h_60a355022a4d31", "Activer le reste")}</button>
                          </div>
                          <p style={{ fontSize: "10px", color: "#9b93b8", margin: "0 0 8px", lineHeight: 1.4 }}>{elakayAutoText("h_f509ea29c7f9f7", "Reliez chaque élément enfant à une clé de")}<code style={{ fontFamily:"ui-monospace,monospace" }}>objet[0]</code>{elakayAutoText("h_4bbaa7720c7239", ", sur plusieurs niveaux.")}</p>
                          <div style={{ maxHeight: "320px", overflowY: "auto", padding: "8px", background: "#faf9fd", border: "1px solid #ece9f5", borderRadius: "8px" }}>
                            {renderChildTree(element.children, first, 0)}
                          </div>
                        </div>
                      );
                    })()}

                    <button onClick={() => setRepeatBind(null)}
                      style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "12px", color: "#e11d48", borderColor: "#f3b4c2" }}>{elakayAutoText("h_3f9055340080c4", "Supprimer la répétition")}</button>
                  </>
                )}
              </Section>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panneau mapping du body de la page (sélectionné via le bouton "Body" du canvas)
// ---------------------------------------------------------------------------
function PageBodyMappingPanel({ page, setPageProp, boundData, setBoundData, doc, datasets = SAMPLE_DATASETS, onDeselect }) {
  const rb = page.repeatBind || null;
  const setRb = (patch) => setPageProp({ repeatBind: patch ? { ...(rb || {}), ...patch } : null });
  const setProp = (k, v) => setPageProp({ [k]: v });

  // ── helpers réutilisés depuis PropertiesPanel ──
  const getLeafKeys = (obj, prefix, d) => {
    if (!obj || typeof obj !== "object" || d > 3) return [];
    const keys = [];
    for (const [k, v] of Object.entries(obj)) {
      const p = prefix ? `${prefix}.${k}` : k;
      if (Array.isArray(v)) {
        if (v.length > 0 && v[0] && typeof v[0] === "object") keys.push(...getLeafKeys(v[0], `${p}.0`, d + 1));
        else if (v.length > 0) keys.push(`${p}.0`);
      } else if (v && typeof v === "object") {
        keys.push(...getLeafKeys(v, p, d + 1));
      } else keys.push(p);
    }
    return keys;
  };
  const patchChildBindKey = (children, childId, newKey) => {
    const patch = (arr) => (arr || []).map(c => {
      if (c.id === childId) return { ...c, bindKey: newKey || undefined };
      if (c.children) return { ...c, children: patch(c.children) };
      return c;
    });
    return patch(children);
  };
  const buildPathChain = (obj, pathStr) => {
    const segs = pathStr ? pathStr.split(".") : [];
    const chain = [];
    let curr = obj;
    for (let i = 0; ; i++) {
      if (curr === null || curr === undefined) break;
      if (Array.isArray(curr)) {
        const seg = segs[i];
        chain.push({ kind: "array", data: curr, selectedSeg: seg, segIdx: i });
        const idx = seg !== undefined ? Number(seg) : NaN;
        if (!isNaN(idx) && curr[idx] !== undefined) curr = curr[idx]; else break;
      } else if (typeof curr === "object") {
        const seg = segs[i];
        chain.push({ kind: "object", data: curr, selectedSeg: seg, segIdx: i, keys: Object.keys(curr) });
        if (seg !== undefined && seg !== "" && Object.prototype.hasOwnProperty.call(curr, seg)) curr = curr[seg]; else break;
      } else {
        chain.push({ kind: "done" });
        break;
      }
    }
    return chain;
  };
  const ChildPathBuilder = ({ rootObj, path, onChangePath }) => {
    const chain = buildPathChain(rootObj, path);
    const segs = path ? path.split(".") : [];
    const isDone = chain.length > 0 && chain[chain.length - 1].kind === "done";
    const isObjBound = path && !isDone;
    const sel = { fontSize: "10px", padding: "1px 3px", border: "1px solid #e4dcfb", borderRadius: "5px", background: "#f3effe", outline: "none", height: "22px", cursor: "pointer" };
    return (
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px", minHeight: "22px" }}>
        {chain.map((level, i) => {
          if (level.kind === "done") return <span key={i} style={{ fontSize: "9px", fontWeight: 700, color: "#16a34a", background: "#dcfce7", border: "1px solid #bbf7d0", padding: "1px 5px", borderRadius: "999px" }}>✓</span>;
          if (level.kind === "array") {
            const maxIdx = Math.min(level.data.length - 1, 4);
            return (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ fontSize: "9px", color: "#c4b5fd" }}>·</span>}
                <select value={level.selectedSeg ?? ""} onChange={(e) => onChangePath([...segs.slice(0, level.segIdx), e.target.value].join("."))}
                  style={{ ...sel, color: level.selectedSeg !== undefined && level.selectedSeg !== "" ? "#6d28d9" : "#9b93b8", minWidth: "38px" }}>
                  <option value="">[ ]</option>
                  {Array.from({ length: maxIdx + 1 }, (_, idx) => <option key={idx} value={String(idx)}>[{idx}]</option>)}
                  {level.data.length > 5 && <option value={String(level.data.length - 1)}>[{level.data.length - 1}]</option>}
                </select>
              </React.Fragment>
            );
          }
          if (level.kind === "object") {
            return (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ fontSize: "9px", color: "#c4b5fd" }}>·</span>}
                <select value={level.selectedSeg ?? ""} onChange={(e) => onChangePath([...segs.slice(0, level.segIdx), e.target.value].join("."))}
                  style={{ ...sel, color: level.selectedSeg ? "#6d28d9" : "#9b93b8", minWidth: "50px" }}>
                  <option value="">{elakayAutoText("h_e86019cad243f5", "— clé —")}</option>
                  {level.keys.map(k => { const v = level.data[k]; const badge = Array.isArray(v) ? "[ ]" : v && typeof v === "object" ? "{ }" : "·"; return <option key={k} value={k}>{badge} {k}</option>; })}
                </select>
              </React.Fragment>
            );
          }
          return null;
        })}
        {isObjBound && <span style={{ fontSize: "9px", fontWeight: 700, color: "#6d28d9", background: "#ede9fe", border: "1px solid #c4b5fd", padding: "1px 5px", borderRadius: "999px" }}>{ }{elakayAutoText("h_9b5c0b859faba0", "obj")}</span>}
        {path && <button onClick={() => onChangePath("")} style={{ fontSize: "9px", color: "#e11d48", background: "none", border: "none", cursor: "pointer", padding: "0 2px", marginLeft: "1px" }} title={elakayAutoText("h_fe23de7b572d52", "Effacer")}>×</button>}
      </div>
    );
  };
  const renderChildTree = (children, rootObj, depth) =>
    (children || []).map(child => {
      const childDef = ELEMENT_TYPES[child.type];
      const lbl = child.text ? (child.text.length > 22 ? child.text.slice(0, 22) + "…" : child.text) : (childDef?.label || child.type);
      const chain = buildPathChain(rootObj, child.bindKey || "");
      const isDone = chain.length > 0 && chain[chain.length - 1].kind === "done";
      const isObjBound = (child.bindKey && !isDone);
      return (
        <React.Fragment key={child.id}>
          <div style={{ paddingLeft: `${depth * 12}px`, marginTop: depth === 0 ? "7px" : "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "2px" }}>
              {childDef?.icon && React.createElement(childDef.icon, { size: 10, color: "#9b93b8" })}
              <span style={{ fontSize: "10px", color: isDone ? "#16a34a" : isObjBound ? "#6d28d9" : "#4a4a68", fontWeight: (isDone || isObjBound) ? 600 : 400, maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lbl}</span>
            </div>
            <div style={{ paddingLeft: "14px" }}>
              <ChildPathBuilder rootObj={rootObj} path={child.bindKey || ""} onChangePath={(p) => setPageProp({ children: patchChildBindKey(page.children, child.id, p) })} />
            </div>
          </div>
          {child.children?.length > 0 && renderChildTree(child.children, rootObj, depth + 1)}
        </React.Fragment>
      );
    });

  return (
    <div style={{ padding: "18px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 700, color: "#1a1a2e" }}>
          <LayoutGrid size={16} color="#6d28d9" /> {page.name || elakayAutoText("h_718a7e8a582e98", "Body")}
        </span>
        <button onClick={onDeselect} style={{ ...miniBtn, color: "#9b93b8" }} title={elakayAutoText("h_bdc31a7262fa08", "Désélectionner")}><X size={14}/></button>
      </div>
      <div style={{ padding: "6px 10px", marginBottom: "14px", borderRadius: "8px", background: "#f3effe", border: "1px solid #e4dcfb", fontSize: "11px", color: "#6d28d9", fontWeight: 600 }}>{elakayAutoText("h_9ab96187f41f3e", "Body de la page — Mapping & Liaison")}</div>

      {/* bindKey page */}
      <Section title={elakayAutoText("h_1335d848223f90", "Liaison de données (page)")} icon={Code2}>
        <StyleField label={elakayAutoText("h_2662e00ff375ca", "Clé de liaison (bindKey)")} value={page.bindKey} onChange={(v) => setProp("bindKey", v)} placeholder={elakayAutoText("h_92dad1a8037480", "ex. productId, slug…")} />
      </Section>

      {/* Mapping & Pagination */}
      <Section title={elakayAutoText("h_8916c6ff2d6881", "Mapping & Pagination")} icon={LayoutGrid}>
        {!rb ? (
          <>
            <p style={{ fontSize: "11px", color: "#9b93b8", margin: "0 0 8px", lineHeight: 1.5 }}>{elakayAutoText("h_de7a493a2c3120", "Le body n'a pas de répétition active.")}</p>
            <button onClick={() => setRb({ source: "", path: "", mode: "children", autoFill: true, paginate: false, pageSize: 12, fieldMap: {} })}
              style={{ ...primaryBtn, width: "100%", justifyContent: "center" }}>{elakayAutoText("h_62da21bff5da0e", "Activer le mapping")}</button>
          </>
        ) : (
          <>
            <label style={{ display: "block", marginBottom: "10px" }}>
              <span style={fieldLabel}>{elakayAutoText("h_c47b2fd69728ed", "Source de données")}</span>
              <select value={rb.source || ""} onChange={(e) => { const k = e.target.value; setRb({ source: k, path: "" }); if (k && datasets[k]) setBoundData?.({ key: k, ...datasets[k] }); }} style={selectStyle}>
                <option value="">{elakayAutoText("h_3e5e6213d2bede", "— sélectionner —")}</option>
                {Object.entries(datasets).filter(([, d]) => !doc?.pageMode || doc.pageMode === "static" || d.pageMode === doc.pageMode)
                  .map(([k, d]) => <option key={k} value={k}>{d.isLive ? `● ${d.label}` : d.label}</option>)}
                {rb.source && !datasets[rb.source] && <option value={rb.source}>{rb.source}{elakayAutoText("h_d0bfca122a20f3", "(personnalisée)")}</option>}
              </select>
            </label>
            {rb.source && (() => {
              const data = (datasets[rb.source]?.data) || boundData?.data || null;
              if (!data) return <p style={{ fontSize: "11px", color: "#e0a800", margin: "0 0 10px" }}>{elakayAutoText("h_489387f166f076", "Sélectionnez d'abord une source.")}</p>;
              const arrayPaths = [];
              const walk = (obj, pfx, d) => { if (d > 4 || !obj || typeof obj !== "object") return; for (const [k, v] of Object.entries(obj)) { const p = pfx ? `${pfx}.${k}` : k; if (Array.isArray(v)) arrayPaths.push({ path: p, length: v.length }); else if (v && typeof v === "object") walk(v, p, d + 1); } };
              walk(data, "", 0);
              return (
                <label style={{ display: "block", marginBottom: "10px" }}>
                  <span style={fieldLabel}>{elakayAutoText("h_48ebb30865346b", "Chemin du tableau")}</span>
                  <select value={rb.path || ""} onChange={(e) => setRb({ path: e.target.value })} style={selectStyle}>
                    <option value="">{elakayAutoText("h_bc8d2b6301fff1", "— sélectionner un tableau —")}</option>
                    {arrayPaths.map(ap => <option key={ap.path} value={ap.path}>{ap.path} [{ap.length}]</option>)}
                    {rb.path && !arrayPaths.find(ap => ap.path === rb.path) && <option value={rb.path}>{rb.path}{elakayAutoText("h_32e1402a7e25f9", "(personnalisé)")}</option>}
                  </select>
                </label>
              );
            })()}
            <CheckField label={elakayAutoText("h_d037d239ba3a26", "Remplissage auto")} checked={rb.autoFill !== false} onChange={(v) => setRb({ autoFill: v })} />
            <CheckField label={elakayAutoText("h_f0037512cff83a", "Avec pagination")} checked={!!rb.paginate} onChange={(v) => setRb({ paginate: v })} />
            {rb.paginate && (
              <label style={{ display: "block", margin: "4px 0 10px" }}>
                <span style={fieldLabel}>{elakayAutoText("h_f06b03333e42ae", "Objets par page")}</span>
                <input type="number" min={1} value={rb.pageSize || 12} onChange={(e) => setRb({ pageSize: Math.max(1, Number(e.target.value) || 12) })} style={{ ...selectStyle, width: "100%" }} />
              </label>
            )}

            {/* Liaison des enfants */}
            {rb.source && rb.path && page.children?.length > 0 && (() => {
              const data = (datasets[rb.source]?.data) || boundData?.data || null;
              const arr = data ? resolvePath(data, rb.path) : null;
              if (!Array.isArray(arr) || !arr[0] || typeof arr[0] !== "object") return null;
              const first = arr[0];
              const leafKeys = getLeafKeys(first, "", 0);
              const activateRest = () => {
                const used = new Set();
                const collect = (ch) => { for (const c of ch || []) { if (c.bindKey) used.add(c.bindKey); collect(c.children); } };
                collect(page.children);
                const avail = leafKeys.filter(k => !used.has(k));
                let idx = 0;
                const assign = (ch) => (ch || []).map(c => { let nc = c; if (!nc.bindKey && idx < avail.length) nc = { ...nc, bindKey: avail[idx++] }; if (nc.children) nc = { ...nc, children: assign(nc.children) }; return nc; });
                setPageProp({ children: assign(page.children) });
              };
              return (
                <div style={{ marginTop: "14px", borderTop: "1px solid #f0edf8", paddingTop: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                    <span style={fieldLabel}>{elakayAutoText("h_1bedecef3e8900", "Liaison éléments enfants")}</span>
                    <button onClick={activateRest} style={{ ...ghostBtn, fontSize: "10px", padding: "4px 9px", borderRadius: "7px" }}>{elakayAutoText("h_60a355022a4d31", "Activer le reste")}</button>
                  </div>
                  <div style={{ maxHeight: "320px", overflowY: "auto", padding: "8px", background: "#faf9fd", border: "1px solid #ece9f5", borderRadius: "8px" }}>
                    {renderChildTree(page.children, first, 0)}
                  </div>
                </div>
              );
            })()}

            <button onClick={() => setRb(null)} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "12px", color: "#e11d48", borderColor: "#f3b4c2" }}>{elakayAutoText("h_3f9055340080c4", "Supprimer la répétition")}</button>
          </>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panneau "Setting Live Boutique" — onglet dédié aux éléments shopLive/shopHeader
// ---------------------------------------------------------------------------
function ShopLiveSettingPanel({ element, onUpdate, page }) {
  const [openVars, setOpenVars] = React.useState(false);
  const [openFns,  setOpenFns]  = React.useState(false);
  const [openSum,  setOpenSum]  = React.useState(false);

  const def    = element ? ELEMENT_TYPES[element.type] : null;
  const schema = element ? SHOP_LIVE_SCHEMA[element.type] : null;
  const isShopEl = def?.group === "shopLive" || def?.group === "shopHeader";
  const hasProductMapping = !!(schema?.arraySource);

  const setOption = (k, v) => onUpdate({ ...element, options: { ...(element.options || {}), [k]: v } });
  const setSlot   = (k, v) => setOption("liveSlots", { ...(element.options?.liveSlots || {}), [k]: v });
  const slots  = element?.options?.liveSlots || {};
  const arrSrc = element?.options?.liveArraySource || schema?.arraySource || "store.products";
  const prodIdx = element?.options?.liveProductIndex ?? 0;

  // Trouve le parent dans l'arbre de pages (pour setup repeat+pagination)
  const parent = (page && element) ? findParentInTree(page.children, element.id) : null;
  const parentDef = parent ? ELEMENT_TYPES[parent.type] : null;
  const parentIsContainer = !!(parentDef?.isContainer);
  const parentHasRepeat = !!(parent?.repeatBind);

  // ─ Calcule tous les slots en fonction du tableau source + index i ─
  const buildSlots = (arr, i) => {
    const base = `${arr}.${i}`;
    const defaultBase = `${schema?.arraySource || "store.products"}.0`;
    const ns = {};
    (schema?.params || []).forEach(p => { ns[p.key] = p.default.replace(defaultBase, base); });
    return ns;
  };

  const applyMapping = (arr, i) => {
    const newSlots = buildSlots(arr, i);
    const newBindSource = schema?.sources?.[0]?.includes(".0")
      ? schema.sources[0].replace(/\.0(\.|$)/, `.${i}$1`)
      : element.bindSource || schema?.sources?.[0] || "";
    onUpdate({ ...element, bindSource: newBindSource, options: { ...(element.options || {}), liveSlots: newSlots, liveArraySource: arr, liveProductIndex: i } });
  };

  // ─ Active repeat + pagination sur le parent ─
  const setupParentRepeatPaginate = (pageSize) => {
    if (!parent) return;
    const usePaginate = element.options?.livePaginate !== false;
    const updatedParent = {
      ...parent,
      repeatBind: {
        source: "store",
        path: arrSrc.replace(/^store\./, ""),
        mode: "children",
        autoFill: true,
        paginate: usePaginate,
        pageSize: pageSize || 12,
        fieldMap: {},
      },
    };
    onUpdate(updatedParent);
  };

  // ─ Statut de configuration ─
  const isConfigured = hasProductMapping
    ? (Object.keys(slots).length >= (schema?.params?.length || 1))
    : !!(element?.bindSource);

  if (!element || !isShopEl) {
    return (
      <div style={{ padding: "22px", textAlign: "center", color: "#9b93b8" }}>
        <Package size={28} style={{ marginBottom: "10px", opacity: 0.4 }} />
        <p style={{ fontSize: "13px", lineHeight: 1.6 }}>{elakayAutoText("h_11d7984800a888", "Sélectionnez un élément")}<strong style={{ color: "#6d28d9" }}>{elakayAutoText("h_f322b814019146", "Boutique Live")}</strong>{elakayAutoText("h_c80d2733de6618", "dans le canvas pour configurer ses données.")}</p>
      </div>
    );
  }

  const fnMeta = SHOPUSER_RUNTIME_FUNCTIONS_META.filter(f => schema?.fns?.includes(f.name));

  return (
    <div style={{ padding: "14px" }}>

      {/* ════ Header ════ */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        {def.icon && React.createElement(def.icon, { size: 15, color: "#6d28d9" })}
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a2e" }}>{def.label}</span>
        <span style={{ marginLeft: "auto", fontSize: "9px", padding: "2px 8px", borderRadius: "999px", background: isConfigured ? "linear-gradient(135deg,#059669,#34d399)" : "linear-gradient(135deg,#d97706,#fbbf24)", color: "#fff", fontWeight: 700 }}>
          {isConfigured ? elakayAutoText("h_04fa02124b6269", "✓ Configuré") : elakayAutoText("h_cd4909a31b633c", "⚠ Non configuré")}
        </span>
      </div>

      {/* ════ SETUP RAPIDE (toujours en tête, bien visible) ════ */}
      {hasProductMapping && (
        <div style={{ marginBottom: "16px", padding: "14px", background: isConfigured ? "#f0fdf4" : "linear-gradient(135deg,#fefce8,#fffbeb)", border: `2px solid ${isConfigured ? "#86efac" : "#fcd34d"}`, borderRadius: "12px" }}>
          <div style={{ fontSize: "12px", fontWeight: 700, color: isConfigured ? "#15803d" : "#92400e", marginBottom: "10px" }}>
            {isConfigured ? elakayAutoText("h_ee992ad3f1828e", "✓ Mapping configuré") : elakayAutoText("h_444831b1947aa7", "⚡ Setup rapide — Mapping & Pagination")}
          </div>

          {/* Étape 1 — Tableau source */}
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "10px", fontWeight: 700, color: "#6d28d9", display: "block", marginBottom: "3px" }}>{elakayAutoText("h_a9cd70ec12708a", "① Tableau de données")}</span>
            <select
              value={arrSrc}
              onChange={(e) => setOption("liveArraySource", e.target.value)}
              style={{ ...selectStyle, fontSize: "11px", padding: "5px 8px" }}
            >
              <option value="store.products">{elakayAutoText("h_05cc74817266e0", "store.products — Produits boutique")}</option>
              <option value="store.categories.categories">{elakayAutoText("h_b8394495cbba59", "store.categories.categories — Catégories")}</option>
              <option value="store.pubCategoryProdMatrix.products">{elakayAutoText("h_75ff2cd5580304", "store.pubCategoryProdMatrix — Vedettes")}</option>
            </select>
          </div>

          {/* Étape 2 — Index i + Max par page */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: "10px", fontWeight: 700, color: "#6d28d9", display: "block", marginBottom: "3px" }}>{elakayAutoText("h_30ca124a5a551e", "② Index")}<code style={{ fontFamily:"ui-monospace,monospace" }}>i</code>
                <span style={{ color: "#9b93b8", fontWeight: 400, marginLeft: "4px" }}>({arrSrc}[{prodIdx}])</span>
              </span>
              <input
                type="number" min={0}
                value={prodIdx}
                onChange={(e) => setOption("liveProductIndex", Math.max(0, Number(e.target.value) || 0))}
                style={{ ...selectStyle, width: "100%", fontSize: "14px", padding: "5px 8px", fontFamily: "ui-monospace,monospace", color: "#6d28d9", fontWeight: 800 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: "10px", fontWeight: 700, color: "#6d28d9", display: "block", marginBottom: "3px" }}>{elakayAutoText("h_215a2d5aa0f122", "③ Max par page")}</span>
              <input
                type="number" min={1} max={100}
                value={element.options?.livePageSize ?? 12}
                onChange={(e) => setOption("livePageSize", Math.max(1, Number(e.target.value) || 12))}
                style={{ ...selectStyle, width: "100%", fontSize: "14px", padding: "5px 8px", fontFamily: "ui-monospace,monospace", color: "#6d28d9", fontWeight: 800 }}
              />
            </div>
          </div>

          {/* Pagination toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", padding: "8px 10px", background: element.options?.livePaginate ? "#f3effe" : "#faf9fd", borderRadius: "8px", border: `1px solid ${element.options?.livePaginate ? "#e4dcfb" : "#ece9f5"}` }}>
            <input
              type="checkbox"
              id="live-paginate"
              checked={!!element.options?.livePaginate}
              onChange={(e) => setOption("livePaginate", e.target.checked)}
              style={{ accentColor: "#6d28d9", width: "15px", height: "15px", cursor: "pointer" }}
            />
            <label htmlFor="live-paginate" style={{ fontSize: "11px", fontWeight: 600, color: element.options?.livePaginate ? "#6d28d9" : "#4a4a68", cursor: "pointer", userSelect: "none" }}>{elakayAutoText("h_d2ebf0b7def8df", "Activer la pagination (")}{element.options?.livePageSize ?? 12}{elakayAutoText("h_ac7e08cd348714", "par page)")}</label>
          </div>

          {/* ① Bouton principal : Appliquer mapping */}
          <button
            onClick={() => applyMapping(arrSrc, prodIdx)}
            style={{ ...primaryBtn, width: "100%", justifyContent: "center", fontSize: "12px", padding: "9px 14px", marginBottom: parentIsContainer ? "8px" : "0", borderRadius: "9px" }}
          >{elakayAutoText("h_b5e4e1066d1d62", "⚡ Appliquer mapping (i=")}{prodIdx}{elakayAutoText("h_05708c11a32389", ") — toutes les variables")}</button>

          {/* ② Bouton setup parent repeat+pagination */}
          {parentIsContainer && !parentHasRepeat && (
            <button
              onClick={() => {
                applyMapping(arrSrc, prodIdx);
                setupParentRepeatPaginate(element.options?.livePageSize ?? 12);
              }}
              style={{ ...ghostBtn, width: "100%", justifyContent: "center", fontSize: "11px", padding: "7px 12px", color: "#6d28d9", borderColor: "#c4b5fd", background: "#f3effe", borderRadius: "9px", marginBottom: "4px" }}
            >{elakayAutoText("h_b8eca1de44e3db", "⚡ + Activer répétition + pagination sur le conteneur parent (")}{element.options?.livePageSize ?? 12}/page)
            </button>
          )}
          {parentIsContainer && parentHasRepeat && (
            <div style={{ fontSize: "10px", color: "#16a34a", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
              <Check size={12}/>{elakayAutoText("h_5d47a3b8120483", "Répétition déjà active sur le parent")}</div>
          )}
          {!parentIsContainer && (
            <p style={{ fontSize: "9px", color: "#b3aacb", margin: "6px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_5bc859af62d2ec", "Placez cet élément dans un")}<strong>{elakayAutoText("h_514459e31f9c7f", "conteneur")}</strong>{elakayAutoText("h_5e8112be61d5c4", "ou une")}<strong>{elakayAutoText("h_110dd48a1e976f", "grille")}</strong>{elakayAutoText("h_21c3382977948e", "pour activer la répétition + pagination automatique.")}</p>
          )}
        </div>
      )}

      {/* ════ Source principale (non-product elements) ════ */}
      {!hasProductMapping && (
        <div style={{ marginBottom: "16px", paddingBottom: "14px", borderBottom: "1px solid #f0edf8" }}>
          <span style={fieldLabel}>{elakayAutoText("h_7f6cf42cbf7f37", "Source principale (bindSource)")}</span>
          <select value={element.bindSource || schema.sources[0]} onChange={(e) => onUpdate({ ...element, bindSource: e.target.value })} style={{ ...selectStyle, fontSize: "11px", padding: "5px 8px", marginTop: "5px" }}>
            {schema.sources.map(s => { const m = ALL_STORE_KEYS.find(k => k.value === s); return <option key={s} value={s}>{m ? m.label : s}</option>; })}
          </select>
        </div>
      )}

      {/* ════ Variables d'entrée (collapsible) ════ */}
      {schema.params?.length > 0 && (() => {
        const overriddenCount = Object.values(slots).filter(v => v).length;
        return (
          <div style={{ marginBottom: "14px", border: "1px solid #ece9f5", borderRadius: "10px", overflow: "hidden" }}>
            <button
              onClick={() => setOpenVars(v => !v)}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", background: "#faf9fd", border: "none", cursor: "pointer", fontSize: "11px", fontWeight: 700, color: "#4a4a68" }}
            >
              <span>{elakayAutoText("h_2079fec58af2c6", "Variables d'entrée (")}{schema.params.length})</span>
              <span style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                {overriddenCount > 0 && <span style={{ fontSize: "9px", background: "#6d28d9", color: "#fff", padding: "1px 6px", borderRadius: "999px" }}>{overriddenCount}{elakayAutoText("h_f761f59eac7c1e", "modifié")}{overriddenCount > 1 ? "s" : ""}</span>}
                {openVars ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
              </span>
            </button>
            {openVars && (
              <div style={{ padding: "8px 10px" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "6px" }}>
                  {overriddenCount > 0 && (
                    <button onClick={() => setOption("liveSlots", {})} style={{ ...ghostBtn, fontSize: "9px", padding: "2px 7px", borderRadius: "6px" }}>{elakayAutoText("h_49c5551e716b01", "↺ Reset tout")}</button>
                  )}
                </div>
                {schema.params.map(param => {
                  /* ── option directe (non chemin store) ── */
                  if (param.type === "option") {
                    const curVal = element.options?.[param.key] ?? param.default;
                    return (
                      <div key={param.key} style={{ marginBottom: "6px", padding: "6px 8px", background: "#f0fdf4", borderRadius: "7px", border: "1px solid #bbf7d0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px" }}>
                          <code style={{ fontSize: "10px", fontFamily: "ui-monospace,monospace", color: "#166534", fontWeight: 700, flex: 1 }}>{param.key}</code>
                          <span style={{ fontSize: "9px", background: "#d1fae5", color: "#065f46", padding: "1px 5px", borderRadius: "4px" }}>{elakayAutoText("h_102210fe594ee9", "opt")}</span>
                        </div>
                        {param.inputType === "boolean" ? (
                          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "#166534", cursor: "pointer" }}>
                            <input type="checkbox" checked={!!curVal}
                              onChange={(e) => setOption(param.key, e.target.checked)} />
                            {String(curVal)}
                          </label>
                        ) : (
                          <input
                            type={param.inputType === "number" ? "number" : "text"}
                            value={curVal}
                            step={param.step ?? "any"}
                            min={param.min}
                            max={param.max}
                            onChange={(e) => setOption(param.key, param.inputType === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)}
                            style={{ width: "100%", padding: "3px 5px", fontSize: "10px", border: "1px solid #86efac", borderRadius: "6px", background: "#fff", color: "#166534", outline: "none", boxSizing: "border-box" }}
                          />
                        )}
                        <div style={{ fontSize: "9px", color: "#4ade80", marginTop: "2px" }}>{param.label}</div>
                      </div>
                    );
                  }
                  /* ── chemin store (sélecteur) ── */
                  const current = slots[param.key] || param.default;
                  const isOver = !!(slots[param.key] && slots[param.key] !== param.default);
                  return (
                    <div key={param.key} style={{ marginBottom: "6px", padding: "6px 8px", background: isOver ? "#f3effe" : "#faf9fd", borderRadius: "7px", border: `1px solid ${isOver ? "#e4dcfb" : "#ece9f5"}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px" }}>
                        <code style={{ fontSize: "10px", fontFamily: "ui-monospace,monospace", color: isOver ? "#6d28d9" : "#4a4a68", fontWeight: 700, flex: 1 }}>{param.key}</code>
                        {isOver && <button onClick={() => setSlot(param.key, "")} style={{ ...miniBtn, fontSize: "9px", color: "#9b93b8" }}>↺</button>}
                      </div>
                      <select value={current} onChange={(e) => setSlot(param.key, e.target.value === param.default ? "" : e.target.value)}
                        style={{ width: "100%", padding: "3px 5px", fontSize: "10px", border: "1px solid #e4e0f0", borderRadius: "6px", background: "#fff", color: isOver ? "#6d28d9" : "#4a4a68", outline: "none" }}>
                        <option value={param.default}>{param.default}{elakayAutoText("h_18ee80cdfd16cf", "(défaut)")}</option>
                        {ALL_STORE_KEYS.filter(k => k.value !== param.default).map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                      </select>
                      <div style={{ fontSize: "9px", color: "#b3aacb", marginTop: "2px" }}>{param.label}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ════ Fonctions ════ */}
      {fnMeta.length > 0 && (
        <div style={{ marginBottom: "14px", border: "1px solid #ece9f5", borderRadius: "10px", overflow: "hidden" }}>
          <button onClick={() => setOpenFns(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", background: "#faf9fd", border: "none", cursor: "pointer", fontSize: "11px", fontWeight: 700, color: "#4a4a68" }}>
            <span>{elakayAutoText("h_d0d68f5251672b", "Fonctions shopUser (")}{fnMeta.length})</span>
            {openFns ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
          </button>
          {openFns && (
            <div style={{ padding: "8px 10px" }}>
              {fnMeta.map(fn => (
                <div key={fn.name} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 8px", marginBottom: "4px", background: "#f3effe", borderRadius: "7px", border: "1px solid #e4dcfb" }}>
                  <span style={{ fontSize: "10px", fontFamily: "ui-monospace,monospace", color: "#6d28d9", flex: 1 }}>{fn.signature}</span>
                  <span style={{ fontSize: "9px", color: "#9b93b8" }}>[{fn.argc}]</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════ Résumé JSON ════ */}
      {isConfigured && (
        <div style={{ border: "1px solid #ece9f5", borderRadius: "10px", overflow: "hidden" }}>
          <button onClick={() => setOpenSum(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", background: "#faf9fd", border: "none", cursor: "pointer", fontSize: "11px", fontWeight: 700, color: "#4a4a68" }}>
            <span>{elakayAutoText("h_5ab6ce608571a9", "Résumé config")}</span>
            {openSum ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
          </button>
          {openSum && (
            <div style={{ padding: "10px", background: "#1a1a2e", fontSize: "10px", fontFamily: "ui-monospace,monospace", color: "#d6cfec", lineHeight: 1.7 }}>
              <div><span style={{ color: "#a78bfa" }}>{elakayAutoText("h_f0f76ad847e1f5", "bindSource")}</span>: <span style={{ color: "#6ee7b7" }}>{element.bindSource || schema.sources[0]}</span></div>
              {hasProductMapping && <div><span style={{ color: "#a78bfa" }}>i</span>: <span style={{ color: "#fbbf24" }}>{prodIdx}</span> <span style={{ color: "#4b5563" }}>({arrSrc}[{prodIdx}])</span></div>}
              {schema.params.map(p => {
                if (p.type === "option") {
                  const v = element.options?.[p.key] ?? p.default;
                  return <div key={p.key}><span style={{ color: "#4ade80" }}>{p.key}</span>: <span style={{ color: "#fde68a" }}>{String(v)}</span> <span style={{ color: "#374151", fontSize: "9px" }}>{elakayAutoText("h_3c5984e91e5e75", "(opt)")}</span></div>;
                }
                return <div key={p.key}><span style={{ color: "#a78bfa" }}>{p.key}</span>: <span style={{ color: slots[p.key] ? "#fbbf24" : "#6ee7b7" }}>{slots[p.key] || p.default}</span></div>;
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="pb-section" style={{ marginBottom: "18px", padding: "12px", borderRadius: "10px", borderBottom: "1px solid #f0edf8" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px" }}>
        {icon && React.createElement(icon, { size: 14, color: "#9b93b8" })}
        <span style={{ fontSize: "12px", fontWeight: "700", color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calques
// ---------------------------------------------------------------------------
function LayerItem({ node, depth, selectedIds, onSelect, onMove, onDelete }) {
  const def = ELEMENT_TYPES[node.type];
  const isSel = selectedIds?.includes(node.id);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const hasSelectedDescendant = useMemo(() => {
    const containsSelected = (children) => children?.some((child) =>
      selectedIds?.includes(child.id) || containsSelected(child.children)
    );
    return hasChildren && containsSelected(node.children);
  }, [hasChildren, node.children, selectedIds]);
  const [childrenOpen, setChildrenOpen] = useState(true);

  useEffect(() => {
    if (hasSelectedDescendant) setChildrenOpen(true);
  }, [hasSelectedDescendant]);

  return (
    <>
      <div onClick={(e) => onSelect(node.id, e.shiftKey)} style={{
        display: "flex", alignItems: "center", gap: "7px", padding: "7px 8px", paddingLeft: `${8 + depth * 16}px`,
        borderRadius: "8px", cursor: "pointer", background: isSel ? "#f3effe" : "transparent",
        color: isSel ? "#6d28d9" : "#4a4a68", fontSize: "13px", fontWeight: isSel ? 600 : 500,
      }}>
        {hasChildren ? (
          <button
            type="button"
            aria-label={childrenOpen ? elakayAutoText("h_3c3b8a4e164087", "Replier ce groupe de calques") : elakayAutoText("h_2db047b02e7872", "Déplier ce groupe de calques")}
            aria-expanded={childrenOpen}
            title={childrenOpen ? elakayAutoText("h_800a47cf7f2bee", "Replier le groupe") : elakayAutoText("h_e489a8a8c4c818", "Déplier le groupe")}
            onClick={(e) => {
              e.stopPropagation();
              setChildrenOpen((open) => !open);
            }}
            style={{ ...miniBtn, flexShrink: 0, opacity: 0.85 }}
          >
            {childrenOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        ) : (
          <span style={{ width: "20px", flexShrink: 0 }} />
        )}
        {def?.icon && React.createElement(def.icon, { size: 14 })}
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.text || def?.label}</span>
        {hasChildren && (
          <span style={{ minWidth: "18px", padding: "1px 5px", borderRadius: "999px", background: isSel ? "#e4dcfb" : "#f3effe", color: "#8b7eae", fontSize: "9px", textAlign: "center" }}>
            {node.children.length}
          </span>
        )}
        <button onClick={(e) => { e.stopPropagation(); onMove(node.id, -1); }} style={miniBtn}><ChevronUp size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); onMove(node.id, 1); }} style={miniBtn}><ChevronDown size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(node.id); }} style={{ ...miniBtn, color: "#e11d48" }}><X size={13} /></button>
      </div>
      {hasChildren && childrenOpen && (
        <div>
          {node.children.map((c) => (
            <LayerItem key={c.id} node={c} depth={depth + 1} selectedIds={selectedIds} onSelect={onSelect} onMove={onMove} onDelete={onDelete} />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Canevas en positionnement libre : chaque élément racine a une position { x, y }
// déplaçable à la souris (glisser) ; les flèches clavier sont gérées plus haut.
// ---------------------------------------------------------------------------
// Poignées de redimensionnement : position CSS + curseur (partagé avec le
// chrome de sélection en mode flux — voir editorKit/useElementResize).
const FREE_CANVAS_HANDLES = RESIZE_HANDLES;

function FreeCanvas({
  nodes, selectedIds, onSelect, onSetPosition, onNudgeSelected, onResize, device, pageStyle,
  layoutDevice = "desktop", onGestureStart, onGestureEnd, onDropReparent, resolveNode, isDroppable,
}) {
  const areaRef = useRef(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  // Déplacement (position libre + drag groupé) — logique extraite dans
  // editorKit/useElementDrag ; le geste souris complet (mousedown → mouseup)
  // ne produit qu'UNE entrée d'historique grâce à onGestureStart/End.
  const { startDrag } = useElementDrag({
    canvasRef: areaRef,
    resolveNode,
    isDroppable,
    onDragStart: () => onGestureStart?.(),
    onMove: onSetPosition,
    onGroupMove: onNudgeSelected,
    onHoverDropTarget: onDropReparent ? setDropTargetId : undefined,
    onDrop: onDropReparent ? (nodeId, containerId, moved) => { if (moved && containerId) onDropReparent(nodeId, containerId); } : undefined,
    onDragEnd: () => onGestureEnd?.(),
  });
  const { startResize } = useElementResize({
    onResizeStart: () => onGestureStart?.(),
    onResize,
    onResizeEnd: () => onGestureEnd?.(),
  });

  const onPointerDownNode = (e, node) => {
    e.stopPropagation();
    const additive = e.shiftKey;
    const wasSelected = selectedIds.includes(node.id);
    if (!wasSelected || additive) onSelect(node.id, additive);
    const groupDrag = wasSelected && selectedIds.length > 1 && !additive;
    startDrag(e, node, { group: groupDrag, freeMode: true, pos: getFreeLayout(node, layoutDevice) });
  };

  return (
    <div
      ref={areaRef}
      onClick={() => onSelect(null)}
      style={{
        position: "relative", minHeight: "100%",
        ...pageStyle, maxWidth: "100%",
        backgroundImage: "linear-gradient(#f0edf8 1px, transparent 1px), linear-gradient(90deg, #f0edf8 1px, transparent 1px)",
        backgroundSize: "10px 10px",
      }}
    >
      {nodes.map((node) => {
        const pos = getFreeLayout(node, layoutDevice);
        const u = pos.unit === "%" ? "%" : "px";
        const isSel = selectedIds.includes(node.id);
        const single = isSel && selectedIds.length === 1;
        const isDropTarget = dropTargetId === node.id;
        return (
          <div
            key={node.id}
            data-pb-node-id={node.id}
            onPointerDown={(e) => onPointerDownNode(e, node)}
            style={{
              position: "absolute", left: `${pos.x}${u}`, top: `${pos.y}${u}`, cursor: "move",
              zIndex: pos.z,
              touchAction: "none", userSelect: "none",
              outline: isDropTarget ? "2px solid #059669" : (isSel ? "2px solid #6d28d9" : "1px dashed transparent"),
              outlineOffset: "2px",
              background: isDropTarget ? "rgba(5,150,105,0.08)" : "transparent",
            }}
          >
            <div style={{ pointerEvents: "none" }}>
              <RenderNode node={node} selectedIds={[]} onSelect={() => {}} editable={false} />
            </div>
            {isSel && (
              <span style={{ position: "absolute", top: "-20px", left: 0, fontSize: "10px", fontWeight: 700, color: "#6d28d9", background: "#fff", padding: "1px 6px", borderRadius: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.12)", whiteSpace: "nowrap" }}>
                x {Math.round(pos.x)}{u} · y {Math.round(pos.y)}{u}
              </span>
            )}
            {/* 8 poignées de redimensionnement (seulement si un seul élément est sélectionné) */}
            {single && FREE_CANVAS_HANDLES.map(({ h, style }) => (
              <div
                key={h}
                onPointerDown={(e) => {
                  const rect = e.currentTarget.parentElement.getBoundingClientRect();
                  startResize(e, node, h, rect, pos);
                }}
                style={{
                  position: "absolute", width: "10px", height: "10px", background: "#fff",
                  border: "2px solid #6d28d9", borderRadius: "3px", zIndex: 10, ...style,
                }}
              />
            ))}
          </div>
        );
      })}
      {nodes.length === 0 && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#b3aacb" }}>
          <Move size={32} style={{ marginBottom: "12px", opacity: 0.5 }} />
          <p>{elakayAutoText("h_205a48c704f076", "Ajoutez des éléments, puis glissez-les où vous voulez. Tirez les poignées pour redimensionner.")}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlowSelectionOverlay — chrome de sélection + resize + reparenting pour un
// élément du FLUX NORMAL (pas en mode "position libre"). Contrairement à
// FreeCanvas (qui ne gère que les nœuds racine positionnés en x/y), cet
// overlay fonctionne pour N'IMPORTE QUEL nœud sélectionné, racine ou
// imbriqué à n'importe quelle profondeur — il se contente de mesurer le
// vrai rectangle DOM du nœud (via l'attribut data-pb-node-id, déjà posé
// par RenderNode sur CHAQUE nœud) plutôt que de connaître sa position dans
// l'arbre. Le déplacement en flux normal n'a pas de sens en x/y — cet
// overlay ne sert donc qu'au redimensionnement (poignées) et au glisser-
// déposer pour reparenter (poignée de déplacement en haut à droite).
// ---------------------------------------------------------------------------
function FlowSelectionOverlay({
  nodeId, containerEl, label, parentLabel,
  onResize, onResizeStart, onResizeEnd,
  onReparentDrop, resolveNode, isDroppable,
}) {
  const [targetEl, setTargetEl] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const canvasRefShim = useRef(containerEl);
  canvasRefShim.current = containerEl;

  useEffect(() => {
    const wrap = document.querySelector(`[data-pb-node-id="${nodeId}"]`);
    setTargetEl(wrap ? (wrap.firstElementChild || wrap) : null);
  }, [nodeId, containerEl]);

  const { startResize } = useElementResize({
    onResizeStart, onResize, onResizeEnd,
  });
  const { startDrag } = useElementDrag({
    canvasRef: canvasRefShim,
    resolveNode,
    isDroppable,
    onHoverDropTarget: setDropTargetId,
    onDrop: (draggedId, containerId, moved) => { if (moved && containerId) onReparentDrop?.(draggedId, containerId); },
  });

  if (!targetEl || !containerEl) return null;

  return (
    <SelectionOverlay
      targetEl={targetEl}
      containerEl={containerEl}
      label={label}
      parentLabel={parentLabel}
      dropHighlight={!!dropTargetId}
      onHandlePointerDown={(e, handle, rect) => startResize(e, { id: nodeId }, handle, rect)}
      onMovePointerDown={(e) => startDrag(e, { id: nodeId })}
    />
  );
}

// ---------------------------------------------------------------------------
// COMPOSANT PRINCIPAL
// ---------------------------------------------------------------------------
const SHOP_SOURCE_LABELS = {
  created: "boutique créée",
  bought: "boutique achetée",
  default: "page par défaut",
  premium: "template premium",
};

const READ_ONLY_SHOP_SOURCES = new Set(["bought", "default", "premium"]);
const SHOP_COMMERCE_SOURCES = new Set(["created", "bought", "default", "premium"]);
const SHOP_CART_STORAGE_KEY = "pb_shop_cart";
const LOCAL_DEFAULT_SHOP_PREFIX = "shop-template-";

const getShopCartKey = (shop) =>
  `${shop?.source || "shop"}:${String(shop?.id || "")}`;

const isLocalDefaultShopId = (value) =>
  String(value || "").startsWith(LOCAL_DEFAULT_SHOP_PREFIX);

const getShopItemId = (item) =>
  String(item?.templateId || item?.pageId?._id || item?.pageId || item?._id || "");

const getShopItemTitle = (item) => {
  const templateId = String(item?.templateId || "");
  const localTemplate = templateId
    ? SHOP_TEMPLATES.find((template) =>
        String(template._id) === templateId || template.key === templateId
      )
    : null;
  return localTemplate?.label || item?.title || item?.pageId?.title || "Sans titre";
};

const getShopPageData = (item) =>
  item?.pageId && typeof item.pageId === "object" ? item.pageId : item;

const getShopItemPrice = (item) =>
  Math.max(0, Number(getShopPageData(item)?.price) || 0);

const getShopDownloadRight = (item) =>
  Boolean(
    item?.downloadRight ||
    item?.purchaseRights?.downloadRight ||
    getShopPageData(item)?.downloadRight ||
    getShopPageData(item)?.purchaseRights?.downloadRight
  );

const getShopDisplayRight = (item) =>
  Boolean(
    item?.displayRight ||
    item?.purchaseRights?.displayRight ||
    getShopPageData(item)?.displayRight ||
    getShopPageData(item)?.purchaseRights?.displayRight
  );

const isPurchasedShopItem = (item) => {
  const page = getShopPageData(item);
  return Boolean(page?.sourceModel || page?.purchasedFrom);
};

const isMongoObjectId = (value) =>
  /^[a-f\d]{24}$/i.test(String(value || ""));

const makeBoundDataset = (datasetKey, dataset) => {
  if (!dataset) return null;
  const sourceKey = dataset.pageMode === "contentPage" ? "contentPage" : datasetKey;
  return { ...dataset, key: sourceKey, datasetKey };
};

const getBoundDatasetKey = (boundData) =>
  boundData?.datasetKey || boundData?.key || "";

const getDatasetPageKey = (datasetKey, dataset) =>
  dataset?.data?.pageKey || dataset?.data?.sourceKey || String(datasetKey || "").replace(/^cp_/, "");

// Family "System Templates" override resolver -- when a Shop/Project starter
// template is picked (SHOP_TEMPLATES/PROJECT_TEMPLATES, both keyed by their
// own stable `key`), check whether Family has linked+turned-on a custom
// PageBuilder document for `${group}.${tpl.key}` FIRST (see
// SystemTemplatesPanel.jsx / server_elakay's pageTemplateOverrideService.js
// for the full architecture). Fails safe to the original hardcoded
// `tpl.make()` on any error/absence -- exactly the same fallback contract
// as every other consumer of this resolver.
const resolveShopOrProjectTemplateDoc = async (tpl, group) => {
  try {
    const result = await getPageTemplateOverride(`${group}.${tpl.key}`);
    if (result?.priority && result?.pagebuilderDoc?.doc) {
      return result.pagebuilderDoc.doc;
    }
  } catch (_error) {
    // fall through to the original template below
  }
  return tpl.make();
};

export default function PageBuilder({
  pageMode: initialPageMode,
  lockPageMode  = false,
  liveDatasets  = null,   // { store: { label, pageMode, data, isLive }, … }
  onDocChange   = null,   // (doc) => void — appelé à chaque changement de doc
  onSave        = null,   // async (payload) => savedDoc — sauvegarde en BD (optionnel)
                          //   payload = { doc, title, description, price, isTemplate,
                          //               forSale, status, tags, pageMode }
                          //   → après succès, supprime automatiquement le brouillon localStorage
  initialDoc    = null,   // doc chargé depuis la BD (ex: page existante ou template)
                          //   → utilisé à l'initialisation du state (préférer key= pour recharger)
  savedPageId   = null,   // _id MongoDB de la page courante (null = nouvelle page)
  savedPage     = null,   // document MongoDB complet de la page sélectionnée
  myPages       = [],     // liste des pages sauvegardées à afficher dans le tab "mySaves"
  onLoadPage    = null,   // async (doc, id, page) => void — sélectionne et charge une page complète
  onDeletePage  = null,   // async (id) => void — supprime une page
  onRefreshPages= null,   // () => void — recharge la liste myPages
  onEditInDb    = null,   // () => void — ouvre le modal d'édition métadonnées BD
  onFamilyUnlockPage = null, // async (sourcePage, password) => copie modifiable (sourcePage fourni) ou simple validation Family (sourcePage=null, débloque l'édition en place)
  infoSource    = null,   // contexte app (i18n, styles, user) — transmis au PageRenderer
  hideHeaderTemplateButton = false,
  hideHeaderPreviewToggle = false,
  hideLeftPreviewToggle = false,
  fitParentHeight = false,
  storeOnly = false,
  canSave = true,         // false = bouton sauvegarder désactivé explicitement
  currentUserId = null,   // _id de l'utilisateur connecté (pour vérifier la propriété)
  onPageMetaUpdated = null, // (updatedPage) => void -- appelé après un Save/Restore HTML réussi, pour que le parent puisse rafraîchir savedPage (htmlSource) sans recharger toute la page
  rendererStoreConfig = null, // storeConfig transmis au PageRenderer de l'Aperçu (même rendu que la boutique réelle, ex. storefront Provider)
  elementPresets = null,      // [{ key, label, items:[{ key, label, icon, make: () => node }] }] -- groupes d'éléments prêts à l'emploi propres à l'hôte (ex. "Provider")
} = {}) {
  // ── i18n hook — même pattern que AnimationSettings.jsx ───────────────────
  __setPbInfoSource(infoSource);
  const pageBuilderText = useCallback(
    (key, fallback) =>
      infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.pageBuilder?.[key] || fallback,
    [infoSource]
  );
  const pbt = pageBuilderText; // alias court pour usage inline
  const draftStorageKeys = useMemo(
    () => buildPageBuilderDraftStorageKeys({
      userId: currentUserId,
      pageId: savedPageId,
      pageMode: initialPageMode || initialDoc?.pageMode || "static",
    }),
    [currentUserId, savedPageId, initialPageMode, initialDoc?.pageMode]
  );

  // Initialise le doc :
  //   1. depuis initialDoc (BD) si fourni et valide
  //   2. depuis initialPageMode si fourni
  //   3. sinon STARTER_DOC
  const [doc, setDoc] = useState(() => {
    if (initialDoc && Array.isArray(initialDoc.pages) && initialDoc.pages.length > 0) {
      return initialDoc;
    }
    if (initialPageMode && PAGE_MODES.find((m) => m.id === initialPageMode)) {
      return { ...STARTER_DOC, pageMode: initialPageMode };
    }
    return STARTER_DOC;
  });
  // Undo/redo — un geste souris complet (drag de déplacement ou de
  // redimensionnement) ne produit qu'UNE seule entrée d'historique ;
  // voir setChildren ci-dessous pour le point d'intégration unique.
  const history = useEditorHistory(doc, setDoc);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bodySelected, setBodySelected] = useState(false);
  const [presetId, setPresetId] = useState("desktop-xl");
  const [responsive, setResponsive] = useState(false);
  // Écrans cochés à afficher en mode responsive (checkbox group)
  const [activeScreens, setActiveScreens] = useState(["desktop", "mobile"]);
  const preset = DEVICE_PRESETS.find((p) => p.id === presetId) || DEVICE_PRESETS[0];
  const device = preset.kind; // "desktop" ou "mobile" — pilote le rendu des surcharges
  const layoutDevice = layoutDeviceForPreset(preset); // desktop / tablet / mobile positions are independent
  const toggleScreen = (id) => setActiveScreens((prev) => prev.includes(id) ? (prev.length > 1 ? prev.filter((x) => x !== id) : prev) : [...prev, id]);
  const [freeMode, setFreeMode] = useState(false);
  // Panneaux mobiles : sur écran < 1100px les asides deviennent rétractables
  const [mobileLeft, setMobileLeft] = useState(false);
  const [mobileRight, setMobileRight] = useState(false);
  const normalCanvasRef = useRef(null);  // conteneur du rendu normal (pour mesurer les positions avant freeMode)
  const canvasFrameRef = useRef(null);   // cadre de l'appareil (position:relative) — repère de FlowSelectionOverlay
  const [showPreview, setShowPreview] = useState(false);
  const [showHelp, setShowHelp]       = useState(false);
  const [shopPricing, setShopPricing] = useState({ displayPrice:35, downloadPrice:15, downloadOwnPrice:8.99, bothPrice:45 });
  const [showBoutiqueActions, setShowBoutiqueActions] = useState(false);
  const [boutiqueActionState, setBoutiqueActionState] = useState({ loading: false, error: null, success: null });
  // ── 4 catégories de boutiques ─────────────────────────────────────────────
  const [myCreatedShops,  setMyCreatedShops]  = useState([]);
  const [myBoughtShops,   setMyBoughtShops]   = useState([]);
  const [editorTemplates, setEditorTemplates] = useState([]);
  const [defaultShops,    setDefaultShops]    = useState([]);
  const [htmlAccessGrantIds, setHtmlAccessGrantIds] = useState([]);
  const [displayAccessGrantIds, setDisplayAccessGrantIds] = useState([]);
  const [shopSelection,   setShopSelection]   = useState(null);
  const [shopCart, setShopCart] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SHOP_CART_STORAGE_KEY) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [showShopCart, setShowShopCart] = useState(false);
  const [shopPurchaseTargetKey, setShopPurchaseTargetKey] = useState("");
  const [shopPurchaseState, setShopPurchaseState] = useState({
    loadingId: null,
    error: null,
    success: null,
  });
  // "</> Edit HTML Code" -- password-gated (see pageHtmlAccess.js for the
  // permission check, PageHtmlSourceModal.jsx for the password + editor flow).
  const [showHtmlEditModal, setShowHtmlEditModal] = useState(false);
  const [showFamilyUnlockModal, setShowFamilyUnlockModal] = useState(false);
  const [familyPassword, setFamilyPassword] = useState("");
  const [familyUnlockError, setFamilyUnlockError] = useState("");
  const [familyUnlockBusy, setFamilyUnlockBusy] = useState(false);
  // Mot de passe Family re-confirmé pour la page actuellement ouverte —
  // permet de la modifier / sauvegarder EN PLACE (pas de copie) tant que
  // cette même page reste ouverte. Réinitialisé dès qu'on change de page.
  const [familyUnlockedPassword, setFamilyUnlockedPassword] = useState("");
  useEffect(() => { setFamilyUnlockedPassword(""); }, [savedPageId]);
  const [showFamilyPassword, setShowFamilyPassword] = useState(false);
  // Escape ferme le modal (sauf pendant la vérification, comme le bouton Annuler).
  useEffect(() => {
    if (!showFamilyUnlockModal) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !familyUnlockBusy) closeFamilyUnlockModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFamilyUnlockModal, familyUnlockBusy]);
  const [simpleMode, setSimpleMode] = useState(() => { try { return localStorage.getItem("pb_simpleMode") === "1"; } catch { return false; } });
  const toggleSimpleMode = () => setSimpleMode(v => { const n = !v; try { localStorage.setItem("pb_simpleMode", n ? "1" : "0"); } catch {} return n; });
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  // ── Dropdown l × c (barre supérieure) ────────────────────────────────────
  const [showLxCDropdown, setShowLxCDropdown] = useState(false);
  const [lxcCustom, setLxcCustom] = useState({ l: 3, c: 4 }); // valeurs saisies manuellement
  const lxcDropdownRef = useRef(null);
  // Boutique tab — clé du template actif + bascule Aperçu/Builder
  const [activeTemplateKey, setActiveTemplateKey] = useState(null);
  // Aperçu — device et page active
  const [previewDevice, setPreviewDevice] = useState("desktop"); // "desktop"|"tablet"|"mobile"
  const [previewPageId, setPreviewPageId] = useState(null);
  const [centerDetached, setCenterDetached] = useState(false);
  const detachedWindowRef = useRef(null);
  const detachedRootRef = useRef(null);
  const [detachedPanelTab, setDetachedPanelTab] = useState(null);
  const detachedPanelWindowRef = useRef(null);
  const detachedPanelRootRef = useRef(null);
  // Section API Boutique dans le panneau Données — repliable
  const [apiBoutiqueOpen, setApiBoutiqueOpen] = useState(true);
  // Groupes d'éléments (panneau gauche + droit) : true = ouvert, false = replié
  const [openGroups, setOpenGroups] = useState({
    quick:true, base:true, layout:true, rich:false, form:false,
    media:false, documents:false, data:false, custom:true,
    shopHeader:false, shopLive:true, html:false, htmlForm:false,
    icons:false,
  });
  const toggleOpenGroup = useCallback((k) => setOpenGroups((p) => ({ ...p, [k]: !p[k] })), []);
  const [rightTab, setRightTab] = useState("props");
  const [leftTab, setLeftTab] = useState("add");
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  // Côté de chaque tab : "left" | "right" — permet de déplacer un tab d'un panneau à l'autre
  const [panelTabSides, setPanelTabSides] = useState({
    add:"left", html:"left", layers:"left", pages:"left", boutique:"left",
    props:"right", anim:"right", links:"right", json:"right", htmlCode:"right", data:"right", shopLive:"right",
    comments:"left", combine:"left", mySaves:"left",
  });
  // ── Commentaires d'éléments ────────────────────────────────────────────
  const elementComments = Array.isArray(doc?.comments) ? doc.comments : [];
  const setElementComments = useCallback((updater) => {
    setDoc((currentDoc) => {
      const previous = Array.isArray(currentDoc?.comments) ? currentDoc.comments : [];
      const next = typeof updater === "function" ? updater(previous) : updater;
      return { ...currentDoc, comments: Array.isArray(next) ? next : [] };
    });
  }, []);
  const [newComment, setNewComment] = useState("");
  // ── Éléments combinés (bibliothèque de groupes nommés) ─────────────────
  const combinedElements = Array.isArray(doc?.combinedElements) ? doc.combinedElements : [];
  const setCombinedElements = useCallback((updater) => {
    setDoc((currentDoc) => {
      const previous = Array.isArray(currentDoc?.combinedElements)
        ? currentDoc.combinedElements
        : [];
      const next = typeof updater === "function" ? updater(previous) : updater;
      return { ...currentDoc, combinedElements: Array.isArray(next) ? next : [] };
    });
  }, []);
  const [newCombineName, setNewCombineName] = useState("");
  const moveTabToPanel = (tabKey, toSide) => {
    setPanelTabSides(prev => ({ ...prev, [tabKey]: toSide }));
    if (toSide === "left") { setLeftTab(tabKey); setShowLeftPanel(true); }
    else                   { setRightTab(tabKey); setShowRightPanel(true); }
  };
  const selectPanelTab = (tabKey) => {
    if (panelTabSides[tabKey] === "left") {
      setLeftTab(tabKey);
      setShowLeftPanel(true);
    } else {
      setRightTab(tabKey);
      setShowRightPanel(true);
    }
  };
  const [projectCssBundle, setProjectCssBundle] = useState("");
  const [jsonText, setJsonText] = useState(() => JSON.stringify(STARTER_DOC, null, 2));
  const [jsonError, setJsonError] = useState("");
  const [autoSync, setAutoSync] = useState(true);     // synchro automatique JSON <-> éditeur
  const editingJson = useRef(false);                  // true quand l'utilisateur tape dans le textarea
  const syncTimer = useRef(null);

  // ── Auto-save localStorage ────────────────────────────────────────────────
  const [autoSaveMeta, setAutoSaveMeta] = useState(() => {
    try { return JSON.parse(localStorage.getItem(draftStorageKeys.metaKey)) || null; } catch { return null; }
  });
  const [showDraftBanner, setShowDraftBanner] = useState(() => {
    try { return !!localStorage.getItem(draftStorageKeys.draftKey); } catch { return false; }
  });
  useEffect(() => {
    try {
      setAutoSaveMeta(JSON.parse(localStorage.getItem(draftStorageKeys.metaKey)) || null);
      setShowDraftBanner(Boolean(localStorage.getItem(draftStorageKeys.draftKey)));
    } catch {
      setAutoSaveMeta(null);
      setShowDraftBanner(false);
    }
  }, [draftStorageKeys.draftKey, draftStorageKeys.metaKey]);
  const [isSaving, setIsSaving] = useState(false);    // en cours de sauvegarde BD
  const [showSaveNameModal, setShowSaveNameModal] = useState(false);
  const [saveNameMode, setSaveNameMode] = useState("create");
  const [saveName, setSaveName] = useState("");
  const [saveNameError, setSaveNameError] = useState("");
  const docRef = useRef(doc);                          // ref toujours à jour pour l'intervalle
  // A successful DB save must not be followed by the idle timer recreating
  // the just-cleared "new-*" draft. Reference identity changes on the next
  // real edit, which automatically re-enables autosave.
  const lastSavedDocRef = useRef(null);
  // Panneau Données : zone de collage, URL d'API, message de statut
  const [pasteText, setPasteText] = useState("");
  // Data-binding : jeu de données chargé + chemin exploré + liaisons élément↔clé
  const [boundData, setBoundData] = useState(null);      // { label, pageMode, data }
  const [explorePath, setExplorePath] = useState("");    // chemin actuellement exploré
  const [contentArrayExpanded, setContentArrayExpanded] = useState(() => new Set()); // tableaux ouverts dans l'explorateur de contenu
  const [bindings, setBindings] = useState([]);          // [{ elementId, source, path, repeat, valueKey, valueType }]
  const [copiedKey, setCopiedKey] = useState("");        // clé dont le chemin vient d'être copié (retour visuel)
  const [pasteError, setPasteError] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [dataStatus, setDataStatus] = useState("");
  const fileInputRef = useRef(null);
  const ALL_TEMPLATES = useMemo(
    () => [...SHOP_TEMPLATES, ...CONTENT_TEMPLATES, ...PROJECT_TEMPLATES],
    []
  );
  const boutiqueTemplates = useMemo(
    () => storeOnly
      ? ALL_TEMPLATES.filter((tpl) => tpl.pageMode === "store")
      : ALL_TEMPLATES,
    [storeOnly, ALL_TEMPLATES]
  );
  const documentLinks = useMemo(() => collectDocumentLinks(doc), [doc]);

  // --- Datasets fusionnés : liveDatasets (données réelles) écrase les statics ---
  const allDatasets = useMemo(() => ({
    ...SAMPLE_DATASETS,
    ...(liveDatasets || {}),
  }), [liveDatasets]);

  const contentPageDatasetEntries = useMemo(
    () => Object.entries(allDatasets).filter(([, dataset]) => dataset?.pageMode === "contentPage"),
    [allDatasets]
  );

  const syncContentPageBindingMeta = useCallback((datasetKey, dataset) => {
    setDoc((current) => {
      if (current?.pageMode !== "contentPage") return current;
      const currentConfig = current.pageModeConfig || {};
      const currentSettings = currentConfig.settings || {};

      if (!dataset) {
        const {
          contentPageId,
          contentPageKey,
          contentPageSourceKey,
          contentPageDataKey,
          contentPageRenderer,
          ...nextMeta
        } = current.meta || {};
        const {
          contentPageId: _settingsContentPageId,
          contentPageKey: _settingsContentPageKey,
          contentPageSourceKey: _settingsContentPageSourceKey,
          contentPageDataKey: _settingsContentPageDataKey,
          contentPageRenderer: _settingsContentPageRenderer,
          ...nextSettings
        } = currentSettings;
        return {
          ...current,
          meta: { ...nextMeta, contentPageRenderer: "LanguagePage" },
          pageModeConfig: {
            ...currentConfig,
            mode: "contentPage",
            settings: { ...nextSettings, contentPageRenderer: "LanguagePage" },
          },
        };
      }

      const pageId = dataset?.data?._id || dataset?.data?.pageId || datasetKey;
      const pageKey = getDatasetPageKey(datasetKey, dataset);
      return {
        ...current,
        meta: {
          ...(current.meta || {}),
          contentPageId: String(pageId || ""),
          contentPageKey: pageKey,
          contentPageSourceKey: datasetKey,
          contentPageRenderer: "contentPage",
        },
        pageModeConfig: {
          ...currentConfig,
          mode: "contentPage",
          settings: {
            ...currentSettings,
            contentPageId: String(pageId || ""),
            contentPageKey: pageKey,
            contentPageSourceKey: datasetKey,
            contentPageRenderer: "contentPage",
          },
        },
      };
    });
  }, []);

  const bindDataset = useCallback((datasetKey, dataset, options = {}) => {
    const { resetExplorer = true } = options;
    if (dataset?.pageMode && !lockPageMode) {
      setDoc((current) => (
        current?.pageMode === dataset.pageMode ? current : { ...current, pageMode: dataset.pageMode }
      ));
    }
    setBoundData(makeBoundDataset(datasetKey, dataset));
    if (dataset?.pageMode === "contentPage") {
      syncContentPageBindingMeta(datasetKey, dataset);
    }
    if (resetExplorer) {
      setExplorePath("");
      setContentArrayExpanded(new Set());
    }
  }, [lockPageMode, syncContentPageBindingMeta]);

  const findDatasetForPageMode = useCallback((pageMode, preferredKey = "") => {
    if (!pageMode) return null;
    const entries = Object.entries(allDatasets).filter(([, dataset]) => dataset?.pageMode === pageMode);
    if (!entries.length) return null;

    const keyToMatch = String(preferredKey || "");
    if (keyToMatch) {
      const preferred = entries.find(([key, dataset]) => (
        key === keyToMatch ||
        String(dataset?.data?._id || "") === keyToMatch ||
        String(dataset?.data?.pageId || "") === keyToMatch ||
        String(dataset?.data?.pageKey || "") === keyToMatch ||
        String(dataset?.data?.sourceKey || "") === keyToMatch
      ));
      if (preferred) return preferred;
    }

    return entries.find(([, dataset]) => dataset?.isLive) || entries[0];
  }, [allDatasets]);

  const selectDatasetForPageMode = useCallback((pageMode, preferredKey = "", options = {}) => {
    const found = findDatasetForPageMode(pageMode, preferredKey);
    if (found) {
      bindDataset(found[0], found[1], options);
      return found;
    }

    setBoundData(null);
    if (pageMode === "contentPage") syncContentPageBindingMeta(null, null);
    return null;
  }, [bindDataset, findDatasetForPageMode, syncContentPageBindingMeta]);

  const savedPageShopSource = savedPage?.shopSource || null;
  const savedPageIsPurchasedShop =
    savedPage?.pageMode === "store" &&
    Boolean(savedPage?.sourceModel || savedPage?.purchasedFrom);
  const activeShopSource = shopSelection?.source || savedPageShopSource;
  const shopSelectionReadOnly =
    READ_ONLY_SHOP_SOURCES.has(activeShopSource) ||
    savedPageIsPurchasedShop;
  const shopSelectionLabel =
    SHOP_SOURCE_LABELS[activeShopSource] ||
    (savedPageIsPurchasedShop ? SHOP_SOURCE_LABELS.bought : "boutique");

  const selectShopSource = useCallback((source, item = null) => {
    if (!source) {
      setShopSelection(null);
      return;
    }
    const pageData = getShopPageData(item) || {};
    setShopSelection({
      source,
      id: getShopItemId(item),
      title: getShopItemTitle(item),
      description: pageData.description || "",
      thumbnail: pageData.thumbnail || "",
      price: getShopItemPrice(item),
      authorType: pageData.authorType || "",
      forSale: Boolean(pageData.forSale),
      sourceModelId: String(pageData.sourceModel?._id || pageData.sourceModel || ""),
      templateKey: item?.templateId || item?.key || pageData?.doc?.meta?.templateKey || "",
    });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SHOP_CART_STORAGE_KEY, JSON.stringify(shopCart));
    } catch {}
  }, [shopCart]);

  const selectedCommerceShop = useMemo(() => {
    if (!shopSelection || !SHOP_COMMERCE_SOURCES.has(shopSelection.source)) return null;
    return {
      ...shopSelection,
      purchasePageId: isMongoObjectId(shopSelection.sourceModelId)
        ? shopSelection.sourceModelId
        : shopSelection.id,
      canPurchase:
        isMongoObjectId(shopSelection.sourceModelId) ||
        isMongoObjectId(shopSelection.id) ||
        isLocalDefaultShopId(shopSelection.id),
    };
  }, [shopSelection]);

  const purchaseShopChoices = useMemo(() => {
    const choices = new Map();
    if (selectedCommerceShop?.canPurchase) {
      choices.set(getShopCartKey(selectedCommerceShop), selectedCommerceShop);
    }
    shopCart.forEach((item) => {
      if (item?.canPurchase) choices.set(getShopCartKey(item), item);
    });
    return Array.from(choices.values());
  }, [selectedCommerceShop, shopCart]);

  const shopPurchaseTarget = useMemo(() => {
    if (purchaseShopChoices.length === 0) return null;
    return purchaseShopChoices.find((item) => getShopCartKey(item) === shopPurchaseTargetKey)
      || purchaseShopChoices[0];
  }, [purchaseShopChoices, shopPurchaseTargetKey]);

  const notifyLoadedPage = useCallback((pageDoc, pageId, fullPage, source) => {
    if (!onLoadPage) return;
    const pageData = fullPage
      ? { ...fullPage, doc: pageDoc, shopSource: source }
      : source
        ? {
            doc: pageDoc,
            pageMode: pageDoc?.pageMode || "store",
            title: pageDoc?.meta?.title || pageDoc?.pages?.[0]?.name || "Boutique",
            shopSource: source,
          }
        : null;
    Promise.resolve(onLoadPage(pageDoc, pageId || null, pageData)).catch(() => {});
  }, [onLoadPage]);

  // ── Data Source Save/Edit (task spec: "AJOUTER SAVE + EDIT DANS CHAQUE
  // DATA SOURCE CARD") ────────────────────────────────────────────────────
  // The "Sources de données" column of the "Templates & Sources de données"
  // modal below lists registry datasets (`allDatasets`, e.g. "about",
  // "cp_aboutUs", "store") Family can bind (`Lier`) into the doc being
  // edited. This is a SEPARATE, additional capability on the SAME cards:
  // persist a custom PageBuilder document FOR that dataset, one document per
  // dataset key, ever -- reusing the exact same `PageTemplateOverride`
  // collection/unique-index/atomic-upsert/Family gate the rest of PageBuilder
  // already uses for Content Pages/Shop Templates/Project Templates (task
  // spec §3-6: never a new identity/storage system). Stable identity:
  // `dataset.<registryKey>`, the registry's OWN existing key -- never the
  // (translatable) `ds.label`.
  //
  // Batch-loaded ONCE (task spec §11: never 1 API call per card) via the
  // same Family-only `listPageTemplateOverrides` the System Templates admin
  // panel uses; Family-gated both here (button only rendered for Family)
  // AND server-side (upsertOverride's own assertFamily -- a non-Family
  // caller is rejected regardless of what the UI shows).
  const isFamilyUser = Family(infoSource?.userInfo?.email);
  // Keyed by the CANONICAL identity (`content.<pageKey>` for a real public
  // Content Page, `dataset.<registryKey>` for a purely internal/demo binding
  // -- see resolveCanonicalDataSourceKey's own header comment), never the
  // raw registry key the modal happens to display. Every action below
  // (Save/Edit/Link/Select System Template) resolves through that SAME
  // function first (fix-400 mission's explicit point 8: "il ne faut pas que
  // Save utilise dataset.about mais Link utilise content.aboutUs").
  const [overridesByCanonicalKey, setOverridesByCanonicalKey] = useState({});
  const [dataSourceBusyKey, setDataSourceBusyKey] = useState("");
  const [dataSourceError, setDataSourceError] = useState("");
  // Single-selection state for "Select System Template + Lier" (task spec
  // §6/§7/§28) -- one System Template checked at a time, independent of any
  // particular Data Source card, exactly like a radio group.
  const [selectedSystemTemplateKey, setSelectedSystemTemplateKey] = useState("");

  const refreshSavedDataSources = useCallback(async () => {
    if (!isFamilyUser) return;
    try {
      const { token: tok } = isAuthenticated() || {};
      const result = await listPageTemplateOverrides(tok);
      const byKey = {};
      (result.overrides || []).forEach((o) => { byKey[o.templateKey] = o; });
      setOverridesByCanonicalKey(byKey);
    } catch (_e) {
      // Fails safe: every card just falls back to showing "Save" (task spec
      // §24-style "never let the UI crash") -- Family can still retry.
    }
  }, [isFamilyUser]);

  useEffect(() => { refreshSavedDataSources(); }, [refreshSavedDataSources]);

  // SAVE (task spec §8): first-ever persistence for this Data Source's own
  // canonical identity. Creates ONE new PageBuilder document (the CURRENT
  // in-editor doc, so whatever Family already built stays intact -- task
  // spec §7: "réutiliser la structure déjà présente"), links it via the same
  // atomic upsert every other override in this system uses, then hands the
  // new document to the parent via `notifyLoadedPage` so it becomes "the
  // currently open page" -- a normal Save afterward (task spec §10) PATCHes
  // this SAME `_id`, never creating a second one.
  const handleSaveDataSource = useCallback(async (datasetKey, ds) => {
    if (!isFamilyUser) return;
    const templateKey = resolveCanonicalDataSourceKey(datasetKey);
    const group = templateKey.startsWith("content.") ? "content" : "dataset";
    setDataSourceBusyKey(datasetKey);
    setDataSourceError("");
    try {
      const { token: tok } = isAuthenticated() || {};
      const created = await createPageBuilder({
        doc,
        title: ds?.label || datasetKey,
        pageMode: ds?.pageMode || doc?.pageMode || "static",
      }, tok);
      const result = await upsertPageTemplateOverride(templateKey, {
        group,
        pagebuilderDoc: created._id,
        sourceType: ds?.pageMode || "",
        title: ds?.label || datasetKey,
        createNew: !overridesByCanonicalKey[templateKey],
      }, tok);
      setOverridesByCanonicalKey((prev) => ({ ...prev, [templateKey]: result.override }));
      notifyLoadedPage(created.doc || doc, created._id, created, null);
    } catch (e) {
      // Fail safe (task spec §41): never show Saved/Linked as a success --
      // the card simply stays on "Save" so Family can retry, and the real
      // error message is surfaced instead of being silently swallowed. No
      // half-created duplicate either way: the upsert is atomic, and if the
      // createPageBuilder step itself failed, no override was ever written.
      setDataSourceError(e?.message || String(e));
    } finally {
      setDataSourceBusyKey("");
    }
  }, [isFamilyUser, doc, overridesByCanonicalKey, notifyLoadedPage]);

  // Central resolver (Classic Content preload mission §6/§7/§9/§10/§11/§30):
  // "which JSON tree should Edit HTML Code start from when this Data Source
  // has no custom HTML of its own yet". Exactly the mission's own decision
  // tree --
  //   1. a linked Page/System Template that is itself active+prioritized
  //      with real custom HTML of ITS OWN -> that HTML (`resolveTemplateJsonDoc`,
  //      the SAME resolver useContentPageOverride.js's public render path
  //      uses -- one source of truth, never duplicated -- task spec §7/§30);
  //   2. a linked `contentTemplate.*` System Template with no customization
  //      -> its own hardcoded registry source (CONTENT_TEMPLATES' `tpl.make()`,
  //      via that same resolver);
  //   3. no linked template at all (or a `contentPageTemplate.*` Family
  //      template with no source of its own yet) -> "Classic Content"
  //      (`getClassicContentDoc()`, ContentPageTemplates.js's own
  //      `contentClassique` entry -- the real, single registry definition,
  //      never a second hardcoded copy, task spec §6/§8).
  // NEVER writes anything to the database itself -- purely resolves a JSON
  // tree in memory (task spec §3/§5/§22: "ne pas copier Classic Content en
  // DB juste parce qu'il a servi de starter").
  const resolveDataSourceStarterDoc = useCallback(async (selectedTemplateKey) => {
    if (selectedTemplateKey) {
      try {
        const resolved = await getPageTemplateOverride(selectedTemplateKey);
        if (resolved?.priority) {
          const doc = resolveTemplateJsonDoc({
            pagebuilderDoc: resolved.pagebuilderDoc,
            templateKey: resolved.templateKey,
            group: resolved.group,
          });
          if (doc) return doc;
        }
      } catch (_e) {
        // Network/lookup failure -- fall through to Classic Content rather
        // than surface an error for what is only ever a starter suggestion.
      }
    }
    return getClassicContentDoc();
  }, []);

  // EDIT (task spec §2/§3/§4/§9/§27, extended by the Classic Content preload
  // mission): opens the REAL "</> Edit HTML Code" modal directly
  // (`PageHtmlSourceModal` -- the exact same component/password flow every
  // other PageBuilder page already uses, never a second editor) -- NOT the
  // generic visual Builder. Two cases:
  //   - This Data Source already has its own `pagebuilderDoc` (real custom
  //     HTML saved before) -> fetch that EXACT document, never create one --
  //     the modal loads `content.aboutUs`'s own saved HTML (task spec §2).
  //   - No custom HTML of its own yet -> resolve the STARTER doc above
  //     (linked template's source, else Classic Content -- task spec §1/§9/
  //     §11) and load THAT into the Builder's in-memory `doc`/`boundData`
  //     state so the modal's own `getGeneratedHtml` (buildSiteHtml, reading
  //     THIS live state, never the DB row) produces the real starter HTML
  //     with this Data Source's OWN runtime data already bound (task spec
  //     §19/§20 -- `withContentPageSourceKey` below stamps the dataset key
  //     into the loaded doc's meta so the Builder's existing auto-bind
  //     effect, `selectDatasetForPageMode`, resolves the correct dataset
  //     automatically, exactly as "Lier" already does). A placeholder
  //     `pagebuilderDoc` is still created+linked right away, same as before
  //     -- but its OWN persisted `doc` stays an empty shell (task spec §26:
  //     "Saved" already means only "identity registered", never "customHtml
  //     exists") -- Classic Content/the linked template's markup is NEVER
  //     written to that row merely by opening Edit, only the in-memory
  //     Builder state (and, in turn, `PageHtmlSourceModal`'s own local draft)
  //     reflects it until Family explicitly clicks Save inside the modal.
  const handleEditDataSource = useCallback(async (datasetKey, ds) => {
    if (!isFamilyUser) return;
    const templateKey = resolveCanonicalDataSourceKey(datasetKey);
    const group = templateKey.startsWith("content.") ? "content" : "dataset";
    const saved = overridesByCanonicalKey[templateKey];
    setDataSourceBusyKey(datasetKey);
    setDataSourceError("");
    try {
      const { token: tok } = isAuthenticated() || {};
      let fetched;
      let docToLoad;
      // `hasCustomHtml` (NOT just `pagebuilderDoc`'s mere presence, task spec
      // §26/§27/§28) is the real signal: a bare pointer can already exist
      // from a PREVIOUS Edit that Family opened but never saved (the
      // placeholder created below) -- that placeholder's own persisted `doc`
      // is an empty shell, never Classic Content/the linked template's
      // markup, so re-fetching it verbatim on a second Edit click would
      // regress to a blank editor. Only a row with real saved customization
      // skips straight to loading it as-is.
      if (saved?.hasCustomHtml && saved?.pagebuilderDoc) {
        fetched = await getPageBuilderById(saved.pagebuilderDoc, tok);
        docToLoad = fetched.doc;
      } else {
        const starterDoc = await resolveDataSourceStarterDoc(saved?.selectedTemplateKey);
        if (saved?.pagebuilderDoc) {
          // A placeholder identity already exists (e.g. an earlier Edit that
          // was never saved) -- reuse the SAME document/_id, never create a
          // second one for this canonical key (task spec §4: "1 dataSourceKey
          // = 1 custom HTML document maximum").
          fetched = await getPageBuilderById(saved.pagebuilderDoc, tok);
        } else {
          const created = await createPageBuilder({
            title: ds?.label || datasetKey,
            pageMode: ds?.pageMode || "static",
            doc: { meta: {}, pageMode: ds?.pageMode || "static", pages: [{ id: "p1", children: [] }], comments: [], combinedElements: [] },
          }, tok);
          const result = await upsertPageTemplateOverride(templateKey, {
            group,
            pagebuilderDoc: created._id,
            sourceType: ds?.pageMode || "",
            title: ds?.label || datasetKey,
            createNew: !saved,
          }, tok);
          setOverridesByCanonicalKey((prev) => ({ ...prev, [templateKey]: result.override }));
          fetched = created;
        }
        docToLoad = starterDoc;
      }
      // Stamp this Data Source's own registry key into the loaded doc's meta
      // (same fields `bindDataset`'s own `syncContentPageBindingMeta` writes
      // on a normal "Lier") so the Preview binds THIS Data Source's own
      // runtime data, never whatever was open before (task spec §19/§20:
      // "About" must preview with About's data, "Make Your 1 Step" with its
      // own -- the SAME starter Classic Content HTML either way).
      const stampedDoc = {
        ...docToLoad,
        meta: { ...docToLoad?.meta, contentPageSourceKey: datasetKey },
        pageModeConfig: {
          ...docToLoad?.pageModeConfig,
          settings: { ...docToLoad?.pageModeConfig?.settings, contentPageSourceKey: datasetKey },
        },
      };
      notifyLoadedPage(stampedDoc, fetched._id, { ...fetched, doc: stampedDoc }, null);
      setShowTemplatePicker(false);
      setShowHtmlEditModal(true);
    } catch (e) {
      // Fail safe -- stays on the modal, Family can retry.
      setDataSourceError(e?.message || String(e));
    } finally {
      setDataSourceBusyKey("");
    }
  }, [isFamilyUser, overridesByCanonicalKey, notifyLoadedPage, resolveDataSourceStarterDoc]);

  // DELETE HTML (task spec §13-20): removes ONLY this Data Source's own
  // custom HTML pointer -- reuses the EXISTING `reset-html` endpoint
  // (`resetPageTemplateOverrideHtml`, already built/tested for
  // SystemTemplatesPanel.jsx) rather than a new one, exactly matching task
  // spec §23's preference for "reset ciblé si le record contient aussi
  // assignment/priority". A targeted `$set: { pagebuilderDoc: null }`
  // server-side -- `active`/`priority`/`selectedTemplateKey`/`group`/`title`
  // (the REAL public page's identity AND any linked System Template) are all
  // left completely untouched (task spec §16/§19), and the underlying
  // `pagebuilders` document Family had customized is never deleted either --
  // only this pointer's reference to it is cleared, so it's recoverable.
  // `priority` is deliberately left AS-IS (task spec §17's own "optionally"
  // -- see the mission's final report for why): the resolver already
  // degrades a missing custom HTML to the linked System Template's source
  // (or the original page) automatically, so forcing `priority:false` here
  // would be a redundant, and less reversible, extra write.
  const handleDeleteDataSourceHtml = useCallback(async (datasetKey, ds) => {
    if (!isFamilyUser) return;
    const templateKey = resolveCanonicalDataSourceKey(datasetKey);
    const saved = overridesByCanonicalKey[templateKey];
    if (!saved?.pagebuilderDoc) return;
    // eslint-disable-next-line no-alert -- same confirm() pattern as every
    // other destructive action in this file (Reset link/Reset HTML in
    // SystemTemplatesPanel.jsx) -- task spec §13/§21: never delete on the
    // first click.
    if (!window.confirm(`${elakayAutoText("h_pb_ds_delete_html_confirm_title", "Delete customized HTML?")}\n\n${elakayAutoText("h_pb_ds_delete_html_confirm_body", "This will remove the saved HTML version for")} "${ds?.label || datasetKey}". ${elakayAutoText("h_pb_ds_delete_html_confirm_note", "The original Content Page data will not be deleted.")}`)) {
      return;
    }
    setDataSourceBusyKey(datasetKey);
    setDataSourceError("");
    try {
      const { token: tok } = isAuthenticated() || {};
      const result = await resetPageTemplateOverrideHtml(templateKey, tok);
      // Fail-safe UI sync (task spec §30): the state is updated ONLY from
      // the backend's own returned document, never optimistically -- if the
      // request had failed this line is never reached, so "Customized HTML"
      // never disappears on a failed Delete. `resetHtml`'s response is the
      // raw override document (not run through the batch-list's
      // `hasCustomHtml` computation) -- set it explicitly to false here,
      // since Delete HTML's entire purpose is exactly that transition
      // (task spec §27/§28: the badge/Priority/Delete-HTML controls must
      // reflect "no custom HTML" immediately, no refresh needed).
      setOverridesByCanonicalKey((prev) => ({ ...prev, [templateKey]: { ...result.override, hasCustomHtml: false } }));
    } catch (e) {
      setDataSourceError(e?.message || String(e));
    } finally {
      setDataSourceBusyKey("");
    }
  }, [isFamilyUser, overridesByCanonicalKey]);

  // USE AS PRIORITY (task spec §8/§9/§10/§11): governs whether THIS Data
  // Source's OWN saved HTML (Save/Edit/Delete HTML above) wins over an
  // ALSO-linked Page/System Template, or the original page -- see
  // resolveContentPageTemplate's own STEP 1 comment for the exact matrix.
  // Deliberately NEVER auto-set by a plain Save (task spec §10: "Save
  // signifie enregistrer... Priority signifie publier en priorité -- deux
  // actions séparées") -- only this explicit toggle changes it. Turning it
  // ON also sets this SAME document's `active` to true in the one call (the
  // established "active+priority" truth table, unchanged since Mission H,
  // still requires both) -- so Family never needs a separate, undiscoverable
  // third toggle just to make Priority ON actually take effect; turning it
  // OFF never touches `active` (task spec §32: Active stays independently
  // toggleable, never coupled to Priority).
  const handleToggleDataSourcePriority = useCallback(async (datasetKey) => {
    if (!isFamilyUser) return;
    const templateKey = resolveCanonicalDataSourceKey(datasetKey);
    const saved = overridesByCanonicalKey[templateKey];
    if (!saved?.pagebuilderDoc) return;
    const nextPriority = !saved.priority;
    setDataSourceBusyKey(datasetKey);
    setDataSourceError("");
    try {
      const { token: tok } = isAuthenticated() || {};
      const result = await upsertPageTemplateOverride(templateKey, {
        group: templateKey.startsWith("content.") ? "content" : "dataset",
        priority: nextPriority,
        ...(nextPriority ? { active: true } : {}),
      }, tok);
      // `upsertPageTemplateOverride`'s response is the raw override document,
      // not run through the batch-list's `hasCustomHtml` computation --
      // preserve whatever this card already knew locally (this toggle never
      // changes whether custom HTML exists, only whether it's prioritized)
      // rather than losing it and prematurely hiding Use as Priority/Delete
      // HTML again (task spec §27/§28).
      setOverridesByCanonicalKey((prev) => ({ ...prev, [templateKey]: { ...result.override, hasCustomHtml: prev[templateKey]?.hasCustomHtml } }));
    } catch (e) {
      setDataSourceError(e?.message || String(e));
    } finally {
      setDataSourceBusyKey("");
    }
  }, [isFamilyUser, overridesByCanonicalKey]);

  // Select System Template + Lier -- the CENTRAL, ONE assignment function
  // (task spec §10/§11/§29/§30), the exact same `assignTemplateToPage`
  // SystemTemplatesPanel.jsx's own Page Template dropdown calls, so both UI
  // entry points can never drift into two different behaviors for the same
  // operation. Auto-activates server-side (task spec §11/§31) -- no second
  // click on "Template Active ON" needed.
  const handleLinkSelectedSystemTemplate = useCallback(async (datasetKey, ds) => {
    if (!isFamilyUser || !selectedSystemTemplateKey) return;
    const templateKey = resolveCanonicalDataSourceKey(datasetKey);
    if (!templateKey.startsWith("content.")) {
      // Only a real public Content Page can meaningfully use a Page/System
      // Template (task spec's own answer, point 5) -- an internal/demo
      // dataset binding has no public render to affect.
      setDataSourceError("This Data Source has no real public page to link a template to.");
      return;
    }
    setDataSourceBusyKey(datasetKey);
    setDataSourceError("");
    try {
      const { token: tok } = isAuthenticated() || {};
      const result = await assignTemplateToPage(templateKey, selectedSystemTemplateKey, {
        hasExistingOverride: Boolean(overridesByCanonicalKey[templateKey]),
        group: "content",
        title: ds?.label || datasetKey,
        token: tok,
      });
      // Same preservation as Use as Priority above -- linking a template
      // never changes whether THIS Data Source's own custom HTML exists.
      setOverridesByCanonicalKey((prev) => ({ ...prev, [templateKey]: { ...result.override, hasCustomHtml: prev[templateKey]?.hasCustomHtml } }));
    } catch (e) {
      setDataSourceError(e?.message || String(e));
    } finally {
      setDataSourceBusyKey("");
    }
  }, [isFamilyUser, selectedSystemTemplateKey, overridesByCanonicalKey]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const buildCssBundle = () => {
      const chunks = [];
      const sheets = Array.from(document.styleSheets || []);
      sheets.forEach((sheet) => {
        try {
          const rules = Array.from(sheet.cssRules || []);
          rules.forEach((rule) => chunks.push(rule.cssText));
        } catch (_) {
          // Ignore les feuilles externes non lisibles (CORS)
        }
      });
      setProjectCssBundle(chunks.join("\n"));
    };
    buildCssBundle();
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => buildCssBundle());
    observer.observe(document.head, { childList: true, subtree: true, attributes: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  // ── Boutique tab : charge un template et bascule Aperçu ↔ Builder ───────────
  const handleBoutiqueTemplate = useCallback(async (tpl) => {
    if (storeOnly && tpl.pageMode !== "store") return;
    selectShopSource("default", { ...tpl, title: tpl.label });
    if (activeTemplateKey === tpl.key) {
      // Les pages par défaut restent toujours en lecture seule.
      setShowPreview(true);
    } else {
      // Nouveau template → charger uniquement dans l'aperçu. Priorité à
      // l'override Family éventuel (shopTemplate.<key>) avant le template
      // codé en dur -- voir resolveShopOrProjectTemplateDoc.
      const newDoc = await resolveShopOrProjectTemplateDoc(tpl, "shopTemplate");
      setDoc(newDoc);
      setSelectedIds([]);
      setJsonText(JSON.stringify(newDoc, null, 2));
      selectDatasetForPageMode(tpl.pageMode, newDoc?.meta?.contentPageSourceKey);
      setActiveTemplateKey(tpl.key);
      setShowPreview(true);
      notifyLoadedPage(newDoc, null, null, "default");
    }
  }, [activeTemplateKey, notifyLoadedPage, selectDatasetForPageMode, selectShopSource, storeOnly]);

  useEffect(() => {
    if (!shopSelectionReadOnly) return;
    setShowPreview(true);
    setSelectedIds([]);
    setBodySelected(false);
    setLeftTab("boutique");
  }, [shopSelectionReadOnly]);

  useEffect(() => {
    if (!storeOnly) return;
    setDoc((d) => (d.pageMode === "store" ? d : { ...d, pageMode: "store" }));
  }, [storeOnly, doc?.pageMode]);

  useEffect(() => {
    const { token } = isAuthenticated() || {};
    if (!token) return;
    let cancelled = false;
    getMyShops(token).then((data) => {
      if (!cancelled && !data.error) {
        setMyCreatedShops(data.myCreated    || []);
        setMyBoughtShops(data.myPurchased   || []);
        setEditorTemplates(data.editorsShops || []);
        setDefaultShops(data.defaultShops   || []);
      }
    });
    if (storeOnly) {
      getShopPricing().then((data) => {
        if (!cancelled && data.displayPrice) setShopPricing(data);
      });
    }
    return () => { cancelled = true; };
  }, [storeOnly]);

  const createdShopPages = useMemo(() => {
    const shops = new Map();
    myCreatedShops.forEach((item) => {
      const id = getShopItemId(item);
      if (id) shops.set(id, item);
    });
    myPages
      .filter((item) => item?.pageMode === "store" && !isPurchasedShopItem(item))
      .forEach((item) => {
        const id = getShopItemId(item);
        if (id) shops.set(id, item);
      });
    return Array.from(shops.values());
  }, [myCreatedShops, myPages]);

  const boughtShopPages = useMemo(() => {
    const shops = new Map();
    myBoughtShops.forEach((item) => {
      const id = getShopItemId(item);
      if (id) shops.set(id, item);
    });
    myPages
      .filter((item) => item?.pageMode === "store" && isPurchasedShopItem(item))
      .forEach((item) => {
        const id = getShopItemId(item);
        if (id) shops.set(id, item);
      });
    return Array.from(shops.values());
  }, [myBoughtShops, myPages]);

  const activeHtmlShopId = String(
    shopSelection?.id ||
    savedPage?._id ||
    savedPageId ||
    ""
  );
  const shopRightsEntries = useMemo(() => [
    ...myCreatedShops,
    ...myBoughtShops,
    ...createdShopPages,
    ...boughtShopPages,
  ], [
    boughtShopPages,
    createdShopPages,
    myBoughtShops,
    myCreatedShops,
  ]);
  const activeHtmlRightsEntry = useMemo(() => {
    if (!activeHtmlShopId) return null;
    return shopRightsEntries.find(
      (item) => getShopItemId(item) === activeHtmlShopId
    ) || null;
  }, [activeHtmlShopId, shopRightsEntries]);
  const canUseHtmlCode =
    doc?.pageMode !== "store" ||
    htmlAccessGrantIds.includes(activeHtmlShopId) ||
    getShopDownloadRight(activeHtmlRightsEntry) ||
    getShopDownloadRight(savedPage);
  // "Edit HTML Code" permission -- unrelated to canUseHtmlCode above (which
  // only gates viewing/exporting the READ-ONLY generated HTML export).
  // See pageHtmlAccess.js: Family always allowed, default pages denied for
  // everyone else, owner of a custom page always allowed. Re-checked by the
  // backend on every request regardless of this client-side value.
  const canEditHtmlCode = canEditPageHtml({
    savedPage,
    currentUserId,
    userEmail: infoSource?.userInfo?.email,
  });
  const htmlAccessMessage = activeHtmlShopId
    ? "Achetez le code HTML ou l'option « Tout acheter » pour le copier, l'ouvrir ou le télécharger."
    : "Sauvegardez d'abord cette boutique, puis achetez le droit du code HTML.";
  const resolveShopPurchaseRights = (shop) => {
    const id = String(shop?.id || "");
    if (!id) return { displayRight: false, downloadRight: false };
    const entry = shopRightsEntries.find((item) => getShopItemId(item) === id);
    return {
      displayRight:
        displayAccessGrantIds.includes(id) ||
        getShopDisplayRight(entry),
      downloadRight:
        htmlAccessGrantIds.includes(id) ||
        getShopDownloadRight(entry),
    };
  };
  const shopPurchaseTargetRights = resolveShopPurchaseRights(shopPurchaseTarget);
  const shopPurchaseHasDisplay = shopPurchaseTargetRights.displayRight;
  const shopPurchaseHasHtml = shopPurchaseTargetRights.downloadRight;
  const shopPurchaseHasAnyRight = shopPurchaseHasDisplay || shopPurchaseHasHtml;

  const otherSavedPages = useMemo(
    () => myPages.filter((item) => item?.pageMode !== "store"),
    [myPages]
  );

  // Auto-bind au dataset live dès qu'il arrive (remplace le mock du même mode)
  useEffect(() => {
    const mode = doc?.pageMode;
    if (!liveDatasets || !mode) return;
    const currentKey = getBoundDatasetKey(boundData);
    const liveEntry =
      (currentKey && liveDatasets[currentKey]?.pageMode === mode
        ? [currentKey, liveDatasets[currentKey]]
        : null) ||
      Object.entries(liveDatasets).find(([, dataset]) => dataset?.pageMode === mode);
    if (!liveEntry) return;

    const [key, dataset] = liveEntry;
    if (boundData?.datasetKey === key && boundData?.isLive) return;
    if (boundData?.pageMode === mode && boundData?.isLive) return;
    bindDataset(key, dataset, { resetExplorer: false });
  }, [bindDataset, boundData, doc?.pageMode, liveDatasets]);

  // Auto-bind fallback : si aucun dataset compatible n'est lié, sélectionner celui du pageMode courant.
  useEffect(() => {
    const mode = doc?.pageMode;
    if (!mode) return;
    if (boundData?.pageMode === mode) return;
    const preferredKey =
      doc?.meta?.contentPageSourceKey ||
      doc?.pageModeConfig?.settings?.contentPageSourceKey ||
      "";
    selectDatasetForPageMode(mode, preferredKey, { resetExplorer: false });
  }, [
    boundData?.pageMode,
    doc?.meta?.contentPageSourceKey,
    doc?.pageMode,
    doc?.pageModeConfig?.settings?.contentPageSourceKey,
    selectDatasetForPageMode,
  ]);

  useEffect(() => {
    if (doc?.pageMode !== "contentPage") return;
    const datasetKey = getBoundDatasetKey(boundData);
    if (!datasetKey) {
      syncContentPageBindingMeta(null, null);
      return;
    }
    const dataset = allDatasets[datasetKey] || boundData;
    if (dataset?.pageMode === "contentPage") {
      syncContentPageBindingMeta(datasetKey, dataset);
    }
  }, [allDatasets, boundData?.datasetKey, boundData?.key, boundData?.pageMode, doc?.pageMode, syncContentPageBindingMeta]);

  // Notifie le parent (index.jsx) à chaque changement de doc
  useEffect(() => {
    onDocChange?.(doc);
  }, [doc, onDocChange]);

  // ── Auto-save : garde docRef à jour sans déclencher l'intervalle ──────────
  useEffect(() => { docRef.current = doc; }, [doc]);

  // Shared by both mechanisms below -- never duplicated (mission: one save
  // path, not two copies that could drift).
  const persistDraftToLocalStorage = useCallback((d) => {
    if (!d || !Array.isArray(d.pages) || d.pages.length === 0) return;
    if (d === lastSavedDocRef.current) return;
    try {
      const meta = { savedAt: Date.now() };
      localStorage.setItem(draftStorageKeys.draftKey, JSON.stringify(d));
      localStorage.setItem(draftStorageKeys.metaKey,  JSON.stringify(meta));
      setAutoSaveMeta(meta);
    } catch (e) {
      console.warn("[PageBuilder] Auto-save localStorage échoué :", e.message);
    }
  }, [draftStorageKeys.draftKey, draftStorageKeys.metaKey]);

  // ── Auto-save : toutes les 10 min (filet de sécurité, quelle que soit
  //    l'activité) → localStorage ───────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => persistDraftToLocalStorage(docRef.current), AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [persistDraftToLocalStorage]);

  // ── Auto-save : 30s après la dernière modification d'un champ ────────────
  useEffect(() => {
    const id = setTimeout(() => persistDraftToLocalStorage(doc), AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [doc, persistDraftToLocalStorage]);

  // ── Click-outside : ferme le dropdown l × c ───────────────────────────────
  useEffect(() => {
    if (!showLxCDropdown) return;
    const handler = (e) => {
      if (lxcDropdownRef.current && !lxcDropdownRef.current.contains(e.target)) {
        setShowLxCDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLxCDropdown]);

  // --- Pages : la page courante est dérivée de doc.currentPageId ---
  const pages = useMemo(() => doc.pages || [], [doc.pages]);
  const currentPageId = doc.currentPageId || pages[0]?.id;
  const page = pages.find((p) => p.id === currentPageId) || pages[0];
  const activePageLayoutMode = getPageLayoutMode(page, layoutDevice);

  useEffect(() => {
    setFreeMode(activePageLayoutMode === "free");
  }, [activePageLayoutMode]);

  // Écrit dans la page courante uniquement.
  // Point d'intégration UNIQUE avec l'historique undo/redo : si un geste
  // souris est en cours (drag de déplacement/redimensionnement — voir
  // history.beginGesture/endGesture dans FreeCanvas), l'appel s'accumule
  // dans le geste sans créer d'entrée d'historique ; sinon (clic, clavier,
  // action ponctuelle) chaque appel devient sa propre entrée. Comme la
  // quasi-totalité des mutations de l'arbre passent par setChildren, tous
  // les appelants (delete, duplicate, reorder, insert, nudge, resize…)
  // bénéficient de l'undo/redo sans modification individuelle.
  const setChildren = (children) => {
    const updater = (d) => ({
      ...d,
      pages: d.pages.map((p) => (p.id === (d.currentPageId || d.pages[0]?.id) ? { ...p, children } : p)),
    });
    if (history.isGestureActive()) history.updateGesture(updater);
    else history.commit(updater);
  };
  // Met à jour une propriété de la page courante (ex. style)
  const setPageProp = (patch) => setDoc((d) => ({
    ...d,
    pages: d.pages.map((p) => (p.id === (d.currentPageId || d.pages[0]?.id) ? { ...p, ...patch } : p)),
  }));

  // Gestion des pages
  const switchPage = useCallback((id) => { setDoc((d) => ({ ...d, currentPageId: id })); setSelectedIds([]); }, []);
  // Met à jour le mode de page par défaut du doc (bloqué si lockPageMode est true)
  const setPageMode = useCallback((mode) => {
    if (lockPageMode) return;
    setDoc((d) => ({ ...d, pageMode: mode }));
    selectDatasetForPageMode(mode);
  }, [lockPageMode, selectDatasetForPageMode]);

  const selectContentPageDataset = useCallback((datasetKey) => {
    if (!datasetKey) {
      setBoundData(null);
      syncContentPageBindingMeta(null, null);
      setRightTab("data");
      return;
    }
    const foundEntry =
      contentPageDatasetEntries.find(([key, dataset]) => (
        key === datasetKey ||
        String(dataset?.data?._id || "") === String(datasetKey) ||
        String(dataset?.data?.pageId || "") === String(datasetKey)
      ));
    const resolvedDatasetKey = foundEntry?.[0] || datasetKey;
    const dataset = foundEntry?.[1] || allDatasets[datasetKey];
    if (!dataset) return;
    if (dataset.pageMode === "contentPage") {
      setDoc((current) => (
        current?.pageMode === "contentPage" ? current : { ...current, pageMode: "contentPage" }
      ));
    }
    bindDataset(resolvedDatasetKey, dataset);
    setRightTab("data");
  }, [allDatasets, bindDataset, contentPageDatasetEntries, syncContentPageBindingMeta]);

  const selectedContentPageDatasetKey =
    doc?.pageMode === "contentPage" ? getBoundDatasetKey(boundData) : "";
  const selectedContentPageSwitchValue =
    doc?.pageMode === "contentPage" ? (boundData?.data?._id || boundData?.data?.pageId || selectedContentPageDatasetKey) : "";

  // Si la prop pageMode change depuis l'app hôte, on synchronise le doc
  useEffect(() => {
    if (initialPageMode && PAGE_MODES.find((m) => m.id === initialPageMode)) {
      setDoc((d) => (d.pageMode === initialPageMode ? d : { ...d, pageMode: initialPageMode }));
      selectDatasetForPageMode(initialPageMode, "", { resetExplorer: false });
    }
  }, [initialPageMode, selectDatasetForPageMode]);
  const addPage = useCallback(() => {
    const id = uid();
    const n = (doc.pages?.length || 0) + 1;
    setDoc((d) => ({ ...d, currentPageId: id, pages: [...d.pages, { id, name: `Page ${n}`, style: { ...PAGE_STYLE }, children: [] }] }));
    setSelectedIds([]);
    return id; // used by PageHtmlSourceModal's "+ Add new page" to select it right away
  }, [doc]);
  const renamePage = useCallback((id, name) => setDoc((d) => ({ ...d, pages: d.pages.map((p) => (p.id === id ? { ...p, name } : p)) })), []);
  const deletePage = useCallback((id) => {
    setDoc((d) => {
      if (d.pages.length <= 1) return d; // on garde au moins une page
      const remaining = d.pages.filter((p) => p.id !== id);
      const nextCurrent = d.currentPageId === id ? remaining[0].id : d.currentPageId;
      return { ...d, pages: remaining, currentPageId: nextCurrent };
    });
    setSelectedIds([]);
  }, []);
  const duplicatePage = useCallback((id) => {
    setDoc((d) => {
      const src = d.pages.find((p) => p.id === id);
      if (!src) return d;
      const clone = JSON.parse(JSON.stringify(src));
      const reId = (nd) => { nd.id = uid(); if (nd.items) nd.items.forEach((it) => (it.id = uid())); nd.children?.forEach(reId); };
      const newId = uid();
      clone.id = newId; clone.name = src.name + " (copie)"; clone.children.forEach(reId);
      const idx = d.pages.findIndex((p) => p.id === id);
      const pagesCopy = [...d.pages]; pagesCopy.splice(idx + 1, 0, clone);
      return { ...d, pages: pagesCopy, currentPageId: newId };
    });
    setSelectedIds([]);
  }, []);

  // L'élément "principal" = le dernier sélectionné (sert à l'édition détaillée mono-élément)
  const primaryId = selectedIds.length ? selectedIds[selectedIds.length - 1] : null;
  const selected = useMemo(() => (primaryId ? findInTree(page.children, primaryId) : null), [page, primaryId]);

  // Auto-switch vers l'onglet "shopLive" quand un élément boutique live est sélectionné
  useEffect(() => {
    if (!selected) return;
    const def = ELEMENT_TYPES[selected.type];
    if (def?.group === "shopLive" || def?.group === "shopHeader") {
      if (panelTabSides["shopLive"] === "right") setRightTab("shopLive");
      else if (panelTabSides["shopLive"] === "left") setLeftTab("shopLive");
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedNodes = useMemo(
    () => selectedIds.map((id) => findInTree(page.children, id)).filter(Boolean),
    [page, selectedIds]
  );
  const selectedElementTypes = useMemo(() => {
    const direct = new Set();
    const nested = new Set();
    const visitChildren = (children) => {
      (children || []).forEach((child) => {
        if (child?.type) nested.add(child.type);
        visitChildren(child?.children);
      });
    };

    selectedNodes.forEach((node) => {
      if (node?.type) direct.add(node.type);
      visitChildren(node?.children);
    });

    return { direct, nested, active: new Set([...direct, ...nested]) };
  }, [selectedNodes]);

  useEffect(() => {
    if (selectedElementTypes.active.size === 0) return;
    const groupsToOpen = new Set();
    selectedElementTypes.active.forEach((type) => {
      const group = ELEMENT_TYPES[type]?.group;
      if (group) groupsToOpen.add(group);
      if (group === "form") groupsToOpen.add("htmlForm");
    });
    if (groupsToOpen.size === 0) return;
    setOpenGroups((current) => {
      let changed = false;
      const next = { ...current };
      groupsToOpen.forEach((group) => {
        if (!next[group]) {
          next[group] = true;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [selectedElementTypes]);

  // Navigation depuis un lien dans l'aperçu : si la cible existe, on change de page
  // Keyframes des animations personnalisées de toutes les pages (pour l'aperçu)
  const customKeyframes = useMemo(() => {
    const out = [];
    const walk = (n) => {
      if (n.animation === "custom") out.push(buildCustomAnim(n).keyframe);
      n.children?.forEach(walk);
    };
    (doc.pages || []).forEach((p) => (p.children || []).forEach(walk));
    return out.join("\n");
  }, [doc]);

  const syncDetachedCenterCss = useCallback((targetWin) => {
    if (typeof document === "undefined" || !targetWin || targetWin.closed) return;
    const targetDoc = targetWin.document;
    targetDoc.querySelectorAll('[data-pb-detached-clone="1"]').forEach((el) => el.remove());
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
      const clone = node.cloneNode(true);
      clone.setAttribute("data-pb-detached-clone", "1");
      targetDoc.head.appendChild(clone);
    });

    let runtimeStyle = targetDoc.getElementById("pb-detached-runtime-style");
    if (!runtimeStyle) {
      runtimeStyle = targetDoc.createElement("style");
      runtimeStyle.id = "pb-detached-runtime-style";
      targetDoc.head.appendChild(runtimeStyle);
    }
    runtimeStyle.textContent = `@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;700&display=swap');
${ANIMATION_KEYFRAMES}
${customKeyframes}
${projectCssBundle}
*{box-sizing:border-box;}
html,body,#pb-detached-root,#pb-detached-panel-root{height:100%;}
body{margin:0;background:#f6f4fb;font-family:'DM Sans',system-ui,sans-serif;color:#1a1a2e;}
.pb-detached-tab-panel{position:static!important;inset:auto!important;transform:none!important;width:100%!important;height:100%!important;box-shadow:none!important;}
::-webkit-scrollbar{width:8px;height:8px;}
::-webkit-scrollbar-thumb{background:#d6cfec;border-radius:8px;}`;
  }, [customKeyframes, projectCssBundle]);

  const closeDetachedCenter = useCallback(() => {
    const win = detachedWindowRef.current;
    if (win && !win.closed) win.close();
    detachedWindowRef.current = null;
    detachedRootRef.current = null;
    setCenterDetached(false);
  }, []);

  const openDetachedCenter = useCallback(() => {
    if (typeof window === "undefined") return;
    const existing = detachedWindowRef.current;
    if (existing && !existing.closed) {
      existing.focus();
      setCenterDetached(true);
      return;
    }
    const win = window.open(
      "",
      "PageBuilderCenterDetached",
      "popup=yes,width=1460,height=920,left=120,top=80,resizable=yes,scrollbars=yes"
    );
    if (!win) {
      alert(elakayAutoText("h_7c7d75978d08cf", "Veuillez autoriser les popups pour détacher le panneau central."));
      return;
    }
    win.document.title = "Page Builder — Panneau central";
    win.document.body.innerHTML = "";
    win.document.body.style.margin = "0";
    win.document.body.style.background = "#f6f4fb";
    syncDetachedCenterCss(win);
    const root = win.document.createElement("div");
    root.id = "pb-detached-root";
    win.document.body.appendChild(root);
    win.addEventListener("beforeunload", () => {
      detachedWindowRef.current = null;
      detachedRootRef.current = null;
      setCenterDetached(false);
    });
    detachedWindowRef.current = win;
    detachedRootRef.current = root;
    setCenterDetached(true);
    win.focus();
  }, [syncDetachedCenterCss]);

  const toggleDetachedCenter = useCallback(() => {
    if (centerDetached) closeDetachedCenter();
    else openDetachedCenter();
  }, [centerDetached, closeDetachedCenter, openDetachedCenter]);

  const closeDetachedPanelTab = useCallback(() => {
    const win = detachedPanelWindowRef.current;
    if (win && !win.closed) win.close();
    detachedPanelWindowRef.current = null;
    detachedPanelRootRef.current = null;
    setDetachedPanelTab(null);
  }, []);

  const openDetachedPanelTab = useCallback((side, key, label) => {
    if (typeof window === "undefined" || !side || !key) return;
    const existing = detachedPanelWindowRef.current;
    if (existing && !existing.closed) existing.close();

    const win = window.open(
      "",
      "PageBuilderPanelTabDetached",
      "popup=yes,width=520,height=900,left=180,top=70,resizable=yes,scrollbars=yes"
    );
    if (!win) {
      alert(elakayAutoText("h_32b249fc87192c", "Veuillez autoriser les popups pour détacher cet onglet."));
      return;
    }

    win.document.title = `Page Builder — ${label || key}`;
    win.document.body.innerHTML = "";
    win.document.body.style.margin = "0";
    win.document.body.style.background = "#f6f4fb";
    syncDetachedCenterCss(win);
    const root = win.document.createElement("div");
    root.id = "pb-detached-panel-root";
    win.document.body.appendChild(root);
    win.addEventListener("beforeunload", () => {
      detachedPanelWindowRef.current = null;
      detachedPanelRootRef.current = null;
      setDetachedPanelTab(null);
    });
    detachedPanelWindowRef.current = win;
    detachedPanelRootRef.current = root;
    setDetachedPanelTab({ side, key, label: label || key });
    win.focus();
  }, [syncDetachedCenterCss]);

  useEffect(() => {
    if (!centerDetached) return;
    const win = detachedWindowRef.current;
    if (win && !win.closed) syncDetachedCenterCss(win);
  }, [centerDetached, customKeyframes, syncDetachedCenterCss]);

  useEffect(() => {
    if (!centerDetached || typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => {
      const win = detachedWindowRef.current;
      if (win && !win.closed) syncDetachedCenterCss(win);
    });
    obs.observe(document.head, { childList: true, subtree: true, attributes: true, characterData: true });
    return () => obs.disconnect();
  }, [centerDetached, syncDetachedCenterCss]);

  useEffect(() => {
    if (!detachedPanelTab) return;
    const win = detachedPanelWindowRef.current;
    if (win && !win.closed) syncDetachedCenterCss(win);
  }, [detachedPanelTab, customKeyframes, syncDetachedCenterCss]);

  useEffect(() => {
    if (!detachedPanelTab || typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => {
      const win = detachedPanelWindowRef.current;
      if (win && !win.closed) syncDetachedCenterCss(win);
    });
    obs.observe(document.head, { childList: true, subtree: true, attributes: true, characterData: true });
    return () => obs.disconnect();
  }, [detachedPanelTab, syncDetachedCenterCss]);

  useEffect(() => {
    return () => {
      const win = detachedWindowRef.current;
      if (win && !win.closed) win.close();
      const panelWin = detachedPanelWindowRef.current;
      if (panelWin && !panelWin.closed) panelWin.close();
    };
  }, []);

  const navigateToPage = useCallback((target) => {
    const t = pages.find((p) => p.id === target || p.name === target);
    if (t) switchPage(t.id);
  }, [pages, switchPage]);

  // Sélection : clic simple = remplace ; Maj+clic = ajoute/retire ; null = tout désélectionner
  const handleSelect = useCallback((id, additive = false) => {
    editingJson.current = false;
    if (id == null) { setSelectedIds([]); return; }
    setBodySelected(false);
    setSelectedIds((prev) => {
      if (additive) return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      return [id];
    });
    // Affiche le panneau propriétés de l'élément sélectionné
    if (panelTabSides.props === "left") {
      setLeftTab("props");
      setShowLeftPanel(true);
    } else {
      setRightTab("props");
      setShowRightPanel(true);
    }
  }, [panelTabSides, setRightTab, setLeftTab, setShowLeftPanel, setShowRightPanel]);

  const handleBodySelect = useCallback(() => {
    setSelectedIds([]);
    setBodySelected(true);
    setShowRightPanel(true);
  }, [setShowRightPanel]);

  const insertElement = useCallback((el) => {
    const enriched = ensureConsoleProps(el);
    if (selected && isChildTypeAllowed(ELEMENT_TYPES[selected.type], enriched.type)) {
      setChildren(insertIntoTree(page.children, selected.id, enriched));
    } else {
      setChildren([...page.children, enriched]);
    }
    setSelectedIds([enriched.id]);
    setShowRightPanel(true);
  }, [selected, page.children, setShowRightPanel]);

  const addElement = useCallback((type) => insertElement(makeElement(type)), [insertElement]);
  const addCustom = useCallback((key) => insertElement(CUSTOM_COMPONENTS[key].build()), [insertElement]);

  // ── Insère une mise en page l (lignes) × c (colonnes) ─────────────────────
  //   l=1, c>1 → row avec c panneaux côte-à-côte
  //   l>1, c=1 → stack avec l panneaux empilés
  //   l>1, c>1 → grid CSS l×c
  const insertLxC = useCallback((l, c) => {
    const cell = () => ({
      id: uid(), type: "container",
      style: { flex: 1, padding: "20px", background: "#fff", borderRadius: "12px", border: "1px solid #ece9f5", minWidth: "60px" },
      children: [],
    });
    let el;
    if (l === 1) {
      el = {
        id: uid(), type: "row",
        style: { display: "flex", gap: "16px", flexWrap: "wrap", width: "100%" },
        children: Array.from({ length: c }, cell),
      };
    } else if (c === 1) {
      el = {
        id: uid(), type: "stack",
        style: { display: "flex", flexDirection: "column", gap: "16px", width: "100%" },
        children: Array.from({ length: l }, cell),
      };
    } else {
      el = {
        id: uid(), type: "grid",
        style: { display: "grid", gridTemplateColumns: `repeat(${c}, 1fr)`, gap: "16px", width: "100%" },
        options: { columns: c },
        children: Array.from({ length: l * c }, cell),
      };
    }
    insertElement(el);
  }, [insertElement]);

  // ── Commentaires d'éléments ────────────────────────────────────────────────
  const addComment = useCallback(() => {
    if (!selected || !newComment.trim()) return;
    const c = {
      id: uid(),
      elementId: selected.id,
      elementType: ELEMENT_TYPES[selected.type]?.label || selected.type,
      text: newComment.trim(),
      resolved: false,
      createdAt: Date.now(),
    };
    setElementComments((prev) => [c, ...prev]);
    setNewComment("");
    setLeftTab("comments");
  }, [selected, newComment, setElementComments]);

  // ── Éléments combinés ─────────────────────────────────────────────────────
  const [newCombinePrice, setNewCombinePrice] = useState(0);

  const saveCombinedElement = useCallback(() => {
    if (!newCombineName.trim() || selectedIds.length === 0) return;
    const nodes = page.children.filter((n) => selectedIds.includes(n.id));
    if (nodes.length === 0) return;
    const ce = {
      id: uid(),
      name:      newCombineName.trim(),
      price:     Math.max(0, Number(newCombinePrice) || 0), // ← prix de l'élément combiné
      description: `${nodes.length} nœud(s) · page "${page.name}"`,
      pageMode:  doc?.pageMode || "static",
      nodes,
      createdAt: Date.now(),
    };
    setCombinedElements((prev) => [ce, ...prev]);
    setNewCombineName("");
    setNewCombinePrice(0);
  }, [newCombineName, newCombinePrice, selectedIds, page, doc, setCombinedElements]);

  const insertCombinedElement = useCallback((ce) => {
    // Marque le nœud racine avec _combineRef pour retrouver le prix à la sauvegarde
    const cloneNode = (n, isRoot = false) => ({
      ...n,
      id: uid(),
      ...(isRoot && ce.price > 0 ? { _combineRef: ce.id, _combineName: ce.name, _combinePrice: ce.price } : {}),
      children: Array.isArray(n.children) ? n.children.map((c) => cloneNode(c)) : undefined,
    });
    for (const node of ce.nodes) {
      insertElement(cloneNode(node, true));
    }
  }, [insertElement]);

  // Calcule le prix automatique depuis les éléments combinés présents dans la page
  const calcCombinePrice = useCallback(() => {
    const seen = new Set();
    let total = 0;
    const walk = (nodes) => {
      for (const n of nodes || []) {
        if (n._combineRef && n._combinePrice > 0 && !seen.has(n._combineRef)) {
          seen.add(n._combineRef);
          total += Number(n._combinePrice);
        }
        if (Array.isArray(n.children)) walk(n.children);
      }
    };
    pages.forEach((p) => walk(p.children || []));
    // Retourne { total, items: [{id, name, price}] }
    const items = [];
    const walk2 = (nodes) => {
      for (const n of nodes || []) {
        if (n._combineRef && n._combinePrice > 0) {
          if (!items.find((x) => x.id === n._combineRef)) {
            items.push({ id: n._combineRef, name: n._combineName || n._combineRef, price: Number(n._combinePrice) });
          }
        }
        if (Array.isArray(n.children)) walk2(n.children);
      }
    };
    pages.forEach((p) => walk2(p.children || []));
    return { total, items };
  }, [pages]);

  const updateElement = useCallback((updated) => setChildren(updateTree(page.children, updated.id, () => updated)), [page.children]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveNodeDoc = useCallback((nodeId, patch) => {
    setChildren(updateTree(page.children, nodeId, (n) => ({ ...n, ...patch })));
  }, [page.children]); // eslint-disable-line react-hooks/exhaustive-deps

  // Met à jour n'importe quel nœud par ID (pour le panneau Toggle — membres du groupe)
  const updateNodePatch = useCallback((nodeId, patch) => {
    setChildren(updateTree(page.children, nodeId, (n) => ({ ...n, ...patch })));
  }, [page.children]); // eslint-disable-line react-hooks/exhaustive-deps

  // Met à jour plusieurs nœuds en une seule transaction (Auto-configure Toggle)
  const bulkUpdateNodes = useCallback((updates) => {
    setDoc((d) => ({
      ...d,
      pages: d.pages.map((p) => {
        if (p.id !== (d.currentPageId || d.pages[0]?.id)) return p;
        let children = p.children;
        for (const { nodeId, patch } of updates) {
          children = updateTree(children, nodeId, (n) => ({ ...n, ...patch }));
        }
        return { ...p, children };
      }),
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Applique une transformation de style à TOUS les éléments sélectionnés
  // NB: passe par setChildren (comme deleteElement juste en dessous) plutôt
  // que par un setDoc(d => ...) manuel — corrige un bug préexistant où ce
  // code lisait/écrivait `d.page.children` alors que le doc n'a jamais eu de
  // champ `page` (c'est `d.pages[]`, voir setChildren) : ce bug faisait
  // planter systématiquement le nudge clavier et le drag groupé multi-
  // sélection. Bénéficie au passage de l'undo/redo géré par setChildren.
  const updateSelectedStyle = useCallback((patch) => {
    let children = page.children;
    for (const id of selectedIds) {
      children = updateTree(children, id, (n) => ({ ...n, style: { ...n.style, ...patch } }));
    }
    setChildren(children);
  }, [selectedIds, page.children]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteElement = useCallback((id) => { setChildren(removeFromTree(page.children, id)); setSelectedIds((p) => p.filter((x) => x !== id)); }, [page.children]); // eslint-disable-line react-hooks/exhaustive-deps
  const deleteSelected = useCallback(() => {
    let children = page.children;
    for (const id of selectedIds) children = removeFromTree(children, id);
    setChildren(children);
    setSelectedIds([]);
  }, [selectedIds, page.children]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateElement = useCallback(() => {
    if (!selected) return;
    const clone = JSON.parse(JSON.stringify(selected));
    const reId = (n) => { n.id = uid(); if (n.items) n.items.forEach((it) => (it.id = uid())); n.children?.forEach(reId); };
    reId(clone);
    if (freeMode) {
      const p = getFreeLayout(clone, layoutDevice);
      Object.assign(clone, withFreeLayout(clone, layoutDevice, { x: p.x + 20, y: p.y + 20, z: p.z + 1 }));
    }
    setChildren([...page.children, clone]); setSelectedIds([clone.id]);
  }, [page.children, selected, freeMode, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps
  const moveElement = useCallback((id, dir) => setChildren(moveInTree(page.children, id, dir)), [page.children]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Positionnement libre : position absolue { x, y } sur chaque élément racine ---
  const setPosition = useCallback((id, x, y) => {
    setChildren(updateTree(page.children, id, (n) => withFreeLayout(n, layoutDevice, { x, y, unit: "px" })));
  }, [page.children, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bascule en position libre EN CONSERVANT la position initiale de chaque élément.
  // On mesure le rectangle réel de chaque élément de premier niveau dans le flux normal,
  // puis on l'écrit dans node.pos (seulement pour ceux qui n'ont pas encore de pos).
  const toggleFreeMode = useCallback(() => {
    if (freeMode) {
      setPageProp({ layoutMode: { ...(page.layoutMode || {}), [layoutDevice]: "flow" } });
      setFreeMode(false);
      return;
    }
    const container = normalCanvasRef.current;
    if (container) {
      const cRect = container.getBoundingClientRect();
      const measured = {};
      container.querySelectorAll(":scope > [data-pbid]").forEach((wrap) => {
        const id = wrap.getAttribute("data-pbid");
        const el = wrap.firstElementChild || wrap;
        const r = el.getBoundingClientRect();
        measured[id] = { x: Math.round(r.left - cRect.left), y: Math.round(r.top - cRect.top), unit: "px" };
      });
      // Initialise only this breakpoint. Editing Mobile/Tablet never mutates
      // Desktop, and historical `pos` remains the Desktop fallback.
      setChildren(page.children.map((n) => (
        n.freeLayout?.[layoutDevice] || (layoutDevice === "desktop" && n.pos) || !measured[n.id]
          ? n
          : withFreeLayout(n, layoutDevice, measured[n.id])
      )));
    }
    setPageProp({ layoutMode: { ...(page.layoutMode || {}), [layoutDevice]: "free" } });
    setFreeMode(true);
  }, [freeMode, page, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps
  const nudge = useCallback((id, dx, dy) => {
    setChildren(updateTree(page.children, id, (n) => {
      const p = getFreeLayout(n, layoutDevice);
      return withFreeLayout(n, layoutDevice, { x: p.x + dx, y: p.y + dy });
    }));
  }, [page.children, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redimensionnement à la souris : applique width/height (et position si poignée haut/gauche),
  // dans le style de l'appareil courant (style desktop ou mobileStyle).
  const resizeElement = useCallback((id, patch) => {
    setChildren(updateTree(page.children, id, (n) => {
      const next = { ...n };
      const styleKey = device === "mobile" ? "mobileStyle" : "style";
      const stylePatch = {};
      if (patch.width != null) stylePatch.width = `${Math.max(20, Math.round(patch.width))}px`;
      if (patch.height != null) stylePatch.height = `${Math.max(20, Math.round(patch.height))}px`;
      next[styleKey] = { ...(n[styleKey] || {}), ...stylePatch };
      // Le patch x/y (poignées nord/ouest) ne s'applique qu'aux éléments déjà
      // positionnés librement (n.pos existe) — un élément du flux normal n'a
      // pas de position absolue à corriger, seules sa largeur/hauteur changent.
      if (patch.x != null || patch.y != null) {
        const p = getFreeLayout(n, layoutDevice);
        return withFreeLayout(next, layoutDevice, { x: patch.x != null ? patch.x : p.x, y: patch.y != null ? patch.y : p.y, unit: "px" });
      }
      return next;
    }));
  }, [page.children, device, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Déplace TOUS les éléments sélectionnés ensemble (drag groupé souris, ET
  // nudge clavier multi-sélection — même bug `d.page` corrigé qu'au-dessus).
  const nudgeSelected = useCallback((dx, dy) => {
    let children = page.children;
    for (const id of selectedIds) {
      children = updateTree(children, id, (n) => {
        const p = getFreeLayout(n, layoutDevice);
        return withFreeLayout(n, layoutDevice, { x: p.x + dx, y: p.y + dy });
      });
    }
    setChildren(children);
  }, [selectedIds, page.children, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveSelectedLayer = useCallback((direction) => {
    if (!selected) return;
    const z = nextLayer(page.children, layoutDevice, direction);
    setChildren(updateTree(page.children, selected.id, (node) => withFreeLayout(node, layoutDevice, { z })));
  }, [selected, page.children, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Insérer un élément dans un autre élément (reparenting) ---------------
  // Déplace un nœud EXISTANT (pas un nouvel élément — voir insertElement pour
  // ça) vers un autre conteneur, en respectant : les cycles (moveNodeToParent
  // refuse déjà A → descendant-de-A) et la compatibilité de type
  // (allowedChildTypes, ex. le cadre produit n'accepte qu'une liste curatée).
  // targetParentId = ROOT_PARENT_ID pour "sortir" au niveau racine de la page.
  const reparentElement = useCallback((nodeId, targetParentId, index) => {
    if (targetParentId !== ROOT_PARENT_ID) {
      const targetNode = findInTree(page.children, targetParentId);
      const movedNode = findInTree(page.children, nodeId);
      if (!targetNode || !movedNode || !isChildTypeAllowed(ELEMENT_TYPES[targetNode.type], movedNode.type)) return;
    }
    setChildren(moveNodeToParent(page.children, nodeId, targetParentId, index));
  }, [page.children]); // eslint-disable-line react-hooks/exhaustive-deps

  // Équivalents clavier du glisser-déposer de reparenting (Ctrl/Cmd+Maj+Flèche
  // Gauche/Droite) — outdent : remonte l'élément sélectionné au niveau de son
  // parent actuel ; indent : le déplace dans le conteneur frère précédent.
  const outdentSelected = useCallback(() => {
    if (!selected) return;
    const parent = findParentInTree(page.children, selected.id);
    if (!parent) return; // déjà à la racine
    const grandParent = findParentInTree(page.children, parent.id);
    const grandParentId = grandParent ? grandParent.id : ROOT_PARENT_ID;
    const siblings = grandParent ? (grandParent.children || []) : page.children;
    const parentIndex = siblings.findIndex((n) => n.id === parent.id);
    reparentElement(selected.id, grandParentId, parentIndex + 1);
  }, [selected, page.children, reparentElement]); // eslint-disable-line react-hooks/exhaustive-deps

  const indentSelected = useCallback(() => {
    if (!selected) return;
    const parent = findParentInTree(page.children, selected.id);
    const siblings = parent ? (parent.children || []) : page.children;
    const idx = siblings.findIndex((n) => n.id === selected.id);
    const prevSibling = idx > 0 ? siblings[idx - 1] : null;
    if (!prevSibling) return;
    reparentElement(selected.id, prevSibling.id, null);
  }, [selected, page.children, reparentElement]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Copier / coller (presse-papiers interne, pas le presse-papiers OS) ---
  const clipboardRef = useRef(null);
  const copySelected = useCallback(() => {
    if (!selected) return;
    clipboardRef.current = JSON.parse(JSON.stringify(selected));
  }, [selected]);
  const pasteClipboard = useCallback(() => {
    if (!clipboardRef.current) return;
    const clone = JSON.parse(JSON.stringify(clipboardRef.current));
    const reId = (n) => { n.id = uid(); if (n.items) n.items.forEach((it) => (it.id = uid())); n.children?.forEach(reId); };
    reId(clone);
    if (freeMode) {
      const p = getFreeLayout(clone, layoutDevice);
      Object.assign(clone, withFreeLayout(clone, layoutDevice, { x: p.x + 20, y: p.y + 20, z: p.z + 1 }));
    }
    if (selected && isChildTypeAllowed(ELEMENT_TYPES[selected.type], clone.type)) {
      setChildren(insertIntoTree(page.children, selected.id, clone));
    } else {
      setChildren([...page.children, clone]);
    }
    setSelectedIds([clone.id]);
  }, [selected, page.children, freeMode, layoutDevice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redimensionnement clavier : lit la taille réelle actuellement rendue
  // (plutôt que de re-parser le CSS, qui peut être en %, auto, etc.) pour
  // pouvoir lui appliquer un delta fiable en pixels.
  const nudgeResizeSelected = useCallback((dw, dh) => {
    if (!selected) return;
    const wrap = document.querySelector(`[data-pb-node-id="${selected.id}"]`);
    const el = wrap ? (wrap.firstElementChild || wrap) : null;
    const rect = el ? el.getBoundingClientRect() : null;
    const curW = rect?.width || parseFloat(selected.style?.width) || 100;
    const curH = rect?.height || parseFloat(selected.style?.height) || 100;
    resizeElement(selected.id, { width: curW + dw, height: curH + dh });
  }, [selected, resizeElement]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Regroupe TOUTES les interactions clavier de manipulation (voir
  // editorKit/useKeyboardManipulation pour la table complète des raccourcis
  // et la justification de chaque choix vis-à-vis des raccourcis navigateur/OS). ---
  useKeyboardManipulation({
    enabled: true,
    hasSelection: selectedIds.length > 0,
    onMove: freeMode ? nudgeSelected : undefined,
    onResize: selected ? nudgeResizeSelected : undefined,
    onReorder: selected ? (dir) => moveElement(selected.id, dir) : undefined,
    onReparent: selected ? (dir) => (dir === "outdent" ? outdentSelected() : indentSelected()) : undefined,
    onDelete: () => (selectedIds.length > 1 ? deleteSelected() : (selected && deleteElement(selected.id))),
    onDuplicate: selected ? duplicateElement : undefined,
    onCopy: copySelected,
    onPaste: pasteClipboard,
    onUndo: history.undo,
    onRedo: history.redo,
    onEscape: () => setSelectedIds([]),
  });

  const openJsonTab = () => { setJsonText(JSON.stringify(doc, null, 2)); editingJson.current = false; setJsonError(""); setRightTab("json"); };
  // Convertit un ancien document mono-page { page } vers le format multi-pages { pages }
  const migrateDoc = (parsed) => {
    const migrateNodes = (nodes = []) => nodes.map((node) => {
      const next = { ...node };
      if (next.consoleCss === undefined) next.consoleCss = "";
      if (next.consoleJs === undefined) next.consoleJs = "";
      // Migration docUpload → docViewer (ancien type)
      if (next.type === "docUpload") {
        next.type = "docViewer";
        next.defaultView = next.defaultView || "html";
        next.allowHtml = next.allowHtml !== false;
        next.allowOffice = next.allowOffice !== false;
        next.allowPdf = next.allowPdf !== false;
        next.allowWord = next.allowWord !== false;
        next.allowDownload = next.allowDownload !== false;
        next.allowSign = !!next.allowSign;
        next.requireSign = !!next.requireSign;
        next.signMode = next.signMode || "both";
        next.signatures = next.signatures || [];
        next.folderLabel = next.folderLabel || "Mes documents";
        next.apiPath = next.apiPath || "/api/documents";
        next.autoName = next.autoName !== false;
        next.customName = next.customName || "";
        next.buttonText = next.buttonText || "Charger un document";
        if (next.fullPageMode === undefined) next.fullPageMode = false;
        next.pageDisplayMode = next.pageDisplayMode === "count" ? "count" : "all";
        next.pagesToShow = Math.max(1, Number(next.pagesToShow) || 1);
        next.sourceLink = next.sourceLink || "";
      }
      // Migration des nœuds docViewer existants — ajout des champs manquants
      if (next.type === "docViewer") {
        if (next.allowOffice === undefined) next.allowOffice = true;
        if (next.allowPdf    === undefined) next.allowPdf    = true;
        if (!next.signatures)               next.signatures  = [];
        if (!next.buttonText)               next.buttonText  = "Charger un document";
        if (!next.viewStyle)                next.viewStyle   = "card";
        if (next.fullPageMode === undefined) next.fullPageMode = false;
        if (!next.pageDisplayMode)          next.pageDisplayMode = "all";
        next.pageDisplayMode = next.pageDisplayMode === "count" ? "count" : "all";
        next.pagesToShow = Math.max(1, Number(next.pagesToShow) || 1);
        if (!next.docKind && next.docName)  next.docKind     = inferDocKind(next.docName, "", next.docUrl || "");
      }
      if (Array.isArray(next.children)) next.children = migrateNodes(next.children);
      return next;
    });

    const resolvedPageMode = parsed?.pageMode || parsed?.meta?.pageMode || "static";
    const templateKey = parsed?.meta?.templateKey || null;
    const migrateStoreChildren = (nodes = []) =>
      ensureShopTemplateFollowButton(
        migrateCategoryToggleGroups(migrateNodes(nodes)),
        templateKey,
        resolvedPageMode
      );

    if (parsed?.pages && Array.isArray(parsed.pages)) {
      // Garantir un pageMode (rétrocompatibilité des anciens docs)
      return {
        ...parsed,
        pageMode: resolvedPageMode,
        pages: parsed.pages.map((page) => ({
          ...page,
          children: migrateStoreChildren(page.children || []),
        })),
      };
    }
    if (parsed?.page?.children) {
      const id = uid();
      return {
        meta: parsed.meta || {},
        pageMode: resolvedPageMode,
        currentPageId: id,
        pages: [{
          id,
          name: "Page 1",
          style: parsed.page.style || { ...PAGE_STYLE },
          children: migrateStoreChildren(parsed.page.children),
        }],
      };
    }
    throw new Error("Structure invalide : attendu { pages: [...] } ou { page: { children } }.");
  };
  const applyJson = () => {
    try { setDoc(migrateDoc(JSON.parse(jsonText))); setJsonError(""); setSelectedIds([]); }
    catch (e) { setJsonError(e.message); }
  };
  const [syncMsg, setSyncMsg] = useState("");
  // Synchro manuelle de secours : aligne code et contenu dans les deux sens
  const syncJson = () => {
    try {
      const migrated = migrateDoc(JSON.parse(jsonText));
      setDoc(migrated); setSelectedIds([]);
      editingJson.current = false;
      setJsonText(JSON.stringify(migrated, null, 2));
      setJsonError(""); setSyncMsg("Synchronisé : le code et le contenu sont alignés.");
    } catch (e) {
      editingJson.current = false;
      setJsonText(JSON.stringify(doc, null, 2));
      setJsonError(""); setSyncMsg("JSON invalide — le code a été régénéré depuis l'éditeur.");
    }
    setTimeout(() => setSyncMsg(""), 3000);
  };

  // SYNCHRO AUTO 1 — éditeur → JSON : quand le document change (et qu'on ne tape pas dans le textarea),
  // le texte JSON se régénère pour rester à jour.
  useEffect(() => {
    if (!autoSync) return;
    if (editingJson.current) return; // l'utilisateur édite le texte : on ne l'écrase pas
    setJsonText(JSON.stringify(doc, null, 2));
    setJsonError("");
  }, [doc, autoSync]);

  // SYNCHRO AUTO 2 — JSON → éditeur : quand l'utilisateur tape du JSON valide,
  // on l'applique à l'éditeur après un court délai (débounce).
  useEffect(() => {
    if (!autoSync || !editingJson.current) return;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      try {
        const migrated = migrateDoc(JSON.parse(jsonText));
        setDoc(migrated);
        setJsonError("");
        editingJson.current = false; // synchro faite : on rouvre le sens éditeur -> JSON
      } catch (e) {
        setJsonError(e.message); // JSON incomplet/invalide pendant la frappe : on attend
      }
    }, 700);
    return () => syncTimer.current && clearTimeout(syncTimer.current);
  }, [jsonText, autoSync]);

  // Handler du textarea : marque qu'on édite le code, puis met à jour le texte
  const onJsonChange = (value) => { editingJson.current = true; setJsonText(value); };

  const download = (filename, content, type = "application/json") => {
    const blob = new Blob([content], { type }); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  };
  const [htmlCodeCopied, setHtmlCodeCopied] = useState(false);
  const htmlCode = useMemo(() => buildSiteHtml(doc, boundData), [doc, boundData]);
  // Per-page variant of the exact same generator, for PageHtmlSourceModal's
  // "generated HTML" seed -- reuses buildSiteHtml() as-is (no new HTML
  // generator) by scoping `doc.pages` down to just the one page being
  // edited. Feature-detection inside buildSiteHtml (Swiper/shop-live/modal/
  // widgets/keyframes) reads `JSON.stringify(doc)`, so this stays accurate
  // per page too. boundData is the same live data the visual builder/shop
  // live bindings already use, so the generated HTML for a brand new page is
  // just as "adapted to the JSON data" as the whole-site version.
  const getGeneratedHtmlForPage = useCallback((pid) => {
    const pg = (doc.pages || []).find((p) => p.id === pid);
    if (!pg) return "";
    return buildSiteHtml({ ...doc, pages: [pg] }, boundData);
  }, [doc, boundData]);

  // ── Inline "Code HTML" panel editing (sidebar tab) ─────────────────────────
  // Same password-gated permission/save/restore pipeline as PageHtmlSourceModal
  // (canEditHtmlCode/canEditPageHtml, verify-html-edit/html-source endpoints,
  // per-JSON-page htmlSourcePages) -- but edited DIRECTLY in the sidebar
  // textarea instead of a popup, once unlocked. The password is asked once
  // per editing session here (kept in `htmlPanelUnlocked`/`htmlPanelConfirmationToken`
  // for as long as PageBuilder stays mounted), never re-asked per keystroke;
  // the server still re-verifies the confirmation token on every save/restore
  // regardless, so this is only ever a UX convenience, same rule as the modal.
  // The full modal (</> icon, see below) stays available for the richer
  // Preview/Console/CSS/JS-tabs/multi-page/Fullscreen experience -- this
  // panel is deliberately a quick single-document HTML editor, not a second
  // copy of that whole modal squeezed into a 330px sidebar.
  const [htmlPanelUnlocked, setHtmlPanelUnlocked] = useState(false);
  const [htmlPanelConfirmationToken, setHtmlPanelConfirmationToken] = useState("");
  const [htmlPanelPasswordPrompt, setHtmlPanelPasswordPrompt] = useState(false);
  const [htmlPanelPassword, setHtmlPanelPassword] = useState("");
  const [htmlPanelPasswordError, setHtmlPanelPasswordError] = useState("");
  const [htmlPanelVerifying, setHtmlPanelVerifying] = useState(false);
  const [htmlPanelLoading, setHtmlPanelLoading] = useState(false);
  const [htmlPanelSaving, setHtmlPanelSaving] = useState(false);
  const [htmlPanelRestoring, setHtmlPanelRestoring] = useState(false);
  const [htmlPanelError, setHtmlPanelError] = useState("");
  const [htmlPanelSavedHtml, setHtmlPanelSavedHtml] = useState("");
  const [htmlPanelDraftHtml, setHtmlPanelDraftHtml] = useState("");
  const [htmlPanelManuallyEdited, setHtmlPanelManuallyEdited] = useState(false);
  const [htmlPanelEditedAt, setHtmlPanelEditedAt] = useState(null);
  const [htmlPanelLoadedPageId, setHtmlPanelLoadedPageId] = useState(null);
  const htmlPanelDirty = htmlPanelDraftHtml !== htmlPanelSavedHtml;

  const loadHtmlPanelSource = useCallback(async (pid) => {
    if (!savedPage?._id || !pid) return;
    setHtmlPanelLoading(true);
    setHtmlPanelError("");
    try {
      const { token } = isAuthenticated() || {};
      const result = await getPageHtmlSourceAPI(savedPage._id, token, pid);
      const manuallyEdited = Boolean(result.isManuallyEdited);
      const seed = !manuallyEdited ? getGeneratedHtmlForPage(pid) || "" : "";
      setHtmlPanelSavedHtml(result.html || "");
      setHtmlPanelDraftHtml(result.html || seed || "");
      setHtmlPanelManuallyEdited(manuallyEdited);
      setHtmlPanelEditedAt(result.editedAt || null);
      setHtmlPanelLoadedPageId(pid);
    } catch (e) {
      setHtmlPanelError(
        e.status === 404
          ? "This page no longer exists on the server (it may have been deleted). Click the Builder's Save button to re-save it, then try again."
          : e.message || String(e)
      );
      if (e.status === 404) setHtmlPanelUnlocked(false);
    } finally {
      setHtmlPanelLoading(false);
    }
  }, [savedPage?._id, getGeneratedHtmlForPage]);

  // Re-loads automatically when the admin switches page in the Builder while
  // already unlocked -- the panel always edits whichever page is currently
  // open, never a stale one left over from before the page switch.
  useEffect(() => {
    if (!htmlPanelUnlocked || !currentPageId) return;
    if (htmlPanelLoadedPageId === currentPageId) return;
    loadHtmlPanelSource(currentPageId);
  }, [htmlPanelUnlocked, currentPageId, htmlPanelLoadedPageId, loadHtmlPanelSource]);

  // The "Modifier" (lock) icon's click handler -- checks AnimationSettings'
  // "Password Required Settings" (passwordRequired.pageBuilder) FIRST: OFF
  // unlocks the panel directly (no password form shown at all); ON (or
  // unknown/fetch failure -- fail-safe) shows the inline password prompt
  // exactly as before. The real enforcement is server-side regardless
  // (assertHtmlEditConfirmation in pageBuilder.js) -- this only decides
  // whether the UI bothers asking.
  const handleHtmlPanelUnlockClick = useCallback(async () => {
    if (htmlPanelUnlocked) return;
    const result = await getFirstAnimationSettingsAPI();
    const required = result?.settings?.passwordRequired?.pageBuilder !== false;
    if (!required) {
      setHtmlPanelUnlocked(true);
      setHtmlPanelConfirmationToken(""); // server bypasses the check entirely when not required
      await loadHtmlPanelSource(currentPageId);
      return;
    }
    setHtmlPanelPasswordPrompt((p) => !p);
  }, [htmlPanelUnlocked, currentPageId, loadHtmlPanelSource]);

  const handleHtmlPanelUnlock = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!htmlPanelPassword || !savedPage?._id) return;
    setHtmlPanelVerifying(true);
    setHtmlPanelPasswordError("");
    try {
      const { token } = isAuthenticated() || {};
      const result = await verifyPageHtmlEditAccess(savedPage._id, htmlPanelPassword, token);
      setHtmlPanelConfirmationToken(result.confirmationToken);
      setHtmlPanelPassword(""); // never kept in memory longer than needed to send it
      setHtmlPanelUnlocked(true);
      setHtmlPanelPasswordPrompt(false);
      await loadHtmlPanelSource(currentPageId);
    } catch (err) {
      // A correct password can still 404 here if this page no longer exists
      // server-side (e.g. deleted from another tab/session while this one
      // stayed open with a now-stale savedPage._id) -- not a wrong-password
      // error, so say so instead of a confusing raw "Page introuvable."
      setHtmlPanelPasswordError(
        err.status === 404
          ? "This page no longer exists on the server (it may have been deleted). Click the Builder's Save button to re-save it, then try again."
          : err.message || "Password verification failed."
      );
    } finally {
      setHtmlPanelVerifying(false);
    }
  }, [htmlPanelPassword, savedPage?._id, currentPageId, loadHtmlPanelSource]);

  const handleHtmlPanelSave = useCallback(async () => {
    if (!htmlPanelDraftHtml.trim() || !savedPage?._id) return;
    setHtmlPanelSaving(true);
    setHtmlPanelError("");
    try {
      const { token } = isAuthenticated() || {};
      const result = await savePageHtmlSourceAPI(savedPage._id, htmlPanelDraftHtml, htmlPanelConfirmationToken, token, currentPageId);
      setHtmlPanelSavedHtml(htmlPanelDraftHtml);
      setHtmlPanelManuallyEdited(true);
      setHtmlPanelEditedAt(result.editedAt || new Date().toISOString());
      onPageMetaUpdated?.(result.page);
    } catch (err) {
      setHtmlPanelError(
        err.status === 404
          ? "This page no longer exists on the server (it may have been deleted). Click the Builder's Save button to re-save it, then try again."
          : err.message || String(err)
      );
      // The confirmation token expired (server-side, ~15 min), the
      // session-level unlock otherwise no longer holds, or the page itself
      // is gone server-side (404) -- re-lock so the admin re-enters their
      // password (and sees the clearer message above) rather than keep
      // failing silently against a stale confirmation/page.
      if (err.status === 404 || /password|confirmation/i.test(err.message || "")) setHtmlPanelUnlocked(false);
    } finally {
      setHtmlPanelSaving(false);
    }
  }, [htmlPanelDraftHtml, savedPage?._id, htmlPanelConfirmationToken, currentPageId, onPageMetaUpdated]);

  const handleHtmlPanelRestore = useCallback(async () => {
    if (!savedPage?._id) return;
    if (!window.confirm(elakayAutoText("h_pg_html_restore_confirm", "Restore the generated page? Your manually edited HTML source will be discarded."))) return;
    setHtmlPanelRestoring(true);
    setHtmlPanelError("");
    try {
      const { token } = isAuthenticated() || {};
      const result = await restorePageGeneratedHtmlAPI(savedPage._id, htmlPanelConfirmationToken, token, currentPageId);
      onPageMetaUpdated?.(result.page);
      await loadHtmlPanelSource(currentPageId);
    } catch (err) {
      setHtmlPanelError(
        err.status === 404
          ? "This page no longer exists on the server (it may have been deleted). Click the Builder's Save button to re-save it, then try again."
          : err.message || String(err)
      );
      if (err.status === 404 || /password|confirmation/i.test(err.message || "")) setHtmlPanelUnlocked(false);
    } finally {
      setHtmlPanelRestoring(false);
    }
  }, [savedPage?._id, htmlPanelConfirmationToken, currentPageId, onPageMetaUpdated, loadHtmlPanelSource]);

  // Quick Reset -- discards unsaved keystrokes, back to the last loaded/saved
  // state for this page. Purely client-side (no network call, no confirm),
  // distinct from "Reset to Generated Code" above.
  const handleHtmlPanelQuickReset = () => setHtmlPanelDraftHtml(htmlPanelSavedHtml);

  const exportJson = () => download("site.json", JSON.stringify(doc, null, 2));
  const exportHtml = () => {
    if (!canUseHtmlCode) {
      openShopPurchaseModal();
      return;
    }
    download("site.html", htmlCode, "text/html");
  };
  const copyHtmlCode = async () => {
    if (!canUseHtmlCode) {
      openShopPurchaseModal();
      return;
    }
    try {
      await navigator.clipboard.writeText(htmlCode);
      setHtmlCodeCopied(true);
      setTimeout(() => setHtmlCodeCopied(false), 1600);
    } catch {
      setHtmlCodeCopied(false);
    }
  };
  const openHtmlPreview = () => {
    if (!canUseHtmlCode) {
      openShopPurchaseModal();
      return;
    }
    const blob = new Blob([htmlCode], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // ── Draft localStorage : effacer / restaurer ──────────────────────────────
  const clearLocalDraft = () => {
    try {
      localStorage.removeItem(draftStorageKeys.draftKey);
      localStorage.removeItem(draftStorageKeys.metaKey);
    } catch {}
    setAutoSaveMeta(null);
    setShowDraftBanner(false);
  };

  const restoreDraft = () => {
    try {
      const raw = localStorage.getItem(draftStorageKeys.draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setDoc(migrateDoc(parsed));
      setSelectedIds([]);
      setShowDraftBanner(false);
    } catch (e) {
      alert(elakayAutoText("h_1b6bb3304f8f64", "Erreur lors de la restauration du brouillon : {value0}", { value0: e.message }));
    }
  };

  // ── Blocage de la sauvegarde : seules les boutiques créées sont modifiables ──
  const isEditingSavedPage = Boolean(savedPageId);
  const saveBusyLabel = isEditingSavedPage ? "Modification…" : "Sauvegarde…";
  // Un mot de passe Family re-confirmé (pour CETTE page précisément) lève le
  // verrou lecture-seule côté client — le verrou admin/propriétaire ci-dessous
  // reste lui totalement infranchissable : Family ne peut jamais éditer en
  // place une boutique qui n'est pas la sienne. Le serveur revalide de toute
  // façon le mot de passe à chaque sauvegarde (voir saveToDb / updatePage).
  const shopSelectionReadOnlyEffective = shopSelectionReadOnly && !familyUnlockedPassword;
  const saveBlocked = !canSave
    || shopSelectionReadOnlyEffective
    || savedPage?.authorType === "admin"
    || (currentUserId && savedPage?.owner && String(savedPage.owner) !== String(currentUserId));
  const saveBlockedReason = shopSelectionReadOnlyEffective
    ? `Lecture seule : cette ${shopSelectionLabel} ne peut pas être modifiée ni sauvegardée.`
    : savedPage?.authorType === "admin"
      ? "Boutique Elakay par défaut — lecture seule"
      : !canSave
        ? "La sauvegarde est désactivée."
        : "Vous n'êtes pas le propriétaire de cette boutique.";

  // ── Sauvegarde en BD ─────────────────────────────────────────────────────
  // Une nouvelle boutique demande toujours son nom. Une page existante est
  // mise à jour directement afin de conserver son identité en base.
  // Prix auto  : somme des éléments combinés payants si present, sinon 0
  const saveToDb = async (titleOverride = "") => {
    if (!onSave || isSaving || saveBlocked) return;
    setIsSaving(true);
    try {
      const { total: autoPrice } = calcCombinePrice();
      const title =
        titleOverride.trim()    ||
        savedPage?.title        ||
        doc?.meta?.title        ||
        pages[0]?.name          ||
        "Ma page";
      await onSave({
        doc,
        title,
        description: savedPage?.description || doc?.meta?.description || "",
        price:       savedPage?.price ?? (autoPrice > 0 ? autoPrice : 0),
        isTemplate:  savedPage?.isTemplate  ?? false,
        forSale:     savedPage?.forSale     ?? false,
        status:      savedPage?.status      || "draft",
        tags:        savedPage?.tags        || [],
        pageMode:    doc?.pageMode           || "static",
        pageModeConfig: doc?.pageModeConfig || undefined,
        // Rejoue la re-confirmation Family sur chaque sauvegarde tant que
        // cette page reste ouverte — le serveur revalide rôle + mot de passe
        // à chaque appel, ce champ n'est jamais fait confiance à lui seul.
        ...(shopSelectionReadOnly && familyUnlockedPassword ? { familyPassword: familyUnlockedPassword } : {}),
      });
      if (titleOverride.trim()) {
        setDoc((current) => ({
          ...current,
          meta: { ...(current.meta || {}), title },
        }));
      }
      lastSavedDocRef.current = doc;
      clearLocalDraft();
      setShowSaveNameModal(false);
      setSaveNameMode("create");
      setSaveName("");
      setSaveNameError("");
    } catch (e) {
      alert(elakayAutoText("h_70e17e15111cb4", "Erreur de sauvegarde : {value0}", { value0: e.message || String(e) }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveToDb = () => {
    if (!onSave || isSaving || saveBlocked) return;
    if (isEditingSavedPage) {
      saveToDb();
      return;
    }
    const suggestedName =
      doc?.meta?.title && doc.meta.title !== "Mon site"
        ? doc.meta.title
        : doc?.pageMode === "store"
          ? ""
          : pages[0]?.name || "";
    setSaveNameMode("create");
    setSaveName(suggestedName);
    setSaveNameError("");
    setShowSaveNameModal(true);
  };

  const handleRenameSavedPage = () => {
    if (!isEditingSavedPage || isSaving || saveBlocked) return;
    setSaveNameMode("rename");
    setSaveName(
      savedPage?.title ||
      doc?.meta?.title ||
      pages[0]?.name ||
      ""
    );
    setSaveNameError("");
    setShowSaveNameModal(true);
  };

  const handleSaveNameSubmit = (event) => {
    event.preventDefault();
    const name = saveName.trim();
    if (!name) {
      setSaveNameError(doc?.pageMode === "store"
        ? "Entrez le nom de la boutique."
        : "Entrez le nom de la page.");
      return;
    }
    saveToDb(name);
  };

  const handleNewBuilder = () => {
    if (!window.confirm(elakayAutoText("h_f9e90cc7b2d58a", "Créer un nouveau Builder vide ? Les modifications non sauvegardées seront perdues."))) return;
    const fresh = {
      ...makeStarterDoc(),
      pageMode: doc?.pageMode || initialPageMode || "static",
    };
    setDoc(fresh);
    setSelectedIds([]);
    setBodySelected(false);
    setJsonText(JSON.stringify(fresh, null, 2));
    setActiveTemplateKey(null);
    selectShopSource(null);
    if (fresh.pageMode === "store" && allDatasets.store) {
      bindDataset("store", allDatasets.store);
    } else {
      setBoundData(null);
    }
    setShowPreview(false);
    setLeftTab("add");
    setShowLeftPanel(true);
    setShowSaveNameModal(false);
    setSaveNameMode("create");
    setSaveName("");
    setSaveNameError("");
    clearLocalDraft();
    notifyLoadedPage(fresh, null, null, null);
  };

  const openFamilyUnlockModal = () => {
    setFamilyPassword("");
    setFamilyUnlockError("");
    setShowFamilyPassword(false);
    setShowFamilyUnlockModal(true);
  };

  const closeFamilyUnlockModal = () => {
    if (familyUnlockBusy) return;
    setShowFamilyUnlockModal(false);
    setShowFamilyPassword(false);
    setFamilyPassword("");
    setFamilyUnlockError("");
  };

  // Re-confirme le mot de passe Family (rôle + mot de passe re-vérifiés côté
  // serveur, voir POST /pageBuilder/family/verify) puis débloque l'édition
  // EN PLACE de la page actuellement ouverte — aucune copie n'est créée,
  // aucun autre état (doc, sélection…) n'est touché ici.
  const handleFamilyUnlockSubmit = async (event) => {
    event?.preventDefault?.();
    if (!onFamilyUnlockPage) return;
    if (!familyPassword.trim()) {
      setFamilyUnlockError("Entrez le mot de passe du compte Développeur.");
      return;
    }

    setFamilyUnlockBusy(true);
    setFamilyUnlockError("");
    try {
      await onFamilyUnlockPage(null, familyPassword);
      setFamilyUnlockedPassword(familyPassword);
      setShowFamilyUnlockModal(false);
      setFamilyPassword("");
    } catch (error) {
      setFamilyUnlockError(error.message || "Validation Développeur impossible.");
    } finally {
      setFamilyUnlockBusy(false);
    }
  };

  const importPageFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const content = String(reader.result || "");
        let parsed;
        if (/\.html?$/i.test(file.name) || file.type === "text/html") {
          const htmlDoc = new DOMParser().parseFromString(content, "text/html");
          const embedded = htmlDoc.getElementById("pb-pagebuilder-document");
          if (!embedded?.textContent?.trim()) {
            throw new Error("Ce HTML ne contient pas de document PageBuilder réimportable.");
          }
          parsed = JSON.parse(embedded.textContent);
        } else {
          parsed = JSON.parse(content);
        }
        const importedDoc = migrateDoc(parsed);
        setDoc(importedDoc);
        setJsonText(JSON.stringify(importedDoc, null, 2));
        setSelectedIds([]);
        setBodySelected(false);
        setDataStatus(`« ${file.name} » chargé dans le PageBuilder.`);
        setTimeout(() => setDataStatus(""), 3000);
      } catch (err) {
        alert(elakayAutoText("h_e519997ae21165", "Import impossible : {value0}", { value0: err.message }));
      } finally {
        e.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  // ---- Panneau Données : export sélection / collage flexible / API ----
  const copyToClipboard = async (text, okMsg) => {
    try { await navigator.clipboard.writeText(text); setDataStatus(okMsg); }
    catch { setDataStatus("Copie impossible — sélectionnez et copiez manuellement."); }
    setTimeout(() => setDataStatus(""), 2500);
  };
  // JSON des éléments sélectionnés (tableau de nœuds)
  const selectionJson = () => JSON.stringify(selectedNodes, null, 2);
  const copySelection = () => {
    if (!selectedNodes.length) { setDataStatus("Aucun élément sélectionné."); setTimeout(() => setDataStatus(""), 2500); return; }
    copyToClipboard(selectionJson(), `${selectedNodes.length} élément(s) copié(s) dans le presse-papiers.`);
  };
  const copyPage = () => copyToClipboard(JSON.stringify(doc, null, 2), "Page entière copiée dans le presse-papiers.");
  const exportSelection = () => {
    if (!selectedNodes.length) { setDataStatus("Aucun élément sélectionné."); setTimeout(() => setDataStatus(""), 2500); return; }
    download("selection.json", selectionJson());
  };

  // Normalise n'importe quel JSON collé en une liste de nœuds prêts à insérer
  const normalizeToNodes = (parsed) => {
    let nodes;
    if (Array.isArray(parsed)) nodes = parsed;                                    // tableau de nœuds
    else if (parsed?.pages?.[0]?.children) nodes = parsed.pages[0].children;       // doc multi-pages -> 1re page
    else if (parsed?.page?.children) nodes = parsed.page.children;                 // ancien doc mono-page
    else if (parsed?.children) nodes = parsed.children;                           // une page seule
    else if (parsed?.type) nodes = [parsed];                                      // un seul nœud
    else throw new Error("JSON non reconnu : attendu un élément, un tableau d'éléments, une page, ou un document.");
    // ré-attribuer des id frais pour éviter les collisions
    const reId = (n) => { n.id = uid(); if (n.items) n.items.forEach((it) => (it.id = uid())); n.children?.forEach(reId); return ensureConsoleProps(n); };
    return nodes.map((n) => reId(JSON.parse(JSON.stringify(n))));
  };
  const addPastedJson = () => {
    try {
      const nodes = normalizeToNodes(JSON.parse(pasteText));
      // Si un conteneur est sélectionné, on insère dedans ; sinon à la racine.
      if (selected && ELEMENT_TYPES[selected.type]?.isContainer) {
        let children = page.children;
        nodes.forEach((n) => { children = insertIntoTree(children, selected.id, n); });
        setChildren(children);
      } else {
        setChildren([...page.children, ...nodes]);
      }
      setPasteError(""); setDataStatus(`${nodes.length} élément(s) ajouté(s) à la page.`);
      setTimeout(() => setDataStatus(""), 2500);
    } catch (e) { setPasteError(e.message); }
  };
  const replaceWithPasted = () => {
    try {
      const parsed = JSON.parse(pasteText);
      if (parsed?.pages || parsed?.page?.children) { setDoc(migrateDoc(parsed)); }
      else { setChildren(normalizeToNodes(parsed)); }
      setPasteError(""); setSelectedIds([]); setDataStatus("Contenu remplacé.");
      setTimeout(() => setDataStatus(""), 2500);
    } catch (e) { setPasteError(e.message); }
  };

  // API : envoyer (POST) la sélection ou la page ; charger (GET) depuis l'URL
  const sendToApi = async (which) => {
    if (!apiUrl) { setDataStatus("Indiquez d'abord une URL d'API."); setTimeout(() => setDataStatus(""), 2500); return; }
    const payload = which === "selection" ? selectionJson() : JSON.stringify(doc);
    setDataStatus("Envoi en cours…");
    try {
      const res = await fetch(apiUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload });
      setDataStatus(res.ok ? "Contenu envoyé avec succès." : `Réponse du serveur : ${res.status}.`);
    } catch (e) { setDataStatus("Échec de l'envoi : " + e.message); }
    setTimeout(() => setDataStatus(""), 3500);
  };
  const loadFromApi = async () => {
    if (!apiUrl) { setDataStatus("Indiquez d'abord une URL d'API."); setTimeout(() => setDataStatus(""), 2500); return; }
    setDataStatus("Chargement…");
    try {
      const res = await fetch(apiUrl);
      const data = await res.json();
      setPasteText(JSON.stringify(data, null, 2));
      setDataStatus("Contenu chargé dans la zone de collage. Cliquez sur « Ajouter » ou « Remplacer ».");
    } catch (e) { setDataStatus("Échec du chargement : " + e.message); }
    setTimeout(() => setDataStatus(""), 4000);
  };

  const refreshShopCollections = (token) => {
    getMyShops(token).then((data) => {
      if (!data.error) {
        setMyCreatedShops(data.myCreated || []);
        setMyBoughtShops(data.myPurchased || []);
        setEditorTemplates(data.editorsShops || []);
        setDefaultShops(data.defaultShops || []);
      }
    });
  };

  const resolveShopDocument = async (shop) => {
    if (!shop) return null;
    if (isLocalDefaultShopId(shop.id)) {
      const template = SHOP_TEMPLATES.find((item) =>
        String(item._id) === String(shop.id) || item.key === shop.templateKey
      );
      return template ? template.make() : null;
    }
    if (String(shop.id) === String(savedPageId) && doc?.pageMode === "store") {
      return doc;
    }
    if (!isMongoObjectId(shop.id)) return null;
    const { token } = isAuthenticated() || {};
    const data = await getPageBuilderById(shop.id, token);
    return data?.doc || data || null;
  };

  const openShopPurchaseModal = (shop = selectedCommerceShop) => {
    const target = shop?.canPurchase
      ? shop
      : purchaseShopChoices[0] || null;
    setShopPurchaseTargetKey(target ? getShopCartKey(target) : "");
    setBoutiqueActionState({ loading:false, error:null, success:null });
    setShopPurchaseState({ loadingId:null, error:null, success:null });
    setShowBoutiqueActions(true);
  };

  // ── Boutique actions : download / display / both ─────────────────────────
  const handleBoutiqueAction = async (action, shop = shopPurchaseTarget) => {
    setBoutiqueActionState({ loading: true, error: null, success: null });
    const { token } = isAuthenticated() || {};
    if (!token) { setBoutiqueActionState({ loading: false, error: elakayAutoText("h_beca9bd73f0fc9", "Non connecté."), success: null }); return; }
    if (!shop?.id) { setBoutiqueActionState({ loading: false, error: elakayAutoText("h_5c0fd86518a225", "Sélectionnez une boutique ou ajoutez-la au panier."), success: null }); return; }
    const currentRights = resolveShopPurchaseRights(shop);
    if (action === "both" && (currentRights.displayRight || currentRights.downloadRight)) {
      setBoutiqueActionState({ loading: false, error: elakayAutoText("h_5414c38133e627", "« Tout acheter » est disponible uniquement avant tout achat de droit."), success: null });
      return;
    }
    if (action === "display-only" && currentRights.displayRight) {
      setBoutiqueActionState({ loading: false, error: elakayAutoText("h_d6f94839495619", "Le droit d'affichage est déjà acheté."), success: null });
      return;
    }
    if (action.startsWith("download") && currentRights.downloadRight) {
      setBoutiqueActionState({ loading: false, error: elakayAutoText("h_200f74cd97feae", "Le code HTML est déjà acheté."), success: null });
      return;
    }
    if (shop.source === "created" && action !== "download-own") {
      setBoutiqueActionState({ loading: false, error: elakayAutoText("h_1c8d0de132da37", "Pour une création, seul l'achat du code HTML est disponible."), success: null });
      return;
    }
    let result;
    if      (action === "download-own")   result = await buyShopDownload(token, shop.id, "own");
    else if (action === "download-other") result = await buyShopDownload(token, shop.id, "other");
    else if (action === "display-only")   result = await buyShopDisplay(token, shop.id);
    else if (action === "both")           result = await buyShopBoth(token, shop.id);
    else { setBoutiqueActionState({ loading: false, error: elakayAutoText("h_77724c80284e21", "Action inconnue."), success: null }); return; }

    if (result.success && result.status === "succeeded") {
      refreshShopCollections(token);
      if (action === "display-only" || action === "both") {
        const grantedId = String(shop.id);
        setDisplayAccessGrantIds((current) =>
          current.includes(grantedId) ? current : [...current, grantedId]
        );
      }
      if (result.includesDownload || action.startsWith("download") || action === "both") {
        const grantedId = String(shop.id);
        setHtmlAccessGrantIds((current) =>
          current.includes(grantedId) ? current : [...current, grantedId]
        );
        const targetDoc = await resolveShopDocument(shop);
        if (targetDoc) triggerHtmlDownload(targetDoc, shop.title);
      }
      setBoutiqueActionState({
        loading: false,
        error: null,
        success: `Achat réussi pour « ${shop.title} ».`,
      });
    } else if (result.requiresAction) {
      setBoutiqueActionState({ loading: false, error: elakayAutoText("h_99e26d0107429f", "Authentification 3DS requise. Completez dans votre portefeuille."), success: null });
    } else {
      setBoutiqueActionState({ loading: false, error: result.error || "Paiement échoué.", success: null });
    }
  };

  const addShopToCart = (shop = selectedCommerceShop) => {
    if (!shop?.canPurchase) {
      setShopPurchaseState({
        loadingId: null,
        error: elakayAutoText("h_448589912eb136", "Cette boutique locale n'est pas encore disponible à l'achat."),
        success: null,
      });
      return;
    }
    setShopCart((current) => {
      if (current.some((item) => String(item.id) === String(shop.id))) return current;
      return [...current, {
        id: shop.id,
        source: shop.source,
        title: shop.title,
        description: shop.description || "",
        thumbnail: shop.thumbnail || "",
        price: Math.max(0, Number(shop.price) || 0),
        authorType: shop.authorType || "",
        sourceModelId: shop.sourceModelId || "",
        purchasePageId: shop.purchasePageId || shop.id,
        templateKey: shop.templateKey || "",
        canPurchase: true,
      }];
    });
    setShopPurchaseState({
      loadingId: null,
      error: null,
      success: `« ${shop.title} » a été ajoutée au panier boutique.`,
    });
  };

  const removeShopFromCart = (shopId) => {
    setShopCart((current) => current.filter((item) => String(item.id) !== String(shopId)));
  };

  const purchaseSelectedShop = async (shop = selectedCommerceShop) => {
    const purchasePageId = isMongoObjectId(shop?.purchasePageId)
      ? shop.purchasePageId
      : isMongoObjectId(shop?.sourceModelId)
        ? shop.sourceModelId
        : shop?.id;
    if (!shop?.canPurchase || !isMongoObjectId(purchasePageId)) {
      setShopPurchaseState({
        loadingId: null,
        error: elakayAutoText("h_61db0d5fc6664e", "Ce modèle local s'achète avec les options d'affichage ou de code HTML."),
        success: null,
      });
      return;
    }
    const { token } = isAuthenticated() || {};
    if (!token) {
      setShopPurchaseState({ loadingId: null, error: elakayAutoText("h_11fb5c27fb7c21", "Connexion requise pour acheter cette boutique."), success: null });
      return;
    }

    setShopPurchaseState({ loadingId: shop.id, error: null, success: null });
    try {
      const copy = await purchasePageBuilder(purchasePageId, token);
      if (!copy?._id) throw new Error("La copie achetée n'a pas été créée.");
      setMyBoughtShops((current) => {
        const exists = current.some((item) => getShopItemId(item) === String(copy._id));
        return exists ? current : [copy, ...current];
      });
      removeShopFromCart(shop.id);
      refreshShopCollections(token);
      setShopPurchaseState({
        loadingId: null,
        error: null,
        success: `Achat réussi : « ${shop.title} » est maintenant dans Mes achats.`,
      });
    } catch (error) {
      setShopPurchaseState({
        loadingId: null,
        error: error.message || "Impossible d'acheter cette boutique.",
        success: null,
      });
    }
  };

  // Génère et télécharge le doc courant en HTML statique
  const triggerHtmlDownload = (shopDoc = doc, shopTitle = "boutique") => {
    try {
      const html = buildSiteHtml(shopDoc, boundData);
      const blob = new Blob([html], { type: "text/html" });
      const url  = URL.createObjectURL(blob);
      const safeName = String(shopTitle || "boutique")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase() || "boutique";
      const a = Object.assign(document.createElement("a"), {
        href: url,
        download: `${safeName}.html`,
      });
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const deviceWidth = preset.width;
  const rootHeight = fitParentHeight ? "100%" : "100vh";
  const rootMinHeight = fitParentHeight ? 0 : 0;
  const isHeaderPanelNode = (node) => !!ELEMENT_TYPES[node?.type]?.isShopHeaderSeller;
  const headerPanelNodes = (page.children || []).filter(isHeaderPanelNode);
  const bodyPanelNodes = (page.children || []).filter((node) => !isHeaderPanelNode(node));
  const hasCanvasSelection = selectedIds.length > 0;
  const selectedIsHeaderNode = !!selected && isHeaderPanelNode(selected);
  const showHeaderCanvasPanel = !hasCanvasSelection || selectedIsHeaderNode;
  const showBodyCanvasPanel = !hasCanvasSelection || !selectedIsHeaderNode;
  const panelShell = {
    border: "1px solid #ece9f5",
    borderRadius: "12px",
    background: "#fff",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(60,30,110,0.05)",
  };
  const bodyPanelShell = {
    ...panelShell,
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: "100%",
  };
  const bodyPanelContent = {
    padding: "8px",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  };
  const pageCanvasStyle = {
    minHeight: "70vh",
    ...page.style,
  };
  const pageCanvasInnerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px",
    minHeight: "inherit",
  };
  const pageCanvasStackStyle = {
    ...pageCanvasStyle,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px",
  };
  const panelTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 10px",
    borderBottom: "1px solid #f0edf8",
    fontSize: "11px",
    fontWeight: 700,
    color: "#6d28d9",
    background: "#faf9fd",
    letterSpacing: "0.02em",
  };
  const renderHtmlCodePanel = () => (
    <div style={{ padding: "14px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: "14px", overflowY: "auto" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: "150px" }}>
            <div style={{ fontSize: "12px", fontWeight: 800, color: "#1a1a2e" }}>{elakayAutoText("h_299f2b3dd485d5", "Code HTML généré")}</div>
            <div style={{ marginTop: "2px", fontSize: "10px", color: "#9b93b8" }}>
              {canUseHtmlCode ? elakayAutoText("h_4f6080f73a9bf9", "Synchronisé automatiquement avec la page courante.") : elakayAutoText("h_5799be79964b38", "Accès protégé par le droit d'achat HTML.")}
            </div>
          </div>
          <button disabled={!canUseHtmlCode} onClick={copyHtmlCode} title={!canUseHtmlCode ? htmlAccessMessage : undefined} style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px", color: htmlCodeCopied ? "#15803d" : "#4a4a68", cursor: canUseHtmlCode ? "pointer" : "not-allowed", opacity: canUseHtmlCode ? 1 : 0.45 }}>
            {htmlCodeCopied ? <Check size={13} /> : <Copy size={13} />}
            {htmlCodeCopied ? elakayAutoText("h_b3ae01521622cb", "Copié") : elakayAutoText("h_cdd28e1a695416", "Copier")}
          </button>
          <button disabled={!canUseHtmlCode} onClick={exportHtml} title={!canUseHtmlCode ? htmlAccessMessage : undefined} style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px", cursor: canUseHtmlCode ? "pointer" : "not-allowed", opacity: canUseHtmlCode ? 1 : 0.45 }}>
            <Download size={13} />{elakayAutoText("h_b332d06dcdd0b2", "Télécharger")}</button>
          <button disabled={!canUseHtmlCode} onClick={openHtmlPreview} style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px", cursor: canUseHtmlCode ? "pointer" : "not-allowed", opacity: canUseHtmlCode ? 1 : 0.45 }} title={canUseHtmlCode ? elakayAutoText("h_95e6aa78cd929a", "Ouvrir le HTML dans un nouvel onglet") : htmlAccessMessage}>
            <Eye size={13} />{elakayAutoText("h_f0f5300429dfee", "Aperçu")}</button>
          {canEditHtmlCode && (
            <>
              <button
                onClick={handleHtmlPanelUnlockClick}
                disabled={htmlPanelUnlocked}
                aria-label={elakayAutoText("h_pg_html_unlock_btn", "Modifier le code HTML")}
                title={htmlPanelUnlocked
                  ? elakayAutoText("h_pg_html_already_unlocked", "Édition déjà déverrouillée ci-dessous")
                  : elakayAutoText("h_pg_html_unlock_btn_title", "Modifier directement le code HTML de cette page (mot de passe requis)")}
                style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px", color: "#6d28d9", borderColor: "#c4b5fd", background: "#f5f3ff", cursor: htmlPanelUnlocked ? "default" : "pointer", opacity: htmlPanelUnlocked ? 0.55 : 1 }}>
                {htmlPanelUnlocked ? <Unlock size={13} /> : <Lock size={13} />}
              </button>
              <button
                onClick={() => setShowHtmlEditModal(true)}
                aria-label={elakayAutoText("h_pg_html_edit_btn", "Éditeur avancé (Preview/Console/pages multiples)")}
                title={elakayAutoText("h_pg_html_edit_btn_title2", "Ouvrir l'éditeur avancé : Preview, Console, CSS/JS, pages multiples, plein écran")}
                style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px", color: "#6d28d9", borderColor: "#c4b5fd", background: "#fff" }}>
                <Maximize2 size={13} />
              </button>
            </>
          )}
        </div>

        {/* Inline password unlock -- same verify-html-edit endpoint/permission
            as PageHtmlSourceModal, just embedded here instead of a popup. */}
        {htmlPanelPasswordPrompt && !htmlPanelUnlocked && (
          <form onSubmit={handleHtmlPanelUnlock} style={{ display: "flex", flexDirection: "column", gap: "6px", padding: "10px", marginBottom: "10px", border: "1px solid #ddd6fe", borderRadius: "10px", background: "#faf5ff" }}>
            <div style={{ fontSize: "11px", color: "#6d28d9", fontWeight: 700 }}>
              {elakayAutoText("h_pg_html_secure_desc", "This action allows direct HTML modification of the page. Confirm your account password to continue.")}
            </div>
            <input
              type="password"
              autoFocus
              autoComplete="current-password"
              value={htmlPanelPassword}
              onChange={(e) => setHtmlPanelPassword(e.target.value)}
              style={{ width: "100%", padding: "7px 10px", fontSize: "12px", border: "1px solid #d1d5db", borderRadius: "7px", boxSizing: "border-box" }}
            />
            {htmlPanelPasswordError && <div style={{ color: "#e11d48", fontSize: "11px" }}>{htmlPanelPasswordError}</div>}
            <div style={{ display: "flex", gap: "6px" }}>
              <button type="button" onClick={() => { setHtmlPanelPasswordPrompt(false); setHtmlPanelPassword(""); setHtmlPanelPasswordError(""); }} style={{ ...ghostBtn, flex: 1, justifyContent: "center", padding: "6px" }} disabled={htmlPanelVerifying}>
                {elakayAutoText("h_pg_html_cancel", "Cancel")}
              </button>
              <button type="submit" style={{ ...primaryBtn, flex: 1, justifyContent: "center", padding: "6px" }} disabled={htmlPanelVerifying || !htmlPanelPassword}>
                {htmlPanelVerifying ? elakayAutoText("h_pg_html_verifying", "Verifying…") : elakayAutoText("h_pg_html_unlock", "Unlock")}
              </button>
            </div>
          </form>
        )}

        {canUseHtmlCode ? (
          <textarea
            value={htmlCode}
            readOnly
            spellCheck={false}
            aria-label={elakayAutoText("h_299f2b3dd485d5", "Code HTML généré")}
            style={{ minHeight: "220px", width: "100%", fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontSize: "11px", lineHeight: 1.55, padding: "12px", border: "1px solid #302a50", borderRadius: "10px", background: "#151326", color: "#d9d4ed", outline: "none", resize: "none", whiteSpace: "pre", overflow: "auto", boxSizing: "border-box" }}
          />
        ) : (
          <div style={{ minHeight: "220px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px", padding: "28px", border: "1px solid #ddd6fe", borderRadius: "10px", background: "linear-gradient(135deg,#faf5ff,#f5f3ff)", textAlign: "center" }}>
            <FileCode size={34} color="#6d28d9" />
            <div style={{ fontSize: "14px", fontWeight: 900, color: "#2a2547" }}>{elakayAutoText("h_6be0940ba1ed42", "Code HTML verrouillé")}</div>
            <p style={{ maxWidth: "330px", margin: 0, fontSize: "12px", lineHeight: 1.55, color: "#7a73a0" }}>{htmlAccessMessage}</p>
            {activeHtmlShopId && (
              <button onClick={() => openShopPurchaseModal()} style={{ ...primaryBtn, justifyContent: "center" }}>
                <FileCode size={14} />{elakayAutoText("h_95af83ee60eb28", "Acheter le code HTML")}</button>
            )}
          </div>
        )}
      </div>

      {/* Inline editable section -- unlocked, edits htmlSourcePages[currentPageId]
          directly, no popup. The full modal (Maximize2 icon above) remains
          available for Preview/Console/CSS-JS tabs/Fullscreen/multi-page. */}
      {htmlPanelUnlocked && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "10px", border: "1px solid #c4b5fd", borderRadius: "10px", background: "#faf9fd" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "12px", fontWeight: 800, color: "#1a1a2e" }}>{elakayAutoText("h_pg_html_panel_title_edit", "Code HTML (édition)")}</div>
              <div style={{ marginTop: "2px", fontSize: "10px", color: "#9b93b8" }}>
                {htmlPanelManuallyEdited
                  ? `${elakayAutoText("h_pg_html_manually_edited", "Manually edited")}${htmlPanelEditedAt ? ` · ${new Date(htmlPanelEditedAt).toLocaleString()}` : ""}`
                  : elakayAutoText("h_pg_html_generated", "Generated from the builder")}
              </div>
            </div>
            <button
              onClick={() => { setHtmlPanelUnlocked(false); setHtmlPanelConfirmationToken(""); }}
              title={elakayAutoText("h_pg_html_lock_again", "Verrouiller l'édition")}
              style={{ ...ghostBtn, padding: "5px 8px", fontSize: "10px" }}>
              <Lock size={12} />
            </button>
          </div>

          {htmlPanelError && <div style={{ color: "#e11d48", fontSize: "11px" }}>{htmlPanelError}</div>}

          {htmlPanelLoading ? (
            <div style={{ minHeight: "220px", display: "flex", alignItems: "center", justifyContent: "center", color: "#9b93b8", fontSize: "12px" }}>
              {elakayAutoText("h_pg_html_loading", "Loading HTML source…")}
            </div>
          ) : (
            <textarea
              value={htmlPanelDraftHtml}
              onChange={(e) => setHtmlPanelDraftHtml(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              aria-label={elakayAutoText("h_pg_html_panel_title_edit", "Code HTML (édition)")}
              style={{ minHeight: "260px", width: "100%", fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontSize: "11px", lineHeight: 1.55, padding: "12px", border: "1px solid #302a50", borderRadius: "10px", background: "#151326", color: "#d9d4ed", outline: "none", resize: "vertical", whiteSpace: "pre", boxSizing: "border-box" }}
            />
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            <button onClick={handleHtmlPanelRestore} disabled={htmlPanelSaving || htmlPanelRestoring || htmlPanelLoading} style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px", color: "#e11d48", borderColor: "#fca5a5" }}>
              <RefreshCw size={12} style={{ marginRight: "4px" }} />{htmlPanelRestoring ? elakayAutoText("h_pg_html_restoring", "Restoring…") : elakayAutoText("h_pg_html_reset_generated", "Reset to Generated Code")}
            </button>
            <button onClick={handleHtmlPanelQuickReset} disabled={!htmlPanelDirty} style={{ ...ghostBtn, padding: "6px 9px", fontSize: "11px" }} title={elakayAutoText("h_pg_html_quick_reset_title", "Discard unsaved edits, back to the last loaded state")}>
              <Undo2 size={12} style={{ marginRight: "4px" }} />{elakayAutoText("h_pg_html_quick_reset", "Reset")}
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={handleHtmlPanelSave} disabled={htmlPanelSaving || !htmlPanelDirty || !htmlPanelDraftHtml.trim()} style={{ ...primaryBtn, padding: "6px 9px", fontSize: "11px" }}>
              <Save size={12} style={{ marginRight: "4px" }} />{htmlPanelSaving ? elakayAutoText("h_pg_html_saving", "Saving…") : elakayAutoText("h_pg_html_apply_save", "Save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
  const centerPanelContent = (
    <>
      {projectCssBundle && <style>{projectCssBundle}</style>}
      {storeOnly && (
        <div
          onClick={(event) => event.stopPropagation()}
          style={{ position:"absolute", top:showPreview ? "78px" : "14px", right:"16px", zIndex:60,
            display:"flex", alignItems:"center", gap:"7px", maxWidth:"calc(100% - 32px)",
            padding:"7px", background:"rgba(255,255,255,0.96)", border:"1px solid #e4dcfb",
            borderRadius:"12px", boxShadow:"0 10px 30px rgba(49,25,90,0.16)", backdropFilter:"blur(8px)" }}
        >
          {selectedCommerceShop && (
            <div title={selectedCommerceShop.title}
              style={{ maxWidth:"150px", padding:"0 5px", overflow:"hidden" }}>
              <div style={{ fontSize:"9px", fontWeight:800, color:
                selectedCommerceShop.source === "created" ? "#6d28d9" :
                selectedCommerceShop.source === "premium" ? "#b45309" : "#059669",
                textTransform:"uppercase", whiteSpace:"nowrap" }}>
                {SHOP_SOURCE_LABELS[selectedCommerceShop.source]}
              </div>
              <div style={{ fontSize:"11px", fontWeight:800, color:"#1a1a2e",
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {selectedCommerceShop.title}
              </div>
            </div>
          )}
          <button
            onClick={() => addShopToCart(selectedCommerceShop)}
            disabled={!selectedCommerceShop?.canPurchase}
            title={elakayAutoText("h_4ad7cdfe7797da", "Ajouter la boutique sélectionnée au panier")}
            style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 10px",
              borderRadius:"8px", border:"1px solid #c4b5fd",
              color:selectedCommerceShop?.canPurchase ? "#6d28d9" : "#9ca3af",
              background:selectedCommerceShop?.canPurchase ? "#f5f3ff" : "#f3f4f6",
              cursor:selectedCommerceShop?.canPurchase ? "pointer" : "not-allowed",
              fontSize:"10px", fontWeight:800, whiteSpace:"nowrap" }}
          >
            <ShoppingCart size={13} />{elakayAutoText("h_87c57ed173ea17", "Ajouter")}</button>
          <button
            onClick={() => openShopPurchaseModal(selectedCommerceShop)}
            disabled={purchaseShopChoices.length === 0}
            title={elakayAutoText("h_117b2cb95f52ff", "Acheter la boutique sélectionnée ou une boutique du panier")}
            style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 11px",
              borderRadius:"8px", border:"none", color:"#fff",
              background:purchaseShopChoices.length > 0 ? "linear-gradient(135deg,#d97706,#f59e0b)" : "#9ca3af",
              cursor:purchaseShopChoices.length > 0 ? "pointer" : "not-allowed",
              fontSize:"10px", fontWeight:800, whiteSpace:"nowrap" }}
          >
            <CreditCard size={13} />{elakayAutoText("h_2faf08bf354e78", "Achat")}</button>
          <button
            onClick={() => setShowShopCart(true)}
            title={elakayAutoText("h_6f5ee48aca448b", "Ouvrir le panier boutique")}
            aria-label={elakayAutoText("h_987c102c6688cc", "Ouvrir le panier boutique, {value0} article(s)", { value0: shopCart.length })}
            style={{ position:"relative", width:"32px", height:"32px", flexShrink:0,
              display:"grid", placeItems:"center", borderRadius:"8px",
              border:"1px solid #e4dcfb", color:"#6d28d9", background:"#fff", cursor:"pointer" }}
          >
            <ShoppingCart size={16} />
            {shopCart.length > 0 && (
              <span style={{ position:"absolute", top:"-6px", right:"-6px", minWidth:"17px", height:"17px",
                display:"grid", placeItems:"center", padding:"0 4px", borderRadius:"20px",
                background:"#e11d48", color:"#fff", border:"2px solid #fff",
                fontSize:"8px", fontWeight:900 }}>
                {shopCart.length}
              </span>
            )}
          </button>
        </div>
      )}
      {showPreview && (() => {
        const pvDev   = previewDevice;
        const pvMaxW  = pvDev === "mobile" ? "390px" : pvDev === "tablet" ? "768px" : "1200px";
        const pvBorder = pvDev === "mobile" ? "10px solid #1a1a2e" : pvDev === "tablet" ? "6px solid #374151" : "none";
        const pvRadius = pvDev !== "desktop" ? "24px" : "18px";
        const pvPage   = previewPageId || doc?.currentPageId || doc?.pages?.[0]?.id;
        const devBtnStyle = (d) => ({
          display: "flex", alignItems: "center", gap: "4px",
          padding: "5px 10px", fontSize: "11px", fontWeight: pvDev === d ? 700 : 500,
          border: `1px solid ${pvDev === d ? "#6d28d9" : "#ece9f5"}`,
          borderRadius: "7px", cursor: "pointer",
          background: pvDev === d ? "#f5f3ff" : "#fff",
          color: pvDev === d ? "#6d28d9" : "#7a73a0",
        });
        return (
          <div style={{ position: "absolute", inset: 0, overflowX: "auto", overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 16px 40px", gap: "10px", background: "radial-gradient(circle at 50% 0%, #efeafa, #f6f4fb)" }}>
            <div style={{ width: "100%", maxWidth: "1200px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", padding: "8px 12px", background: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(60,30,110,0.07)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 700, color: "#1a1a2e", marginRight: "4px" }}>
                <Eye size={15} color="#6d28d9" />
                {doc.meta?.title || elakayAutoText("h_f0f5300429dfee", "Aperçu")}
              </span>
              {boundData?.isLive
                ? <span style={{ fontSize: "10px", fontWeight: 700, color: "#059669", padding: "2px 8px", background: "#ecfdf5", borderRadius: "50px", border: "1px solid #a7f3d0" }}>● {boundData.label}</span>
                : boundData
                  ? <span style={{ fontSize: "10px", color: "#9b93b8", padding: "2px 8px", background: "#f9fafb", borderRadius: "50px", border: "1px solid #e5e7eb" }}>{elakayAutoText("h_b8f8cfd35c4d7c", "Démo ·")}{boundData.label}</span>
                  : <span style={{ fontSize: "10px", color: "#e11d48", padding: "2px 8px", background: "#fff1f2", borderRadius: "50px", border: "1px solid #fca5a5" }}>{elakayAutoText("h_bb79c780dc2e9e", "Aucune donnée liée")}</span>
              }

              <span style={{ flex: 1 }} />
              <div style={{ display: "flex", gap: "4px" }}>
                <button onClick={() => setPreviewDevice("desktop")} style={devBtnStyle("desktop")}><Monitor size={12} />{elakayAutoText("h_532c67fe1b5afa", "Desktop")}</button>
                <button onClick={() => setPreviewDevice("tablet")}  style={devBtnStyle("tablet")} ><Smartphone size={12} />{elakayAutoText("h_7f73b0f8ab423f", "Tablette")}</button>
                <button onClick={() => setPreviewDevice("mobile")}  style={devBtnStyle("mobile")} ><Smartphone size={12} />{elakayAutoText("h_b1d702459399a2", "Mobile")}</button>
              </div>

              {pages.length > 1 && (
                <div style={{ display: "flex", gap: "3px", marginLeft: "4px" }}>
                  {pages.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPreviewPageId(p.id)}
                      style={{ padding: "4px 8px", fontSize: "10px", fontWeight: pvPage === p.id ? 700 : 500, border: `1px solid ${pvPage === p.id ? "#6d28d9" : "#ece9f5"}`, borderRadius: "6px", cursor: "pointer", background: pvPage === p.id ? "#f5f3ff" : "#fff", color: pvPage === p.id ? "#6d28d9" : "#7a73a0", maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={p.name}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => { if (!shopSelectionReadOnly) setShowPreview(false); }}
                disabled={shopSelectionReadOnly}
                title={shopSelectionReadOnly ? saveBlockedReason : elakayAutoText("h_419649446b903e", "Retourner dans le Builder")}
                style={{ display: "flex", alignItems: "center", gap: "5px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, color: "#fff", background: shopSelectionReadOnly ? "#9ca3af" : "linear-gradient(135deg,#6d28d9,#8b5cf6)", border: "none", borderRadius: "8px", cursor: shopSelectionReadOnly ? "not-allowed" : "pointer" }}
              >
                {shopSelectionReadOnly ? <><Eye size={13} />{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</> : <><PenLine size={13} />{elakayAutoText("h_4759bf14d7a511", "Builder")}</>}
              </button>
              <button
                onClick={handleNewBuilder}
                style={{ display: "flex", alignItems: "center", gap: "5px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, color: "#6d28d9", background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: "8px", cursor: "pointer" }}
              >
                <Plus size={13} />{elakayAutoText("h_5bd0e9b0e368bb", "New Builder")}</button>
            </div>

            <div style={{ width: "100%", display: "flex", justifyContent: "center", paddingBottom: "24px" }}>
              <div style={{ width: pvMaxW, maxWidth: "100%", background: "#fff", borderRadius: pvRadius, overflow: "hidden", boxShadow: "0 8px 40px rgba(60,30,110,0.14)", border: pvBorder, transition: "width 0.3s ease, border 0.3s ease" }}>
                <PageRenderer
                  doc={doc}
                  pageData={boundData?.data ?? null}
                  device={pvDev}
                  pageId={pvPage}
                  infoSource={infoSource}
                  token={null}
                  onNavigate={(id) => setPreviewPageId(id)}
                  storeConfig={rendererStoreConfig ? { ...rendererStoreConfig, hidePageNavigation: false } : null}
                  // Pre-existing gap fixed here: this Preview panel never
                  // passed htmlSource at all, so a page's manually-edited
                  // HTML (</> Edit HTML Code) never showed up in the
                  // Builder's own Preview -- only on the real published shop
                  // page (ShopUser.js). Same props as every other
                  // PageRenderer consumer now.
                  htmlSource={savedPage?.htmlSource}
                  htmlSourcePages={savedPage?.htmlSourcePages}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {!showPreview && <div style={{ position: "absolute", inset: 0, overflowX: "auto", overflowY: "auto", padding: "32px", display: "flex", flexDirection: responsive ? "column" : "row", justifyContent: responsive ? "flex-start" : "center", alignItems: "center", gap: "20px" }} onClick={() => handleSelect(null)}>
        {responsive ? (
          <>
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center", padding: "10px 14px", background: "#fff", borderRadius: "12px", boxShadow: "0 2px 10px rgba(60,30,110,0.06)" }}>
              {DEVICE_PRESETS.map((p) => {
                const on = activeScreens.includes(p.id);
                return (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 10px", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: 600, background: on ? "#f3effe" : "transparent", color: on ? "#6d28d9" : "#9b93b8", border: `1px solid ${on ? "#e4dcfb" : "transparent"}` }}>
                    <input type="checkbox" checked={on} onChange={() => toggleScreen(p.id)} style={{ accentColor: "#6d28d9" }} />
                    {React.createElement(p.icon, { size: 13 })} {p.label}
                  </label>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: "28px", alignItems: "flex-start", justifyContent: "center", flexWrap: "wrap" }}>
              {DEVICE_PRESETS.filter((p) => activeScreens.includes(p.id)).map((p) => (
                <div key={p.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 700, color: p.kind === "mobile" ? "#d97706" : "#6d28d9" }}>
                    {React.createElement(p.icon, { size: 14 })} {p.label}
                  </span>
                  <DeviceContext.Provider value={p.kind}>
                    <div style={{ width: p.width === "100%" ? "680px" : p.width, maxWidth: "46vw", minHeight: "55vh", background: "#fff", borderRadius: "18px", boxShadow: "0 12px 40px rgba(60,30,110,0.10)", overflow: "hidden", alignSelf: "flex-start", border: p.kind === "mobile" ? "8px solid #1a1a2e" : "none" }} onClick={(e) => e.stopPropagation()}>
                      <div style={pageCanvasStyle}>
                        <div style={pageCanvasInnerStyle}>
                          {showHeaderCanvasPanel && <div style={panelShell}>
                            <div style={panelTitle}>
                              <span>{elakayAutoText("h_eb17a13d5c9a38", "PanneauHeader")}</span>
                              <span style={{ fontSize: "10px", color: "#9b93b8" }}>{headerPanelNodes.length}</span>
                            </div>
                            <div style={{ padding: "8px" }}>
                              {headerPanelNodes.length > 0 ? (
                                headerPanelNodes.map((node) => (
                                  <RenderNode key={node.id} node={node} selectedIds={selectedIds} onSelect={handleSelect} editable />
                                ))
                              ) : (
                                <div style={{ padding: "14px 10px", color: "#b3aacb", fontSize: "11px", textAlign: "center" }}>{elakayAutoText("h_bd399057d140e1", "Ajoutez un élément `headerShopSeller` pour ce panneau.")}</div>
                              )}
                            </div>
                          </div>}

                          {showBodyCanvasPanel && <div style={bodyPanelShell}>
                            <div style={panelTitle}>
                              <span>{elakayAutoText("h_d4b08929f2bc26", "PanneauBody")}</span>
                              <button onClick={handleBodySelect} title={elakayAutoText("h_97d442acec22d0", "Sélectionner le body de la page")} style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "6px", border: `1px solid ${bodySelected && !selected ? "#6d28d9" : "#e4dcfb"}`, background: bodySelected && !selected ? "#6d28d9" : "#f3effe", color: bodySelected && !selected ? "#fff" : "#6d28d9", cursor: "pointer", fontWeight: 600 }}>
                                {bodySelected && !selected ? elakayAutoText("h_85be56a857d8df", "✓ Body") : elakayAutoText("h_718a7e8a582e98", "Body")}
                              </button>
                            </div>
                            <div style={bodyPanelContent}>
                              {bodyPanelNodes.map((node) => (
                                <RenderNode key={node.id} node={node} selectedIds={selectedIds} onSelect={handleSelect} editable />
                              ))}
                              {bodyPanelNodes.length === 0 && (
                                <div style={{ textAlign:"center", padding:"60px 20px", color:"#b3aacb" }}>
                                  <div style={{ fontSize:"40px", marginBottom:"12px" }}>🎨</div>
                                  <p style={{ fontSize:"14px", fontWeight:600, color:"#6d28d9", margin:"0 0 6px" }}>{elakayAutoText("h_16bed7d2706192", "Page vide")}</p>
                                  <p style={{ fontSize:"12px", margin:"0 0 16px", lineHeight:1.6 }}>{elakayAutoText("h_838efe110f1c4d", "Ajoutez des éléments depuis le panneau gauche,")}<br/>{elakayAutoText("h_98a71bcbbd159b", "ou cliquez sur \"⚡ Sections rapides\"")}</p>
                                  <button
                                    onClick={() => { setLeftTab("add"); setShowLeftPanel(true); }}
                                    style={{ padding:"9px 20px", fontSize:"12px", fontWeight:700, color:"#fff", background:"linear-gradient(135deg,#6d28d9,#8b5cf6)", border:"none", borderRadius:"10px", cursor:"pointer" }}
                                  >{elakayAutoText("h_d85c5866c6481e", "+ Ajouter des éléments")}</button>
                                </div>
                              )}
                            </div>
                          </div>}
                        </div>
                      </div>
                    </div>
                  </DeviceContext.Provider>
                </div>
              ))}
            </div>
          </>
        ) : (
        <DeviceContext.Provider value={device}>
        <NodeUpdateContext.Provider value={saveNodeDoc}>
        <DataContext.Provider value={{ boundData, item: null }}>
        <div ref={canvasFrameRef} style={{ width: deviceWidth, maxWidth: "100%", minHeight: "70vh", background: "#fff", borderRadius: "18px", boxShadow: "0 12px 40px rgba(60,30,110,0.10)", transition: "width 0.3s ease", overflow: "hidden", alignSelf: "flex-start", position: "relative", border: device === "mobile" ? "8px solid #1a1a2e" : "none" }} onClick={(e) => e.stopPropagation()}>
          {freeMode ? (
            <div style={pageCanvasStackStyle}>
              {showHeaderCanvasPanel && <div style={panelShell}>
                <div style={panelTitle}>
                  <span>{elakayAutoText("h_eb17a13d5c9a38", "PanneauHeader")}</span>
                  <span style={{ fontSize: "10px", color: "#9b93b8" }}>{headerPanelNodes.length}</span>
                </div>
                <div style={{ padding: "8px" }}>
                  {headerPanelNodes.length > 0 ? (
                    headerPanelNodes.map((node) => (
                      <RenderNode key={node.id} node={node} selectedIds={selectedIds} onSelect={handleSelect} editable />
                    ))
                  ) : (
                    <div style={{ padding: "14px 10px", color: "#b3aacb", fontSize: "11px", textAlign: "center" }}>{elakayAutoText("h_bd399057d140e1", "Ajoutez un élément `headerShopSeller` pour ce panneau.")}</div>
                  )}
                </div>
              </div>}
              {showBodyCanvasPanel && <div style={bodyPanelShell}>
                <div style={panelTitle}>
                  <span>{elakayAutoText("h_233430d42040fa", "PanneauBody (Mode libre)")}</span>
                  <button onClick={handleBodySelect} title={elakayAutoText("h_97d442acec22d0", "Sélectionner le body de la page")} style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "6px", border: `1px solid ${bodySelected && !selected ? "#6d28d9" : "#e4dcfb"}`, background: bodySelected && !selected ? "#6d28d9" : "#f3effe", color: bodySelected && !selected ? "#fff" : "#6d28d9", cursor: "pointer", fontWeight: 600 }}>
                    {bodySelected && !selected ? elakayAutoText("h_85be56a857d8df", "✓ Body") : elakayAutoText("h_718a7e8a582e98", "Body")}
                  </button>
                </div>
                <FreeCanvas
                  nodes={bodyPanelNodes}
                  selectedIds={selectedIds}
                  onSelect={handleSelect}
                  onSetPosition={setPosition}
                  onNudgeSelected={nudgeSelected}
                  onResize={resizeElement}
                  device={device}
                  layoutDevice={layoutDevice}
                  pageStyle={{ minHeight: "100%", padding: "10px" }}
                  onGestureStart={history.beginGesture}
                  onGestureEnd={history.endGesture}
                  resolveNode={(id) => findInTree(page.children, id)}
                  isDroppable={(node, draggedId) => {
                    const draggedNode = findInTree(page.children, draggedId);
                    return !!draggedNode && isChildTypeAllowed(ELEMENT_TYPES[node.type], draggedNode.type);
                  }}
                  onDropReparent={(nodeId, containerId) => reparentElement(nodeId, containerId)}
                />
              </div>}
            </div>
          ) : (
            <div style={pageCanvasStyle} ref={normalCanvasRef}>
              <div style={pageCanvasInnerStyle}>
                {showHeaderCanvasPanel && <div style={panelShell}>
                  <div style={panelTitle}>
                    <span>{elakayAutoText("h_eb17a13d5c9a38", "PanneauHeader")}</span>
                    <span style={{ fontSize: "10px", color: "#9b93b8" }}>{headerPanelNodes.length}</span>
                  </div>
                  <div style={{ padding: "8px" }}>
                    {headerPanelNodes.length > 0 ? (
                      headerPanelNodes.map((node) => (
                        <div key={node.id} data-pbid={node.id} style={{ display: "contents" }}>
                          <RenderNode node={node} selectedIds={selectedIds} onSelect={handleSelect} editable />
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "14px 10px", color: "#b3aacb", fontSize: "11px", textAlign: "center" }}>{elakayAutoText("h_bd399057d140e1", "Ajoutez un élément `headerShopSeller` pour ce panneau.")}</div>
                    )}
                  </div>
                </div>}

                {showBodyCanvasPanel && <div style={bodyPanelShell}>
                  <div style={panelTitle}>
                    <span>{elakayAutoText("h_d4b08929f2bc26", "PanneauBody")}</span>
                    <button onClick={handleBodySelect} title={elakayAutoText("h_97d442acec22d0", "Sélectionner le body de la page")} style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "6px", border: `1px solid ${bodySelected && !selected ? "#6d28d9" : "#e4dcfb"}`, background: bodySelected && !selected ? "#6d28d9" : "#f3effe", color: bodySelected && !selected ? "#fff" : "#6d28d9", cursor: "pointer", fontWeight: 600 }}>
                      {bodySelected && !selected ? elakayAutoText("h_85be56a857d8df", "✓ Body") : elakayAutoText("h_718a7e8a582e98", "Body")}
                    </button>
                  </div>
                  <div style={bodyPanelContent}>
                    {bodyPanelNodes.map((node) => (
                      <div key={node.id} data-pbid={node.id} style={{ display: "contents" }}>
                        <RenderNode node={node} selectedIds={selectedIds} onSelect={handleSelect} editable />
                      </div>
                    ))}
                    {bodyPanelNodes.length === 0 && (
                      <div style={{ textAlign:"center", padding:"60px 20px", color:"#b3aacb" }}>
                        <div style={{ fontSize:"40px", marginBottom:"12px" }}>🎨</div>
                        <p style={{ fontSize:"14px", fontWeight:600, color:"#6d28d9", margin:"0 0 6px" }}>{elakayAutoText("h_16bed7d2706192", "Page vide")}</p>
                        <p style={{ fontSize:"12px", margin:"0 0 16px", lineHeight:1.6 }}>{elakayAutoText("h_838efe110f1c4d", "Ajoutez des éléments depuis le panneau gauche,")}<br/>{elakayAutoText("h_98a71bcbbd159b", "ou cliquez sur \"⚡ Sections rapides\"")}</p>
                        <button
                          onClick={() => { setLeftTab("add"); setShowLeftPanel(true); }}
                          style={{ padding:"9px 20px", fontSize:"12px", fontWeight:700, color:"#fff", background:"linear-gradient(135deg,#6d28d9,#8b5cf6)", border:"none", borderRadius:"10px", cursor:"pointer" }}
                        >{elakayAutoText("h_d85c5866c6481e", "+ Ajouter des éléments")}</button>
                      </div>
                    )}
                  </div>
                </div>}
              </div>
            </div>
          )}
          {/* Sélection/resize/reparenting pour un élément du flux normal (racine ou
              imbriqué) — le mode libre a déjà son propre chrome dans FreeCanvas. */}
          {!freeMode && selected && selectedIds.length === 1 && (
            <FlowSelectionOverlay
              nodeId={selected.id}
              containerEl={canvasFrameRef.current}
              label={ELEMENT_TYPES[selected.type]?.label || selected.type}
              parentLabel={(() => {
                const parent = findParentInTree(page.children, selected.id);
                return parent ? (ELEMENT_TYPES[parent.type]?.label || parent.type) : null;
              })()}
              onResize={resizeElement}
              onResizeStart={history.beginGesture}
              onResizeEnd={history.endGesture}
              onReparentDrop={(draggedId, containerId) => reparentElement(draggedId, containerId)}
              resolveNode={(id) => findInTree(page.children, id)}
              isDroppable={(node, draggedId) => {
                const draggedNode = findInTree(page.children, draggedId);
                return !!draggedNode && isChildTypeAllowed(ELEMENT_TYPES[node.type], draggedNode.type);
              }}
            />
          )}
        </div>
        </DataContext.Provider>
        </NodeUpdateContext.Provider>
        </DeviceContext.Provider>
        )}
      </div>}

    </>
  );

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", height: rootHeight, minHeight: rootMinHeight, background: "#f6f4fb", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#1a1a2e" }}>
      <style>{ANIMATION_KEYFRAMES}</style>
      <style>{customKeyframes}</style>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: #d6cfec; border-radius: 8px; }
        button:hover { filter: brightness(0.97); }
        button { transition: filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease; }
        input, select, textarea { font-family: inherit; }
        input:focus, select:focus, textarea:focus { outline: 2px solid #c9b8f5; outline-offset: 1px; }

        /* Alternance de couleurs entre les sections des panneaux gauche/droite (ex: Taille rapide / Tailles prédéfinies) */
        .pb-aside-left .pb-section:nth-child(odd),
        .pb-aside-right .pb-section:nth-child(odd) {
          background-color: #ffffff;
        }
        .pb-aside-left .pb-section:nth-child(even),
        .pb-aside-right .pb-section:nth-child(even) {
          background-color: #faf9fd;
        }

        /* --- RESPONSIVE de l'éditeur lui-même ---
           Sur écrans < 1100px : les panneaux latéraux deviennent rétractables (drawers).
           Le canevas garde toute la largeur pour rester confortable. */
        .pb-mobile-toggle { display: none; }
        @media (max-width: 1100px) {
          .pb-mobile-toggle { display: inline-flex !important; }
          .pb-aside-left, .pb-aside-right {
            position: fixed !important;
            top: 64px;
            bottom: 0;
            z-index: 50;
            box-shadow: 0 12px 40px rgba(40,20,80,0.18);
            transition: transform 0.25s ease;
          }
          .pb-aside-left { left: 0; transform: translateX(-100%); }
          .pb-aside-right { right: 0; transform: translateX(100%); }
          .pb-aside-left.open { transform: translateX(0); }
          .pb-aside-right.open { transform: translateX(0); }
          .pb-aside-backdrop {
            position: fixed; inset: 64px 0 0 0; background: rgba(20,12,40,0.4); z-index: 49;
          }
          .pb-header-actions { gap: 4px !important; }
          .pb-header-actions button span { display: none; }
        }
        @media (max-width: 1450px) {
          .pb-topbar { flex-wrap: wrap !important; row-gap: 8px; }
          .pb-header-actions {
            order: 3;
            width: 100%;
            overflow-x: auto;
            padding-bottom: 2px;
          }
          .pb-primary-actions {
            width: 100%;
            justify-content: flex-end;
            flex-wrap: wrap;
          }
        }
        @media (max-width: 700px) {
          .pb-aside-left, .pb-aside-right { width: 88vw !important; }
          .pb-header-title { display: none; }
        }`}</style>

      {/* Barre supérieure */}
      <header className="pb-topbar" style={{ display: "flex", flexDirection: "column", background: "#fff", borderBottom: "1px solid #ece9f5", boxShadow: "0 1px 3px rgba(40,20,80,0.04)", flexShrink: 0 }}>

        {/* ── Bannière brouillon restauration ── */}
        {showDraftBanner && (
          <div style={{ display:"flex", alignItems:"center", gap:"12px", padding:"7px 20px", background:"#fefce8", borderBottom:"1px solid #fde68a" }}>
            <span style={{ fontSize:"12px", color:"#92400e", flex:1 }}>{elakayAutoText("h_acfa3d5ae6ea01", "💾 Un brouillon local a été trouvé")}{autoSaveMeta?.savedAt ? ` (${formatRelativeTime(autoSaveMeta.savedAt)})` : ""}{elakayAutoText("h_ef9f916507bc12", "— voulez-vous le restaurer ?")}</span>
            <button onClick={restoreDraft}
              style={{ padding:"4px 14px", fontSize:"11px", fontWeight:700, color:"#fff", background:"#d97706", border:"none", borderRadius:"6px", cursor:"pointer" }}>{elakayAutoText("h_ef511f8e09f53e", "Restaurer")}</button>
            <button onClick={() => setShowDraftBanner(false)}
              style={{ padding:"4px 12px", fontSize:"11px", color:"#78350f", background:"transparent", border:"1px solid #fcd34d", borderRadius:"6px", cursor:"pointer" }}>{elakayAutoText("h_b0d64e49f37838", "Ignorer")}</button>
            <button onClick={clearLocalDraft}
              style={{ padding:"4px 12px", fontSize:"11px", color:"#e11d48", background:"transparent", border:"1px solid #fda4af", borderRadius:"6px", cursor:"pointer" }}>{elakayAutoText("h_1acfc1c7d76131", "Supprimer")}</button>
          </div>
        )}

        {/* ══ PARTIE 1 : Logo + contrôles appareil + vue + espace jusqu'à Détacher centre ══ */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 20px", flexWrap: "wrap" }}>
          {/* Hamburger mobile */}
          <button className="pb-mobile-toggle" onClick={() => setMobileLeft(true)}
            style={{ display: "none", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", border: "1px solid #ece9f5", borderRadius: "10px", background: "#fff", cursor: "pointer" }}>
            <Layers size={17} color="#6d28d9" />
          </button>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "34px", height: "34px", borderRadius: "9px", background: "linear-gradient(135deg,#6d28d9,#a855f7)", flexShrink: 0 }}>
            <Sparkles size={18} color="#fff" />
          </div>
          <div className="pb-header-title" style={{ flexShrink: 0 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "16px", fontWeight: 700, letterSpacing: "-0.02em" }}>{elakayAutoText("h_e24b181d7d4aec", "Page Builder")}</div>
          </div>
          {/* Séparateur */}
          <div style={{ width: "1px", height: "28px", background: "#ece9f5", flexShrink: 0 }} />
          {/* Desktop / Mobile / Responsive */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px", background: "#f3effe", padding: "3px", borderRadius: "9px", flexShrink: 0 }}>
            <button onClick={() => { setResponsive(false); setPresetId("desktop-xl"); }} style={toggleBtn(!responsive && device === "desktop")}><Monitor size={14} />{elakayAutoText("h_532c67fe1b5afa", "Desktop")}</button>
            <button onClick={() => { setResponsive(false); setPresetId("mobile"); }} style={toggleBtn(!responsive && device === "mobile")}><Smartphone size={14} />{elakayAutoText("h_b1d702459399a2", "Mobile")}</button>
            <button onClick={() => { setResponsive(true); setFreeMode(false); }} style={toggleBtn(responsive)}><Columns size={14} />{elakayAutoText("h_3e23c24f6c0136", "Responsive")}</button>
          </div>
          {/* Sélecteur taille précise */}
          {!responsive && (
            <select value={presetId} onChange={(e) => setPresetId(e.target.value)}
              style={{ padding: "7px 9px", fontSize: "12px", fontWeight: 600, border: "1px solid #ece9f5", borderRadius: "8px", background: "#fff", color: "#4a4a68", outline: "none", cursor: "pointer" }}>
              <optgroup label={elakayAutoText("h_532c67fe1b5afa", "Desktop")}>
                {DEVICE_PRESETS.filter((p) => p.kind === "desktop").map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </optgroup>
              <optgroup label={elakayAutoText("h_da6ce067aac477", "Mobile / Tablette")}>
                {DEVICE_PRESETS.filter((p) => p.kind === "mobile").map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </optgroup>
            </select>
          )}
          {/* Position libre */}
          <button onClick={toggleFreeMode} disabled={responsive}
            style={{ ...toggleBtn(freeMode), background: freeMode ? "#6d28d9" : "#f3effe", color: freeMode ? "#fff" : "#7a73a0", boxShadow: "none", padding: "7px 12px", opacity: responsive ? 0.5 : 1 }}
            title={elakayAutoText("h_026f98de83da41", "Positionnement libre")}>
            <Move size={14} />{elakayAutoText("h_a688511115fd79", "Libre")}</button>
          {/* Annuler / Rétablir — Ctrl/Cmd+Z, Ctrl/Cmd+Maj+Z (voir useKeyboardManipulation) */}
          <div style={{ display: "flex", alignItems: "center", gap: "3px", flexShrink: 0 }}>
            <button onClick={() => history.undo()} disabled={!history.canUndo}
              title={elakayAutoText("h_undo_title_3a71", "Annuler (Ctrl/Cmd+Z)")}
              style={{ ...ghostBtn, padding: "7px 8px", opacity: history.canUndo ? 1 : 0.4, cursor: history.canUndo ? "pointer" : "not-allowed" }}>
              <Undo2 size={14} />
            </button>
            <button onClick={() => history.redo()} disabled={!history.canRedo}
              title={elakayAutoText("h_redo_title_9c02", "Rétablir (Ctrl/Cmd+Maj+Z)")}
              style={{ ...ghostBtn, padding: "7px 8px", opacity: history.canRedo ? 1 : 0.4, cursor: history.canRedo ? "pointer" : "not-allowed" }}>
              <Redo2 size={14} />
            </button>
          </div>
          {/* Simple / Expert */}
          <button onClick={toggleSimpleMode}
            style={{ display:"flex", alignItems:"center", gap:"4px", padding:"5px 10px", fontSize:"11px", fontWeight:700, borderRadius:"8px", border:`1px solid ${simpleMode ? "#6d28d9" : "#e4dcfb"}`, background: simpleMode ? "#6d28d9" : "#f5f3ff", color: simpleMode ? "#fff" : "#6d28d9", cursor:"pointer" }}>
            {simpleMode ? elakayAutoText("h_b52db88d07d1b5", "🎨 Simple") : elakayAutoText("h_0528dffa0dc7cd", "⚙ Expert")}
          </button>
          {/* Séparateur */}
          <div style={{ flex: 1 }} />
          {/* Templates */}
          {!hideHeaderTemplateButton && (
            <button onClick={() => setShowTemplatePicker(true)} style={{ ...ghostBtn, color: "#6d28d9", borderColor: "#c9b8f5", background: "#f7f3fe" }}><Sparkles size={14} />{elakayAutoText("h_f25b700ed9f092", "Templates")}</button>
          )}
          {doc?.pageMode === "contentPage" && contentPageDatasetEntries.length > 0 && (
            <label
              title={boundData?.data?._id ? `${elakayAutoText("h_0edb0b86ad3327", "ID page contenu")} : ${boundData.data._id}` : elakayAutoText("h_9b9303ec0e37f0", "LanguagePage reste le rendu par défaut si aucune source contentPage n'est liée.")}
              style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 9px", border:"1px solid #a7f3d0", borderRadius:"9px", background:"#ecfdf5", color:"#047857", fontSize:"11px", fontWeight:700, maxWidth:"320px" }}
            >
              <FileText size={13} />
              <span style={{ whiteSpace:"nowrap" }}>{elakayAutoText("h_2c9edbcf1428b2", "Content")}</span>
              <select
                value={selectedContentPageSwitchValue}
                onChange={(event) => selectContentPageDataset(event.target.value)}
                style={{ minWidth:"150px", maxWidth:"220px", border:"1px solid #bbf7d0", borderRadius:"7px", background:"#fff", color:"#065f46", fontSize:"11px", fontWeight:700, padding:"5px 7px", outline:"none" }}
              >
                <option value="">{elakayAutoText("h_d602741ab021d9", "LanguagePage défaut")}</option>
                {contentPageDatasetEntries.map(([datasetKey, dataset]) => {
                  const pageId = dataset?.data?._id || dataset?.data?.pageId || datasetKey;
                  const shortId = String(pageId).length > 18 ? `${String(pageId).slice(0, 18)}…` : String(pageId);
                  return (
                    <option key={datasetKey} value={pageId}>
                      {dataset.isLive ? "● " : ""}{dataset.label} · {shortId}
                    </option>
                  );
                })}
              </select>
            </label>
          )}
          {/* Aperçu toggle */}
          {!hideHeaderPreviewToggle && (
            <label
              title={shopSelectionReadOnly ? saveBlockedReason : undefined}
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", fontSize: "13px", fontWeight: showPreview ? 700 : 600, cursor: shopSelectionReadOnly ? "not-allowed" : "pointer", borderWidth: "1px", borderStyle: "solid", borderColor: showPreview ? "#c4b5fd" : "#ece9f5", borderRadius: "9px", background: showPreview ? "#f5f3ff" : "#fff", color: showPreview ? "#6d28d9" : "#4a4a68", userSelect: "none", opacity: shopSelectionReadOnly ? 0.75 : 1 }}
            >
              <input type="checkbox" checked={showPreview} disabled={shopSelectionReadOnly} onChange={(e) => setShowPreview(e.target.checked)} style={{ accentColor: "#6d28d9", width: "14px", height: "14px", cursor: shopSelectionReadOnly ? "not-allowed" : "pointer" }} />
              {shopSelectionReadOnly
                ? <><Eye size={13} />{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</>
                : showPreview ? <><PenLine size={13} />{elakayAutoText("h_4759bf14d7a511", "Builder")}</> : <><Eye size={13} />{elakayAutoText("h_f0f5300429dfee", "Aperçu")}</>}
            </label>
          )}
          {/* ── Bouton dynamique : Sauvegarder / Modifier ── */}
          {!storeOnly && onSave && (
            <button
              onClick={handleSaveToDb}
              disabled={isSaving || saveBlocked}
              title={saveBlocked
                ? saveBlockedReason
                : isEditingSavedPage ? elakayAutoText("h_52054b2450b262", "Modifier la page sélectionnée en BD") : elakayAutoText("h_fcba2965c1e133", "Sauvegarder la nouvelle page en BD")}
              style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 14px", fontSize:"12px", fontWeight:700,
                borderRadius:"9px", border: saveBlocked ? "1px solid #e5e7eb" : "none",
                cursor: (isSaving || saveBlocked) ? "not-allowed" : "pointer",
                background: saveBlocked ? "#f3f4f6" : isSaving ? "#9ca3af" : isEditingSavedPage ? "linear-gradient(135deg,#2563eb,#4f46e5)" : "linear-gradient(135deg,#6d28d9,#a855f7)",
                color: saveBlocked ? "#9ca3af" : "#fff",
                boxShadow: (isSaving || saveBlocked) ? "none" : "0 2px 8px rgba(109,40,217,0.25)",
                transition:"all 0.15s" }}>
              {isSaving
                ? <><span style={{ display:"inline-block", width:"11px", height:"11px", border:"2px solid rgba(255,255,255,0.4)", borderTopColor:"#fff", borderRadius:"50%", animation:"pb-spin 0.7s linear infinite" }}/> {saveBusyLabel}</>
                : saveBlocked
                  ? <><Download size={13}/>{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</>
                  : isEditingSavedPage ? <><PenLine size={13}/>{elakayAutoText("h_f260e757cec750", "Modifier")}</> : <><Download size={13}/>{elakayAutoText("h_9ada14395d664f", "Sauvegarder")}</>
              }
            </button>
          )}
          {savedPageId && onEditInDb && !saveBlocked && (
            <button onClick={onEditInDb}
              title={elakayAutoText("h_bf50af9731abcd", "Modifier les métadonnées (titre, prix, statut…)")}
              style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 14px", fontSize:"12px", fontWeight:700,
                borderRadius:"9px", border:"1px solid #6d28d9", background:"#f5f3ff", color:"#6d28d9", cursor:"pointer",
                transition:"all 0.15s" }}>
              <PenLine size={13}/>{elakayAutoText("h_db3ce186d6a794", "Métadonnées")}</button>
          )}
          {/* ── Boutique action buttons (mode boutique uniquement) ── */}
          {storeOnly && (
            <>
              {/* Save — pour les boutiques créées (own) */}
              {onSave && (
                <button
                  onClick={handleSaveToDb}
                  disabled={isSaving || saveBlocked}
                  title={saveBlocked
                    ? saveBlockedReason
                    : isEditingSavedPage ? elakayAutoText("h_10502c3b2e528b", "Modifier la boutique sélectionnée") : elakayAutoText("h_1265ca311d9c4d", "Sauvegarder la nouvelle boutique")}
                  style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 13px", fontSize:"12px", fontWeight:700,
                    borderRadius:"9px", border: saveBlocked ? "1px solid #e5e7eb" : "none",
                    cursor: (isSaving || saveBlocked) ? "not-allowed" : "pointer",
                    background: saveBlocked ? "#f3f4f6" : isSaving ? "#9ca3af" : isEditingSavedPage ? "linear-gradient(135deg,#2563eb,#4f46e5)" : "linear-gradient(135deg,#6d28d9,#a855f7)",
                    color: saveBlocked ? "#9ca3af" : "#fff" }}>
                  {isSaving ? saveBusyLabel
                    : saveBlocked ? <><Check size={13}/>{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</>
                    : isEditingSavedPage ? <><PenLine size={13}/>{elakayAutoText("h_f260e757cec750", "Modifier")}</> : <><Check size={13}/>{elakayAutoText("h_9ada14395d664f", "Sauvegarder")}</>}
                </button>
              )}
            </>
          )}
          {shopSelectionReadOnlyEffective && onFamilyUnlockPage && Family(infoSource?.userInfo?.email) && (
            <button
              onClick={openFamilyUnlockModal}
              disabled={familyUnlockBusy}
              title={elakayAutoText("h_9d3a1f8e2c7b40", "Débloquer l'édition en place après validation du mot de passe Développeur")}
              style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 13px", fontSize:"12px", fontWeight:700,
                borderRadius:"9px", border:"1px solid #f59e0b", background:"#fffbeb", color:"#b45309",
                cursor: familyUnlockBusy ? "not-allowed" : "pointer" }}>
              <Unlock size={13}/>{elakayAutoText("h_4c8d2b91a06e35", "Débloquer (Développeur)")}</button>
          )}
          {shopSelectionReadOnly && familyUnlockedPassword && (
            <span title={elakayAutoText("h_7f1e9a530bd642", "Édition en place débloquée pour cette page — re-confirmée à chaque sauvegarde")}
              style={{ display:"flex", alignItems:"center", gap:"5px", padding:"6px 12px", fontSize:"11px", fontWeight:700,
                borderRadius:"9px", background:"#f0fdf4", color:"#16a34a", border:"1px solid #bbf7d0" }}>
              🔓{elakayAutoText("h_2e6a4f81730bcd", "Développeur débloqué")}</span>
          )}
          {/* Détacher centre */}
          <button onClick={toggleDetachedCenter} style={ghostBtn} title={centerDetached ? elakayAutoText("h_ff39f37f932d4c", "Ramener le panneau central") : elakayAutoText("h_e4b5842874dbc8", "Détacher le panneau central")}>
            <Maximize2 size={14} /> {centerDetached ? elakayAutoText("h_572c4b074bbe07", "Rattacher") : elakayAutoText("h_8d47651715aa53", "Détacher centre")}
          </button>
          {/* Aide */}
          {/* <button onClick={() => setShowHelp(true)}
            style={{ display:"flex", alignItems:"center", gap:"5px", padding:"7px 13px", fontSize:"12px", fontWeight:700,
              borderRadius:"9px", border:"1px solid #c4b5fd", background:"#f5f3ff", color:"#6d28d9", cursor:"pointer" }}
            title="Guide Page Builder">
            <HelpCircle size={14} /> Aide
          </button> */}
          {/* Hamburger mobile droit */}
          <button className="pb-mobile-toggle" onClick={() => setMobileRight(true)}
            style={{ display: "none", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", border: "1px solid #ece9f5", borderRadius: "10px", background: "#fff", cursor: "pointer" }}>
            <PanelRightOpen size={17} color="#6d28d9" />
          </button>
        </div>

        {/* ══ PARTIE 2 : Actions secondaires + UN bouton Composants riches ══ */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 20px 8px", borderTop: "1px solid #f3effe", flexWrap: "wrap" }}>
          <input ref={fileInputRef} type="file" accept=".json,.html,.htm,application/json,text/html" onChange={importPageFile} style={{ display: "none" }} />
          {/* ── Seul bouton d'ajout : Composants riches ── */}
          <button
            onClick={() => { setLeftTab("add"); setShowLeftPanel(true); setOpenGroups((p) => ({ ...p, rich: true })); }}
            disabled={shopSelectionReadOnly}
            style={{ display: "flex", alignItems: "center", gap: "5px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6d28d9,#a855f7)", border: "none", borderRadius: "8px", cursor: "pointer" }}
            title={shopSelectionReadOnly ? saveBlockedReason : elakayAutoText("h_a7f05d70aecb80", "Ouvrir le groupe Composants riches dans le panneau gauche")}>
            <Plus size={13} />{elakayAutoText("h_0860e88d172f38", "Composants riches")}</button>
          <button
            onClick={handleNewBuilder}
            style={{ display:"flex", alignItems:"center", gap:"5px", padding:"6px 14px", fontSize:"12px",
              fontWeight:700, color:"#6d28d9", background:"#f5f3ff", border:"1px solid #c4b5fd",
              borderRadius:"8px", cursor:"pointer" }}
            title={elakayAutoText("h_be7db8ddebf6bc", "Créer un nouveau Builder vide")}>
            <Plus size={13} />{elakayAutoText("h_5bd0e9b0e368bb", "New Builder")}</button>
          {/* Séparateur */}
          <div style={{ width: "1px", height: "22px", background: "#ece9f5" }} />
          {/* Indicateur auto-save */}
          {autoSaveMeta?.savedAt && (
            <span title={elakayAutoText("h_c22227165c3f96", "Brouillon local {value0}", { value0: formatRelativeTime(autoSaveMeta.savedAt) })}
              style={{ fontSize: "11px", color: "#9b93b8", display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap", userSelect: "none" }}>
              💾 {formatRelativeTime(autoSaveMeta.savedAt)}
            </span>
          )}
          {/* Sauvegarder / Modifier en BD */}
          {onSave && (
            <button onClick={handleSaveToDb} disabled={isSaving || saveBlocked}
              title={saveBlocked
                ? saveBlockedReason
                : isEditingSavedPage ? elakayAutoText("h_081ac94b592e5e", "Modifier la page sélectionnée") : elakayAutoText("h_eac8cc10909b54", "Sauvegarder une nouvelle page")}
              style={{ ...primaryBtn,
                background: (isSaving || saveBlocked) ? "#9ca3af" : isEditingSavedPage ? "#2563eb" : "#16a34a",
                borderColor: (isSaving || saveBlocked) ? "#9ca3af" : isEditingSavedPage ? "#2563eb" : "#16a34a",
                opacity: (isSaving || saveBlocked) ? 0.8 : 1,
                cursor: (isSaving || saveBlocked) ? "not-allowed" : "pointer" }}>
              {isSaving
                ? saveBusyLabel
                : saveBlocked
                  ? <><Check size={14} />{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</>
                  : isEditingSavedPage
                  ? <><PenLine size={14} />{elakayAutoText("h_f260e757cec750", "Modifier")}</>
                  : <><Download size={14} />{elakayAutoText("h_9ada14395d664f", "Sauvegarder")}</>}
            </button>
          )}
          {isEditingSavedPage && doc?.pageMode === "store" && !saveBlocked && (
            <button
              onClick={handleRenameSavedPage}
              disabled={isSaving}
              style={{ ...ghostBtn, display:"flex", alignItems:"center", gap:"5px",
                color:"#2563eb", borderColor:"#bfdbfe", background:"#eff6ff",
                cursor:isSaving ? "not-allowed" : "pointer", opacity:isSaving ? 0.6 : 1 }}
              title={elakayAutoText("h_48e84e335496bc", "Modifier le nom de la boutique")}>
              <PenLine size={14} />{elakayAutoText("h_8e8a86e80cbb7b", "Renommer")}</button>
          )}
          <button onClick={() => fileInputRef.current?.click()} disabled={shopSelectionReadOnly} title={shopSelectionReadOnly ? saveBlockedReason : elakayAutoText("h_7fb045cfb7a64c", "Importer un document PageBuilder JSON ou un HTML exporté")} style={{ ...ghostBtn, cursor: shopSelectionReadOnly ? "not-allowed" : "pointer", opacity: shopSelectionReadOnly ? 0.55 : 1 }}><Upload size={14} />{elakayAutoText("h_0336ab8a4b4dce", "Importer JSON/HTML")}</button>
          <button onClick={exportJson} style={ghostBtn}><FileJson size={14} />{elakayAutoText("h_031a4e76f0b39d", "JSON")}</button>
          <button onClick={() => selectPanelTab("htmlCode")} style={ghostBtn} title={canUseHtmlCode ? elakayAutoText("h_8592d33e2b50fb", "Afficher le code HTML généré dans le panneau latéral") : htmlAccessMessage}><FileCode size={14} />{elakayAutoText("h_121344756746b3", "Code HTML")}</button>
          <button disabled={!canUseHtmlCode} onClick={exportHtml} title={!canUseHtmlCode ? htmlAccessMessage : undefined} style={{ ...ghostBtn, cursor: canUseHtmlCode ? "pointer" : "not-allowed", opacity: canUseHtmlCode ? 1 : 0.45 }}><Download size={14} />{elakayAutoText("h_1b55b08dcc5985", "Télécharger HTML")}</button>
          <button disabled={!canUseHtmlCode} onClick={openHtmlPreview} style={{ ...ghostBtn, cursor: canUseHtmlCode ? "pointer" : "not-allowed", opacity: canUseHtmlCode ? 1 : 0.45 }} title={canUseHtmlCode ? elakayAutoText("h_f06a436885e231", "Lire le HTML en pleine page") : htmlAccessMessage}>
            <PanelRightOpen size={14} />{elakayAutoText("h_ba63befa405f71", "Pleine page")}</button>
          {canEditHtmlCode && (
            <button onClick={() => setShowHtmlEditModal(true)}
              style={{ ...ghostBtn, color:"#6d28d9", borderColor:"#c4b5fd", background:"#f5f3ff" }}
              title={elakayAutoText("h_pg_html_edit_btn_title", "Edit the raw HTML source of this page (password required)")}>
              <Code2 size={14} />{elakayAutoText("h_pg_html_edit_btn", "</> Edit HTML Code")}
            </button>
          )}
          {/* Séparateur droit */}
          <div style={{ flex: 1 }} />
          {/* Toggles panneaux (déplacés ici depuis Ligne 2) */}
          <button onClick={() => setShowLeftPanel((v) => !v)} title={showLeftPanel ? elakayAutoText("h_3f2d791012370a", "Masquer gauche") : elakayAutoText("h_234ba5ccb9476f", "Afficher gauche")}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "26px", height: "22px", borderRadius: "6px", border: "none", cursor: "pointer", background: showLeftPanel ? "#6d28d9" : "transparent", color: showLeftPanel ? "#fff" : "#c4b5fd" }}>
            <PanelLeft size={13} />
          </button>
          <button onClick={() => setShowRightPanel((v) => !v)} title={showRightPanel ? elakayAutoText("h_cc3ec4f47834f1", "Masquer droite") : elakayAutoText("h_a432987617833c", "Afficher droite")}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "26px", height: "22px", borderRadius: "6px", border: "none", cursor: "pointer", background: showRightPanel ? "#6d28d9" : "transparent", color: showRightPanel ? "#fff" : "#c4b5fd" }}>
            <PanelRight size={13} />
          </button>
        </div>

      </header>

      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* Panneau gauche */}
        {(() => {
          const leftIsDetached = detachedPanelTab?.side === "left";
          const activeLeftTab = leftIsDetached ? detachedPanelTab?.key : leftTab;
          const leftPanel = (
        <aside className={`pb-aside-left${mobileLeft ? " open" : ""}${leftIsDetached ? " pb-detached-tab-panel" : ""}`} style={{ width: leftIsDetached ? "100%" : "258px", height: leftIsDetached ? "100%" : "auto", background: "#fff", borderRight: "1px solid #ece9f5", display: leftIsDetached || showLeftPanel ? "flex" : "none", flexDirection: "column", minHeight: 0, overflowX: "auto", overflowY: "auto", overscrollBehavior: "contain" }}>
          {/* ── Tabs gauche (dynamiques) ── */}
          {(() => {
            const ALL_TAB_DEFS = [
              { key:"add",      icon:<Plus size={13}/>,           label:pbt("t001","Éléments") },
              { key:"html",     icon:<FileCode size={13}/>,       label:pbt("t002","HTML") },
              { key:"layers",   icon:<Layers size={13}/>,         label:pbt("t003","Calques") },
              { key:"pages",    icon:<FileJson size={13}/>,       label:pbt("t004","Pages") },
              { key:"boutique", icon:<Package size={13}/>,        label:pbt("t005","Boutique") },
              { key:"props",    icon:<Settings2 size={13}/>,      label:pbt("t009","Général") },
              { key:"anim",     icon:<Sparkles size={13}/>,       label:pbt("t012","Anim.") },
              { key:"links",    icon:<ChevronRight size={13}/>,   label:pbt("t011","Liens") },
              { key:"json",     icon:<Code2 size={13}/>,          label:pbt("t008","JSON") },
              { key:"htmlCode", icon:<FileCode size={13}/>,       label:elakayAutoText("h_121344756746b3", "Code HTML") },
              { key:"data",     icon:<Boxes size={13}/>,          label:pbt("t006","Données") },
              { key:"shopLive", icon:<Package size={13}/>,        label:pbt("t007","Live ⚡") },
              { key:"comments", icon:<MessageSquare size={13}/>,  label:elakayAutoText("h_70440046a3dc2e", "Notes") },
              { key:"combine",  icon:<FolderClosed size={13}/>,   label:elakayAutoText("h_687778b839b54d", "Combinés") },
              { key:"mySaves",  icon:<Download size={13}/>,       label:elakayAutoText("h_16c551be41249f", "Mes pages") },
            ];
            const visibleLeft = ALL_TAB_DEFS.filter((d) =>
              panelTabSides[d.key] === "left" &&
              (!leftIsDetached || d.key === detachedPanelTab?.key) &&
              (!shopSelectionReadOnly || ["boutique", "mySaves"].includes(d.key))
            );
            const cols = Math.min(visibleLeft.length, 3) || 1;
            const rows = Math.ceil(visibleLeft.length / cols);
            const activeLabel = ALL_TAB_DEFS.find(d => d.key === activeLeftTab)?.label || "";
            return (
              <div style={{ borderBottom: "1px solid #ece9f5" }}>
                {/* Grille des tabs */}
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: "4px", padding: "8px 8px 4px" }}>
                  {visibleLeft.map(({ key, icon, label }) => (
                    <button
                      key={key}
                      onClick={() => setLeftTab(key)}
                      style={{ ...tabBtn(activeLeftTab === key), width:"100%", padding:"7px 2px", fontSize:"10px", display:"flex", flexDirection:"column", alignItems:"center", gap:"2px",
                        // tabBtn() sets the `border` SHORTHAND (border:"none") -- never add a
                        // separate `borderColor` longhand alongside it (that combination is
                        // exactly what triggers React's "Removing a style property ... when a
                        // conflicting property is set" warning as this condition toggles across
                        // renders, and border-style stays "none" regardless, so the color was
                        // never actually visible anyway). The gradient background alone already
                        // carries the "active" highlight here.
                        ...(key === "boutique" && activeLeftTab === "boutique" ? { background:"linear-gradient(135deg,#6d28d9,#8b5cf6)", color:"#fff" } : {}),
                      }}
                    >
                      {icon}{label}
                    </button>
                  ))}
                  {/* Bouton Aperçu / Edit — visible uniquement quand la page a du contenu */}
                  {!hideLeftPreviewToggle && page.children.length > 0 && (
                    <button
                      onClick={() => { if (!shopSelectionReadOnly) setShowPreview(v => !v); }}
                      disabled={shopSelectionReadOnly}
                      title={shopSelectionReadOnly ? saveBlockedReason : showPreview ? elakayAutoText("h_c84094f381dad8", "Retour en mode édition") : elakayAutoText("h_7b6cca56b5ef26", "Prévisualiser la page")}
                      style={{
                        display:"flex", flexDirection:"column", alignItems:"center", gap:"2px",
                        padding:"7px 2px", fontSize:"10px", fontWeight: 700, cursor:shopSelectionReadOnly ? "not-allowed" : "pointer",
                        borderRadius:"9px", borderWidth:"1px", borderStyle:"solid",
                        borderColor: showPreview ? "#6d28d9" : "#ece9f5",
                        background: showPreview
                          ? "linear-gradient(135deg,#6d28d9,#8b5cf6)"
                          : "transparent",
                        color: showPreview ? "#fff" : "#7a73a0",
                        transition:"all 0.15s", width:"100%",
                      }}
                    >
                      {shopSelectionReadOnly ? <Eye size={13}/> : showPreview ? <PenLine size={13}/> : <Eye size={13}/>}
                      {shopSelectionReadOnly ? elakayAutoText("h_3bdb363e1c0690", "Lecture") : showPreview ? elakayAutoText("h_5301648dcf6b53", "Edit") : elakayAutoText("h_f0f5300429dfee", "Aperçu")}
                    </button>
                  )}
                </div>
                {/* Bouton de déplacement → vers panneau droit */}
                <div style={{ display:"flex", justifyContent:"flex-end", gap:"5px", padding:"2px 8px 5px" }}>
                  {!leftIsDetached && (
                    <button
                      onClick={() => { if (panelTabSides[activeLeftTab] === "left") moveTabToPanel(activeLeftTab, "right"); }}
                      disabled={panelTabSides[activeLeftTab] !== "left"}
                      title={elakayAutoText("h_e4a30c9421c151", "Déplacer l'onglet \"{value0}\" vers le panneau droit", { value0: activeLabel })}
                      style={{ display:"flex", alignItems:"center", gap:"4px", padding:"3px 8px", borderRadius:"6px", border:"1px solid #e4dcfb", background:"#f5f3ff", color:panelTabSides[activeLeftTab]==="left"?"#6d28d9":"#c4b5fd", cursor:panelTabSides[activeLeftTab]==="left"?"pointer":"not-allowed", fontSize:"10px", fontWeight:600, transition:"all 0.15s" }}
                    >
                      <span style={{ fontFamily:"ui-monospace,monospace", color:"#9b93b8" }}>{cols}×{rows}</span>
                      {activeLabel} →
                    </button>
                  )}
                  <button
                    onClick={() => leftIsDetached ? closeDetachedPanelTab() : openDetachedPanelTab("left", activeLeftTab, activeLabel)}
                    title={leftIsDetached ? `Rattacher l'onglet "${activeLabel}"` : `Détacher l'onglet "${activeLabel}"`}
                    style={{ display:"flex", alignItems:"center", gap:"4px", padding:"3px 8px", borderRadius:"6px", border:"1px solid #d8cdf7", background:leftIsDetached?"#6d28d9":"#fff", color:leftIsDetached?"#fff":"#6d28d9", cursor:"pointer", fontSize:"10px", fontWeight:700 }}
                  >
                    {leftIsDetached ? <ChevronLeft size={12}/> : <Maximize2 size={12}/>}
                    {leftIsDetached ? elakayAutoText("h_572c4b074bbe07", "Rattacher") : elakayAutoText("h_e7f310727801a8", "Détacher")}
                  </button>
                </div>
              </div>
            );
          })()}
          <div style={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "auto", padding: "4px 12px 16px" }}>
            {activeLeftTab === "add" ? (
              <>
                <CollapsibleGroup open={openGroups.quick} onToggle={() => toggleOpenGroup("quick")} label={elakayAutoText("h_0a167aead0ca34", "⚡ Sections rapides")} count={4}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px" }}>
                    {[
                      { label:elakayAutoText("h_ff9afef1eae0a9", "Texte"),    icon:"T",  fn: () => ({ id:uid(), type:"text", text:elakayAutoText("h_db0dbb3f32d95a", "Votre texte ici"), tag:"p", style:{ fontSize:"16px", color:"#4a4a68", lineHeight:"1.7", margin:"0 0 16px" } }) },
                      { label:elakayAutoText("h_50e19fda0d5b4b", "Image"),    icon:"🖼", fn: () => ({ id:uid(), type:"image", src:"", style:{ width:"100%", borderRadius:"12px" } }) },
                      { label:elakayAutoText("h_17f5d4f0fb0dc9", "Hero"),     icon:"🏆", fn: () => ({ id:uid(), type:"container", style:{ padding:"56px 40px", background:"linear-gradient(135deg,#6d28d9,#a855f7)", borderRadius:"20px", textAlign:"center" }, children:[ {id:uid(), type:"heading", tag:"h1", text:elakayAutoText("h_4fac2a0f71936d", "Votre titre"), style:{ fontSize:"42px", fontWeight:"800", color:"#fff", margin:"0 0 14px", lineHeight:"1.1" }}, {id:uid(), type:"text", tag:"p", text:elakayAutoText("h_d6508e91e35527", "Sous-titre de présentation"), style:{ fontSize:"18px", color:"#f0e9ff", margin:"0 0 28px" }}, {id:uid(), type:"button", text:elakayAutoText("h_c1037398239181", "Commencer →"), style:{ display:"inline-block", padding:"14px 32px", fontSize:"16px", fontWeight:"700", color:"#6d28d9", background:"#fff", border:"none", borderRadius:"12px", cursor:"pointer" }} ] }) },
                      { label:elakayAutoText("h_48000473cdad77", "Produits"), icon:"🛒", fn: () => ({ id:uid(), type:"shopProductGrid", style:{ width:"100%" }, options:{ limit:12, gridAnimated:false, animateCards:false, resultAnimated:false }, bindSource:"store.products" }) },
                    ].map(({ label, icon, fn }) => (
                      <button key={label} onClick={() => { const el = fn(); setChildren([...page.children, el]); setSelectedIds([el.id]); setShowRightPanel(true); }}
                        style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", padding:"8px 4px", fontSize:"11px", fontWeight:600, color:"#6d28d9", background:"#f3effe", border:"1px solid #e4dcfb", borderRadius:"10px", cursor:"pointer" }}>
                        <span style={{ fontSize:"18px" }}>{icon}</span>{label}
                      </button>
                    ))}
                  </div>
                </CollapsibleGroup>
                {Array.isArray(elementPresets) && elementPresets.map((grp) => (
                  <CollapsibleGroup key={grp.key} open={openGroups[grp.key] !== false} onToggle={() => setOpenGroups((p) => ({ ...p, [grp.key]: p[grp.key] === false }))} label={grp.label} count={grp.items.length}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px" }}>
                      {grp.items.map(({ key, label, icon, make }) => (
                        <button key={key} type="button" data-element-preset={key}
                          onClick={() => { const el = { ...make(), id: uid() }; setChildren([...page.children, el]); setSelectedIds([el.id]); setShowRightPanel(true); }}
                          style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", padding:"8px 4px", fontSize:"11px", fontWeight:600, color:"#0f5f7a", background:"#edf7fa", border:"1px solid #b9d6e0", borderRadius:"10px", cursor:"pointer" }}>
                          <span style={{ fontSize:"18px" }}>{icon}</span>{label}
                        </button>
                      ))}
                    </div>
                  </CollapsibleGroup>
                ))}
                <CollapsibleGroup open={openGroups.base} onToggle={() => toggleOpenGroup("base")} label={elakayAutoText("h_077fe9c54ed5ed", "Base")} icon={Type} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="base").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="base")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.layout} onToggle={() => toggleOpenGroup("layout")} label={elakayAutoText("h_f09e7eee2eefe8", "Mise en page")} icon={Columns} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="layout").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="layout")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.rich} onToggle={() => toggleOpenGroup("rich")} label={elakayAutoText("h_0860e88d172f38", "Composants riches")} icon={Sparkles} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="rich").length}>
                  <LxCPicker onPick={insertLxC} label={elakayAutoText("h_fd64b09eca15ae", "Grille l × c")} />
                  <div style={{ height:"1px", background:"#f0edf8", margin:"8px 0" }} />
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="rich")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.form} onToggle={() => toggleOpenGroup("form")} label={elakayAutoText("h_edb3637fe7e2c6", "Formulaires")} icon={TextCursorInput} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="form").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="form")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.media} onToggle={() => toggleOpenGroup("media")} label={elakayAutoText("h_486d82f4995b6d", "Médias")} icon={Video} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="media").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="media")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                {/* Icon Elements — catégorie séparée, icônes réelles (SVG) via iconKit, distincte des icônes d'UI */}
                <CollapsibleGroup open={openGroups.icons} onToggle={() => toggleOpenGroup("icons")} label={elakayAutoText("h_icons_group", "Icônes")} icon={Star} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="icons").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="icons")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.documents} onToggle={() => toggleOpenGroup("documents")} label={elakayAutoText("h_687c82861c956e", "Documents")} icon={FileText} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="documents").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="documents")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.data} onToggle={() => toggleOpenGroup("data")} label={elakayAutoText("h_e3e0c5ec8a3b34", "Données")} icon={BarChart3} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="data").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="data")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.custom} onToggle={() => toggleOpenGroup("custom")} label={elakayAutoText("h_7a25f4ee29df7d", "Composants custom")} icon={Boxes} count={Object.keys(CUSTOM_COMPONENTS).length}>
                  <CardGridButtons entries={Object.entries(CUSTOM_COMPONENTS)} onClick={addCustom} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.shopHeader} onToggle={() => toggleOpenGroup("shopHeader")} label={elakayAutoText("h_a7f28403d7113f", "Panneau Header")} icon={Monitor} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="shopHeader").length} note="Élément d’en-tête boutique, avec ses propres styles et actions (recherche, filtre, modes).">
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="shopHeader")} onClick={addElement} cardHeight="76px" activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.shopLive} onToggle={() => toggleOpenGroup("shopLive")} label={elakayAutoText("h_f322b814019146", "Boutique Live")} icon={Package} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="shopLive").length} note={<>{elakayAutoText("h_86fa68a7cb2a0a", "Se synchronisent avec les données boutique (source")}<strong style={{ color:"#6d28d9" }}>{elakayAutoText("h_3a21295d813c26", "store")}</strong>).</>}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="shopLive")} onClick={addElement} cardHeight="76px" activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
              </>
            ) : activeLeftTab === "html" ? (
              <>
                <CollapsibleGroup open={openGroups.html} onToggle={() => toggleOpenGroup("html")} label={elakayAutoText("h_d4d881140eb33e", "Éléments HTML")} icon={FileCode} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="html").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="html")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <CollapsibleGroup open={openGroups.htmlForm} onToggle={() => toggleOpenGroup("htmlForm")} label={elakayAutoText("h_32915dfad6d484", "Champs (mêmes propriétés)")} icon={TextCursorInput} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="form").length}>
                  <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="form")} onClick={addElement} activeKeys={selectedElementTypes.active} directActiveKeys={selectedElementTypes.direct} />
                </CollapsibleGroup>
                <p style={{ fontSize:"11px", color:"#b3aacb", marginTop:"8px", lineHeight:1.6 }}>{elakayAutoText("h_175faa2933a4ab", "Ces éléments utilisent les mêmes propriétés que les autres, plus leurs attributs HTML spécifiques dans le panneau de droite.")}</p>
              </>
            ) : activeLeftTab === "layers" ? (
              <div>
                {page.children.length === 0
                  ? <p style={{ fontSize: "13px", color: "#9b93b8", padding: "12px" }}>{elakayAutoText("h_123f4152c90f54", "Aucun élément. Ajoutez-en depuis l'onglet « Éléments ».")}</p>
                  : page.children.map((n) => (
                      <LayerItem key={n.id} node={n} depth={0} selectedIds={selectedIds} onSelect={handleSelect} onMove={moveElement} onDelete={deleteElement} />
                    ))}
              </div>
            ) : activeLeftTab === "templates" ? (
              (() => {
                // Helper : charge un doc BD complet dans l'éditeur
                const loadDbShop = async (id, source = "created", item = null) => {
                  const { token } = isAuthenticated() || {};
                  try {
                    const localTemplate = isLocalDefaultShopId(id)
                      ? SHOP_TEMPLATES.find((template) =>
                          String(template._id) === String(id) || template.key === item?.templateKey
                        )
                      : null;
                    const data = localTemplate
                      ? { ...(item || {}), doc: localTemplate.make() }
                      : await getPageBuilderById(id, token);
                    const newDoc = data?.doc || data;
                    if (!newDoc) return;
                    const migrated = migrateDoc(newDoc);
                    selectShopSource(source, item || data);
                    setDoc(migrated);
                    setSelectedIds([]);
                    setJsonText(JSON.stringify(migrated, null, 2));
                    if (allDatasets.store) bindDataset("store", allDatasets.store);
                    setActiveTemplateKey(migrated.meta?.templateKey || id);
                    setShowPreview(source !== "created");
                    setLeftTab(source === "created" ? "add" : "boutique");
                    notifyLoadedPage(migrated, id, data, source);
                  } catch {}
                };
                // Helper : ouvre le même modal d'achat que la zone centrale.
                const purchaseEditorTpl = (tpl) => {
                  const target = {
                    id: String(tpl._id),
                    source: "premium",
                    title: tpl.title,
                    description: tpl.description || "",
                    thumbnail: tpl.thumbnail || "",
                    price: Math.max(0, Number(tpl.price) || 0),
                    authorType: tpl.authorType || "",
                    canPurchase: true,
                  };
                  selectShopSource("premium", tpl);
                  openShopPurchaseModal(target);
                };
                // Sous-section header
                const SectionHdr = ({ emoji, label, count, color = "#6d28d9" }) => (
                  <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"6px 4px 4px", marginBottom:"4px", borderBottom:`1px solid ${color}22` }}>
                    <span style={{ fontSize:"13px" }}>{emoji}</span>
                    <span style={{ fontSize:"10px", fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.05em", flex:1 }}>{label}</span>
                    {count > 0 && <span style={{ fontSize:"9px", background:`${color}18`, color, padding:"1px 6px", borderRadius:"20px", fontWeight:700 }}>{count}</span>}
                  </div>
                );
                // Carte DB (shop acheté ou créé)
                const DbShopCard = ({ item, source, onLoad }) => {
                  const readOnly = source !== "created";
                  const itemId = getShopItemId(item);
                  return (
                  <div style={{ marginBottom:"8px", border:"1px solid #ece9f5", borderRadius:"10px", background:"#fff", padding:"8px 10px" }}>
                    <div style={{ fontSize:"12px", fontWeight:700, color:"#1a1a2e", marginBottom:"2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{getShopItemTitle(item)}</div>
                    <div style={{ fontSize:"10px", color:"#9b93b8", marginBottom:"6px" }}>
                      {item.pageMode || item.pageId?.pageMode || elakayAutoText("h_3a21295d813c26", "store")} · {readOnly ? elakayAutoText("h_a9a41b7135c5b2", "lecture seule") : item.status || elakayAutoText("h_d4c4f7e41fe151", "modifiable")}
                    </div>
                    <button onClick={() => onLoad(itemId, source, item)}
                      style={{ ...primaryBtn, width:"100%", justifyContent:"center", fontSize:"11px", padding:"5px 10px", background:readOnly?"#64748b":"#6d28d9" }}>
                      {readOnly ? <Eye size={12} /> : <PenLine size={12} />} {readOnly ? elakayAutoText("h_8a754f1f426ce1", "Voir") : elakayAutoText("h_f260e757cec750", "Modifier")}
                    </button>
                  </div>
                  );
                };
                // Carte template éditeur (à vendre)
                const EditorTplCard = ({ tpl, onPreview, onPurchase }) => (
                  <div style={{ marginBottom:"8px", border:"1px solid #fef3c7", borderRadius:"10px", background:"#fffbeb", padding:"8px 10px" }}>
                    <div style={{ fontSize:"12px", fontWeight:700, color:"#1a1a2e", marginBottom:"2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tpl.title}</div>
                    <div style={{ fontSize:"10px", color:"#9b93b8", marginBottom:"6px" }}>{tpl.description || elakayAutoText("h_281b2d85ce599a", "Template boutique")}</div>
                    <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
                      <span style={{ fontSize:"12px", fontWeight:800, color:"#d97706" }}>${tpl.price || 0}</span>
                      <button onClick={() => onPreview(tpl)}
                        style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"4px", padding:"5px 8px", fontSize:"11px", fontWeight:700, color:"#92400e", background:"#fff", border:"1px solid #f59e0b", borderRadius:"7px", cursor:"pointer" }}>
                        <Eye size={11}/>{elakayAutoText("h_8a754f1f426ce1", "Voir")}</button>
                      <button onClick={() => onPurchase(tpl)}
                        style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:"4px", padding:"5px 8px", fontSize:"11px", fontWeight:700, color:"#fff", background:"linear-gradient(135deg,#d97706,#f59e0b)", border:"none", borderRadius:"7px", cursor:"pointer" }}>{elakayAutoText("h_dfc5a2d3cf67e0", "🛒 Acheter")}</button>
                    </div>
                  </div>
                );
                return (
                  <div>
                    {/* 1 — Boutiques achetées */}
                    <SectionHdr emoji="🛒" label={elakayAutoText("h_d2c7cc666bb624", "Achetées")} count={myBoughtShops.length} color="#d97706" />
                    {myBoughtShops.length === 0
                      ? <p style={{ fontSize:"10px", color:"#c4b5fd", margin:"0 0 10px 4px" }}>{elakayAutoText("h_0044f189c63355", "Aucune boutique achetée.")}</p>
                      : myBoughtShops.map((s) => <DbShopCard key={getShopItemId(s)} item={s} source="bought" onLoad={loadDbShop} />)
                    }
                    {/* 2 — Boutiques créées */}
                    <SectionHdr emoji="🎨" label={elakayAutoText("h_4b3f024a2e1b35", "Créées")} count={myCreatedShops.length} color="#6d28d9" />
                    {myCreatedShops.length === 0
                      ? <p style={{ fontSize:"10px", color:"#c4b5fd", margin:"0 0 10px 4px" }}>{elakayAutoText("h_de9f5e86aa31d2", "Aucune boutique créée.")}</p>
                      : myCreatedShops.map((s) => <DbShopCard key={getShopItemId(s)} item={s} source="created" onLoad={loadDbShop} />)
                    }
                    {/* 3 — Templates par défaut (SHOP_TEMPLATES) */}
                    <SectionHdr emoji="⚡" label={elakayAutoText("h_c8c562ce931744", "Templates par défaut")} count={boutiqueTemplates.length + defaultShops.length} color="#059669" />
                    {boutiqueTemplates.map((tpl) => (
                      <div key={tpl.key} style={{ marginBottom:"8px", border:"1px solid #d1fae5", borderRadius:"10px", background:"#f0fdf4", overflow:"hidden" }}>
                        <div style={{ display:"flex", gap:"4px", padding:"6px 8px 4px" }}>
                          {tpl.palette.map((c, i) => <span key={i} style={{ width:"10px", height:"10px", borderRadius:"50%", background:c, border:"1px solid rgba(0,0,0,0.08)" }} />)}
                        </div>
                        <div style={{ padding:"0 8px 8px" }}>
                          <div style={{ fontSize:"12px", fontWeight:700, color:"#1a1a2e", marginBottom:"2px" }}>{tpl.label}</div>
                          <div style={{ fontSize:"10px", color:"#6b7280", marginBottom:"6px", lineHeight:1.4 }}>{tpl.description}</div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:"5px" }}>
                            <button onClick={() => handleBoutiqueTemplate(tpl)}
                              style={{ ...primaryBtn, width:"100%", justifyContent:"center", fontSize:"11px", padding:"5px 10px", background:"#059669", border:"none" }}>
                              <Eye size={12} />{elakayAutoText("h_8a754f1f426ce1", "Voir")}</button>
                            <button
                              onClick={() => addShopToCart({
                                id: tpl._id || tpl.key,
                                source: "default",
                                title: tpl.label,
                                description: tpl.description || "",
                                thumbnail: "",
                                price: 0,
                                authorType: "admin",
                                templateKey: tpl.key,
                                canPurchase: true,
                              })}
                              title={elakayAutoText("h_40945907080ad7", "Ajouter au panier boutique")}
                              style={{ width:"30px", display:"grid", placeItems:"center", border:"1px solid #10b981",
                                borderRadius:"7px", color:"#047857", background:"#ecfdf5", cursor:"pointer" }}>
                              <ShoppingCart size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {defaultShops.map((shop) => (
                      <DbShopCard key={getShopItemId(shop)} item={shop} source="default" onLoad={loadDbShop} />
                    ))}
                    {/* 4 — Templates éditeur/admin/family (à vendre) */}
                    <SectionHdr emoji="💎" label={elakayAutoText("h_a26e1f1bec8f8d", "Templates premium")} count={editorTemplates.length} color="#b45309" />
                    {editorTemplates.length === 0
                      ? <p style={{ fontSize:"10px", color:"#c4b5fd", margin:"0 0 10px 4px" }}>{elakayAutoText("h_0438dda1e06ab6", "Aucun template premium disponible.")}</p>
                      : editorTemplates.map((tpl) => (
                          <EditorTplCard
                            key={tpl._id}
                            tpl={tpl}
                            onPreview={(item) => loadDbShop(item._id, "premium", item)}
                            onPurchase={purchaseEditorTpl}
                          />
                        ))
                    }
                  </div>
                );
              })()

            ) : activeLeftTab === "shopLive" ? (
              <ShopLiveSettingPanel element={selected} onUpdate={updateElement} page={page} />
            ) : activeLeftTab === "boutique" ? (
              /* ── Galerie Boutique compacte — 4 catégories ── */
              (() => {
                const loadDbShopCompact = async (id, source = "created", item = null) => {
                  const { token } = isAuthenticated() || {};
                  try {
                    const localTemplate = isLocalDefaultShopId(id)
                      ? SHOP_TEMPLATES.find((template) =>
                          String(template._id) === String(id) || template.key === item?.templateKey
                        )
                      : null;
                    const data = localTemplate
                      ? { ...(item || {}), doc: localTemplate.make() }
                      : await getPageBuilderById(id, token);
                    const newDoc = data?.doc || data;
                    if (!newDoc) return;
                    const migrated = migrateDoc(newDoc);
                    selectShopSource(source, item || data);
                    setDoc(migrated); setSelectedIds([]); setJsonText(JSON.stringify(migrated,null,2));
                    if (allDatasets.store) bindDataset("store", allDatasets.store);
                    setActiveTemplateKey(migrated.meta?.templateKey || id);
                    setShowPreview(source !== "created");
                    notifyLoadedPage(migrated, id, data, source);
                  } catch {}
                };
                const row = (key, label, accent, btn, onAction, readOnly = false, secondary = null) => (
                  <div key={key}
                    style={{ display:"flex", alignItems:"center", gap:"5px", marginBottom:"3px", padding:"4px 6px", borderRadius:"7px", borderTop:`1px solid ${activeTemplateKey===key?"#c4b5fd":"#ece9f5"}`, borderRight:`1px solid ${activeTemplateKey===key?"#c4b5fd":"#ece9f5"}`, borderBottom:`1px solid ${activeTemplateKey===key?"#c4b5fd":"#ece9f5"}`, borderLeft:`3px solid ${accent}`, background:activeTemplateKey===key?"#f5f3ff":"#fff" }}>
                    <span style={{ flex:1, fontSize:"10px", fontWeight:activeTemplateKey===key?700:500, color:activeTemplateKey===key?"#6d28d9":"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 }}>{label}</span>
                    <button onClick={onAction} title={readOnly ? elakayAutoText("h_4dbeb1d861c1f0", "Voir en lecture seule") : elakayAutoText("h_f260e757cec750", "Modifier")} style={{ flexShrink:0, width:"22px", height:"22px", display:"flex", alignItems:"center", justifyContent:"center", border:"none", borderRadius:"5px", cursor:"pointer", color:"#fff", background:accent, fontSize:"11px" }}>{btn}</button>
                    {secondary && (
                      <button onClick={secondary.onAction} title={secondary.title} style={{ flexShrink:0, width:"22px", height:"22px", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #f59e0b", borderRadius:"5px", cursor:"pointer", color:"#92400e", background:"#fffbeb", fontSize:"10px" }}>
                        {secondary.btn}
                      </button>
                    )}
                  </div>
                );
                const CatHdr = ({ emoji, label, color }) => (
                  <div style={{ fontSize:"9px", fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.05em", margin:"8px 0 3px 2px" }}>{emoji} {label}</div>
                );
                return (
                  <div>
                    <CatHdr emoji="🛒" label={elakayAutoText("h_d2c7cc666bb624", "Achetées")} color="#d97706" />
                    {myBoughtShops.length === 0
                      ? <div style={{ fontSize:"9px", color:"#c4b5fd", marginBottom:"4px", marginLeft:"4px" }}>—</div>
                      : myBoughtShops.map((s) => {
                          const id = getShopItemId(s);
                          return row(id, getShopItemTitle(s), "#d97706", "👁", () => loadDbShopCompact(id, "bought", s), true);
                        })
                    }
                    <CatHdr emoji="🎨" label={elakayAutoText("h_4b3f024a2e1b35", "Créées")} color="#6d28d9" />
                    {myCreatedShops.length === 0
                      ? <div style={{ fontSize:"9px", color:"#c4b5fd", marginBottom:"4px", marginLeft:"4px" }}>—</div>
                      : myCreatedShops.map((s) => {
                          const id = getShopItemId(s);
                          return row(id, getShopItemTitle(s), "#6d28d9", "✏", () => loadDbShopCompact(id, "created", s));
                        })
                    }
                    <CatHdr emoji="⚡" label={elakayAutoText("h_4550c68ee1cf77", "Par défaut")} color="#059669" />
                    {boutiqueTemplates.map((tpl) => {
                      const isActive = activeTemplateKey === tpl.key;
                      return row(
                        tpl.key,
                        tpl.label.replace("Boutique ","").replace("Matrix ","M."),
                        isActive?"#6d28d9":"#059669",
                        "👁",
                        () => handleBoutiqueTemplate(tpl),
                        true,
                        {
                          btn: "🛒",
                          title: elakayAutoText("h_0ae29edb383a9a", "Ajouter cette boutique par défaut au panier"),
                          onAction: () => addShopToCart({
                            id: tpl._id || tpl.key,
                            source: "default",
                            title: tpl.label,
                            description: tpl.description || "",
                            thumbnail: "",
                            price: 0,
                            authorType: "admin",
                            templateKey: tpl.key,
                            canPurchase: true,
                          }),
                        }
                      );
                    })}
                    {defaultShops.map((shop) => {
                      const id = getShopItemId(shop);
                      return row(
                        id,
                        getShopItemTitle(shop),
                        "#059669",
                        "👁",
                        () => loadDbShopCompact(id, "default", shop),
                        true,
                        {
                          btn: "🛒",
                          title: elakayAutoText("h_40945907080ad7", "Ajouter au panier boutique"),
                          onAction: () => addShopToCart({
                            id,
                            source: "default",
                            title: getShopItemTitle(shop),
                            description: shop.description || "",
                            thumbnail: shop.thumbnail || "",
                            price: getShopItemPrice(shop),
                            authorType: shop.authorType || "admin",
                            canPurchase: isMongoObjectId(id),
                          }),
                        }
                      );
                    })}
                    <CatHdr emoji="💎" label={elakayAutoText("h_6c2f2888c561e0", "Premium")} color="#b45309" />
                    {editorTemplates.length === 0
                      ? <div style={{ fontSize:"9px", color:"#c4b5fd", marginBottom:"4px", marginLeft:"4px" }}>—</div>
                      : editorTemplates.map((tpl) => row(
                          tpl._id,
                          tpl.title,
                          "#b45309",
                          "👁",
                          () => loadDbShopCompact(tpl._id, "premium", tpl),
                          true,
                          {
                            btn: "🛒",
                            title: elakayAutoText("h_40945907080ad7", "Ajouter au panier boutique"),
                            onAction: () => addShopToCart({
                              id: String(tpl._id),
                              source: "premium",
                              title: tpl.title,
                              description: tpl.description || "",
                              thumbnail: tpl.thumbnail || "",
                              price: Math.max(0, Number(tpl.price) || 0),
                              authorType: tpl.authorType || "",
                              canPurchase: true,
                            }),
                          }
                        ))
                    }
                  </div>
                );
              })()

            ) : activeLeftTab === "props" ? (
              selectedIds.length > 1
                ? <MultiSelectPanel count={selectedIds.length} onStyle={updateSelectedStyle} onDelete={deleteSelected} onClear={() => handleSelect(null)} freeMode={freeMode} onNudgeSelected={nudgeSelected} />
                : bodySelected && !selected
                  ? <PageBodyMappingPanel page={page} setPageProp={setPageProp} boundData={boundData} setBoundData={setBoundData} doc={doc} datasets={allDatasets} onDeselect={() => setBodySelected(false)} />
                  : <PropertiesPanel element={selected} onUpdate={updateElement} onDelete={() => selected && deleteElement(selected.id)} onDuplicate={duplicateElement} freeMode={freeMode} onNudge={nudge} onAddInside={() => setLeftTab("add")} device={device} layoutDevice={layoutDevice} onBringFront={() => moveSelectedLayer("front")} onSendBack={() => moveSelectedLayer("back")} pages={pages} onNavigate={navigateToPage} boundData={boundData} setBoundData={setBoundData} doc={doc} setPageMode={setPageMode} lockPageMode={lockPageMode} datasets={allDatasets} simpleMode={simpleMode} page={page} onUpdateNode={updateNodePatch} onBulkUpdateNodes={bulkUpdateNodes} />
            ) : activeLeftTab === "comments" ? (
              /* ── Notes / Commentaires d'éléments ── */
              <div>
                {selected ? (
                  <div style={{ marginBottom:"12px", padding:"10px", background:"#f5f3ff", borderRadius:"8px", border:"1px solid #e4dcfb" }}>
                    <div style={{ fontSize:"11px", fontWeight:700, color:"#6d28d9", marginBottom:"6px" }}>{elakayAutoText("h_eb43f1bc456089", "Note sur :")}{ELEMENT_TYPES[selected.type]?.label || selected.type}
                    </div>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={elakayAutoText("h_b6954f9a094130", "Votre annotation...")}
                      rows={3}
                      style={{ width:"100%", padding:"7px 9px", fontSize:"12px", border:"1px solid #e4dcfb", borderRadius:"6px", resize:"vertical", outline:"none", boxSizing:"border-box", background:"#fff" }}
                    />
                    <button
                      onClick={addComment}
                      disabled={!newComment.trim()}
                      style={{ marginTop:"5px", padding:"5px 12px", fontSize:"11px", fontWeight:700, color:"#fff", background: newComment.trim() ? "#6d28d9" : "#c4b5fd", border:"none", borderRadius:"6px", cursor: newComment.trim() ? "pointer" : "not-allowed" }}>{elakayAutoText("h_c3812bb924c5dc", "+ Ajouter la note")}</button>
                  </div>
                ) : (
                  <p style={{ fontSize:"11px", color:"#b3aacb", marginBottom:"12px", lineHeight:1.5 }}>{elakayAutoText("h_cdf8cca2adc0cc", "Sélectionnez un élément pour y ajouter une note.")}</p>
                )}
                {elementComments.length === 0 ? (
                  <p style={{ fontSize:"12px", color:"#9b93b8", padding:"8px 0", lineHeight:1.5 }}>{elakayAutoText("h_b6ec5fabd46d39", "Aucune note. Annotez vos éléments pendant la conception.")}</p>
                ) : (
                  elementComments.map((c) => (
                    <div key={c.id} style={{ marginBottom:"8px", padding:"8px 10px", background: c.resolved ? "#f0fdf4" : "#fff", border:`1px solid ${c.resolved ? "#bbf7d0" : "#e4dcfb"}`, borderRadius:"8px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"3px" }}>
                        <span style={{ fontSize:"10px", fontWeight:600, color:"#6d28d9", background:"#f3effe", padding:"1px 6px", borderRadius:"4px" }}>{c.elementType}</span>
                        <span style={{ fontSize:"9px", color:"#b3aacb", marginLeft:"auto" }}>
                          {new Date(c.createdAt).toLocaleString("fr-FR",{ day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                        </span>
                      </div>
                      <p style={{ fontSize:"12px", color:"#2a2547", lineHeight:1.5, margin:"0 0 6px" }}>{c.text}</p>
                      <div style={{ display:"flex", gap:"5px" }}>
                        <button
                          onClick={() => setElementComments((prev) => prev.map((x) => x.id === c.id ? { ...x, resolved: !x.resolved } : x))}
                          style={{ fontSize:"10px", padding:"2px 8px", borderRadius:"4px", border:`1px solid ${c.resolved ? "#16a34a" : "#6d28d9"}`, background:"transparent", color: c.resolved ? "#16a34a" : "#6d28d9", cursor:"pointer" }}>
                          {c.resolved ? elakayAutoText("h_a076b6a1eae564", "✓ Résolu") : elakayAutoText("h_2a5ddafb65f59d", "Résoudre")}
                        </button>
                        <button
                          onClick={() => setElementComments((prev) => prev.filter((x) => x.id !== c.id))}
                          style={{ fontSize:"10px", padding:"2px 8px", borderRadius:"4px", border:"1px solid #fda4af", background:"transparent", color:"#e11d48", cursor:"pointer" }}>{elakayAutoText("h_1acfc1c7d76131", "Supprimer")}</button>
                      </div>
                    </div>
                  ))
                )}
              </div>

            ) : activeLeftTab === "combine" ? (
              /* ── Éléments combinés ── */
              <div>
                <p style={{ fontSize:"11px", color:"#9b93b8", marginBottom:"10px", lineHeight:1.5 }}>{elakayAutoText("h_d8fb5ffe44893a", "Sélectionnez plusieurs éléments dans le canevas, nommez-les et sauvegardez-les pour les réutiliser.")}</p>
                {selectedIds.length > 1 ? (
                  <div style={{ marginBottom:"14px", padding:"10px", background:"#f5f3ff", borderRadius:"8px", border:"1px solid #e4dcfb" }}>
                    <div style={{ fontSize:"11px", fontWeight:700, color:"#6d28d9", marginBottom:"6px" }}>
                      {selectedIds.length}{elakayAutoText("h_a9f8e6ce7a1d28", "éléments sélectionnés")}</div>
                    {/* Nom */}
                    <input
                      value={newCombineName}
                      onChange={(e) => setNewCombineName(e.target.value)}
                      placeholder={elakayAutoText("h_32e308026bfd2f", "Nom de l'élément combiné...")}
                      style={{ width:"100%", padding:"6px 10px", fontSize:"12px", border:"1px solid #e4dcfb", borderRadius:"6px", boxSizing:"border-box", marginBottom:"6px", outline:"none" }}
                    />
                    {/* Prix de l'élément combiné */}
                    <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"7px" }}>
                      <span style={{ fontSize:"12px", color:"#9b93b8" }}>$</span>
                      <input
                        type="number" min={0} step={0.01}
                        value={newCombinePrice}
                        onChange={(e) => setNewCombinePrice(e.target.value)}
                        placeholder={elakayAutoText("h_1539c8fd8665e3", "Prix (0 = gratuit)")}
                        style={{ flex:1, padding:"5px 8px", fontSize:"12px", fontWeight:700, color:"#059669", border:"1px solid #e4dcfb", borderRadius:"6px", outline:"none" }}
                      />
                      <span style={{ fontSize:"10px", color:"#b3aacb", whiteSpace:"nowrap" }}>
                        {Number(newCombinePrice) > 0 ? `$${Number(newCombinePrice).toFixed(2)}` : elakayAutoText("h_auto_free_lower", "gratuit")}
                      </span>
                    </div>
                    <button
                      onClick={saveCombinedElement}
                      disabled={!newCombineName.trim()}
                      style={{ width:"100%", padding:"6px 10px", fontSize:"11px", fontWeight:700, color:"#fff", background: newCombineName.trim() ? "#6d28d9" : "#c4b5fd", border:"none", borderRadius:"6px", cursor: newCombineName.trim() ? "pointer" : "not-allowed", display:"flex", alignItems:"center", justifyContent:"center", gap:"5px" }}>
                      <FolderClosed size={12} />{elakayAutoText("h_b9d61aafdb95f1", "Sauvegarder comme combiné")}</button>
                  </div>
                ) : (
                  <div style={{ padding:"8px 10px", background:"#faf9fd", borderRadius:"6px", border:"1px dashed #e4dcfb", marginBottom:"12px", fontSize:"11px", color:"#9b93b8", lineHeight:1.5 }}>{elakayAutoText("h_a923944e456f58", "Shift+clic ou Ctrl+clic pour sélectionner plusieurs éléments.")}</div>
                )}
                {combinedElements.length === 0 ? (
                  <p style={{ fontSize:"12px", color:"#9b93b8", padding:"4px 0", lineHeight:1.5 }}>{elakayAutoText("h_4f590cf061330f", "Aucun élément combiné. Créez-en un à partir de votre sélection.")}</p>
                ) : (
                  combinedElements.map((ce) => (
                    <div key={ce.id} style={{ marginBottom:"8px", padding:"10px", background:"#fff", border:"1px solid #e4dcfb", borderRadius:"8px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"2px" }}>
                        <span style={{ flex:1, fontSize:"13px", fontWeight:700, color:"#1a1a2e" }}>{ce.name}</span>
                        {ce.price > 0 && (
                          <span style={{ fontSize:"11px", fontWeight:800, color:"#059669", background:"#d1fae5", padding:"2px 7px", borderRadius:"99px" }}>
                            ${Number(ce.price).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:"10px", color:"#9b93b8", marginBottom:"7px" }}>{ce.description}</div>
                      <div style={{ display:"flex", gap:"6px" }}>
                        <button
                          onClick={() => insertCombinedElement(ce)}
                          style={{ flex:1, padding:"5px 8px", fontSize:"11px", fontWeight:700, color:"#fff", background:"#6d28d9", border:"none", borderRadius:"6px", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:"4px" }}>
                          <Plus size={11} />{elakayAutoText("h_0ae4f1aba4706e", "Insérer")}</button>
                        <button
                          onClick={() => setCombinedElements((prev) => prev.filter((x) => x.id !== ce.id))}
                          style={{ padding:"5px 8px", borderRadius:"6px", border:"1px solid #fda4af", background:"transparent", color:"#e11d48", cursor:"pointer" }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

            ) : activeLeftTab === "mySaves" ? (
              /* ── Mes pages sauvegardées (BD) ── */
              <div>
                {/* Boutons d'action */}
                <div style={{ display:"flex", gap:"6px", marginBottom:"10px", flexWrap:"wrap" }}>
                  {onSave && (
                    <button onClick={handleSaveToDb} disabled={isSaving || saveBlocked}
                      title={saveBlocked ? saveBlockedReason : isEditingSavedPage ? elakayAutoText("h_081ac94b592e5e", "Modifier la page sélectionnée") : elakayAutoText("h_eac8cc10909b54", "Sauvegarder une nouvelle page")}
                      style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:"5px",
                        padding:"7px 10px", fontSize:"12px", fontWeight:700,
                        borderRadius:"8px", border:"none", cursor: (isSaving || saveBlocked) ? "not-allowed" : "pointer",
                        background: (isSaving || saveBlocked) ? "#9ca3af" : isEditingSavedPage ? "linear-gradient(135deg,#2563eb,#4f46e5)" : "linear-gradient(135deg,#16a34a,#15803d)",
                        color:"#fff" }}>
                      {isSaving
                        ? saveBusyLabel
                        : saveBlocked
                          ? <><Check size={12}/>{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</>
                          : isEditingSavedPage
                          ? <><PenLine size={12}/>{elakayAutoText("h_f260e757cec750", "Modifier")}</>
                          : <><Download size={12}/>{elakayAutoText("h_9ada14395d664f", "Sauvegarder")}</>}
                    </button>
                  )}
                  {savedPageId && onEditInDb && !saveBlocked && (
                    <button onClick={onEditInDb}
                      style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:"5px",
                        padding:"7px 10px", fontSize:"12px", fontWeight:700,
                        borderRadius:"8px", border:"1px solid #6d28d9", background:"#f5f3ff",
                        color:"#6d28d9", cursor:"pointer" }}>
                      <PenLine size={12}/>{elakayAutoText("h_db3ce186d6a794", "Métadonnées")}</button>
                  )}
                  {onRefreshPages && (
                    <button onClick={onRefreshPages}
                      style={{ padding:"7px 10px", fontSize:"12px", fontWeight:700, borderRadius:"8px",
                        border:"1px solid #e4dcfb", background:"transparent", color:"#6d28d9", cursor:"pointer" }}
                      title={elakayAutoText("h_1c2f4d311a1cff", "Actualiser la liste")}>
                      ↻
                    </button>
                  )}
                </div>

                {/* Page courante en BD */}
                {savedPageId && (
                  <div style={{ padding:"7px 10px", marginBottom:"10px", background:"#f0fdf4", borderRadius:"8px", border:"1px solid #bbf7d0" }}>
                    <span style={{ fontSize:"10px", fontWeight:700, color:"#16a34a" }}>{elakayAutoText("h_f9b4ad66b88a5b", "✓ Sauvegardée")}</span>
                    <p style={{ fontSize:"9px", color:"#166534", margin:"2px 0 0", fontFamily:"ui-monospace,monospace", wordBreak:"break-all" }}>{elakayAutoText("h_92dbcfdbef5601", "id :")}{savedPageId}
                    </p>
                  </div>
                )}

                {/* Pages et boutiques regroupées */}
                {(() => {
                  const totalItems = createdShopPages.length + boughtShopPages.length + otherSavedPages.length;
                  const loadItem = (item, source = null) => {
                    if (!onLoadPage) return;
                    const id = getShopItemId(item);
                    const pageData = getShopPageData(item) || {};
                    selectShopSource(source, item);
                    onLoadPage(pageData.doc, id, source ? { ...pageData, shopSource: source } : pageData);
                  };
                  const sectionTitle = (label, count, color, icon) => (
                    <div style={{ display:"flex", alignItems:"center", gap:"6px", margin:"9px 0 5px", padding:"5px 6px",
                      borderRadius:"7px", background:`${color}0d`, border:`1px solid ${color}22` }}>
                      <span style={{ fontSize:"12px" }}>{icon}</span>
                      <span style={{ flex:1, fontSize:"10px", fontWeight:800, color, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
                      <span style={{ fontSize:"9px", fontWeight:800, color, background:"#fff", padding:"1px 6px", borderRadius:"20px" }}>{count}</span>
                    </div>
                  );
                  const renderItem = (item, source = null) => {
                    const pageData = getShopPageData(item) || {};
                    const id = getShopItemId(item);
                    const isCurrent = String(id) === String(savedPageId);
                    const readOnly = source === "bought";
                    const mode = pageData.pageMode || (source ? "store" : "static");
                    const modeIcon = mode === "store" ? "🛍" : mode === "contentPage" ? "📄" : mode === "project" ? "📊" : "🏗";
                    const status = pageData.status || (readOnly ? "acheté" : "brouillon");
                    const statusColor = readOnly
                      ? "#d97706"
                      : ({ draft:"#d97706", published:"#059669", archived:"#6b7280" }[status] || "#9b93b8");
                    return (
                      <div key={`${source || "page"}-${id}`} style={{ marginBottom:"7px", padding:"9px 10px",
                        borderRadius:"9px", border:`1px solid ${isCurrent ? "#c4b5fd" : "#e4dcfb"}`,
                        background: isCurrent ? "#f5f3ff" : "#fff" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"4px" }}>
                          <span style={{ fontSize:"14px" }}>{modeIcon}</span>
                          <span style={{ flex:1, fontSize:"12px", fontWeight:700, color:isCurrent ? "#6d28d9" : "#1a1a2e",
                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {isCurrent && "● "}{getShopItemTitle(item)}
                          </span>
                          <span style={{ fontSize:"9px", fontWeight:700, color:statusColor,
                            background:`${statusColor}18`, padding:"1px 6px", borderRadius:"99px", flexShrink:0 }}>
                            {status}
                          </span>
                        </div>
                        <div style={{ fontSize:"9px", color:"#b3aacb", marginBottom:"6px" }}>
                          {pageData.updatedAt && new Date(pageData.updatedAt).toLocaleDateString("fr-FR")}
                          {readOnly && <span style={{ color:"#64748b" }}>{elakayAutoText("h_a958270ca541b8", "Lecture seule")}</span>}
                          {pageData.price > 0 && <span style={{ marginLeft:"6px", color:"#059669", fontWeight:700 }}>${pageData.price}</span>}
                          {pageData.isTemplate && <span style={{ marginLeft:"6px", color:"#0369a1", background:"#e0f2fe", padding:"0 4px", borderRadius:"3px" }}>{elakayAutoText("h_3226ecbe650213", "template")}</span>}
                        </div>
                        <div style={{ display:"flex", gap:"5px" }}>
                          {!isCurrent && onLoadPage && (
                            <button onClick={() => loadItem(item, source)}
                              style={{ flex:1, padding:"4px 0", fontSize:"11px", fontWeight:700, color:"#fff",
                                background:readOnly ? "#64748b" : "#6d28d9", border:"none", borderRadius:"6px", cursor:"pointer" }}>
                              {readOnly ? elakayAutoText("h_8a754f1f426ce1", "Voir") : elakayAutoText("h_ab31a36fe7c04a", "Sélectionner")}
                            </button>
                          )}
                          {isCurrent && (
                            <span style={{ flex:1, textAlign:"center", fontSize:"10px", fontWeight:700,
                              color:"#6d28d9", padding:"4px 0" }}>
                              {readOnly ? elakayAutoText("h_1337b87b1f2c04", "Boutique affichée") : elakayAutoText("h_24aaac387b0679", "Page sélectionnée")}
                            </span>
                          )}
                          {!readOnly && onDeletePage && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(elakayAutoText("h_87cd3fef15673a", "Supprimer « {value0} » ?", { value0: getShopItemTitle(item) }))) return;
                                await onDeletePage(id);
                              }}
                              style={{ padding:"4px 8px", fontSize:"10px", color:"#e11d48",
                                background:"transparent", border:"1px solid #fda4af", borderRadius:"6px", cursor:"pointer" }}>
                              ✕
                            </button>
                          )}
                          {/* Suppression d'une boutique achetée/lecture seule — réservée à Family,
                              re-confirmation du mot de passe requise (aucune restriction serveur sur
                              la suppression d'une page qu'on possède : c'est un garde-fou côté client
                              contre une suppression accidentelle d'un achat). */}
                          {readOnly && onDeletePage && Family(infoSource?.userInfo?.email) && (
                            familyUnlockedPassword ? (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(elakayAutoText("h_9e4b7c2a058fd1", "Supprimer « {value0} » (Développeur) ?", { value0: getShopItemTitle(item) }))) return;
                                  await onDeletePage(id);
                                }}
                                title={elakayAutoText("h_5d1a8fc3269e07", "Supprimer (Développeur débloqué)")}
                                style={{ padding:"4px 8px", fontSize:"10px", color:"#e11d48",
                                  background:"transparent", border:"1px solid #fda4af", borderRadius:"6px", cursor:"pointer" }}>
                                ✕
                              </button>
                            ) : (
                              <button
                                onClick={openFamilyUnlockModal}
                                disabled={familyUnlockBusy}
                                title={elakayAutoText("h_0f7c93de4a1b56", "Débloquer (Développeur) pour pouvoir supprimer cette boutique")}
                                style={{ padding:"4px 8px", fontSize:"10px", color:"#b45309",
                                  background:"transparent", border:"1px solid #f59e0b", borderRadius:"6px",
                                  cursor: familyUnlockBusy ? "not-allowed" : "pointer" }}>
                                🔓
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  };

                  if (totalItems === 0) {
                    return (
                      <p style={{ fontSize:"12px", color:"#9b93b8", padding:"8px 0", lineHeight:1.5 }}>{elakayAutoText("h_477938ac3bca42", "Aucune page sauvegardée. Cliquez sur « Sauvegarder » pour en créer une.")}</p>
                    );
                  }

                  return (
                    <div>
                      <div style={{ margin:"12px 0 4px", fontSize:"12px", fontWeight:800, color:"#1a1a2e" }}>{elakayAutoText("h_9347a4d365dbc5", "Mes boutiques")}</div>
                      {sectionTitle("Mes créations", createdShopPages.length, "#6d28d9", "✏")}
                      {createdShopPages.length === 0
                        ? <p style={{ fontSize:"10px", color:"#b3aacb", margin:"0 4px 8px" }}>{elakayAutoText("h_de9f5e86aa31d2", "Aucune boutique créée.")}</p>
                        : createdShopPages.map((item) => renderItem(item, "created"))}

                      {sectionTitle("Mes achats", boughtShopPages.length, "#d97706", "🛒")}
                      {boughtShopPages.length === 0
                        ? <p style={{ fontSize:"10px", color:"#b3aacb", margin:"0 4px 8px" }}>{elakayAutoText("h_0044f189c63355", "Aucune boutique achetée.")}</p>
                        : boughtShopPages.map((item) => renderItem(item, "bought"))}

                      {otherSavedPages.length > 0 && (
                        <>
                          {sectionTitle("Mes autres pages", otherSavedPages.length, "#2563eb", "📄")}
                          {otherSavedPages.map((item) => renderItem(item))}
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>

            ) : activeLeftTab === "anim" ? (
              <AnimationPanel element={selected} onUpdate={updateElement} />
            ) : activeLeftTab === "htmlCode" ? (
              renderHtmlCodePanel()
            ) : activeLeftTab === "json" ? (
              <div style={{ padding:"14px", display:"flex", flexDirection:"column", height:"100%" }}>
                <p style={{ fontSize:"11px", color:"#9b93b8", margin:"0 0 8px" }}>{elakayAutoText("h_ce8fa2947145c1", "Éditeur JSON — onglet JSON déplacé ici.")}</p>
                <textarea value={jsonText} onChange={(e) => { editingJson.current = true; setJsonText(e.target.value); }} style={{ flex:1, padding:"10px", fontFamily:"ui-monospace,monospace", fontSize:"11px", border:"1px solid #e4e0f0", borderRadius:"8px", resize:"none", outline:"none", background:"#faf9fd", color:"#2a2547" }} />
                <button onClick={openJsonTab} style={{ ...primaryBtn, marginTop:"8px", justifyContent:"center" }}>{elakayAutoText("h_d5915a94f3ee9b", "Sync JSON")}</button>
              </div>
            ) : (
              <div>
                <button onClick={addPage} style={{ ...primaryBtn, width: "100%", justifyContent: "center", marginBottom: "12px" }}>
                  <Plus size={15} /> {pbt("t085","Nouvelle page")}
                </button>
                {pages.map((p) => {
                  const active = p.id === currentPageId;
                  return (
                    <div key={p.id} style={{ marginBottom: "8px", border: `1px solid ${active ? "#c9b8f5" : "#ece9f5"}`, borderRadius: "10px", background: active ? "#f7f3fe" : "#fff", overflow: "hidden" }}>
                      <div onClick={() => switchPage(p.id)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "9px 10px", cursor: "pointer" }}>
                        <FileJson size={14} color={active ? "#6d28d9" : "#9b93b8"} />
                        <input value={p.name} onClick={(e) => e.stopPropagation()} onChange={(e) => renamePage(p.id, e.target.value)}
                          style={{ flex: 1, border: "none", background: "transparent", fontSize: "13px", fontWeight: active ? 700 : 500, color: active ? "#6d28d9" : "#4a4a68", outline: "none", padding: 0 }} />
                        <button onClick={(e) => { e.stopPropagation(); duplicatePage(p.id); }} title={elakayAutoText("h_c5e1d3f16fc3af", "Dupliquer")} style={miniBtn}><Copy size={13} /></button>
                        <button onClick={(e) => { e.stopPropagation(); deletePage(p.id); }} title={elakayAutoText("h_1acfc1c7d76131", "Supprimer")} disabled={pages.length <= 1}
                          style={{ ...miniBtn, color: pages.length <= 1 ? "#d8d2e8" : "#e11d48", cursor: pages.length <= 1 ? "not-allowed" : "pointer" }}><Trash2 size={13} /></button>
                      </div>
                      <div style={{ fontSize: "10px", color: "#b3aacb", padding: "0 10px 7px", fontFamily: "ui-monospace, monospace" }}>{elakayAutoText("h_92dbcfdbef5601", "id :")}{p.id} · {p.children.length}{elakayAutoText("h_15e7ff358952f5", "élément(s)")}</div>
                    </div>
                  );
                })}
                <p style={{ fontSize: "11px", color: "#b3aacb", marginTop: "10px", lineHeight: 1.5 }}>{elakayAutoText("h_afb4bd2e26214c", "Cliquez une page pour l'éditer. Reliez vos pages avec l'élément « Lien (page) » du groupe Base.")}</p>
              </div>
            )}
          </div>
        </aside>
          );
          return leftIsDetached && detachedPanelRootRef.current
            ? createPortal(leftPanel, detachedPanelRootRef.current)
            : leftPanel;
        })()}

        {/* ── Canevas central (Builder OU Aperçu selon l'onglet) ─── */}
        <main style={{ flex: 1, minHeight: "600px", overflowX: "auto", overflowY: "auto", overscrollBehavior: "contain", position: "relative", background: "radial-gradient(circle at 50% 0%, #efeafa, #f6f4fb)" }}>
          {!centerDetached ? (
            centerPanelContent
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "30px", textAlign: "center", color: "#7a73a0" }}>
              <div style={{ background: "#fff", border: "1px solid #ece9f5", borderRadius: "14px", padding: "20px 24px", boxShadow: "0 8px 24px rgba(60,30,110,0.08)" }}>
                <div style={{ fontSize: "14px", fontWeight: 700, color: "#1a1a2e", marginBottom: "6px" }}>{elakayAutoText("h_9367375dd75b76", "Panneau central détaché")}</div>
                <div style={{ fontSize: "12px" }}>{elakayAutoText("h_d0bb55677519d6", "La zone Builder/Aperçu est ouverte dans une autre fenêtre.")}</div>
              </div>
            </div>
          )}
        {/* ── fin vue Builder ── */}
        </main>

        {/* Panneau droit */}
        {(() => {
          const rightIsDetached = detachedPanelTab?.side === "right";
          const activeRightTab = rightIsDetached ? detachedPanelTab?.key : rightTab;
          const rightPanel = (
        <aside className={`pb-aside-right${mobileRight ? " open" : ""}${rightIsDetached ? " pb-detached-tab-panel" : ""}`} style={{ width: rightIsDetached ? "100%" : "330px", height: rightIsDetached ? "100%" : "auto", background: "#fff", borderLeft: "1px solid #ece9f5", display: rightIsDetached || (showRightPanel && !shopSelectionReadOnly) ? "flex" : "none", flexDirection: "column", minHeight: 0, overflowX: "auto", overflowY: "auto", overscrollBehavior: "contain" }}>
          {/* ── Tabs droite (dynamiques) ── */}
          {(() => {
            const isShopLiveSelected = selected && (ELEMENT_TYPES[selected.type]?.group === "shopLive" || ELEMENT_TYPES[selected.type]?.group === "shopHeader");
            const ALL_TAB_DEFS_R = [
              { key:"props",    icon:<Settings2 size={13}/>,   label:pbt("t009","Général"),   onClick:() => setRightTab("props") },
              { key:"anim",     icon:<Sparkles size={13}/>,    label:pbt("t012","Anim."),    onClick:() => setRightTab("anim") },
              { key:"links",    icon:<ChevronRight size={13}/>,label:pbt("t011","Liens"),    onClick:() => setRightTab("links") },
              { key:"json",     icon:<Code2 size={13}/>,       label:pbt("t008","JSON"),     onClick:openJsonTab },
              { key:"htmlCode", icon:<FileCode size={13}/>,    label:elakayAutoText("h_121344756746b3", "Code HTML"),           onClick:() => setRightTab("htmlCode") },
              { key:"data",     icon:<Boxes size={13}/>,       label:pbt("t006","Données"),  onClick:() => setRightTab("data") },
              { key:"shopLive", icon:<Package size={13}/>,     label:pbt("t007","Live ⚡"),  onClick:() => setRightTab("shopLive"), pulse: isShopLiveSelected },
              { key:"add",      icon:<Plus size={13}/>,        label:pbt("t001","Éléments"), onClick:() => setRightTab("add") },
              { key:"html",     icon:<FileCode size={13}/>,    label:pbt("t002","HTML"),     onClick:() => setRightTab("html") },
              { key:"layers",   icon:<Layers size={13}/>,      label:pbt("t003","Calques"),  onClick:() => setRightTab("layers") },
              { key:"pages",    icon:<FileJson size={13}/>,    label:pbt("t004","Pages"),    onClick:() => setRightTab("pages") },
              { key:"boutique", icon:<Package size={13}/>,     label:pbt("t005","Boutique"), onClick:() => setRightTab("boutique") },
            ];
            const visibleRight = ALL_TAB_DEFS_R.filter(d =>
              panelTabSides[d.key] === "right" &&
              (!rightIsDetached || d.key === detachedPanelTab?.key)
            );
            const activeLabel = ALL_TAB_DEFS_R.find(d => d.key === activeRightTab)?.label || "";
            return (
              <div style={{ borderBottom: "1px solid #f0edf8" }}>
                {/* Ligne des tabs */}
                <div style={{ display:"flex", flexWrap:"wrap", padding:"8px 8px 4px", gap:"4px" }}>
                  {visibleRight.map(({ key, icon, label, onClick, pulse }) => (
                    <button
                      key={key}
                      onClick={onClick}
                      style={{
                        ...tabBtn(activeRightTab === key), padding:"6px 8px", fontSize:"11px", display:"flex", alignItems:"center", gap:"3px",
                        // Same fix as the left tabs above: no `borderColor` longhand next to
                        // tabBtn()'s `border` shorthand (dead weight anyway -- border-style
                        // stays "none" so it was never visible; the gradient carries the pulse).
                        ...(pulse && activeRightTab !== key ? { background:"linear-gradient(135deg,#6d28d9,#8b5cf6)", color:"#fff" } : {}),
                      }}
                    >
                      {icon} {label}
                    </button>
                  ))}
                </div>
                {/* Bouton de déplacement ← vers panneau gauche */}
                <div style={{ display:"flex", justifyContent:"flex-start", gap:"5px", padding:"2px 8px 5px" }}>
                  {!rightIsDetached && (
                    <button
                      onClick={() => { if (panelTabSides[activeRightTab] === "right") moveTabToPanel(activeRightTab, "left"); }}
                      disabled={panelTabSides[activeRightTab] !== "right"}
                      title={elakayAutoText("h_f9ab3ba565e6d0", "Déplacer l'onglet \"{value0}\" vers le panneau gauche", { value0: activeLabel })}
                      style={{ display:"flex", alignItems:"center", gap:"4px", padding:"3px 8px", borderRadius:"6px", border:"1px solid #e4dcfb", background:"#f5f3ff", color:panelTabSides[activeRightTab]==="right"?"#6d28d9":"#c4b5fd", cursor:panelTabSides[activeRightTab]==="right"?"pointer":"not-allowed", fontSize:"10px", fontWeight:600, transition:"all 0.15s" }}
                    >
                      ← {activeLabel}
                    </button>
                  )}
                  <button
                    onClick={() => rightIsDetached ? closeDetachedPanelTab() : openDetachedPanelTab("right", activeRightTab, activeLabel)}
                    title={rightIsDetached ? `Rattacher l'onglet "${activeLabel}"` : `Détacher l'onglet "${activeLabel}"`}
                    style={{ display:"flex", alignItems:"center", gap:"4px", padding:"3px 8px", borderRadius:"6px", border:"1px solid #d8cdf7", background:rightIsDetached?"#6d28d9":"#fff", color:rightIsDetached?"#fff":"#6d28d9", cursor:"pointer", fontSize:"10px", fontWeight:700 }}
                  >
                    {rightIsDetached ? <ChevronLeft size={12}/> : <Maximize2 size={12}/>}
                    {rightIsDetached ? elakayAutoText("h_572c4b074bbe07", "Rattacher") : elakayAutoText("h_e7f310727801a8", "Détacher")}
                  </button>
                </div>
              </div>
            );
          })()}
          <div style={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "auto" }}>
            {activeRightTab === "props" ? (
              selectedIds.length > 1 ? (
                <MultiSelectPanel count={selectedIds.length} onStyle={updateSelectedStyle} onDelete={deleteSelected} onClear={() => handleSelect(null)} freeMode={freeMode} onNudgeSelected={nudgeSelected} />
              ) : bodySelected && !selected ? (
                <PageBodyMappingPanel page={page} setPageProp={setPageProp} boundData={boundData} setBoundData={setBoundData} doc={doc} datasets={allDatasets} onDeselect={() => setBodySelected(false)} />
              ) : (
                <PropertiesPanel element={selected} onUpdate={updateElement} onDelete={() => selected && deleteElement(selected.id)} onDuplicate={duplicateElement} freeMode={freeMode} onNudge={nudge} onAddInside={() => setLeftTab("add")} device={device} layoutDevice={layoutDevice} onBringFront={() => moveSelectedLayer("front")} onSendBack={() => moveSelectedLayer("back")} pages={pages} onNavigate={navigateToPage} boundData={boundData} setBoundData={setBoundData} doc={doc} setPageMode={setPageMode} lockPageMode={lockPageMode} datasets={allDatasets} simpleMode={simpleMode} page={page} onUpdateNode={updateNodePatch} onBulkUpdateNodes={bulkUpdateNodes} />
              )
            ) : activeRightTab === "shopLive" ? (
              <ShopLiveSettingPanel element={selected} onUpdate={updateElement} page={page} />
            ) : activeRightTab === "anim" ? (
              <AnimationPanel element={selected} onUpdate={updateElement} />
            ) : activeRightTab === "links" ? (
              <div style={{ padding: "16px" }}>
                <Section title={elakayAutoText("h_086fabfd610860", "Liens détectés ({value0})", { value0: documentLinks.length })} icon={ChevronRight}>
                  <p style={{ fontSize: "12px", color: "#9b93b8", margin: "0 0 10px", lineHeight: 1.5 }}>{elakayAutoText("h_2e896eb940e688", "Tous les `href`/`target` trouvés dans le document. Cliquez sur copy pour copier l’URL.")}</p>
                  {documentLinks.length === 0 ? (
                    <div style={{ fontSize: "12px", color: "#9b93b8", background: "#faf9fd", border: "1px solid #ece9f5", borderRadius: "10px", padding: "10px 12px" }}>
                      {pbt("t043","Aucun lien trouvé pour le moment.")}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "7px", maxHeight: "62vh", overflowY: "auto", paddingRight: "2px" }}>
                      {documentLinks.map((item, idx) => (
                        <div key={`${item.nodeId}-${item.path}-${idx}`} style={{ border: "1px solid #ece9f5", borderRadius: "10px", padding: "8px 9px", background: "#fff" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                            <span style={{ fontSize: "9px", fontWeight: 700, color: "#6d28d9", background: "#f3effe", borderRadius: "999px", padding: "2px 7px" }}>{item.type}</span>
                            <span style={{ fontSize: "10px", color: "#9b93b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {item.pageName || item.pageId}
                            </span>
                          </div>
                          <div style={{ fontSize: "11px", fontWeight: 700, color: "#1a1a2e", marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.label}>
                            {item.label}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <code style={{ flex: 1, fontSize: "10px", color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "7px", padding: "4px 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.href}>
                              {item.href}
                            </code>
                            <button
                              onClick={() => {
                                if (!item.href) return;
                                navigator.clipboard?.writeText(item.href);
                                setCopiedKey(`doc-link-${idx}`);
                                setTimeout(() => setCopiedKey(""), 1200);
                              }}
                              style={{ ...miniBtn, width: "28px", height: "24px", border: "1px solid #e4dcfb", borderRadius: "6px", background: "#fff", color: copiedKey === `doc-link-${idx}` ? "#16a34a" : "#6d28d9", opacity: 1 }}
                              title={elakayAutoText("h_31505d9ade3127", "Copier le lien")}
                            >
                              {copiedKey === `doc-link-${idx}` ? <Check size={13} /> : <Copy size={13} />}
                            </button>
                          </div>
                          {item.target ? (
                            <div style={{ marginTop: "4px", fontSize: "10px", color: "#9b93b8", fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.target}>{elakayAutoText("h_83e75b7fd51929", "target:")}{item.target}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </div>
            ) : activeRightTab === "htmlCode" ? (
              renderHtmlCodePanel()
            ) : activeRightTab === "json" ? (
              <div style={{ padding: "14px", display: "flex", flexDirection: "column", height: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600, color: "#4a4a68" }}>
                    <input type="checkbox" checked={autoSync} onChange={(e) => { editingJson.current = false; setAutoSync(e.target.checked); }} style={{ accentColor: "#6d28d9", width: "16px", height: "16px" }} />
                    {pbt("t086","Synchro automatique")}
                  </label>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 700, color: autoSync ? (jsonError ? "#e11d48" : "#16a34a") : "#9b93b8" }}>
                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: autoSync ? (jsonError ? "#e11d48" : "#16a34a") : "#c9c2e0" }} />
                    {autoSync ? (jsonError ? pbt("t088","JSON incomplet") : pbt("t087","À jour")) : pbt("t089","Manuel")}
                  </span>
                </div>
                <textarea value={jsonText} onChange={(e) => onJsonChange(e.target.value)} spellCheck={false}
                  style={{ flex: 1, minHeight: "300px", width: "100%", fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontSize: "12px", lineHeight: "1.6", padding: "12px", border: `1px solid ${jsonError ? "#f3b4c2" : "#e4e0f0"}`, borderRadius: "10px", background: "#1a1a2e", color: "#d6cfec", outline: "none", resize: "none" }} />
                {jsonError && <p style={{ color: "#e11d48", fontSize: "12px", marginTop: "8px" }}>⚠ {jsonError}</p>}
                {syncMsg && <p style={{ color: "#6d28d9", fontSize: "12px", marginTop: "8px", fontWeight: 600 }}>↔ {syncMsg}</p>}
                <p style={{ fontSize: "11px", color: "#b3aacb", margin: "8px 0 0", lineHeight: 1.5 }}>
                  {autoSync
                    ? elakayAutoText("h_3f63b816790e04", "Les modifications de l'éditeur et du code se synchronisent automatiquement dans les deux sens.")
                    : elakayAutoText("h_5ce535c500082c", "Synchro manuelle : utilisez « Synchroniser » ou « Appliquer le JSON ».")}
                </p>
                {!autoSync && (
                  <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                    <button onClick={syncJson} style={{ ...ghostBtn, flex: 1, justifyContent: "center" }} title={elakayAutoText("h_5727bfd2ccb4ca", "Aligner le code et le contenu dans les deux sens")}><ChevronsUpDown size={15} />{elakayAutoText("h_fd467317d72b32", "Synchroniser")}</button>
                    <button onClick={applyJson} style={{ ...primaryBtn, flex: 1, justifyContent: "center" }}>{elakayAutoText("h_a4d6613920a38d", "Appliquer")}</button>
                  </div>
                )}
              </div>
            ) : ["add","html","layers","pages","boutique"].includes(activeRightTab) ? (
              /* Tabs originellement à gauche déplacés dans le panneau droit */
              <div style={{ flex:1, overflowY:"auto", padding:"4px 12px 16px" }}>
                {activeRightTab === "add" ? (
                  <>
                    <CollapsibleGroup open={openGroups.base} onToggle={() => toggleOpenGroup("base")} label={elakayAutoText("h_077fe9c54ed5ed", "Base")} icon={Type} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="base").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="base")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.layout} onToggle={() => toggleOpenGroup("layout")} label={elakayAutoText("h_f09e7eee2eefe8", "Mise en page")} icon={Columns} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="layout").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="layout")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.rich} onToggle={() => toggleOpenGroup("rich")} label={elakayAutoText("h_0860e88d172f38", "Composants riches")} icon={Sparkles} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="rich").length}>
                      <LxCPicker onPick={insertLxC} label={elakayAutoText("h_fd64b09eca15ae", "Grille l × c")} />
                      <div style={{ height:"1px", background:"#f0edf8", margin:"8px 0" }} />
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="rich")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.form} onToggle={() => toggleOpenGroup("form")} label={elakayAutoText("h_edb3637fe7e2c6", "Formulaires")} icon={TextCursorInput} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="form").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="form")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.media} onToggle={() => toggleOpenGroup("media")} label={elakayAutoText("h_486d82f4995b6d", "Médias")} icon={Video} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="media").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="media")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.icons} onToggle={() => toggleOpenGroup("icons")} label={elakayAutoText("h_icons_group", "Icônes")} icon={Star} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="icons").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="icons")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.documents} onToggle={() => toggleOpenGroup("documents")} label={elakayAutoText("h_687c82861c956e", "Documents")} icon={FileText} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="documents").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="documents")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.data} onToggle={() => toggleOpenGroup("data")} label={elakayAutoText("h_e3e0c5ec8a3b34", "Données")} icon={BarChart3} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="data").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="data")} onClick={addElement} />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.shopLive} onToggle={() => toggleOpenGroup("shopLive")} label={elakayAutoText("h_f322b814019146", "Boutique Live")} icon={Package} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="shopLive"||d.group==="shopHeader").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="shopLive"||d.group==="shopHeader")} onClick={addElement} cardHeight="76px" />
                    </CollapsibleGroup>
                    <CollapsibleGroup open={openGroups.custom} onToggle={() => toggleOpenGroup("custom")} label={elakayAutoText("h_081ae3fdc40360", "Custom")} icon={Boxes} count={Object.keys(CUSTOM_COMPONENTS).length}>
                      <CardGridButtons entries={Object.entries(CUSTOM_COMPONENTS)} onClick={addCustom} />
                    </CollapsibleGroup>
                  </>
                ) : activeRightTab === "html" ? (
                  <>
                    <CollapsibleGroup open={openGroups.html} onToggle={() => toggleOpenGroup("html")} label={elakayAutoText("h_9f738ce8457f29", "HTML")} icon={FileCode} count={Object.values(ELEMENT_TYPES).filter(d => d.group==="html"||d.group==="form").length}>
                      <CardGridButtons entries={Object.entries(ELEMENT_TYPES).filter(([,d]) => d.group==="html"||d.group==="form")} onClick={addElement} />
                    </CollapsibleGroup>
                  </>
                ) : activeRightTab === "layers" ? (
                  <div>
                    {page.children.length === 0
                      ? <p style={{ fontSize:"13px", color:"#9b93b8", padding:"12px" }}>{pbt("t042","Aucun élément.")}</p>
                      : page.children.map((n) => <LayerItem key={n.id} node={n} depth={0} selectedIds={selectedIds} onSelect={handleSelect} onMove={moveElement} onDelete={deleteElement} />)}
                  </div>
                ) : activeRightTab === "pages" ? (
                  <div>
                    <button onClick={addPage} style={{ ...primaryBtn, width:"100%", justifyContent:"center", marginBottom:"12px" }}>
                      <Plus size={15} /> {pbt("t085","Nouvelle page")}
                    </button>
                    {pages.map((p) => {
                      const active = p.id === currentPageId;
                      return (
                        <div key={p.id} style={{ marginBottom:"8px", border:`1px solid ${active?"#c9b8f5":"#ece9f5"}`, borderRadius:"10px", background:active?"#f7f3fe":"#fff", overflow:"hidden" }}>
                          <div onClick={() => switchPage(p.id)} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"9px 10px", cursor:"pointer" }}>
                            <FileJson size={14} color={active?"#6d28d9":"#9b93b8"} />
                            <input value={p.name} onClick={(e) => e.stopPropagation()} onChange={(e) => renamePage(p.id, e.target.value)} style={{ flex:1, border:"none", background:"transparent", fontSize:"13px", fontWeight:active?700:500, color:active?"#6d28d9":"#1a1a2e", outline:"none" }} />
                            <button onClick={(e) => { e.stopPropagation(); deletePage(p.id); }} disabled={pages.length<=1} style={{ width:"22px", height:"22px", border:"none", background:"transparent", cursor:"pointer", color:"#e11d48", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:"4px" }}><X size={13}/></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : /* boutique */ (
                  <div style={{ fontSize:"12px", color:"#9b93b8", padding:"12px" }}>
                    <Package size={20} style={{ marginBottom:"6px" }} /><br/>{elakayAutoText("h_fdb7aa3d5c6ef3", "Templates Boutique — utilisez l'onglet gauche pour y accéder.")}</div>
                )}
              </div>
            ) : (
              <div style={{ padding: "18px" }}>
                {/* CONNEXION DES DONNÉES (data-binding) */}
                <Section title={pbt("t013","Source de données")} icon={Boxes}>
                  <p style={{ fontSize: "12px", color: "#9b93b8", lineHeight: 1.5, margin: "0 0 8px" }}>
                    {pbt("t049","Chargez un jeu de données d'exemple Elakay, puis liez ses clés aux éléments de la page.")}
                  </p>
                  <label style={{ display: "block", marginBottom: "10px" }}>
                    <span style={fieldLabel}>{pbt("t050","Jeu de données")}</span>
                    {(() => {
                      const onChange = (e) => {
                        const k = e.target.value;
                        if (!k) {
                          setBoundData(null);
                          if (doc.pageMode === "contentPage") syncContentPageBindingMeta(null, null);
                          setExplorePath("");
                          setContentArrayExpanded(new Set());
                        } else {
                          bindDataset(k, allDatasets[k]);
                        }
                      };
                      const modeFilter = ([, d]) => !doc.pageMode || doc.pageMode === "static" || d.pageMode === doc.pageMode;
                      // Datasets live (injectés par l'app hôte)
                      const liveEntries  = Object.entries(allDatasets).filter(([, d]) => d.isLive).filter(modeFilter);
                      // Exemples avec vrai contenu (about, seller + entries de sampleData.js)
                      const sampleEntries = Object.entries(allDatasets).filter(([k, d]) => !d.isLive && !k.startsWith("cp_")).filter(modeFilter);
                      // Pages de contenu menuAddLang (cp_*)
                      const cpEntries = Object.entries(allDatasets).filter(([k, d]) => !d.isLive && k.startsWith("cp_")).filter(modeFilter);

                      if (doc.pageMode === "contentPage" && cpEntries.length > 0) {
                        // Mode contentPage → sélecteur groupé avec optgroup par groupe menuAddLang
                        return (
                          <select value={getBoundDatasetKey(boundData)} onChange={onChange} size={1} style={{ ...selectStyle, maxHeight: "220px" }}>
                            <option value="">{elakayAutoText("h_7d8166b0db754a", "— aucun —")}{elakayAutoText("h_428dadd5ef9a49", "· LanguagePage")}</option>
                            {liveEntries.length > 0 && (
                              <optgroup label={elakayAutoText("h_bb596f1b16bbae", "● Données live")}>
                                {liveEntries.map(([k, d]) => <option key={k} value={k}>● {d.label}</option>)}
                              </optgroup>
                            )}
                            {sampleEntries.length > 0 && (
                              <optgroup label={elakayAutoText("h_7ad3df136641c2", "Exemples")}>
                                {sampleEntries.map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
                              </optgroup>
                            )}
                            {CONTENT_PAGE_GROUPS.map((grp) => {
                              const entries = cpEntries.filter(([k]) => {
                                const regEntry = CONTENT_PAGE_REGISTRY.find((r) => `cp_${r.key}` === k);
                                return regEntry?.group === grp.id;
                              });
                              if (!entries.length) return null;
                              return (
                                <optgroup key={grp.id} label={grp.label}>
                                  {entries.map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
                                </optgroup>
                              );
                            })}
                          </select>
                        );
                      }

                      // Autres modes → sélecteur plat
                      return (
                        <select value={getBoundDatasetKey(boundData)} onChange={onChange} style={selectStyle}>
                          <option value="">{elakayAutoText("h_7d8166b0db754a", "— aucun —")}</option>
                          {liveEntries.map(([k, d]) => <option key={k} value={k}>● {d.label}</option>)}
                          {sampleEntries.map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
                        </select>
                      );
                    })()}
                    {doc.pageMode && doc.pageMode !== "static" && (
                      <p style={{ fontSize: "10px", color: "#9b93b8", margin: "4px 0 0", lineHeight: 1.4 }}>
                        {pbt("t052","Sources filtrées par le mode :")} <strong style={{ color: "#6d28d9" }}>{doc.pageMode}</strong>
                        {doc.pageMode === "contentPage" && <span style={{ color: "#8b5cf6" }}> · {Object.keys(allDatasets).filter((k) => k.startsWith("cp_")).length}{elakayAutoText("h_14484d718d2cef", "pages disponibles")}</span>}
                      </p>
                    )}
                  </label>

                  {boundData && (() => {
                    const root = boundData.data;
                    const current = resolvePath(root, explorePath);
                    const keys = listKeys(current);
                    // Détecte si on explore actuellement l'intérieur d'une entrée de tableau (.0)
                    // pour proposer le "check" comme mapping relatif sur le gabarit.
                    // Trouve le chemin du tableau parent et la clé relative depuis l'entrée
                    const arrayInfo = (() => {
                      const idx = explorePath.split(".").lastIndexOf("0");
                      if (idx === -1) return null;
                      const parts = explorePath.split(".");
                      return { arrayPath: parts.slice(0, idx).join("."), relPrefix: parts.slice(idx + 1).join(".") };
                    })();
                    const previewVal = (v) => {
                      if (v == null) return "";
                      if (Array.isArray(v)) return `[${v.length} éléments]`;
                      if (typeof v === "object") return "{…}";
                      return String(v);
                    };
                    const fullVal = (v) => {
                      try { return typeof v === "object" ? JSON.stringify(v, null, 1).slice(0, 600) : String(v); }
                      catch { return String(v); }
                    };
                    return (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "11px", color: "#9b93b8" }}>{elakayAutoText("h_42b647fd7f5cbc", "Mode :")}<strong style={{ color: "#6d28d9" }}>{boundData.pageMode}</strong></span>
                          {explorePath && (
                            <button onClick={() => { const parts = explorePath.split("."); parts.pop(); setExplorePath(parts.join(".")); }}
                              style={{ ...miniBtn, fontSize: "11px", padding: "2px 8px" }}>{elakayAutoText("h_cfa44815114ab9", "← retour")}</button>
                          )}
                        </div>
                        <div style={{ fontSize: "11px", color: "#7a73a0", marginBottom: "6px", fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }}>{elakayAutoText("h_9f8866427bf4d0", "chemin :")}{explorePath || elakayAutoText("h_93c2e7b1b1c1d6", "(racine)")}
                        </div>
                        {arrayInfo && (
                          <div style={{ fontSize: "11px", color: "#6d28d9", marginBottom: "6px", padding: "6px 8px", background: "#f3effe", borderRadius: "8px", lineHeight: 1.4 }}>{elakayAutoText("h_0ae6f32810f585", "Vous explorez le schéma d'une entrée de")}<strong>{arrayInfo.arrayPath}</strong>{elakayAutoText("h_14f11881ec7f51", ". Cochez une clé pour la mapper sur l'élément sélectionné du gabarit.")}</div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxHeight: "260px", overflowY: "auto", border: "1px solid #ece9f5", borderRadius: "10px", padding: "6px" }}>
                          {keys.map((k) => {
                            const keyName = k.key.split(".").pop();
                            const childPath = explorePath ? `${explorePath}.${keyName}` : keyName;
                            const drillable = k.isObject || k.isArray;
                            const val = k.sample;
                            return (
                              <div key={k.key} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 8px", borderRadius: "8px", background: "#faf9fd" }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontSize: "12px", color: "#2a2547", fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{keyName} <span style={{ fontSize: "10px", color: "#9b93b8" }}>({k.type})</span></div>
                                  {/* Aperçu court de la valeur + tooltip complet au survol */}
                                  <div title={fullVal(val)} style={{ fontSize: "10px", color: "#9b93b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" }}>
                                    {previewVal(val).slice(0, 40)}{previewVal(val).length > 40 ? "…" : ""}
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: "3px", alignItems: "center", flexShrink: 0 }}>
                                  {/* OUVRIR : descendre dans un objet, ou entrer dans la 1re entrée d'un tableau */}
                                  {drillable && (
                                    <button onClick={() => setExplorePath(k.isArray ? `${childPath}.0` : childPath)}
                                      style={{ ...miniBtn, fontSize: "10px", padding: "3px 7px" }} title={k.isArray ? elakayAutoText("h_1c90e8b60363e4", "Explorer le schéma (1re entrée)") : elakayAutoText("h_675b76be233f4d", "Explorer cet objet")}>
                                      {k.isArray ? elakayAutoText("h_auto_map_action", "mapper") : elakayAutoText("h_auto_open_action", "ouvrir")}
                                    </button>
                                  )}
                                  {/* CHECK (toggle) : poser le chemin COMPLET en bindKey, ou le retirer s'il est déjà actif */}
                                  {selected && (() => {
                                    // Chemin complet à poser : "<source>.<childPath>" (la source est la racine logique)
                                    const fullKey = `${boundData.key}.${childPath}`;
                                    const isActive = selected.bindKey === fullKey || selected.repeatBind?.path === childPath;
                                    return (
                                      <button onClick={() => {
                                        if (isActive) {
                                          // Désactive : retire bindKey ou repeatBind selon le cas
                                          if (selected.repeatBind?.path === childPath) updateElement({ ...selected, repeatBind: null });
                                          else updateElement({ ...selected, bindKey: null });
                                          setBindings((prev) => prev.filter((x) => x.elementId !== selected.id));
                                        } else if (k.isArray) {
                                          // Tableau : on relie sur le tableau (le rendu prendra .0 comme premier élément)
                                          updateElement(withAutoShopUserSync({
                                            ...selected,
                                            repeatBind: {
                                              source: boundData.key,
                                              path: childPath,
                                              mode: "self",
                                              autoFill: true,
                                              paginate: selected.repeatBind?.paginate || false,
                                              pageSize: selected.repeatBind?.pageSize || 12,
                                              fieldMap: selected.repeatBind?.fieldMap || {},
                                            },
                                          }));
                                          setBindings((prev) => [...prev.filter((x) => x.elementId !== selected.id), { elementId: selected.id, source: boundData.key, path: childPath, repeat: true }]);
                                        } else {
                                          // Valeur ou objet : pose le chemin COMPLET (avec racine) dans bindKey
                                          updateElement({ ...selected, bindKey: fullKey });
                                          setBindings((prev) => [...prev.filter((x) => x.elementId !== selected.id), { elementId: selected.id, source: boundData.key, path: childPath, repeat: false, valueKey: childPath, valueType: /img|image|photo/i.test(k.key) ? "image" : "text" }]);
                                        }
                                      }} style={{ ...miniBtn, padding: "4px", color: isActive ? "#fff" : "#16a34a", background: isActive ? "#16a34a" : "#fff", borderColor: "#bbf7d0" }}
                                        title={isActive ? elakayAutoText("h_9c0fde39d80c6d", "Désactiver la liaison") : (k.isArray ? elakayAutoText("h_fb7861c62a046e", "Relier (répétition)") : elakayAutoText("h_7f37b45c5f075d", "Relier à l'élément (chemin complet)"))}>
                                        <Check size={13} />
                                      </button>
                                    );
                                  })()}
                                  {/* COPY CHEMIN : copier le chemin complet (avec racine) */}
                                  <button onClick={() => {
                                    const fullPath = `${boundData.key}.${childPath}`;
                                    if (navigator.clipboard) navigator.clipboard.writeText(fullPath);
                                    setCopiedKey("p:" + childPath);
                                    setTimeout(() => setCopiedKey(""), 1200);
                                  }} style={{ ...miniBtn, padding: "4px", color: copiedKey === "p:" + childPath ? "#16a34a" : "#6d28d9", borderColor: "#c9b8f5" }} title={elakayAutoText("h_7ce91f59acb333", "Copier le chemin complet")}>
                                    {copiedKey === "p:" + childPath ? <Check size={13} /> : <Copy size={13} />}
                                  </button>
                                  {/* COPY VALEUR : copier la valeur de la clé */}
                                  <button onClick={() => {
                                    const v = k.sample;
                                    const text = typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
                                    if (navigator.clipboard) navigator.clipboard.writeText(text);
                                    setCopiedKey("v:" + childPath);
                                    setTimeout(() => setCopiedKey(""), 1200);
                                  }} style={{ ...miniBtn, padding: "4px", color: copiedKey === "v:" + childPath ? "#16a34a" : "#7a73a0", borderColor: "#ece9f5", fontSize: "9px", fontWeight: 700 }} title={elakayAutoText("h_c3f9eafd10dca7", "Copier la valeur")}>
                                    {copiedKey === "v:" + childPath ? <Check size={13} /> : "abc"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          {keys.length === 0 && (
                            <div style={{ fontSize: "12px", color: "#4a4a68", padding: "8px" }}>
                              <div style={{ fontWeight: 600, marginBottom: "4px" }}>{elakayAutoText("h_4e10b432d56220", "Valeur :")}</div>
                              <div style={{ fontFamily: "ui-monospace, monospace", fontSize: "11px", color: "#7a73a0", wordBreak: "break-all" }}>{fullVal(current)}</div>
                            </div>
                          )}
                        </div>
                        {!selected && <p style={{ fontSize: "11px", color: "#e0a800", margin: "8px 0 0" }}>{elakayAutoText("h_54feb29794930d", "Sélectionnez un élément de la page pour pouvoir lier une clé.")}</p>}
                      </div>
                    );
                  })()}
                </Section>

                {/* ── CONTENU DISPONIBLE — scalaires + tableaux du dataset chargé ── */}
                {boundData?.data && (() => {
                  const data = boundData.data;
                  const srcKey = boundData.key;
                  const genId = () => `g-${Math.random().toString(36).slice(2, 9)}`;

                  const scalars = Object.entries(data).filter(([, v]) => typeof v === "string" || typeof v === "number");
                  const arrays  = Object.entries(data).filter(([, v]) => Array.isArray(v) && v.length > 0);
                  if (scalars.length === 0 && arrays.length === 0) return null;

                  const itemLabel = (item) => {
                    if (typeof item !== "object" || item === null) return String(item).slice(0, 40);
                    return item.title || item.name || item.label || item.question || item.value
                      || Object.values(item).find((v) => typeof v === "string" && v.length > 2)
                      || "item";
                  };

                  const itemScalars = (item) =>
                    typeof item === "object" && item !== null
                      ? Object.entries(item).filter(([k, v]) => (typeof v === "string" || typeof v === "number") && k !== "_id").slice(0, 4)
                      : [];

                  const createElemFromItem = (arrKey, item) => {
                    const fields = typeof item === "object" && item !== null ? Object.entries(item) : [];
                    const children = [];
                    fields.forEach(([k, v]) => {
                      if (k === "_id") return;
                      if (typeof v === "string" && v === "") return;
                      if (typeof v === "string" || typeof v === "number") {
                        const isTitle = ["title", "name", "label", "question", "value"].includes(k);
                        const isImg   = /image|photo|img|thumb|path/i.test(k) && typeof v === "string" && (v.startsWith("http") || v === "");
                        if (isImg) {
                          children.push({ id: genId(), type: "image", src: v || "https://placehold.co/400x240/f3f4f6/9ca3af?text=Image", bindKey: k, style: { width: "100%", maxHeight: "240px", objectFit: "cover", borderRadius: "8px", display: "block" } });
                        } else {
                          children.push({ id: genId(), type: isTitle ? "heading" : "text", text: String(v), bindKey: k, tag: isTitle ? "h2" : "p",
                            style: isTitle ? { fontSize: "18px", fontWeight: 700, color: "#111827", margin: "0 0 6px" } : { fontSize: "14px", color: "#4b5563", lineHeight: 1.6, margin: "0 0 4px" } });
                        }
                      }
                      if (Array.isArray(v) && v.length > 0 && typeof v[0] === "string") {
                        v.slice(0, 3).forEach((txt, i) => {
                          if (txt) children.push({ id: genId(), type: "text", text: txt, bindKey: `${k}.${i}`, tag: "p", style: { fontSize: "14px", color: "#4b5563", lineHeight: 1.6, margin: "0 0 2px" } });
                        });
                      }
                    });
                    insertElement({ id: genId(), type: "panel", style: { background: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px", display: "flex", flexDirection: "column", gap: "8px" }, children });
                  };

                  const toggleArr = (k) => setContentArrayExpanded((prev) => { const next = new Set(prev); next.has(k) ? next.delete(k) : next.add(k); return next; });

                  return (
                    <Section title={pbt("t014","Contenu disponible")} icon={Boxes}>
                      <p style={{ fontSize: "11px", color: "#9b93b8", margin: "0 0 10px", lineHeight: 1.5 }}>{elakayAutoText("h_c75b3437f8caaf", "Dataset :")}<strong style={{ color: "#6d28d9" }}>{boundData.label}</strong>
                      </p>

                      {/* Scalaires */}
                      {scalars.length > 0 && (
                        <div style={{ marginBottom: "12px" }}>
                          <div style={{ fontSize: "10px", fontWeight: 700, color: "#9b93b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "5px" }}>{pbt("t015","Champs")}</div>
                          {scalars.map(([k, v]) => (
                            <div key={k} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "5px 8px", background: "#f5f3ff", borderRadius: "6px", marginBottom: "3px" }}>
                              <code style={{ fontSize: "10px", color: "#6d28d9", fontFamily: "ui-monospace,monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${srcKey}.${k}`}>{srcKey}.{k}</code>
                              <span style={{ fontSize: "10px", color: "#9b93b8", maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }} title={String(v)}>{String(v).slice(0, 28)}</span>
                              <button onClick={() => { navigator.clipboard?.writeText(`${srcKey}.${k}`); setCopiedKey(`sc:${k}`); setTimeout(() => setCopiedKey(""), 1200); }}
                                style={{ ...miniBtn, padding: "2px 5px", fontSize: "9px", flexShrink: 0, color: copiedKey === `sc:${k}` ? "#16a34a" : "#6d28d9" }}>
                                {copiedKey === `sc:${k}` ? "✓" : elakayAutoText("h_auto_copy_lower", "copy")}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tableaux */}
                      {arrays.map(([arrKey, items]) => {
                        const isOpen = contentArrayExpanded.has(arrKey);
                        return (
                          <div key={arrKey} style={{ marginBottom: "8px", border: "1px solid #e4dcfb", borderRadius: "10px", overflow: "hidden" }}>
                            {/* Header */}
                            <button onClick={() => toggleArr(arrKey)}
                              style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#f5f3ff", border: "none", cursor: "pointer", textAlign: "left" }}>
                              <span style={{ flex: 1, fontSize: "11px", fontWeight: 700, color: "#6d28d9" }}>
                                {arrKey} <span style={{ fontWeight: 400, color: "#9b93b8" }}>({items.length})</span>
                              </span>
                              <code style={{ fontSize: "9px", color: "#9b93b8", fontFamily: "ui-monospace,monospace" }}>{srcKey}.{arrKey}</code>
                              <span style={{ fontSize: "10px", color: "#9b93b8" }}>{isOpen ? "▲" : "▼"}</span>
                            </button>

                            {isOpen && (
                              <>
                                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                                  {items.map((item, idx) => (
                                    <div key={idx} style={{ padding: "8px 12px", borderBottom: "1px solid #f0edf8", background: idx % 2 === 0 ? "#faf9fd" : "#fff", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                      <span style={{ fontSize: "9px", fontWeight: 700, color: "#9b93b8", background: "#f0edf8", padding: "2px 5px", borderRadius: "4px", flexShrink: 0, marginTop: "2px" }}>#{idx}</span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#2a2547", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(itemLabel(item)).slice(0, 50)}</div>
                                        {itemScalars(item).map(([fk, fv]) => (
                                          <div key={fk} style={{ fontSize: "10px", color: "#9b93b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            <span style={{ color: "#7c6fa8", fontFamily: "ui-monospace,monospace" }}>{fk}:</span> {String(fv).slice(0, 46)}
                                          </div>
                                        ))}
                                      </div>
                                      <div style={{ display: "flex", flexDirection: "column", gap: "3px", flexShrink: 0 }}>
                                        <button onClick={() => createElemFromItem(arrKey, item)}
                                          title={elakayAutoText("h_9d8459df2fe377", "Insérer comme élément dans la page")}
                                          style={{ ...miniBtn, fontSize: "9px", padding: "3px 6px", color: "#6d28d9", borderColor: "#e4dcfb", display: "flex", alignItems: "center", gap: "2px" }}>
                                          <Plus size={10} />{elakayAutoText("h_767013ce0ee0f6", "page")}</button>
                                        <button
                                          onClick={() => { navigator.clipboard?.writeText(JSON.stringify(item, null, 2)); setCopiedKey(`it:${arrKey}:${idx}`); setTimeout(() => setCopiedKey(""), 1200); }}
                                          title={elakayAutoText("h_fb0db9a18c0e6f", "Copier le JSON")}
                                          style={{ ...miniBtn, fontSize: "9px", padding: "3px 6px", color: copiedKey === `it:${arrKey}:${idx}` ? "#16a34a" : "#7a73a0", display: "flex", alignItems: "center", gap: "2px" }}>
                                          {copiedKey === `it:${arrKey}:${idx}` ? <Check size={10} /> : <Copy size={10} />}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {/* Footer */}
                                <div style={{ display: "flex", gap: "6px", padding: "7px 12px", background: "#f0edf8", justifyContent: "space-between", alignItems: "center" }}>
                                  <code style={{ fontSize: "9px", color: "#9b93b8", fontFamily: "ui-monospace,monospace" }}>{srcKey}.{arrKey}[]</code>
                                  <div style={{ display: "flex", gap: "4px" }}>
                                    <button onClick={() => items.forEach((item) => createElemFromItem(arrKey, item))}
                                      style={{ ...ghostBtn, fontSize: "9px", padding: "3px 8px", display: "flex", alignItems: "center", gap: "3px" }}>
                                      <Plus size={10} />{elakayAutoText("h_c8a0ba42574a15", "Tous (")}{items.length})
                                    </button>
                                    <button onClick={() => { navigator.clipboard?.writeText(`${srcKey}.${arrKey}`); setCopiedKey(`arr:${arrKey}`); setTimeout(() => setCopiedKey(""), 1200); }}
                                      style={{ ...miniBtn, fontSize: "9px", padding: "3px 7px", color: copiedKey === `arr:${arrKey}` ? "#16a34a" : "#6d28d9" }}>
                                      {copiedKey === `arr:${arrKey}` ? "✓" : elakayAutoText("h_d69761ba4ce3a9", "copy key")}
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </Section>
                  );
                })()}

                {/* RÉPÉTITION SUR L'ÉLÉMENT SÉLECTIONNÉ */}
                {selected && selected.repeatBind && (() => {
                  const rb = selected.repeatBind;
                  const sample = boundData?.key === rb.source ? (() => { const arr = resolvePath(boundData.data, rb.path); return Array.isArray(arr) && arr.length ? arr[0] : null; })() : null;
                  const itemKeys = sample ? listKeys(sample) : [];
                  const setRb = (patch) => updateElement(withAutoShopUserSync({ ...selected, repeatBind: { ...rb, ...patch } }));
                  return (
                    <Section title={pbt("t017","Répétition de l'élément")} icon={LayoutGrid}>
                      <p style={{ fontSize: "11px", color: "#7a73a0", margin: "0 0 8px", lineHeight: 1.5 }}>{elakayAutoText("h_98d8fe2e3f5251", "Répète sur")}<code style={{ fontFamily: "ui-monospace, monospace", color: "#6d28d9" }}>{rb.source}.{rb.path}</code>
                      </p>
                      <label style={{ display: "block", marginBottom: "8px" }}>
                        <span style={fieldLabel}>{elakayAutoText("h_a7b93d2128e820", "Mode")}</span>
                        <select value={rb.mode || "self"} onChange={(e) => setRb({ mode: e.target.value })} style={selectStyle}>
                          <option value="self">{elakayAutoText("h_eef3e2b8142e80", "L'élément est le gabarit (dupliqué)")}</option>
                          <option value="children">{elakayAutoText("h_ce60d8487281be", "Les enfants sont les gabarits (l'élément reste le conteneur)")}</option>
                        </select>
                      </label>
                      <CheckField label={elakayAutoText("h_43920149f81216", "Remplissage auto (deviner les clés)")} checked={rb.autoFill !== false} onChange={(v) => setRb({ autoFill: v })} />
                      <CheckField label={elakayAutoText("h_2c4277f5be5ebc", "Avec pagination (limiter le nombre par page)")} checked={!!rb.paginate} onChange={(v) => setRb({ paginate: v })} />
                      {rb.paginate && (
                        <label style={{ display: "block", margin: "4px 0 10px" }}>
                          <span style={fieldLabel}>{elakayAutoText("h_b8a0aa3d1df981", "Taille de page (nombre d'éléments)")}</span>
                          <input type="number" min={1} value={rb.pageSize || 12} onChange={(e) => setRb({ pageSize: Math.max(1, Number(e.target.value) || 12) })}
                            style={{ ...selectStyle, width: "100%" }} />
                          <p style={{ fontSize: "10px", color: "#9b93b8", margin: "4px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_3d12afc9c18c51", "Le système applique automatiquement le mapping aux entrées suivantes. La 1re page s'affiche dans l'aperçu.")}</p>
                        </label>
                      )}
                      {sample && (
                        <>
                          <span style={fieldLabel}>{elakayAutoText("h_a4f441508fb41c", "Correspondances clé → élément du gabarit")}</span>
                          <p style={{ fontSize: "10px", color: "#9b93b8", margin: "2px 0 6px" }}>{elakayAutoText("h_14ca6c1d960837", "Auto par défaut. Sélectionnez un élément du gabarit pour l'associer à une clé manuellement (priorité sur l'auto).")}</p>
                          <div style={{ display: "flex", flexDirection: "column", gap: "5px", maxHeight: "200px", overflowY: "auto" }}>
                            {itemKeys.map((k) => {
                              const keyName = k.key.split(".").pop();
                              const mappedId = (rb.fieldMap || {})[keyName];
                              return (
                                <div key={k.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", padding: "5px 8px", borderRadius: "6px", background: "#faf9fd", fontSize: "11px" }}>
                                  <span style={{ fontFamily: "ui-monospace, monospace", color: "#2a2547", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{keyName} <span style={{ color: "#9b93b8" }}>({k.type})</span></span>
                                  {mappedId ? (
                                    <span style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                      <span style={{ fontSize: "10px", color: "#6d28d9", fontWeight: 600 }}>→ {String(mappedId).slice(0, 8)}</span>
                                      <button onClick={() => { const fm = { ...(rb.fieldMap || {}) }; delete fm[keyName]; setRb({ fieldMap: fm }); }} style={{ ...miniBtn, padding: "2px 6px", fontSize: "10px", color: "#e11d48" }}>×</button>
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: "10px", color: "#9b93b8" }}>{elakayAutoText("h_0d612c12d2ac33", "auto")}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <p style={{ fontSize: "10px", color: "#b3aacb", marginTop: "8px", lineHeight: 1.5 }}>{elakayAutoText("h_29f6b8f0e8d0f7", "Astuce : pour forcer manuellement, sélectionnez d'abord un élément du gabarit, puis dans ses propriétés mettez sa")}<strong>{elakayAutoText("h_a67c9ae449db04", "clé de liaison")}</strong>{elakayAutoText("h_d2c50a1688a9e5", "(bindKey) au nom voulu.")}</p>
                        </>
                      )}
                      {/* ── Action click : catégorie, lien produit, pagination ── */}
                      <div style={{ marginTop: "12px", borderTop: "1px solid #ece9f5", paddingTop: "12px" }}>
                        <span style={{ ...fieldLabel, display: "block", marginBottom: "6px" }}>{elakayAutoText("h_1afc0f1a9f214d", "Action au clic")}</span>
                        <label style={{ display: "block", marginBottom: "8px" }}>
                          <select value={rb.clickAction || "auto"} onChange={(e) => setRb({ clickAction: e.target.value })} style={selectStyle}>
                            <option value="auto">{elakayAutoText("h_46a501c166926d", "Auto-detect (catégorie ou produit selon le chemin)")}</option>
                            <option value="none">{elakayAutoText("h_77b96b458275d5", "Aucune action")}</option>
                            <option value="loadCategory">{elakayAutoText("h_30ff15b81f5434", "Charger catégorie → loadProductsByArrival_(catId, catName)")}</option>
                            <option value="productLink">{elakayAutoText("h_070af0a07e228b", "Lien produit → /product/preview/")}{elakayAutoText("h_6cef2721bfcb9b", "{_id}")}</option>
                          </select>
                          <p style={{ fontSize: "10px", color: "#9b93b8", margin: "4px 0 0", lineHeight: 1.4 }}>{elakayAutoText("h_143bce49a7651a", "Auto : détecte \"categories\" → charge catégorie ; \"products\" → lien produit.")}</p>
                        </label>
                        {(rb.clickAction === "productLink" || rb.clickAction === "auto" || !rb.clickAction) && (
                          <StyleField
                            label={elakayAutoText("h_dde4e53bc31fa3", "Champ ID produit")}
                            value={rb.productIdField || "_id"}
                            onChange={(v) => setRb({ productIdField: v })}
                            placeholder={elakayAutoText("h_a6526f25fbdfbb", "_id")}
                          />
                        )}
                        <CheckField
                          label={elakayAutoText("h_ac19f932346eeb", "Injecter liens produit sur les images (imageProductLink)")}
                          checked={!!rb.imageProductLink}
                          onChange={(v) => setRb({ imageProductLink: v })}
                        />
                        {rb.imageProductLink && (
                          <p style={{ fontSize: "10px", color: "#9b93b8", margin: "-4px 0 8px", lineHeight: 1.4 }}>{elakayAutoText("h_b8d8857c048f75", "Chaque image dans le gabarit reçoit automatiquement le lien /product/preview/")}{elakayAutoText("h_6cef2721bfcb9b", "{_id}")}.
                          </p>
                        )}
                      </div>

                      {/* ── Pagination via élément dédié ── */}
                      <div style={{ marginTop: "10px", borderTop: "1px solid #ece9f5", paddingTop: "10px" }}>
                        <span style={{ ...fieldLabel, display: "block", marginBottom: "6px" }}>{elakayAutoText("h_100325b8d6de92", "Pagination")}</span>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 12px", background: "#f3effe", borderRadius: "9px", border: "1px solid #e4dcfb" }}>
                          <ChevronsDownUp size={16} color="#6d28d9" style={{ flexShrink: 0, marginTop: "1px" }} />
                          <div>
                            <p style={{ fontSize: "11px", fontWeight: 700, color: "#6d28d9", margin: "0 0 3px" }}>{elakayAutoText("h_23eedb034ea1ab", "Utilisez l'élément « Pagination (← / →) »")}</p>
                            <p style={{ fontSize: "10px", color: "#7a73a0", margin: 0, lineHeight: 1.5 }}>{elakayAutoText("h_d27a21c6b7e99b", "Ajoutez un élément")}<strong>{elakayAutoText("h_2e6ced95027232", "Boutique Live → Pagination")}</strong>{elakayAutoText("h_41239e239b9db3", "sur la page pour contrôler la navigation. Il se synchronise automatiquement avec")}<code style={{ fontFamily:"ui-monospace,monospace" }}>storeState</code>{elakayAutoText("h_dc560abfd3cbaa", "(skip · limit · siseProduct).")}</p>
                          </div>
                        </div>
                      </div>

                      <button onClick={() => updateElement({ ...selected, repeatBind: null })} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "12px", color: "#e11d48", borderColor: "#f3b4c2" }}>{elakayAutoText("h_3f9055340080c4", "Supprimer la répétition")}</button>
                    </Section>
                  );
                })()}

                {/* LIAISONS ACTIVES */}
                {bindings.length > 0 && (
                  <Section title={`${pbt("t018","Liaisons actives")} (${bindings.length})`} icon={Code2}>
                    {bindings.map((b, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", padding: "8px 10px", marginBottom: "6px", border: "1px solid #ece9f5", borderRadius: "10px" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "11px", fontWeight: 700, color: b.repeat ? "#6d28d9" : "#2a2547" }}>{b.repeat ? elakayAutoText("h_3aa64b35b8601a", "RÉPÉTITION") : elakayAutoText("h_c76309609b43c6", "CHAMP")}</div>
                          <div style={{ fontSize: "11px", color: "#7a73a0", fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.source}.{b.path}</div>
                          <div style={{ fontSize: "10px", color: "#9b93b8" }}>{elakayAutoText("h_13fdcf9aaaf46b", "→ élément")}{b.elementId.slice(0, 8)}</div>
                        </div>
                        <button onClick={() => setBindings((prev) => prev.filter((_, j) => j !== i))} style={{ ...miniBtn, color: "#e11d48" }}><X size={14} /></button>
                      </div>
                    ))}
                    <p style={{ fontSize: "11px", color: "#b3aacb", margin: "4px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_b94b68aee14437", "Les liaisons connectent une clé de donnée à un élément. Elles seront sauvegardées avec la page (champ « bindings »).")}</p>
                  </Section>
                )}

                {/* EXPORTER */}
                <Section title={pbt("t019","Exporter")} icon={Download}>
                  <p style={{ fontSize: "12px", color: "#9b93b8", lineHeight: 1.5, margin: "0 0 10px" }}>
                    {selectedIds.length ? `${selectedIds.length} élément(s) sélectionné(s).` : pbt("t048","Aucune sélection — sélectionnez des éléments pour les exporter séparément.")}
                  </p>
                  <button onClick={copySelection} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginBottom: "8px" }}><Copy size={15} /> {pbt("t029","Copier la sélection (JSON)")}</button>
                  <button onClick={exportSelection} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginBottom: "8px" }}><Download size={15} /> {pbt("t030","Télécharger la sélection")}</button>
                  <button onClick={copyPage} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginBottom: "8px" }}><Copy size={15} /> {pbt("t031","Copier toute la page")}</button>
                  <button onClick={exportJson} style={{ ...ghostBtn, width: "100%", justifyContent: "center" }}><FileJson size={15} /> {pbt("t032","Télécharger toute la page")}</button>
                </Section>

                {/* COLLER / IMPORTER */}
                <Section title={pbt("t020","Coller du JSON")} icon={FileJson}>
                  <p style={{ fontSize: "12px", color: "#9b93b8", lineHeight: 1.5, margin: "0 0 8px" }}>{elakayAutoText("h_cffc5e8ddb6f4f", "Collez n'importe quel contenu : un élément, un tableau d'éléments, ou un document complet.")}</p>
                  <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} spellCheck={false}
                    placeholder={elakayAutoText("h_573a563cad3d9b", "{ \"type\": \"heading\", \"text\": \"...\" } ou [ {...}, {...} ]")}
                    style={{ width: "100%", minHeight: "140px", fontFamily: "ui-monospace, monospace", fontSize: "12px", lineHeight: 1.5, padding: "10px", border: "1px solid #e4e0f0", borderRadius: "10px", background: "#1a1a2e", color: "#d6cfec", outline: "none", resize: "vertical" }} />
                  {pasteError && <p style={{ color: "#e11d48", fontSize: "12px", margin: "8px 0 0" }}>⚠ {pasteError}</p>}
                  <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                    <button onClick={addPastedJson} style={{ ...primaryBtn, flex: 1, justifyContent: "center" }}><Plus size={15} />{elakayAutoText("h_87c57ed173ea17", "Ajouter")}</button>
                    <button onClick={replaceWithPasted} style={{ ...ghostBtn, flex: 1, justifyContent: "center" }}>{elakayAutoText("h_db2c67bc24f1ff", "Remplacer")}</button>
                  </div>
                  <p style={{ fontSize: "11px", color: "#b3aacb", margin: "8px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_e7475ac2a54734", "« Ajouter » insère dans le conteneur sélectionné (ou à la racine). « Remplacer » écrase la page.")}</p>
                </Section>

                {/* BASE DE DONNÉES (API) */}
                <Section title={pbt("t021","Base de données (API)")} icon={Boxes}>
                  <StyleField label={elakayAutoText("h_7e8c03854820a4", "URL de votre API")} value={apiUrl} onChange={setApiUrl} placeholder="https://mon-api.com/contenu" />
                  <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                    <button onClick={() => sendToApi("page")} style={{ ...ghostBtn, flex: 1, justifyContent: "center", fontSize: "12px" }}>{pbt("t034","Envoyer la page")}</button>
                    <button onClick={() => sendToApi("selection")} style={{ ...ghostBtn, flex: 1, justifyContent: "center", fontSize: "12px" }}>{pbt("t035","Envoyer la sélection")}</button>
                  </div>
                  <button onClick={loadFromApi} style={{ ...ghostBtn, width: "100%", justifyContent: "center", marginTop: "8px" }}><Upload size={15} /> {pbt("t033","Charger depuis l'API")}</button>
                  <p style={{ fontSize: "11px", color: "#b3aacb", margin: "8px 0 0", lineHeight: 1.5 }}>{elakayAutoText("h_4d3264df3bcbb6", "Envoi en POST (JSON). Le chargement (GET) place le contenu dans la zone de collage ci-dessus.")}</p>
                </Section>

                {dataStatus && (
                  <div style={{ padding: "10px 12px", borderRadius: "10px", background: "#f3effe", color: "#6d28d9", fontSize: "12px", fontWeight: 600, lineHeight: 1.4 }}>
                    {dataStatus}
                  </div>
                )}
              </div>
            )}

            {/* ── Section API Boutique (ShopUser) ─────────────────────────── */}
            {doc.pageMode === "store" && (() => {
              const API_ENDPOINTS = [
                { method: "POST", label: elakayAutoText("h_f1e0fa3ab03749", "Produits boutique"), url: "/products/lakayUser/{userID}", returns: "products, categories, owner, pubCategoryProdMatrix, filters, siseProduct" },
                { method: "GET",  label: elakayAutoText("h_e7fd877809fa5f", "Publications / Slider"), url: "/user_pub/{userID}", returns: "sliderShop[]" },
                { method: "PUT",  label: elakayAutoText("h_0956345fcd2b01", "Modifier adresse"), url: "/user/editAddressUser/{id}", returns: "address updated" },
                { method: "PUT", label: elakayAutoText("h_1018336fac123b", "Suivre / ne plus suivre boutique"), url: "/user/addFollowUserShop/{userID}", returns: "followShopUser" },
              ];
              const DATA_KEYS = [
                { key: "store.owner.shopName.0",                desc: "Nom boutique (1er)" },
                { key: "store.owner.firstname",                 desc: "Prénom vendeur" },
                { key: "store.owner.email",                     desc: "Email vendeur" },
                { key: "store.siseProductAll",                  desc: "Total produits" },
                { key: "store.siseProduct",                     desc: "Produits chargés" },
                { key: "store.sliderShop",                      desc: "Slides hero bannière" },
                { key: "store.products",                        desc: "Tableau produits (paginé)" },
                { key: "store.products.0._id",                  desc: "ID du 1er produit" },
                { key: "store.products.0.name",                 desc: "Nom du 1er produit" },
                { key: "store.products.0.price",                desc: "Prix du 1er produit" },
                { key: "store.products.0.salePrice",            desc: "Remise (% ou 0)" },
                { key: "store.products.0.Stars",                desc: "Note (étoiles)" },
                { key: "store.products.0.ProductImageUrls.0", desc: "Image produit (URL)" },
                { key: "store.categories.categories",           desc: "Tableau catégories" },
                { key: "store.categories.owner",                desc: "ID propriétaire boutique" },
                { key: "store.shopPubMatrix.products",          desc: "Produits vedettes (alias SwiperPubCategoryProdMatrix)" },
                { key: "store.pubCategoryProdMatrix.products",  desc: "Produits vedettes" },
                { key: "store.filters",                         desc: "Filtres disponibles" },
                { key: "store.links.shopPage",                  desc: "URL page boutique" },
                { key: "store.links.productDetail",             desc: "URL détail produit (base)" },
                { key: "store.links.myShop",                    desc: "URL mon espace vendeur" },
              ];
              const ROUTES = [
                { route: "/lakay-ShopSeller/{userID}", desc: "Page boutique" },
                { route: "/lakay-ShopSeller/{userID}?ci={catID}", desc: "Boutique filtrée catégorie" },
                { route: "/product/preview/{productID}", desc: "Détail produit" },
                { route: "/cart", desc: "Panier et validation de commande" },
                { route: "/myShop", desc: "Mon espace vendeur" },
                { route: "/pageBuilder?mode=store", desc: "Éditeur boutique" },
              ];
              const STORE_ACTIONS = [
                { fn: "loadProducts(params?)", desc: "Recharge les produits avec pagination, recherche, catégorie et filtres." },
                { fn: "loadindSearCh(searchText)", desc: "Recherche boutique (texte)" },
                { fn: "loadProductsByArrival(search?, filters?)", desc: "Reload global produits + owner + catégories + vedettes" },
                { fn: "loadProductsByArrival_(catID?, catName?)", desc: "Filtrer par catégorie (clic categories/pub matrix)" },
                { fn: "loadMore(limit?)", desc: "Page suivante produits (pagination)" },
                { fn: "loadMore0(limit?)", desc: "Page précédente produits (pagination)" },
                { fn: "onFilterShop()", desc: "Reset recherche + afficher filtres (compat shopUser)" },
                { fn: "setFilterShop(value)", desc: "Toggle filtre boutique (compat shopUser)" },
                { fn: "setSearch(text)", desc: "Met à jour la recherche locale (compat shopUser)" },
                { fn: "clearSearch()", desc: "Efface la recherche et recharge les produits." },
                { fn: "resetShopFilters()", desc: "Réinitialise les filtres et recharge la boutique." },
                { fn: "scrollToProducts()", desc: "Fait défiler la page jusqu'à la grille produits." },
                { fn: "viewProduct(product?)", desc: "Ouvre la page détail du produit." },
                { fn: "goToCart()", desc: "Ouvre le panier." },
                { fn: "goToCheckout()", desc: "Ouvre la page de validation configurée." },
                { fn: "goToShop()", desc: "Ouvre la page publique de la boutique." },
                { fn: "clickToFollowShop()", desc: "Bascule Follow / Unfollow avec addFollowUserShop, puis actualise le compte connecté." },
                { fn: "findToFollowU(...)", desc: "Détermine si l'utilisateur peut suivre la boutique." },
                { fn: "unFollowShop(...)", desc: "Synchronise l'état Follow / Unfollow depuis followShopUser." },
                { fn: "loadingUserPub(userID)", desc: "Compat héritée chargement pubs (stub runtime)" },
                { fn: "addToCart(product?, payload?)", desc: "Ajout panier. Sur bouton d'une carte produit (repeatBind), le produit courant est passé automatiquement — aucun arg nécessaire." },
                { fn: "buyNow(product?, payload?)", desc: "Ajoute le produit au panier puis ouvre la validation." },
                { fn: "getCart()", desc: "Charge le panier de l'utilisateur connecté." },
              ];
              const copyBtn = (text) => (
                <button
                  onClick={() => navigator.clipboard?.writeText(text)}
                  title={elakayAutoText("h_cdd28e1a695416", "Copier")}
                  style={{ flexShrink: 0, padding: "2px 6px", fontSize: "9px", border: "1px solid #e4dcfb", borderRadius: "4px", background: "#fff", color: "#6d28d9", cursor: "pointer" }}
                >{elakayAutoText("h_f84e2e2dadd873", "copy")}</button>
              );
              const methodColor = { GET: "#d1fae5", POST: "#dbeafe", PUT: "#fef3c7" };
              const methodText  = { GET: "#065f46", POST: "#1e40af", PUT: "#92400e" };
              return (
                <div style={{ marginTop: "14px", border: "1px solid #ece9f5", borderRadius: "12px", overflow: "hidden" }}>
                  {/* Header collapsible */}
                  <button
                    onClick={() => setApiBoutiqueOpen((v) => !v)}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: "7px", padding: "10px 14px", background: "#f5f3ff", border: "none", cursor: "pointer", textAlign: "left" }}
                  >
                    <Package size={13} color="#6d28d9" />
                    <span style={{ flex: 1, fontSize: "11px", fontWeight: 700, color: "#6d28d9" }}>{elakayAutoText("h_23ec7b37b023fc", "API ShopUser · Endpoints, Données & Liens")}</span>
                    <span style={{ fontSize: "12px", color: "#9b93b8" }}>{apiBoutiqueOpen ? "▲" : "▼"}</span>
                  </button>

                  {apiBoutiqueOpen && (
                    <div style={{ padding: "10px 14px 14px" }}>

                      {/* ── Élément sélectionné (JSON) ── */}
                      {selected ? (
                        <div style={{ marginBottom: "14px", border: "1px solid #e4dcfb", borderRadius: "10px", overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: "#f5f3ff", borderBottom: "1px solid #e4dcfb" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", fontWeight: 700, color: "#6d28d9" }}>
                              <Code2 size={12} />{elakayAutoText("h_bfe9dc4921c369", "Élément sélectionné ·")}<code style={{ fontWeight: 400, color: "#9b93b8" }}>{selected.type}</code>
                            </span>
                            <button
                              onClick={() => navigator.clipboard?.writeText(JSON.stringify(selected, null, 2))}
                              style={{ padding: "2px 7px", fontSize: "9px", border: "1px solid #e4dcfb", borderRadius: "4px", background: "#fff", color: "#6d28d9", cursor: "pointer" }}
                            >{elakayAutoText("h_f84e2e2dadd873", "copy")}</button>
                          </div>
                          <pre style={{ margin: 0, padding: "10px 12px", maxHeight: "220px", overflowY: "auto", background: "#1a1a2e", color: "#d6cfec", fontSize: "10px", lineHeight: 1.6, fontFamily: "ui-monospace,'SF Mono',Menlo,monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {JSON.stringify(selected, null, 2)}
                          </pre>
                        </div>
                      ) : (
                        <div style={{ marginBottom: "12px", padding: "8px 10px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #ece9f5", fontSize: "10px", color: "#9b93b8", textAlign: "center" }}>{elakayAutoText("h_67051b58a39a2a", "Aucun élément sélectionné")}</div>
                      )}

                      {/* Endpoints API */}
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#9b93b8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "7px" }}>{elakayAutoText("h_911b4b8062afee", "Endpoints API")}</div>
                      {API_ENDPOINTS.map((api, i) => (
                        <div key={i} style={{ marginBottom: "5px", padding: "6px 8px", background: "#f9fafb", borderRadius: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "2px" }}>
                            <span style={{ padding: "1px 5px", borderRadius: "3px", background: methodColor[api.method] || "#f3f4f6", color: methodText[api.method] || "#374151", fontSize: "9px", fontWeight: 700, flexShrink: 0 }}>{api.method}</span>
                            <span style={{ fontSize: "10px", fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{api.label}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <code style={{ fontSize: "9px", color: "#7a73a0", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{api.url}</code>
                            {copyBtn(api.url)}
                          </div>
                          <div style={{ fontSize: "9px", color: "#9b93b8", marginTop: "2px" }}>→ {api.returns}</div>
                        </div>
                      ))}

                      {/* Clés de données */}
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#9b93b8", letterSpacing: "0.08em", textTransform: "uppercase", margin: "12px 0 7px" }}>{elakayAutoText("h_fbcd25d36398e8", "Clés bindKey (store.*)")}</div>
                      {DATA_KEYS.map((item, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "3px", padding: "4px 7px", background: "#f5f3ff", borderRadius: "6px" }}>
                          <code style={{ fontSize: "9px", color: "#6d28d9", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.key}>{item.key}</code>
                          <span style={{ fontSize: "9px", color: "#9b93b8", flexShrink: 0, maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.desc}>{item.desc}</span>
                          {copyBtn(item.key)}
                        </div>
                      ))}

                      {/* Liens & Routes */}
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#9b93b8", letterSpacing: "0.08em", textTransform: "uppercase", margin: "12px 0 7px" }}>{elakayAutoText("h_6505ca2e741453", "Liens & Routes")}</div>
                      {ROUTES.map((link, i) => (
                        <div key={i} style={{ marginBottom: "4px", padding: "5px 8px", background: "#f0f9ff", borderRadius: "7px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <code style={{ fontSize: "9px", color: "#0369a1", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={link.route}>{link.route}</code>
                            {copyBtn(link.route)}
                          </div>
                          <div style={{ fontSize: "9px", color: "#9b93b8" }}>{link.desc}</div>
                        </div>
                      ))}

                      {/* Fonctions runtime disponibles côté rendu */}
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#9b93b8", letterSpacing: "0.08em", textTransform: "uppercase", margin: "12px 0 7px" }}>{elakayAutoText("h_4b24ecf6e87898", "Fonctions runtime (storeActions)")}</div>
                      <p style={{ fontSize: "9px", color: "#9b93b8", margin: "0 0 6px", lineHeight: 1.5 }}>{elakayAutoText("h_70028870d03cf2", "Utilisables dans le")}<strong>{elakayAutoText("h_df3a5fe8cefc43", "consoleJs")}</strong>{elakayAutoText("h_d5ae2c8953c9bc", "d'un élément via")}<code>context.storeActions</code>.
                      </p>
                      {STORE_ACTIONS.map((item, i) => (
                        <div key={i} style={{ marginBottom: "4px", padding: "5px 8px", background: "#ecfeff", borderRadius: "7px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <code style={{ fontSize: "9px", color: "#0f766e", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.fn}>
                              context.storeActions.{item.fn}
                            </code>
                            {copyBtn(`context.storeActions.${item.fn}`)}
                          </div>
                          <div style={{ fontSize: "9px", color: "#9b93b8" }}>{item.desc}</div>
                        </div>
                      ))}
                      <div style={{ marginTop: "6px", padding: "6px 8px", background: "#faf9fd", border: "1px solid #ece9f5", borderRadius: "7px" }}>
                        <code style={{ fontSize: "9px", color: "#6d28d9", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {"context.storeActions.loadProductsByArrival_('catID123', 'Sneakers')"}
                        </code>
                      </div>

                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        </aside>
          );
          return rightIsDetached && detachedPanelRootRef.current
            ? createPortal(rightPanel, detachedPanelRootRef.current)
            : rightPanel;
        })()}
      </div>

      {/* Backdrop derrière les panneaux rétractables (mobile) — visible quand un drawer est ouvert */}
      {(mobileLeft || mobileRight) && (
        <div className="pb-aside-backdrop" onClick={() => { setMobileLeft(false); setMobileRight(false); }} />
      )}

      {centerDetached && detachedRootRef.current && createPortal(
        <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "#f6f4fb" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", padding: "10px 14px", background: "#fff", borderBottom: "1px solid #ece9f5" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#1a1a2e" }}>{elakayAutoText("h_9367375dd75b76", "Panneau central détaché")}</span>
            <button onClick={closeDetachedCenter} style={{ ...ghostBtn, padding: "6px 10px" }}>
              <ChevronLeft size={14} />{elakayAutoText("h_5a76fdb6d2b6eb", "Revenir")}</button>
          </div>
          <div style={{ position: "relative", flex: 1, overflow: "hidden", background: "radial-gradient(circle at 50% 0%, #efeafa, #f6f4fb)" }}>
            {centerPanelContent}
          </div>
        </div>,
        detachedRootRef.current
      )}

      {/* ── Modal nom de sauvegarde ───────────────────────────────────── */}
      {showSaveNameModal && (
        <ViewportModal>
          <div
            onClick={(event) => {
              if (event.target === event.currentTarget && !isSaving) {
                setShowSaveNameModal(false);
                setSaveNameError("");
              }
            }}
            style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center", justifyContent:"center",
              overflowY:"auto", padding:"20px", background:"rgba(10,8,24,0.58)", backdropFilter:"blur(4px)" }}
          >
            <form
              onSubmit={handleSaveNameSubmit}
              style={{ width:"100%", maxWidth:"430px", maxHeight:"calc(100vh - 40px)", background:"#fff", borderRadius:"18px",
                boxShadow:"0 24px 80px rgba(0,0,0,0.32)", overflowY:"auto" }}
            >
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"16px 20px", background:"linear-gradient(135deg,#6d28d9,#a855f7)" }}>
              <div>
                <div style={{ fontSize:"15px", fontWeight:800, color:"#fff" }}>
                  {saveNameMode === "rename"
                    ? doc?.pageMode === "store" ? elakayAutoText("h_48e84e335496bc", "Modifier le nom de la boutique") : elakayAutoText("h_987ab973e5141e", "Modifier le nom de la page")
                    : doc?.pageMode === "store" ? elakayAutoText("h_f249188fb805a3", "Sauvegarder la boutique") : elakayAutoText("h_cbbf19a02663ea", "Sauvegarder la page")}
                </div>
                <div style={{ marginTop:"2px", fontSize:"11px", color:"rgba(255,255,255,0.78)" }}>
                  {saveNameMode === "rename"
                    ? elakayAutoText("h_1716be44de7576", "Le nouveau nom sera mis à jour dans « Mes pages ».")
                    : elakayAutoText("h_a81df0746dec2c", "Choisissez le nom qui apparaîtra dans « Mes pages ».")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { if (!isSaving) setShowSaveNameModal(false); }}
                disabled={isSaving}
                aria-label={elakayAutoText("h_5ab4ec640d654d", "Fermer")}
                style={{ border:"none", background:"transparent", color:"#fff", fontSize:"22px",
                  lineHeight:1, cursor:isSaving ? "not-allowed" : "pointer", opacity:isSaving ? 0.5 : 0.9 }}
              >
                ×
              </button>
            </div>

            <div style={{ padding:"22px" }}>
              <label style={{ display:"block" }}>
                <span style={{ display:"block", marginBottom:"6px", fontSize:"11px", fontWeight:800,
                  color:"#6d28d9", textTransform:"uppercase", letterSpacing:"0.05em" }}>
                  {doc?.pageMode === "store" ? elakayAutoText("h_417e80d1004649", "Nom de la boutique *") : elakayAutoText("h_812043956d3084", "Nom de la page *")}
                </span>
                <input
                  autoFocus
                  value={saveName}
                  onChange={(event) => {
                    setSaveName(event.target.value);
                    if (saveNameError) setSaveNameError("");
                  }}
                  placeholder={doc?.pageMode === "store" ? elakayAutoText("h_d09a4b320f58b8", "Ex. Boutique Mode Élégance") : elakayAutoText("h_1b3ed808ccf510", "Ex. Ma page d’accueil")}
                  disabled={isSaving}
                  style={{ width:"100%", boxSizing:"border-box", padding:"11px 13px", fontSize:"14px",
                    color:"#1a1a2e", background:"#faf9fd", border:`1px solid ${saveNameError ? "#fb7185" : "#d9d3ec"}`,
                    borderRadius:"9px", outline:"none" }}
                />
              </label>

              {saveNameError && (
                <div style={{ marginTop:"7px", fontSize:"11px", fontWeight:700, color:"#e11d48" }}>
                  {saveNameError}
                </div>
              )}

              <div style={{ display:"flex", justifyContent:"flex-end", gap:"8px", marginTop:"20px" }}>
                <button
                  type="button"
                  onClick={() => { setShowSaveNameModal(false); setSaveNameError(""); }}
                  disabled={isSaving}
                  style={{ padding:"9px 16px", fontSize:"12px", fontWeight:700, color:"#6d28d9",
                    background:"#fff", border:"1px solid #d9d3ec", borderRadius:"8px",
                    cursor:isSaving ? "not-allowed" : "pointer" }}
                >{elakayAutoText("h_49ba3292420c74", "Annuler")}</button>
                <button
                  type="submit"
                  disabled={isSaving}
                  style={{ display:"flex", alignItems:"center", gap:"6px", padding:"9px 18px",
                    fontSize:"12px", fontWeight:800, color:"#fff", border:"none", borderRadius:"8px",
                    background:isSaving ? "#9ca3af" : "linear-gradient(135deg,#16a34a,#15803d)",
                    cursor:isSaving ? "not-allowed" : "pointer" }}
                >
                  <Download size={13} />
                  {isSaving ? elakayAutoText("h_853081ab6b1461", "Sauvegarde…") : saveNameMode === "rename" ? elakayAutoText("h_aff833480075d2", "Enregistrer le nom") : elakayAutoText("h_9ada14395d664f", "Sauvegarder")}
                </button>
              </div>
            </div>
            </form>
          </div>
        </ViewportModal>
      )}

      {/* ── Modal Templates & Sources de données ───────────────────────── */}
      {showTemplatePicker && (() => {
        const allTemplates = boutiqueTemplates;
        const modeLabel = PAGE_MODES.find((m) => m.id === doc.pageMode)?.label || doc.pageMode;
        const modeTpls = allTemplates.filter((t) => t.pageMode === doc.pageMode);
        const modeDatasets = Object.entries(allDatasets).filter(([, d]) => d.pageMode === doc.pageMode);

        const loadTemplate = async (tpl) => {
          // Priorité à l'override Family (shopTemplate.<key>/projectTemplate.<key>/
          // contentTemplate.<key>) avant le template codé en dur -- voir
          // resolveShopOrProjectTemplateDoc. "contentTemplate" (fix-400 +
          // system-template-selection mission) is PageBuilder's own starter
          // Content Templates (Classic/Hero/Magazine/...), distinct from the
          // 34 real Content Pages -- now has the SAME registered override
          // group as Shop/Project templates, so a Family customization made
          // via SystemTemplatesPanel is reflected here too, not just tpl.make().
          const overrideGroup = tpl.pageMode === "store" ? "shopTemplate" : tpl.pageMode === "project" ? "projectTemplate" : tpl.pageMode === "contentPage" ? "contentTemplate" : null;
          const newDoc = overrideGroup ? await resolveShopOrProjectTemplateDoc(tpl, overrideGroup) : tpl.make();
          const source = tpl.pageMode === "store" ? "default" : null;
          selectShopSource(source, source ? { _id: tpl._id || tpl.key, title: tpl.label } : null);
          setDoc(newDoc);
          setSelectedIds([]);
          setJsonText(JSON.stringify(newDoc, null, 2));
          selectDatasetForPageMode(tpl.pageMode, newDoc?.meta?.contentPageSourceKey);
          setShowTemplatePicker(false);
          setShowPreview(Boolean(source));
          setLeftTab(source ? "boutique" : "add");
          notifyLoadedPage(newDoc, null, null, source);
        };

        const loadDataset = (key, ds) => {
          bindDataset(key, ds);
          setShowTemplatePicker(false);
          setRightTab("data");
        };

        const modeColors = {
          store:       { bg: "#f5f3ff", text: "#6d28d9", border: "#e4dcfb" },
          contentPage: { bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" },
          static:      { bg: "#f0f9ff", text: "#0369a1", border: "#bae6fd" },
          project:     { bg: "#fffbeb", text: "#b45309", border: "#fcd34d" },
          elakayJod:   { bg: "#fff1f2", text: "#be123c", border: "#fecdd3" },
        };
        const modeIcon = (id, size = 11) => {
          if (id === "store")       return <Package size={size} />;
          if (id === "contentPage") return <FileText size={size} />;
          if (id === "project")     return <TrendingUp size={size} />;
          if (id === "elakayJod")   return <SquareStack size={size} />;
          return <LayoutGrid size={size} />;
        };
        const mc = modeColors[doc.pageMode] || { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" };

        return (
          <ViewportModal>
            <div
              onClick={() => setShowTemplatePicker(false)}
              style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center",
                justifyContent:"center", overflowY:"auto", padding:"24px",
                background:"rgba(20,12,40,0.55)", backdropFilter:"blur(4px)" }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ width:"100%", maxWidth:"820px", maxHeight:"calc(100vh - 48px)", display:"flex",
                  flexDirection:"column", background:"#fff", borderRadius:"18px", overflow:"hidden",
                  boxShadow:"0 24px 80px rgba(0,0,0,0.35)" }}
              >
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px", borderBottom: "1px solid #ece9f5", background: "#faf9fd", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Sparkles size={18} color="#6d28d9" />
                  <span style={{ fontSize: "15px", fontWeight: "800", color: "#1a1a2e" }}>{elakayAutoText("h_8986a239fcbcb8", "Templates & Sources de données")}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "50px", fontSize: "11px", fontWeight: "700", background: mc.bg, color: mc.text, border: `1px solid ${mc.border}` }}>
                    {modeIcon(doc.pageMode)}
                    {modeLabel}
                  </span>
                </div>
                <button onClick={() => setShowTemplatePicker(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", border: "1px solid #ece9f5", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#9b93b8" }}>
                  <X size={16} />
                </button>
              </div>

              {/* Mode tabs */}
              <div style={{ display: "flex", gap: "6px", padding: "12px 22px 0", flexShrink: 0 }}>
                {PAGE_MODES.map((m) => {
                  const active = m.id === doc.pageMode;
                  const c = modeColors[m.id] || { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" };
                  const tplCount = allTemplates.filter((t) => t.pageMode === m.id).length;
                  const dsCount = Object.values(allDatasets).filter((d) => d.pageMode === m.id).length;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setPageMode(m.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: "6px",
                        padding: "7px 14px", fontSize: "12px", fontWeight: active ? 700 : 500,
                        borderTop: `1px solid ${active ? c.border : "#ece9f5"}`,
                        borderLeft: `1px solid ${active ? c.border : "#ece9f5"}`,
                        borderRight: `1px solid ${active ? c.border : "#ece9f5"}`,
                        borderRadius: "10px 10px 0 0", borderBottom: active ? "none" : "1px solid #ece9f5",
                        background: active ? "#fff" : "#faf9fd", color: active ? c.text : "#9b93b8",
                        cursor: "pointer", marginBottom: active ? "-1px" : "0", position: "relative", zIndex: active ? 2 : 1,
                      }}
                    >
                      {modeIcon(m.id, 12)}
                      {m.label.split(" ")[0]}
                      <span style={{ fontSize: "10px", fontWeight: 600, padding: "1px 5px", borderRadius: "50px", background: active ? c.bg : "#f3f4f6", color: active ? c.text : "#9b93b8" }}>
                        {tplCount}t · {dsCount}d
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflowY: "auto", display: "flex", gap: "0", borderTop: "1px solid #ece9f5" }}>

                {/* ── Colonne Templates ── */}
                <div style={{ flex: "0 0 55%", padding: "20px 22px", borderRight: "1px solid #ece9f5", overflowY: "auto" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#9b93b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Sparkles size={12} />{elakayAutoText("h_a74a8a512d0bd6", "Templates (")}{modeTpls.length})
                  </div>

                  {modeTpls.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px 16px", color: "#b3aacb" }}>
                      <LayoutGrid size={32} style={{ margin: "0 auto 10px", opacity: 0.4 }} />
                      <p style={{ fontSize: "13px", margin: 0 }}>{elakayAutoText("h_0cf6dd9036843f", "Aucun template pour le mode")}<strong>{doc.pageMode}</strong>.</p>
                      <p style={{ fontSize: "11px", margin: "6px 0 0", color: "#c9b8f5" }}>
                        {doc.pageMode === "contentPage"
                          ? elakayAutoText("h_2ee2fcf2ebd9ba", "Ajoutez des entrées dans ContentPageTemplates.js.")
                          : doc.pageMode === "project"
                            ? elakayAutoText("h_0640ee2c12e3e7", "Ajoutez des entrées dans ProjectInvestTemplates.js.")
                            : elakayAutoText("h_51caa83211063b", "Ajoutez des entrées dans ShopBoutiqueTemplates.js.")}
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {modeTpls.map((tpl) => {
                        // SELECT (task spec §6-9): a SEPARATE concept from
                        // Load -- "Load" opens the template INTO the Builder
                        // to explore/customize it; "Select" just marks it as
                        // the one a Data Source's "Lier" button will assign.
                        // Only meaningful for Content Templates (System/
                        // Default Templates, `contentTemplate.<key>` --
                        // task spec's own scope for this mission); Shop/
                        // Project templates keep their existing Load-only
                        // flow, unchanged.
                        const isSystemTemplateCard = tpl.pageMode === "contentPage";
                        const systemTemplateKey = `contentTemplate.${tpl.key}`;
                        const isSelected = isSystemTemplateCard && selectedSystemTemplateKey === systemTemplateKey;
                        return (
                        <div
                          key={tpl.key}
                          style={{ border: isSelected ? "2px solid #6d28d9" : "1px solid #ece9f5", borderRadius: "14px", overflow: "hidden", background: isSelected ? "#f5f3ff" : "#faf9fd", transition: "box-shadow 0.15s, border-color 0.15s" }}
                          onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 20px rgba(109,40,217,0.10)"}
                          onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}
                        >
                          {/* Bande palette */}
                          <div style={{ height: "6px", background: `linear-gradient(to right, ${tpl.palette.join(", ")})` }} />
                          <div style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", minWidth: 0 }}>
                                {isSystemTemplateCard && (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedSystemTemplateKey(isSelected ? "" : systemTemplateKey)}
                                    title={elakayAutoText("h_pb_select_template_hint", "Select this System Template so a Data Source's \"Lier\" button can assign it")}
                                    style={{ flexShrink: 0, marginTop: "2px", width: "20px", height: "20px", borderRadius: "6px", border: `1.5px solid ${isSelected ? "#6d28d9" : "#d8d2ee"}`, background: isSelected ? "#6d28d9" : "#fff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}
                                  >
                                    {isSelected && <Check size={13} />}
                                  </button>
                                )}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
                                    {tpl.palette.map((c, i) => (
                                      <span key={i} style={{ width: "12px", height: "12px", borderRadius: "50%", background: c, border: "1px solid rgba(0,0,0,0.10)", flexShrink: 0 }} />
                                    ))}
                                  </div>
                                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a2e" }}>{tpl.label}</div>
                                  <div style={{ fontSize: "11px", color: "#9b93b8", marginTop: "2px", lineHeight: 1.5 }}>{tpl.description}</div>
                                </div>
                              </div>
                              <button
                                onClick={() => loadTemplate(tpl)}
                                title={elakayAutoText("h_pb_load_template_hint", "Open this template in the Builder to explore/customize it")}
                                style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "8px 14px", fontSize: "12px", fontWeight: 700, border: "none", borderRadius: "9px", background: "linear-gradient(135deg,#6d28d9,#8b5cf6)", color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                <Sparkles size={12} />{elakayAutoText("h_8f8603009234f4", "Charger")}</button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Colonne Sources de données ── */}
                <div style={{ flex: "0 0 45%", padding: "20px 22px", overflowY: "auto" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#9b93b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Boxes size={12} />{elakayAutoText("h_f74e049e64dd22", "Sources de données (")}{modeDatasets.length})
                  </div>

                  {modeDatasets.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px 16px", color: "#b3aacb" }}>
                      <Boxes size={32} style={{ margin: "0 auto 10px", opacity: 0.4 }} />
                      <p style={{ fontSize: "13px", margin: 0 }}>{elakayAutoText("h_26b245fa6e4418", "Aucune source pour ce mode.")}</p>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {modeDatasets.map(([key, ds]) => {
                        const active = getBoundDatasetKey(boundData) === key;
                        const c = modeColors[ds.pageMode] || { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" };
                        // ONE canonical identity for EVERY action on this
                        // card (fix-400 mission's own point 8) -- Save, Edit,
                        // Link Template and the Page Template status below
                        // all resolve through this SAME function, never a
                        // different key each.
                        const canonicalKey = resolveCanonicalDataSourceKey(key);
                        const isRealPage = isRealContentPageDataSource(key);
                        // Data Source Save/Edit (task spec §1-2/§12): whether
                        // THIS canonical identity already has its own saved
                        // PageBuilder document, decided purely from the
                        // batch-loaded `overridesByCanonicalKey` map, never a
                        // per-card API call (task spec §11).
                        const savedDataSource = overridesByCanonicalKey[canonicalKey];
                        const dsBusy = dataSourceBusyKey === key;
                        const selectedTemplateLabel = selectedSystemTemplateKey
                          ? (CONTENT_TEMPLATES.find((t) => `contentTemplate.${t.key}` === selectedSystemTemplateKey)?.label || selectedSystemTemplateKey)
                          : "";
                        return (
                          <div
                            key={key}
                            style={{ border: `1px solid ${active ? c.border : "#ece9f5"}`, borderRadius: "12px", padding: "12px 14px", background: active ? c.bg : "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}
                          >
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px", flexWrap: "wrap" }}>
                                <span style={{ padding: "2px 8px", borderRadius: "50px", fontSize: "10px", fontWeight: 700, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                                  {key}
                                </span>
                                {active && <span style={{ fontSize: "10px", color: "#6d28d9", fontWeight: 700 }}>{elakayAutoText("h_ccecffc690a002", "● actif")}</span>}
                                {/* Status (task spec §13, optional but recommended) --
                                    only shown for Family, who are the only ones who can
                                    act on it anyway. */}
                                {isFamilyUser && (
                                  <span style={{ fontSize: "10px", fontWeight: 700, color: savedDataSource ? "#059669" : "#9ca3af" }}>
                                    {savedDataSource ? elakayAutoText("h_pb_ds_saved", "Saved") : elakayAutoText("h_pb_ds_not_saved", "Not saved")}
                                  </span>
                                )}
                                {/* Original/System HTML vs Customized HTML (task spec §27/§28):
                                    "Saved" alone only means the identity is registered -- Edit may
                                    have merely created a placeholder pointer seeded with a starter
                                    (linked template/Classic Content) that Family never actually
                                    saved yet (`hasCustomHtml`, server-computed from the REAL
                                    underlying document's own `htmlSource.isManuallyEdited`, never
                                    guessed from the pointer's mere existence). This must never read
                                    "Customized HTML" before a real Save happened inside the modal. */}
                                {isFamilyUser && savedDataSource && (
                                  <span style={{ fontSize: "10px", fontWeight: 600, color: savedDataSource.hasCustomHtml ? "#6d28d9" : "#9b93b8" }}>
                                    {savedDataSource.hasCustomHtml
                                      ? elakayAutoText("h_pb_ds_customized_html", "Customized HTML")
                                      : elakayAutoText("h_pb_ds_original_html", "Original/System HTML")}
                                  </span>
                                )}
                                {/* Real public page identity (fix-400 mission) -- shown
                                    only when it differs from the raw registry key, so
                                    Family can see this card genuinely affects
                                    content.aboutUs, never a look-alike internal key. */}
                                {isFamilyUser && isRealPage && canonicalKey !== key && (
                                  <span style={{ fontSize: "10px", color: "#6d28d9", fontFamily: "ui-monospace, monospace" }} title={elakayAutoText("h_4d72628df7f5a5", "This card acts on the real public Content Page")}>
                                    → {canonicalKey}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: "13px", fontWeight: "600", color: "#1a1a2e" }}>{ds.label}</div>
                              <div style={{ fontSize: "11px", color: "#9b93b8", marginTop: "2px" }}>
                                {Object.keys(ds.data || {}).join(" · ")}
                              </div>
                              {/* Current Page/System Template assignment (task spec §27) --
                                  only meaningful for a real public Content Page. */}
                              {isFamilyUser && isRealPage && savedDataSource?.selectedTemplateKey && (
                                <div style={{ fontSize: "11px", color: "#4a4a68", marginTop: "4px" }}>
                                  {elakayAutoText("h_pb_ds_template_label", "Page Template")}: <strong>{savedDataSource.selectedTemplateKey.replace(/^(contentTemplate|contentPageTemplate)\./, "")}</strong>
                                  {" · "}
                                  {elakayAutoText("h_pb_ds_template_active_label", "Template Active")}: <strong style={{ color: savedDataSource.active ? "#059669" : "#b45309" }}>{savedDataSource.active ? "ON" : "OFF"}</strong>
                                </div>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                              <button
                                onClick={() => loadDataset(key, ds)}
                                style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", fontSize: "12px", fontWeight: 700, border: `1px solid ${active ? c.border : "#ece9f5"}`, borderRadius: "9px", background: active ? c.bg : "#fff", color: active ? c.text : "#6d28d9", cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                {active ? <Check size={12} /> : <Boxes size={12} />} {active ? elakayAutoText("h_2eb75f8458bd81", "Actif") : elakayAutoText("h_16a4aafba1b2c6", "Lier")}
                              </button>
                              {/* Save / Edit (task spec §1-2/§14: a separate action from
                                  Lier -- persists/reopens this Data Source's OWN saved
                                  HTML document, one document ever). Family-only, both
                                  here (hidden for anyone else) and server-side
                                  (upsertOverride's assertFamily -- task spec §16). */}
                              {isFamilyUser && (
                                savedDataSource ? (
                                  <button
                                    type="button"
                                    disabled={dsBusy}
                                    onClick={() => handleEditDataSource(key, ds)}
                                    title={elakayAutoText("h_pb_ds_edit_hint", "Open this Data Source's own saved document -- never creates a new one")}
                                    style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", fontSize: "12px", fontWeight: 700, border: "1px solid #ece9f5", borderRadius: "9px", background: "#fff", color: "#4a4a68", cursor: "pointer", whiteSpace: "nowrap" }}
                                  >
                                    <PenLine size={12} />{elakayAutoText("h_pb_ds_edit", "Edit")}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={dsBusy}
                                    onClick={() => handleSaveDataSource(key, ds)}
                                    title={elakayAutoText("h_pb_ds_save_hint", "Save this Data Source's own document (the page currently open in the editor)")}
                                    style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", fontSize: "12px", fontWeight: 700, border: "1px solid #ece9f5", borderRadius: "9px", background: "#fff", color: "#4a4a68", cursor: "pointer", whiteSpace: "nowrap" }}
                                  >
                                    <Save size={12} />{elakayAutoText("h_pb_ds_save", "Save")}
                                  </button>
                                )
                              )}
                              {/* Link Template (task spec §6/§10/§11/§28) -- assigns the
                                  CHECKED System Template (left column) to THIS real Content
                                  Page and auto-activates it, all in one click. Deliberately a
                                  DIFFERENT, clearly-labeled button from the pre-existing
                                  "Lier" above (that one only binds sample data for JSON
                                  preview inside the editor -- a completely different,
                                  unrelated action, never merged with this one). Disabled with
                                  a tooltip when nothing is selected yet (task spec §28's
                                  preferred UX) rather than hidden -- Family always sees the
                                  option exists. */}
                              {isFamilyUser && isRealPage && (
                                <button
                                  type="button"
                                  disabled={dsBusy || !selectedSystemTemplateKey}
                                  onClick={() => handleLinkSelectedSystemTemplate(key, ds)}
                                  title={selectedSystemTemplateKey
                                    ? elakayAutoText("h_pb_ds_link_template_hint", "Assign the selected System Template to this Content Page and activate it immediately")
                                    : elakayAutoText("h_pb_ds_link_template_none_hint", "Select a template first (checkbox on a Templates card, left)")}
                                  style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", fontSize: "12px", fontWeight: 700, border: "1px solid #ece9f5", borderRadius: "9px", background: selectedSystemTemplateKey ? "linear-gradient(135deg,#6d28d9,#8b5cf6)" : "#f3f4f6", color: selectedSystemTemplateKey ? "#fff" : "#9ca3af", cursor: selectedSystemTemplateKey ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}
                                >
                                  <LayoutGrid size={12} />
                                  {selectedSystemTemplateKey
                                    ? `${elakayAutoText("h_pb_ds_link_template", "Link Template")}: ${selectedTemplateLabel}`
                                    : elakayAutoText("h_pb_ds_link_template", "Link Template")}
                                </button>
                              )}
                              {/* Use as Priority (task spec §8/§9/§10/§11/§32) -- ONLY this
                                  toggle decides whether THIS Data Source's OWN saved HTML wins
                                  over an also-linked template (see resolveContentPageTemplate's
                                  STEP 1). Deliberately never auto-set by Save (task spec §10)
                                  -- shown only once custom HTML actually exists, exactly like
                                  Delete HTML below; always directly clickable (task spec §32),
                                  never blocked by anything else. */}
                              {isFamilyUser && savedDataSource?.hasCustomHtml && (
                                <button
                                  type="button"
                                  disabled={dsBusy}
                                  onClick={() => handleToggleDataSourcePriority(key)}
                                  title={elakayAutoText("h_pb_ds_priority_hint", "Whether THIS Data Source's own customized HTML is used in the real public page, instead of a linked template or the original")}
                                  style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", fontSize: "12px", fontWeight: 700, border: "1px solid #ece9f5", borderRadius: "9px", background: "#fff", color: savedDataSource.priority ? "#b45309" : "#059669", cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                  {savedDataSource.priority ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                                  {elakayAutoText("h_pb_ds_priority_label", "Use as Priority")} {savedDataSource.priority ? elakayAutoText("h_a89ae8a41cc61f", "● ON") : elakayAutoText("h_a885196f2fa2f2", "○ OFF")}
                                </button>
                              )}
                              {/* Delete HTML (task spec §13-21/§31): "Delete HTML" -- never
                                  just "Delete" -- to make explicit that only the customized
                                  HTML pointer is removed, never the real Content Page/its
                                  data/the linked System Template/the template registry (task
                                  spec §14/§16). Only shown once real custom HTML actually
                                  exists (savedDataSource.hasCustomHtml, NOT just a pointer -- task
                                  spec §27/§28) -- a bare template assignment/placeholder with no
                                  HTML actually saved yet has nothing to delete. */}
                              {isFamilyUser && savedDataSource?.hasCustomHtml && (
                                <button
                                  type="button"
                                  disabled={dsBusy}
                                  onClick={() => handleDeleteDataSourceHtml(key, ds)}
                                  title={elakayAutoText("h_pb_ds_delete_html_hint", "Removes only this Data Source's own customized HTML -- the real Content Page, its data, and any linked System Template are never affected")}
                                  style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "5px", padding: "7px 12px", fontSize: "12px", fontWeight: 700, border: "1px solid #fecaca", borderRadius: "9px", background: "#fff", color: "#dc2626", cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                  <Trash2 size={12} />{elakayAutoText("h_pb_ds_delete_html", "Delete HTML")}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {isFamilyUser && dataSourceError && (
                    <div style={{ marginTop: "12px", padding: "10px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", color: "#b91c1c", fontSize: "12px" }}>
                      ⚠ {dataSourceError}
                    </div>
                  )}

                  <div style={{ marginTop: "20px", padding: "12px", background: "#f9f7ff", borderRadius: "10px", border: "1px solid #ece9f5" }}>
                    <p style={{ fontSize: "11px", color: "#9b93b8", margin: 0, lineHeight: 1.6 }}>{elakayAutoText("h_3d7c0951e10132", "Changer de")}<strong style={{ color: "#6d28d9" }}>{elakayAutoText("h_b47bc6d70304bc", "mode de page")}</strong>{elakayAutoText("h_fb0ced123f7a04", "via les onglets ci-dessus pour voir les templates et données disponibles pour chaque mode.")}</p>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </ViewportModal>
        );
      })()}

      {showHtmlEditModal && savedPage?._id && (
        <PageHtmlSourceModal
          show={showHtmlEditModal}
          onHide={() => setShowHtmlEditModal(false)}
          pageId={savedPage._id}
          pageTitle={savedPage.title}
          jsonPages={pages}
          initialJsonPageId={currentPageId}
          htmlSourcePages={savedPage.htmlSourcePages}
          legacyHtmlSource={savedPage.htmlSource}
          getGeneratedHtml={getGeneratedHtmlForPage}
          onCreatePage={addPage}
          onSaveDocument={saveToDb}
          onApplied={({ pageId: appliedPageId, entry }) => {
            if (!appliedPageId) return;
            const nextHtmlSourcePages = { ...(savedPage?.htmlSourcePages || {}) };
            if (entry) nextHtmlSourcePages[appliedPageId] = entry;
            else delete nextHtmlSourcePages[appliedPageId];
            onPageMetaUpdated?.({
              ...savedPage,
              htmlSourcePages: nextHtmlSourcePages,
            });
          }}
          onSaved={(updatedPage) => {
            onPageMetaUpdated?.(updatedPage);
            // The advanced modal and the inline sidebar panel edit the SAME
            // per-page htmlSourcePages -- if the inline panel is currently
            // unlocked and showing this same page, force it to re-fetch
            // rather than keep displaying what's now a stale draft.
            if (htmlPanelUnlocked) setHtmlPanelLoadedPageId(null);
            // Data Source card sync (Classic Content preload mission §7): a
            // successful Save inside Edit HTML Code must flip "Customized
            // HTML"/enable Use as Priority+Delete HTML immediately, with no
            // reload -- find which canonical Data Source (if any) this
            // pagebuilders _id belongs to and mark it customized locally.
            const savedId = String(updatedPage?._id || savedPage?._id || "");
            if (savedId) {
              setOverridesByCanonicalKey((prev) => {
                const entry = Object.entries(prev).find(([, o]) => String(o?.pagebuilderDoc || "") === savedId);
                if (!entry) return prev;
                const [key, o] = entry;
                return { ...prev, [key]: { ...o, hasCustomHtml: true } };
              });
            }
          }}
        />
      )}

      {showFamilyUnlockModal && (
        <ViewportModal>
          <div
            onClick={(e) => { if (e.target === e.currentTarget) closeFamilyUnlockModal(); }}
            style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center",
              justifyContent:"center", overflowY:"auto", padding:"18px", background:"rgba(15,10,40,0.58)" }}
          >
            <form
              onSubmit={handleFamilyUnlockSubmit}
              style={{ width:"100%", maxWidth:"460px", maxHeight:"calc(100vh - 36px)", background:"#fff",
                borderRadius:"18px", overflowY:"auto", boxShadow:"0 24px 80px rgba(15,10,40,0.28)" }}
            >
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", background:"linear-gradient(135deg,#92400e,#d97706)" }}>
              <div>
                <div style={{ color:"#fff", fontSize:"15px", fontWeight:800 }}>{elakayAutoText("h_9f8a2811f519ca", "Validation Développeur requise")}</div>
                <div style={{ color:"rgba(255,255,255,0.78)", fontSize:"11px", marginTop:"2px" }}>{elakayAutoText("h_8b3f76c2d94a01", "Débloquer cette")}{shopSelectionLabel}{elakayAutoText("h_1a5c908e73fd26", "pour pouvoir la modifier directement.")}</div>
              </div>
              <button
                type="button"
                onClick={closeFamilyUnlockModal}
                disabled={familyUnlockBusy}
                style={{ border:"none", background:"transparent", color:"#fff", fontSize:"22px", lineHeight:1, cursor:familyUnlockBusy?"not-allowed":"pointer" }}
              >
                ×
              </button>
            </div>

            <div style={{ padding:"20px", display:"flex", flexDirection:"column", gap:"12px" }}>
              <div style={{ padding:"10px 12px", borderRadius:"10px", background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e", fontSize:"12px", lineHeight:1.55 }}>{elakayAutoText("h_c73b1e0f5a8d94", "Après validation, cette page devient modifiable en place (aucune copie n'est créée) tant qu'elle reste ouverte. Le mot de passe est re-vérifié à chaque sauvegarde.")}</div>

              <div>
                <label htmlFor="pb-family-unlock-password" style={{ display:"block", fontSize:"11px", fontWeight:800, color:"#7a73a0", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"5px" }}>{elakayAutoText("h_d6d1d7e5ff8b0f", "Mot de passe du compte Développeur")}</label>
                <div style={{ position:"relative" }}>
                  <input
                    id="pb-family-unlock-password"
                    type={showFamilyPassword ? "text" : "password"}
                    value={familyPassword}
                    onChange={(e) => setFamilyPassword(e.target.value)}
                    autoFocus
                    autoComplete="current-password"
                    placeholder={elakayAutoText("h_e5e7a7c0504d7e", "Entrez le mot de passe Développeur")}
                    aria-invalid={familyUnlockError ? true : undefined}
                    aria-describedby={familyUnlockError ? "pb-family-unlock-error" : undefined}
                    style={{ width:"100%", padding:"10px 38px 10px 12px", fontSize:"13px", border:"1px solid #e4dcfb", borderRadius:"9px", outline:"none", boxSizing:"border-box", background:"#faf9fd" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowFamilyPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showFamilyPassword ? elakayAutoText("h_pg_pw_hide", "Masquer le mot de passe") : elakayAutoText("h_pg_pw_show", "Afficher le mot de passe")}
                    style={{ position:"absolute", top:"50%", right:"8px", transform:"translateY(-50%)", border:"none", background:"transparent", color:"#9b93b8", cursor:"pointer", display:"flex", alignItems:"center", padding:"4px" }}
                  >
                    {showFamilyPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {familyUnlockError && (
                <div id="pb-family-unlock-error" role="alert" style={{ padding:"9px 11px", borderRadius:"9px", background:"#fef2f2", border:"1px solid #fca5a5", color:"#b91c1c", fontSize:"12px" }}>
                  {familyUnlockError}
                </div>
              )}

              <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end", marginTop:"4px" }}>
                <button
                  type="button"
                  onClick={closeFamilyUnlockModal}
                  disabled={familyUnlockBusy}
                  style={{ padding:"9px 14px", fontSize:"12px", fontWeight:700, borderRadius:"9px", border:"1px solid #e5e7eb", background:"#fff", color:"#64748b", cursor:familyUnlockBusy?"not-allowed":"pointer" }}
                >{elakayAutoText("h_49ba3292420c74", "Annuler")}</button>
                <button
                  type="submit"
                  disabled={familyUnlockBusy}
                  style={{ display:"flex", alignItems:"center", gap:"6px", padding:"9px 15px", fontSize:"12px", fontWeight:800, borderRadius:"9px", border:"none", background:familyUnlockBusy?"#9ca3af":"linear-gradient(135deg,#d97706,#f59e0b)", color:"#fff", cursor:familyUnlockBusy?"not-allowed":"pointer" }}
                >
                  {familyUnlockBusy
                    ? elakayAutoText("h_938b4aa3e718b5", "Vérification...")
                    : <><Unlock size={13} />{elakayAutoText("h_pg_unlock_edit", "Déverrouiller l'édition")}</>}
                </button>
              </div>
            </div>
            </form>
          </div>
        </ViewportModal>
      )}

      {/* ── Overlay Guide Page Builder ── */}
      {showHelp && (
        <div style={{ position:"absolute", inset:0, zIndex:200, background:"#f6f4fb", display:"flex", flexDirection:"column" }}>
          <PageBuilderHelp
            mode={storeOnly ? "boutique" : undefined}
            onClose={() => setShowHelp(false)}
          />
        </div>
      )}

      {/* ── Modal panier boutique ── */}
      {showShopCart && (
        <ViewportModal>
          <div
            style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center",
              justifyContent:"center", overflowY:"auto", padding:"18px", background:"rgba(15,10,40,0.55)" }}
            onClick={() => setShowShopCart(false)}
          >
            <div
              style={{ width:"min(560px,100%)", maxHeight:"calc(100vh - 36px)", overflow:"hidden", background:"#fff",
                borderRadius:"20px", boxShadow:"0 24px 70px rgba(15,10,40,0.28)", display:"flex", flexDirection:"column" }}
              onClick={(event) => event.stopPropagation()}
            >
            <div style={{ display:"flex", alignItems:"center", gap:"10px", padding:"17px 20px",
              background:"linear-gradient(135deg,#5b21b6,#8b5cf6)", color:"#fff" }}>
              <div style={{ position:"relative", width:"36px", height:"36px", display:"grid", placeItems:"center",
                borderRadius:"10px", background:"rgba(255,255,255,0.16)" }}>
                <ShoppingCart size={19} />
                {shopCart.length > 0 && (
                  <span style={{ position:"absolute", top:"-5px", right:"-5px", minWidth:"17px", height:"17px",
                    display:"grid", placeItems:"center", padding:"0 4px", borderRadius:"20px",
                    background:"#e11d48", color:"#fff", border:"2px solid #6d28d9", fontSize:"8px", fontWeight:900 }}>
                    {shopCart.length}
                  </span>
                )}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"16px", fontWeight:900 }}>{elakayAutoText("h_8abcae22f1e200", "Panier boutique")}</div>
                <div style={{ marginTop:"2px", fontSize:"10px", color:"rgba(255,255,255,0.75)" }}>{elakayAutoText("h_da30250828129d", "Créations, boutiques Par défaut et Premium")}</div>
              </div>
              <button onClick={() => setShowShopCart(false)} aria-label={elakayAutoText("h_39f06d83c54f29", "Fermer le panier")}
                style={{ width:"32px", height:"32px", display:"grid", placeItems:"center", border:"none",
                  borderRadius:"9px", color:"#fff", background:"rgba(255,255,255,0.14)", cursor:"pointer", fontSize:"19px" }}>
                ×
              </button>
            </div>

            <div style={{ padding:"14px 16px", overflowY:"auto" }}>
              {shopCart.length === 0 ? (
                <div style={{ padding:"36px 12px", textAlign:"center", color:"#9b93b8" }}>
                  <ShoppingCart size={30} style={{ marginBottom:"8px", opacity:0.55 }} />
                  <div style={{ fontSize:"13px", fontWeight:700 }}>{elakayAutoText("h_093b77238cd9f1", "Le panier boutique est vide.")}</div>
                  <div style={{ marginTop:"4px", fontSize:"11px" }}>{elakayAutoText("h_7b067730b43bd2", "Sélectionnez une boutique puis cliquez sur « Ajouter ».")}</div>
                </div>
              ) : shopCart.map((item) => (
                <div key={getShopCartKey(item)}
                  style={{ display:"flex", alignItems:"center", gap:"10px", padding:"10px",
                    marginBottom:"8px", border:"1px solid #ece9f5", borderRadius:"12px", background:"#faf9fd" }}>
                  <div style={{ width:"44px", height:"44px", flexShrink:0, borderRadius:"9px", overflow:"hidden",
                    display:"grid", placeItems:"center", background:
                      item.source === "premium" ? "#fef3c7" : item.source === "created" ? "#ede9fe" : "#d1fae5" }}>
                    {item.thumbnail
                      ? <img src={item.thumbnail} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                      : <Package size={19} color={item.source === "premium" ? "#b45309" : item.source === "created" ? "#6d28d9" : "#059669"} />}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:"9px", fontWeight:800, color:"#9b93b8", textTransform:"uppercase" }}>
                      {SHOP_SOURCE_LABELS[item.source] || elakayAutoText("h_f65c7563452437", "boutique")}
                    </div>
                    <div style={{ marginTop:"2px", fontSize:"12px", fontWeight:800, color:"#1a1a2e",
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {item.title}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowShopCart(false);
                      openShopPurchaseModal(item);
                    }}
                    style={{ display:"flex", alignItems:"center", gap:"4px", padding:"7px 10px",
                      border:"none", borderRadius:"8px", color:"#fff",
                      background:"linear-gradient(135deg,#d97706,#f59e0b)", cursor:"pointer",
                      fontSize:"10px", fontWeight:800, whiteSpace:"nowrap" }}
                  >
                    <CreditCard size={12} />{elakayAutoText("h_53fdf200df83d3", "Acheter")}</button>
                  <button onClick={() => removeShopFromCart(item.id)} title={elakayAutoText("h_2d18d5fb0affee", "Retirer du panier")}
                    aria-label={elakayAutoText("h_f626d89c376a8a", "Retirer {value0} du panier", { value0: item.title })}
                    style={{ width:"29px", height:"29px", display:"grid", placeItems:"center",
                      color:"#e11d48", background:"#fff1f2", border:"1px solid #fda4af",
                      borderRadius:"8px", cursor:"pointer", fontSize:"17px" }}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            </div>
          </div>
        </ViewportModal>
      )}

      {/* ── Modal achat boutique ── */}
      {showBoutiqueActions && (
        <ViewportModal>
          <div
            style={{ position:"fixed", inset:0, zIndex:13000, display:"flex", alignItems:"center",
              justifyContent:"center", overflowY:"auto", padding:"18px", background:"rgba(15,10,40,0.58)" }}
            onClick={() => { if (!boutiqueActionState.loading) setShowBoutiqueActions(false); }}
          >
            <div
              style={{ background:"#fff", borderRadius:"20px", padding:"24px", maxWidth:"620px", width:"100%",
                boxShadow:"0 24px 70px rgba(15,10,40,0.28)", position:"relative",
                maxHeight:"calc(100vh - 36px)", overflowY:"auto" }}
              onClick={(event) => event.stopPropagation()}
            >
            <button onClick={() => setShowBoutiqueActions(false)} disabled={boutiqueActionState.loading}
              aria-label={elakayAutoText("h_5e3b97a0c63571", "Fermer le modal d'achat")}
              style={{ position:"absolute", top:"12px", right:"14px", width:"30px", height:"30px",
                display:"grid", placeItems:"center", background:"#f5f3ff", border:"1px solid #e4dcfb",
                borderRadius:"8px", cursor:"pointer", color:"#7a73a0", fontSize:"17px" }}>
              ×
            </button>

            <h2 style={{ margin:"0 36px 4px 0", fontSize:"18px", fontWeight:900, color:"#1a1a2e" }}>{elakayAutoText("h_3192cd3ca6e58f", "Acheter une boutique")}</h2>
            <p style={{ margin:"0 0 14px", fontSize:"11px", color:"#6b7280", lineHeight:1.5 }}>{elakayAutoText("h_cd034a1cc601d7", "Choisissez la boutique sélectionnée ou une boutique du panier, puis choisissez ce que vous souhaitez acheter.")}</p>

            {purchaseShopChoices.length > 0 ? (
              <>
                <div style={{ display:"flex", gap:"7px", overflowX:"auto", padding:"2px 1px 10px", marginBottom:"3px" }}>
                  {purchaseShopChoices.map((item) => {
                    const active = getShopCartKey(item) === getShopCartKey(shopPurchaseTarget);
                    return (
                      <button key={getShopCartKey(item)}
                        onClick={() => {
                          setShopPurchaseTargetKey(getShopCartKey(item));
                          setBoutiqueActionState({ loading:false, error:null, success:null });
                          setShopPurchaseState({ loadingId:null, error:null, success:null });
                        }}
                        style={{ minWidth:"135px", maxWidth:"180px", padding:"8px 10px", textAlign:"left",
                          borderRadius:"10px", border:`1px solid ${active ? "#8b5cf6" : "#e4dcfb"}`,
                          background:active ? "#f5f3ff" : "#fff", cursor:"pointer",
                          boxShadow:active ? "0 0 0 2px rgba(139,92,246,.12)" : "none" }}
                      >
                        <span style={{ display:"block", fontSize:"8px", fontWeight:900,
                          color:active ? "#6d28d9" : "#9b93b8", textTransform:"uppercase" }}>
                          {SHOP_SOURCE_LABELS[item.source] || elakayAutoText("h_f65c7563452437", "boutique")}
                        </span>
                        <span style={{ display:"block", marginTop:"2px", fontSize:"11px", fontWeight:800,
                          color:"#1a1a2e", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {item.title}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ display:"flex", alignItems:"center", gap:"10px", padding:"10px 12px",
                  border:"1px solid #e4dcfb", borderRadius:"11px", background:"#faf9fd", marginBottom:"12px" }}>
                  <div style={{ width:"38px", height:"38px", flexShrink:0, display:"grid", placeItems:"center",
                    borderRadius:"9px", background:
                      shopPurchaseTarget?.source === "premium" ? "#fef3c7" :
                      shopPurchaseTarget?.source === "created" ? "#ede9fe" : "#d1fae5" }}>
                    <Package size={18} color={
                      shopPurchaseTarget?.source === "premium" ? "#b45309" :
                      shopPurchaseTarget?.source === "created" ? "#6d28d9" : "#059669"
                    } />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:"12px", fontWeight:900, color:"#1a1a2e",
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {shopPurchaseTarget?.title}
                    </div>
                    <div style={{ marginTop:"2px", fontSize:"10px", color:"#7a73a0" }}>
                      {shopPurchaseTarget?.source === "created"
                        ? elakayAutoText("h_965bf34e8e49da", "Création : achat du code HTML uniquement")
                        : shopPurchaseHasDisplay && shopPurchaseHasHtml
                          ? elakayAutoText("h_c46e8b82880d89", "Tous les droits ont déjà été achetés")
                          : shopPurchaseHasDisplay
                            ? elakayAutoText("h_fff30c4191ae69", "Affichage acheté : le code HTML reste disponible")
                            : shopPurchaseHasHtml
                              ? elakayAutoText("h_8f2ceb1269ba3f", "Code HTML acheté : l'affichage reste disponible")
                              : elakayAutoText("h_f6520db220b177", "Boutique complète : affichage, code HTML ou les deux")}
                    </div>
                  </div>
                </div>

                {(boutiqueActionState.success || boutiqueActionState.error || shopPurchaseState.success || shopPurchaseState.error) && (
                  <div style={{ padding:"9px 11px", marginBottom:"10px", borderRadius:"9px", fontSize:"11px", fontWeight:700,
                    color:(boutiqueActionState.error || shopPurchaseState.error) ? "#b91c1c" : "#15803d",
                    background:(boutiqueActionState.error || shopPurchaseState.error) ? "#fef2f2" : "#f0fdf4",
                    border:`1px solid ${(boutiqueActionState.error || shopPurchaseState.error) ? "#fecaca" : "#bbf7d0"}` }}>
                    {boutiqueActionState.error || shopPurchaseState.error || boutiqueActionState.success || shopPurchaseState.success}
                  </div>
                )}

                <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                  {shopPurchaseTarget?.source === "created" ? (
                    <div style={{ display:"flex", alignItems:"center", gap:"11px", padding:"11px 13px",
                      border:"1px solid #bfdbfe", borderRadius:"11px", background:"#eff6ff" }}>
                      <FileCode size={19} color="#1d4ed8" />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:"12px", fontWeight:800, color:"#1a1a2e" }}>{elakayAutoText("h_75595f68914733", "Code HTML de ma création")}</div>
                        <div style={{ fontSize:"10px", color:"#6b7280" }}>{elakayAutoText("h_1abdd9c010aa57", "Acheter et télécharger le fichier HTML complet.")}</div>
                      </div>
                      <button onClick={() => handleBoutiqueAction("download-own", shopPurchaseTarget)}
                        disabled={boutiqueActionState.loading || shopPurchaseHasHtml}
                        style={{ padding:"7px 12px", border:"none", borderRadius:"8px", color:"#fff",
                          background:shopPurchaseHasHtml ? "#94a3b8" : "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                          cursor:(boutiqueActionState.loading || shopPurchaseHasHtml)?"not-allowed":"pointer", fontSize:"11px", fontWeight:800 }}>
                        {boutiqueActionState.loading ? "..." : shopPurchaseHasHtml ? elakayAutoText("h_974a605b767ef2", "Acheté") : `$${shopPricing.downloadOwnPrice}`}
                      </button>
                    </div>
                  ) : (
                    <>
                      {isMongoObjectId(shopPurchaseTarget?.id) && (
                        <div style={{ display:"flex", alignItems:"center", gap:"11px", padding:"11px 13px",
                          border:"1px solid #fde68a", borderRadius:"11px", background:"#fffbeb" }}>
                          <Package size={19} color="#b45309" />
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:"12px", fontWeight:800, color:"#1a1a2e" }}>{elakayAutoText("h_62af47de0f7ac0", "Boutique dans Mes achats")}</div>
                            <div style={{ fontSize:"10px", color:"#6b7280" }}>{elakayAutoText("h_b3582f9a860787", "Créer une copie de cette boutique dans votre compte.")}</div>
                          </div>
                          <button onClick={() => purchaseSelectedShop(shopPurchaseTarget)}
                            disabled={shopPurchaseState.loadingId === shopPurchaseTarget.id || boutiqueActionState.loading}
                            style={{ padding:"7px 12px", border:"none", borderRadius:"8px", color:"#fff",
                              background:"linear-gradient(135deg,#d97706,#f59e0b)", cursor:"pointer",
                              fontSize:"11px", fontWeight:800, whiteSpace:"nowrap" }}>
                            {shopPurchaseState.loadingId === shopPurchaseTarget.id
                              ? "..."
                              : elakayAutoText("h_8bf36646d7bbe2", "Acheter une copie")}
                          </button>
                        </div>
                      )}

                      <div style={{ display:"flex", alignItems:"center", gap:"11px", padding:"11px 13px",
                        border:"1px solid #a7f3d0", borderRadius:"11px", background:"#f0fdf4" }}>
                        <Eye size={19} color="#059669" />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:"12px", fontWeight:800, color:"#1a1a2e" }}>{elakayAutoText("h_d7faa6c90e42b9", "Afficher dans Elakay")}</div>
                          <div style={{ fontSize:"10px", color:"#6b7280" }}>{elakayAutoText("h_ecfb749e2d5bc9", "Utiliser cette boutique comme boutique affichée.")}</div>
                        </div>
                        <button onClick={() => handleBoutiqueAction("display-only", shopPurchaseTarget)}
                          disabled={boutiqueActionState.loading || shopPurchaseHasDisplay}
                          style={{ padding:"7px 12px", border:"none", borderRadius:"8px", color:"#fff",
                            background:shopPurchaseHasDisplay ? "#94a3b8" : "linear-gradient(135deg,#059669,#10b981)",
                            cursor:(boutiqueActionState.loading || shopPurchaseHasDisplay)?"not-allowed":"pointer", fontSize:"11px", fontWeight:800 }}>
                          {boutiqueActionState.loading ? "..." : shopPurchaseHasDisplay ? elakayAutoText("h_974a605b767ef2", "Acheté") : `$${shopPricing.displayPrice}`}
                        </button>
                      </div>

                      <div style={{ display:"flex", alignItems:"center", gap:"11px", padding:"11px 13px",
                        border:"1px solid #ddd6fe", borderRadius:"11px", background:"#f5f3ff" }}>
                        <FileCode size={19} color="#6d28d9" />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:"12px", fontWeight:800, color:"#1a1a2e" }}>{elakayAutoText("h_121344756746b3", "Code HTML")}</div>
                          <div style={{ fontSize:"10px", color:"#6b7280" }}>{elakayAutoText("h_de9bb3e6571a40", "Acheter et télécharger le code HTML complet.")}</div>
                        </div>
                        <button onClick={() => handleBoutiqueAction("download-other", shopPurchaseTarget)}
                          disabled={boutiqueActionState.loading || shopPurchaseHasHtml}
                          style={{ padding:"7px 12px", border:"none", borderRadius:"8px", color:"#fff",
                            background:shopPurchaseHasHtml ? "#94a3b8" : "linear-gradient(135deg,#6d28d9,#8b5cf6)",
                            cursor:(boutiqueActionState.loading || shopPurchaseHasHtml)?"not-allowed":"pointer", fontSize:"11px", fontWeight:800 }}>
                          {boutiqueActionState.loading ? "..." : shopPurchaseHasHtml ? elakayAutoText("h_974a605b767ef2", "Acheté") : `$${shopPricing.downloadPrice}`}
                        </button>
                      </div>

                      <div style={{ position:"relative", display:"flex", alignItems:"center", gap:"11px",
                        padding:"13px", marginTop:"2px", border:`2px solid ${shopPurchaseHasAnyRight ? "#cbd5e1" : "#fbbf24"}`, borderRadius:"11px", background:shopPurchaseHasAnyRight ? "#f8fafc" : "#fffbeb", opacity:shopPurchaseHasAnyRight ? 0.72 : 1 }}>
                        <span style={{ position:"absolute", top:"-8px", left:"12px", padding:"2px 7px",
                          borderRadius:"20px", background:shopPurchaseHasAnyRight ? "#64748b" : "#d97706", color:"#fff", fontSize:"8px", fontWeight:900 }}>
                          {shopPurchaseHasAnyRight ? elakayAutoText("h_d96a1674422860", "INDISPONIBLE") : elakayAutoText("h_ef504a2ce78444", "TOUT ACHETER")}
                        </span>
                        <CheckSquare size={19} color="#d97706" />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:"12px", fontWeight:800, color:"#1a1a2e" }}>{elakayAutoText("h_6d2c2181d143ec", "Afficher + code HTML")}</div>
                          <div style={{ fontSize:"10px", color:"#6b7280" }}>
                            {shopPurchaseHasAnyRight
                              ? elakayAutoText("h_4b5b008b9b4f12", "Désactivé dès qu'un droit individuel est déjà acheté.")
                              : elakayAutoText("h_c6b3732d974e7f", "Acheter les deux droits en une seule fois.")}
                          </div>
                        </div>
                        <button onClick={() => handleBoutiqueAction("both", shopPurchaseTarget)}
                          disabled={boutiqueActionState.loading || shopPurchaseHasAnyRight}
                          style={{ padding:"7px 12px", border:"none", borderRadius:"8px", color:"#fff",
                            background:shopPurchaseHasAnyRight ? "#94a3b8" : "linear-gradient(135deg,#d97706,#f59e0b)",
                            cursor:(boutiqueActionState.loading || shopPurchaseHasAnyRight)?"not-allowed":"pointer", fontSize:"11px", fontWeight:800 }}>
                          {boutiqueActionState.loading ? "..." : shopPurchaseHasAnyRight ? elakayAutoText("h_2d32fa951d2dc3", "Désactivé") : `$${shopPricing.bothPrice}`}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ marginTop:"13px", padding:"8px 11px", background:"#f9fafb", border:"1px solid #e5e7eb",
                  borderRadius:"9px", fontSize:"10px", color:"#6b7280", lineHeight:1.45 }}>{elakayAutoText("h_e700f14e3c6e87", "Paiement effectué avec la carte enregistrée dans votre portefeuille Elakay.")}</div>
              </>
            ) : (
              <div style={{ padding:"30px 12px", textAlign:"center", color:"#9b93b8" }}>
                <Package size={30} style={{ marginBottom:"8px", opacity:0.55 }} />
                <div style={{ fontSize:"12px", fontWeight:800 }}>{elakayAutoText("h_b5e0f335256cbf", "Aucune boutique disponible pour l'achat.")}</div>
                <div style={{ marginTop:"4px", fontSize:"10px" }}>{elakayAutoText("h_cb6cbaea126691", "Sélectionnez une boutique ou ajoutez-en une au panier.")}</div>
              </div>
            )}
            </div>
          </div>
        </ViewportModal>
      )}

    </div>
  );
}

function GroupLabel({ children }) {
  return <div style={{ fontSize: "10px", fontWeight: 700, color: "#9b93b8", textTransform: "uppercase", letterSpacing: "0.06em", margin: "12px 4px 6px" }}>{children}</div>;
}
function CollapsibleGroup({ open, onToggle, label, icon: Icon, count, note, children }) {
  return (
    <div style={{ border:"1px solid #ece9f5", borderRadius:"10px", overflow:"hidden", marginBottom:"6px" }}>
      <button
        onClick={onToggle}
        style={{ width:"100%", display:"flex", alignItems:"center", gap:"6px", padding:"8px 10px", background:open?"#f5f3ff":"#fff", border:"none", cursor:"pointer", textAlign:"left", transition:"background 0.15s" }}>
        {Icon && <Icon size={12} color={open?"#6d28d9":"#9b93b8"} />}
        <span style={{ flex:1, fontSize:"11px", fontWeight:700, color:open?"#6d28d9":"#4a4a68", userSelect:"none" }}>{label}</span>
        {count != null && <span style={{ fontSize:"9px", color:"#b3aacb", background:"#f3effe", padding:"1px 6px", borderRadius:"99px", flexShrink:0 }}>{count}</span>}
        {open ? <ChevronUp size={11} color="#9b93b8"/> : <ChevronDown size={11} color="#9b93b8"/>}
      </button>
      {open && (
        <div style={{ padding:"8px 8px 10px", borderTop:"1px solid #f0edf8" }}>
          {note && <p style={{ fontSize:"10px", color:"#b3aacb", margin:"0 0 7px", lineHeight:1.4 }}>{note}</p>}
          {children}
        </div>
      )}
    </div>
  );
}
function CardGridButtons({ entries, onClick, cardHeight, activeKeys, directActiveKeys }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
      {entries.map(([key, def]) => {
        const isDirectActive = directActiveKeys?.has(key);
        const isNestedActive = !isDirectActive && activeKeys?.has(key);
        const activeColor = isDirectActive ? "#6d28d9" : isNestedActive ? "#0284c7" : "#ece9f5";
        return (
          <button
            key={key}
            onClick={() => onClick(key)}
            data-element-active={isDirectActive ? "selected" : isNestedActive ? "group-child" : undefined}
            title={isDirectActive ? `${def.label} — élément sélectionné` : isNestedActive ? `${def.label} — présent dans le groupe sélectionné` : def.label}
            style={{
              ...addCard,
              position: "relative",
              border: `${isDirectActive || isNestedActive ? 2 : 1}px solid ${activeColor}`,
              background: isDirectActive ? "#f5f3ff" : isNestedActive ? "#f0f9ff" : addCard.background,
              color: isDirectActive ? "#5b21b6" : isNestedActive ? "#0369a1" : addCard.color,
              boxShadow: isDirectActive
                ? "0 0 0 2px rgba(109,40,217,.12)"
                : isNestedActive
                  ? "0 0 0 2px rgba(2,132,199,.10)"
                  : "none",
              ...(cardHeight ? { height: cardHeight, overflow: "hidden" } : {}),
            }}
          >
            {(isDirectActive || isNestedActive) && (
              <span style={{
                position: "absolute", top: "4px", right: "5px", padding: "1px 5px",
                borderRadius: "999px", background: activeColor, color: "#fff",
                fontSize: "8px", fontWeight: 800, lineHeight: 1.4,
              }}>
                {isDirectActive ? elakayAutoText("h_0fc6c6b82e5e80", "ACTIF") : elakayAutoText("h_8b2b5c4850fc99", "GROUPE")}
              </span>
            )}
            {React.createElement(def.icon, { size: 20, color: isDirectActive ? "#6d28d9" : isNestedActive ? "#0284c7" : "#6d28d9" })}
            <span style={{ fontSize: "12px", fontWeight: 600, marginTop: "6px", textAlign: "center", lineHeight: 1.2 }}>{def.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  LxCPicker — Sélecteur matriciel l (lignes) × c (colonnes)
//
//  Permet d'insérer rapidement une mise en page en survolant une grille de
//  cellules (max MAX_L lignes × MAX_C colonnes).
//  On clic → callback onPick(l, c) avec les dimensions sélectionnées.
//
//  Usage :
//    <LxCPicker onPick={(l, c) => insertRowGrid(l, c)} />
// ---------------------------------------------------------------------------
// LxCPicker — cellSize réduit si beaucoup de colonnes pour tenir dans le panneau
function LxCPicker({ onPick, label = "Insérer une grille l × c", maxL = 6, maxC = 8, compact = false }) {
  const [hov, setHov] = React.useState({ l: 0, c: 0 });
  const sz = compact ? 14 : 18;   // taille de cellule en px
  const gap = compact ? 2 : 3;

  return (
    <div style={{ marginBottom: "6px" }}>
      {label && (
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#7a73a0", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
          {label}
        </div>
      )}
      {/* Matrice de cellules */}
      <div
        style={{ display: "inline-flex", flexDirection: "column", gap: `${gap}px`, cursor: "pointer" }}
        onMouseLeave={() => setHov({ l: 0, c: 0 })}
      >
        {Array.from({ length: maxL }, (_, li) => (
          <div key={li} style={{ display: "flex", gap: `${gap}px` }}>
            {Array.from({ length: maxC }, (_, ci) => {
              const active = li < hov.l && ci < hov.c;
              return (
                <div
                  key={ci}
                  onMouseEnter={() => setHov({ l: li + 1, c: ci + 1 })}
                  onClick={() => { if (hov.l > 0 && hov.c > 0) onPick(hov.l, hov.c); }}
                  style={{
                    width: `${sz}px`, height: `${sz}px`,
                    borderRadius: "3px",
                    background: active ? "#6d28d9" : "#f3effe",
                    border: `1px solid ${active ? "#6d28d9" : "#e4dcfb"}`,
                    transition: "background 0.08s, border-color 0.08s",
                    flexShrink: 0,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      {/* Label dimensions courant */}
      <div style={{ fontSize: "11px", fontWeight: 700, color: hov.l > 0 ? "#6d28d9" : "#b3aacb", marginTop: "5px", minHeight: "16px", letterSpacing: "0.03em" }}>
        {hov.l > 0 && hov.c > 0 ? `${hov.l} × ${hov.c}` : "—"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Génération HTML autonome (avec carrousel vanilla JS)
// ---------------------------------------------------------------------------
function styleToString(style = {}) {
  return Object.entries(style).map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())}:${v}`).join(";");
}
function esc(s = "") { return String(s).replace(/"/g, "&quot;"); }
function escText(s = "") {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function escAttr(s = "") {
  return escText(s).replace(/"/g, "&quot;");
}
function textWithCollapseHtml(node) {
  const text = String(node?.text || "");
  const cfg = getTextCollapseConfig(node);
  if (!canUseTextCollapse(node) || !cfg.enabled || text.length <= cfg.maxChars) {
    return escText(text);
  }
  const controlColor = cfg.controlColor ? `color:${escAttr(cfg.controlColor)};` : "color:inherit;";
  const controlFontSize = cfg.controlFontSize ? `font-size:${escAttr(cfg.controlFontSize)};` : "font-size:inherit;";
  const controlStyle = cfg.controlType === "button"
    ? `margin-left:.5em;padding:2px 8px;border:1px solid currentColor;border-radius:999px;background:transparent;${controlColor}font-family:inherit;${controlFontSize}font-weight:700;cursor:pointer;`
    : `margin-left:.35em;padding:0;border:0;background:transparent;${controlColor}font-family:inherit;${controlFontSize}font-weight:700;text-decoration:underline;cursor:pointer;`;
  return `<span data-pb-text-short>${escText(truncateTextByChars(text, cfg.maxChars))}</span><span data-pb-text-full hidden>${escText(text)}</span><button type="button" data-pb-text-toggle aria-expanded="false" data-more-label="${escAttr(cfg.seeMoreLabel)}" data-less-label="${escAttr(cfg.seeLessLabel)}" style="${controlStyle}">${escText(cfg.seeMoreLabel)}</button>`;
}
function firstShopValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}
function shopArray(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const candidates = [
    value.products,
    value.items,
    value.categories,
    value.slides,
    value.data,
    value.results,
  ];
  return candidates.find(Array.isArray) || [];
}
function shopImageUrl(item) {
  if (!item || typeof item !== "object") return "";
  const canonicalValue = resolvePath(item, "ProductImageUrls.0");
  const galleryValue = resolvePath(item, "galeriesID.galeries.0.urlImg");
  const value = firstShopValue(
    canonicalValue,
    Array.isArray(galleryValue) ? galleryValue[0] : galleryValue,
    item.urlImg,
    item.image,
    item.imageUrl,
    item.imgUrl,
    item.src,
    item.thumbnail
  );
  return Array.isArray(value) ? String(value[0] || "") : String(value || "");
}
function shopItemName(item, fallback = "Produit") {
  const raw = firstShopValue(
    item?.name,
    item?.title,
    item?.productName,
    item?.categoryName,
    Array.isArray(item?.name) ? item.name[0] : null,
    fallback
  );
  return Array.isArray(raw) ? String(raw[0] || fallback) : String(raw || fallback);
}
function shopPriceInfo(item) {
  const regular = Number(firstShopValue(item?.price, item?.regularPrice, item?.amount, 0)) || 0;
  const sale = Number(firstShopValue(item?.salePrice, item?.discountPrice, 0)) || 0;
  const onSale = sale > 0 && (!regular || sale < regular);
  const current = onSale ? sale : regular;
  const discount = onSale && regular > 0 ? Math.round((1 - sale / regular) * 100) : 0;
  return { regular, sale, current, onSale, discount };
}
function shopMoney(value) {
  const amount = Number(value) || 0;
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function shopProductHref(item, store) {
  const direct = firstShopValue(item?.href, item?.url, item?.link);
  if (direct) return String(direct);
  const base = firstShopValue(store?.links?.productDetail, "#");
  const id = firstShopValue(item?.publicId, item?._id, item?.id);
  if (!id || base === "#") return String(base);
  return `${String(base).replace(/\/$/, "")}/${encodeURIComponent(String(id))}`;
}
function shopEntityId(value) {
  if (value == null) return "";
  if (Array.isArray(value)) {
    for (const item of value) {
      const id = shopEntityId(item);
      if (id) return id;
    }
    return "";
  }
  if (typeof value === "object") {
    return shopEntityId(
      value._id ??
      value.id ??
      value.productID ??
      value.productId ??
      value.categoryID ??
      value.categoryId ??
      value.catID ??
      value.owner
    );
  }
  const normalized = String(value).trim();
  return normalized && normalized !== "[object Object]" ? normalized : "";
}
function shopOwnerId(item, store) {
  return shopEntityId(
    item?.owner ??
    store?.owner ??
    resolvePath(store, "categories.owner") ??
    resolvePath(store, "pubCategoryProdMatrix.owner") ??
    resolvePath(store, "shopPubMatrix.owner")
  );
}
function shopProductActionAttrs(item, store, quantity = 1) {
  const firstCategory = Array.isArray(item?.categoriesID) ? item.categoriesID[0] : item?.categoriesID;
  const firstFilter = Array.isArray(item?.filterProduct) ? item.filterProduct[0] : item?.filterProduct;
  const categoryId = shopEntityId(firstCategory?.catID ?? firstCategory);
  const filterProductId = shopEntityId(firstFilter);
  const productId = shopEntityId(item?._id ?? item?.id);
  return [
    `data-shop-product-id="${esc(productId)}"`,
    `data-shop-name="${esc(shopItemName(item))}"`,
    `data-shop-owner-id="${esc(shopOwnerId(item, store))}"`,
    `data-shop-category-id="${esc(categoryId)}"`,
    `data-shop-filter-id="${esc(filterProductId)}"`,
    `data-shop-image="${esc(shopImageUrl(item))}"`,
    `data-shop-product-href="${esc(shopProductHref(item, store))}"`,
    `data-shop-quantity="${Math.max(1, Number(quantity) || 1)}"`,
  ].join(" ");
}
function shopProductCategoryTokens(item) {
  const categories = Array.isArray(item?.categoriesID) ? item.categoriesID : [item?.categoriesID];
  return categories
    .flatMap((category) => [
      shopEntityId(category),
      shopEntityId(category?.catID),
      shopEntityId(category?.categoryID),
      shopEntityId(category?.categoryId),
    ])
    .filter(Boolean)
    .join(" ");
}
function shopRatingMarkup(item, size = "14px") {
  const rating = Math.max(0, Math.min(5, Math.round(Number(firstShopValue(item?.Stars, item?.rating, 0)) || 0)));
  return `<span aria-label="${rating} sur 5" style="font-size:${esc(size)};color:#f59e0b;letter-spacing:1px;">${"★".repeat(rating)}${"☆".repeat(5 - rating)}</span>`;
}
function shopProductCardHtml(item, store, options = {}, indent = "      ") {
  const name = shopItemName(item);
  const image = shopImageUrl(item);
  const href = shopProductHref(item, store);
  const price = shopPriceInfo(item);
  const productId = esc(firstShopValue(item?._id, item?.id, name));
  const imageMarkup = image
    ? `<img src="${esc(image)}" alt="${esc(name)}" style="display:block;width:100%;height:190px;object-fit:cover;background:#f0f9ff;" />`
    : `<div style="height:190px;display:grid;place-items:center;background:linear-gradient(135deg,#dbeafe,#ede9fe);color:#6d28d9;font-size:42px;">&#9635;</div>`;
  const original = price.onSale
    ? `<span style="font-size:12px;color:#9ca3af;text-decoration:line-through;">${shopMoney(price.regular)}</span>`
    : "";
  const badge = price.discount
    ? `<span style="position:absolute;top:10px;left:10px;padding:4px 8px;border-radius:7px;background:#ef4444;color:#fff;font-size:11px;font-weight:800;">-${price.discount}%</span>`
    : "";
  const addButton = options.showAddToCartButton === false
    ? ""
    : `<button type="button" data-shop-action="addToCart" data-shop-add="${productId}" ${shopProductActionAttrs(item, store)} style="width:34px;height:34px;border:0;border-radius:50%;background:#0073aa;color:#fff;font-size:20px;cursor:pointer;">+</button>`;
  const pageItem = options.pageItem ? " data-shop-page-item" : "";
  return `${indent}<article${pageItem} data-shop-product-id="${productId}" data-shop-product-name="${esc(name.toLowerCase())}" data-shop-product-categories="${esc(shopProductCategoryTokens(item))}" style="min-width:0;border:1px solid #e5e7eb;border-radius:16px;overflow:hidden;background:#fff;box-shadow:0 4px 16px rgba(15,23,42,.08);">
${indent}  <a href="${esc(href)}" style="position:relative;display:block;text-decoration:none;color:inherit;">${imageMarkup}${badge}</a>
${indent}  <div style="padding:13px 14px;">
${indent}    <a href="${esc(href)}" style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#1a1a2e;text-decoration:none;font-size:14px;font-weight:700;">${escText(name)}</a>
${indent}    <div style="margin-top:6px;">${shopRatingMarkup(item)} <span style="font-size:11px;color:#9ca3af;">(${Number(item?.totalSold || item?.reviewsCount || 0)})</span></div>
${indent}    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:8px;">
${indent}      <div style="display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;"><strong style="font-size:17px;color:#0073aa;">${shopMoney(price.current)}</strong>${original}</div>
${indent}      ${addButton}
${indent}    </div>
${indent}  </div>
${indent}</article>`;
}

// Wrapper : enrobe l'élément dans une ancre si un lien universel (linkHref/linkTarget) est défini
function nodeToHtml(node, indent = "      ") {
  let inner = nodeToHtmlInner(node, indent);
  if (node.linkHref || node.linkTarget) {
    const meta = resolveNodeHrefTarget(node.linkHref, node.linkTarget);
    const href = esc(meta.href);
    const tgt = node.linkTargetBlank ? ` target="_blank" rel="noopener"` : "";
    inner = `${indent}<a href="${href}"${tgt} style="text-decoration:none;color:inherit;display:contents;">\n${inner.replace(/^/gm, "  ")}\n${indent}</a>`;
  }
  const repeatedItemAttrs = node._exportRepeatGroup && node._exportItem
    ? ` data-shop-page-item data-shop-repeat-group="${esc(node._exportRepeatGroup)}" data-shop-product-id="${esc(shopEntityId(node._exportItem?._id ?? node._exportItem?.id))}" data-shop-product-name="${esc(shopItemName(node._exportItem).toLowerCase())}" data-shop-product-categories="${esc(shopProductCategoryTokens(node._exportItem))}"`
    : "";
  return `${indent}<div data-pb-node-id="${esc(node.id || "")}"${repeatedItemAttrs} style="display:contents;">\n${inner.replace(/^/gm, "  ")}\n${indent}</div>`;
}

function nodeToHtmlInner(node, indent = "      ") {
  const tag = node.tag || ELEMENT_TYPES[node.type]?.tag || "div";
  const def = ELEMENT_TYPES[node.type];
  const cls = node.mobileStyle && Object.keys(node.mobileStyle).length ? ` class="pbm-${node.id}"` : "";
  // Attributs HTML libres (id, name, role, aria-*, data-*, action, method…) communs à toutes les balises
  const attrStr = Object.entries(node.attrs || {})
    .filter(([k, v]) => k && v !== "" && v != null)
    .map(([k, v]) => (v === true ? esc(k) : `${esc(k)}="${esc(v)}"`))
    .join(" ");
  const attrPart = attrStr ? " " + attrStr : "";
  // Animation éventuelle, fusionnée dans le style inline
  let animCss = "";
  if (node.animation === "custom") {
    animCss = `animation:${buildCustomAnim(node).css}`;
  } else if (node.animation && node.animation !== "none" && ANIMATIONS[node.animation]?.css) {
    animCss = `animation:${ANIMATIONS[node.animation].css};${node.animDelay ? `animation-delay:${node.animDelay};` : ""}${node.animDuration ? `animation-duration:${node.animDuration};` : ""}`;
  }
  const actionName = node.clickActionType === "function" ? normalizeHrefLikeValue(node.clickActionFunction) : "";
  const actionArgs = [node.clickActionArg1, node.clickActionArg2, node.clickActionArg3];
  const actionPart = actionName
    ? ` data-shop-action="${esc(actionName)}" data-shop-action-args="${esc(JSON.stringify(actionArgs))}"`
    : "";
  const clickHref = node.clickActionType === "page"
    ? `#${normalizeHrefLikeValue(node.clickActionPage)}`
    : ["url", "href", "link"].includes(node.clickActionType)
      ? normalizeHrefLikeValue(node.clickActionHref)
      : "";
  const clickTarget = node.clickActionTargetBlank ? ` target="_blank" rel="noopener"` : "";
  const style = `${cls} data-pb-node-id="${esc(node.id || "")}"${attrPart}${actionPart} style="${styleToString(node.style)}${animCss ? ";" + animCss : ""}"`;
  const liveData = node._exportData;
  const liveStore = node._exportStore || {};

  if (def?.isShopVote) {
    const o = node.options || {};
    const max = Math.max(1, Number(o.maxStars) || 5);
    const rating = Math.max(0, Math.min(max, Math.round(Number(firstShopValue(liveData?.Stars, liveData?.rating, o.defaultRating, 0)) || 0)));
    const stars = "★".repeat(rating) + "☆".repeat(max - rating);
    return `${indent}<div${style}><span style="font-size:${esc(o.starSize || "28px")};color:#f59e0b;letter-spacing:2px;">${stars}</span><span style="margin-left:8px;font-size:13px;color:#6b7280;">${rating}/${max}</span></div>`;
  }

  if (def?.isShopPrice) {
    const o = node.options || {};
    const price = shopPriceInfo(liveData || {});
    const productId = esc(firstShopValue(liveData?._id, liveData?.id, "product"));
    const original = price.onSale
      ? `<span style="font-size:16px;color:#9ca3af;text-decoration:line-through;">${shopMoney(price.regular)}</span>`
      : "";
    const badge = price.discount
      ? `<span style="padding:3px 9px;border-radius:6px;background:#fee2e2;color:#ef4444;font-size:12px;font-weight:800;">-${price.discount}%</span>`
      : "";
    const button = o.showAddToCartButton === false
      ? ""
      : `${indent}  <button type="button" data-shop-action="addToCart" data-shop-add="${productId}" ${shopProductActionAttrs(liveData || {}, liveStore)} style="margin-top:10px;padding:10px 22px;border:0;border-radius:8px;background:#0073aa;color:#fff;font-size:14px;font-weight:700;cursor:pointer;">Ajouter au panier</button>`;
    return `${indent}<div${style}>
${indent}  <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;"><strong style="font-size:24px;color:#0073aa;">${shopMoney(price.current)}</strong>${original}${badge}</div>
${button}
${indent}</div>`;
  }

  if (def?.isShopCheckout) {
    const o = node.options || {};
    const products = shopArray(liveData);
    const product = products[0] || liveStore?.products?.[0] || {};
    const productId = esc(firstShopValue(product?._id, product?.id, "cart"));
    return `${indent}<div${style}>
${indent}  <div style="display:flex;flex-direction:column;gap:10px;">
${indent}    <button type="button" data-shop-action="addToCart" data-shop-add="${productId}" ${shopProductActionAttrs(product, liveStore, o.quantity)} style="padding:13px 28px;border:0;border-radius:10px;background:#0073aa;color:#fff;font-size:15px;font-weight:700;cursor:pointer;">${escText(o.label || "Ajouter au panier")}</button>
${o.showBuyNow === false ? "" : `${indent}    <a href="${esc(liveStore?.links?.checkoutPage || "#")}" data-shop-action="buyNow" data-shop-buy="${productId}" ${shopProductActionAttrs(product, liveStore, o.quantity)} style="padding:11px 28px;border:2px solid #0073aa;border-radius:10px;background:#fff;color:#0073aa;text-align:center;text-decoration:none;font-size:15px;font-weight:700;">${escText(o.labelBuy || "Acheter maintenant")}</a>`}
${indent}  </div>
${indent}</div>`;
  }

  if (def?.isShowImage) {
    const image = shopImageUrl(liveData || {});
    const href = shopProductHref(liveData || {}, liveStore);
    const o = node.options || {};
    return `${indent}<div${style}>
${indent}  <a href="${esc(href)}" style="position:relative;display:block;">
${image ? `${indent}    <img src="${esc(image)}" alt="${esc(shopItemName(liveData))}" style="display:block;width:100%;height:auto;object-fit:cover;" />` : `${indent}    <div style="min-height:220px;display:grid;place-items:center;background:linear-gradient(135deg,#dbeafe,#ede9fe);color:#6d28d9;">Image indisponible</div>`}
${o.showB === false ? "" : `${indent}    <span style="position:absolute;left:9px;top:9px;padding:5px 8px;border-radius:999px;background:rgba(255,255,255,.92);color:#6d28d9;font-size:11px;font-weight:800;">Voir</span>`}
${indent}  </a>
${indent}</div>`;
  }

  if (def?.isShopCard) {
    return `${indent}<div${style}>
${shopProductCardHtml(liveData || {}, liveStore, node.options, indent + "  ")}
${indent}</div>`;
  }

  if (def?.isShopProductGrid) {
    const o = node.options || {};
    const products = shopArray(liveData);
    const limit = Math.max(1, Number(o.limit) || products.length || 12);
    const cards = products.map((item) => shopProductCardHtml(item, liveStore, { ...o, pageItem: true }, indent + "  ")).join("\n");
    return `${indent}<section${style}>
${indent}  <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:14px;"><h2 style="font-size:20px;color:#1f2937;">${escText(o.prodCat || "Tous les produits")}</h2><span style="font-size:12px;color:#64748b;">${products.length} produit(s)</span></div>
${indent}  <div data-shop-product-grid data-shop-paginated-grid data-shop-page-size="${limit}" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:14px;">
${cards || `${indent}    <p style="color:#64748b;">Aucun produit disponible.</p>`}
${indent}  </div>
${indent}</section>`;
  }

  if (def?.isShopCarousel || def?.isShopPubMatrix) {
    const o = node.options || {};
    const products = shopArray(liveData);
    const title = firstShopValue(o.title, liveData?.title, liveData?.shopName, def.isShopPubMatrix ? "Produits en vedette" : "Notre sélection");
    const cards = products.map((item) => `<div style="flex:0 0 190px;">\n${shopProductCardHtml(item, liveStore, o, indent + "    ")}\n${indent}  </div>`).join("\n");
    return `${indent}<section${style}>
${o.inView === false ? "" : `${indent}  <h2 style="margin:0 0 12px;font-size:${esc(o.titleFontSize || "1.5rem")};font-weight:${esc(o.titleFontWeight || 700)};color:${esc(o.titleColor || "#1f2937")};">${escText(title)}</h2>`}
${indent}  <div style="display:flex;gap:12px;overflow-x:auto;padding:4px 2px 12px;scroll-snap-type:x proximity;">
${cards || `${indent}    <p style="color:#64748b;">Aucun produit disponible.</p>`}
${indent}  </div>
${indent}</section>`;
  }

  if (def?.isShopSlider) {
    const slides = shopArray(liveData);
    const shopName = firstShopValue(
      Array.isArray(liveStore?.owner?.shopName) ? liveStore.owner.shopName[0] : liveStore?.owner?.shopName,
      "Notre boutique"
    );
    const normalizedSlides = slides.length ? slides : [{}];
    const slideHtml = normalizedSlides.map((item, i) => {
      const image = shopImageUrl(item);
      const title = firstShopValue(item?.title, item?.name, shopName);
      const text = firstShopValue(item?.description, item?.subtitle, "");
      const background = image
        ? `linear-gradient(rgba(15,23,42,.48),rgba(15,23,42,.48)),url('${esc(image)}')`
        : "linear-gradient(135deg,#0f172a,#075985)";
      return `${indent}    <div class="pb-slide${i === 0 ? " active" : ""}" style="position:absolute;inset:0;opacity:${i === 0 ? 1 : 0};transition:opacity .5s;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px;text-align:center;background-image:${background};background-size:cover;background-position:center;">
${indent}      <h2 style="margin:0;color:#fff;font-size:clamp(28px,5vw,52px);font-weight:900;">${escText(title)}</h2>
${text ? `${indent}      <p style="max-width:640px;margin:10px 0 0;color:#e2e8f0;font-size:16px;">${escText(text)}</p>` : ""}
${indent}    </div>`;
    }).join("\n");
    const dots = normalizedSlides.map((_, i) => `<span data-i="${i}" style="width:${i === 0 ? 22 : 8}px;height:8px;border-radius:8px;background:${i === 0 ? "#38bdf8" : "rgba(255,255,255,.55)"};cursor:pointer;"></span>`).join("");
    return `${indent}<div class="pb-swiper"${style}>
${indent}  <div class="pb-track" style="position:relative;height:${esc(node.options?.height || "360px")};overflow:hidden;border-radius:12px;">
${slideHtml}
${indent}    <button class="pb-prev" type="button" aria-label="Précédent" style="position:absolute;z-index:2;top:50%;left:12px;transform:translateY(-50%);width:38px;height:38px;border:0;border-radius:50%;background:rgba(255,255,255,.9);color:#075985;cursor:pointer;">&#8249;</button>
${indent}    <button class="pb-next" type="button" aria-label="Suivant" style="position:absolute;z-index:2;top:50%;right:12px;transform:translateY(-50%);width:38px;height:38px;border:0;border-radius:50%;background:rgba(255,255,255,.9);color:#075985;cursor:pointer;">&#8250;</button>
${indent}    <div class="pb-dots" style="position:absolute;z-index:2;left:0;right:0;bottom:14px;display:flex;justify-content:center;gap:7px;">${dots}</div>
${indent}  </div>
${indent}</div>`;
  }

  if (def?.isShopHeaderSeller) {
    const o = node.options || {};
    const owner = liveData || liveStore?.owner || {};
    const shopName = firstShopValue(Array.isArray(owner.shopName) ? owner.shopName[0] : owner.shopName, owner.name, "Ma boutique");
    const shopHref = liveStore?.links?.shopPage || "#";
    return `${indent}<header${style}>
${indent}  <div style="padding:12px 14px;border:1px solid ${esc(o.borderColor || "#ece9f5")};border-radius:10px;background:${esc(o.backgroundColor || "snow")};box-shadow:0 4px 8px ${esc(o.shadowColor || "rgba(0,0,0,.1)")};">
${indent}    <div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
${indent}      <a href="${esc(shopHref)}" style="color:${esc(o.shopNameColor || "#111")};text-decoration:none;font-family:${esc(o.shopNameFontFamily || "inherit")};font-size:${esc(o.shopNameFontSize || "24px")};font-weight:${esc(o.shopNameFontWeight || 700)};">${escText(shopName)}</a>
${indent}      <input type="search" data-shop-search placeholder="Rechercher un produit..." style="flex:1;min-width:180px;padding:9px 13px;border:1px solid ${esc(o.searchBorderColor || "#ccc")};border-radius:999px;background:${esc(o.searchBackgroundColor || "#fff")};color:${esc(o.searchTextColor || "#111")};font-size:${esc(o.searchFontSize || "16px")};" />
${indent}    </div>
${indent}    <nav style="display:flex;align-items:center;gap:16px;flex-wrap:wrap;margin-top:10px;font-size:${esc(o.menuFontSize || "16px")};font-weight:${esc(o.menuFontWeight || 600)};"><a href="${esc(liveStore?.links?.homePage || "#")}" style="color:${esc(o.menuColor || "#111")};text-decoration:none;">Accueil</a><a href="${esc(shopHref)}" style="color:${esc(o.menuColor || "#111")};text-decoration:none;">Boutique</a><a href="${esc(liveStore?.links?.aboutPage || "#")}" style="color:${esc(o.menuColor || "#111")};text-decoration:none;">About Us</a><a href="${esc(liveStore?.links?.cartPage || "#")}" style="margin-left:auto;color:${esc(o.modeActiveColor || "#6d28d9")};text-decoration:none;">Panier <span data-shop-cart-count>0</span></a></nav>
${indent}  </div>
${indent}</header>`;
  }

  if (def?.isShopCatMatrix) {
    const o = node.options || {};
    const categories = shopArray(liveData);
    const cards = categories.map((category) => {
      const products = shopArray(category).slice(0, 4);
      const images = products.map((item) => {
        const image = shopImageUrl(item);
        return image
          ? `<img src="${esc(image)}" alt="${esc(shopItemName(item))}" style="width:100%;height:76px;object-fit:cover;" />`
          : `<div style="height:76px;background:#e0f2fe;"></div>`;
      }).join("");
      return `${indent}    <article style="flex:0 0 220px;border:1px solid #e5e7eb;border-radius:14px;overflow:hidden;background:#fff;">
${indent}      <h3 style="padding:10px 12px;font-size:14px;color:#1f2937;">${escText(shopItemName(category, elakayAutoText("page_builder_category", "Category")))}</h3>
${indent}      <div style="display:grid;grid-template-columns:1fr 1fr;gap:2px;background:#e5e7eb;">${images || `<div style="grid-column:1/-1;height:154px;background:#f1f5f9;"></div>`}</div>
${indent}    </article>`;
    }).join("\n");
    return `${indent}<section${style}>
${o.inView === false ? "" : `${indent}  <h2 style="margin:0 0 12px;font-size:${esc(o.titleFontSize || "1.75rem")};font-weight:${esc(o.titleFontWeight || 700)};color:${esc(o.titleColor || "#1f2937")};">${escText(o.title || elakayAutoText("page_builder_our_categories", "Our categories"))}</h2>`}
${indent}  <div style="display:flex;gap:12px;overflow-x:auto;padding-bottom:10px;">
${cards || `${indent}    <p style="color:#64748b;">${escText(elakayAutoText("page_builder_no_categories", "No categories available."))}</p>`}
${indent}  </div>
${indent}</section>`;
  }

  if (def?.isShopButtonPagination) {
    const products = shopArray(liveData);
    const pageSize = Math.max(1, Number(node.options?.limit) || 12);
    const pageCount = Math.max(1, Math.ceil(products.length / pageSize));
    return `${indent}<nav${style} data-shop-pagination data-shop-page-size="${pageSize}" aria-label="${esc(elakayAutoText("page_builder_product_pagination", "Product pagination"))}">
${indent}  <div style="display:flex;align-items:center;justify-content:center;gap:8px;">
${indent}    <button type="button" data-shop-action="loadMore0" data-shop-page-prev disabled style="padding:8px 16px;border:1px solid #e5e7eb;border-radius:8px;background:#f3f4f6;color:#9ca3af;cursor:pointer;">&#8592; ${escText(elakayAutoText("page_builder_previous", "Previous"))}</button>
${indent}    <span data-shop-page-status style="font-size:13px;color:#4b5563;">${escText(elakayAutoText("page_builder_page", "Page"))} 1 / ${pageCount}</span>
${indent}    <button type="button" data-shop-action="loadMore" data-shop-page-next ${pageCount <= 1 ? "disabled " : ""}style="padding:8px 16px;border:0;border-radius:8px;background:${pageCount <= 1 ? "#e5e7eb" : "#6d28d9"};color:${pageCount <= 1 ? "#9ca3af" : "#fff"};cursor:pointer;">${escText(elakayAutoText("page_builder_next", "Next"))} &#8594;</button>
${indent}  </div>
${indent}</nav>`;
  }

  if (node.type === "image") {
    const src = mediaString(node.src);
    if (node._hideWhenEmptyMedia || (node.bindKey && !src) || !src) return "";
    return `${indent}<img src="${esc(src)}" alt="${esc(node.alt)}"${style} />`;
  }
  if (node.type === "button") {
    const meta = resolveNodeHrefTarget(clickHref || node.href, node.target);
    const productAttrs = node._exportItem ? ` ${shopProductActionAttrs(node._exportItem, liveStore)}` : "";
    return actionName
      ? `${indent}<button type="button"${productAttrs}${style}>${node.text || ""}</button>`
      : `${indent}<a href="${esc(meta.href)}"${clickTarget}${productAttrs}${style}>${node.text || ""}</a>`;
  }
  if (node.type === "link") {
    // Lien externe prioritaire, sinon ancre vers la page cible (#id)
    const meta = resolveNodeHrefTarget(clickHref || node.href, node.target);
    const productAttrs = node._exportItem ? ` ${shopProductActionAttrs(node._exportItem, liveStore)}` : "";
    return `${indent}<a href="${esc(meta.href)}"${clickTarget}${productAttrs}${style}>${node.text || ""}</a>`;
  }
  if (node.type === "spacer") return `${indent}<div${style}></div>`;

  if (node.type === "swiper") {
    const o = node.options || {}; const is = node.itemStyle || {};
    const slides = (node.items || []).map((it, i) => `${indent}    <div class="pb-slide${i === 0 ? " active" : ""}" style="position:absolute;inset:0;opacity:${i === 0 ? 1 : 0};transition:opacity .5s;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;padding:32px;background-image:linear-gradient(${is.overlay || "rgba(0,0,0,.4)"},${is.overlay || "rgba(0,0,0,.4)"}),url('${esc(it.image)}');background-size:cover;background-position:center;">
${indent}      <h3 style="font-size:30px;font-weight:800;color:${is.titleColor || "#fff"};margin:0 0 10px;">${it.title || ""}</h3>
${indent}      <p style="font-size:16px;color:${is.textColor || "#eee"};max-width:560px;margin:0;line-height:1.6;">${it.text || ""}</p>
${indent}    </div>`).join("\n");
    const dots = o.showDots !== false ? `${indent}  <div class="pb-dots" style="display:flex;justify-content:center;gap:8px;padding:12px;">${(node.items || []).map((_, i) => `<span data-i="${i}" style="width:${i === 0 ? 22 : 8}px;height:8px;border-radius:8px;background:${i === 0 ? "#6d28d9" : "#cabfe8"};cursor:pointer;transition:all .3s;"></span>`).join("")}</div>` : "";
    const arrows = o.showArrows !== false ? `${indent}    <button class="pb-prev" style="position:absolute;top:50%;left:12px;transform:translateY(-50%);width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,.9);color:#6d28d9;cursor:pointer;font-size:18px;">‹</button>
${indent}    <button class="pb-next" style="position:absolute;top:50%;right:12px;transform:translateY(-50%);width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,.9);color:#6d28d9;cursor:pointer;font-size:18px;">›</button>` : "";
    return `${indent}<div class="pb-swiper"${style}>
${indent}  <div class="pb-track" style="position:relative;height:${o.height || "300px"};overflow:hidden;">
${slides}
${arrows}
${indent}  </div>
${dots}
${indent}</div>`;
  }

  if (node.type === "card") {
    const o = node.options || {}; const c = node.content || {};
    const landscape = o.orientation === "landscape";
    const imageRight = landscape && o.imageSide === "right";
    const img = `<img src="${esc(c.image)}" alt="${esc(c.title)}" style="display:block;object-fit:cover;width:${landscape ? "42%" : "100%"};height:${landscape ? "auto" : (c.imageHeight || "180px")};${landscape ? "align-self:stretch;" : ""}" />`;
    const btn = o.showButton ? `<a href="${esc(c.buttonHref || "#")}" style="display:inline-block;padding:9px 20px;font-size:14px;font-weight:600;color:#fff;background:#6d28d9;border-radius:9px;text-decoration:none;">${c.buttonText || ""}</a>` : "";
    const body = `<div style="padding:18px;${landscape ? "flex:1;" : ""}">
${indent}    <h4 style="font-size:18px;font-weight:700;color:${c.titleColor || "#1a1a2e"};margin:0 0 8px;">${c.title || ""}</h4>
${indent}    <p style="font-size:14px;color:${c.textColor || "#4a4a68"};line-height:1.6;margin:0${o.showButton ? " 0 14px 0" : ""};">${c.text || ""}</p>
${indent}    ${btn}
${indent}  </div>`;
    const inner = imageRight ? `${body}\n${indent}  ${img}` : `${img}\n${indent}  ${body}`;
    return `${indent}<div style="${styleToString(node.style)};display:flex;flex-direction:${landscape ? "row" : "column"};">
${indent}  ${inner}
${indent}</div>`;
  }

  if (node.type === "textGroup") {
    const lines = (node.items || []).map((it) => `${indent}  <${it.tag || "p"} style="margin:0;font-size:${it.fontSize || "16px"};font-weight:${it.fontWeight || "400"};color:${it.color || "#4a4a68"};letter-spacing:${it.letterSpacing || "normal"};line-height:1.4;">${it.text || ""}</${it.tag || "p"}>`).join("\n");
    return `${indent}<div style="${styleToString(node.style)}">
${lines}
${indent}</div>`;
  }

  if (node.type === "grid") {
    const inner = (node.children || []).map((c) => nodeToHtml(c, indent + "  ")).join("\n");
    const gridStyle = { ...node.style, display: "grid", gridTemplateColumns: `repeat(${node.options?.columns || 3}, minmax(0,1fr))` };
    const repeatedChild = (node.children || []).find((child) => child?._exportRepeatGroup);
    const paginationAttrs = repeatedChild
      ? ` data-shop-paginated-grid data-shop-page-size="${Math.max(1, Number(repeatedChild._exportPageSize) || 12)}" data-shop-repeat-group="${esc(repeatedChild._exportRepeatGroup)}"`
      : "";
    return `${indent}<div${paginationAttrs} style="${styleToString(gridStyle)}">
${inner}
${indent}</div>`;
  }

  if (def?.isField) {
    const f = node.field || {};
    // Construit la chaîne d'attributs HTML à partir des propriétés du champ
    const attrParts = [];
    if (f.name) attrParts.push(`name="${esc(f.name)}"`);
    if (f.value != null && f.value !== "") attrParts.push(`value="${esc(f.value)}"`);
    if (f.required) attrParts.push("required");
    if (f.disabled) attrParts.push("disabled");
    if (f.readonly) attrParts.push("readonly");
    if (f.min != null && f.min !== "") attrParts.push(`min="${esc(f.min)}"`);
    if (f.max != null && f.max !== "") attrParts.push(`max="${esc(f.max)}"`);
    if (f.step != null && f.step !== "") attrParts.push(`step="${esc(f.step)}"`);
    if (f.pattern) attrParts.push(`pattern="${esc(f.pattern)}"`);
    if (f.autocomplete) attrParts.push(`autocomplete="${esc(f.autocomplete)}"`);
    const a = attrParts.length ? " " + attrParts.join(" ") : "";
    const label = f.label ? `${indent}  <label style="display:block;font-size:13px;font-weight:600;color:#4a4a68;margin-bottom:6px;">${esc(f.label)}</label>\n` : "";
    let control;
    if (node.type === "textarea") control = `${indent}  <textarea placeholder="${esc(f.placeholder)}" rows="${f.rows || 4}"${a}${style}></textarea>`;
    else if (node.type === "select") control = `${indent}  <select${a}${style}>${(node.options?.choices || []).map((c) => `<option>${esc(c)}</option>`).join("")}</select>`;
    else if (def?.hasDatalist) {
      const listId = `dl-${node.id}`;
      control = `${indent}  <input type="${f.inputType || "text"}" placeholder="${esc(f.placeholder)}" list="${listId}"${a}${style} />\n${indent}  <datalist id="${listId}">${(node.options?.choices || []).map((c) => `<option value="${esc(c)}">`).join("")}</datalist>`;
    }
    else control = `${indent}  <input type="${f.inputType || "text"}" placeholder="${esc(f.placeholder)}"${a}${style} />`;
    return `${indent}<div style="margin-bottom:16px;">\n${label}${control}\n${indent}</div>`;
  }

  if (def?.isCheckbox || def?.isRadio) {
    const f = node.field || {};
    const type = def?.isRadio ? "radio" : "checkbox";
    const nm = f.name ? ` name="${esc(f.name)}"` : "";
    const val = f.value ? ` value="${esc(f.value)}"` : "";
    return `${indent}<label style="${styleToString(node.style)}"><input type="${type}"${nm}${val}${f.checked ? " checked" : ""} style="accent-color:#6d28d9;width:17px;height:17px;" /> ${esc(f.label)}</label>`;
  }

  if (def?.isOutput) {
    return `${indent}<output style="${styleToString(node.style)}">${esc(node.content?.text)}</output>`;
  }

  if (def?.isVideo) {
    const o = node.options || {};
    const url = mediaString(node.videoUrl || node.src || o.url || "");
    if (node._hideWhenEmptyMedia || (node.bindKey && !url) || !url) return "";
    const isFile = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(url);
    if (isFile) {
      return `${indent}<video src="${esc(url)}" controls style="${styleToString(node.style)}"></video>`;
    }
    return `${indent}<div style="${styleToString(node.style)};aspect-ratio:${o.ratio || "16 / 9"};position:relative;">
${indent}  <iframe src="${esc(toEmbedUrl(url))}" title="vidéo" allowfullscreen style="width:100%;height:100%;border:none;"></iframe>
${indent}</div>`;
  }

  if (def?.isElakaySwiper) {
    // Export statique : un swiper est interactif et vivant (Swiper Manager) --
    // on n'en fige jamais une copie ici, seulement sa référence.
    const swiperId = String(node.options?.swiperId || "");
    if (!/^[0-9a-fA-F]{24}$/.test(swiperId)) return "";
    return `${indent}<div data-elakay-swiper-id="${esc(swiperId)}" style="${styleToString(node.style)}"></div>`;
  }

  if (def?.isIcon) {
    const o = node.options || {};
    const svgMarkup = renderIconSvgMarkup(node.content?.iconId, {
      width: o.size || "48px", height: o.size || "48px", color: o.color || "#6d28d9",
      rotation: o.rotation, opacity: o.opacity,
      decorative: o.decorative !== false, ariaLabel: o.ariaLabel,
    });
    const inner = svgMarkup || `<span style="font-size:${o.size || "48px"};color:${o.color || "#6d28d9"};line-height:1;">${esc(o.glyph || "★")}</span>`;
    return `${indent}<div style="${styleToString(node.style)}">${inner}</div>`;
  }

  if (def?.isQuote) {
    const c = node.content || {};
    return `${indent}<blockquote style="${styleToString(node.style)}">
${indent}  <p style="margin:0 0 8px;font-size:18px;font-style:italic;color:#2a2547;line-height:1.6;">${esc(c.text)}</p>
${indent}  ${c.author ? `<footer style="font-size:14px;font-weight:700;color:#6d28d9;">${esc(c.author)}</footer>` : ""}
${indent}</blockquote>`;
  }

  if (def?.isStat) {
    const c = node.content || {};
    return `${indent}<div style="${styleToString(node.style)}">
${indent}  <div style="font-size:34px;font-weight:800;color:${c.valueColor || "#6d28d9"};line-height:1.1;">${esc(c.value)}</div>
${indent}  <div style="font-size:14px;color:${c.labelColor || "#7a73a0"};margin-top:6px;">${esc(c.label)}</div>
${indent}</div>`;
  }

  if (def?.isProgress) {
    const c = node.content || {};
    const pct = Math.max(0, Math.min(100, Number(c.percent) || 0));
    return `${indent}<div style="${styleToString(node.style)}">
${indent}  <div style="display:flex;justify-content:space-between;font-size:13px;color:#4a4a68;margin-bottom:6px;"><span>${esc(c.label)}</span><span style="font-weight:700;">${pct}%</span></div>
${indent}  <div style="height:10px;border-radius:10px;background:${c.trackColor || "#ece9f5"};overflow:hidden;"><div style="width:${pct}%;height:100%;background:${c.barColor || "#6d28d9"};border-radius:10px;"></div></div>
${indent}</div>`;
  }

  if (def?.isRating) {
    const c = node.content || {};
    const max = c.max || 5; const val = Math.round(Number(c.value) || 0);
    const stars = "★".repeat(val) + "☆".repeat(Math.max(0, max - val));
    return `${indent}<div style="${styleToString(node.style)}"><span style="font-size:22px;color:${c.color || "#f59e0b"};letter-spacing:2px;">${stars}</span>${c.label ? `<span style="font-size:14px;color:#7a73a0;">${esc(c.label)}</span>` : ""}</div>`;
  }

  if (def?.isAccordion) {
    const items = (node.items || []).map((it) => `${indent}  <details style="border:1px solid #ece9f5;border-radius:12px;overflow:hidden;background:#fff;">
${indent}    <summary style="padding:14px 18px;cursor:pointer;font-size:15px;font-weight:600;color:#1a1a2e;">${esc(it.q)}</summary>
${indent}    <div style="padding:0 18px 16px;font-size:14px;color:#4a4a68;line-height:1.6;">${esc(it.a)}</div>
${indent}  </details>`).join("\n");
    return `${indent}<div style="${styleToString(node.style)}">\n${items}\n${indent}</div>`;
  }

  if (def?.isDivider) return `${indent}<hr${style} />`;

  if (def?.isModal) {
    let inner;
    if (node.contentMode === "simple") {
      const s = node.simple || {};
      const txt = s.text ? `${indent}    <p style="font-size:16px;color:#4a4a68;line-height:1.6;margin:0;">${esc(s.text)}</p>\n` : "";
      // bouton : ferme le modal si pas de lien, sinon ancre vers le lien
      const btn = s.buttonText
        ? (s.buttonHref
            ? `${indent}    <a href="${esc(s.buttonHref)}" style="align-self:flex-start;padding:10px 22px;font-size:15px;font-weight:600;color:#fff;background:#6d28d9;border-radius:10px;text-decoration:none;">${esc(s.buttonText)}</a>\n`
            : `${indent}    <button type="button" data-modal-close="${node.id}" style="align-self:flex-start;padding:10px 22px;font-size:15px;font-weight:600;color:#fff;background:#6d28d9;border:none;border-radius:10px;cursor:pointer;">${esc(s.buttonText)}</button>\n`)
        : "";
      inner = txt + btn;
    } else {
      inner = (node.children || []).map((c) => nodeToHtml(c, indent + "    ")).join("\n") + "\n";
    }
    const title = node.title ? `${indent}    <strong style="font-size:20px;color:#1a1a2e;">${esc(node.title)}</strong>\n` : "";
    return `${indent}<button type="button" data-modal-open="${node.id}" style="${styleToString(node.triggerStyle || {})}">${esc(node.triggerText || "Ouvrir")}</button>
${indent}<div id="modal-${node.id}" class="pb-modal" style="display:none;position:fixed;inset:0;background:rgba(20,12,40,0.5);z-index:999;align-items:center;justify-content:center;padding:20px;">
${indent}  <div${style}>
${indent}    <div style="display:flex;align-items:center;justify-content:space-between;">
${title}${indent}      <button type="button" data-modal-close="${node.id}" aria-label="Fermer" style="border:none;background:none;cursor:pointer;color:#9b93b8;font-size:22px;line-height:1;">×</button>
${indent}    </div>
${inner}${indent}  </div>
${indent}</div>`;
  }

  if (def?.isDropdown) {
    const items = (node.items || []).map((it) => `${indent}    <a href="${esc(it.href || "#")}" style="display:block;padding:9px 16px;font-size:14px;color:#2a2547;text-decoration:none;">${esc(it.label)}</a>`).join("\n");
    return `${indent}<div class="pb-dd" style="position:relative;display:inline-block;">
${indent}  <button type="button" data-dd-toggle style="${styleToString(node.triggerStyle || {})}">${esc(node.triggerText || "Menu")}</button>
${indent}  <div class="pb-dd-menu"${style.replace(' style="', ' style="display:none;position:absolute;margin-top:6px;z-index:50;')}>
${items}
${indent}  </div>
${indent}</div>`;
  }

  if (def?.isTabs) {
    const items = node.items || [];
    const heads = items.map((it, i) => `${indent}    <button type="button" data-tab="${i}" style="padding:12px 20px;border:none;background:none;cursor:pointer;font-size:14px;font-weight:600;color:${i === 0 ? "#6d28d9" : "#7a73a0"};border-bottom:2px solid ${i === 0 ? "#6d28d9" : "transparent"};">${esc(it.label)}</button>`).join("\n");
    const panels = items.map((it, i) => `${indent}    <div data-tabpanel="${i}" style="padding:20px;font-size:15px;color:#4a4a68;line-height:1.6;display:${i === 0 ? "block" : "none"};">${esc(it.text)}</div>`).join("\n");
    return `${indent}<div class="pb-tabs"${style}>
${indent}  <div style="display:flex;border-bottom:1px solid #ece9f5;background:#faf9fd;">
${heads}
${indent}  </div>
${panels}
${indent}</div>`;
  }

  if (def?.isOffcanvas) {
    const side = ["left", "right", "top", "bottom"].includes(node.side) ? node.side : "right";
    const horizontal = side === "left" || side === "right";
    const panelPos = horizontal ? `position:fixed;top:0;bottom:0;${side}:0;` : `position:fixed;left:0;right:0;${side}:0;`;
    const inner = (node.children || []).map((c) => nodeToHtml(c, indent + "    ")).join("\n");
    const title = node.title ? `${indent}    <strong style="font-size:18px;color:#1a1a2e;">${esc(node.title)}</strong>\n` : "";
    return `${indent}<button type="button" data-oc-open="${node.id}" style="${styleToString(node.triggerStyle || {})}">${esc(node.triggerText || "Ouvrir")}</button>
${indent}<div id="oc-${node.id}" class="pb-oc" style="display:none;position:fixed;inset:0;background:rgba(20,12,40,0.4);z-index:998;">
${indent}  <div${style.replace(' style="', ` style="${panelPos}`)}>
${indent}    <div style="display:flex;align-items:center;justify-content:space-between;">
${title}${indent}      <button type="button" data-oc-close="${node.id}" aria-label="Fermer" style="border:none;background:none;cursor:pointer;color:#9b93b8;font-size:20px;">×</button>
${indent}    </div>
${inner}
${indent}  </div>
${indent}</div>`;
  }

  if (def?.isTooltip) {
    return `${indent}<span class="pb-tip"${style.replace(' style="', ' style="position:relative;')} data-tip="${esc(node.tip)}">${esc(node.text)}</span>`;
  }

  if (def?.isContainer) {
    const inner = (node.children || []).map((c) => nodeToHtml(c, indent + "  ")).join("\n");
    const legend = def?.isFieldset && node.legend ? `${indent}  <legend style="font-size:13px;font-weight:700;color:#6d28d9;padding:0 6px;">${esc(node.legend)}</legend>\n` : "";
    return `${indent}<${tag}${style}>\n${legend}${inner}\n${indent}</${tag}>`;
  }
  return `${indent}<${tag}${style}>${canUseTextCollapse(node) && getTextCollapseConfig(node).enabled ? textWithCollapseHtml(node) : (node.text || "")}</${tag}>`;
}

// Script d'ouverture/fermeture des modals (déclencheurs + liens ciblant un modal)
const MODAL_SCRIPT = `
<script>
(function(){
  function openModal(id){ var m=document.getElementById('modal-'+id); if(m) m.style.display='flex'; }
  function closeModal(id){ var m=document.getElementById('modal-'+id); if(m) m.style.display='none'; }
  document.querySelectorAll('[data-modal-open]').forEach(function(b){ b.addEventListener('click',function(e){ e.preventDefault(); openModal(b.getAttribute('data-modal-open')); }); });
  document.querySelectorAll('[data-modal-close]').forEach(function(b){ b.addEventListener('click',function(e){ e.preventDefault(); closeModal(b.getAttribute('data-modal-close')); }); });
  document.querySelectorAll('.pb-modal').forEach(function(m){ m.addEventListener('click',function(e){ if(e.target===m) m.style.display='none'; }); });
  // Liens vers un modal : href="#<id>" où un modal-<id> existe
  document.querySelectorAll('a[href^="#"]').forEach(function(a){ a.addEventListener('click',function(e){ var id=a.getAttribute('href').slice(1); if(document.getElementById('modal-'+id)){ e.preventDefault(); openModal(id); } }); });
})();
</script>`;

// Script des composants interactifs : menu déroulant, onglets, panneau latéral, info-bulle
const WIDGETS_SCRIPT = `
<script>
(function(){
  // Menus déroulants
  document.querySelectorAll('.pb-dd').forEach(function(dd){
    var btn = dd.querySelector('[data-dd-toggle]'); var menu = dd.querySelector('.pb-dd-menu');
    if(btn&&menu){ btn.addEventListener('click',function(e){ e.stopPropagation(); menu.style.display = menu.style.display==='block'?'none':'block'; }); }
  });
  document.addEventListener('click',function(){ document.querySelectorAll('.pb-dd-menu').forEach(function(m){ m.style.display='none'; }); });
  // Onglets
  document.querySelectorAll('.pb-tabs').forEach(function(t){
    var heads = t.querySelectorAll('[data-tab]'); var panels = t.querySelectorAll('[data-tabpanel]');
    heads.forEach(function(h){ h.addEventListener('click',function(){
      var idx = h.getAttribute('data-tab');
      heads.forEach(function(x){ x.style.color='#7a73a0'; x.style.borderBottom='2px solid transparent'; });
      h.style.color='#6d28d9'; h.style.borderBottom='2px solid #6d28d9';
      panels.forEach(function(p){ p.style.display = p.getAttribute('data-tabpanel')===idx ? 'block':'none'; });
    }); });
  });
  // Panneaux latéraux (offcanvas)
  function ocOpen(id){ var o=document.getElementById('oc-'+id); if(o) o.style.display='block'; }
  function ocClose(id){ var o=document.getElementById('oc-'+id); if(o) o.style.display='none'; }
  document.querySelectorAll('[data-oc-open]').forEach(function(b){ b.addEventListener('click',function(){ ocOpen(b.getAttribute('data-oc-open')); }); });
  document.querySelectorAll('[data-oc-close]').forEach(function(b){ b.addEventListener('click',function(){ ocClose(b.getAttribute('data-oc-close')); }); });
  document.querySelectorAll('.pb-oc').forEach(function(o){ o.addEventListener('click',function(e){ if(e.target===o) o.style.display='none'; }); });
  // Info-bulles
  document.querySelectorAll('.pb-tip').forEach(function(t){
    var tip=document.createElement('span');
    tip.textContent=t.getAttribute('data-tip');
    tip.style.cssText='position:absolute;bottom:calc(100% + 8px);left:50%;transform:translateX(-50%);background:#1a1a2e;color:#fff;padding:6px 12px;border-radius:8px;font-size:13px;white-space:nowrap;z-index:60;display:none;';
    t.appendChild(tip);
    t.addEventListener('mouseenter',function(){ tip.style.display='block'; });
    t.addEventListener('mouseleave',function(){ tip.style.display='none'; });
  });
})();
</script>`;

const SWIPER_SCRIPT = `
<script>
document.querySelectorAll('.pb-swiper').forEach(function(sw){
  var slides = sw.querySelectorAll('.pb-slide');
  var dots = sw.querySelectorAll('.pb-dots span');
  var i = 0;
  function show(n){
    i = (n + slides.length) % slides.length;
    slides.forEach(function(s,k){ s.style.opacity = k===i ? 1 : 0; });
    dots.forEach(function(d,k){ d.style.width = k===i ? '22px':'8px'; d.style.background = k===i ? '#6d28d9':'#cabfe8'; });
  }
  var prev = sw.querySelector('.pb-prev'); var next = sw.querySelector('.pb-next');
  if(prev) prev.addEventListener('click', function(){ show(i-1); });
  if(next) next.addEventListener('click', function(){ show(i+1); });
  dots.forEach(function(d){ d.addEventListener('click', function(){ show(+d.dataset.i); }); });
});
</script>`;

const buildTextCollapseScript = () => {
  const lessLabel = jsonForInlineScript(elakayAutoText("h_pb_text_collapse_less", "See less"));
  const moreLabel = jsonForInlineScript(elakayAutoText("h_pb_text_collapse_more", "See more"));
  return `
<script>
(function(){
  var lessLabel = ${lessLabel};
  var moreLabel = ${moreLabel};
  document.querySelectorAll('[data-pb-text-toggle]').forEach(function(btn){
    btn.addEventListener('click', function(e){
      e.preventDefault();
      var full = btn.previousElementSibling;
      var short = full && full.previousElementSibling;
      if(!short || !full) return;
      var next = btn.getAttribute('aria-expanded') !== 'true';
      short.hidden = next;
      full.hidden = !next;
      btn.setAttribute('aria-expanded', next ? 'true' : 'false');
      btn.textContent = next ? (btn.getAttribute('data-less-label') || lessLabel) : (btn.getAttribute('data-more-label') || moreLabel);
    });
  });
})();
</script>`;
};

function jsonForInlineScript(value) {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

function buildShopLiveScript(store = {}) {
  const configuredApiBase = String(elakay_API || "").trim();
  const apiBase = configuredApiBase && configuredApiBase !== "undefined"
    ? configuredApiBase
    : "/elakay_api";
  const links = {
    homePage: store?.links?.homePage || "/",
    shopPage: store?.links?.shopPage || "#",
    aboutPage: store?.links?.aboutPage || "#",
    productDetail: store?.links?.productDetail || "/product/preview",
    cartPage: store?.links?.cartPage || "/cart",
    checkoutPage: store?.links?.checkoutPage || "/cart",
    signinPage: store?.links?.signinPage || "/signin",
  };
  const configJson = jsonForInlineScript({
    apiBase,
    ownerId: shopOwnerId(null, store),
    links,
    endpoints: {
      addCart: "/cart/addCart",
      getCart: "/cart/getCart",
      followShop: "/user/addFollowUserShop",
      productsByShop: "/products/lakayUser",
    },
    storageKey: "elakay-export-cart",
    sessionKey: "elakayUser",
    redirectUnauthenticated: true,
    labels: {
      page: elakayAutoText("page_builder_page", "Page"),
      shopNotFound: elakayAutoText("page_builder_shop_not_found", "Shop not found"),
      ownShopFollow: elakayAutoText("page_builder_own_shop_follow", "You cannot follow your own shop"),
      followed: elakayAutoText("page_builder_followed", "Following"),
      follow: elakayAutoText("page_builder_follow", "Follow"),
      followUpdated: elakayAutoText("page_builder_follow_updated", "Shop follow status updated"),
    },
  });

  return `
<script>
(function(){
  'use strict';
  var generatedConfig = ${configJson};
  var config = Object.assign({}, generatedConfig, window.ElakayShopConfig || {});
  config.links = Object.assign({}, generatedConfig.links, (window.ElakayShopConfig || {}).links || {});
  config.endpoints = Object.assign({}, generatedConfig.endpoints, (window.ElakayShopConfig || {}).endpoints || {});
  window.ElakayShopConfig = config;

  function joinUrl(base, path){
    if(!base) return path || '';
    return String(base).replace(/\\/$/, '') + '/' + String(path || '').replace(/^\\//, '');
  }
  function readJsonStorage(key, fallback){
    try {
      var parsed = JSON.parse(localStorage.getItem(key) || '');
      return parsed == null ? fallback : parsed;
    } catch(_error) {
      return fallback;
    }
  }
  function getSession(){
    var session = readJsonStorage(config.sessionKey, null);
    return session && typeof session === 'object' ? session : null;
  }
  function getToken(){
    var session = getSession();
    return config.token || (session && session.token) || '';
  }
  function getCurrentUserId(){
    var session = getSession() || {};
    return String((session.user && session.user._id) || session._id || '');
  }
  function readCart(){
    var parsed = readJsonStorage(config.storageKey, []);
    return Array.isArray(parsed) ? parsed : [];
  }
  function writeCart(cart){
    try { localStorage.setItem(config.storageKey, JSON.stringify(cart)); } catch(_error) {}
  }
  function updateCount(cart){
    var count = cart.reduce(function(total, item){ return total + Math.max(1, Number(item.quantity) || 1); }, 0);
    document.querySelectorAll('[data-shop-cart-count]').forEach(function(el){ el.textContent = count; });
  }
  function notify(message, isError){
    var region = document.querySelector('[data-shop-live-status]');
    if(!region){
      region = document.createElement('div');
      region.setAttribute('data-shop-live-status', '');
      region.setAttribute('role', 'status');
      region.style.cssText = 'position:fixed;right:18px;bottom:18px;z-index:9999;max-width:340px;padding:11px 14px;border-radius:10px;background:#0f172a;color:#fff;font:600 13px/1.35 system-ui,sans-serif;box-shadow:0 10px 30px rgba(15,23,42,.25);display:none;';
      document.body.appendChild(region);
    }
    region.textContent = message;
    region.style.background = isError ? '#b91c1c' : '#0f172a';
    region.style.display = 'block';
    clearTimeout(region._hideTimer);
    region._hideTimer = setTimeout(function(){ region.style.display = 'none'; }, 2600);
  }
  async function decodeResponse(response){
    var contentType = response.headers.get('content-type') || '';
    var payload = contentType.indexOf('application/json') !== -1
      ? await response.json()
      : await response.text();
    if(!response.ok){
      var message = payload && (payload.error || payload.message);
      throw new Error(message || ('Erreur HTTP ' + response.status));
    }
    return typeof config.decrypt === 'function' ? config.decrypt(payload) : payload;
  }
  async function request(path, options){
    if(!config.apiBase) throw new Error('API Elakay non configurée');
    var opts = Object.assign({}, options || {});
    opts.headers = Object.assign({ Accept:'application/json' }, opts.headers || {});
    var token = getToken();
    if(token && !opts.headers.Authorization) opts.headers.Authorization = 'Bearer ' + token;
    return decodeResponse(await fetch(joinUrl(config.apiBase, path), opts));
  }
  function productFromElement(element){
    var host = element && element.closest ? element.closest('[data-shop-product-id]') : null;
    var source = element || host;
    function attr(name){
      return (element && element.getAttribute && element.getAttribute(name)) ||
        (host && host.getAttribute && host.getAttribute(name)) || '';
    }
    return {
      id: attr('data-shop-product-id') || attr('data-shop-add') || attr('data-shop-buy'),
      name: attr('data-shop-name') || 'Produit',
      ownerId: attr('data-shop-owner-id') || config.ownerId || '',
      categoryId: attr('data-shop-category-id') || '',
      filterProductId: attr('data-shop-filter-id') || '',
      image: attr('data-shop-image') || '',
      href: attr('data-shop-product-href') || '',
      quantity: Math.max(1, Number(attr('data-shop-quantity')) || 1),
      element: source
    };
  }
  function saveLocalProduct(product){
    var cart = readCart();
    var existing = cart.find(function(item){
      return String(item.id) === String(product.id) &&
        String(item.filterProductId || '') === String(product.filterProductId || '');
    });
    if(existing) existing.quantity += product.quantity;
    else cart.push({
      id: product.id,
      name: product.name,
      quantity: product.quantity,
      filterProductId: product.filterProductId,
      image: product.image,
      href: product.href
    });
    writeCart(cart);
    updateCount(cart);
    return cart;
  }
  function redirectToSignin(){
    if(!config.redirectUnauthenticated || location.protocol === 'file:') return;
    var target = config.links.signinPage || '/signin';
    if(target && target !== '#') window.location.assign(target);
  }
  async function addToCart(productOrElement, payload){
    var product = productOrElement && productOrElement.nodeType
      ? productFromElement(productOrElement)
      : Object.assign({}, productOrElement || {}, payload || {});
    if(!product.id) throw new Error('Produit introuvable');
    product.quantity = Math.max(1, Number(product.quantity || (payload && payload.count)) || 1);
    saveLocalProduct(product);

    var token = getToken();
    if(!token){
      notify('Produit enregistré localement. Connectez-vous pour synchroniser le panier.');
      redirectToSignin();
      return { success:true, local:true, authenticated:false };
    }

    await request(config.endpoints.addCart, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body:JSON.stringify({
        count:product.quantity,
        categoryID:product.categoryId || null,
        productID:product.id,
        productOrner:product.ownerId || config.ownerId || null,
        urlImg:product.image || '',
        filterProduct:product.filterProductId || null
      })
    });
    notify(product.name + ' ajouté au panier');
    document.dispatchEvent(new CustomEvent('elakay:cart-updated', { detail:{ product:product } }));
    return { success:true, local:true, authenticated:true };
  }
  async function buyNow(element){
    var result = await addToCart(element);
    if(result && result.authenticated === false) return result;
    var href = (element && element.getAttribute('href')) || config.links.checkoutPage;
    if(href && href !== '#') window.location.assign(href);
    return result;
  }
  async function getCart(){
    if(!getToken()) return readCart();
    return request(config.endpoints.getCart, { method:'GET' });
  }
  async function loadProducts(params){
    params = params || {};
    var ownerId = params.ownerId || config.ownerId;
    if(!ownerId) throw new Error('Propriétaire de boutique introuvable');
    var query = new URLSearchParams({
      sortBy: params.sortBy || 'createdAt',
      skip: String(Math.max(0, Number(params.skip) || 0)),
      order: 'desc',
      limit: String(Math.max(1, Number(params.limit) || 12)),
      ci: params.categoryId || '',
      search: params.search || ''
    });
    return request(config.endpoints.productsByShop + '/' + encodeURIComponent(ownerId) + '?' + query.toString(), {
      method:'POST',
      headers:{
        'Content-Type':'application/json',
        Authorization:'Bearer ' + (getToken() || 'null')
      },
      body:JSON.stringify(params.filters || { filters:{} })
    });
  }

  var viewState = { search:'', categoryId:'', page:0 };
  function visibleGridItems(grid){
    var query = String(viewState.search || '').trim().toLowerCase();
    var categoryId = String(viewState.categoryId || '').trim();
    return Array.prototype.filter.call(grid.querySelectorAll('[data-shop-page-item]'), function(card){
      var name = (card.getAttribute('data-shop-product-name') || '').toLowerCase();
      var categories = ' ' + (card.getAttribute('data-shop-product-categories') || '') + ' ';
      return (!query || name.indexOf(query) !== -1) &&
        (!categoryId || categories.indexOf(' ' + categoryId + ' ') !== -1);
    });
  }
  function renderPagination(targetPage, forcedLimit){
    var primary = null;
    document.querySelectorAll('[data-shop-paginated-grid]').forEach(function(grid){
      var pageSize = Math.max(1, Number(forcedLimit || grid.getAttribute('data-shop-page-size')) || 12);
      var matching = visibleGridItems(grid);
      var pageCount = Math.max(1, Math.ceil(matching.length / pageSize));
      var page = Math.max(0, Math.min(Number(targetPage) || 0, pageCount - 1));
      var start = page * pageSize;
      var matchingSet = new Set(matching);
      grid.querySelectorAll('[data-shop-page-item]').forEach(function(card){
        var matchIndex = matching.indexOf(card);
        if(!card.hasAttribute('data-shop-original-display')){
          card.setAttribute('data-shop-original-display', card.style.display || '');
        }
        var originalDisplay = card.getAttribute('data-shop-original-display') || '';
        card.style.display = matchingSet.has(card) && matchIndex >= start && matchIndex < start + pageSize
          ? originalDisplay
          : 'none';
      });
      grid.setAttribute('data-shop-current-page', String(page));
      grid.setAttribute('data-shop-page-count', String(pageCount));
      if(!primary) primary = { page:page, pageCount:pageCount, pageSize:pageSize };
    });
    if(!primary) primary = { page:0, pageCount:1, pageSize:Math.max(1, Number(forcedLimit) || 12) };
    viewState.page = primary.page;
    document.querySelectorAll('[data-shop-pagination]').forEach(function(nav){
      var prev = nav.querySelector('[data-shop-page-prev]');
      var next = nav.querySelector('[data-shop-page-next]');
      var status = nav.querySelector('[data-shop-page-status]');
      if(prev) prev.disabled = primary.page <= 0;
      if(next) next.disabled = primary.page >= primary.pageCount - 1;
      if(status) status.textContent = config.labels.page + ' ' + (primary.page + 1) + ' / ' + primary.pageCount;
    });
    return primary;
  }
  function setSearch(value){
    viewState.search = String(value || '');
    return renderPagination(0);
  }
  function loadindSearCh(value){ return setSearch(value); }
  function clearSearch(){
    viewState.search = '';
    document.querySelectorAll('[data-shop-search]').forEach(function(input){ input.value = ''; });
    return renderPagination(0);
  }
  function loadProductsByArrival(search, _filters, limit){
    viewState.categoryId = '';
    viewState.search = String(search || '');
    return renderPagination(0, limit);
  }
  function loadProductsByArrival_(categoryId, _categoryName, limit){
    viewState.categoryId = String(categoryId || '');
    viewState.search = '';
    document.querySelectorAll('[data-shop-search]').forEach(function(input){ input.value = ''; });
    return renderPagination(0, limit);
  }
  function loadMore(limit){ return renderPagination(viewState.page + 1, limit); }
  function loadMore0(limit){ return renderPagination(viewState.page - 1, limit); }
  function onFilterShop(){ return renderPagination(0); }
  function setFilterShop(value){
    document.documentElement.toggleAttribute('data-shop-filter-open', !!value);
    return !!value;
  }
  function resetShopFilters(){
    viewState.categoryId = '';
    clearSearch();
    setFilterShop(false);
    return renderPagination(0);
  }
  function scrollToProducts(){
    var target = document.querySelector('[data-shop-product-grid], [data-shop-paginated-grid], [data-shop-products]');
    if(target && target.scrollIntoView) target.scrollIntoView({ behavior:'smooth', block:'start' });
    return !!target;
  }
  function resolveProductHref(productOrElement){
    if(productOrElement && productOrElement.nodeType){
      var fromElement = productFromElement(productOrElement);
      if(fromElement.href) return fromElement.href;
      productOrElement = fromElement;
    }
    var product = productOrElement || {};
    var direct = product.href || product.url || product.link;
    if(direct) return String(direct);
    var id = product._id || product.id || product.productID || '';
    var base = config.links.productDetail || '/product/preview';
    return id ? String(base).replace(/\\/$/, '') + '/' + encodeURIComponent(String(id)) : base;
  }
  function navigateTo(href){
    if(href && href !== '#') window.location.assign(href);
    return href;
  }
  function viewProduct(productOrElement){ return navigateTo(resolveProductHref(productOrElement)); }
  function goToCart(){ return navigateTo(config.links.cartPage || '/cart'); }
  function goToCheckout(){ return navigateTo(config.links.checkoutPage || config.links.cartPage || '/cart'); }
  function goToShop(){ return navigateTo(config.links.shopPage || '#'); }
  async function clickToFollowShop(elementOrOwnerId){
    var ownerId = typeof elementOrOwnerId === 'string' ? elementOrOwnerId : config.ownerId;
    if(!ownerId) throw new Error(config.labels.shopNotFound);
    if(ownerId === getCurrentUserId()) throw new Error(config.labels.ownShopFollow);
    if(!getToken()){
      redirectToSignin();
      return { success:false, authenticated:false };
    }
    await request(config.endpoints.followShop + '/' + encodeURIComponent(ownerId), { method:'PUT' });
    var element = elementOrOwnerId && elementOrOwnerId.nodeType ? elementOrOwnerId : null;
    if(element){
      var followed = element.getAttribute('data-shop-followed') !== 'true';
      element.setAttribute('data-shop-followed', String(followed));
      element.textContent = followed ? config.labels.followed : (element.getAttribute('data-shop-follow-label') || config.labels.follow);
    }
    notify(config.labels.followUpdated);
    return { success:true };
  }
  function findToFollowU(_entries, adminIds, userId){
    var ownerId = String(config.ownerId || '');
    var admins = Array.isArray(adminIds) ? adminIds.map(String) : [];
    return !!ownerId && ownerId !== String(userId || getCurrentUserId()) && admins.indexOf(ownerId) === -1;
  }
  function unFollowShop(entries){
    var ownerId = String(config.ownerId || '');
    return !(Array.isArray(entries) && entries.some(function(entry){
      return String((entry && (entry._id || entry.id)) || entry || '') === ownerId;
    }));
  }
  function loadingUserPub(){
    var href = config.links.shopPage;
    if(href && href !== '#') window.location.assign(href);
  }

  var actions = {
    request:request,
    addToCart:addToCart,
    buyNow:buyNow,
    getCart:getCart,
    loadProducts:loadProducts,
    loadindSearCh:loadindSearCh,
    loadProductsByArrival:loadProductsByArrival,
    loadProductsByArrival_:loadProductsByArrival_,
    loadMore:loadMore,
    loadMore0:loadMore0,
    onFilterShop:onFilterShop,
    setFilterShop:setFilterShop,
    setSearch:setSearch,
    clearSearch:clearSearch,
    resetShopFilters:resetShopFilters,
    scrollToProducts:scrollToProducts,
    viewProduct:viewProduct,
    goToCart:goToCart,
    goToCheckout:goToCheckout,
    goToShop:goToShop,
    clickToFollowShop:clickToFollowShop,
    findToFollowU:findToFollowU,
    unFollowShop:unFollowShop,
    loadingUserPub:loadingUserPub
  };
  window.ElakayShop = actions;
  Object.keys(actions).forEach(function(name){
    if(typeof window[name] !== 'function') window[name] = actions[name];
  });

  function parseActionArgs(element){
    try {
      var args = JSON.parse(element.getAttribute('data-shop-action-args') || '[]');
      return Array.isArray(args) ? args.map(function(value){ return value == null || value === '' ? undefined : value; }) : [];
    } catch(_error) {
      return [];
    }
  }
  function setButtonFeedback(element, label){
    if(!element) return;
    var original = element.getAttribute('data-shop-original-label');
    if(!original){
      original = element.textContent;
      element.setAttribute('data-shop-original-label', original);
    }
    element.textContent = label;
    clearTimeout(element._shopLabelTimer);
    element._shopLabelTimer = setTimeout(function(){ element.textContent = original; }, 1200);
  }
  document.addEventListener('click', function(event){
    var element = event.target.closest('[data-shop-action]');
    if(!element) return;
    var actionName = element.getAttribute('data-shop-action');
    var action = actions[actionName] || window[actionName];
    if(typeof action !== 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var task;
    try {
      if(actionName === 'addToCart') task = action(element);
      else if(actionName === 'buyNow') task = action(element);
      else if(actionName === 'viewProduct') task = action(element);
      else if(actionName === 'clickToFollowShop') task = action(element);
      else task = action.apply(null, parseActionArgs(element));
    } catch(error) {
      task = Promise.reject(error);
    }
    Promise.resolve(task).then(function(){
      if(actionName === 'addToCart') setButtonFeedback(element, 'Ajouté');
    }).catch(function(error){
      console.warn('[ElakayShop]', actionName, error);
      notify((error && error.message) || 'Action impossible', true);
      setButtonFeedback(element, 'Erreur');
    });
  });
  document.querySelectorAll('[data-shop-search]').forEach(function(input){
    input.addEventListener('input', function(){ setSearch(input.value); });
  });

  var cart = readCart();
  updateCount(cart);
  renderPagination(0);
  document.dispatchEvent(new CustomEvent('elakay:shop-ready', { detail:{ api:actions, config:config } }));
})();
</script>`;
}

function attachExportItem(node, item) {
  if (!node || typeof node !== "object") return node;
  const next = { ...node, _exportItem: item };
  if (Array.isArray(next.children)) {
    next.children = next.children.map((child) => attachExportItem(child, item));
  }
  return next;
}

function bindNodeForExport(node, sourceData, sourceKey) {
  if (!node || typeof node !== "object") return [];
  let outNode = JSON.parse(JSON.stringify(node));
  if (sourceKey === "store" && outNode.repeatBind) {
    outNode = withAutoShopUserSync(outNode);
  }

  const rb = outNode.repeatBind;
  if (rb && rb.source && rb.path && sourceData && sourceKey === rb.source) {
    let arr = resolvePath(sourceData, rb.path);
    if (Array.isArray(arr) && arr.length > 0) {
      const pageSize = Math.max(1, rb.pageSize || 12);
      const repeated = [];
      arr.forEach((item) => {
        if (rb.mode === "children") {
          const filledChildren = (outNode.children || []).map((child) =>
            fillNodeWithItem(
              JSON.parse(JSON.stringify(child)),
              item,
              rb.fieldMap,
              rb.autoFill !== false,
              sourceData,
              sourceKey
            )
          ).map((child) => attachExportItem(child, item));
          const base = {
            ...outNode,
            children: filledChildren,
            repeatBind: null,
            ...(rb.paginate ? {
              _exportRepeatGroup: outNode.id,
              _exportPageSize: pageSize,
              _exportItem: item,
            } : {}),
          };
          repeated.push(...bindNodeForExport(base, sourceData, sourceKey));
          return;
        }
        const filled = fillNodeWithItem(
          JSON.parse(JSON.stringify(outNode)),
          item,
          rb.fieldMap,
          rb.autoFill !== false,
          sourceData,
          sourceKey
        );
        const filledWithItem = attachExportItem(filled, item);
        filledWithItem.repeatBind = null;
        if (rb.paginate) {
          filledWithItem._exportRepeatGroup = outNode.id;
          filledWithItem._exportPageSize = pageSize;
        }
        repeated.push(...bindNodeForExport(filledWithItem, sourceData, sourceKey));
      });
      return repeated;
    }
  }

  if (outNode.bindKey && sourceData && sourceKey) {
    const key = outNode.bindKey;
    if (key === sourceKey || key.startsWith(sourceKey + ".")) {
      const val = resolvePath(sourceData, key.slice(sourceKey.length + 1));
      if (val != null) {
        const replaced = { ...outNode, bindKey: null };
        if (isImageNode(outNode)) {
          const src = mediaString(val);
          replaced.src = src;
          replaced._hideWhenEmptyMedia = !src;
        }
        else if (outNode.type === "button" || outNode.type === "link") {
          replaced.text = String(val);
          if (typeof val === "string" && /^https?:\/\//.test(val)) replaced.href = String(val);
        }
        else if (outNode.type === "input" || outNode.type === "textarea") replaced.field = { ...(outNode.field || {}), value: String(val) };
        else if (outNode.type === "select") replaced.options = { ...(outNode.options || {}), choices: Array.isArray(val) ? val.map(String) : [String(val)] };
        else if (isVideoNode(outNode)) {
          const url = mediaString(val);
          replaced.videoUrl = url;
          replaced.options = { ...(outNode.options || {}), url };
          replaced._hideWhenEmptyMedia = !url;
        }
        else replaced.text = String(val);
        outNode = replaced;
      }
    }
  }

  const exportDef = ELEMENT_TYPES[outNode.type];
  if (
    sourceData &&
    sourceKey &&
    (exportDef?.group === "shopLive" || exportDef?.group === "shopHeader")
  ) {
    const sourcePath = outNode.bindSource || sourceKey;
    const relativePath = sourcePath === sourceKey
      ? ""
      : sourcePath.startsWith(sourceKey + ".")
        ? sourcePath.slice(sourceKey.length + 1)
        : sourcePath;
    const resolved = relativePath ? resolvePath(sourceData, relativePath) : sourceData;
    outNode._exportData = resolved ?? null;
    outNode._exportStore = sourceData;
  }

  if (sourceKey === "store" && sourceData) {
    outNode._exportStore = sourceData;
    if (
      outNode.clickActionType === "function" &&
      outNode.clickActionFunction === "addToCart" &&
      !outNode._exportItem
    ) {
      outNode._exportItem = shopArray(sourceData?.products)[0] || null;
    }
  }

  const resolveExportActionToken = (raw) => {
    if (typeof raw !== "string") return raw;
    const value = normalizeHrefLikeValue(raw);
    if (!value || !sourceData || !sourceKey) return raw;
    if (value === sourceKey) return sourceData;
    if (value.startsWith(sourceKey + ".")) {
      const resolved = resolvePath(sourceData, value.slice(sourceKey.length + 1));
      return resolved === undefined ? raw : resolved;
    }
    return raw;
  };
  outNode.clickActionArg1 = resolveExportActionToken(outNode.clickActionArg1);
  outNode.clickActionArg2 = resolveExportActionToken(outNode.clickActionArg2);
  outNode.clickActionArg3 = resolveExportActionToken(outNode.clickActionArg3);
  outNode.clickActionHref = resolveExportActionToken(outNode.clickActionHref);

  if (outNode.type === "swiper" && outNode.bindSource && sourceData && sourceKey) {
    const bindPath = outNode.bindSource.startsWith(sourceKey + ".")
      ? outNode.bindSource.slice(sourceKey.length + 1)
      : outNode.bindSource;
    const slideArr = resolvePath(sourceData, bindPath);
    if (Array.isArray(slideArr) && slideArr.length > 0) {
      outNode = {
        ...outNode,
        bindSource: null,
        items: slideArr.map((item, idx) => ({
          id: item._id || `ds-${idx}`,
          image: item.urlImg || item.image || item.url || item.imgUrl || "",
          title: item.title || item.caption || "",
          text: item.description || item.subtitle || "",
        })),
      };
    }
  }

  if ((outNode.type === "button" || outNode.type === "link") && sourceKey === "store" && sourceData) {
    const hasHref = normalizeHrefLikeValue(outNode.href);
    if (!hasHref) {
      const fallbackShopHref = normalizeHrefLikeValue(resolvePath(sourceData, "links.shopPage"));
      if (fallbackShopHref) outNode.href = fallbackShopHref;
    }
  }

  if (Array.isArray(outNode.children) && outNode.children.length) {
    const boundChildren = [];
    outNode.children.forEach((child) => {
      boundChildren.push(...bindNodeForExport(child, sourceData, sourceKey));
    });
    outNode.children = boundChildren;
  }

  return [outNode];
}

// Rend le corps HTML d'une page (children + surcharges mobile + positionnement)
function renderPageBody(pg, sourceData, sourceKey) {
  const boundChildren = (pg.children || []).flatMap((n) => bindNodeForExport(n, sourceData, sourceKey));
  const hasPositions = getPageLayoutMode(pg, "desktop") === "free";
  const body = boundChildren.map((n) => {
    const html = nodeToHtml(n, "      ");
    if (hasPositions) {
      const pos = getFreeLayout(n, "desktop");
      const u = pos.unit === "%" ? "%" : "px";
      const wcls = n.mobileStyle && Object.keys(n.mobileStyle).length ? ` class="pbm-${n.id}"` : "";
      return `      <div${wcls} style="position:absolute;left:${Math.round(pos.x)}${u};top:${Math.round(pos.y)}${u};z-index:${pos.z};">\n${html.replace(/^/gm, "  ")}\n      </div>`;
    }
    return html;
  }).join("\n");
  const rootStyle = { ...pg.style, ...(hasPositions ? { position: "relative" } : {}) };
  return { body, rootStyle };
}

// Collecte les règles @media mobile sur un ensemble de pages
function collectMediaRules(pages) {
  const rules = [];
  const toImportant = (st) => Object.entries(st).map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())}:${v} !important`).join(";");
  const collect = (n) => {
    if (n.mobileStyle && Object.keys(n.mobileStyle).length) rules.push(`    .pbm-${n.id} { ${toImportant(n.mobileStyle)}; }`);
    n.children?.forEach(collect);
  };
  pages.forEach((p) => (p.children || []).forEach(collect));
  return rules.length ? `\n    @media (max-width: 600px) {\n${rules.join("\n")}\n    }` : "";
}

function collectConsoleCssRules(pages) {
  const rules = [];
  const walk = (n) => {
    if (n?.id && typeof n.consoleCss === "string" && n.consoleCss.trim()) {
      rules.push(`\n[data-pb-node-id="${n.id}"], [data-pb-node-id="${n.id}"] > * { ${n.consoleCss} }`);
    }
    n?.children?.forEach(walk);
  };
  pages.forEach((p) => (p.children || []).forEach(walk));
  return rules.join("\n");
}

function collectConsoleJsScript(pages) {
  // Keep the export call site stable while making historical arbitrary script
  // fields inert. The document data is still embedded for lossless editing.
  void pages;
  return "";
}

function hasTextCollapseNodes(pages = []) {
  const walk = (node) => {
    if (!node) return false;
    if (canUseTextCollapse(node) && getTextCollapseConfig(node).enabled) return true;
    return (node.children || []).some(walk);
  };
  return pages.some((page) => (page.children || []).some(walk));
}

// Export du site complet : toutes les pages dans un seul fichier, navigation par liens (#id)
function buildSiteHtml(doc, boundData = null) {
  const pages = doc.pages || [];
  const sourceData = boundData?.data || null;
  const sourceKey = boundData?.key || "";
  const mediaBlock = collectMediaRules(pages);
  const consoleCssBlock = collectConsoleCssRules(pages);
  const consoleJsBlock = collectConsoleJsScript(pages);
  const embeddedPageBuilderDoc = jsonForInlineScript(doc);
  // Keyframes des animations personnalisées présentes dans le site
  const customKf = [];
  const walkKf = (n) => { if (n.animation === "custom") customKf.push(buildCustomAnim(n).keyframe); n.children?.forEach(walkKf); };
  pages.forEach((p) => (p.children || []).forEach(walkKf));
  const customKfBlock = customKf.join("");
  const docStr = JSON.stringify(doc);
  const hasSwiper = docStr.includes('"type":"swiper"') || docStr.includes('"type":"shopSlider"');
  const hasModal = docStr.includes('"type":"modal"');
  const hasShopLive =
    sourceKey === "store" ||
    doc.pageMode === "store" ||
    ['"shopVote"', '"shopCarousel"', '"shopPrice"', '"shopCard"', '"shopSlider"', '"shopProductGrid"', '"shopCheckout"', '"shopHeaderSeller"', '"shopCatMatrix"', '"shopPubMatrix"', '"shopButtonPagination"', '"showImage"'].some((type) => docStr.includes(type));
  const hasWidgets = ['"dropdown"', '"tabs"', '"offcanvas"', '"tooltip"'].some((t) => docStr.includes(t));
  const hasTextCollapse = hasTextCollapseNodes(pages);

  const sections = pages.map((pg, i) => {
    const { body, rootStyle } = renderPageBody(pg, sourceData, sourceKey);
    return `  <section id="page-${pg.id}" class="pb-page" style="display:${i === 0 ? "block" : "none"};">
    <div style="${styleToString(rootStyle)}">
${body}
    </div>
  </section>`;
  }).join("\n");

  // Script de navigation : les liens vers #page-XXX affichent la bonne section
  const navScript = `
<script>
(function(){
  function normalizePageId(raw){
    if(!raw) return '';
    return raw.indexOf('page-')===0 ? raw.slice(5) : raw;
  }
  function show(id){
    var clean = normalizePageId(id);
    document.querySelectorAll('.pb-page').forEach(function(s){ s.style.display = s.id===('page-'+clean) ? 'block' : 'none'; });
    window.scrollTo(0,0);
  }
  document.querySelectorAll('a[href^="#"]').forEach(function(a){
    a.addEventListener('click', function(e){
      var raw = a.getAttribute('href').slice(1);
      var id = normalizePageId(raw);
      if (document.getElementById('page-'+id)) { e.preventDefault(); show(id); history.replaceState(null,'','#'+id); }
    });
  });
  if (location.hash) {
    var raw = location.hash.slice(1);
    var id = normalizePageId(raw);
    if (document.getElementById('page-'+id)) show(id);
  }
})();
</script>`;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="description" content="${esc(doc.meta?.description)}" />
  <title>${doc.meta?.title || "Site"}</title>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
  <style>*{box-sizing:border-box;margin:0;padding:0}${ANIMATION_KEYFRAMES}${customKfBlock}${mediaBlock}${consoleCssBlock}</style>
</head>
<body>
${sections}
<script id="pb-pagebuilder-document" type="application/json">${embeddedPageBuilderDoc}</script>
${hasSwiper ? SWIPER_SCRIPT : ""}${hasShopLive ? buildShopLiveScript(sourceData || {}) : ""}${hasModal ? MODAL_SCRIPT : ""}${hasWidgets ? WIDGETS_SCRIPT : ""}${hasTextCollapse ? buildTextCollapseScript() : ""}${navScript}${consoleJsBlock}
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Styles partagés
// ---------------------------------------------------------------------------
const iconBtn = { display: "flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", border: "1px solid #ece9f5", borderRadius: "8px", background: "#fff", cursor: "pointer", color: "#6d28d9" };
const padBtn = { display: "flex", alignItems: "center", justifyContent: "center", height: "32px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#f3effe", cursor: "pointer", color: "#6d28d9" };
const miniBtn = { display: "flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderWidth: 0, borderStyle: "none", borderColor: "transparent", background: "none", cursor: "pointer", color: "inherit", opacity: 0.6, padding: 0 };
const fieldLabel = { fontSize: "11px", fontWeight: "600", color: "#7a73a0", textTransform: "uppercase", letterSpacing: "0.04em" };
const selectStyle = { width: "100%", marginTop: "4px", padding: "8px 10px", fontSize: "13px", border: "1px solid #e4e0f0", borderRadius: "8px", background: "#faf9fd", color: "#2a2547", outline: "none" };
function toggleBtn(active) {
  return { display: "flex", alignItems: "center", gap: "5px", padding: "6px 12px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "8px", cursor: "pointer", background: active ? "#fff" : "transparent", color: active ? "#6d28d9" : "#7a73a0", boxShadow: active ? "0 1px 3px rgba(60,30,110,0.12)" : "none" };
}
function tabBtn(active) {
  return { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "8px 10px", fontSize: "13px", fontWeight: 600, border: "none", borderRadius: "9px", cursor: "pointer", background: active ? "#f3effe" : "transparent", color: active ? "#6d28d9" : "#7a73a0" };
}
const ghostBtn = { display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", fontSize: "13px", fontWeight: 600, borderWidth: "1px", borderStyle: "solid", borderColor: "#ece9f5", borderRadius: "9px", background: "#fff", color: "#4a4a68", cursor: "pointer" };
const primaryBtn = { display: "flex", alignItems: "center", gap: "6px", padding: "8px 16px", fontSize: "13px", fontWeight: 600, borderWidth: 0, borderStyle: "none", borderColor: "transparent", borderRadius: "9px", background: "linear-gradient(135deg,#6d28d9,#8b5cf6)", color: "#fff", cursor: "pointer" };
const addCard = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "14px 6px", border: "1px solid #ece9f5", borderRadius: "12px", background: "#faf9fd", cursor: "pointer", color: "#4a4a68" };
